export type Locale = "ar" | "en";

/**
 * CLDR plural categories. Arabic uses all six, English only `one`/`other` —
 * declaring the rest optional keeps the English dictionary honest instead of
 * forcing it to invent forms it never selects.
 */
export interface PluralForms {
  zero?: string;
  one: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

/**
 * The single source of truth for every user-visible string. Both dictionaries
 * are annotated with this type, so a key added here fails to compile until it
 * exists in Arabic *and* English.
 */
export interface Dictionary {
  app: {
    name: string;
    byline: string;
    description: string;
  };
  auth: {
    signInTitle: string;
    signInSubtitle: string;
    userLabel: string;
    passwordLabel: string;
    submit: string;
    submitting: string;
    errors: {
      userRequired: string;
      passwordRequired: string;
      invalidCredentials: string;
      tenantNotFound: string;
      accountLocked: string;
      mustChangePassword: string;
      sessionExpired: string;
      forbidden: string;
      forbiddenHint: string;
      network: string;
      unknown: string;
    };
  };
  dataTable: {
    search: string; columns: string; print: string; pdf: string; excel: string;
    previous: string; next: string; rowsPerPage: string; showing: string;
    selected: string; selectAll: string; selectRow: string; emptyTitle: string;
    view: string; edit: string; review: string; post: string; cancel: string;
    actions: string;
  };
  shell: {
    navigation: string;
    breadcrumb: string;
    skipToContent: string;
    openScreens: string;
    closeScreen: string;
    closeOtherScreens: string;
    toggleNavigation: string;
    closeNavigation: string;
    primaryNavigation: string;
    module: string;
    searchTheSystem: string;
    collapseSidebar: string;
    expandSidebar: string;
    expandSection: string;
    collapseSection: string;
    search: string;
    searchPlaceholder: string;
    searchHint: string;
    searchNoResults: string;
    searchSearching: string;
    notifications: string;
    notificationsEmpty: string;
    notificationsEmptyHint: string;
    fiscalYear: string;
    fiscalYearUnavailable: string;
    switchCompany: string;
    openMenu: string;
    account: string;
    company: string;
    branch: string;
    signOut: string;
    languageToggle: string;
    themeToggle: string;
    themeLight: string;
    themeDark: string;
  };
  /**
   * Every label in the navigation hierarchy, in one place.
   *
   * The sidebar, the breadcrumb trail and the section overview screens all read
   * from here through `nav-tree.ts`, so a module can never be called one thing in
   * the menu and another in the trail.
   */
  nav: {
    home: string;
    customerTransactionSettlement: string;
    dashboard: string;
    comingSoon: string;

    /** The three top-level system sections. */
    financialManagement: string;
    humanResources: string;
    hotelFrontDesk: string;
    systemSettings: string;

    /** Financial Management modules. */
    generalLedger: string;
    cashAndBanks: string;
    payments: string;
    receipts: string;
    fixedAssets: string;
    inventory: string;

    /** Human Resources modules. */
    planningAndRecruitment: string;
    employeeAffairs: string;
    payrollAndWages: string;
    trainingAndDevelopment: string;
    attendanceMonitoring: string;

    /** Human Resources screens. */
    employees: string;

    /** System Settings modules. */
    usersAndPermissions: string;
    controlPanel: string;

    /** System Settings screens. */
    users: string;
    roles: string;
    permissions: string;
    auditTrail: string;
    accessMatrix: string;
    companies: string;
    branches: string;

    /** The five standard sections every financial module carries. */
    masterData: string;
    identityCards: string;
    dailyTransactions: string;
    inquiries: string;
    reports: string;
    settings: string;

    /**
     * General Ledger › Master Data has no labels of its own: the three screens are
     * named by `masterData.currencies` and friends, which the tree points straight at.
     */

    /** General Ledger › Daily Transactions. */
    journalEntries: string;
    journalAuditPosting: string;

    /** General Ledger › Inquiries. */
    dailyTransactionsInquiry: string;
    accountMovements: string;
    costCenterAccount: string;
    cashSubledgerMovements: string;
    unauditedJournalNotes: string;
    taxSettings: string;
    detailedVoucherTax: string;
    financialPosition: string;
    movementsWithoutCostCenters: string;

    /** General Ledger › Reports. The sidebar stops at these category names. */
    reportsAccounting: string;
    reportsVouchers: string;
    reportsBatchVoucherPrinting: string;
    reportsSalesperson: string;
    reportsCostCenters: string;
    reportsTrialBalance: string;
    reportsClosing: string;
    reportsCheques: string;
    reportsCashBoxes: string;
    reportsGeneral: string;

    /** General Ledger › Settings. Ledger settings, NOT global system settings. */
    financialStatementsSetup: string;
    freezeTransactions: string;
    accountCategories: string;
    accountTypes: string;
    accountOpeningBalances: string;
    voucherTypes: string;
    financialIntegrationSettings: string;
    /**
     * General Ledger › Settings › System Settings - the same asymmetry as
     * `fixedAssetsSystemSettings`: Arabic reads plain "إعدادات النظام" per the approved list,
     * English is module-qualified ("General Ledger System Settings") to stay distinct from the
     * unrelated global `systemSettings` page. Both are recorded verbatim.
     */
    generalLedgerSettings: string;
    journalEntryAuditNotes: string;
    costCenterGroups: string;

    /** Cash and Banks › Master Data. */
    cashboxDefinition: string;
    bankDefinition: string;
    bankingFacilities: string;

    /**
     * Cash and Banks › Daily Transactions.
     *
     * `lettersOfCredit` and `bankLoans` are each used twice — once as a transaction
     * screen, once as the inquiry over it — so they are declared once here.
     */
    cashDeposit: string;
    cashWithdrawal: string;
    cashboxTransactions: string;
    immediateChequeDeposit: string;
    chequeDepositForCollection: string;
    collectChequeForCollection: string;
    withdrawChequesForCollection: string;
    returnedCheques: string;
    processReturnedCheques: string;
    immediateChequePayment: string;
    collectPostDatedCheques: string;
    deliverPostDatedCheque: string;
    deliverImmediateOrPostDatedCheques: string;
    returnImmediateOrPostDatedCheque: string;
    processReturnedPaidCheques: string;
    lettersOfCredit: string;
    letterOfCreditPreparationRequest: string;
    bankReconciliation: string;
    interbankTransfer: string;
    bankTransactions: string;
    changeChequeDueDate: string;
    cancelCheque: string;
    withdrawDeliverFromChequeCashbox: string;
    calculateBankInterest: string;
    bankLoans: string;
    repayLoanInstallments: string;
    dueChequesReminder: string;

    /**
     * Cash and Banks › Inquiries.
     *
     * Account Movements and Daily Transactions are named by the General Ledger keys
     * above: same noun, one string. Only what is genuinely new is declared here.
     */
    cashboxClosingTransactions: string;
    bankAccountCurrencyLocal: string;
    bankAccount: string;
    chequeMovements: string;
    paidCheques: string;
    receivedCheques: string;
    immediatePostDatedChequeDelivery: string;
    undeliveredCheques: string;
    cashboxMovements: string;
    cashboxAccount: string;
    cashboxCount: string;
    cashboxClosingFollowUp: string;
    missingChequeSequences: string;

    /**
     * Cash and Banks › Settings.
     *
     * Voucher Types and Financial Integration Settings reuse the General Ledger
     * keys: every financial module carries its own, and the label is identical.
     */
    editCashboxData: string;
    cashboxGroups: string;
    cashboxCashFloat: string;
    bankOpeningBalances: string;
    cashboxOpeningBalances: string;
    cashAndBanksSystemSettings: string;
    letterOfCreditTypes: string;
    linkUsersToCashboxes: string;
    reportSignatures: string;
    chequeReturnReasons: string;
    paymentMethods: string;
    chequeBooks: string;
    expenseTypes: string;

    /** Payments › Master Data. */
    suppliers: string;
    userSupplierAccess: string;
    coordinators: string;
    salespersons: string;
    collectors: string;
    drivers: string;

    /** Payments › Daily Transactions. */
    creditNote: string;
    debitNote: string;
    paymentVoucher: string;
    paymentOrder: string;
    paymentBooks: string;
    servicePurchaseInvoice: string;
    servicePurchaseReturn: string;
    servicePurchaseRequest: string;
    supplierAccountSettlement: string;
    supplierCreditLimitAdjustment: string;
    cancelInvoicePayment: string;
    editSupplierInformation: string;
    prepaidExpenses: string;
    supplierBalanceReminder: string;
    transferSupplierTransactions: string;
    accountReconciliation: string;

    /**
     * Payments › Inquiries.
     *
     * "Daily Transactions" is named by the key above: same noun, one string.
     * `agingReport` is declared once and used by both the inquiry and, later, the
     * report of the same name.
     */
    paymentOrdersNotInVouchers: string;
    supplierInformation: string;
    supplierMovements: string;
    supplierAccountMovements: string;
    supplierAccountCurrencyLocal: string;
    totalSupplierPayables: string;
    totalSupplierBalancesCostCenter: string;
    expenseTransactions: string;
    expenseAccount: string;
    purchaseOrdersWaitingPayment: string;
    deliverySchedule: string;
    receiptAndPaymentBooks: string;
    paymentsDailyTransactionsInquiry: string;
    paymentVoucherMovements: string;
    servicePurchaseDocumentMovements: string;
    unsettledPaymentOrders: string;
    supplierInformationInquiry: string;
    supplierAccountCurrency: string;
    supplierBalancesCostCenter: string;
    purchaseOrdersAwaitingPayment: string;
    receiptAndPaymentBooksInquiry: string;
    supplierItemTransactions: string;
    outstandingSupplierInvoicesInquiry: string;
    paidUnpaidSupplierInvoices: string;
    unappliedPaymentVouchers: string;
    expenseMovementsInquiry: string;
    expenseAccountInquiry: string;
    expenseMovementsCostCenter: string;
    expenseAccountCostCenterInquiry: string;
    customerAgingInquiry: string;
    purchaseItems: string;
    purchaseOrders: string;
    paymentBooksInquiry: string;
    missingPaymentBookSequences: string;
    itemTransactionsSupplier: string;
    voucherTransactions: string;
    journalVouchersInquiry: string;
    serviceVoucherTransactions: string;
    outstandingSupplierInvoices: string;
    paidAndUnpaidInvoices: string;
    expenseAccountCostCenter: string;
    supplierMovementsByCostCenter: string;
    agingReport: string;
    paymentVouchersWithoutSettledInvoices: string;
    expenseTransactionsCostCenter: string;

    /** Payments › Reports. The single approved category. */
    reportsPayments: string;

    /**
     * Payments › Settings.
     *
     * Voucher Types, Financial Integration Settings, Report Signatures and Expense
     * Types reuse the keys declared above — every financial module carries its own
     * screen, and the label is the same word.
     */
    businessActivities: string;
    businessNature: string;
    supplierCategories: string;
    supplierOpeningBalances: string;
    workflowProcedures: string;
    workflowActions: string;
    paymentsSystemSettings: string;
    linkUsersToSuppliers: string;
    discountTypes: string;
    servicePurchaseTypes: string;
    countriesAndRegions: string;
    documentTypes: string;
    supplierEvaluationCriteria: string;
    agingPeriodDefinitions: string;
    expenseOpeningBalances: string;

    /** Receipts › Master Data. */
    customers: string;

    /**
     * Receipts › Daily Transactions.
     *
     * Credit Note, Debit Note and Account Reconciliation reuse the Payments keys:
     * both modules carry the screen, and the label is the same word.
     */
    receiptVoucher: string;
    receiptOrder: string;
    receiptBooks: string;
    serviceSalesInvoice: string;
    serviceSalesReturn: string;
    serviceQuotation: string;
    deferredRevenue: string;
    advanceReceivedRevenue: string;
    customerBalanceReminder: string;
    transferCustomerTransactions: string;
    customerSettlement: string;
    cancelInvoiceSettlement: string;

    /**
     * Receipts › Inquiries. Seven of the twenty reuse keys declared above -
     * Voucher Transactions, Daily Transactions, Aging Report and friends.
     */
    customerTransactions: string;
    customerAccountCurrencyLocal: string;
    customerAccount: string;
    totalCustomerBalances: string;
    totalCustomerBalancesCostCenter: string;
    receiptVouchersWithoutInvoices: string;
    missingReceiptBookSequences: string;
    missingReceiptVoucherSequences: string;
    itemTransactionsCustomer: string;
    outstandingCustomerInvoices: string;
    shortAgingReport: string;
    customerAccountCostCenter: string;
    detailedAgingReport: string;

    /** Receipts › Reports. The single approved category. */
    reportsReceipts: string;

    /**
     * Receipts › Settings. Eleven of the twenty-two reuse keys declared above -
     * including `systemSettings`, which the approved list names verbatim and which
     * the top-level section already declares.
     */
    customerCategories: string;
    customerClasses: string;
    customerTypes: string;
    customerOpeningBalances: string;
    smsSettings: string;
    ePaymentPolicy: string;
    serviceSalesTypes: string;
    custodyCategories: string;
    linkUsersToCustomers: string;

    /** Fixed Assets › Master Data. `suppliers` is reused from Payments. */
    assetGroups: string;
    assetLocations: string;
    assetCard: string;
    insuranceCompanies: string;

    /**
     * Fixed Assets › Daily Transactions.
     *
     * `assetExpenses` is declared once and used twice — the transaction screen and
     * the inquiry over it carry the same name.
     */
    custodyDeposit: string;
    custodyIssue: string;
    assetPurchaseReturns: string;
    assetPurchaseInvoice: string;
    purchaseOrder: string;
    quotationComparison: string;
    quotationAward: string;
    requestForQuotation: string;
    assetSale: string;
    assetDepreciation: string;
    assetRevenue: string;
    assetExpenseReturns: string;
    assetExpenses: string;
    changeAssetLocation: string;
    assetInsurance: string;
    changeAssetCustody: string;
    releaseReturnCustody: string;
    assetItemTransfer: string;
    assetInventoryCount: string;
    assetInventoryWorksheet: string;
    assetMaintenance: string;
    assetDisposal: string;
    /** Approved Daily Transactions with no entry in the registered list; added, not renamed. */
    assetRevaluation: string;
    assetSplit: string;

    /** Fixed Assets › Inquiries. Asset Expenses and Daily Transactions reuse keys above. */
    assetLifeCycle: string;
    assetFinancialMovements: string;
    assetMovements: string;
    assetRecords: string;
    assetItemTransferStatus: string;
    assetTree: string;

    /** Fixed Assets › Reports. The single approved category. */
    reportsFixedAssets: string;

    /**
     * Fixed Assets › Settings.
     *
     * Voucher Types, Financial Integration Settings, Report Signatures, Countries &
     * Regions and Expense Types all reuse keys declared above.
     *
     * `fixedAssetsSystemSettings` is the one entry whose two approved lists disagree:
     * the English reads "Fixed Assets System Settings", the Arabic reads
     * "إعدادات النظام" (plain "System Settings"). Both are recorded verbatim rather
     * than one being invented to match the other.
     */
    assetModels: string;
    brands: string;
    assetClasses: string;
    assetCategories: string;
    assetProperties: string;
    fixedAssetsSystemSettings: string;
    shippingMethods: string;
    assetCodingSettings: string;
    salesTypes: string;
    assetDisposalReasons: string;
    assetTypes: string;
    deliveryMethods: string;
    purchaseRoutes: string;
    quotationTerms: string;
  };
  /** Copy shared by every section overview and structural placeholder screen. */
  overview: {
    chooseSection: string;
    comingSoonHint: string;
    moduleComingSoon: string;
    moduleComingSoonHint: string;
    standardSections: string;
    systemSettingsEmpty: string;
    systemSettingsHint: string;
    open: string;
  };
  dashboard: {
    placeholder: string;
    title: string;
    subtitle: string;
    kpi: {
      accounts: string;
      costCenters: string;
      currencies: string;
      reports: string;
      postable: string;
      groups: string;
      activeOf: string;
      baseCurrency: string;
      categories: string;
    };
    quickActions: string;
    quickActionsHint: string;
    activity: string;
    activityEmpty: string;
    activityEmptyHint: string;
    financialRestricted: string;
    financialAsOf: string;
    financial: string;
    financialEmpty: string;
    financialEmptyHint: string;
    notAvailable: string;
    metrics: {
      revenue: string;
      expenses: string;
      netResult: string;
      cash: string;
    };
    banks: {
      title: string;
      hint: string;
      empty: string;
      emptyHint: string;
      restricted: string;
      total: string;
      account: string;
      balance: string;
      main: string;
      asOf: string;
    };
    sales: {
      title: string;
      hint: string;
      restricted: string;
      day: string;
      material: string;
      service: string;
      total: string;
      invoices: string;
      noSales: string;
      today: string;
      yesterday: string;
    };
  };
  reports: {
    title: string;
    subtitle: string;
    empty: string;
    emptyHint: string;
    recent: string;
    recentEmpty: string;
    favorites: string;
    favoritesEmpty: string;
    clearRecent: string;
    searchAll: string;
    searchAllPlaceholder: string;
    inCategory: string;
    comingSoon: string;
    reportCount: PluralForms;
    categoryCount: PluralForms;
    /** The category screen that lists reports. Shared by every category. */
    category: {
      backToCenter: string;
      searchLabel: string;
      searchPlaceholder: string;
      clearSearch: string;
      /** Live region after filtering, e.g. "3 of 23 reports". */
      searchResults: string;
      noMatches: string;
      noMatchesHint: string;
      empty: string;
      emptyHint: string;
      notFound: string;
      notFoundHint: string;
      openReport: string;
      addFavorite: string;
      removeFavorite: string;
    };
    /** The per-report parameter screen, not built yet. */
    parameters: {
      title: string;
      pending: string;
      pendingHint: string;
      blocked: string;
      blockedReasons: {
        cancelled: string;
        noDueDate: string;
        noExpiry: string;
        noSource: string;
        noBills: string;
        ambiguous: string;
      };
      backToCategory: string;
    };
  };
  /** Financial Management > General Ledger > Master Data. Shared by all three screens. */
  masterData: {
    currencies: string;
    costCenters: string;
    chartOfAccounts: string;
    add: string;
    addRoot: string;
    addChild: string;
    edit: string;
    activate: string;
    deactivate: string;
    delete: string;
    save: string;
    saving: string;
    cancel: string;
    close: string;
    searchLabel: string;
    searchPlaceholder: string;
    clearSearch: string;
    listView: string;
    treeView: string;
    expandAll: string;
    collapseAll: string;
    /** Per-node disclosure. Distinct from expandAll/collapseAll, which act on the whole tree. */
    expandNode: string;
    collapseNode: string;
    active: string;
    inactive: string;
    status: string;
    actions: string;
    empty: string;
    emptyHint: string;
    noMatches: string;
    noMatchesHint: string;
    loadFailed: string;
    saveFailed: string;
    companyRequired: string;
    createdOk: string;
    updatedOk: string;
    activatedOk: string;
    deactivatedOk: string;
    deletedOk: string;
    confirmDeleteTitle: string;
    confirmDelete: string;
    confirmDeactivateTitle: string;
    confirmDeactivate: string;
    requiredField: string;
    code: string;
    nameEn: string;
    nameAr: string;
    notes: string;
    parent: string;
    noParent: string;
    level: string;
    filterAll: string;
    filterActive: string;
    filterInactive: string;
    isGroup: string;
    isGroupHint: string;
    detail: {
      title: string;
      noSelection: string;
      noSelectionHint: string;
      identity: string;
      classification: string;
      hierarchy: string;
      flags: string;
      audit: string;
      createdAt: string;
      updatedAt: string;
      openingBalance: string;
      debit: string;
      credit: string;
      parentPath: string;
      children: string;
      backToList: string;
    };
    currency: {
      symbol: string;
      decimalPlaces: string;
      exchangeRate: string;
      exchangeRateHint: string;
      isBase: string;
      isBaseHint: string;
      baseBadge: string;
      inUse: string;
      inUseHint: string;
      codeLocked: string;
      /** Rate history and amount-in-words, added when the screen was completed. */
      currentRate: string;
      effectiveFrom: string;
      effectiveTo: string;
      effectiveToHint: string;
      baseFilter: string;
      baseOnly: string;
      nonBaseOnly: string;
      refresh: string;
      view: string;
      addRate: string;
      rateHistory: string;
      rateHistoryEmpty: string;
      rateAdded: string;
      current: string;
      superseded: string;
      future: string;
      openEnded: string;
      sectionBasic: string;
      sectionWordsAr: string;
      sectionWordsEn: string;
      sectionRate: string;
      iconUrl: string;
      iconUrlHint: string;
      wordsSingular: string;
      wordsDual: string;
      wordsPlural: string;
      wordsFractionSingular: string;
      wordsFractionDual: string;
      wordsFractionPlural: string;
      wordsPreview: string;
      wordsPreviewHint: string;
      wordsUnset: string;
      baseRateFixed: string;
      baseCannotDelete: string;
      rateNotSet: string;
      rateOptionalHint: string;
      makeBase: string;
      makeBaseTitle: string;
      makeBaseMessage: string;
      makeBaseConfirm: string;
      baseChanged: string;
      viewTitle: string;
      sectionStatusBase: string;
    };
    account: {
      taxSection: string; taxHint: string; taxNone: string; taxConfigure: string;
      taxActive: string; taxInactive: string;
      taxSideOutput: string; taxSideInput: string; taxSideBoth: string;
      accountType: string;
      accountTypeLookup: string;
      normalBalance: string;
      financialStatement: string;
      statements: { balanceSheet: string; incomeStatement: string };
      statementFollowsTypeHint: string;
      debit: string;
      credit: string;
      allowPosting: string;
      allowPostingHint: string;
      requiresCostCenter: string;
      requiresCostCenterHint: string;
      defaultCostCenter: string;
      defaultCostCenterHint: string;
      defaultCostCenterNone: string;
      defaultCostCenterRequired: string;
      systemAccount: string;
      systemAccountHint: string;
      category: string;
      openingDate: string;
      referenceNumber: string;
      closed: string;
      closedHint: string;
      typeLockedTitle: string;
      typeLockedHint: string;
      costCentersHint: string;
      types: {
        asset: string;
        liability: string;
        equity: string;
        revenue: string;
        expense: string;
      };
    };
  };
  /** General Ledger › Daily Transactions › Journal Entries. */
  /** General Ledger › Daily Transactions › Journal Audit & Posting. */
  auditPosting: {
    /** Workflow refusals, localised by errorCode. The server's own wording is the fallback. */
    errorSelfAudit: string;
    errorSelfPost: string;
    errorAlreadyAudited: string;
    errorAlreadyPosted: string;
    errorInvalidAuditTransition: string;
    errorInvalidPostTransition: string;
    errorInvalidRejectTransition: string;
    errorCannotRejectPosted: string;
    errorAuditRequired: string;
    errorUnbalanced: string;
    errorZeroValue: string;
    errorInsufficientLines: string;
    errorBranchOutOfScope: string;
    errorForbidden: string;
    title: string;
    filters: string;
    search: string;
    reset: string;
    results: string;
    noResults: string;
    openReview: string;
    inquiryTitle: string;
    inquiryDialogTitle: string;
    review: string;
    selectPrompt: string;
    auditStatus: string;
    postingStatus: string;
    validationStatus: string;
    valid: string;
    invalid: string;
    pendingAudit: string;
    approved: string;
    rejected: string;
    posted: string;
    unposted: string;
    auditDecision: string;
    auditNotes: string;
    auditNotesHint: string;
    approveAudit: string;
    rejectReturn: string;
    saveNotes: string;
    rejectReason: string;
    rejectReasonRequired: string;
    postJournal: string;
    postingBlocked: string;
    blockers: {
      noPermission: string;
      alreadyPosted: string;
      rejected: string;
      pendingAudit: string;
      wrongStatus: string;
      outOfBalance: string;
      periodClosed: string;
    };
    postingReady: string;
    confirmPost: string;
    confirmPostBody: string;
    approvedOn: string;
    rejectedOn: string;
    postedOn: string;
    readOnlyReview: string;
    voucherTypeFrom: string;
    voucherTypeTo: string;
    voucherNumberFrom: string;
    voucherNumberTo: string;
  };
  /** General Ledger › Inquiries › Daily Transactions Inquiry. */
  inquiry: {
    title: string;
    filters: string;
    account: string;
    includeChildren: string;
    dateFrom: string;
    dateTo: string;
    fiscalYear: string;
    branch: string;
    reportScope: string;
    scopeCompany: string;
    scopeBranch: string;
    scopeAllBranches: string;
    voucherFrom: string;
    voucherTo: string;
    referenceNumber: string;
    referenceDateFrom: string;
    referenceDateTo: string;
    user: string;
    salesperson: string;
    costCenter: string;
    subledger: string;
    currency: string;
    auditStatus: string;
    postingStatus: string;
    descriptionSearch: string;
    selectAccountPrompt: string;
    noResults: string;
    results: string;
    voucherDate: string;
    voucherNumber: string;
    voucherType: string;
    accountNumber: string;
    accountName: string;
    description: string;
    exchangeRate: string;
    debit: string;
    credit: string;
    localDebit: string;
    localCredit: string;
    sourceModule: string;
    movementCount: string;
    totalDebit: string;
    totalCredit: string;
    totalLocalDebit: string;
    totalLocalCredit: string;
    netMovement: string;
    posted: string;
    unposted: string;
    invalidRange: string;
    balancesElsewhere: string;
  };
  cashSubledger: {
    title: string;
    selectPrompt: string;
    partyType: string;
    partyAll: string;
    partyCustomer: string;
    partySupplier: string;
    partyNone: string;
    noPartyHint: string;
    customer: string;
    supplier: string;
    selectParty: string;
    partyCode: string;
    partyName: string;
    documentNumber: string;
    documentType: string;
    documentStatus: string;
    documentFrom: string;
    documentTo: string;
    openDocument: string;
    paymentMethod: string;
    methodCash: string;
    methodBankTransfer: string;
    methodCard: string;
    methodCheque: string;
    methodImmediate: string;
    methodMixed: string;
    cashbox: string;
    bankAccount: string;
    severalAccounts: string;
    netAmount: string;
    taxAmount: string;
    totalAmount: string;
    paidAmount: string;
    outstandingAmount: string;
    localAmount: string;
    salesperson: string;
    notAvailable: string;
    taxUnavailableHint: string;
    outstandingWarning: string;
    includeCancelled: string;
    includeCancelledHint: string;
    statusCancelled: string;
    statusReversed: string;
    excludedNote: string;
    documentCount: string;
    totalNet: string;
    totalTax: string;
    totalGross: string;
    totalPaid: string;
    totalOutstanding: string;
    totalLocal: string;
    totalCash: string;
    totalBank: string;
    totalCard: string;
    totalCheque: string;
    totalReturns: string;
    totalNoParty: string;
    settlementNote: string;
    unpostedNote: string;
    notConfigured: string;
  };
  unauditedNotes: {
    title: string;
    noResults: string;
    scope: string;
    scopeOutstanding: string;
    scopeUnresolved: string;
    scopeAll: string;
    statePendingAudit: string;
    stateReturned: string;
    stateResubmitted: string;
    stateAudited: string;
    statePosted: string;
    stateCancelled: string;
    stateReversed: string;
    voucherDate: string;
    createdBy: string;
    auditor: string;
    lastAction: string;
    lastActionAt: string;
    actionCreated: string;
    actionModified: string;
    actionAudited: string;
    actionReturned: string;
    actionPosted: string;
    actionReversed: string;
    auditNotes: string;
    journalNotes: string;
    noNote: string;
    notesHidden: string;
    notesHiddenHint: string;
    unresolved: string;
    returnCount: string;
    resubmissionCount: string;
    returnedRepeatedly: string;
    totalDebit: string;
    totalCredit: string;
    difference: string;
    validationStatus: string;
    balanced: string;
    unbalanced: string;
    unbalancedHint: string;
    createdAt: string;
    lastModifiedAt: string;
    openForCorrection: string;
    openReadOnly: string;
    viewAuditHistory: string;
    referenceDateFrom: string;
    referenceDateTo: string;
    auditNoteSearch: string;
    journalNotesSearch: string;
    returnedMoreThanOnce: string;
    returnedMoreThanOnceHint: string;
    unbalancedOnly: string;
    unbalancedOnlyHint: string;
    includeCancelled: string;
    includeReversed: string;
    entryCount: string;
    pendingAuditCount: string;
    returnedCount: string;
    resubmittedCount: string;
    unresolvedNotesCount: string;
    unbalancedCount: string;
    totalDifference: string;
    ownEntriesOnly: string;
    unbalancedWarning: string;
  };
  freezeTransactions: {
    title: string; pageDescription: string; readOnlyNotice: string; breadcrumb: string;
    phaseNotice: string; sectionHeader: string; sectionScope: string; sectionVoucherTypes: string;
    code: string; freezeDate: string; fromDate: string; toDate: string; description: string;
    freezeDateHint: string; branches: string; voucherTypes: string; addVoucherTypes: string;
    remove: string; search: string; refresh: string; save: string; cancel: string; newFreeze: string;
    active: string; inactive: string; activate: string; deactivate: string; catalogue: string;
    period: string; empty: string; emptyVoucherTypes: string; noneSelected: string;
    required: string; created: string; saved: string; activated: string; deactivated: string;
    loadError: string; saveError: string; searchPlaceholder: string; selectAll: string;
    clearAll: string;
  };
  accountCategories: {
    title: string; pageDescription: string; readOnlyNotice: string; breadcrumb: string;
    sectionCategory: string; code: string; descriptionAr: string; descriptionEn: string;
    active: string; inactive: string; catalogue: string; save: string; cancel: string;
    deleteAction: string; usage: string; inUse: string; searchPlaceholder: string;
    search: string; refresh: string; empty: string; required: string; created: string;
    saved: string; deleted: string; activated: string; deactivated: string;
    loadError: string; saveError: string; deleteTitle: string; deleteConfirm: string;
    close: string; comingSoonHint: string; print: string; exportPdf: string; exportExcel: string;
    inactiveHint: string;
  };
  accountTypes: {
    title: string; pageDescription: string; readOnlyNotice: string; breadcrumb: string;
    sectionAccountType: string; code: string; descriptionAr: string; descriptionEn: string;
    scope: string; local: string; international: string; active: string; inactive: string;
    catalogue: string; newType: string; save: string; cancel: string; deleteAction: string;
    activate: string; deactivate: string; usage: string; inUse: string;
    searchPlaceholder: string; search: string; refresh: string; empty: string;
    required: string; created: string; saved: string; deleted: string; activated: string;
    deactivated: string; loadError: string; saveError: string; deleteTitle: string;
    deleteConfirm: string; close: string; comingSoonHint: string; print: string;
    exportPdf: string; exportExcel: string;
  };
  serviceSalesTypes: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionServiceSalesType: string; catalogue: string;
    code: string; descriptionAr: string; descriptionEn: string; account: string;
    taxType: string; taxable: string; active: string; inactive: string;
    selectAccount: string; selectTaxType: string; noTaxType: string;
    yes: string; no: string; back: string; query: string; save: string; saveAndNew: string;
    cancel: string; refresh: string; search: string; searchPlaceholder: string;
    statusAll: string; statusActive: string; statusInactive: string;
    activate: string; deactivate: string; auditHistory: string; auditNone: string;
    auditFields: string; close: string; empty: string; required: string;
    taxTypeRequired: string; created: string; saved: string; activated: string;
    deactivated: string; loadError: string; saveError: string; noDeleteHint: string;
    lastChanged: string;
  };
  paymentMethodsScreen: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; list: string; code: string; descriptionAr: string; descriptionEn: string;
    active: string; inactive: string; usage: string; actions: string;
    newMethod: string; save: string; cancel: string; edit: string; remove: string;
    activate: string; deactivate: string; search: string; searchPlaceholder: string;
    refresh: string; showInactive: string; empty: string; required: string;
    saved: string; deleted: string; activated: string; deactivated: string;
    loadError: string; saveError: string; deleteConfirm: string; inUseHint: string;
    settlement: string; settlesInto: string; targetNone: string; targetCashbox: string;
    targetBank: string; cashbox: string; bank: string; bankBranch: string; bankAccount: string;
    settlementAccount: string; accountFromCashbox: string; choose: string; branchOptional: string;
    fromBankAccounts: string; noBankAccountsHint: string;
  };
  inventoryLinks: { title: string; breadcrumb: string; notice: string };
  inventoryMaster: {
    printError: string;
    inquiry: string; inquiryTitle: string; newRecord: string; save: string; saveAndNew: string;
    print: string; cancel: string; delete: string; close: string; search: string;
    saved: string; deleted: string; saveError: string; deleteError: string; loadError: string;
    readOnly: string; editing: string; noRecords: string; none: string;
    /** The searchable list: prompt inside the box, and the line shown when nothing matches. */
    typeToSearch: string; noMatch: string;
    code: string; nameAr: string; nameEn: string; active: string; notes: string;
    cards: {
      items: string; itemGroups: string; itemCategories: string; units: string;
      conversions: string; warehouses: string; locations: string; reasons: string;
    };
    units: { decimalAllowed: string; decimalAllowedHint: string; itemCount: string };
    groups: { parent: string; parentHint: string; path: string; itemCount: string };
    warehouses: { branch: string; branchHint: string; custodian: string; account: string;
      accountHint: string; hasMovements: string };
    commercial: {
      prices: string; pricesHint: string; priceList: string; price: string; currency: string;
      from: string; to: string; status: string; openEnded: string; active: string; ended: string;
      noPrices: string; addPrice: string;
      suppliers: string; suppliersHint: string; supplier: string; supplierItemCode: string;
      preferred: string; leadTime: string; moq: string; lastPurchasePrice: string;
      noSuppliers: string; addSupplier: string;
      choose: string; remove: string; saveFirst: string; saveFailed: string;
    };

    categories: { level: string; levelHint: string };
    locations: { warehouse: string; warehouseHint: string; parent: string; path: string };
    reasons: { direction: string; scope: string; requiresNotes: string; requiresNotesHint: string;
      requiresApproval: string; account: string;
      directions: Record<"0" | "1" | "2", string>;
      scopes: Record<"1" | "2" | "3" | "9", string> };
    item: {
      barcode: string; barcodeHint: string; group: string; category: string; type: string;
      category1: string; category2: string; category3: string; categoryHint: string;
      previous: string; next: string; backToRegister: string;
      baseUnit: string; baseUnitHint: string; baseUnitLocked: string;
      purchaseUnit: string; salesUnit: string; purchasable: string; sellable: string;
      minimumStock: string; reorderLevel: string; defaultWarehouse: string;
      countryOfOrigin: string; countryOfOriginHint: string;
      purchaseTax: string; purchaseTaxHint: string;
      inventoryAccount: string; cogsAccount: string; adjustmentAccount: string; accountsHint: string;
      conversions: string; conversionsHint: string; addConversion: string; unit: string;
      factor: string; onHand: string; onHandValue: string; averageCost: string; derivedHint: string;
      types: Record<"1" | "2" | "3", string>;
    };
  };
  inventorySettings: {
    title: string; breadcrumb: string; save: string; saved: string; saveError: string;
    loadError: string; readOnly: string; neverSaved: string; notSet: string;
    readiness: string; canPost: string; cannotPost: string; blocksPosting: string;
    groups: { general: string; costing: string; count: string; purchase: string; issue: string;
      warehouse: string; transfer: string; numbering: string };
    allowNegativeStock: string; allowNegativeStockHint: string;
    allowFractionalQuantity: string; allowFractionalQuantityHint: string;
    quantityPrecision: string; quantityPrecisionHint: string;
    costPrecision: string; costPrecisionHint: string;
    blockInactiveItems: string; blockInactiveWarehouses: string; defaultWarehouse: string;
    costingMethod: string; movingAverage: string; costingHint: string;
    allowBackdated: string; allowBackdatedHint: string;
    allowBackdatedAfter: string; allowBackdatedAfterHint: string;
    freezeDuringCount: string; freezeDuringCountHint: string;
    showCostDuringCount: string; showCostDuringCountHint: string;
    createAdjustmentFromCount: string; createAdjustmentFromCountHint: string;
    requireSupplierInvoiceNumber: string; requireSupplierInvoiceDate: string;
    preventDuplicateSupplierInvoice: string; preventDuplicateHint: string;
    requirePurchaseOrder: string; allowPurchaseWithoutOrder: string; exclusiveHint: string;
    allowQuantityAboveOrder: string; quantityTolerance: string; quantityToleranceHint: string;
    defaultPurchaseWarehouse: string;
    requireStorageLocation: string; requireStorageLocationHint: string; warehouseRules: string;
    requireCostCenterOnIssue: string; requireCostCenterHint: string;
    requireIssueReason: string; allowDirectIssue: string;
    requireTransferApproval: string; requireTransferApprovalHint: string;
    requireTransferReceipt: string; transferReceiptUnsupported: string; transferNoJournal: string;
    numberingHint: string; documentType: string; prefix: string;
    sequences: ReadonlyArray<{ code: string; name: string }>;
  };
  bankStatement: {
    section: string; attach: string; reread: string; noFile: string; fileName: string;
    uploadedAt: string; uploadedBy: string; rows: string; parseStatus: string;
    parseStatuses: Record<"1" | "2" | "3" | "4", string>;
    mapping: string; mappingHint: string;
    columns: Record<"0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9", string>;
    comparison: string; runMatch: string; amountTolerance: string; dateTolerance: string;
    toleranceWarning: string; imported: string; manuallyMatched: string; unmatched: string;
    ledgerRows: string; statementRows: string; ledgerTotal: string; statementTotal: string;
    ledgerBalance: string; statementBalance: string; difference: string; counts: string;
    status: string; bankDate: string; erpDate: string; reference: string; cheque: string;
    statementAmount: string; erpAmount: string; gap: string; description: string; action: string;
    matchManually: string; choosing: string; pairWith: string; unmatch: string;
    pairingHint: string; manualTag: string; noAutoFix: string;
    matchTypes: Record<"0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8", string>;
  };
  chequeBatch: {
    documentSection: string; searchSection: string; eligibleSection: string; resultSection: string;
    date: string; reference: string; referenceHint: string; notes: string; choose: string;
    chequeNumberFrom: string; chequeNumberTo: string; chequeDateFrom: string; chequeDateTo: string;
    dueFrom: string; dueTo: string; amountFrom: string; amountTo: string; party: string;
    partyHint: string; chequeDate: string; drawerBank: string;
    searchCheques: string; clearFilters: string; documentInquiry: string; pressSearch: string;
    noEligible: string; noDocuments: string; select: string; selectAll: string;
    selectedCount: string; selectedTotal: string; selectAtLeastOne: string;
    referenceRequired: string; saved: string; chequeCount: string; journalVoucher: string;
    savedSlip: string; statusThen: string; statusNow: string; total: string; tools: string;
    readOnly: string;
    reason: string; reasonRequired: string; deliveredTo: string; deliveredToHint: string;
    newDueDate: string; newDueDateHint: string; newDueDateRequired: string; processing: string;
    represent: string; handBack: string; legal: string; redeliver: string; voidCheque: string;
    operationalNote: string; postedNothing: string;
    operation: string;
    operations: Record<
      "depositForCollection" | "depositImmediately" | "collect" | "withdraw" | "returnUnpaid"
      | "handBack" | "processReturn" | "deliver" | "settle" | "returnIssued"
      | "processIssuedReturn" | "cancel" | "changeDueDate",
      string
    >;
  };
  ownerNote: { label: string; dismiss: string; dismissHint: string };
  journalEntryDialog: {
    title: string; close: string; noVoucher: string; voucherNumber: string; date: string;
    account: string; party: string; costCenter: string; debit: string; credit: string;
    total: string; balanced: string; notBalanced: string; operationalOnly: string;
    viewJournal: string;
  };
  chequeDesk: {
    chequeNumber: string;
    party: string;
    drawer: string;
    beneficiary: string;
    bank: string;
    bankAccount: string;
    drawnOnAccount: string;
    cashbox: string;
    chequeDate: string;
    dueDate: string;
    amount: string;
    currency: string;
    status: string;
    term: string;
    direction: string;
    daysOverdue: string;
    notDueYet: string;
    notes: string;
    reference: string;
    deliveredTo: string;
    deliveredDate: string;
    returnReason: string;
    cancellationReason: string;
    settlementVoucher: string;
    search: string;
    empty: string;
    loading: string;
    totals: string;
    totalsHint: string;
    count: string;
    actions: string;
    history: string;
    historyTitle: string;
    noHistory: string;
    movementDate: string;
    movement: string;
    journal: string;
    noJournal: string;
    operationalOnly: string;
    date: string;
    reason: string;
    newDueDate: string;
    confirm: string;
    cancelButton: string;
    submitting: string;
    done: string;
    required: string;
    chooseBankAccount: string;
    readOnlyNotice: string;
    noAction: string;
    refresh: string;
    filters: string;
    allCurrencies: string;
    dueFrom: string;
    dueTo: string;
    statusUnderCollection: string;
    statusDeposited: string;
    statusCleared: string;
    statusBounced: string;
    statusReturned: string;
    statusIssued: string;
    statusDelivered: string;
    statusPaid: string;
    statusReturnedUnpaid: string;
    statusCancelled: string;
    termImmediate: string;
    termPostDated: string;
    directionIncoming: string;
    directionOutgoing: string;
    mvReceived: string;
    mvDepositedForCollection: string;
    mvCollected: string;
    mvWithdrawnFromCollection: string;
    mvReturnedByBank: string;
    mvReturnedChequeProcessed: string;
    mvDepositedImmediately: string;
    mvIssued: string;
    mvDelivered: string;
    mvPaid: string;
    mvReturnedToUs: string;
    mvReturnedPaidChequeProcessed: string;
    mvCancelled: string;
    mvDueDateChanged: string;
    mvWithdrawnFromChequeBox: string;
    mvHandedBackToDrawer: string;
    actDepositForCollection: string;
    actDepositImmediately: string;
    actCollect: string;
    actWithdraw: string;
    actReturnUnpaid: string;
    actHandBack: string;
    actProcessReturn: string;
    actIssue: string;
    actDeliver: string;
    actSettle: string;
    actReturnIssued: string;
    actProcessIssuedReturn: string;
    actCancel: string;
    actChangeDueDate: string;
    procRepresent: string;
    procRepresentHint: string;
    procLegal: string;
    procLegalHint: string;
    procHandBack: string;
    procHandBackHint: string;
    procRedeliver: string;
    procRedeliverHint: string;
    procVoid: string;
    procVoidHint: string;
    screens: {
      immediateChequeDeposit: { title: string; breadcrumb: string; description: string };
      chequeDepositCollection: { title: string; breadcrumb: string; description: string };
      collectChequeCollection: { title: string; breadcrumb: string; description: string };
      withdrawChequesCollection: { title: string; breadcrumb: string; description: string };
      returnedCheques: { title: string; breadcrumb: string; description: string };
      processReturnedCheques: { title: string; breadcrumb: string; description: string };
      immediateChequePayment: { title: string; breadcrumb: string; description: string };
      collectPostDatedCheques: { title: string; breadcrumb: string; description: string };
      deliverPostDatedCheque: { title: string; breadcrumb: string; description: string };
      deliverImmediatePostDated: { title: string; breadcrumb: string; description: string };
      returnImmediatePostDated: { title: string; breadcrumb: string; description: string };
      processReturnedPaidCheques: { title: string; breadcrumb: string; description: string };
      changeChequeDueDate: { title: string; breadcrumb: string; description: string };
      cancelCheque: { title: string; breadcrumb: string; description: string };
      chequeBooks: { title: string; breadcrumb: string; description: string };
      dueChequesReminder: { title: string; breadcrumb: string; description: string };
    };
  };
  bankOps: {
    voucherNumber: string;
    documentDate: string;
    valueDate: string;
    branch: string;
    description: string;
    bank: string;
    bankAccount: string;
    counterAccount: string;
    counterAccountHint: string;
    costCenter: string;
    facility: string;
    currency: string;
    amount: string;
    exchangeRate: string;
    localAmount: string;
    bankReference: string;
    notes: string;
    kind: string;
    direction: string;
    directionIncrease: string;
    directionDecrease: string;
    directionHint: string;
    kindCharges: string;
    kindInterestEarned: string;
    kindInterestCharged: string;
    kindOtherCredit: string;
    kindOtherDebit: string;
    status: string;
    statusDraft: string;
    statusPosted: string;
    statusReversed: string;
    totalDebit: string;
    totalCredit: string;
    balanced: string;
    save: string;
    post: string;
    reverse: string;
    reverseHint: string;
    reversalDate: string;
    reason: string;
    delete: string;
    deleteConfirm: string;
    newRecord: string;
    search: string;
    empty: string;
    loading: string;
    saving: string;
    saved: string;
    posted: string;
    readOnlyNotice: string;
    required: string;
    cancel: string;
    actions: string;
    refresh: string;
    fromAccount: string;
    toAccount: string;
    sentAmount: string;
    receivedAmount: string;
    fee: string;
    feeAccount: string;
    feeBorneBySource: string;
    transferHint: string;
    fxHint: string;
    reconciliationNumber: string;
    period: string;
    fromDate: string;
    toDate: string;
    statementDate: string;
    statementBalance: string;
    ledgerBalance: string;
    reconciledBalance: string;
    difference: string;
    differenceHint: string;
    ledgerLines: string;
    statementLines: string;
    ledgerLinesHint: string;
    matched: string;
    unmatched: string;
    outstandingDeposits: string;
    outstandingPayments: string;
    unrecorded: string;
    unrecordedHint: string;
    addStatementLine: string;
    chequeNumber: string;
    complete: string;
    completeHint: string;
    completed: string;
    nonAccounting: string;
    creditNumber: string;
    beneficiary: string;
    creditAmount: string;
    margin: string;
    marginAccount: string;
    marginPaidFrom: string;
    requestDate: string;
    openedDate: string;
    expiryDate: string;
    latestShipmentDate: string;
    goodsDescription: string;
    daysToExpiry: string;
    lcStatusRequested: string;
    lcStatusOpened: string;
    lcStatusSettled: string;
    lcStatusCancelled: string;
    openCredit: string;
    settleCredit: string;
    cancelCredit: string;
    lcAccountingHint: string;
    loanNumber: string;
    principal: string;
    interestRate: string;
    interestAccount: string;
    loanAccount: string;
    repaymentType: string;
    numberOfPayments: string;
    yearDays: string;
    drawdownDate: string;
    firstInstallmentDate: string;
    schedule: string;
    installment: string;
    principalPart: string;
    interestPart: string;
    installmentTotal: string;
    paid: string;
    unpaid: string;
    paidDate: string;
    paidFrom: string;
    outstandingPrincipal: string;
    scheduledInterest: string;
    drawdown: string;
    drawdownHint: string;
    repay: string;
    repayHint: string;
    loanStatusDraft: string;
    loanStatusActive: string;
    loanStatusSettled: string;
    repayEqual: string;
    repayDecreasing: string;
    repayLumpSum: string;
    repayInterestOnly: string;
    scheduleHint: string;
    screens: {
      bankTransactions: { title: string; breadcrumb: string; description: string };
      interbankTransfer: { title: string; breadcrumb: string; description: string };
      calculateBankInterest: { title: string; breadcrumb: string; description: string };
      bankReconciliation: { title: string; breadcrumb: string; description: string };
      lettersOfCredit: { title: string; breadcrumb: string; description: string };
      letterOfCreditRequest: { title: string; breadcrumb: string; description: string };
      bankLoans: { title: string; breadcrumb: string; description: string };
      repayLoanInstallments: { title: string; breadcrumb: string; description: string };
    };
  };
  chequeBooksScreen: {
    title: string;
    breadcrumb: string;
    description: string;
    numberingHint: string;
    bankAccount: string;
    bookNumber: string;
    fromChequeNumber: string;
    toChequeNumber: string;
    rangeHint: string;
    leafCount: string;
    receivedDate: string;
    cashbox: string;
    isActive: string;
    notes: string;
    leaves: string;
    leafNumber: string;
    leafStatus: string;
    leafUnused: string;
    leafWithdrawn: string;
    leafUsed: string;
    leafVoid: string;
    withdrawnBy: string;
    withdrawnDate: string;
    withdraw: string;
    withdrawHint: string;
    counts: string;
    newBook: string;
    save: string;
    cancel: string;
    search: string;
    empty: string;
    noLeaves: string;
    loading: string;
    saving: string;
    saved: string;
    withdrawn: string;
    required: string;
    readOnlyNotice: string;
    actions: string;
    refresh: string;
    rangeLocked: string;
  };
  issueCheque: {
    bankAccount: string;
    beneficiary: string;
    chequeNumber: string;
    chequeNumberHint: string;
    fromChequeBook: string;
    chooseLeaf: string;
    leafHint: string;
    manualNumber: string;
    chequeDate: string;
    dueDate: string;
    amount: string;
    currency: string;
    notes: string;
    term: string;
    issue: string;
    issuing: string;
    issued: string;
    recent: string;
    empty: string;
    loading: string;
    required: string;
    readOnlyNotice: string;
    clear: string;
    cancel: string;
    chooseAccount: string;
    chooseBeneficiary: string;
    chooseCurrency: string;
    immediateAccounting: string;
    deferredAccounting: string;
    accountingTitle: string;
  };
  cashBanksInquiries: {
    readOnly: string;
    date: string;
    dateFrom: string;
    dateTo: string;
    asOfDate: string;
    asOfHint: string;
    branch: string;
    currency: string;
    costCenter: string;
    cashbox: string;
    bank: string;
    bankAccount: string;
    party: string;
    search: string;
    all: string;
    unassigned: string;
    voucher: string;
    account: string;
    description: string;
    reference: string;
    status: string;
    debit: string;
    credit: string;
    amount: string;
    localAmount: string;
    exchangeRate: string;
    runningBalance: string;
    openingBalance: string;
    closingBalance: string;
    balance: string;
    totalDebit: string;
    totalCredit: string;
    count: string;
    noRows: string;
    perCurrency: string;
    movementType: string;
    direction: string;
    counterparty: string;
    cashIn: string;
    cashOut: string;
    netMovement: string;
    mtDeposit: string;
    mtZeroing: string;
    mtFunding: string;
    mtCount: string;
    mtRepresentative: string;
    dirFrom: string;
    dirTo: string;
    code: string;
    inClosingRoutine: string;
    zeroings: string;
    lastZeroing: string;
    lastCount: string;
    closingOnly: string;
    outstandingOnly: string;
    zeroedBoxes: string;
    outstandingBoxes: string;
    totalOutstanding: string;
    chequeNumber: string;
    chequeDate: string;
    dueDate: string;
    dueFrom: string;
    dueTo: string;
    term: string;
    daysOverdue: string;
    daysUnderCollection: string;
    depositedDate: string;
    deliveredDate: string;
    deliveredTo: string;
    returnedDate: string;
    returnReason: string;
    clearedTo: string;
    sourceDocument: string;
    settlementVoucher: string;
    movements: string;
    chequeTotal: string;
    movement: string;
    fromStatus: string;
    toStatus: string;
    journal: string;
    noJournal: string;
    recordedBy: string;
    recordedAt: string;
    postedOnly: string;
    postedMovements: string;
    operationalMovements: string;
    finding: string;
    book: string;
    fromNumber: string;
    toNumber: string;
    leaves: string;
    leafStatus: string;
    skippedLeaf: string;
    unregisteredRange: string;
    gapsHint: string;
    leavesAffected: string;
    documentKind: string;
    from: string;
    to: string;
    dkCashDeposit: string;
    dkCashWithdrawal: string;
    dkCashboxTransaction: string;
    dkBankTransaction: string;
    dkBankTransfer: string;
    posted: string;
    unposted: string;
    documents: string;
    creditNumber: string;
    beneficiary: string;
    facility: string;
    facilityCeiling: string;
    facilityCommitted: string;
    facilityRemaining: string;
    creditAmount: string;
    margin: string;
    openedDate: string;
    expiryDate: string;
    daysToExpiry: string;
    openOnly: string;
    expiredOnly: string;
    openCredits: string;
    expiredCredits: string;
    totalMargin: string;
    loanNumber: string;
    principal: string;
    interestRate: string;
    installments: string;
    paidInstallments: string;
    remainingInstallments: string;
    overdueInstallments: string;
    paidPrincipal: string;
    outstandingPrincipal: string;
    scheduledInterest: string;
    nextDue: string;
    overdueOnly: string;
    activeLoans: string;
    overdueLoans: string;
    screens: {
      cashboxClosing: { title: string; breadcrumb: string; description: string };
      bankAccountCurrency: { title: string; breadcrumb: string; description: string };
      bankAccount: { title: string; breadcrumb: string; description: string };
      accountMovements: { title: string; breadcrumb: string; description: string };
      dailyTransactions: { title: string; breadcrumb: string; description: string };
      chequeMovements: { title: string; breadcrumb: string; description: string };
      paidCheques: { title: string; breadcrumb: string; description: string };
      receivedCheques: { title: string; breadcrumb: string; description: string };
      lettersOfCredit: { title: string; breadcrumb: string; description: string };
      chequeDelivery: { title: string; breadcrumb: string; description: string };
      undeliveredCheques: { title: string; breadcrumb: string; description: string };
      cashboxMovements: { title: string; breadcrumb: string; description: string };
      cashboxAccount: { title: string; breadcrumb: string; description: string };
      cashboxCount: { title: string; breadcrumb: string; description: string };
      cashboxClosingFollowUp: { title: string; breadcrumb: string; description: string };
      missingChequeSequences: { title: string; breadcrumb: string; description: string };
      bankLoans: { title: string; breadcrumb: string; description: string };
    };
  };
  cashDeposit: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionSources: string; sequence: string; assignedOnSave: string;
    documentDate: string; bank: string; bankBranch: string; bankAccount: string;
    bankAccountHint: string; currency: string; branch: string; fiscalYear: string;
    fromDocumentDate: string; amount: string; exchangeRate: string; baseRateHint: string;
    localAmount: string; description: string; costCenter: string; noCostCenters: string;
    cashbox: string; cashboxHint: string; creditSide: string;
    accountNumber: string; reachingBank: string; reachingBankHint: string;
    panels: { costCenters: string; documents: string; attachments: string; expenses: string };
    documentNumber: string; documentsHint: string; noDocuments: string;
    expenseAccount: string; expensesHint: string; noExpenses: string; saveBeforeAttaching: string;
    tools: string; insertAllCostCenters: string; insertCostCentersByGroup: string;
    deleteRecord: string; deleteConfirm: string; deleted: string; postedNotDeletable: string;
    linesTotal: string; unbalancedHint: string; actions: string; addRow: string; remove: string;
    branchOptional: string; choose: string; newDeposit: string;
    inquiry: string; save: string; saveAndNew: string; cancel: string; post: string; print: string;
    attachments: string; auditTrail: string; searchPlaceholder: string; empty: string;
    status: string; statusDraft: string; statusPosted: string; alreadyPosted: string;
    saved: string; postedNotice: string; loadError: string; saveError: string; printError: string;
  };
  cashWithdrawal: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionSources: string; sequence: string; assignedOnSave: string;
    documentDate: string; bank: string; bankBranch: string; bankAccount: string;
    bankAccountHint: string; currency: string; branch: string; fiscalYear: string;
    fromDocumentDate: string; amount: string; exchangeRate: string; baseRateHint: string;
    localAmount: string; description: string; costCenter: string; noCostCenters: string;
    cashbox: string; cashboxHint: string; creditSide: string;
    accountNumber: string;
    panels: { costCenters: string; attachments: string };
    saveBeforeAttaching: string;
    tools: string; insertAllCostCenters: string; insertCostCentersByGroup: string;
    deleteRecord: string; deleteConfirm: string; deleted: string; postedNotDeletable: string;
    linesTotal: string; unbalancedHint: string; actions: string; addRow: string; remove: string;
    branchOptional: string; choose: string; newDeposit: string;
    inquiry: string; save: string; saveAndNew: string; cancel: string; post: string; print: string;
    attachments: string; auditTrail: string; searchPlaceholder: string; empty: string;
    status: string; statusDraft: string; statusPosted: string; alreadyPosted: string;
    saved: string; postedNotice: string; loadError: string; saveError: string; printError: string;
  };
  cashboxTransactions: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionSources: string; sequence: string; assignedOnSave: string;
    documentDate: string; bank: string; bankBranch: string; bankAccount: string;
    bankAccountHint: string; currency: string; branch: string; fiscalYear: string;
    fromDocumentDate: string; amount: string; exchangeRate: string; baseRateHint: string;
    localAmount: string; description: string; costCenter: string; noCostCenters: string;
    cashbox: string; cashboxHint: string; creditSide: string;
    accountNumber: string;
    panels: { costCenters: string; attachments: string };
    saveBeforeAttaching: string;
    movementTime: string; direction: string; directionFrom: string; directionTo: string;
    partyType: string; partyCashbox: string; partyBank: string; movementType: string;
    typeDeposit: string; typeZeroing: string; typeFunding: string; typeCount: string;
    typeRepresentative: string; counterCashbox: string; counterAccount: string;
    counterAccountHint: string;
    tools: string; insertAllCostCenters: string; insertCostCentersByGroup: string;
    deleteRecord: string; deleteConfirm: string; deleted: string; postedNotDeletable: string;
    linesTotal: string; unbalancedHint: string; actions: string; addRow: string; remove: string;
    branchOptional: string; choose: string; newDeposit: string;
    inquiry: string; save: string; saveAndNew: string; cancel: string; post: string; print: string;
    attachments: string; auditTrail: string; searchPlaceholder: string; empty: string;
    status: string; statusDraft: string; statusPosted: string; alreadyPosted: string;
    saved: string; postedNotice: string; loadError: string; saveError: string; printError: string;
  };
  bankFacilities: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionFacility: string; sectionLoanAccount: string;
    sectionCurrentAccount: string; sectionExpenses: string; list: string;
    code: string; facilityType: string; currency: string; ceiling: string; interestRate: string;
    loanType: string; graceDays: string; yearDays: string; numberOfPayments: string;
    restrictionBasis: string; active: string; inactive: string; ceilingExceeded: string;
    externalParty: string; bank: string; branch: string; account: string; branchOptional: string;
    fromBankAccounts: string; noBankAccountsHint: string;
    choose: string; expenseAccount: string; expenseAccountHint: string; percentage: string;
    percentageHint: string; amount: string; computed: string; paymentMethod: string;
    methodOptional: string; actions: string; addRow: string; edit: string; remove: string;
    newFacility: string; save: string; cancel: string; activate: string; deactivate: string;
    search: string; searchPlaceholder: string; refresh: string; showInactive: string;
    empty: string; noExpenses: string; saveFirstHint: string;
    typeLoan: string; typeOverdraft: string; typeLetterOfCredit: string;
    typeLetterOfGuarantee: string; typeChequesForCollection: string; typeBillDiscounting: string;
    loanEqual: string; loanDecreasing: string; loanLumpSum: string; loanInterestFirst: string;
    basisOutstanding: string; basisCeiling: string; basisUtilised: string; basisDaily: string;
    saved: string; activated: string; deactivated: string; expenseSaved: string;
    expenseRemoved: string; expenseDeleteConfirm: string; loadError: string; saveError: string;
  };
  banks: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; templateNotice: string; sectionBank: string; sectionBranches: string;
    list: string; code: string; descriptionAr: string; descriptionEn: string; swiftCode: string;
    bankCode: string; templateCode: string; isDefault: string; active: string; inactive: string;
    branchCode: string; phone: string; fax: string; mobile: string; address: string; email: string;
    postalAddress: string; actions: string; branches: string; templates: string;
    sectionAccounts: string; noAccounts: string; currency: string; glAccount: string;
    accountNumber: string; iban: string; isMain: string; branch: string; branchOptional: string;
    choose: string; accountSaved: string; accountRemoved: string; accountRemoveHint: string;
    accountDeleteConfirm: string;
    newBank: string; inquiry: string; save: string; saveAndNew: string; cancel: string; edit: string;
    activate: string; deactivate: string; deleteBank: string; addRow: string; remove: string;
    search: string; searchPlaceholder: string; refresh: string; showInactive: string;
    empty: string; noBranches: string; noSelection: string; noSelectionHint: string;
    created: string; saved: string; deleted: string; activated: string; deactivated: string;
    branchSaved: string; branchRemoved: string; branchRemoveHint: string;
    loadError: string; saveError: string; deleteConfirm: string; branchDeleteConfirm: string;
    auditHistory: string; close: string; required: string;
  };
  cashboxes: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionDefinition: string; sectionAccounts: string; tree: string;
    parent: string; noParent: string; code: string; descriptionAr: string; descriptionEn: string;
    openingCashbox: string; closingCashbox: string; cashboxGroup: string; groupPending: string;
    cashAccount: string; chequesAccount: string; differencesAccount: string; exchangeAccount: string;
    returnedLegalAccount: string; returnedCustomersAccount: string;
    unusedNoOperation: string; unusedHandBackPostsNothing: string;
    creationDate: string; closingDate: string; active: string; inactive: string; closed: string;
    closedByParent: string; inactiveByParent: string;
    newCashbox: string; newChild: string; save: string; cancel: string; edit: string;
    activate: string; deactivate: string; deleteCashbox: string; search: string;
    searchPlaceholder: string; refresh: string; showInactive: string; pickAccount: string;
    clear: string; empty: string; noSelection: string; noSelectionHint: string; required: string;
    created: string; saved: string; deleted: string; activated: string; deactivated: string;
    loadError: string; saveError: string; deleteConfirm: string; close: string;
    auditHistory: string; historyEmpty: string; openState: string;
  };
  costCenterGroups: {
    title: string; pageDescription: string; breadcrumb: string; readOnlyNotice: string;
    purposeNotice: string; sectionGroup: string; sectionCostCenters: string;
    code: string; descriptionAr: string; descriptionEn: string; active: string; inactive: string;
    costCenter: string; costCenterCode: string; actions: string; members: string;
    parent: string; path: string; level: string; notes: string; noParent: string;
    newGroup: string; save: string; cancel: string; edit: string; remove: string; deleteGroup: string;
    activate: string; deactivate: string; addCostCenter: string; addSelected: string;
    search: string; searchPlaceholder: string; refresh: string; query: string; back: string;
    pickerTitle: string; pickerEmpty: string; alreadyInGroup: string; treeGroupHint: string;
    empty: string; noMembers: string; required: string; saveFirst: string;
    created: string; saved: string; deleted: string; activated: string; deactivated: string;
    memberAdded: string; memberRemoved: string; removeHint: string;
    loadError: string; saveError: string; deleteConfirm: string; deleteTitle: string; close: string;
    print: string; exportPdf: string; exportExcel: string; comingSoonHint: string;
    exporting: string;
  };
  openingBalances: {
    title: string; pageDescription: string; readOnlyNotice: string; breadcrumb: string;
    phaseNotice: string; accountNumber: string; accountName: string; debit: string; credit: string;
    fiscalYear: string; currency: string; exchangeRate: string; localDebit: string;
    localCredit: string; notes: string; status: string; actions: string; branch: string;
    allBranches: string; accountType: string; financialStatement: string; showZeroBalances: string;
    showInactiveAccounts: string; search: string; searchPlaceholder: string; refresh: string;
    importComingSoon: string; print: string; exportPdf: string; exportExcel: string;
    totalDebit: string; totalCredit: string; totalLocalDebit: string; totalLocalCredit: string;
    difference: string; balanced: string; unbalanced: string; batchStatus: string;
    stNotEntered: string; stZero: string; stEntered: string; stInvalid: string; stLocked: string;
    bsDraft: string; bsBalanced: string; bsUnbalanced: string; bsLocked: string; bsPosted: string;
    rateRequired: string; yearClosed: string; empty: string; loadError: string; edit: string;
    comingSoonHint: string; baseCurrency: string;
    saveSelected: string; saveAll: string; cancelChanges: string; resetRow: string;
    selectRow: string; selectAll: string; changedCount: string; selectedCount: string;
    unsavedBanner: string; discardConfirm: string; noChanges: string; nothingSelected: string;
    savedCreated: string; savedUpdated: string; saveError: string; fixErrorsFirst: string;
    errBothSides: string; errNegative: string; errBaseRate: string; errRatePositive: string;
    calculated: string; changedRow: string;
    printPreview: string; exporting: string; exportError: string; saveBeforeExport: string;
  };
  voucherTypes: {
    title: string;
    pageDescription: string;
    readOnlyNotice: string;
    breadcrumb: string;
    back: string;
    query: string;
    save: string;
    cancel: string;
    newType: string;
    sectionVoucherType: string;
    code: string;
    descriptionAr: string;
    descriptionEn: string;
    sequenceType: string;
    taxInclusion: string;
    numberingMethod: string;
    reportDestination: string;
    numberOfCopies: string;
    effectType: string;
    active: string;
    inactive: string;
    seqYearly: string;
    seqMonthly: string;
    seqContinuous: string;
    taxIncluded: string;
    taxNotIncluded: string;
    numUnified: string;
    numPerBranch: string;
    numPerCompany: string;
    destScreen: string;
    destPrinter: string;
    destPdf: string;
    effectAutomatic: string;
    effectManual: string;
    effectApproval: string;
    receivablesBreadcrumb: string;
    cashBanksBreadcrumb: string;
    searchPlaceholder: string;
    statusAll: string;
    statusActive: string;
    statusInactive: string;
    catalogue: string;
    usage: string;
    usageTitle: string;
    usageCount: string;
    usageNone: string;
    usageRecent: string;
    voucherNumber: string;
    voucherDate: string;
    auditHistory: string;
    auditNone: string;
    auditAction: string;
    auditUser: string;
    auditWhen: string;
    auditFields: string;
    createdBy: string;
    updatedBy: string;
    activatedBy: string;
    deactivatedBy: string;
    print: string;
    exportPdf: string;
    exportExcel: string;
    activate: string;
    deactivate: string;
    systemType: string;
    systemTypeLocked: string;
    close: string;
    required: string;
    copiesPositive: string;
    created: string;
    saved: string;
    activated: string;
    deactivated: string;
    loadError: string;
    saveError: string;
    empty: string;
    inUse: string;
  };
  financialStatements: {
    title: string;
    pageDescription: string;
    readOnlyNotice: string;
    breadcrumb: string;
    back: string;
    query: string;
    save: string;
    cancel: string;
    deleteAll: string;
    sectionStatement: string;
    sectionSettings: string;
    code: string;
    descriptionAr: string;
    descriptionEn: string;
    statementType: string;
    statementActive: string;
    required: string;
    newStatement: string;
    searchTitle: string;
    searchPlaceholder: string;
    searchEmpty: string;
    searchClose: string;
    noStatements: string;
    loadError: string;
    saveError: string;
    saved: string;
    created: string;
    deleted: string;
    deleteTitle: string;
    deleteConfirm: string;
    deleteCancel: string;
    unsavedTitle: string;
    structurePending: string;
    inactive: string;
      lines: string; addLine: string; addChild: string; editLine: string; deleteLine: string;
    moveUp: string; moveDown: string; expandAll: string; collapseAll: string; lineSearch: string;
    refreshLines: string; parent: string; noParent: string; lineCode: string; lineTypeLabel: string;
    account: string; costCenter: string; disclosure: string; effectNature: string;
    balanceMethod: string; displaySequence: string; bold: string; printed: string;
    ltHeader: string; ltAccount: string; ltGroup: string; ltTotal: string; ltCalculated: string;
    ltSpacer: string; ltNote: string; ltCostCenter: string;
    enAdd: string; enSubtract: string; enNoEffect: string;
    bmAccountBalance: string; bmSumOfChildren: string; bmDebitMinusCredit: string;
    bmCreditMinusDebit: string; bmNoBalance: string;
    noLines: string; lineSaved: string; lineDeleted: string; lineMoved: string;
    deleteSubtreeTitle: string; deleteSubtreeConfirm: string; selectStatementFirst: string;
  };
  financialLinks: {
    title: string;
    pageDescription: string;
    readOnlyNotice: string;
    sectionAccounts: string;
    sectionAssignments: string;
    linkType: string;
    linkLevel: string;
    entity: string;
    account: string;
    accountNumber: string;
    active: string;
    selectEntity: string;
    notLinked: string;
    noRows: string;
    noAssignments: string;
    search: string;
    searchPlaceholder: string;
    edit: string;
    clearLink: string;
    refresh: string;
    /** Templated with {linked} and {total}. */
    linkedOf: string;
    loadError: string;
    saveError: string;
    soon: string;
    levelUnavailable: string;
    settingsContext: string;
    openSettings: string;
    /** Keyed by the numeric FinancialLinkLevel value. */
    levels: Record<"1" | "2" | "3" | "4" | "5" | "6" | "7", string>;
  };
  financialPosition: {
    title: string;
    subtitle: string;
    prompt: string;

    asOfDate: string;
    fiscalYear: string;
    fiscalYearHint: string;
    fiscalYearAuto: string;
    branch: string;
    defaultBranch: string;

    levelFrom: string;
    levelTo: string;
    levelRangeInvalid: string;
    parentAccount: string;
    accountFrom: string;
    accountTo: string;
    accountType: string;
    allAccountTypes: string;
    accountCategory: string;
    costCenter: string;
    costCenterHint: string;
    currency: string;
    allCurrencies: string;
    search: string;
    searchPlaceholder: string;

    showChildren: string;
    showChildrenHint: string;
    includeZeroBalances: string;
    includeZeroBalancesHint: string;
    includeClosedAccounts: string;
    includeClosedAccountsHint: string;
    includeInactiveAccounts: string;
    includeUnpostedMovements: string;
    includeUnpostedMovementsHint: string;
    consolidateBranches: string;
    consolidateBranchesHint: string;
    showPercentageOfTotal: string;

    comparison: string;
    comparisonNone: string;
    comparisonPreviousPeriod: string;
    comparisonPreviousYear: string;
    comparisonCustom: string;
    comparisonDerivedHint: string;
    comparativeDate: string;
    comparativeBalance: string;
    changeAmount: string;
    changePercentage: string;

    accountNumber: string;
    accountName: string;
    debit: string;
    credit: string;
    balance: string;
    balanceNature: string;
    accountLevel: string;
    percentageOfTotalAssets: string;
    isGroupAccount: string;
    isPostingAccount: string;
    status: string;
    closed: string;

    natureDebit: string;
    natureCredit: string;
    natureZero: string;
    natureAll: string;
    natureNormalOnly: string;
    natureAbnormalOnly: string;
    abnormal: string;
    /** Templated with {expected} and {actual}. */
    abnormalHint: string;
    systemCalculated: string;
    systemCalculatedHint: string;

    assets: string;
    liabilities: string;
    equity: string;

    expand: string;
    collapse: string;
    expandAll: string;
    collapseAll: string;
    openMovements: string;

    equationTitle: string;
    balanced: string;
    unbalanced: string;
    unbalancedWarning: string;
    difference: string;
    totalAssets: string;
    totalCurrentAssets: string;
    totalNonCurrentAssets: string;
    totalLiabilities: string;
    totalCurrentLiabilities: string;
    totalNonCurrentLiabilities: string;
    totalEquity: string;
    currentPeriodResult: string;
    totalLiabilitiesAndEquity: string;
    displayedAccounts: string;
    abnormalBalances: string;

    unpostedWarning: string;
    /** Templated with {branches}. */
    consolidatedNotice: string;
  };
  taxTypes: {
    title: string;
    createTitle: string;
    editTitle: string;
    empty: string;
    emptyHint: string;
    code: string;
    name: string;
    nameAr: string;
    nameEn: string;
    category: string;
    allCategories: string;
    effectiveFrom: string;
    effectiveTo: string;
    effectiveToHint: string;
    effectivePeriod: string;
    status: string;
    accounts: string;
    debitAccount: string;
    creditAccount: string;
    descriptionAr: string;
    descriptionEn: string;
    notes: string;
    sortOrder: string;
    isActive: string;
    isActiveHint: string;
    allowManualRateOverride: string;
    allowManualRateOverrideHint: string;
    isRecoverable: string;
    isRecoverableHint: string;
    affectsItemCost: string;
    affectsItemCostHint: string;
    requiresTaxRegistration: string;
    requiresTaxRegistrationHint: string;
    requiresNationalNumber: string;
    requiresNationalNumberHint: string;
    outOfWindow: string;
    outOfWindowHint: string;
    deleteTitle: string;
    deleteHint: string;
    codeRequired: string;
    nameRequired: string;
    nameArRequired: string;
    effectiveFromRequired: string;
    effectiveRangeInvalid: string;
    categories: {
      salesTax: string;
      purchaseTax: string;
      additionalTax: string;
      specialTax: string;
      zeroRated: string;
      exempt: string;
      nonRecoverable: string;
      withholding: string;
      other: string;
    };
  };
  taxRates: {
    title: string;
    createTitle: string;
    createHint: string;
    editTitle: string;
    editHint: string;
    empty: string;
    emptyHint: string;

    taxType: string;
    taxTypeRequired: string;
    selectTaxType: string;
    searchTaxTypes: string;
    noTaxTypes: string;
    taxTypeInactive: string;
    taxTypeInactiveHint: string;

    percentage: string;
    percentageHint: string;
    percentageRequired: string;
    percentageNegative: string;
    percentageTooLarge: string;

    effectiveFrom: string;
    effectiveFromHint: string;
    effectiveFromRequired: string;
    effectiveTo: string;
    effectiveToHint: string;
    effectiveRangeInvalid: string;
    effectivePeriod: string;
    openEnded: string;

    rateStatus: string;
    allStatuses: string;
    statusCurrent: string;
    statusFuture: string;
    statusExpired: string;
    statusInactive: string;

    referenceNumber: string;
    referenceNumberHint: string;
    decisionNumber: string;
    legalReference: string;
    legalReferenceHint: string;
    sortOrder: string;
    descriptionAr: string;
    descriptionEn: string;
    notes: string;
    isActive: string;
    isActiveHint: string;

    /** Shown on a rate whose percentage and dates may no longer be changed. */
    lockedInUse: string;
    lockedExpired: string;

    history: string;
    historyTitle: string;
    historyEmpty: string;
    /** Accessible name for the Table / Timeline switch inside the history panel. */
    historyView: string;
    tableView: string;
    timelineView: string;
    createdBy: string;
    closedBy: string;

    close: string;
    closeTitle: string;
    closeHint: string;
    closeDateHint: string;
    closeBeforeStart: string;
    closedOk: string;
    closePredecessorTitle: string;
    closePredecessorConfirm: string;

    deleteTitle: string;
    deleteHint: string;
  };
  customerReceivables: {
    title: string;
    statementTitle: string;
    date: string;
    asOfDate: string;
    currentBalance: string;
    balanceAsOfDate: string;
    asOfHint: string;
    currentHint: string;
    customer: string;
    category: string;
    salesRepresentative: string;
    controlAccount: string;
    city: string;
    country: string;
    creditLimit: string;
    includeZeroBalances: string;
    balance: string;
    balanceNature: string;
    natureDebit: string;
    natureCredit: string;
    natureBalanced: string;
    totalDebit: string;
    totalCredit: string;
    netReceivables: string;
    customerCount: string;
    baseCurrency: string;
    totalsCoverAll: string;
    openStatement: string;
    selectCustomer: string;
    dateFrom: string;
    dateTo: string;
    openingBalance: string;
    closingBalance: string;
    runningBalance: string;
    movementCount: string;
    voucherType: string;
    voucherNumber: string;
    reference: string;
    description: string;
    debit: string;
    credit: string;
    documentCurrency: string;
    noDrillDown: string;
    allCustomers: string;
    all: string;
  };
  customerCostCenterReceivables: {
    title: string;
    costCenter: string;
    costCenterCode: string;
    noCostCenter: string;
    costCenterCount: string;
    includeChildCostCenters: string;
    onlyWithoutCostCenter: string;
    unassigned: string;
    unassignedNote: string;
    ledgerOnlyHint: string;
    currentScope: string;
    asOfScope: string;
  };
  customerCurrencyStatement: {
    title: string;
    currency: string;
    selectCustomerFirst: string;
    selectCurrency: string;
    noLedgerCurrencies: string;
    exchangeRate: string;
    transactionCurrencyNote: string;
    baseCurrencyBadge: string;
    movementsInCurrency: string;
  };
  customerTransactions: {
    title: string;
    voucherDate: string;
    customerNumber: string;
    accountNumber: string;
    accountName: string;
    currency: string;
    rate: string;
    currencyDebit: string;
    currencyCredit: string;
    paymentMethod: string;
    notes: string;
    branch: string;
    year: string;
    netMovement: string;
    mixedCurrencies: string;
    noPaymentMethod: string;
    reversed: string;
    mappingNote: string;
  };
  serviceVouchers: {
    title: string;
    voucherDate: string;
    paymentMethod: string;
    partyCode: string;
    partyName: string;
    subtotal: string;
    discount: string;
    tax: string;
    finalTotal: string;
    currency: string;
    supplierInvoiceNumber: string;
    project: string;
    notes: string;
    branch: string;
    userName: string;
    year: string;
    status: string;
    documents: string;
    documentType: string;
    totalsByCurrency: string;
    returnNote: string;
    noProjectModel: string;
    noPurchaseModule: string;
    openDocument: string;
  };
  customerItems: {
    title: string;
    voucherDate: string;
    customerCode: string;
    customerName: string;
    itemCode: string;
    itemName: string;
    itemType: string;
    uom: string;
    quantity: string;
    unitPrice: string;
    discount: string;
    netUnitPrice: string;
    tax: string;
    lineTotal: string;
    currency: string;
    rate: string;
    branch: string;
    costCenter: string;
    salesRep: string;
    notes: string;
    nature: string;
    sale: string;
    returned: string;
    customers: string;
    documents: string;
    lines: string;
    netSales: string;
    totalsByCurrency: string;
    returnNote: string;
    servicesOnly: string;
    postedOnly: string;
    allCustomers: string;
    openDocument: string;
  };
  paidUnpaid: {
    title: string;
    invoiceDate: string;
    invoiceNumber: string;
    invoiceType: string;
    customerCode: string;
    customerName: string;
    currency: string;
    invoiceTotal: string;
    paid: string;
    remaining: string;
    status: string;
    dueDate: string;
    daysOverdue: string;
    paymentMethod: string;
    settledBy: string;
    salesRep: string;
    costCenter: string;
    branch: string;
    reference: string;
    notes: string;
    statusPaid: string;
    statusPartial: string;
    statusUnpaid: string;
    multiple: string;
    invoices: string;
    overSettled: string;
    overSettledWarning: string;
    totalsByCurrency: string;
    noDueDateNote: string;
    postedOnly: string;
    settlementDetails: string;
    settlementDate: string;
    settlementNumber: string;
    receiptNumber: string;
    amountApplied: string;
    settlementStatus: string;
    createdBy: string;
    active: string;
    cancelled: string;
    cancelledNote: string;
    noSettlements: string;
    close: string;
    allCustomers: string;
    openInvoice: string;
  };
  customerAging: {
    title: string;
    asOfDate: string;
    basis: string;
    basisDue: string;
    basisDocument: string;
    customerCode: string;
    customerName: string;
    salesRep: string;
    accountNumber: string;
    accountName: string;
    notDue: string;
    unclassified: string;
    totalOutstanding: string;
    unappliedCredits: string;
    creditLimit: string;
    maxAge: string;
    customers: string;
    totalOverdue: string;
    overdueOnly: string;
    minimumOutstanding: string;
    country: string;
    city: string;
    agingDetails: string;
    documentDate: string;
    dueDate: string;
    voucherNumber: string;
    documentType: string;
    reference: string;
    originalAmount: string;
    settledAmount: string;
    remainingAmount: string;
    ageDays: string;
    agingPeriod: string;
    currency: string;
    rate: string;
    branch: string;
    fiscalYear: string;
    noOpenItems: string;
    detailsReconcile: string;
    close: string;
    allCustomers: string;
    openItems: string;
    configurePeriods: string;
    noCostCentreFilter: string;
  };
  detailedAging: {
    title: string;
    documentDate: string;
    documentDateFrom: string;
    documentDateTo: string;
    originalBase: string;
    settledBase: string;
    remainingBase: string;
    originalTransaction: string;
    ageDays: string;
    agingPeriod: string;
    dueStatus: string;
    statusNotDue: string;
    statusOverdue: string;
    statusPaid: string;
    unclassified: string;
    openItems: string;
    customers: string;
    totalOverdue: string;
    unappliedCredits: string;
    showFullySettled: string;
    minimumRemaining: string;
    maximumRemaining: string;
    settlementDetails: string;
    settlementDate: string;
    settlementNumber: string;
    receiptNumber: string;
    amountApplied: string;
    paymentMethod: string;
    createdBy: string;
    cancelledLater: string;
    noSettlements: string;
    settlementAsOfNote: string;
    close: string;
    summaryLink: string;
  };
  receiptsReportsExtra: {
    title: Record<string, string>;
    note: Record<string, string>;
    empty: Record<string, string>;
    partyTypeLabel: (value: string) => string;
    natureLabel: (value: string) => string;
    statusLabel: (value: string) => string;
    documentTypeLabel: (value: string) => string;
    recognitionStatus: (value: string) => string;
    auditAction: (value: string) => string;
    dateFrom: string; dateTo: string; asOf: string; openingBefore: string;
    partyType: string; partyCode: string; partyName: string; category: string;
    controlAccount: string; currency: string; lastActivity: string; never: string;
    openingDebit: string; openingCredit: string; periodDebit: string; periodCredit: string;
    closingDebit: string; closingCredit: string; nature: string; net: string;
    parties: string; customers: string; customer: string; supplier: string; bothTypes: string;
    rows: string; date: string; voucherType: string; voucherNumber: string;
    accountNumber: string; accountName: string; description: string;
    debit: string; credit: string; balance: string; runningBalance: string;
    openingBalance: string; closingBalance: string; accountSummary: string;
    costCenter: string; branch: string; customerRequired: string;
    receiptNumber: string; amount: string; paymentMethod: string; destination: string;
    collector: string; salesRep: string; reference: string; status: string; createdBy: string;
    multiple: string; receipts: string; total: string;
    orderNumber: string; requested: string; collected: string; remaining: string;
    expectedDate: string; orders: string; notes: string;
    documentType: string; documentNumber: string; receiptVoucher: string; receiptOrder: string;
    bothDocuments: string; documents: string; collectors: string;
    collectedReceipts: string; orderedAmount: string;
    recognitionDate: string; invoiceNumber: string; contractPeriod: string; periodNumber: string;
    scheduled: string; recognised: string; due: string; notYetDue: string;
    schedules: string; periods: string; allStatuses: string;
    serviceType: string; saleNature: string; subtotal: string; discount: string; tax: string;
    netTotal: string; invoices: string; byTypeSummary: string;
    isParent: string; parent: string; child: string; standalone: string; parents: string;
    ownBalance: string; childrenBalance: string; consolidated: string; children: string;
    parentCustomer: string; selectParent: string; parentRequired: string; role: string;
    movements: string;
    changedAt: string; field: string; oldValue: string; newValue: string; action: string;
    changedBy: string; fieldChanges: string; auditEvents: string;
  };
  customerMasterReports: {
    title: (kind: "customers-exceeded-credit-limit" | "total-customers" | "customer-information" | "customer-blacklist" | "customer-credit-limit") => string;
    note: (kind: "customers-exceeded-credit-limit" | "total-customers" | "customer-information" | "customer-blacklist" | "customer-credit-limit") => string;
    noRows: (kind: "customers-exceeded-credit-limit" | "total-customers" | "customer-information" | "customer-blacklist" | "customer-credit-limit") => string;
    natureLabel: (nature: string) => string;
    code: string; name: string; nameEn: string; category: string; status: string;
    active: string; inactive: string; customer: string; salesRep: string;
    controlAccount: string; creditLimit: string; balance: string; nature: string;
    exceeded: string; utilisation: string; available: string; blacklistReason: string;
    city: string; country: string; taxNumber: string; registration: string;
    phone: string; mobile: string; email: string; address: string; contactPerson: string;
    paymentMethod: string; parentCustomer: string;
    customers: string; totalLimit: string; totalBalance: string; totalExceeded: string;
  };
  agingGrouped: {
    title: (dimension: "Category" | "Currency") => string;
    groupHeader: (dimension: "Category" | "Currency") => string;
    unassigned: (dimension: "Category" | "Currency") => string;
    asOf: string;
    customers: string;
    openItems: string;
    notDue: string;
    unclassified: string;
    total: string;
    grandTotal: string;
    groups: string;
    noRows: string;
    basisNote: string;
  };
  reportsReceipts: {
    currencyLocalTitle: string;
    currencyLocalNote: string;
    transactionBasis: string;
    localBasis: string;
  };
  customerAccountCostCenter: {
    title: string;
    date: string;
    voucherType: string;
    voucherNumber: string;
    reference: string;
    description: string;
    costCenterCode: string;
    costCenterName: string;
    debit: string;
    credit: string;
    runningBalance: string;
    currency: string;
    exchangeRate: string;
    currencyDebit: string;
    currencyCredit: string;
    branch: string;
    customer: string;
    customerCode: string;
    customerName: string;
    customerCategory: string;
    controlAccount: string;
    dateFrom: string;
    dateTo: string;
    openingBalance: string;
    totalDebit: string;
    totalCredit: string;
    closingBalance: string;
    costCenterFilter: string;
    allCostCenters: string;
    noCostCenter: string;
    summary: string;
    movements: string;
    coverage: string;
    noRows: string;
    selectCustomer: string;
    customerRequired: string;
    grainNote: string;
    runningBalanceNote: string;
    openDocument: string;
  };
  voucherTransactionsInquiry: {
    title: string;
    voucherDate: string;
    voucherNumber: string;
    voucherType: string;
    status: string;
    accountingEffect: string;
    partyType: string;
    partyCode: string;
    partyName: string;
    currency: string;
    exchangeRate: string;
    voucherAmount: string;
    voucherAmountLocal: string;
    nature: string;
    paymentMethod: string;
    branch: string;
    costCenter: string;
    reference: string;
    notes: string;
    createdBy: string;
    createdAt: string;
    reversalReference: string;
    counterparty: string;
    customer: string;
    allTypes: string;
    natureDebit: string;
    natureCredit: string;
    natureNeutral: string;
    effectPosted: string;
    effectNotPosted: string;
    effectReversed: string;
    effectNone: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusRejected: string;
    statusCancelled: string;
    statusReversed: string;
    multiple: string;
    voucherCount: string;
    postedCount: string;
    unpostedCount: string;
    reversedCount: string;
    partyCount: string;
    totalDebit: string;
    totalCredit: string;
    totalNeutral: string;
    effectiveDebit: string;
    effectiveCredit: string;
    effectiveNet: string;
    historicalNet: string;
    countsByType: string;
    noRows: string;
    documentGrainNote: string;
    reversalNote: string;
    journalUnavailable: string;
    details: string;
    openDocument: string;
    close: string;
  };
  outstandingInvoices: {
    title: string;
    invoiceDate: string;
    invoiceNumber: string;
    customerCode: string;
    customerName: string;
    customerCategory: string;
    currency: string;
    invoiceTotal: string;
    settledAmount: string;
    remainingAmount: string;
    paymentStatus: string;
    dueDate: string;
    paymentMethod: string;
    salesRepresentative: string;
    branch: string;
    reference: string;
    notes: string;
    asOf: string;
    allOutstanding: string;
    unpaid: string;
    partiallyPaid: string;
    invoiceCount: string;
    unpaidCount: string;
    partiallyCount: string;
    customerCount: string;
    totalInvoice: string;
    totalSettled: string;
    totalRemaining: string;
    totalRemainingLocal: string;
    minRemaining: string;
    maxRemaining: string;
    noRows: string;
    flatListNote: string;
    dueDateNote: string;
    settlementDetails: string;
    openInvoice: string;
  };
  receiptsWithoutInvoices: {
    title: string;
    receiptDate: string;
    receiptNumber: string;
    customerCode: string;
    customerName: string;
    customerCategory: string;
    currency: string;
    receiptAmount: string;
    allocatedAmount: string;
    unallocatedAmount: string;
    matchingStatus: string;
    paymentMethod: string;
    destination: string;
    reference: string;
    branch: string;
    costCenter: string;
    collector: string;
    salesRepresentative: string;
    notes: string;
    createdBy: string;
    createdAt: string;
    unallocated: string;
    partiallyAllocated: string;
    fullyAllocated: string;
    multiple: string;
    showFullyAllocated: string;
    minUnallocated: string;
    maxUnallocated: string;
    receiptCount: string;
    unallocatedCount: string;
    partiallyCount: string;
    fullyCount: string;
    totalReceipt: string;
    totalAllocated: string;
    totalUnallocated: string;
    totalUnallocatedLocal: string;
    overAllocated: string;
    overAllocatedWarning: string;
    noRows: string;
    note: string;
    readOnlyNote: string;
    details: string;
    payments: string;
    allocations: string;
    settlementDate: string;
    settlementNumber: string;
    documentNumber: string;
    documentType: string;
    documentDate: string;
    status: string;
    active: string;
    cancelled: string;
    cancellationReason: string;
    noAllocations: string;
    line: string;
    amount: string;
    chequeNumber: string;
    chequeDueDate: string;
    fromReceiptOrder: string;
    close: string;
  };
  missingVoucherSequences: {
    title: string;
    missingNumber: string;
    sequence: string;
    year: string;
    missingFrom: string;
    missingTo: string;
    missingCount: string;
    gapCount: string;
    firstMissing: string;
    lastMissing: string;
    previousNumber: string;
    nextNumber: string;
    previousDate: string;
    nextDate: string;
    previousStatus: string;
    nextStatus: string;
    gapSize: string;
    scopes: string;
    resetPolicy: string;
    startNumber: string;
    lastIssued: string;
    issuedCount: string;
    duplicates: string;
    duplicateSerial: string;
    duplicateVouchers: string;
    noGaps: string;
    note: string;
    auditOnly: string;
    viewDetails: string;
    groupedGaps: string;
    close: string;
    numberFrom: string;
    numberTo: string;
  };
  costCenterAccount: {
    title: string;
    costCenter: string;
    costCenterCode: string;
    costCenterName: string;
    includeChildCostCenters: string;
    allocationPercentage: string;
    allocatedDebit: string;
    allocatedCredit: string;
    allocatedLocalDebit: string;
    allocatedLocalCredit: string;
    totalAllocatedDebit: string;
    totalAllocatedCredit: string;
    selectPrompt: string;
    rolledUp: string;
    wholeLineNote: string;
  };
  accountMovements: {
    title: string;
    movementDate: string;
    sourceReference: string;
    runningBalance: string;
    balanceType: string;
    actions: string;
    open: string;
    openingBalance: string;
    closingBalance: string;
    includeOpeningBalance: string;
    netMovement: string;
    advancedFilters: string;
    partyType: string;
    party: string;
    customer: string;
    supplier: string;
    employee: string;
    voucherKind: string;
    postingStatusPosted: string;
    postingStatusUnposted: string;
    postingStatusAll: string;
    zero: string;
    runningBalanceUnavailable: string;
    unpostedNote: string;
    balancesNote: string;
    modules: {
      manual: string;
      receipts: string;
      payments: string;
      cashBanks: string;
      payroll: string;
      fixedAssets: string;
      sales: string;
      purchases: string;
      openingBalance: string;
      closing: string;
      system: string;
    };
  };
  /** The unified inquiry standard. Shared by every module's inquiry screen. */
  /**
   * The unified Daily Transaction standard. Every transaction route lands on its register and
   * offers the same five actions in the same order, so the workflow is learned once.
   */
  /**
   * المخزون والمستودعات › الحركات اليومية — the words the eleven documents share.
   *
   * One vocabulary for the whole chain, so «مرحّل» never means one thing on a receipt and
   * another on an invoice.
   */
  /**
   * المخزون والمستودعات › الاستعلامات — the words the twenty-four screens share.
   *
   * One vocabulary for the module, so «المتبقية» never means outstanding-to-receive on
   * one screen and outstanding-to-invoice on the next.
   */
  inventoryInquiries: {
    itemTypes: { "1": string; "2": string; "3": string };
    register: {
      title: string; breadcrumb: string; searchPlaceholder: string; expandAll: string;
      collapseAll: string; columnChooser: string; clear: string; summary: string;
      itemsCount: string; loadError: string; empty: string; notStockNote: string;
      level: string; code: string; name: string; nameAr: string; group: string;
      class1: string; class2: string; class3: string; unit: string; type: string;
      country: string; tax: string; warehouse: string; barcode: string; price: string;
      supplier: string; min: string; reorder: string; conversions: string; movement: string;
      status: string; open: string; active: string; inactive: string; yes: string; no: string;
      neverMoved: string;
    };
    noRows: string;
    noItemCards: string;
    unspecified: string;
    cards: string;
    conversions: string;
    hasMovements: string;
    incomplete: string;
    incompleteYes: string;
    incompleteOnly: string;
    neverMovedOnly: string;
    activeOnly: string;
    inactiveOnly: string;
    withGroup: string;
    withCategory: string;
    withCountry: string;
    withPurchaseTax: string;
    dateFrom: string;
    dateTo: string;
    asOfDate: string;
    item: string;
    itemCode: string;
    itemName: string;
    warehouse: string;
    toWarehouse: string;
    group: string;
    category: string;
    supplier: string;
    costCenter: string;
    branch: string;
    reason: string;
    unit: string;
    search: string;
    documentNumber: string;
    documentType: string;
    document: string;
    date: string;
    status: string;
    direction: string;
    directionIn: string;
    directionOut: string;
    quantity: string;
    quantityIn: string;
    quantityOut: string;
    valueIn: string;
    valueOut: string;
    runningQuantity: string;
    runningValue: string;
    openingQuantity: string;
    closingQuantity: string;
    unitCost: string;
    averageCost: string;
    inventoryValue: string;
    available: string;
    minimumStock: string;
    reorderLevel: string;
    shortage: string;
    lastMovement: string;
    daysSince: string;
    days: string;
    orderNumber: string;
    orderDate: string;
    expectedDate: string;
    ordered: string;
    received: string;
    remaining: string;
    invoiced: string;
    uninvoiced: string;
    uninvoicedValue: string;
    returned: string;
    progress: string;
    lastReceipt: string;
    receiptNumber: string;
    receiptDate: string;
    receiptValue: string;
    comparison: string;
    difference: string;
    differenceValue: string;
    invoiceNumber: string;
    supplierInvoice: string;
    dueDate: string;
    subTotal: string;
    tax: string;
    total: string;
    paid: string;
    outstanding: string;
    paymentStatus: string;
    returnNumber: string;
    returnValue: string;
    taxReversal: string;
    countNumber: string;
    countDate: string;
    systemQuantity: string;
    countedQuantity: string;
    adjustment: string;
    adjustmentStatus: string;
    responsible: string;
    unadjustedOnly: string;
    positiveOnly: string;
    negativeOnly: string;
    includeDraft: string;
    outstandingOnly: string;
    overdueOnly: string;
    uninvoicedOnly: string;
    balanceSign: string;
    positive: string;
    zero: string;
    negative: string;
    all: string;
    openSource: string;
    runningQty: string;
    movements: string;
    summedQuantity: string;
    checkedPairs: string;
    mismatches: string;
    mismatchesOnly: string;
    itemsCount: string;
    minimumAgeDays: string;
    againstMinimum: string;
    screens: {
      itemBalance: string;
      stockByWarehouse: string;
      itemMovements: string;
      itemLedgerCard: string;
      warehouseMovements: string;
      stockAsOf: string;
      belowReorder: string;
      nonMoving: string;
      purchaseOrders: string;
      orderReceipts: string;
      goodsReceipts: string;
      receivedNotInvoiced: string;
      purchaseInvoices: string;
      purchaseReturns: string;
      stockReceipts: string;
      stockIssues: string;
      transfers: string;
      adjustments: string;
      stockCounts: string;
      countDifferences: string;
      itemBySupplier: string;
      itemRegister: string;
      itemsByGroup: string;
      stockLedger: string;
      reconciliation: string;
    };
  };
  /**
   * The Inventory reports screen — only what the 101 filter bars need beyond the inquiry copy.
   *
   * Everything the two share (dates, warehouse, item, group, supplier, quantity, value) is read
   * from `inventoryInquiries`, so a label cannot say one thing on an inquiry and another on the
   * report that answers the same question.
   */
  inventoryReports: {
    customer: string;
    purchaseOrder: string;
    stockCount: string;
    movementType: string;
    adjustmentReason: string;
    storageLocation: string;
    top: string;
    topAll: string;
    marginBelow: string;
    valueAbove: string;
    activeOnly: string;
    costsHidden: string;
  };
  inventoryTransactions: {
    tools: string;
    audit: string;
    post: string;
    reverse: string;
    cancelDocument: string;
    open: string;
    noJournalYet: string;
    noJournalEver: string;
    noRows: string;
    saveFailed: string;
    filterSearch: string;
    filterFrom: string;
    filterTo: string;
    filterApply: string;
    saved: string;
    posted: string;
    audited: string;
    cancelled: string;
    reversed: string;
    addLine: string;
    removeLine: string;
    date: string;
    warehouse: string;
    toWarehouse: string;
    storageLocation: string;
    supplier: string;
    reference: string;
    notes: string;
    reason: string;
    costCenter: string;
    item: string;
    unit: string;
    quantity: string;
    unitCost: string;
    unitPrice: string;
    lineValue: string;
    available: string;
    averageCost: string;
    total: string;
    status: string;
    number: string;
    branch: string;
    reversalDate: string;
    selectOrder: string;
    selectReceipt: string;
    selectInvoice: string;
    selectCount: string;
    orderNumber: string;
    orderDate: string;
    receiptNumber: string;
    invoiceNumber: string;
    deliveryNote: string;
    supplierInvoiceNumber: string;
    supplierInvoiceDate: string;
    dueDate: string;
    ordered: string;
    previouslyReceived: string;
    remaining: string;
    receivedNow: string;
    difference: string;
    comparison: string;
    receivedDescription: string;
    orderPrice: string;
    invoicePrice: string;
    priceDifference: string;
    invoiced: string;
    remainingToInvoice: string;
    returned: string;
    remainingToReturn: string;
    systemQuantity: string;
    countedQuantity: string;
    differenceValue: string;
    responsible: string;
    loadWarehouseItems: string;
    createAdjustment: string;
    requiredDate: string;
    requestedBy: string;
    department: string;
    approve: string;
    close: string;
    conversion: string;
    progress: string;
    /** The two cycles الحركات اليومية is split into. */
    groups: {
      purchases: string;
      sales: string;
    };
    cards: {
      requests: string;
      orders: string;
      receipts: string;
      invoices: string;
      returns: string;
      stockReceipt: string;
      stockIssue: string;
      transfer: string;
      adjustment: string;
      count: string;
      countAdjustment: string;
      opening: string;
    };
    hints: {
      requests: string;
      orders: string;
      receipts: string;
      invoices: string;
      returns: string;
      stockReceipt: string;
      stockIssue: string;
      transfer: string;
      adjustment: string;
      count: string;
      countAdjustment: string;
      opening: string;
    };
  };
  /**
   * المخزون والمستودعات › مبيعات المواد.
   *
   * PHYSICAL goods only — service sales keep their own captions, and the two are never mixed.
   */
  inventorySales: {
    /** Printed on a sales order: the offer's prices hold only for the quantities as offered. */
    indivisibleOfferClause: string;
    /** The per-order yes/no that decides whether that clause prints. */
    indivisibleOffer: string; yes: string; no: string;
    /** «مدة التسليم» on the offer, and the item's country of origin shown beside it. */
    deliveryPeriod: string; origin: string;
    /** Invoice: optional insert sources, and what the delivery box says on a direct draft. */
    insertFromOffer: string; insertFromDelivery: string; autoDeliveryOnPost: string;
    /** A sales order may not be dated before today. */
    orderDateBeforeToday: string;
    orderNumber: string;
    deliveryNumber: string;
    invoiceNumber: string;
    returnNumber: string;
    customer: string;
    customerCode: string;
    customerPo: string;
    customerPoDate: string;
    customerReference: string;
    billingAddress: string;
    deliveryAddress: string;
    contactPerson: string;
    phone: string;
    email: string;
    salesperson: string;
    driver: string;
    vehicle: string;
    deliveryReference: string;
    paymentMethod: string;
    paymentTerms: string;
    returnReason: string;
    internalNotes: string;
    selectOrder: string;
    selectDelivery: string;
    selectInvoice: string;
    ordered: string;
    deliveredNow: string;
    previouslyDelivered: string;
    remainingToDeliver: string;
    delivered: string;
    previouslyInvoiced: string;
    remainingToInvoice: string;
    invoiceNow: string;
    sold: string;
    previouslyReturned: string;
    maxReturnable: string;
    returnNow: string;
    available: string;
    shortStock: string;
    issueCost: string;
    costTotal: string;
    grossBeforeDiscount: string;
    discountTotal: string;
    netBeforeTax: string;
    taxTotal: string;
    grandTotal: string;
    baseCurrencyTotal: string;
    exchangeRate: string;
    discountType: string;
    discountValue: string;
    taxType: string;
    fulfilment: string;
    closeOrder: string;
    reopenOrder: string;
    approve: string;
    noStockEffect: string;
    stockOutHere: string;
    cards: {
      orders: string;
      deliveries: string;
      invoices: string;
      returns: string;
    };
    hints: {
      orders: string;
      deliveries: string;
      invoices: string;
      returns: string;
    };
  };
  dailyTransactionKit: {
    inquiry: string;
    newDocument: string;
    save: string;
    saveAndNew: string;
    /** Tooltip on a greyed-out save: the form still lacks what the document needs. */
    saveIncomplete: string;
    print: string;
    cancel: string;
    lookupTitle: string;
    registerTitle: string;
    emptyTitle: string;
    emptyHint: string;
  };
  /**
   * The three same-module inquiries added so every Daily Transaction has one. Each reads the
   * document's own authoritative list query — no second table, no sync.
   */
  sameModuleInquiries: {
    noRows: string;
    open: string;
    receiptOrders: {
      title: string; breadcrumb: string; notice: string; sourceNotice: string;
      orderNumber: string; date: string; customer: string; amount: string;
      collected: string; remaining: string; currency: string; status: string;
      branch: string; reference: string; collector: string;
      totalOrdered: string; totalCollected: string; totalRemaining: string; count: string;
    };
    reminderHistory: {
      title: string; breadcrumb: string; notice: string; sourceNotice: string;
      batchNumber: string; date: string; channel: string; status: string;
      recipients: string; sent: string; skipped: string; failed: string;
      totalBalance: string; createdBy: string; count: string;
    };
    bankReconciliation: {
      title: string; breadcrumb: string; notice: string; sourceNotice: string;
      number: string; bank: string; account: string; period: string; statementDate: string;
      statementBalance: string; ledgerBalance: string; reconciled: string; difference: string;
      status: string; count: string; statusDraft: string; statusCompleted: string;
    };
  };
  inquiryKit: {
    search: string;
    reset: string;
    refresh: string;
    print: string;
    printPreview: string;
    exportPdf: string;
    exportExcel: string;
    columns: string;
    columnSettings: string;
    columnSettingsHint: string;
    columnLocked: string;
    restoreDefaults: string;
    done: string;
    resultInfo: string;
    rowsPerPage: string;
    pageOf: string;
    firstPage: string;
    previousPage: string;
    nextPage: string;
    lastPage: string;
    sortAscending: string;
    sortDescending: string;
    printFailed: string;
  };
  journal: {
    title: string;
    voucherType: string;
    voucherInformation: string;
    referenceInformation: string;
    tabAttachments: string;
    tabPrintOptions: string;
    tabAuditHistory: string;

    /** Compact secondary modules and the print dialog. */
    linkedDocuments: string;
    additionalInformation: string;
    reverses: string;
    reversedBy: string;
    noLinkedDocuments: string;
    openDocument: string;
    saveBeforeAttaching: string;
    printDialogTitle: string;
    withCostCenters: string;
    withoutCostCenters: string;
    auditHistoryEmpty: string;
    auditWhen: string;
    auditHistoryUnsaved: string;
    createdBy: string;
    createdOn: string;
    modifiedBy: string;
    modifiedOn: string;
    auditCategories: { header: string; line: string; attachment: string };
    auditActions: {
      created: string;
      updated: string;
      deleted: string;
      audited: string;
      posted: string;
      returned: string;
      reversed: string;
      reopened: string;
      other: string;
    };
    dropFilesHere: string;
    accountBalanceHint: string;
    selectCostCenter: string;
    searchCostCenters: string;
    noCostCenters: string;
    groupCostCenter: string;
    groupAccount: string;
    statusDraft: string;
    statusNotAudited: string;
    delete: string;
    journalVoucher: string;
    status: string;
    statuses: { unposted: string; posted: string; audited: string; rejected: string; cancelled: string; reversed: string };
    sequence: string;
    voucherDate: string;
    fiscalYear: string;
    period: string;
    referenceNumber: string;
    referenceDate: string;
    referenceSequence: string;
    salesperson: string;
    user: string;
    branch: string;
    notes: string;
    notesHint: string;
    save: string;
    saveAndNew: string;
    inquiry: string;
    print: string;
    cancel: string;
    newEntry: string;
    printOptions: string;
    printSubledger: string;
    showCostCenters: string;
    showCurrency: string;
    printStamps: string;
    lines: string;
    addLine: string;
    removeLine: string;
    accountNumber: string;
    accountName: string;
    subledger: string;
    currency: string;
    exchangeRate: string;
    debit: string;
    credit: string;
    localDebit: string;
    localCredit: string;
    balance: string;
    balanceType: string;
    description: string;
    costCenter: string;
    actions: string;
    totalDebit: string;
    totalCredit: string;
    totalLocalDebit: string;
    totalLocalCredit: string;
    difference: string;
    balanced: string;
    unbalanced: string;
    attachments: string;
    uploadFile: string;
    allowedTypes: string;
    noAttachments: string;
    attachmentsPending: string;
    attachmentPendingNote: string;
    attachmentStatuses: { pending: string; uploading: string; uploaded: string; failed: string };
    attachmentFileType: string;
    attachmentFileSize: string;
    attachmentTypeRejected: string;
    attachmentTooLarge: string;
    attachmentEmpty: string;
    attachmentUploadFailed: string;
    attachmentRetry: string;
    attachmentRemove: string;
    attachmentsLinked: string;
    download: string;
    saveFirst: string;
    selectAccount: string;
    searchAccounts: string;
    searchParties: string;
    noAccounts: string;
    noParties: string;
    partyRequired: string;
    costCenterRequired: string;
    notesRequired: string;
    rateOverridden: string;
    rateMissing: string;
    readOnlyPosted: string;
    discardChanges: string;
    discardBody: string;
    savedAs: string;
    filters: string;
    dateFrom: string;
    dateTo: string;
    apply: string;
    clear: string;
    results: string;
    noResults: string;
    open: string;
    edit: string;
    view: string;
    deleteEntry: string;
    deleteBody: string;
    lineCount: string;
    backToEntry: string;
    balanceInfo: string;
    reopen: string;
    reopenTitle: string;
    reopenWarning: string;
    reopenReason: string;
    reopenReasonHint: string;
    reopenConfirm: string;
    reopened: string;
  };
  glSettings: {
    title: string;
    pageDescription: string;
    descriptionColumn: string;
    valueColumn: string;

    requireDefaultCostCenter: string;
    requireDefaultCostCenterHint: string;
    requireJournalEntryNotes: string;
    requireJournalEntryNotesHint: string;
    aggregateChildSubledger: string;
    aggregateChildSubledgerHint: string;
    costPercentage: string;
    costPercentageHint: string;
    manualCostCenterEntryMethod: string;
    manualCostCenterEntryMethodHint: string;

    yes: string;
    no: string;
    methodAmount: string;
    methodPercentage: string;

    restoreDefaults: string;
    viewAuditHistory: string;

    savedOk: string;
    saveError: string;
    loadError: string;
    unsavedChangesWarning: string;
    readOnlyNotice: string;

    restoreDefaultsConfirmTitle: string;
    restoreDefaultsConfirmMessage: string;
    restoreDefaultsConfirmLabel: string;
    restoredOk: string;

    enableConfirmTitle: string;
    enableConfirmLabel: string;

    auditHistoryTitle: string;
    auditHistoryEmpty: string;
    auditHistoryLoadError: string;
    auditHistoryWhen: string;
    auditHistoryUser: string;
    auditHistorySetting: string;
    auditHistoryOldValue: string;
    auditHistoryNewValue: string;
    auditHistoryCreated: string;
    auditHistorySystem: string;
  };
  customerOpeningBalances: {
    title: string; breadcrumb: string; purposeNotice: string; readOnlyNotice: string;
    types: {
      customerReceivables: string; unbilledReceivables: string; advancePayments: string;
      promissoryNotes: string; supplierReceivables: string; chequesInCollection: string;
    };
    category: string; allCategories: string; fiscalYear: string; currency: string;
    allCurrencies: string; status: string; allStatuses: string;
    statusNotEntered: string; statusZero: string; statusEntered: string; statusInvalid: string;
    statusLocked: string;
    customerNumber: string; customer: string; exchangeRate: string;
    debit: string; credit: string; localDebit: string; localCredit: string;
    controlAccount: string; noControlAccount: string; inactiveCustomer: string;
    search: string; searchPlaceholder: string; refresh: string;
    print: string; pdf: string; excel: string;
    save: string; cancelChanges: string; resetRow: string;
    unsavedOne: string; unsavedMany: string; leaveWarning: string;
    totals: string; totalDebit: string; totalCredit: string; totalLocalDebit: string;
    totalLocalCredit: string; difference: string; balanced: string;
    entered: string; zero: string; notEntered: string;
    empty: string; rows: string;
    saved: string; nothingToSave: string;
    loadError: string; saveError: string;
    preparationNotice: string;
  };
  serviceQuotations: {
    title: string; breadcrumb: string; purposeNotice: string; noAccountingNotice: string;
    readOnlyNotice: string; dependencyNotice: string;
    headerTitle: string; linesTitle: string; listTitle: string;
    newRecord: string; number: string; date: string; status: string;
    draft: string; cancelled: string;
    customer: string; currency: string; exchangeRate: string; paymentMethod: string;
    cash: string; credit: string; salesperson: string; costCenter: string; notes: string;
    service: string; description: string; quantity: string; unitPrice: string;
    discountPercent: string; taxPercent: string; gross: string; discount: string;
    tax: string; net: string; taxable: string; pricingMode: string;
    taxExclusive: string; taxInclusive: string; exemptCustomer: string; addLine: string; removeLine: string; noLines: string;
    subtotal: string; discountTotal: string; taxTotal: string; netTotal: string;
    save: string; saveNew: string; cancelEdit: string; cancelDoc: string; cancelConfirm: string;
    edit: string; refresh: string; search: string; noRows: string; count: string;
    choose: string; none: string; actions: string; view: string; back: string;
    saved: string; cancelledOk: string;
    loadError: string; saveError: string;
    taxType: string; taxTypeFromService: string; duplicateLine: string;
    paymentInstrument: string;
    branch: string; fiscalYear: string;
    discountType: string; discountValue: string; discountTypeNone: string;
    discountAsPercent: string; discountAsAmount: string; discountTypeHint: string;
    customerInfoTitle: string; customerInfoEmpty: string;
    customerCode: string; customerNameLabel: string; phone: string; mobile: string;
    email: string; taxNumber: string; addressLabel: string; customerCategory: string;
    creditLimit: string; taxTreatment: string;
    treatmentInclusive: string; treatmentExclusive: string; treatmentExempt: string;
    attachmentsTitle: string; attach: string; attachHint: string; noAttachments: string;
    attachmentSaveFirst: string; attachmentFile: string; attachmentSize: string;
    attachmentUploadedBy: string; attachmentUploadedAt: string;
    attachmentView: string; attachmentDownload: string; attachmentRemove: string;
    attachmentRemoveConfirm: string; attachmentUploadFailed: string;
    attachmentOpenFailed: string; attachmentUploaded: string; attachmentRemoved: string;
    documentActions: string; printAction: string; exportPdf: string; exportExcel: string;
    exportSaveFirst: string; exportFailed: string;
  };
  serviceSalesInvoices: {
    settlementTitle: string; settlementInvoiceTotal: string; settlementSettled: string;
    settlementOutstanding: string; settlementPaymentNo: string; settlementDate: string;
    settlementAmount: string; settlementStatus: string; settlementAction: string;
    settlementCancel: string; settlementReason: string; settlementConfirm: string;
    settlementDismiss: string;
    settlementCancelled: string; settlementNone: string; settlementActive: string;
    settlementCancelledLabel: string; settlementNotice: string;
    settlementPaid: string; settlementPartiallyPaid: string; settlementUnpaid: string;
    title: string; breadcrumb: string; purposeNotice: string; lifecycleNotice: string;
    readOnlyNotice: string; postedNotice: string;
    headerTitle: string; linesTitle: string; listTitle: string; customerInfoTitle: string;
    customerInfoEmpty: string; attachmentsTitle: string;
    newRecord: string; number: string; date: string; status: string;
    statusDraft: string; statusAudited: string; statusPosted: string; statusReversed: string;
    customer: string; paymentMethod: string; paymentFromCustomer: string;
    saleNature: string; saleOneTime: string; saleContract: string;
    sourceQuotation: string; noQuotation: string; imported: string;
    currency: string; exchangeRate: string; branch: string; fiscalYear: string; notes: string;
    taxType: string; taxTypeFromService: string;
    service: string; description: string; quantity: string; unitPrice: string;
    duplicateLine: string; discountType: string; discountValue: string; discountTypeNone: string;
    discountPercent: string; taxPercent: string; taxable: string; tax: string; gross: string;
    addLine: string; removeLine: string; noLines: string;
    subtotal: string; discountTotal: string; grandTotal: string;
    saveDraft: string; cancelEdit: string;
    auditAction: string; postAction: string; reverseAction: string; reverseConfirm: string;
    reverseReason: string;
    printAction: string; exportPdf: string; exportExcel: string;
    exportSaveFirst: string; exportFailed: string;
    attach: string; attachHint: string; noAttachments: string; attachmentSaveFirst: string;
    attachmentFile: string; attachmentSize: string; attachmentUploadedBy: string;
    attachmentUploadedAt: string; attachmentView: string; attachmentDownload: string;
    attachmentRemove: string; attachmentRemoveConfirm: string; attachmentUploaded: string;
    attachmentRemoved: string; attachmentUploadFailed: string; attachmentOpenFailed: string;
    customerCode: string; customerNameLabel: string; phone: string; mobile: string;
    email: string; taxNumber: string; addressLabel: string; creditLimit: string;
    edit: string; view: string; refresh: string; search: string; noRows: string; count: string;
    choose: string; none: string; actions: string;
    saved: string; audited: string; posted: string; reversed: string;
    loadError: string; saveError: string; actionError: string;
  };
  debitNotes: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    lifecycleNotice: string;
    readOnlyNotice: string;
    postedNotice: string;
    sourceInvoiceNotice: string;
    headerTitle: string;
    linesTitle: string;
    attachmentsTitle: string;
    listTitle: string;
    filtersTitle: string;
    newRecord: string;
    number: string;
    date: string;
    status: string;
    branch: string;
    createdBy: string;
    customer: string;
    customerInfo: string;
    currency: string;
    exchangeRate: string;
    reference: string;
    notes: string;
    sourceInvoice: string;
    sourceInvoiceNone: string;
    taxInclusive: string;
    inclusive: string;
    exclusive: string;
    costCenter: string;
    lineDescription: string;
    offsetAccount: string;
    amount: string;
    taxType: string;
    taxRate: string;
    lineNet: string;
    lineTax: string;
    lineTotal: string;
    rowNotes: string;
    addRow: string;
    duplicateRow: string;
    removeRow: string;
    totalNet: string;
    totalTax: string;
    grandTotal: string;
    actions: string;
    choose: string;
    none: string;
    count: string;
    search: string;
    refresh: string;
    noRows: string;
    edit: string;
    view: string;
    save: string;
    cancelEdit: string;
    auditAction: string;
    postAction: string;
    reverseAction: string;
    reverseConfirm: string;
    reverseReason: string;
    printAction: string;
    pdfAction: string;
    excelAction: string;
    saveFirst: string;
    exportFailed: string;
    fromDate: string;
    toDate: string;
    allStatuses: string;
    apply: string;
    reset: string;
    attachmentAdd: string;
    attachmentRemove: string;
    attachmentRemoveConfirm: string;
    attachmentUploaded: string;
    attachmentRemoved: string;
    attachmentUploadFailed: string;
    attachmentDraftOnly: string;
    attachmentOpenFailed: string;
    attachmentFile: string;
    attachmentSize: string;
    attachmentBy: string;
    attachmentAt: string;
    attachmentView: string;
    attachmentDownload: string;
    noAttachments: string;
    attachmentPolicy: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
    saved: string;
    audited: string;
    posted: string;
    reversed: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };

  creditNotes: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    lifecycleNotice: string;
    readOnlyNotice: string;
    postedNotice: string;
    sourceInvoiceNotice: string;
    headerTitle: string;
    linesTitle: string;
    attachmentsTitle: string;
    listTitle: string;
    filtersTitle: string;
    newRecord: string;
    number: string;
    date: string;
    status: string;
    branch: string;
    createdBy: string;
    customer: string;
    customerInfo: string;
    currency: string;
    exchangeRate: string;
    reference: string;
    notes: string;
    sourceInvoice: string;
    sourceInvoiceNone: string;
    taxInclusive: string;
    inclusive: string;
    exclusive: string;
    costCenter: string;
    lineDescription: string;
    offsetAccount: string;
    amount: string;
    taxType: string;
    taxRate: string;
    lineNet: string;
    lineTax: string;
    lineTotal: string;
    rowNotes: string;
    addRow: string;
    duplicateRow: string;
    removeRow: string;
    totalNet: string;
    totalTax: string;
    grandTotal: string;
    actions: string;
    choose: string;
    none: string;
    count: string;
    search: string;
    refresh: string;
    noRows: string;
    edit: string;
    view: string;
    save: string;
    cancelEdit: string;
    auditAction: string;
    postAction: string;
    reverseAction: string;
    reverseConfirm: string;
    reverseReason: string;
    printAction: string;
    pdfAction: string;
    excelAction: string;
    saveFirst: string;
    exportFailed: string;
    fromDate: string;
    toDate: string;
    allStatuses: string;
    apply: string;
    reset: string;
    attachmentAdd: string;
    attachmentRemove: string;
    attachmentRemoveConfirm: string;
    attachmentUploaded: string;
    attachmentRemoved: string;
    attachmentUploadFailed: string;
    attachmentDraftOnly: string;
    attachmentOpenFailed: string;
    attachmentFile: string;
    attachmentSize: string;
    attachmentBy: string;
    attachmentAt: string;
    attachmentView: string;
    attachmentDownload: string;
    noAttachments: string;
    attachmentPolicy: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
    saved: string;
    audited: string;
    posted: string;
    reversed: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };
  serviceSalesReturns: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    lifecycleNotice: string;
    readOnlyNotice: string;
    postedNotice: string;
    sourceRequiredNotice: string;
    noInventoryNotice: string;
    pickInvoiceTitle: string;
    headerTitle: string;
    linesTitle: string;
    attachmentsTitle: string;
    listTitle: string;
    filtersTitle: string;
    newRecord: string;
    number: string;
    date: string;
    status: string;
    branch: string;
    createdBy: string;
    sourceInvoice: string;
    invoiceNumber: string;
    invoiceDate: string;
    customer: string;
    currency: string;
    exchangeRate: string;
    costCenter: string;
    reference: string;
    notes: string;
    changeInvoice: string;
    chooseInvoice: string;
    noInvoices: string;
    invoiceSearch: string;
    fullyReturned: string;
    service: string;
    lineDescription: string;
    originalQty: string;
    previouslyReturned: string;
    availableQty: string;
    returnQty: string;
    unitPrice: string;
    taxType: string;
    taxRate: string;
    lineNet: string;
    lineTax: string;
    lineTotal: string;
    nothingSelected: string;
    totalNet: string;
    totalTax: string;
    grandTotal: string;
    actions: string;
    choose: string;
    none: string;
    count: string;
    search: string;
    refresh: string;
    noRows: string;
    edit: string;
    view: string;
    save: string;
    cancelEdit: string;
    auditAction: string;
    postAction: string;
    reverseAction: string;
    reverseConfirm: string;
    reverseReason: string;
    printAction: string;
    pdfAction: string;
    excelAction: string;
    saveFirst: string;
    exportFailed: string;
    fromDate: string;
    toDate: string;
    allStatuses: string;
    apply: string;
    reset: string;
    attachmentAdd: string;
    attachmentRemove: string;
    attachmentRemoveConfirm: string;
    attachmentUploaded: string;
    attachmentRemoved: string;
    attachmentUploadFailed: string;
    attachmentDraftOnly: string;
    attachmentOpenFailed: string;
    attachmentFile: string;
    attachmentSize: string;
    attachmentBy: string;
    attachmentAt: string;
    attachmentView: string;
    attachmentDownload: string;
    noAttachments: string;
    attachmentPolicy: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
    saved: string;
    audited: string;
    posted: string;
    reversed: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };
  receiptVouchers: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    lifecycleNotice: string;
    readOnlyNotice: string;
    postedNotice: string;
    tabVouchers: string;
    tabCheques: string;
    headerTitle: string;
    paymentsTitle: string;
    chequesTitle: string;
    attachmentsTitle: string;
    listTitle: string;
    newRecord: string;
    number: string;
    date: string;
    status: string;
    branch: string;
    createdBy: string;
    sourceType: string;
    sourceCustomer: string;
    sourceAccount: string;
    customer: string;
    customerInfo: string;
    glAccount: string;
    glAccountHint: string;
    currency: string;
    exchangeRate: string;
    referenceNumber: string;
    totalAmount: string;
    notes: string;
    method: string;
    amount: string;
    destination: string;
    destinationFromMethod: string;
    cashbox: string;
    bankAccount: string;
    bankAccountHint: string;
    chequeNumber: string;
    drawerBank: string;
    drawerBranch: string;
    chequeDate: string;
    dueDate: string;
    rowNotes: string;
    addRow: string;
    duplicateRow: string;
    removeRow: string;
    actions: string;
    choose: string;
    none: string;
    count: string;
    search: string;
    refresh: string;
    noRows: string;
    edit: string;
    view: string;
    save: string;
    cancelEdit: string;
    auditAction: string;
    postAction: string;
    reverseAction: string;
    reverseConfirm: string;
    reverseReason: string;
    printAction: string;
    pdfAction: string;
    excelAction: string;
    saveFirst: string;
    attachmentAdd: string;
    attachmentRemove: string;
    attachmentRemoveConfirm: string;
    attachmentUploaded: string;
    attachmentRemoved: string;
    attachmentUploadFailed: string;
    attachmentDraftOnly: string;
    attachmentSize: string;
    attachmentBy: string;
    attachmentFile: string;
    attachmentView: string;
    attachmentDownload: string;
    attachmentAt: string;
    noAttachments: string;
    attachmentOpenFailed: string;
    attachmentPolicy: string;
    exportFailed: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
    chequeUnderCollection: string;
    chequeDeposited: string;
    chequeCleared: string;
    chequeBounced: string;
    chequeReturned: string;
    saved: string;
    audited: string;
    posted: string;
    reversed: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };
  receiptOrders: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    noAccountingNotice: string;
    readOnlyNotice: string;
    cancelledNotice: string;
    headerTitle: string;
    amountsTitle: string;
    linkedTitle: string;
    attachmentsTitle: string;
    listTitle: string;
    filtersTitle: string;
    newRecord: string;
    number: string;
    date: string;
    status: string;
    branch: string;
    createdBy: string;
    customer: string;
    currency: string;
    exchangeRate: string;
    collector: string;
    salesRepresentative: string;
    expectedDate: string;
    expectedPaymentMethod: string;
    reference: string;
    notes: string;
    cancellationReason: string;
    orderAmount: string;
    collectedAmount: string;
    remainingAmount: string;
    derivedHint: string;
    approveAction: string;
    cancelAction: string;
    cancelConfirm: string;
    cancelReason: string;
    createReceiptAction: string;
    printAction: string;
    pdfAction: string;
    excelAction: string;
    saveFirst: string;
    exportFailed: string;
    receiptNumber: string;
    receiptDate: string;
    receiptAmount: string;
    receiptStatus: string;
    paymentSummary: string;
    counted: string;
    notCounted: string;
    noLinked: string;
    openReceipt: string;
    statusDraft: string;
    statusOpen: string;
    statusCancelled: string;
    fullyCollected: string;
    partiallyCollected: string;
    actions: string;
    choose: string;
    none: string;
    count: string;
    search: string;
    refresh: string;
    noRows: string;
    edit: string;
    view: string;
    save: string;
    cancelEdit: string;
    fromDate: string;
    toDate: string;
    allStatuses: string;
    apply: string;
    reset: string;
    attachmentAdd: string;
    attachmentRemove: string;
    attachmentRemoveConfirm: string;
    attachmentUploaded: string;
    attachmentRemoved: string;
    attachmentUploadFailed: string;
    attachmentOpenFailed: string;
    attachmentCancelledOnly: string;
    attachmentFile: string;
    attachmentSize: string;
    attachmentBy: string;
    attachmentAt: string;
    attachmentView: string;
    attachmentDownload: string;
    noAttachments: string;
    attachmentPolicy: string;
    saved: string;
    approved: string;
    cancelled: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };
  advanceReceivedRevenues: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    contractRuleNotice: string;
    collectionNotice: string;
    noSchedulerNotice: string;
    readOnlyNotice: string;
    eligibleTitle: string;
    schedulesTitle: string;
    scheduleTitle: string;
    periodsTitle: string;
    recognitionsTitle: string;
    invoice: string;
    invoiceDate: string;
    customer: string;
    currency: string;
    contractStart: string;
    contractEnd: string;
    periodCount: string;
    originalDeferred: string;
    recognized: string;
    returned: string;
    remaining: string;
    duePending: string;
    period: string;
    periodStart: string;
    periodEnd: string;
    recognitionDate: string;
    scheduledAmount: string;
    recognizedAmount: string;
    periodStatus: string;
    recognitionNumber: string;
    fiscalYear: string;
    amount: string;
    revenueAccount: string;
    service: string;
    asOfDate: string;
    generate: string;
    activate: string;
    remove: string;
    recognizeDue: string;
    audit: string;
    post: string;
    reverse: string;
    open: string;
    back: string;
    search: string;
    notEligible: string;
    noSchedules: string;
    noEligible: string;
    noRecognitions: string;
    generated: string;
    activated: string;
    removed: string;
    recognizedToast: string;
    auditedToast: string;
    postedToast: string;
    reversedToast: string;
    confirmRecognize: string;
    confirmPost: string;
    confirmReverse: string;
    statusDraft: string;
    statusActive: string;
    statusCompleted: string;
    periodPending: string;
    periodDue: string;
    periodDrafted: string;
    periodRecognized: string;
    periodReduced: string;
    entryNotice: string;
  };
  customerTransactionTransfers: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    historyNotice: string;
    entryNotice: string;
    readOnlyNotice: string;
    selectionTitle: string;
    transfersTitle: string;
    detailTitle: string;
    transferDate: string;
    oldCustomer: string;
    newCustomer: string;
    fromDate: string;
    toDate: string;
    statement: string;
    search: string;
    load: string;
    select: string;
    documentType: string;
    documentNumber: string;
    documentDate: string;
    reference: string;
    description: string;
    currency: string;
    debit: string;
    credit: string;
    netEffect: string;
    currentCustomer: string;
    movementStatus: string;
    lastTransfer: string;
    transferNumber: string;
    movementCount: string;
    totalEffect: string;
    sameControlAccount: string;
    differentControlAccounts: string;
    fiscalYear: string;
    status: string;
    execute: string;
    audit: string;
    post: string;
    reverse: string;
    open: string;
    back: string;
    notSelectable: string;
    sourceReversed: string;
    noMovements: string;
    noTransfers: string;
    saved: string;
    auditedToast: string;
    postedToast: string;
    reversedToast: string;
    confirmExecute: string;
    confirmPost: string;
    confirmReverse: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
  };
  supplierTransactionTransfers: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    historyNotice: string;
    entryNotice: string;
    readOnlyNotice: string;
    selectionTitle: string;
    transfersTitle: string;
    detailTitle: string;
    transferDate: string;
    oldSupplier: string;
    newSupplier: string;
    fromDate: string;
    toDate: string;
    statement: string;
    search: string;
    load: string;
    select: string;
    documentType: string;
    documentNumber: string;
    documentDate: string;
    reference: string;
    description: string;
    currency: string;
    debit: string;
    credit: string;
    netEffect: string;
    currentSupplier: string;
    movementStatus: string;
    lastTransfer: string;
    transferNumber: string;
    movementCount: string;
    totalEffect: string;
    sameControlAccount: string;
    differentControlAccounts: string;
    fiscalYear: string;
    status: string;
    execute: string;
    audit: string;
    post: string;
    reverse: string;
    open: string;
    back: string;
    notSelectable: string;
    sourceReversed: string;
    noMovements: string;
    noTransfers: string;
    saved: string;
    auditedToast: string;
    postedToast: string;
    reversedToast: string;
    confirmExecute: string;
    confirmPost: string;
    confirmReverse: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusReversed: string;
  };
  batchPrint: {
    select: string;
    selectAll: string;
    clear: string;
    selected: string;
    printSelected: string;
    nothingSelected: string;
    preparing: string;
    failed: string;
    hint: string;
  };
  insuranceCompanies: {
    title: string;
    breadcrumb: string;
    code: string;
    name: string;
    phone: string;
    mobile: string;
    email: string;
    address: string;
    notes: string;
    status: string;
    active: string;
    inactive: string;
    policies: string;
    openCard: string;
    noRows: string;
    total: string;
    search: string;
    searchHint: string;
    refresh: string;
    sectorHint: string;
  };
  cancelInvoiceSettlementScreen: {
    noParties: string;
    party: string;
    customer: string;
    supplier: string;
    choose: string;
    settlements: string;
    settlementNo: string;
    settledDate: string;
    invoice: string;
    creditDocument: string;
    amount: string;
    currency: string;
    state: string;
    active: string;
    cancelled: string;
    cancelAction: string;
    reasonPrompt: string;
    reasonRequired: string;
    confirm: string;
    cancelled_ok: string;
    noRows: string;
    pickParty: string;
    cancelledBy: string;
    reason: string;
    refresh: string;
    title: string;
    breadcrumb: string;
    scaffoldNotice: string;
  };
  accountReconciliations: {
    selectColumn: string;
    ctsTitle: string; ctsBreadcrumb: string; ctsNotice: string; ctsBalanceNotice: string;
    sequence: string; branch: string; year: string; date: string; user: string; notesLabel: string;
    upToDate: string; category: string; inquiry: string; saveAction: string; cancelAction: string;
    debitSection: string; creditSection: string;
    txnNumber: string; txnDate: string; txnType: string; txnBranch: string;
    debitCurrencyCol: string; creditCurrencyCol: string; rate: string;
    prevSettleCurrency: string; prevSettleLocal: string;
    thisSettleCurrency: string; thisSettleLocal: string; remaining: string;
    totalCurrency: string; totalLocal: string; balanced: string; unbalanced: string;
    savedToast: string; pickCustomerFirst: string;
    customerTitle: string; customerBreadcrumb: string; showSettled: string;
    noDueDateNotice: string; settlementAmount: string; settle: string;
    title: string; breadcrumb: string; purposeNotice: string; noAccountingNotice: string;
    cancelNotice: string; readOnlyNotice: string;
    partyType: string; customer: string; supplier: string; party: string;
    currency: string; fromDate: string; toDate: string; status: string;
    statusAll: string; statusUnreconciled: string; statusPartial: string; statusFull: string;
    load: string; outstandingTitle: string; creditsTitle: string;
    documentType: string; documentNumber: string; documentDate: string; dueDate: string;
    originalAmount: string; settledAmount: string; outstandingAmount: string;
    allocatedAmount: string; availableAmount: string; allocate: string;
    reconcile: string; reconciled: string; notes: string;
    totalOutstanding: string; totalAvailable: string; totalAllocating: string;
    historyTitle: string; action: string; actionReconciled: string; actionCancelled: string;
    cancelSettlement: string; cancellationReason: string; cancelConfirm: string; cancelled: string;
    invoiceTotal: string; paymentInfoTitle: string; paymentNo: string; paymentDate: string;
    paid: string; partiallyPaid: string; unpaid: string;
    by: string; at: string; reason: string; noRows: string; noHistory: string;
    pickParty: string; save: string; close: string;
  };
  customerBalanceReminders: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    noAccountingNotice: string;
    transportNotice: string;
    localModeNotice: string;
    localModeBadge: string;
    print: string;
    exportPdf: string;
    exportExcel: string;
    exportNotice: string;
    readOnlyNotice: string;
    filtersTitle: string;
    customersTitle: string;
    reminderTitle: string;
    summaryTitle: string;
    historyTitle: string;
    search: string;
    customer: string;
    currency: string;
    collector: string;
    salesRepresentative: string;
    category: string;
    positiveOnly: string;
    minimumBalance: string;
    apply: string;
    reset: string;
    select: string;
    selectAll: string;
    code: string;
    name: string;
    email: string;
    mobile: string;
    balance: string;
    lastReminder: string;
    status: string;
    noEmail: string;
    noRows: string;
    count: string;
    channel: string;
    channelEmail: string;
    template: string;
    subject: string;
    message: string;
    preview: string;
    previewTitle: string;
    previewFor: string;
    placeholders: string;
    loadTemplate: string;
    selectedCount: string;
    selectedBalance: string;
    sendAction: string;
    sendConfirm: string;
    sending: string;
    batchNumber: string;
    batchDate: string;
    recipients: string;
    sent: string;
    skipped: string;
    failed: string;
    totalBalance: string;
    createdBy: string;
    createdAt: string;
    statusPrepared: string;
    statusCompleted: string;
    statusCompletedWithErrors: string;
    statusFailed: string;
    deliverySent: string;
    deliverySkipped: string;
    deliveryFailed: string;
    reason: string;
    destination: string;
    snapshot: string;
    viewBatch: string;
    noBatches: string;
    back: string;
    refresh: string;
    none: string;
    actions: string;
    sendSuccess: string;
    loadError: string;
    sendError: string;
    previewError: string;
  };
  supplierBalanceReminders: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    noAccountingNotice: string;
    transportNotice: string;
    localModeNotice: string;
    localModeBadge: string;
    print: string;
    exportPdf: string;
    exportExcel: string;
    exportNotice: string;
    readOnlyNotice: string;
    filtersTitle: string;
    suppliersTitle: string;
    reminderTitle: string;
    summaryTitle: string;
    historyTitle: string;
    search: string;
    supplier: string;
    currency: string;
    collector: string;
    salesRepresentative: string;
    category: string;
    positiveOnly: string;
    minimumBalance: string;
    apply: string;
    reset: string;
    select: string;
    selectAll: string;
    code: string;
    name: string;
    email: string;
    mobile: string;
    balance: string;
    lastReminder: string;
    status: string;
    noEmail: string;
    noRows: string;
    count: string;
    channel: string;
    channelEmail: string;
    template: string;
    subject: string;
    message: string;
    preview: string;
    previewTitle: string;
    previewFor: string;
    placeholders: string;
    loadTemplate: string;
    selectedCount: string;
    selectedBalance: string;
    sendAction: string;
    sendConfirm: string;
    sending: string;
    batchNumber: string;
    batchDate: string;
    recipients: string;
    sent: string;
    skipped: string;
    failed: string;
    totalBalance: string;
    createdBy: string;
    createdAt: string;
    statusPrepared: string;
    statusCompleted: string;
    statusCompletedWithErrors: string;
    statusFailed: string;
    deliverySent: string;
    deliverySkipped: string;
    deliveryFailed: string;
    reason: string;
    destination: string;
    snapshot: string;
    viewBatch: string;
    noBatches: string;
    back: string;
    refresh: string;
    none: string;
    actions: string;
    sendSuccess: string;
    loadError: string;
    sendError: string;
    previewError: string;
  };
  prepaidExpenses: {
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    noPayableNotice: string; readOnlyNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; periodsTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    stateFilter: string; allStates: string; totalCount: string; totalOriginal: string;
    totalRecognized: string;
    number: string; date: string; supplier: string; sourceInvoice: string;
    chooseInvoice: string; sourceLine: string; chooseLine: string; lineNet: string;
    available: string; alreadyScheduled: string; returned: string;
    amount: string; prepaidAccount: string; expenseAccount: string; chooseAccount: string;
    costCenter: string; none: string; coverageStart: string; coverageEnd: string;
    basis: string; basisMonthly: string; periodCount: string; currency: string;
    exchangeRate: string; notes: string;
    original: string; recognized: string; remaining: string; state: string;
    draft: string; active: string; partiallyRecognized: string; fullyRecognized: string;
    cancelled: string;
    periodNumber: string; periodFrom: string; periodTo: string; recognitionDate: string;
    scheduled: string; periodRecognized: string; periodRemaining: string; voucher: string;
    periodState: string; due: string; notDue: string; reversedLabel: string;
    save: string; cancelEdit: string; edit: string; view: string; activate: string;
    recognize: string; recognizeOne: string; reverse: string; cancelSchedule: string;
    cancelReason: string; confirmCancel: string; reversalDate: string; reversalReason: string;
    confirmReverse: string; close: string; details: string; createdBy: string;
    createdAt: string; cancelledBy: string;
    printAction: string; pdfAction: string; excelAction: string;
    saved: string; activatedOk: string; recognizedOk: string; reversedOk: string;
    cancelledOk: string; skippedLabel: string;
    loadError: string; saveError: string; activateError: string; recognizeError: string;
    reverseError: string; cancelError: string; exportFailed: string;
  };
  supplierMaster: {
    infoTitle: string; infoBreadcrumb: string; infoPurpose: string; infoNoAccounting: string;
    infoDelegation: string; limitTitle: string; limitBreadcrumb: string; limitPurpose: string;
    limitNoAccounting: string; readOnlyNotice: string; noSuppliersInScope: string;
    supplier: string; chooseSupplier: string; currentValue: string; proposedValue: string;
    reason: string; apply: string; refresh: string; noChanges: string;
    historyTitle: string; field: string; oldValue: string; newValue: string;
    changedBy: string; changedAt: string; noHistory: string;
    name: string; nameAr: string; startDate: string; controlAccount: string; currency: string;
    taxTreatment: string; paymentTerm: string; phone: string; mobile: string; fax: string;
    email: string; website: string; contactPerson: string; taxNumber: string;
    commercialRegistration: string; nationalIdCompany: string; referenceNumber: string;
    country: string; city: string; area: string; street: string; building: string;
    postalCode: string; poBox: string; address: string; addressNotes: string; notes: string;
    isActive: string; isBlacklisted: string; blacklistReason: string;
    creditLimitReadOnly: string;
    currentLimit: string; newLimit: string; changeAmount: string; balance: string;
    balanceExceeds: string; changeLimit: string; limitHistory: string; previousLimit: string;
    keep: string; clear: string; unchanged: string;
    savedFields: string; limitChanged: string;
    loadError: string; saveError: string; limitError: string;
  };
  cheques: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    readOnlyNotice: string;
    noActionNotice: string;
    filtersTitle: string;
    listTitle: string;
    detailTitle: string;
    chequeNumber: string;
    drawer: string;
    drawerBank: string;
    bankBranch: string;
    chequeDate: string;
    dueDate: string;
    dueFrom: string;
    dueTo: string;
    amount: string;
    currency: string;
    cashbox: string;
    status: string;
    sourceVoucher: string;
    clearedTo: string;
    clearedDate: string;
    settlementVoucher: string;
    notes: string;
    allStatuses: string;
    statusUnderCollection: string;
    statusDeposited: string;
    statusCleared: string;
    statusBounced: string;
    statusReturned: string;
    depositAction: string;
    clearAction: string;
    bounceAction: string;
    returnAction: string;
    depositTitle: string;
    clearTitle: string;
    bounceTitle: string;
    returnTitle: string;
    depositConfirm: string;
    clearConfirm: string;
    bounceConfirm: string;
    returnConfirm: string;
    bankAccount: string;
    chooseBankAccount: string;
    clearedOn: string;
    actionNotes: string;
    confirm: string;
    cancel: string;
    deposited: string;
    cleared: string;
    bounced: string;
    returned: string;
    apply: string;
    reset: string;
    refresh: string;
    search: string;
    noRows: string;
    count: string;
    choose: string;
    none: string;
    actions: string;
    view: string;
    close: string;
    loadError: string;
    actionError: string;
  };
  receiptBooks: {
    title: string;
    breadcrumb: string;
    purposeNotice: string;
    noAccountingNotice: string;
    readOnlyNotice: string;
    closedNotice: string;
    noHoldersNotice: string;
    bookTitle: string;
    handoverTitle: string;
    historyTitle: string;
    listTitle: string;
    newBook: string;
    bookNumber: string;
    fromNumber: string;
    toNumber: string;
    receiptCount: string;
    receivedDate: string;
    status: string;
    statusAvailable: string;
    statusAssigned: string;
    statusClosed: string;
    allStatuses: string;
    currentHolder: string;
    holderType: string;
    holder: string;
    holderEmployee: string;
    holderCollector: string;
    holderSalesRep: string;
    assignmentDate: string;
    branch: string;
    notes: string;
    lastMovement: string;
    assignAction: string;
    receiveAction: string;
    closeAction: string;
    closeConfirm: string;
    save: string;
    moveAction: string;
    moveFrom: string;
    moveTo: string;
    moveDate: string;
    movePerformedBy: string;
    moveReceived: string;
    moveAssigned: string;
    moveReturned: string;
    moveClosed: string;
    company: string;
    openBook: string;
    refresh: string;
    search: string;
    noRows: string;
    count: string;
    choose: string;
    none: string;
    actions: string;
    saved: string;
    assigned: string;
    received: string;
    closed: string;
    loadError: string;
    saveError: string;
    actionError: string;
  };
  salesPurchaseTax: {
    title: string; breadcrumb: string; purposeNotice: string; reuseNotice: string;
    readOnlyNotice: string;
    formTitle: string; gridTitle: string; newRecord: string;
    code: string; descriptionAr: string; descriptionEn: string;
    fromDate: string; toDate: string; rate: string; category: string; account: string;
    side: string; sideFromCategory: string; sideSales: string; sidePurchase: string;
    debitAccount: string; creditAccount: string; active: string; defaultForSales: string;
    actions: string; edit: string; activate: string; deactivate: string;
    search: string; refresh: string; showInactive: string; noRows: string; counts: string;
    save: string; saveNew: string; cancel: string; choose: string; none: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    saved: string; activated: string; deactivated: string; defaultHint: string;
    loadError: string; saveError: string; auditError: string;
  };
  agingPeriods: {
    title: string; breadcrumb: string; purposeNotice: string; emptyNotice: string;
    notCalculatedNotice: string; readOnlyNotice: string;
    gridTitle: string; formTitle: string; newRow: string;
    sequence: string; fromDay: string; toDay: string; actions: string;
    edit: string; remove: string; removeConfirm: string; refresh: string;
    save: string; saveNew: string; cancel: string; noRows: string; count: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    saved: string; removed: string;
    loadError: string; saveError: string; auditError: string;
  };
  paymentOrders: {
    title: string; breadcrumb: string; purposeNotice: string; derivedNotice: string;
    readOnlyNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; newRecord: string; search: string; searchHint: string;
    refresh: string; noRows: string; statusFilter: string; allStatuses: string; payableOnly: string;
    number: string; date: string; supplier: string; chooseSupplier: string;
    requestedAmount: string; paidAmount: string; remainingAmount: string;
    currency: string; exchangeRate: string; branch: string; expectedDate: string;
    expectedMethod: string; responsible: string; reference: string; notes: string; none: string;
    status: string; open: string; cancelled: string;
    progress: string; unpaid: string; partiallyPaid: string; paid: string;
    save: string; cancelEdit: string; edit: string; view: string; cancelOrder: string;
    cancelReason: string; confirmCancel: string; close: string; details: string; createdAt: string;
    vouchers: string; voucherNumber: string; voucherDate: string; voucherStatus: string;
    voucherAmount: string; effective: string; notEffective: string; noVouchers: string;
    createVoucher: string;
    saved: string; cancelledOk: string;
    loadError: string; saveError: string; cancelError: string; totalCount: string;
  };
  paymentVouchers: {
    settlementTitle: string;
    settlementHint: string;
    settlementInvoice: string;
    settlementSupplierInvoice: string;
    settlementDate: string;
    settlementDue: string;
    settlementOriginal: string;
    settlementSettled: string;
    settlementOutstanding: string;
    settlementAmount: string;
    settlementAllocated: string;
    settlementUnapplied: string;
    settlementNone: string;
    settlementOverPayment: string;
    settlementEffectiveOnPost: string;
    settlementLoading: string;
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    chequeNotice: string; readOnlyNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    statusFilter: string; allStatuses: string;
    number: string; date: string; mode: string; modeSupplier: string; modeDirect: string;
    supplier: string; chooseSupplier: string; directAccount: string; chooseAccount: string;
    paymentOrder: string; noOrder: string; currency: string; exchangeRate: string; branch: string;
    costCenter: string; reference: string; notes: string; none: string; amount: string;
    paymentMethod: string; chooseMethod: string; lineAmount: string; lineNotes: string;
    addLine: string; removeLine: string; total: string;
    status: string; draft: string; audited: string; posted: string; reversed: string;
    save: string; cancelEdit: string; edit: string; view: string;
    audit: string; post: string; reverse: string; auditNotes: string;
    reversalDate: string; reversalReason: string; confirmReverse: string; close: string;
    details: string; createdAt: string; remainingOnOrder: string;
    saved: string; auditedOk: string; postedOk: string; reversedOk: string;
    loadError: string; saveError: string; auditError: string; postError: string;
    reverseError: string; totalCount: string; totalPosted: string;
    printAction: string; pdfAction: string; excelAction: string; exportFailed: string;
  };
  supplierDebitNotes: {
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    lineageNotice: string; readOnlyNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    statusFilter: string; allStatuses: string; totalCount: string; totalPosted: string;
    number: string; date: string; supplier: string; chooseSupplier: string;
    sourceInvoice: string; noSourceInvoice: string; currency: string; exchangeRate: string;
    branch: string; costCenter: string; reference: string; notes: string; none: string;
    taxInclusive: string;
    description: string; offsetAccount: string; chooseAccount: string; amount: string;
    taxType: string; noTax: string; taxPercent: string; tax: string; lineTotal: string;
    lineNotes: string; addLine: string; removeLine: string;
    net: string; total: string;
    status: string; draft: string; audited: string; posted: string; reversed: string;
    save: string; cancelEdit: string; edit: string; view: string;
    audit: string; post: string; reverse: string; auditNotes: string;
    reversalDate: string; reversalReason: string; confirmReverse: string; close: string;
    details: string; createdAt: string; createdBy: string;
    printAction: string; pdfAction: string; excelAction: string;
    saved: string; auditedOk: string; postedOk: string; reversedOk: string;
    loadError: string; saveError: string; auditError: string; postError: string;
    reverseError: string; exportFailed: string;
  };
  supplierCreditNotes: {
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    lineageNotice: string; readOnlyNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    statusFilter: string; allStatuses: string; totalCount: string; totalPosted: string;
    number: string; date: string; supplier: string; chooseSupplier: string;
    sourceInvoice: string; noSourceInvoice: string; currency: string; exchangeRate: string;
    branch: string; costCenter: string; reference: string; notes: string; none: string;
    taxInclusive: string;
    description: string; offsetAccount: string; chooseAccount: string; amount: string;
    taxType: string; noTax: string; taxPercent: string; tax: string; lineTotal: string;
    lineNotes: string; addLine: string; removeLine: string;
    net: string; total: string;
    status: string; draft: string; audited: string; posted: string; reversed: string;
    save: string; cancelEdit: string; edit: string; view: string;
    audit: string; post: string; reverse: string; auditNotes: string;
    reversalDate: string; reversalReason: string; confirmReverse: string; close: string;
    details: string; createdAt: string; createdBy: string;
    printAction: string; pdfAction: string; excelAction: string;
    saved: string; auditedOk: string; postedOk: string; reversedOk: string;
    loadError: string; saveError: string; auditError: string; postError: string;
    reverseError: string; exportFailed: string;
  };
  supplierAccountSettlement: {
    title: string; breadcrumb: string; notice: string; partyLabel: string;
    pickPartyFirst: string; polarityNotice: string; noSuppliersInScope: string;
    cancelTitle: string; cancelBreadcrumb: string; cancelNotice: string;
  };
  paymentBooks: {
    title: string; breadcrumb: string; purposeNotice: string; noAccountingNotice: string;
    historyNotice: string; readOnlyNotice: string;
    listTitle: string; formTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    statusFilter: string; allStatuses: string; counts: string; numbers: string;
    bookNumber: string; fromNumber: string; toNumber: string; count: string;
    branch: string; none: string; receivedDate: string; assignedDate: string;
    returnedDate: string; holder: string; chooseHolder: string; notes: string;
    status: string; available: string; assigned: string; closed: string;
    actions: string; edit: string; view: string; assign: string; takeBack: string; close: string;
    save: string; cancelEdit: string; movementDate: string; movementNotes: string;
    confirm: string; history: string; historyAction: string; historyFrom: string;
    historyTo: string; historyDate: string; historyBy: string; historyNotes: string;
    actionReceived: string; actionAssigned: string; actionReturned: string; actionClosed: string;
    details: string; noHolders: string;
    saved: string; movedOk: string;
    loadError: string; saveError: string; moveError: string; totalCount: string;
  };
  servicePurchaseReturn: {
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    boundNotice: string; readOnlyNotice: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    dateFrom: string; dateTo: string; statusFilter: string; allStatuses: string;
    number: string; date: string; sourceInvoice: string; chooseInvoice: string;
    supplier: string; currency: string; reference: string; notes: string; costCenter: string;
    none: string; branch: string;
    lineNo: string; expenseAccount: string; lineDescription: string;
    originalQuantity: string; returnedQuantity: string; remainingQuantity: string;
    returnQuantity: string; unitPrice: string; taxPercent: string;
    netAmount: string; taxAmount: string; totalAmount: string;
    status: string; draft: string; audited: string; posted: string; reversed: string;
    save: string; cancelEdit: string; edit: string; view: string;
    audit: string; post: string; reverse: string; auditNotes: string;
    reversalDate: string; reversalReason: string; confirmReverse: string; close: string;
    details: string; createdAt: string;
    saved: string; auditedOk: string; postedOk: string; reversedOk: string;
    loadError: string; saveError: string; auditError: string; postError: string;
    reverseError: string; totalCount: string; nothingReturnable: string;
    print: string; exportPdf: string; exportExcel: string; exportError: string;
    invoiceNotPosted: string; pickInvoiceFirst: string;
  };
  servicePurchaseInvoice: {
    title: string; breadcrumb: string; purposeNotice: string; postingNotice: string;
    accountNotice: string; readOnlyNotice: string; scopeNotice: string; noSuppliersInScope: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    dateFrom: string; dateTo: string; statusFilter: string; allStatuses: string;
    number: string; date: string; supplier: string; chooseSupplier: string;
    supplierInvoiceNumber: string; sourceRequest: string;
    currency: string; exchangeRate: string; branch: string; paymentMethod: string;
    costCenter: string; none: string; notes: string; pricing: string;
    taxInclusive: string; taxExclusive: string;
    expenseAccount: string; chooseAccount: string; lineDescription: string; unit: string;
    quantity: string; unitPrice: string; discountPercent: string; taxType: string;
    lineGross: string; lineDiscount: string; lineTax: string; lineNet: string;
    addLine: string; removeLine: string; taxComputedOnSave: string;
    subtotal: string; discountTotal: string; taxTotal: string; netTotal: string;
    status: string; draft: string; audited: string; posted: string; rejected: string;
    reversed: string;
    save: string; cancelEdit: string; edit: string; view: string;
    audit: string; post: string; reverse: string; auditNotes: string;
    reversalDate: string; reversalReason: string; confirmReverse: string; close: string;
    details: string; createdAt: string; settled: string;
    saved: string; auditedOk: string; postedOk: string; reversedOk: string;
    loadError: string; saveError: string; auditError: string; postError: string;
    reverseError: string; totalCount: string;
    print: string; exportPdf: string; exportExcel: string; exportError: string;
  };
  servicePurchaseRequest: {
    title: string; breadcrumb: string; purposeNotice: string; noAccountingNotice: string;
    readOnlyNotice: string; scopeNotice: string;
    listTitle: string; formTitle: string; linesTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; noRows: string;
    dateFrom: string; dateTo: string; statusFilter: string; allStatuses: string;
    number: string; date: string; requiredDate: string; supplier: string; chooseSupplier: string;
    currency: string; exchangeRate: string; branch: string; paymentMethod: string;
    requestedBy: string; costCenter: string; none: string; reference: string; notes: string;
    taxInclusive: string; taxExclusive: string; pricing: string;
    lineDescription: string; unit: string; quantity: string; unitPrice: string;
    discountPercent: string; discountType: string; discountValue: string; taxType: string;
    lineGross: string; lineDiscount: string; lineTax: string; lineNet: string;
    addLine: string; removeLine: string;
    subtotal: string; discountTotal: string; taxTotal: string; netTotal: string;
    status: string; draft: string; cancelled: string;
    save: string; saveNew: string; cancelEdit: string; edit: string; view: string;
    cancelRequest: string; cancelReason: string; confirmCancel: string; close: string;
    details: string; createdBy: string; createdAt: string; attachments: string;
    saved: string; cancelledOk: string;
    loadError: string; saveError: string; cancelError: string;
    linesRequired: string; totalCount: string; taxComputedOnSave: string;
    noSuppliersInScope: string;
  };
  personnel: {
    collectorsTitle: string; driversTitle: string; coordinatorsTitle: string;
    salesRepsTitle: string;
    collectorsPurpose: string; driversPurpose: string; coordinatorsPurpose: string;
    salesRepsPurpose: string;
    breadcrumb: string; identityNotice: string; noAccountingNotice: string;
    readOnlyNotice: string;
    listTitle: string; formTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; showInactive: string; noRows: string;
    branchFilter: string; allBranches: string; linkFilter: string; anyLink: string;
    linkedOnly: string; unlinkedOnly: string;
    code: string; nameAr: string; nameEn: string; user: string; noUser: string; userHint: string;
    mobile: string; phone: string; email: string; branch: string; noBranch: string;
    notes: string; active: string; actions: string;
    edit: string; view: string; activate: string; deactivate: string;
    save: string; saveNew: string; cancel: string;
    details: string; updatedAt: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    saved: string; activated: string; deactivated: string; counts: string; matched: string;
    loadError: string; saveError: string; auditError: string;
  };
  fixedAssetsMasterData: {
    common: {
      code: string; nameAr: string; nameEn: string; active: string; notes: string;
      actions: string; edit: string; activate: string; deactivate: string; delete: string;
      save: string; cancel: string; search: string; refresh: string; showInactive: string;
      noRows: string; readOnlyNotice: string; saved: string; activated: string;
      deactivated: string; deleted: string; loadError: string; saveError: string;
      parent: string; noParent: string; level: string; assets: string;
      inactiveByParent: string; deleteConfirm: string; none: string; required: string;
    };
    groups: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      treeTitle: string; formTitle: string; newRoot: string; newChild: string;
      accountsSection: string; depreciationSection: string;
      assetAccount: string; accumulatedAccount: string; expenseAccount: string;
      disposalGainAccount: string; disposalLossAccount: string;
      inheritedFrom: string; effective: string; notMapped: string; chooseAccount: string;
      method: string; straightLine: string; decliningBalance: string;
      usefulLifeMonths: string; ratePercent: string; salvagePercent: string;
      defaultsNotice: string;
    };
    locations: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      treeTitle: string; formTitle: string; newRoot: string; newChild: string;
      branch: string;
    };
    card: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      listTitle: string; newAsset: string; backToList: string;
      tabGeneral: string; tabAcquisition: string; tabDepreciation: string;
      tabLocation: string; tabAccounting: string; tabAttachments: string;
      assetNumber: string; group: string; location: string; branch: string; status: string;
      description: string;
      statusDraft: string; statusInService: string; statusSuspended: string;
      statusFullyDepreciated: string; statusSold: string; statusDisposed: string;
      statusWrittenOff: string; statusOperationOnly: string;
      acquisitionSource: string; sourcePurchase: string; sourceOpeningBalance: string;
      supplier: string; purchaseDate: string; inServiceDate: string; documentReference: string;
      documentReferenceHint: string;
      acquisitionCost: string; currency: string; exchangeRate: string; localCost: string;
      serialNumber: string; manufacturer: string; model: string; warrantyExpiry: string;
      method: string; usefulLifeMonths: string; ratePercent: string; salvageValue: string;
      depreciationStart: string; openingAccumulated: string; openingAccumulatedHint: string;
      depreciableAmount: string; accumulated: string; bookValue: string;
      derivedNotice: string; noEngineNotice: string; inheritFromGroup: string;
      inheritFromGroupHint: string;
      custodian: string; department: string;
      accountingNotice: string;
      attachmentsHint: string; upload: string; uploading: string; download: string;
      fileName: string; fileSize: string; uploadedAt: string; noAttachments: string;
      closedNotice: string;
      filterGroup: string; filterLocation: string; filterSupplier: string; filterStatus: string;
      totalCost: string; totalBookValue: string; count: string;
    };
  };
  /**
   * Fixed Assets › Daily Transactions — the sixteen approved movements.
   *
   * `common` carries what every screen shares, because a document lifecycle reads the same whether
   * it is a sale or a revaluation, and repeating "Approve" sixteen times invites sixteen wordings.
   * Everything below it is the vocabulary only that one transaction uses.
   */
  fixedAssetsTransactions: {
    common: {
      asset: string; selectAsset: string; date: string; notes: string; description: string;
      amount: string; currency: string; exchangeRate: string; localAmount: string;
      status: string; statusDraft: string; statusApproved: string; statusPosted: string;
      statusCancelled: string; statusReversed: string;
      approve: string; post: string; cancel: string; reverse: string; save: string;
      saveNew: string; newDocument: string; edit: string; close: string; refresh: string;
      search: string; none: string; actions: string; number: string; noRows: string;
      readOnlyNotice: string; loadError: string; saveError: string;
      saved: string; approvedMsg: string; postedMsg: string; cancelledMsg: string;
      reversedMsg: string; required: string;
      reasonPrompt: string; confirmPost: string; confirmReverse: string; confirmCancel: string;
      journalVoucher: string; notPosted: string; postedNotice: string;
      condition: string; conditionGood: string; conditionFair: string;
      conditionDamaged: string; conditionOutOfService: string;
      settlement: string; settlementSupplier: string; settlementCashOrBank: string;
      /** Sale and Revenue settle against a CUSTOMER, so "supplier" would misname the party. */
      settlementOnAccount: string;
      settlementAccount: string; supplier: string; customer: string; costCenter: string;
      branch: string; location: string; total: string; count: string; all: string;
      valuationTitle: string; valuationCost: string; valuationAccumulated: string;
      valuationNetBookValue: string; valuationSalvage: string; valuationDepreciable: string;
      valuationNotice: string;
    };
    purchaseOrders: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; orderNumber: string; orderDate: string; expectedDate: string;
      netTotal: string; availableOnly: string; invoiced: string; available: string;
      totalOnOrder: string; availableCount: string; createInvoice: string;
      orderStatus: string;
    };
    purchaseInvoices: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; invoiceNumber: string; invoiceDate: string;
      supplierInvoiceNumber: string; purchaseOrder: string; noPurchaseOrder: string;
      linesTitle: string; addLine: string; removeLine: string; assetName: string;
      assetNameAr: string; group: string; serialNumber: string; manufacturer: string;
      model: string; unitCost: string; lineTotal: string; subtotal: string;
      totalAcquired: string; assetCount: string; createdCards: string;
      linesRequired: string; postNotice: string;
    };
    custodyIssue: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; custodian: string; issuedOn: string;
      expectedReturnOn: string; conditionOnIssue: string; issue: string; issued: string;
      openOnly: string; openCount: string; overdueCount: string; overdue: string;
      returnedOn: string; conditionOnReturn: string; open: string; closed: string;
    };
    custodyReturn: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      formTitle: string; returnedOn: string; conditionOnReturn: string;
      returnToLocation: string; keepLocation: string; returnAction: string; returned: string;
      openCustodyTitle: string; noOpenCustody: string; heldBy: string; heldSince: string;
    };
    custodyTransfer: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      formTitle: string; fromCustodian: string; toCustodian: string; transferDate: string;
      transfer: string; transferred: string; historyTitle: string; movementType: string;
      movementIssue: string; movementReturn: string; movementTransfer: string;
      from: string; to: string; recordedAt: string;
    };
    locationMovements: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; fromLocation: string; toLocation: string;
      movementDate: string; reason: string; move: string; moved: string;
      currentLocation: string; noLocation: string; from: string; to: string;
    };
    insurance: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; insurer: string; policyNumber: string;
      startDate: string; endDate: string; insuredValue: string; premiumAmount: string;
      coverageType: string; premiumExpense: string; noPremiumExpense: string;
      policyStatus: string; statusActive: string; statusExpired: string;
      statusCancelled: string; expired: string; daysToExpiry: string;
      activeCount: string; expiringSoon: string; totalInsuredValue: string;
      premiumNotice: string;
    };
    maintenance: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; maintenanceNumber: string;
      maintenanceType: string; typePreventive: string; typeCorrective: string;
      typeEmergency: string; typeImprovement: string; maintenanceDate: string;
      serviceProvider: string; cost: string; costExpense: string; noCostExpense: string;
      nextMaintenanceDate: string; meterReading: string; conditionAfter: string;
      due: string; dueCount: string; totalCost: string; costNotice: string;
    };
    inventoryCounts: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; sheetTitle: string; countNumber: string; countDate: string;
      responsible: string; openCount: string; opened: string; complete: string;
      completed: string; countStatus: string; statusDraft: string; statusCompleted: string;
      statusCancelled: string; lineNumber: string; expectedLocation: string;
      actualLocation: string; result: string; resultNotCounted: string; resultFound: string;
      resultFoundElsewhere: string; resultMissing: string; discrepancy: string;
      resolved: string; markResolved: string; recordLine: string; recorded: string;
      totalLines: string; countedLines: string; foundCount: string;
      foundElsewhereCount: string; missingCount: string; unresolved: string;
      backToRegister: string; allLocations: string; changesNothingNotice: string;
      cancelCount: string;
    };
    depreciation: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; runTitle: string; runNumber: string; period: string;
      periodYear: string; periodMonth: string; runDate: string; group: string;
      allGroups: string; prepare: string; prepared: string; totalDepreciation: string;
      assetCount: string; postedCount: string; totalPosted: string;
      lineNumber: string; costBasis: string; salvageValue: string; method: string;
      methodStraightLine: string; methodDecliningBalance: string; usefulLifeMonths: string;
      ratePercent: string; openingAccumulated: string; charge: string;
      closingAccumulated: string; closingBookValue: string; capped: string;
      cappedNotice: string; backToRegister: string; noLines: string;
      preparedNotice: string;
    };
    annualDepreciation: {
      title: string; periodYear: string; fromMonth: string; toMonth: string;
      preview: string; execute: string; previewNotice: string; voucherDate: string;
      runCount: string; monthsCharged: string; category: string; voucherLines: string;
      account: string; debit: string; credit: string; total: string;
      confirmExecute: string; executed: string;
    };
    sales: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; saleNumber: string; saleDate: string;
      saleAmount: string; costAtSale: string; accumulatedAtSale: string;
      bookValueAtSale: string; gainOrLoss: string; gain: string; loss: string;
      totalProceeds: string; totalGainOrLoss: string; postedCount: string;
      snapshotNotice: string;
    };
    disposals: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; disposalNumber: string; disposalDate: string;
      reason: string; reasonDamaged: string; reasonObsolete: string; reasonLost: string;
      reasonStolen: string; reasonDonated: string; reasonOther: string;
      costAtDisposal: string; accumulatedAtDisposal: string; bookValueAtDisposal: string;
      totalWrittenOff: string; postedCount: string; reasonMasterNotice: string;
    };
    expenses: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; expenseNumber: string; expenseDate: string;
      expenseAccount: string; treatment: string; treatmentExpensed: string;
      treatmentCapitalised: string; totalExpensed: string; totalCapitalised: string;
      postedCount: string; allTreatments: string; treatmentNotice: string;
    };
    revenues: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; revenueNumber: string; revenueDate: string;
      revenueAccount: string; totalRevenue: string; postedCount: string;
      notASaleNotice: string;
    };
    revaluations: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; revaluationNumber: string;
      revaluationDate: string; previousCarryingAmount: string; revaluedAmount: string;
      difference: string; upward: string; downward: string; reason: string;
      totalUpward: string; totalDownward: string; postedCount: string;
      carryingNotice: string; carryingUnavailable: string;
    };
    splits: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; splitNumber: string; splitDate: string;
      postedCount: string;
      parentAsset: string; parentCost: string; parentAccumulated: string;
      parentBookValue: string; reason: string; childCount: string;
      linesTitle: string; addLine: string; removeLine: string; childAssetNumber: string;
      childName: string; childNameAr: string; group: string;
      allocatedCost: string; allocatedAccumulated: string; allocatedBookValue: string;
      allocatedTotals: string; balanced: string; notBalanced: string;
      balanceNotice: string; complete: string; completed: string;
      minimumLines: string; backToRegister: string;
    };
  };
  /**
   * The final eight of the registered Daily Transactions catalogue.
   *
   * Held apart from `fixedAssetsTransactions` only because it was added later; the screens share
   * that section's `common` block, so nothing here repeats a word already defined there.
   */
  fixedAssetsProcurement: {
    custodyDeposit: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      formTitle: string; registerTitle: string;
      keeper: string; depositedOn: string; depositLocation: string; keepLocation: string;
      conditionOnDeposit: string; expectedReturnOn: string;
      deposit: string; deposited: string;
      movementIssue: string; movementReturn: string; movementTransfer: string;
      movementDeposit: string; movementType: string;
      heldNotice: string; openCount: string;
    };
    purchaseReturns: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; returnNumber: string; returnDate: string;
      sourceInvoice: string; selectInvoice: string; supplierInvoice: string;
      reason: string; reasonDefective: string; reasonNotAsSpecified: string;
      reasonOverDelivered: string; reasonPurchaseCancelled: string; reasonOther: string;
      referenceNumber: string; returnAmount: string; originalCost: string;
      assetsTitle: string; returnable: string; notReturnable: string; blockedReason: string;
      selectAll: string; assetCount: string; totalReturned: string; postedCount: string;
      eligibilityNotice: string; postNotice: string; nothingReturnable: string;
      returnedStatus: string;
    };
    expenseReturns: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; returnNumber: string; returnDate: string;
      sourceExpense: string; selectExpense: string; sourceAmount: string;
      alreadyReturned: string; remainingReturnable: string; returnAmount: string;
      treatment: string; treatmentExpensed: string; treatmentCapitalised: string;
      reason: string; totalReturned: string; totalCapitalisedReturned: string;
      postedCount: string; capitalisedNotice: string; treatmentLockedNotice: string;
      noneReturnable: string;
    };
    transfers: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; transferNumber: string; transferDate: string;
      fromSide: string; toSide: string;
      fromLocation: string; toLocation: string; fromBranch: string; toBranch: string;
      fromGroup: string; toGroup: string; fromCustodian: string; toCustodian: string;
      keepGroup: string; keepLocation: string; keepBranch: string; keepCustodian: string;
      reason: string;
      transferStatus: string; statusDraft: string; statusApproved: string;
      statusDispatched: string; statusReceived: string; statusCancelled: string;
      dispatch: string; dispatched: string; receive: string; received: string;
      dispatchedOn: string; receivedOn: string;
      conditionOnDispatch: string; conditionOnReceipt: string;
      inTransit: string; inTransitCount: string; receivedCount: string;
      reclassified: string; reclassifiedCount: string;
      willReclassify: string; willNotReclassify: string;
      costAtTransfer: string; accumulatedAtTransfer: string;
      reclassificationNotice: string; receiptNotice: string; dispatchNotice: string;
    };
    worksheet: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      selectCount: string; sheetTitle: string; countNumber: string; countDate: string;
      responsible: string; discrepanciesOnly: string; print: string;
      lineNumber: string; serialNumber: string; group: string; custodian: string;
      expectedLocation: string; actualLocation: string; result: string; difference: string;
      notCounted: string; counted: string; discrepancies: string; unresolved: string;
      resolved: string; readOnlyNotice: string; noCountSelected: string;
    };
    rfq: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      registerTitle: string; formTitle: string; requestNumber: string; requestDate: string;
      requiredDate: string; closingDate: string; subject: string; terms: string;
      purpose: string; purposeGeneral: string; purposeFixedAsset: string;
      assetGroup: string; intendedLocation: string;
      requestStatus: string; statusDraft: string; statusIssued: string;
      statusAwarded: string; statusCancelled: string;
      linesTitle: string; addLine: string; removeLine: string;
      description: string; specification: string; unit: string; quantity: string;
      suppliersTitle: string; addSupplier: string; invitedOn: string; responded: string;
      notResponded: string; supplierCount: string; quotationCount: string;
      issue: string; issued: string; issueNotice: string;
      draftCount: string; issuedCount: string; awardedCount: string;
      noAccountingNotice: string; awardedTo: string;
    };
    quotations: {
      title: string; sectionTitle: string; formTitle: string;
      supplier: string; supplierReference: string; quotationDate: string; validUntil: string;
      lapsed: string; deliveryDays: string; paymentTermDays: string;
      commercialNotes: string; technicalNotes: string;
      unitPrice: string; discount: string; taxPercent: string; lineTotal: string;
      netTotal: string; record: string; recorded: string; withdraw: string; withdrawn: string;
      quotationStatus: string; statusReceived: string; statusWithdrawn: string;
      notInvitedNotice: string; issueFirstNotice: string; noQuotations: string;
      liveCount: string; answersLine: string;
    };
    comparison: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      matrixTitle: string; selectRequest: string; noRequestSelected: string;
      requestedLine: string; lowest: string; lowestOverall: string; awarded: string;
      didNotQuote: string; partialOffer: string;
      invited: string; responded: string; currencyWarning: string;
      notComparable: string; ownCurrency: string; comparableTotal: string;
      delivery: string; paymentTerms: string; validity: string; print: string;
      noQuotationsNotice: string; readOnlyNotice: string;
    };
    award: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      selectRequest: string; wholeAward: string; splitAward: string; awardMode: string;
      awardDate: string; awardReason: string; award: string; awarded: string;
      chooseQuotation: string; chooseePerLine: string; awardedQuantity: string;
      alreadyAwardedNotice: string; noAccountingNotice: string;
      awardedBy: string; awardedOn: string; unawarded: string;
      confirmAward: string; feedsOrderNotice: string;
    };
  };
  /**
   * Fixed Assets › Inquiries — the eight registered screens.
   *
   * `screens` is keyed by what the catalogue calls each one, so a rename in the registry has one
   * place to land here rather than eight.
   */
    hrEmployeeReports: { category: string; documentsCategory: string };
    hrInquiries: {
      title: string; description: string;
      filters: string; all: string; none: string;
      apply: string; reset: string; refresh: string; search: string;
      loadFailed: string; noResults: string;
      employee: string; employeeNumber: string; fromDate: string; toDate: string;
      department: string; status: string; jobTitle: string; hireDate: string;
      responsibleEntity: string; activeOnly: string;
      number: string; documentTitle: string; documentType: string; documentNumber: string;
      issueDate: string; expiryDate: string; daysToExpiry: string;
      expiryWindow: string; alreadyExpired: string; withinDays: string;
      noExpiry: string; expiredSince: string;
      effectiveDate: string; changeKind: string; sourceDocument: string; notes: string;
      transactionKind: string;
      assetNumber: string; assetName: string; handedOver: string; dueBack: string;
      returned: string; custodyState: string; stillOut: string;
      branch: string; asOfDate: string; withinDaysFilter: string; totals: string;
      print: string; exportExcel: string;
      pageOf: string; nextPage: string; previousPage: string;
      activeEmployeesOnly: string;
      contractType: string; contractStatus: string;
      expiryFrom: string; expiryTo: string;
      contractStatuses: Record<number, string>;
      changeKinds: Record<number, string>;
      employmentStatuses: Record<number, string>;
      screens: {
        jobTitleChanges: { title: string; description: string };
        departureRequests: { title: string; description: string };
        departureAmendments: { title: string; description: string };
        iqamaMovements: { title: string; description: string };
        healthCertificates: { title: string; description: string };
        disciplinaryActions: { title: string; description: string };
        promotions: { title: string; description: string };
        transfers: { title: string; description: string };
        overtimeRequests: { title: string; description: string };
        companyDocuments: { title: string; description: string };
        employeeDirectory: { title: string; description: string };
        employeeActions: { title: string; description: string };
        employeeCustody: { title: string; description: string };
        dailyRegister: { title: string; description: string };
      };
    };
    hrSettings: {
      title: string; description: string;
      cardGroup: string; dailyGroup: string; rulesGroup: string;
      add: string; edit: string; save: string; saving: string; cancel: string; search: string;
      code: string; nameAr: string; nameEn: string; status: string;
      active: string; retired: string; activate: string; deactivate: string;
      newEntry: string; editEntry: string; emptyList: string; noPermission: string;
      saved: string; activated: string; deactivated: string;
      loadFailed: string; saveFailed: string;
      groupIdentity: string; groupNature: string; groupDurations: string;
      groupEntitlement: string; groupEntitlementHint: string;
      screens: {
        leaveTypes: { title: string; description: string };
        religions: { title: string; description: string };
        departments: { title: string; description: string };
        jobTitles: { title: string; description: string };
        employmentTypes: { title: string; description: string };
        technicalClassifications: { title: string; description: string };
        grades: { title: string; description: string };
        employeeClassifications: { title: string; description: string };
        contractTypes: { title: string; description: string };
        socialInsurancePolicies: { title: string; description: string };
        healthInsurancePolicies: { title: string; description: string };
        departureTypes: { title: string; description: string };
        terminationReasons: { title: string; description: string };
        violationTypes: { title: string; description: string };
        disciplinaryActionTypes: { title: string; description: string };
        employeeDocumentTypes: { title: string; description: string };
        letterTypes: { title: string; description: string };
        injuryTypes: { title: string; description: string };
        medicalRecordTypes: { title: string; description: string };
        companyDocumentTypes: { title: string; description: string };
        policyCategories: { title: string; description: string };
        clearanceDepartments: { title: string; description: string };
      };
    };
    fixedAssetsSettings: {
      search: string; add: string; edit: string; save: string; saving: string; cancel: string;
      choose: string; none: string; active: string; inactive: string; inUse: string;
      activate: string; deactivate: string; activated: string; deactivated: string; saved: string;
      emptyList: string; noPermission: string; readOnlyNotice: string;
      newEntry: string; editEntry: string; saveFailed: string;
      treatmentHint: string; classificationHint: string; defaultAccountHint: string;
      textHint: string;
      columns: {
        code: string; name: string; nameAr: string; treatment: string; classification: string;
        defaultAccount: string; text: string; notes: string; status: string;
      };
      treatment: { expensed: string; capitalised: string };
      disposalReason: Record<1 | 2 | 3 | 4 | 5 | 99, string>;
      frequency: { monthly: string };
      groups: { depreciation: string; documents: string; numbering: string };
      fields: {
        frequency: string; frequencyHint: string;
        defaultUsefulLife: string; defaultUsefulLifeHint: string;
        defaultSalvage: string; defaultSalvageHint: string;
        requireDisposalReason: string; requireDisposalReasonHint: string;
        requireExpenseType: string; requireExpenseTypeHint: string;
        autoNumber: string; autoNumberHint: string;
        prefix: string; padding: string; paddingHint: string;
        nextValue: string; nextValueHint: string; preview: string;
      };
      screens: Record<
        "assetModels" | "brands" | "assetClasses" | "assetCategories" | "assetProperties"
        | "shippingMethods" | "expenseTypes" | "salesTypes" | "disposalReasons" | "assetTypes"
        | "deliveryMethods" | "purchaseRoutes" | "quotationTerms" | "system" | "coding",
        { title: string; description: string }
      >;
    };
  fixedAssetsInquiries: {
    readOnly: string;
    noRows: string;
    noCostCentre: string;
    voucherSeriesMismatchHint: string;
    screens: {
      lifeCycle: { title: string; description: string; assetRequired: string };
      financialMovements: { title: string; description: string };
      movements: { title: string; description: string };
      expenses: { title: string; description: string };
      records: { title: string; description: string };
      transferStatus: { title: string; description: string };
      tree: { title: string; description: string };
      dailyTransactions: { title: string; description: string };
    };
    filters: {
      dateFrom: string; dateTo: string; asOfDate: string;
      asset: string; chooseAsset: string; allAssets: string;
      group: string; allGroups: string; wholeTree: string;
      location: string; allLocations: string;
      supplier: string; allSuppliers: string;
      branch: string; allBranches: string;
      custodian: string; allCustodians: string;
      costCentre: string; allCostCentres: string;
      status: string; allStatuses: string;
      transferStatus: string;
      treatment: string; allTreatments: string;
      event: string; allEvents: string;
      search: string;
      includeUnposted: string; postedOnly: string;
      periodFrom: string; periodTo: string;
      includeChildGroups: string; includeChildLocations: string;
      detailedView: string;
      includeClosed: string; includeEmptyGroups: string; inTransitOnly: string;
    };
    columns: {
      date: string; event: string; document: string; asset: string; description: string;
      from: string; to: string; status: string;
      debit: string; credit: string; costChange: string; depreciation: string;
      bookValue: string; voucher: string;
      assetNumber: string; assetName: string; serialNumber: string;
      group: string; location: string; custodian: string; supplier: string;
      acquired: string; acquisitionCost: string; additions: string; revaluation: string;
      cost: string; accumulated: string;
      code: string; level: string; ownAssets: string; ownCost: string;
      totalAssets: string; totalCost: string;
      treatment: string; expenseAccount: string; costCentre: string;
      original: string; returned: string; net: string;
      fromGroup: string; toGroup: string;
      dispatched: string; received: string; daysInTransit: string;
    };
    figures: {
      events: string; transactions: string; assets: string; groups: string;
      expenses: string; transfers: string;
      acquired: string; closed: string; daysHeld: string;
      cost: string; accumulated: string; bookValue: string;
      acquisitionCost: string; additions: string; revaluation: string;
      totalDebit: string; totalCredit: string;
      costIncrease: string; costDecrease: string; totalDepreciation: string;
      closingBookValue: string;
      custodyEvents: string; locationEvents: string; transferEvents: string;
      inventoryEvents: string;
      posted: string; unposted: string; operational: string;
      original: string; returned: string; operating: string; capitalised: string; net: string;
      reclassified: string; costMoved: string;
    };
    kinds: {
      acquisition: string; purchaseReturn: string;
      custodyIssue: string; custodyDeposit: string; custodyReturn: string; custodyTransfer: string;
      locationMovement: string; transfer: string;
      insurance: string; maintenance: string; inventoryCount: string;
      depreciation: string; expense: string; expenseReturn: string; revenue: string;
      revaluation: string; sale: string; disposal: string; split: string;
    };
    assetStatus: {
      draft: string; inService: string; suspended: string; fullyDepreciated: string;
      sold: string; disposed: string; writtenOff: string; returned: string;
    };
    transferStatus: {
      draft: string; approved: string; dispatched: string; received: string; cancelled: string;
    };
    treatment: { expensed: string; capitalised: string };
  };
  cashBanksSettings: {
    scoped: {
      financialIntegrationTitle: string; financialIntegrationBreadcrumb: string;
      financialIntegrationNotice: string;
      reportSignaturesTitle: string; reportSignaturesBreadcrumb: string;
      reportSignaturesNotice: string;
      bankOpeningTitle: string; bankOpeningBreadcrumb: string; bankOpeningNotice: string;
      cashboxOpeningTitle: string; cashboxOpeningBreadcrumb: string; cashboxOpeningNotice: string;
      sharedScreenNotice: string;
    };
    common: {
      code: string; nameAr: string; nameEn: string; displayOrder: string; active: string;
      notes: string; actions: string; edit: string; activate: string; deactivate: string;
      save: string; saveNew: string; cancel: string; newRecord: string;
      search: string; refresh: string; showInactive: string; noRows: string;
      counts: string; usage: string; readOnlyNotice: string;
      saved: string; activated: string; deactivated: string;
      loadError: string; saveError: string; formTitle: string; listTitle: string;
    };
    cashboxGroups: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      listTitle: string; newRecord: string; assignedCashboxes: string;
    };
    cashboxCashFloat: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      listTitle: string; formTitle: string; newRecord: string;
      cashbox: string; selectCashbox: string; currency: string; selectCurrency: string;
      floatAmount: string; effectiveFrom: string; custodianAr: string; custodianEn: string;
      currentBalance: string; variance: string; noAccount: string; overFloat: string;
      totalAuthorised: string; overFloatCount: string;
    };
    letterOfCreditTypes: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      listTitle: string; newRecord: string; defaultMargin: string;
    };
    chequeReturnReasons: {
      title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
      listTitle: string; newRecord: string; drawerFault: string; drawerFaultHint: string;
    };
    userCashboxAccess: {
      title: string; breadcrumb: string; purposeNotice: string; enforcementOffNotice: string;
      enforcementOnNotice: string; readOnlyNotice: string;
      userSection: string; user: string; selectUser: string; userInactive: string;
      searchUsers: string; grantsSection: string; cashboxCode: string; cashboxName: string;
      allowed: string; grantedAt: string; cashboxInactive: string;
      grantAll: string; revokeAll: string; noUser: string; noRows: string;
      grantedCount: string; saved: string; loadError: string; saveError: string;
    };
    system: {
      title: string; breadcrumb: string; purposeNotice: string; prospectiveNotice: string;
      readOnlyNotice: string;
      cashboxSection: string; chequeSection: string; bankSection: string;
      preventNegative: string; preventNegativeHint: string;
      enforceAccess: string; enforceAccessHint: string; enforceAccessEmpty: string;
      defaultCashbox: string; defaultBankAccount: string; none: string;
      requireLeaf: string; requireLeafHint: string;
      requireReason: string; requireReasonHint: string; requireReasonEmpty: string;
      defaultTerm: string; termImmediate: string; termPostDated: string;
      save: string; restoreDefaults: string; restoreConfirm: string;
      saved: string; restored: string; loadError: string; saveError: string;
    };
  };
  custodyCategories: {
    title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
    readOnlyNotice: string;
    listTitle: string; formTitle: string; newRecord: string;
    search: string; refresh: string; showInactive: string; noRows: string;
    code: string; descriptionAr: string; descriptionEn: string; active: string;
    actions: string; edit: string; activate: string; deactivate: string;
    save: string; saveNew: string; cancel: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    saved: string; activated: string; deactivated: string; counts: string;
    loadError: string; saveError: string; auditError: string;
  };
  countryRegions: {
    title: string; breadcrumb: string; purposeNotice: string; timeZoneNotice: string;
    readOnlyNotice: string;
    treeTitle: string; formTitle: string; newRoot: string; newChild: string;
    search: string; refresh: string; showInactive: string; noRows: string;
    parent: string; noParent: string; code: string; descriptionAr: string; descriptionEn: string;
    nationalityAr: string; nationalityEn: string; currency: string; timeZone: string;
    shortCode: string; internationalCode: string; mobileDigits: string; documentDigits: string;
    isChild: string; active: string; none: string;
    flagSection: string; flagUpload: string; flagReplace: string; flagRemove: string;
    flagNone: string; flagHint: string; flagRemoveConfirm: string;
    save: string; saveNew: string; cancel: string; deactivate: string; activate: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    country: string; region: string; level: string; children: string;
    saved: string; activated: string; deactivated: string; flagSaved: string; flagRemoved: string;
    loadError: string; saveError: string; auditError: string; flagError: string;
  };
  userSupplierAccess: {
    title: string; breadcrumb: string; purposeNotice: string; denyByDefaultNotice: string;
    readOnlyNotice: string; notIntegratedNotice: string;
    userSection: string; user: string; selectUser: string; userInactive: string;
    dailySection: string; inquirySection: string; reportSection: string;
    partnerCode: string; partnerName: string; allowed: string; actions: string;
    add: string; remove: string; search: string; refresh: string;
    noRows: string; noUser: string; partnerInactive: string;
    pickerTitle: string; pickerSearch: string; pickerNone: string; cancel: string;
    confirmRemove: string; granted: string; removed: string; toggled: string;
    auditHistory: string; auditNone: string; auditAction: string; auditChangedBy: string;
    auditChangedAt: string; auditFields: string; close: string;
    loadError: string; saveError: string; auditError: string;
  };
  userCustomerAccess: {
    title: string; breadcrumb: string; purposeNotice: string; denyByDefaultNotice: string;
    readOnlyNotice: string; notIntegratedNotice: string;
    userSection: string; user: string; selectUser: string; userInactive: string;
    dailySection: string; inquirySection: string; reportSection: string;
    partnerCode: string; partnerName: string; allowed: string; actions: string;
    add: string; remove: string; search: string; refresh: string;
    noRows: string; noUser: string; partnerInactive: string;
    pickerTitle: string; pickerSearch: string; pickerNone: string; cancel: string;
    confirmRemove: string; granted: string; removed: string; toggled: string;
    auditHistory: string; auditNone: string; auditAction: string; auditChangedBy: string;
    auditChangedAt: string; auditFields: string; close: string;
    loadError: string; saveError: string; auditError: string;
  };
  smsSettings: {
    title: string; breadcrumb: string; purposeNotice: string; unavailableNotice: string;
    readOnlyNotice: string; shortMonthNotice: string;
    scheduleSection: string; day: string; hour: string; minute: string;
    windowSection: string; startMonth: string; endMonth: string;
    messageSection: string; messageText: string; messageHint: string;
    automaticSending: string; active: string;
    save: string; cancel: string; restoreDefaults: string; restoreConfirm: string;
    auditHistory: string; auditNone: string; auditFields: string; auditChangedBy: string;
    auditChangedAt: string; close: string; lastChanged: string;
    saved: string; restored: string;
    loadError: string; saveError: string; auditError: string;
  };
  ePaymentPolicy: {
    title: string; breadcrumb: string; purposeNotice: string; readOnlyNotice: string;
    sections: string; cardTypesSection: string; commissionSection: string;
    newCardType: string; edit: string;
    code: string; descriptionAr: string; descriptionEn: string; active: string; inactive: string;
    usage: string; list: string; search: string; searchPlaceholder: string; showInactive: string;
    refresh: string; save: string; saveAndNew: string; cancel: string;
    activate: string; deactivate: string; actions: string; empty: string;
    cardType: string; branch: string; chooseCardType: string; chooseBranch: string;
    ranges: string; fromAmount: string; toAmount: string; openEnded: string;
    commissionPercent: string; commissionAmount: string; addRange: string; removeRange: string;
    policyList: string; noPolicies: string; rangesCount: string;
    savePolicy: string; editPolicy: string; newPolicy: string;
    saved: string; policySaved: string; activated: string; deactivated: string;
    loadError: string; saveError: string;
    configurationNotice: string;
  };
  discountTypesScreen: {
    title: string; breadcrumb: string; purposeNotice: string; readOnlyNotice: string;
    newType: string; edit: string;
    code: string; descriptionAr: string; descriptionEn: string;
    calculationType: string; percentage: string; fixedAmount: string;
    account: string; accountHint: string;
    isDefault: string; active: string; inactive: string;
    list: string; search: string; searchPlaceholder: string; showInactive: string;
    refresh: string; save: string; saveAndNew: string; cancel: string;
    activate: string; deactivate: string; actions: string; empty: string;
    auditHistory: string; auditNone: string; auditFields: string; auditAction: string;
    auditChangedBy: string; auditChangedAt: string; close: string; lastChanged: string;
    yes: string; no: string;
    saved: string; activated: string; deactivated: string;
    loadError: string; saveError: string; auditError: string;
  };
  workflowActions: {
    title: string; breadcrumb: string; purposeNotice: string; scopeNotice: string;
    readOnlyNotice: string;
    newAction: string; edit: string;
    code: string; descriptionAr: string; descriptionEn: string; sequence: string;
    mandatory: string; active: string; inactive: string;
    list: string; search: string; searchPlaceholder: string; showInactive: string;
    refresh: string; save: string; saveAndNew: string; cancel: string;
    activate: string; deactivate: string; actions: string; empty: string;
    auditHistory: string; auditNone: string; auditFields: string; auditAction: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    lastChanged: string; yes: string; no: string;
    saved: string; activated: string; deactivated: string;
    loadError: string; saveError: string; auditError: string;
  };
  receivablesLinks: {
    title: string; breadcrumb: string; purposeNotice: string; readOnlyNotice: string;
    unlinkedNotice: string;
    accountsSection: string; account: string; linkLevel: string; detailPreview: string;
    actions: string; companyLevel: string; companyLevelFixed: string;
    linked: string; notLinked: string; linkedOf: string;
    edit: string; clearLink: string; clearConfirm: string; save: string; cancel: string;
    search: string; searchPlaceholder: string; refresh: string; print: string;
    auditHistory: string; auditNone: string; auditFields: string; auditAction: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    chooseAccount: string; accountHint: string; requiredTypeHint: string;
    detailsTitle: string; detailLinkType: string; detailLinkLevel: string; detailCompany: string;
    detailAccountNumber: string; detailAccountNameAr: string; detailAccountNameEn: string;
    detailStatus: string; detailChangedBy: string; detailChangedAt: string;
    active: string; inactive: string;
    saved: string; cleared: string;
    loadError: string; saveError: string; printError: string; auditError: string;
    typeExpense: string; typeRevenue: string;
  };
  receivablesSettings: {
    title: string; breadcrumb: string; purposeNotice: string; readOnlyNotice: string;
    pendingModulesNotice: string;
    description: string; value: string;
    sectionCustomerCard: string; sectionReceipts: string; sectionAging: string;
    sectionFinancing: string; sectionPaymentTax: string; sectionCheques: string;
    save: string; cancel: string; restoreDefaults: string; restoreConfirm: string;
    auditHistory: string; auditNone: string; auditFields: string; auditSetting: string;
    auditOldValue: string; auditNewValue: string; auditChangedBy: string; auditChangedAt: string;
    close: string; yes: string; no: string; choose: string; none: string;
    lastChanged: string;
    saved: string; restored: string;
    loadError: string; saveError: string; auditError: string;

    requireNationalOrTaxNumber: string;
    requireCustomerCategory: string;
    requireAddressAndMobile: string;
    requireCustomerTypeClassAndNature: string;
    defaultCustomerCreditLimit: string;
    defaultCustomerPaymentMethod: string;
    defaultCustomerPriceTaxTreatment: string;
    hideCollectorFromVouchers: string;
    linkReferenceWithCollector: string;
    electronicPaymentSalesRepresentative: string;
    enableReceiptCommissionEntry: string;
    allowedSettlementDifference: string;
    maximumDeliveriesToSamePerson: string;
    minimumCustomerResponsePercentage: string;
    legalSettlementAlertDays: string;
    paymentMethodEnforcementSource: string;
    createCustomerReceivablesForChequesInCollection: string;
    customerAgingGracePeriodDays: string;
    receivablesOpeningBalanceDate: string;
    useOpeningBalanceDateAsAgingStart: string;
    agingInvoiceDateBasis: string;
    showNotYetDueAndAdvancePaymentsInAging: string;
    customerFinancingType: string;
    accountingFinancingType: string;
    financingCostBasis: string;
    deferredInterestBasis: string;
    decliningInterestMultiplier: string;
    decliningInterestOnBalanceBeforePayment: string;
    createIntermediateAccountInFinancingRequest: string;
    promissoryNoteDelayPenalty: string;
    hideCommissionFieldsInScheduling: string;

    financingFixed: string; financingDeclining: string;
    costBasisCurrencies: string;
    interestMonthly: string;
    enforcementCustomerCard: string;
    agingByInvoiceDate: string; agingByDueDate: string;
    paymentCash: string; paymentCredit: string;
    taxInclusive: string; taxExclusive: string; taxExempt: string;
  };
  partnerLookups: {
    title: string; breadcrumb: string; lockedBreadcrumb: string;
    purposeNotice: string; readOnlyNotice: string;
    vocabulary: string; allVocabularies: string; newValue: string; edit: string;
    code: string; descriptionAr: string; descriptionEn: string; active: string; inactive: string;
    collector: string; noCollector: string; taxCode: string;
    usage: string; list: string; search: string; searchPlaceholder: string; showInactive: string;
    refresh: string; save: string; cancel: string; activate: string; deactivate: string;
    actions: string; empty: string; inUseHint: string;
    saved: string; activated: string; deactivated: string;
    loadError: string; saveError: string;
    types: {
      category: string; importance: string; partnerType: string; class: string; title: string;
      nature: string; source: string; nationality: string; parentClassification: string;
      reportTemplate: string; salePriceType: string; commercialActivity: string; documentType: string;
    };
  };
  partners: {
    delete: string; deleteConfirm: string; deleted: string; deleteError: string;
    settlementActiveLabel: string; settlementCancelledOnCard: string;
    settlementsTitle: string; settlementInvoice: string; settlementReceipt: string;
    settlementAmountCol: string; settlementDateCol: string; settlementStatusCol: string;
    settlementNoneOnCard: string; settlementReadOnly: string;
    customersTitle: string; suppliersTitle: string;
    customersBreadcrumb: string; suppliersBreadcrumb: string;
    customerSingular: string; supplierSingular: string;
    purposeNotice: string; readOnlyNotice: string;

    sectionBasic: string; sectionFinancial: string; sectionContact: string;
    sectionLink: string; sectionAddress: string;

    code: string; nameAr: string; nameEn: string; startDate: string;
    category: string; importance: string; partnerTypeLookup: string; class: string; title: string;
    /** The same two fields as they read on a SUPPLIER card. Presentation only — one shared column. */
    supplierTypeLookup: string; supplierClass: string;
    nature: string; source: string; nationality: string; parentClassification: string;
    reportTemplate: string; parentPartner: string; noParent: string;
    nationalIdIndividual: string; nationalIdCompany: string; commercialRegistration: string;
    referenceNumber: string; notes: string;

    controlAccount: string; controlAccountHint: string; currency: string; creditLimit: string;
    creditSectionTitle: string; creditLimitLocked: string; creditNoEnforcement: string;
    creditPickCustomer: string;
    currentBalance: string; balanceExceedsLimit: string; changeCreditLimit: string;
    newCreditLimit: string; changeReason: string; creditHistoryTitle: string;
    previousLimit: string; newLimit: string; changedBy: string; changedAt: string;
    balanceAtChange: string; noCreditHistory: string; creditLimitChanged: string;
    confirmCreditChange: string; creditZeroAccounting: string;
    taxTreatment: string; taxInclusive: string; taxExclusive: string; taxExempt: string;
    paymentMethod: string; paymentCash: string; paymentCredit: string;
    defaultPaymentMethod: string; defaultPaymentMethodHint: string;
    salePriceType: string; taxNumber: string; turnoverRate: string; turnoverRateHint: string;

    salesRepresentative: string; marketer: string; collector: string; employee: string;
    owner: string; staffHint: string;

    phone: string; mobile: string; fax: string; email: string; website: string;
    contactPerson: string;

    country: string; city: string; area: string; street: string; building: string;
    postalCode: string; poBox: string; address: string; addressNotes: string;
    latitude: string; longitude: string; coordinatesHint: string;

    active: string; inactive: string; blacklisted: string; blacklistReason: string;
    blacklistHint: string;

    newRecord: string; edit: string; save: string; saveAndNew: string; cancel: string;
    print: string; auditHistory: string; activate: string; deactivate: string;
    list: string; search: string; searchPlaceholder: string; showInactive: string;
    inquiry: string; inquiryTitle: string; inquiryEmpty: string;
    refresh: string; actions: string; empty: string; status: string; choose: string;
    chooseCountryFirst: string;
    manageLookups: string;

    auditNone: string; auditFields: string; auditCreated: string; auditUpdated: string;
    auditDeleted: string; close: string;

    saved: string; activated: string; deactivated: string;
    loadError: string; saveError: string; printError: string; auditError: string;

    identificationRequired: string; categoryRequired: string;
    addressAndMobileRequired: string; typeClassNatureRequired: string;
  };
  common: {
    loading: string;
    notFound: string;
    notFoundHint: string;
    retry: string;
    error: string;
    errorTitle: string;
    dismiss: string;
    optional: string;
    cancel: string;
  };
  /**
   * Financial Management › Payments › Inquiries — shared labels.
   *
   * Nine screens share one vocabulary so the same column never gets two names. Anything genuinely
   * specific to one screen lives in that screen's own block below.
   */
  paymentsReports: {
    supplierBlacklist: string; blacklisted: string; notBlacklisted: string; reason: string;
    includeAllowed: string; noBlacklisted: string; flagNote: string;
    expiringDocuments: string; party: string; partyType: string; customer: string;
    supplier: string; documentType: string; documentNumber: string; issuedBy: string;
    issued: string; expires: string; daysLeft: string; withinDays: string;
    includeExpired: string; expired: string; expiringSoon: string; noDocuments: string;
    expiryNote: string;
    infoChanges: string; whenChanged: string; action: string; changedColumns: string;
    user: string; created: string; updated: string; parties: string; noChanges: string;
    auditNote: string;
    ageing: string; currencyAgeing: string; current: string; over120: string;
    undated: string; ageByDueDate: string; byCurrency: string; oldestDocument: string;
    documentsCount: string; grandTotal: string; noOutstanding: string; bucketNote: string;
    trialBalance: string; opening: string; periodDebit: string; periodCredit: string;
    closingDebit: string; closingCredit: string; customers: string; suppliers: string;
    noBalances: string; ledgerNote: string;
    orders: string; side: string; receiptOrder: string; paymentOrder: string;
    orderNumber: string; orderAmount: string; settled: string; remaining: string;
    outstandingOnly: string; noOrders: string; ordersNote: string;
    guarantees: string; facilityCode: string; bank: string; ceiling: string; rate: string;
    ceilingExceedable: string; includeAllFacilities: string; noGuarantees: string;
    guaranteeNote: string;
    invoicesByType: string; groupBy: string; byItem: string; byAccount: string;
    invoices: string; lines: string; sharePercent: string; noInvoices: string;
    byTypeNote: string;
    capitalInvoices: string; assetAccount: string; noCapitalInvoices: string;
    capitalNote: string;
    byAccountStatement: string; accountNumber: string; accountName: string;
    movements: string; bySupplier: string; accounts: string; noMovements: string;
    accountsNote: string;
  };
  paymentsSettings: {
    signaturesTitle: string; signaturesBreadcrumb: string; signaturesNotice: string;
    criteriaTitle: string; criteriaBreadcrumb: string; criteriaNotice: string;
    weightNotice: string;
    code: string; title: string; titleAr: string; signatoryName: string; signatoryNameAr: string;
    module: string; allModules: string; displayOrder: string; notes: string;
    name: string; nameAr: string; weight: string; maximumScore: string; description: string;
    descriptionAr: string; totalWeight: string;
    newRecord: string; editRecord: string; save: string; cancel: string; edit: string;
    activate: string; deactivate: string; actions: string; status: string; active: string;
    inactive: string; search: string; searchHint: string; refresh: string;
    noSignatures: string; noCriteria: string; saved: string; showInactive: string;
  };
  purchaseOrders: {
    title: string; breadcrumb: string; purposeNotice: string; noAccountingNotice: string;
    newRecord: string; editRecord: string; searchHint: string; noRows: string; details: string;
    orderNumber: string; orderDate: string; supplier: string; chooseSupplier: string;
    branch: string; currency: string; expectedDate: string; paymentTerm: string;
    costCenter: string; supplierReference: string; taxInclusive: string; taxInclusiveHint: string;
    lines: string; addLine: string; removeLine: string; item: string; description: string;
    quantity: string; unitPrice: string; discount: string; tax: string; lineNet: string;
    subtotal: string; netTotal: string; invoiced: string; paid: string; remaining: string;
    beforeTax: string; taxComputedNote: string; status: string; draft: string; approved: string;
    closed: string; cancelled: string; actions: string; view: string; edit: string;
    approve: string; close: string; cancel: string; save: string; none: string;
    saved: string; approvedOk: string; closedOk: string; cancelledOk: string;
  };
  purchaseItems: {
    title: string; breadcrumb: string; purposeNotice: string; defaultsNotice: string;
    newRecord: string; editRecord: string; searchHint: string; noRows: string;
    code: string; name: string; nameAr: string; itemType: string; service: string;
    goods: string; unit: string; defaultExpenseAccount: string; defaultTaxType: string;
    defaultCostCenter: string; usageCount: string; status: string; active: string;
    actions: string; edit: string; activate: string; deactivate: string; save: string;
    notes: string; none: string; saved: string;
  };
  paymentsInquiries: {
    title: string;
    subtitle: string;
    readOnly: string;
    scopeNote: string;
    supplier: string;
    allSuppliers: string;
    chooseSupplier: string;
    supplierRequired: string;
    dateFrom: string;
    dateTo: string;
    branch: string;
    currency: string;
    costCenter: string;
    account: string;
    documentType: string;
    documentNumber: string;
    documentDate: string;
    reference: string;
    description: string;
    amountFrom: string;
    amountTo: string;
    debit: string;
    credit: string;
    netMovement: string;
    balance: string;
    status: string;
    postedOnly: string;
    includeReversed: string;
    reversed: string;
    statusDraft: string;
    statusAudited: string;
    statusPosted: string;
    statusRejected: string;
    statusCancelled: string;
    statusReversed: string;
    paymentMethod: string;
    user: string;
    all: string;
    yes: string;
    no: string;
    none: string;
    totalDebit: string;
    totalCredit: string;
    rowCount: string;
    side: string;
    sideDebit: string;
    sideCredit: string;
    open: string;
  };
  supplierPayables: {
    title: string;
    description: string;
    code: string;
    name: string;
    controlAccount: string;
    accountingBalance: string;
    payable: string;
    advance: string;
    balanceType: string;
    payableOnly: string;
    advanceOnly: string;
    zeroOnly: string;
    minimumBalance: string;
    maximumBalance: string;
    openDocuments: string;
    withOpenOnly: string;
    withoutOpenOnly: string;
    unsettledInvoices: string;
    unappliedPayments: string;
    lastMovement: string;
    creditLimit: string;
    availableCredit: string;
    supplierCount: string;
    totalPayable: string;
    totalAdvance: string;
    activeOnly: string;
    inactiveOnly: string;
    noSuppliers: string;
    ledgerNote: string;
  };
  supplierAccountMovements: {
    title: string;
    description: string;
    openingBalance: string;
    closingBalance: string;
    runningBalance: string;
    pickSupplier: string;
    noMovements: string;
    balanceNote: string;
    supplierInvoiceNumber: string;
    supplierInvoiceDate: string;
    dueDate: string;
    natureDebit: string;
    natureCredit: string;
  };
  supplierMovements: {
    title: string;
    description: string;
    noMovements: string;
    everySupplier: string;
  };
  paymentsDailyTransactions: {
    title: string;
    description: string;
    documentFamily: string;
    nature: string;
    accounting: string;
    nonAccounting: string;
    accountingCount: string;
    nonAccountingCount: string;
    totalAccountingAmount: string;
    createdAt: string;
    noDocuments: string;
    natureNote: string;
    /** The document families, named by the code the server groups them under. */
    families: Record<string, string>;
  };
  paymentVoucherMovements: {
    title: string;
    description: string;
    voucherNumber: string;
    voucherDate: string;
    paidTo: string;
    directAccount: string;
    paymentOrder: string;
    components: string;
    componentCount: string;
    cashbox: string;
    bankAccount: string;
    localAmount: string;
    includeDirect: string;
    postedCount: string;
    reversedCount: string;
    totalAmount: string;
    noVouchers: string;
    splitNote: string;
    viewComponents: string;
    hideComponents: string;
  };
  servicePurchaseDocuments: {
    title: string;
    description: string;
    request: string;
    invoice: string;
    documentReturn: string;
    supplierReference: string;
    sourceDocument: string;
    netAmount: string;
    taxAmount: string;
    totalAmount: string;
    requestCount: string;
    invoiceCount: string;
    returnCount: string;
    totalNet: string;
    totalTax: string;
    includeLines: string;
    lineDescription: string;
    quantity: string;
    unitPrice: string;
    taxPercent: string;
    expenseAccount: string;
    noDocuments: string;
    historicalNote: string;
    viewLines: string;
    hideLines: string;
  };
  supplierCostCenterMovements: {
    title: string;
    description: string;
    includeChildren: string;
    costCenterOnly: string;
    uncosted: string;
    uncostedCount: string;
    parentCostCenter: string;
    bySupplier: string;
    byCostCenter: string;
    byAccount: string;
    subtotals: string;
    noMovements: string;
    authoritativeNote: string;
  };
  paymentBooksInquiry: {
    title: string;
    description: string;
    bookNumber: string;
    fromNumber: string;
    toNumber: string;
    voucherCount: string;
    holder: string;
    holderType: string;
    receivedDate: string;
    assignedDate: string;
    returnedDate: string;
    assignedFrom: string;
    assignedTo: string;
    lastMovement: string;
    movements: string;
    viewMovements: string;
    hideMovements: string;
    totalBooks: string;
    totalVouchers: string;
    noBooks: string;
    custodyNote: string;
  };
  paymentBookRangeGaps: {
    title: string;
    description: string;
    previousBook: string;
    previousEnd: string;
    nextBook: string;
    nextStart: string;
    gapFrom: string;
    gapTo: string;
    gapSize: string;
    gapCount: string;
    unregisteredNumbers: string;
    booksExamined: string;
    rangeFrom: string;
    rangeTo: string;
    noGaps: string;
    detectionNote: string;
  };
  expenseInquiries: {
    movements: string;
    account: string;
    movementsByCostCenter: string;
    accountByCostCenter: string;
    glNote: string;
  };
  unsettledPaymentOrders: {
    title: string; description: string; orderNumber: string; orderDate: string;
    orderAmount: string; paidAmount: string; remainingAmount: string; settlementState: string;
    expectedPaymentDate: string; voucherCount: string; includeFullyPaid: string;
    includeCancelled: string; minimumRemaining: string; totalOrdered: string;
    unpaidCount: string; partiallyPaidCount: string; unpaid: string; partiallyPaid: string;
    paid: string; noOrders: string; derivedNote: string;
  };
  supplierInformationInquiry: {
    title: string; description: string; taxNumber: string; commercialRegistration: string;
    nationalId: string; phone: string; mobile: string; email: string; address: string;
    city: string; country: string; creditLimit: string; activeCount: string;
    inactiveCount: string; active: string; inactive: string; noSuppliers: string;
    readOnlyNote: string;
  };
  supplierCurrencyStatement: {
    title: string; description: string; transactionCurrency: string; exchangeRate: string;
    foreignDebit: string; foreignCredit: string; foreignBalance: string; localDebit: string;
    localCredit: string; localBalance: string; openingForeign: string; openingLocal: string;
    closingForeign: string; closingLocal: string; ledgerCurrencies: string; movements: string;
    pickSupplier: string; noMovements: string; rateNote: string;
  };
  supplierBalancesByCostCenter: {
    title: string; description: string; unassigned: string; unassignedAmount: string;
    includeUnassigned: string; includeZeroBalances: string; upToDate: string;
    costCenterCount: string; noBalances: string; reconcileNote: string;
  };
  purchaseOrdersAwaitingPayment: {
    title: string; description: string; orderNumber: string; committed: string;
    invoiced: string; uninvoiced: string; expectedDate: string; paymentTerm: string;
    includeSettled: string; includeNonOutstanding: string; totalCommitted: string;
    totalInvoiced: string; totalUninvoiced: string; noOrders: string; genuineNote: string;
  };
  combinedBooks: {
    title: string; description: string; bookType: string; receiptBook: string;
    paymentBook: string; allBooks: string; holder: string; movementCount: string;
    receiptBookCount: string; paymentBookCount: string; registeredNumbers: string;
    noBooks: string; separateNote: string;
  };
  supplierItemTransactions: {
    title: string; description: string; item: string; allItems: string; itemType: string;
    service: string; goods: string; quantity: string; unit: string; unitPrice: string;
    discount: string; net: string; uncatalogued: string; includeUncatalogued: string;
    documentType: string; purchaseOrder: string; purchaseRequest: string;
    purchaseInvoice: string; purchaseReturn: string; totalQuantity: string; itemCount: string;
    noTransactions: string; historicalNote: string;
  };
  outstandingSupplierInvoices: {
    title: string; description: string; invoiceNumber: string; invoiceDate: string;
    dueDate: string; originalAmount: string; appliedAmount: string; outstandingAmount: string;
    daysOutstanding: string; daysOverdue: string; overdueOnly: string; outstandingOnly: string;
    minimumOutstanding: string; asOfDate: string; overdueCount: string; overdueAmount: string;
    totalOriginal: string; totalApplied: string; totalOutstanding: string; noInvoices: string;
    allocationNote: string;
  };
  paidUnpaidSupplierInvoices: {
    title: string; description: string; paymentState: string; paid: string;
    partiallyPaid: string; unpaid: string; invoiceAmount: string; remainingAmount: string;
    totalInvoiced: string; totalPaid: string; totalRemaining: string; noInvoices: string;
    engineNote: string;
  };
  unappliedPaymentVouchers: {
    title: string; description: string; voucherAmount: string; appliedAmount: string;
    unappliedAmount: string; cashboxOrBank: string; minimumUnapplied: string;
    includeFullyApplied: string; totalVoucherAmount: string; totalUnapplied: string;
    noVouchers: string; noGuessNote: string;
  };

  /** Human Resources › Employee Affairs › Basic Data › بطاقة الموظف. */
  employees: {
    title: string; breadcrumb: string; purposeNotice: string; identityNotice: string;
    noPayrollNotice: string; vocabulariesEmpty: string; readOnlyNotice: string;
    listTitle: string; formTitle: string; newRecord: string;
    search: string; searchHint: string; refresh: string; showInactive: string; noRows: string;
    branchFilter: string; allBranches: string; allDepartments: string;
    linkFilter: string; anyLink: string; linkedOnly: string; unlinkedOnly: string;
    code: string; nameAr: string; nameEn: string; user: string; noUser: string; userHint: string;
    jobTitle: string; noJobTitle: string; department: string; noDepartment: string;
    contractType: string; noContractType: string;
    branch: string; noBranch: string; hireDate: string; nationalId: string;
    mobile: string; phone: string; email: string; notes: string; active: string; actions: string;
    edit: string; view: string; activate: string; deactivate: string;
    save: string; saveNew: string; cancel: string;
    details: string; updatedAt: string;
    auditHistory: string; auditNone: string; auditAction: string; auditFields: string;
    auditChangedBy: string; auditChangedAt: string; close: string;
    saved: string; activated: string; deactivated: string; counts: string; matched: string;
    loadError: string; saveError: string; auditError: string;
  };
  /**
   * System Settings — إعدادات النظام.
   *
   * Labels only. No key here describes a policy or explains a screen: the standing rule that
   * ERP screens carry no permanent explanatory notes applies to this department too.
   */
  /** Print / PDF / Excel on a master-data or settings list. Shared by nine screens. */
  settingsExport: {
    print: string;
    exportPdf: string;
    exportExcel: string;
    working: string;
    failed: string;
  };
  /** دليل الأستاذ العام — an administrator's reference to every screen in the module. */
  /** Human Resources › Employee Affairs › Master Data. */
  hr: {
    employeeCard: string; responsibleEntities: string; employeeRegister: string;
    inquiry: string; newRecord: string; save: string; saveAndNew: string; cancel: string;
    close: string; edit: string; delete: string; refresh: string; search: string;
    searchAndFilter: string; all: string; none: string; actions: string; status: string;
    active: string; inactive: string; activeRecord: string; unsaved: string;
    saved: string; deleted: string; loadFailed: string; saveFailed: string; deleteFailed: string;
    confirmDeleteTitle: string; deleteEntityConfirm: string;
    code: string; nameAr: string; nameEn: string; notes: string; employees: string;
    newEntity: string; editEntity: string; noEntities: string; noEmployees: string;
    selectEmployee: string;
    identification: string; serial: string; employeeNumber: string;
    firstNameAr: string; secondNameAr: string; thirdNameAr: string; familyNameAr: string;
    firstNameEn: string; secondNameEn: string; thirdNameEn: string; familyNameEn: string;
    photo: string; uploadPhoto: string; removePhoto: string; photoSaved: string; photoFailed: string;
    personalInfo: string; nationality: string; religion: string; gender: string; male: string;
    female: string; maritalStatus: string; single: string; married: string; divorced: string;
    widowed: string; nationalId: string; birthDate: string; birthDateHijri: string;
    birthPlace: string; motherName: string; specialNeeds: string;
    address: string; country: string; governorate: string; city: string; area: string;
    addressLine: string; phone: string; altPhone: string; email: string;
    employmentInfo: string; hireDate: string; department: string; jobTitle: string;
    employmentType: string; responsibleEntity: string; branch: string; costCenter: string;
    employmentStatus: string; statusActive: string; statusOnLeave: string; statusSuspended: string;
    statusTerminated: string; statusResigned: string;
    jobDetails: string;
    contractSection: string;
    insuranceSection: string;
    secondaryJobTitle: string;
    technicalClassification: string;
    grade: string;
    employeeClassification: string;
    directManager: string;
    supervisor: string;
    dailyWorkingHours: string;
    probationPeriodDays: string;
    employmentStatusDate: string;
    isHazardousOccupation: string;
    allowsOvertime: string;
    spouseIsEmployed: string;
    isFamilyProvider: string;
    payFrequency: string;
    payMonthly: string;
    payDaily: string;
    payHourly: string;
    contractType: string;
    contractStart: string;
    contractEnd: string;
    isFixedTermContract: string;
    renewalMethod: string;
    renewalDaily: string;
    renewalMonthly: string;
    renewalCalendar: string;
    leaveCategory: string;
    isGosiRegistered: string;
    gosiRegistrationDate: string;
    gosiNumber: string;
    gosiPolicy: string;
    hasHealthInsurance: string;
    healthInsurancePolicy: string;
  };
  hrLeave: {
    leave: string;
    leaveTypes: string;
    leaveRequests: string;
    leaveAmendments: string;
    leaveBalanceAdjustments: string;
    leaveMovements: string;
    leaveBalances: string;
    new: string;
    save: string;
    edit: string;
    delete: string;
    cancel: string;
    close: string;
    refresh: string;
    search: string;
    actions: string;
    status: string;
    all: string;
    none: string;
    saved: string;
    deleted: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    noResults: string;
    required: string;
    confirmDeleteTitle: string;
    confirmDelete: string;
    yes: string;
    no: string;
    days: string;
    day: string;
    typeCode: string;
    typeNameAr: string;
    typeNameEn: string;
    isActive: string;
    isPaid: string;
    requiresBalance: string;
    allowsNegativeBalance: string;
    requiresAttachment: string;
    minimumDays: string;
    maximumDays: string;
    noticeDays: string;
    annualEntitlementDays: string;
    carryForwardAllowed: string;
    maximumCarryForwardDays: string;
    notes: string;
    newLeaveType: string;
    editLeaveType: string;
    noLeaveTypes: string;
    typeInUse: string;
    deactivateInstead: string;
    policy: string;
    paid: string;
    unpaid: string;
    balanceDriven: string;
    noBalance: string;
    number: string;
    employee: string;
    leaveType: string;
    requestDate: string;
    fromDate: string;
    toDate: string;
    returnDate: string;
    requestedDays: string;
    approvedDays: string;
    replacement: string;
    branch: string;
    department: string;
    reason: string;
    newRequest: string;
    noRequests: string;
    submit: string;
    approve: string;
    reject: string;
    cancelRequest: string;
    rejectionReason: string;
    submitted: string;
    approved: string;
    rejected: string;
    cancelledStatus: string;
    draft: string;
    available: string;
    upcoming: string;
    attachments: string;
    addAttachment: string;
    noAttachments: string;
    amendments: string;
    noAmendments: string;
    amend: string;
    amendAction: string;
    actionCancel: string;
    actionExtend: string;
    actionInterrupt: string;
    actionChangeCategory: string;
    effectiveDate: string;
    newToDate: string;
    newLeaveType2: string;
    daysDifference: string;
    oldDays: string;
    newDays: string;
    submittedAt: string;
    approvedAt: string;
    rejectedAt: string;
    cancelledAt: string;
    by: string;
    adjustment: string;
    newAdjustment: string;
    direction: string;
    increase: string;
    decrease: string;
    isOpeningBalance: string;
    adjustmentDays: string;
    adjustmentReason: string;
    noAdjustments: string;
    movementType: string;
    movementOpeningBalance: string;
    movementEntitlement: string;
    movementUsage: string;
    movementAdjustment: string;
    movementReversal: string;
    balanceBefore: string;
    balanceAfter: string;
    sourceDocument: string;
    entitled: string;
    used: string;
    adjusted: string;
    noMovements: string;
    noBalances: string;
    activeEmployeesOnly: string;
    asOf: string;
    filters: string;
    apply: string;
    reset: string;
    rows: string;
    print: string;
    exportExcel: string;
    allowSelfApproval: string;
    employmentStatus: string;
    groupBy: string;
    groupByLeaveType: string;
    groupByEmployee: string;
    groupByDepartment: string;
    groupByBranch: string;
    groupByMonth: string;
    statusOnLeave: string;
    statusSuspended: string;
    statusTerminated: string;
    statusResigned: string;
    statusActiveEmployee: string;
  };
  hotelFrontDesk: {
    title: string;
    roomBoardTitle: string;
    masterDataTitle: string;
    settingsTitle: string;
    roomTypesTitle: string;
    roomsTitle: string;

    statusVacant: string;
    statusOccupiedFirstNight: string;
    statusOccupiedContinuing: string;
    statusOccupiedCheckoutToday: string;
    statusVacantDirtyToday: string;
    statusVacantDirtyStale: string;
    statusReserved: string;
    statusOutOfService: string;
    needsCleaningBadge: string;

    book: string;
    checkIn: string;
    checkOut: string;
    markCleaned: string;
    flagNeedsCleaning: string;
    setOutOfService: string;
    returnToService: string;
    outOfServiceReasonPrompt: string;
    outOfServiceReasonHint: string;
    outOfServiceBadge: string;

    guestName: string;
    guestPhone: string;
    findOrAddGuest: string;
    existingGuestFound: string;
    newGuestWillBeCreated: string;

    checkInDate: string;
    checkOutDate: string;
    actualCheckOutDate: string;
    nights: string;
    nightlyRate: string;
    discountAmount: string;
    paymentMethod: string;
    cash: string;
    visa: string;
    passportNumber: string;
    totalPax: string;
    notes: string;

    code: string;
    name: string;
    nameAr: string;
    pricePerNight: string;
    serviceSalesType: string;
    roomNumber: string;
    floor: string;
    roomType: string;
    outOfService: string;
    outOfServiceReason: string;
    active: string;

    defaultCostCenter: string;
    defaultGuestControlAccount: string;
    discountType: string;
    none: string;
    cleaningDueTime: string;

    save: string;
    cancel: string;
    close: string;
    confirm: string;
    newRoomType: string;
    newRoom: string;

    invoiceCreatedNotice: string;
    loadFailed: string;
    saveFailed: string;
    empty: string;
  };
  hrDaily: {
    inquiry: string;
    new: string;
    save: string;
    saveAndNew: string;
    print: string;
    cancel: string;
    submit: string;
    approve: string;
    reject: string;
    execute: string;
    send: string;
    sent: string;
    cancelDocument: string;
    close: string;
    delete: string;
    refresh: string;
    apply: string;
    all: string;
    none: string;
    actions: string;
    status: string;
    draft: string;
    submitted: string;
    approved: string;
    rejected: string;
    cancelled: string;
    executed: string;
    number: string;
    employee: string;
    employeeNumber: string;
    documentDate: string;
    fromDate: string;
    toDate: string;
    branch: string;
    department: string;
    attachments: string;
    addAttachment: string;
    noAttachments: string;
    audit: string;
    submittedAt: string;
    approvedAt: string;
    rejectedAt: string;
    cancelledAt: string;
    executedAt: string;
    rejectionReason: string;
    noResults: string;
    loadFailed: string;
    saveFailed: string;
    saved: string;
    openDocument: string;
    remove: string;
    dailyTransactions: string;
    sectionLeave: string;
    sectionDepartures: string;
    sectionJobMovement: string;
    sectionContracts: string;
    sectionDocuments: string;
    sectionTimeTravel: string;
    sectionConductHealth: string;
    sectionCustody: string;
    sectionAdministration: string;
    custodyNote: string;
    iqamaMovements: string;
    healthCertificates: string;
    employeeCustody: string;
  };
  glGuide: {
    subtitleTree: string;
    module: string;
    coverage: string;
    notWritten: string;
    notBuilt: string;
    title: string;
    subtitle: string;
    search: string;
    noResults: string;
    openScreen: string;
    purpose: string;
    usage: string;
    accounting: string;
    caution: string;
  };
  systemSettings: {
    /** Shared across every System Settings screen. */
    search: string;
    add: string;
    edit: string;
    save: string;
    cancel: string;
    close: string;
    delete: string;
    remove: string;
    retry: string;
    saved: string;
    deleted: string;
    actions: string;
    status: string;
    active: string;
    inactive: string;
    all: string;
    none: string;
    yes: string;
    no: string;
    noResults: string;
    showing: string;
    previous: string;
    next: string;
    code: string;
    name: string;
    nameAr: string;
    description: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    country: string;
    loadError: string;
    saveError: string;
    deleteError: string;
    confirmDeleteTitle: string;

    /** Users. */
    users: string;
    newUser: string;
    editUser: string;
    userName: string;
    fullName: string;
    fullNameAr: string;
    password: string;
    newPassword: string;
    showPassword: string;
    hidePassword: string;
    confirmPassword: string;
    passwordMismatch: string;
    language: string;
    english: string;
    arabic: string;
    userRoles: string;
    companyAccess: string;
    allCompanies: string;
    defaultCompany: string;
    lastLogin: string;
    neverSignedIn: string;
    mustChangePassword: string;
    systemAdministrator: string;
    resetPassword: string;
    passwordWasReset: string;
    assignRoles: string;
    rolesAssigned: string;
    deleteUserConfirm: string;
    statusPending: string;
    statusActive: string;
    statusSuspended: string;
    statusLocked: string;
    statusDisabled: string;
    noUsers: string;

    /** Roles. */
    roles: string;
    newRole: string;
    editRole: string;
    systemRole: string;
    userCount: string;
    permissionCount: string;
    editPermissions: string;
    permissionsSaved: string;
    deleteRoleConfirm: string;
    selectAll: string;
    clearAll: string;
    notGrantable: string;
    granted: string;
    noRoles: string;

    /** Permission catalogue. */
    permissions: string;
    permissionKey: string;
    module: string;
    grantedToRoles: string;
    grantedToNoRole: string;
    catalogueCounts: string;

    /** Audit trail. */
    auditTrail: string;
    occurredAt: string;
    user: string;
    action: string;
    entity: string;
    record: string;
    summary: string;
    changedFields: string;
    ipAddress: string;
    correlationId: string;
    viewDetails: string;
    valuesBefore: string;
    valuesAfter: string;
    dateFrom: string;
    dateTo: string;
    anyAction: string;
    anyEntity: string;
    noAuditEntries: string;
    actionCreated: string;
    actionUpdated: string;
    actionDeleted: string;
    actionLogin: string;
    actionLoginFailed: string;
    actionLogout: string;
    actionPermissionDenied: string;
    actionJournalAudited: string;
    actionJournalPosted: string;
    actionJournalRejected: string;
    actionJournalReversed: string;
    actionJournalReopened: string;
    actionExported: string;

    /** Access matrix. */
    accessMatrix: string;
    effectivePermissions: string;
    viaRoles: string;
    holdsPermission: string;
    anyPermission: string;
    noAccess: string;

    /** Companies and branches. */
    companies: string;
    newCompany: string;
    editCompany: string;
    legalName: string;
    taxNumber: string;
    registrationNumber: string;
    website: string;
    localCurrency: string;
    fiscalYearStart: string;
    branchCount: string;
    deleteCompanyConfirm: string;
    noCompanies: string;
    branches: string;
    newBranch: string;
    editBranch: string;
    company: string;
    anyCompany: string;
    headOffice: string;
    deleteBranchConfirm: string;
    noBranches: string;
    /** Users definition screen — toolbar, register and detail sections. */
    query: string;
    newRecord: string;
    saveAndNew: string;
    userInformation: string;
    userRegister: string;
    loginName: string;
    arabicDescription: string;
    englishDescription: string;
    defaultLanguage: string;
    createdAt: string;
    passwordChangedAt: string;
    lockedOutUntil: string;
    failedAttempts: string;
    notSaved: string;
    unsavedRecord: string;
    selectUser: string;
    changePassword: string;
    passwordOnCreateOnly: string;
    defaultCompanyBranch: string;
    defaultBranch: string;
    tabPermissions: string;
    tabCompanies: string;
    tabRestricted: string;
    assigned: string;
    permissionsFromRoles: string;
    noPermissions: string;
    grantedByRole: string;
    restrictedSuppliers: string;
    restrictedCustomers: string;
    restrictedCashboxes: string;
    openManager: string;
    accessRows: string;
    unrestricted: string;
    notSupported: string;
    fiscalYearAccess: string;
    blockedAccounts: string;
    reminderNotifications: string;
    interactiveScreens: string;
    specialPermissions: string;
    twoFactor: string;
    cloudUser: string;
    userCode: string;
    identificationCode: string;
    expiryDate: string;
    months: readonly string[];
  };
}

const ar: Dictionary = {
  cashBanksInquiries: {
    readOnly: "استعلام للقراءة فقط. لا يُنشئ ولا يعدّل أي مستند أو قيد.",
    date: "التاريخ",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    asOfDate: "كما في تاريخ",
    asOfHint: "يُحتسب الرصيد من القيود حتى هذا التاريخ فقط.",
    branch: "الفرع",
    currency: "العملة",
    costCenter: "مركز التكلفة",
    cashbox: "الصندوق",
    bank: "البنك",
    bankAccount: "الحساب البنكي",
    party: "الطرف",
    search: "بحث",
    all: "الكل",
    unassigned: "غير محدد",
    voucher: "السند",
    account: "الحساب",
    description: "البيان",
    reference: "المرجع",
    status: "الحالة",
    debit: "مدين",
    credit: "دائن",
    amount: "المبلغ",
    localAmount: "بالعملة الأساسية",
    exchangeRate: "سعر الصرف",
    runningBalance: "الرصيد الجاري",
    openingBalance: "الرصيد الافتتاحي",
    closingBalance: "الرصيد الختامي",
    balance: "الرصيد",
    totalDebit: "إجمالي المدين",
    totalCredit: "إجمالي الدائن",
    count: "العدد",
    noRows: "لا توجد نتائج مطابقة.",
    perCurrency: "الإجماليات لكل عملة على حدة.",
    movementType: "نوع الحركة",
    direction: "الاتجاه",
    counterparty: "الطرف الآخر",
    cashIn: "وارد",
    cashOut: "صادر",
    netMovement: "صافي الحركة",
    mtDeposit: "إيداع",
    mtZeroing: "صفر الصندوق",
    mtFunding: "تغذية صندوق",
    mtCount: "محضر جرد صندوق",
    mtRepresentative: "محاسبة مندوبين",
    dirFrom: "من الصندوق",
    dirTo: "إلى الصندوق",
    code: "الرمز",
    inClosingRoutine: "يشارك في الإغلاق",
    zeroings: "مرات التصفير",
    lastZeroing: "آخر تصفير",
    lastCount: "آخر جرد",
    closingOnly: "صناديق الإغلاق فقط",
    outstandingOnly: "التي ما زالت برصيد فقط",
    zeroedBoxes: "صناديق مُصفّرة",
    outstandingBoxes: "ما زالت برصيد",
    totalOutstanding: "إجمالي المتبقي",
    chequeNumber: "رقم الشيك",
    chequeDate: "تاريخ الشيك",
    dueDate: "تاريخ الاستحقاق",
    dueFrom: "الاستحقاق من",
    dueTo: "الاستحقاق إلى",
    term: "النوع",
    daysOverdue: "أيام التأخير",
    daysUnderCollection: "أيام لدى البنك",
    depositedDate: "تاريخ الإيداع",
    deliveredDate: "تاريخ التسليم",
    deliveredTo: "سُلّم إلى",
    returnedDate: "تاريخ الارتجاع",
    returnReason: "سبب الارتجاع",
    clearedTo: "حُصّل في",
    sourceDocument: "المستند المصدر",
    settlementVoucher: "سند التسوية",
    movements: "الحركات",
    chequeTotal: "إجمالي الشيكات",
    movement: "الحركة",
    fromStatus: "من حالة",
    toStatus: "إلى حالة",
    journal: "القيد",
    noJournal: "بدون قيد",
    recordedBy: "سجّلها",
    recordedAt: "وقت التسجيل",
    postedOnly: "الحركات ذات القيد فقط",
    postedMovements: "حركات بقيد",
    operationalMovements: "حركات تشغيلية",
    finding: "النتيجة",
    book: "الدفتر",
    fromNumber: "من رقم",
    toNumber: "إلى رقم",
    leaves: "عدد الأوراق",
    leafStatus: "حالة الورقة",
    skippedLeaf: "ورقة متخطاة",
    unregisteredRange: "مدى غير مسجل",
    gapsHint: "لا تدّعي هذه الشاشة أن شيكاً حُرّر وفُقد. تعرض ورقة تُخطّيت، أو مدى أرقام لا يغطيه أي دفتر مسجل.",
    leavesAffected: "أوراق متأثرة",
    documentKind: "نوع المستند",
    from: "من",
    to: "إلى",
    dkCashDeposit: "إيداع نقدي",
    dkCashWithdrawal: "سحب نقدي",
    dkCashboxTransaction: "حركة صندوق",
    dkBankTransaction: "حركة بنكية",
    dkBankTransfer: "تحويل بين البنوك",
    posted: "مُرحَّل",
    unposted: "غير مُرحَّل",
    documents: "المستندات",
    creditNumber: "رقم الاعتماد",
    beneficiary: "المستفيد",
    facility: "التسهيل",
    facilityCeiling: "سقف التسهيل",
    facilityCommitted: "المستخدم من التسهيل",
    facilityRemaining: "المتاح من التسهيل",
    creditAmount: "قيمة الاعتماد",
    margin: "التأمين النقدي",
    openedDate: "تاريخ الفتح",
    expiryDate: "تاريخ الانتهاء",
    daysToExpiry: "أيام حتى الانتهاء",
    openOnly: "المفتوحة فقط",
    expiredOnly: "المنتهية فقط",
    openCredits: "اعتمادات مفتوحة",
    expiredCredits: "اعتمادات منتهية",
    totalMargin: "إجمالي التأمينات",
    loanNumber: "رقم القرض",
    principal: "مبلغ القرض",
    interestRate: "الفائدة %",
    installments: "الأقساط",
    paidInstallments: "أقساط مسددة",
    remainingInstallments: "أقساط متبقية",
    overdueInstallments: "أقساط متأخرة",
    paidPrincipal: "الأصل المسدد",
    outstandingPrincipal: "الأصل المتبقي",
    scheduledInterest: "الفوائد المجدولة",
    nextDue: "القسط القادم",
    overdueOnly: "التي بها أقساط متأخرة فقط",
    activeLoans: "قروض نشطة",
    overdueLoans: "قروض متأخرة",
    screens: {
      cashboxClosing: { title: "حركات إغلاق الصناديق", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حركات إغلاق الصناديق", description: "مستندات صفر الصندوق — روتين الإغلاق كما يسجّله النظام." },
      bankAccountCurrency: { title: "حساب بنكي - عملة - محلي", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حساب بنكي - عملة - محلي", description: "رصيد كل حساب بنكي بعملته وبالمعادل بالعملة الأساسية." },
      bankAccount: { title: "حساب بنكي", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حساب بنكي", description: "كشف حركات حساب بنكي برصيد جارٍ محسوب في الخادم." },
      accountMovements: { title: "حركات الحسابات", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حركات الحسابات", description: "حركات حسابات الصناديق والبنوك معاً." },
      dailyTransactions: { title: "الحركات اليومية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « الحركات اليومية", description: "كل مستندات النقد والبنوك المحرّرة خلال الفترة، أياً كان نوعها." },
      chequeMovements: { title: "حركات الشيكات", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حركات الشيكات", description: "سجل الشيكات كما كُتب، لا كما يُستنتج من حالتها الحالية." },
      paidCheques: { title: "الشيكات المدفوعة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « الشيكات المدفوعة", description: "الشيكات التي حرّرتها الشركة." },
      receivedCheques: { title: "الشيكات المقبوضة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « الشيكات المقبوضة", description: "الشيكات المقبوضة من العملاء ودورة تحصيلها." },
      lettersOfCredit: { title: "الاعتمادات المستندية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « الاعتمادات المستندية", description: "سجل الاعتمادات مع استخدام التسهيل." },
      chequeDelivery: { title: "تسليم الشيكات الفورية / الآجلة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « تسليم الشيكات الفورية / الآجلة", description: "الشيكات التي حرّرناها وسُلّمت فعلاً." },
      undeliveredCheques: { title: "الشيكات غير المسلّمة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « الشيكات غير المسلّمة", description: "شيكات محرّرة ما زالت في الدرج." },
      cashboxMovements: { title: "كشف حركات الصناديق", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « كشف حركات الصناديق", description: "كل حركات الصناديق أياً كان نوعها." },
      cashboxAccount: { title: "حساب الصندوق", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « حساب الصندوق", description: "كشف حركات حساب الصندوق برصيد جارٍ محسوب في الخادم." },
      cashboxCount: { title: "جرد الصندوق", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « جرد الصندوق", description: "محاضر جرد الصناديق كما سُجّلت." },
      cashboxClosingFollowUp: { title: "متابعة إغلاق الصناديق", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « متابعة إغلاق الصناديق", description: "أي الصناديق تم تصفيرها، وأيها ما زال يحمل رصيداً." },
      missingChequeSequences: { title: "تسلسلات الشيكات المفقودة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « تسلسلات الشيكات المفقودة", description: "الفجوات في ترقيم الشيكات المسجّل." },
      bankLoans: { title: "القروض البنكية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « القروض البنكية", description: "سجل القروض مع أرقام جدول السداد." },
    },
  },
  issueCheque: {
    bankAccount: "الحساب المسحوب عليه",
    beneficiary: "المستفيد",
    chequeNumber: "رقم الشيك",
    chequeNumberHint: "رقم البنك المطبوع على الورقة، وليس رقم سند في النظام.",
    fromChequeBook: "من دفتر شيكات",
    chooseLeaf: "اختر ورقة شيك",
    leafHint: "اختيار ورقة يملأ رقم الشيك ويستهلك الورقة عند الحفظ. الأوراق المستخدمة لا تُعرض.",
    manualNumber: "إدخال الرقم يدوياً",
    chequeDate: "تاريخ الشيك",
    dueDate: "تاريخ الاستحقاق",
    amount: "المبلغ",
    currency: "العملة",
    notes: "ملاحظات",
    term: "نوع الشيك",
    issue: "تحرير الشيك",
    issuing: "جارٍ التحرير…",
    issued: "تم تحرير الشيك.",
    recent: "الشيكات المحررة",
    empty: "لم يُحرَّر أي شيك بعد.",
    loading: "جارٍ التحميل…",
    required: "هذا الحقل مطلوب.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكنك تحرير شيكات.",
    clear: "مسح",
    cancel: "إلغاء",
    chooseAccount: "اختر الحساب البنكي",
    chooseBeneficiary: "اختر المستفيد",
    chooseCurrency: "اختر العملة",
    immediateAccounting: "القيد: من ح/ المستفيد إلى ح/ البنك. البنك يُدائن فور التحرير.",
    deferredAccounting: "القيد: من ح/ المستفيد إلى ح/ الشيكات الآجلة برسم الدفع. البنك لا يُدائن إلا عند الصرف.",
    accountingTitle: "الأثر المحاسبي",
  },
  chequeBooksScreen: {
    title: "دفاتر الشيكات",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « سحب / تسليم من صندوق الشيكات",
    description: "دفاتر الشيكات البيضاء وأوراقها. ضبط قرطاسية لا يُنتج أي قيد محاسبي.",
    numberingHint: "رقم ورقة الشيك هو رقم البنك المطبوع، ولا يصبح أبداً رقم سند في النظام.",
    bankAccount: "الحساب البنكي",
    bookNumber: "رقم الدفتر",
    fromChequeNumber: "من رقم شيك",
    toChequeNumber: "إلى رقم شيك",
    rangeHint: "تُشتق أوراق الدفتر من المدى تلقائياً، مع الحفاظ على الأصفار البادئة.",
    leafCount: "عدد الأوراق",
    receivedDate: "تاريخ الاستلام",
    cashbox: "صندوق الشيكات",
    isActive: "مفعّل",
    notes: "ملاحظات",
    leaves: "الأوراق",
    leafNumber: "رقم الورقة",
    leafStatus: "الحالة",
    leafUnused: "غير مستخدمة",
    leafWithdrawn: "مسحوبة",
    leafUsed: "مستخدمة",
    leafVoid: "تالفة",
    withdrawnBy: "سُحبت بواسطة",
    withdrawnDate: "تاريخ السحب",
    withdraw: "سحب من الصندوق",
    withdrawHint: "خطوة بين استلام الدفتر وتحرير الشيك. الورقة البيضاء لا قيمة لها حتى تُحرَّر.",
    counts: "حالة الأوراق",
    newBook: "دفتر جديد",
    save: "حفظ",
    cancel: "إلغاء",
    search: "بحث برقم الدفتر أو الشيك",
    empty: "لا توجد دفاتر مسجلة.",
    noLeaves: "لا توجد أوراق.",
    loading: "جارٍ التحميل…",
    saving: "جارٍ الحفظ…",
    saved: "تم الحفظ.",
    withdrawn: "تم سحب الورقة.",
    required: "هذا الحقل مطلوب.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    actions: "الإجراءات",
    refresh: "تحديث",
    rangeLocked: "لا يمكن تعديل مدى دفتر مسجَّل — أوراقه قائمة وبعضها محرَّر.",
  },
  bankOps: {
    voucherNumber: "رقم السند",
    documentDate: "تاريخ السند",
    valueDate: "تاريخ القيمة لدى البنك",
    branch: "الفرع",
    description: "البيان",
    bank: "البنك",
    bankAccount: "الحساب البنكي",
    counterAccount: "الحساب المقابل",
    counterAccountHint: "الحساب الذي تخص به العمولة أو الفائدة. يُختار لكل حركة ولا يُفترض.",
    costCenter: "مركز التكلفة",
    facility: "التسهيل",
    currency: "العملة",
    amount: "المبلغ",
    exchangeRate: "سعر الصرف",
    localAmount: "المبلغ بالعملة الأساسية",
    bankReference: "مرجع البنك",
    notes: "ملاحظات",
    kind: "نوع الحركة",
    direction: "الاتجاه",
    directionIncrease: "زيادة في الحساب",
    directionDecrease: "نقص في الحساب",
    directionHint: "الاتجاه من وجهة نظر دفاترنا، لا من وجهة نظر كشف البنك.",
    kindCharges: "عمولات ومصاريف بنكية",
    kindInterestEarned: "فوائد دائنة",
    kindInterestCharged: "فوائد مدينة",
    kindOtherCredit: "حركة تزيد الحساب",
    kindOtherDebit: "حركة تنقص الحساب",
    status: "الحالة",
    statusDraft: "مسودة",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
    totalDebit: "مجموع المدين",
    totalCredit: "مجموع الدائن",
    balanced: "القيد متوازن",
    save: "حفظ",
    post: "ترحيل",
    reverse: "عكس القيد",
    reverseHint: "يُنشأ قيد عكسي مقابل. القيد الأصلي لا يُعدَّل أبداً.",
    reversalDate: "تاريخ العكس",
    reason: "السبب",
    delete: "حذف",
    deleteConfirm: "سيتم حذف هذا المستند وسنده. هل تريد المتابعة؟",
    newRecord: "حركة جديدة",
    search: "بحث برقم السند أو المرجع",
    empty: "لا توجد حركات مسجلة.",
    loading: "جارٍ التحميل…",
    saving: "جارٍ الحفظ…",
    saved: "تم الحفظ.",
    posted: "تم الترحيل إلى الأستاذ العام.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    required: "هذا الحقل مطلوب.",
    cancel: "إلغاء",
    actions: "الإجراءات",
    refresh: "تحديث",
    fromAccount: "من حساب",
    toAccount: "إلى حساب",
    sentAmount: "المبلغ المسحوب",
    receivedAmount: "المبلغ الواصل",
    fee: "العمولة",
    feeAccount: "حساب العمولة",
    feeBorneBySource: "العمولة على الحساب المُرسِل",
    transferHint: "المسحوب = الواصل + العمولة. مستند واحد وقيد واحد، لا حركتين منفصلتين.",
    fxHint: "التحويل بين عملتين يُسجَّل بسعرَي البنك، وأي فرق يذهب لحساب فروقات العملة.",
    reconciliationNumber: "رقم التسوية",
    period: "الفترة",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    statementDate: "تاريخ الكشف",
    statementBalance: "الرصيد حسب كشف البنك",
    ledgerBalance: "الرصيد الدفتري",
    reconciledBalance: "الرصيد بعد التسوية",
    difference: "الفرق",
    differenceHint: "فرق غير صفري نتيجة، لا خطأ يُخفى. التسوية تُفسّر الفرق ولا تُنشئه.",
    ledgerLines: "حركات دفاترنا",
    statementLines: "حركات كشف البنك",
    ledgerLinesHint: "تُقرأ آلياً من الحركات المرحّلة على الحساب. لا تُدخل يدوياً.",
    matched: "مطابق",
    unmatched: "غير مطابق",
    outstandingDeposits: "إيداعات لم يُظهرها البنك",
    outstandingPayments: "مدفوعات لم يُظهرها البنك",
    unrecorded: "حركات بنكية غير مسجلة لدينا",
    unrecordedHint: "كل حركة هنا مستند ناقص يجب تسجيله بمستنده الحقيقي، لا بالتأشير عليه.",
    addStatementLine: "إضافة حركة كشف",
    chequeNumber: "رقم الشيك",
    complete: "اعتماد التسوية",
    completeHint: "تصبح التسوية سجلاً للقراءة فقط بعد الاعتماد.",
    completed: "تم اعتماد التسوية.",
    nonAccounting: "التسوية البنكية لا تُنتج أي قيد محاسبي.",
    creditNumber: "رقم الاعتماد",
    beneficiary: "المستفيد",
    creditAmount: "قيمة الاعتماد",
    margin: "التأمين النقدي",
    marginAccount: "حساب التأمين النقدي",
    marginPaidFrom: "التأمين مدفوع من",
    requestDate: "تاريخ الطلب",
    openedDate: "تاريخ الفتح",
    expiryDate: "تاريخ الانتهاء",
    latestShipmentDate: "آخر موعد للشحن",
    goodsDescription: "وصف البضاعة",
    daysToExpiry: "أيام حتى الانتهاء",
    lcStatusRequested: "طلب",
    lcStatusOpened: "مفتوح",
    lcStatusSettled: "مسوّى",
    lcStatusCancelled: "ملغى",
    openCredit: "فتح الاعتماد",
    settleCredit: "تسوية الاعتماد",
    cancelCredit: "إلغاء الاعتماد",
    lcAccountingHint: "الفتح يُسجّل التأمين النقدي فقط. البضاعة والذمة يسجّلهما مستند الشراء لا الاعتماد.",
    loanNumber: "رقم القرض",
    principal: "مبلغ القرض",
    interestRate: "سعر الفائدة %",
    interestAccount: "حساب الفوائد",
    loanAccount: "حساب القرض",
    repaymentType: "نوع السداد",
    numberOfPayments: "عدد الأقساط",
    yearDays: "أيام السنة المعتمدة",
    drawdownDate: "تاريخ السحب",
    firstInstallmentDate: "تاريخ أول قسط",
    schedule: "جدول الأقساط",
    installment: "القسط",
    principalPart: "الأصل",
    interestPart: "الفائدة",
    installmentTotal: "إجمالي القسط",
    paid: "مسدد",
    unpaid: "غير مسدد",
    paidDate: "تاريخ السداد",
    paidFrom: "مسدد من",
    outstandingPrincipal: "الأصل المتبقي",
    scheduledInterest: "إجمالي الفوائد المجدولة",
    drawdown: "سحب القرض",
    drawdownHint: "يُثبت الدين ويصل المال. الفوائد لا تُسجَّل هنا بل مع كل قسط.",
    repay: "سداد القسط",
    repayHint: "الأصل والفائدة في قيد واحد، لأن البنك يأخذ دفعة واحدة.",
    loanStatusDraft: "مسودة",
    loanStatusActive: "مسحوب",
    loanStatusSettled: "مسدد",
    repayEqual: "أقساط متساوية",
    repayDecreasing: "أقساط متناقصة",
    repayLumpSum: "دفعة واحدة عند الاستحقاق",
    repayInterestOnly: "فائدة فقط ثم أصل",
    scheduleHint: "يُبنى الجدول كاملاً عند الحفظ، ومجموع الأصل يساوي مبلغ القرض تماماً.",
    screens: {
      bankTransactions: { title: "حركات البنوك", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « حركات البنوك", description: "العمولات والفوائد وما يظهر في كشف البنك دون مستند آخر يفسّره." },
      interbankTransfer: { title: "التحويل بين البنوك", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « التحويل بين البنوك", description: "نقل المال بين حسابين من حساباتنا. مستند واحد وقيد واحد." },
      calculateBankInterest: { title: "حساب الفوائد البنكية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « حساب الفوائد البنكية", description: "الفوائد الدائنة والمدينة التي قيّدها البنك على الحساب." },
      bankReconciliation: { title: "التسوية البنكية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « التسوية البنكية", description: "مطابقة كشف البنك مع دفاترنا. لا تُنتج أي قيد محاسبي." },
      lettersOfCredit: { title: "الاعتمادات المستندية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « الاعتمادات المستندية", description: "سجل الاعتمادات: الفتح والتسوية والإلغاء." },
      letterOfCreditRequest: { title: "طلب تجهيز اعتماد مستندي", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « طلب تجهيز اعتماد مستندي", description: "طلبات الاعتمادات التي لم تُفتح بعد لدى البنك. لا تُنتج قيداً." },
      bankLoans: { title: "القروض البنكية", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « القروض البنكية", description: "القروض المسحوبة على تسهيلات القروض، وجداول سدادها." },
      repayLoanInstallments: { title: "سداد أقساط القروض", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « سداد أقساط القروض", description: "الأقساط المستحقة على كل القروض النشطة." },
    },
  },
  inventoryLinks: {
    title: "الربط المالي للمخزون",
    breadcrumb: "المخزون والمستودعات / الإعدادات",
    notice: "أربعة حسابات: المخزون، وأرباح وخسائر التسوية، وتكلفة الصرف. الترتيب عند الترحيل هو المادة ← المجموعة ← المستودع ← هذا الربط، وما لا يُحدَّد هنا يمنع الترحيل برسالة account_not_configured.",
  },
  inventoryMaster: {
    printError: "تعذّرت الطباعة أو التصدير.",
    inquiry: "استعلام", inquiryTitle: "السجلات المعرَّفة", newRecord: "جديد", save: "حفظ",
    saveAndNew: "حفظ + جديد", print: "طباعة", cancel: "إلغاء", delete: "حذف", close: "إغلاق",
    search: "بحث",
    saved: "تم الحفظ.", deleted: "تم الحذف.",
    saveError: "تعذّر الحفظ", deleteError: "تعذّر الحذف", loadError: "تعذّر التحميل",
    readOnly: "لديك صلاحية عرض فقط على هذه البطاقة.",
    editing: "أنت تعدّل سجلاً قائماً. اضغط «جديد» لبدء سجل فارغ.",
    noRecords: "لا توجد سجلات مطابقة.", none: "— بدون —",
    typeToSearch: "اكتب للبحث…", noMatch: "لا نتائج مطابقة",
    code: "الرمز", nameAr: "الاسم بالعربية", nameEn: "الاسم بالإنجليزية",
    active: "فعّال", notes: "ملاحظات",
    cards: {
      items: "بطاقة المادة", itemGroups: "مجموعات المواد", itemCategories: "تصنيفات المواد",
      units: "وحدات القياس", conversions: "تحويل وحدات القياس", warehouses: "المستودعات",
      locations: "مواقع التخزين", reasons: "أسباب حركات المخزون",
    },
    units: {
      decimalAllowed: "تقبل الكسور",
      decimalAllowedHint: "الكيلوغرام يقبل الكسور والقطعة لا. يُفرض على الخادم عند إدخال أي كمية.",
      itemCount: "عدد المواد",
    },
    groups: {
      parent: "المجموعة الرئيسية",
      parentHint: "تغيير المجموعة الرئيسية يُعيد ترتيب مسارات كل الفروع تلقائياً.",
      path: "المسار", itemCount: "عدد المواد",
    },
    warehouses: {
      branch: "الفرع (اختياري)",
      branchHint: "يمكن تعريف المستودع الآن وتحديد فرعه لاحقاً — اتركه فارغاً إن لم يتحدد بعد.",
      custodian: "أمين المستودع", account: "حساب المخزون (اختياري)",
      accountHint: "يُترك فارغاً في الغالب: الربط المالي على مستوى الشركة يجيب عن كل المستودعات.",
      hasMovements: "عليه حركات",
    },
    commercial: {
      prices: "الأسعار", pricesHint: "سعر بيع، لا تكلفة. تكلفة المخزون تأتي من دفتر الحركات وحده، ولا شيء هنا يكتبها.",
      priceList: "قائمة الأسعار", price: "السعر", currency: "العملة",
      from: "من تاريخ", to: "إلى تاريخ", status: "الحالة", openEnded: "مفتوح",
      active: "ساري", ended: "منتهٍ", noPrices: "لا يوجد سعر لهذا الصنف بعد.", addPrice: "إضافة سعر",
      suppliers: "الموردون", suppliersHint: "من سجل الشركاء نفسه — لا سجل موردين ثانٍ. «آخر سعر شراء» للتفاوض فقط ولا يُقيَّم به المخزون.",
      supplier: "المورّد", supplierItemCode: "رمز الصنف لدى المورّد",
      preferred: "المفضل", leadTime: "مدة التوريد (يوم)", moq: "أقل كمية طلب",
      lastPurchasePrice: "آخر سعر شراء",
      noSuppliers: "لا يوجد مورّد مرتبط بهذا الصنف.", addSupplier: "إضافة مورّد",
      choose: "اختر…", remove: "حذف",
      saveFirst: "احفظ بطاقة الصنف أولاً، ثم تظهر الأسعار والموردون.",
      saveFailed: "تعذّر الحفظ.",
    },
    categories: {
      level: "المستوى",
      levelHint: "1 أو 2 أو 3 — أي عمود تصنيف في المصدر ينتمي إليه. ليست شجرة، ولا علاقة لها بمجموعة الصنف.",
    },
    locations: {
      warehouse: "المستودع",
      warehouseHint: "الموقع يتبع مستودعاً واحداً، والموقع الأب يجب أن يكون في نفس المستودع.",
      parent: "الموقع الأب", path: "المسار",
    },
    reasons: {
      direction: "الاتجاه", scope: "النطاق", requiresNotes: "يتطلب ملاحظات",
      requiresNotesHint: "مثل «تالف»: الرمز يقول ماذا حدث، والملاحظة تقول ما الذي تلف فعلاً.",
      requiresApproval: "يتطلب اعتماداً", account: "الحساب (اختياري)",
      directions: { "0": "زيادة ونقص", "1": "زيادة فقط", "2": "نقص فقط" },
      scopes: { "1": "تسوية مخزون", "2": "إخراج مخزني", "3": "إدخال مخزني", "9": "أي مستند" },
    },
    item: {
      barcode: "الباركود", barcodeHint: "اختياري، ولا يجوز أن يتكرر بين مادتين فعّالتين.",
      category1: "تصنيف مادة 1", category2: "تصنيف مادة 2", category3: "تصنيف مادة 3",
      previous: "السابق", next: "التالي", backToRegister: "العودة إلى السجل",
      categoryHint: "ثلاثة تصنيفات مستقلة من المصدر، لا شجرة — ومنفصلة تماماً عن مجموعة الصنف.",
      group: "المجموعة", category: "التصنيف", type: "نوع المادة",
      baseUnit: "الوحدة الأساسية",
      baseUnitHint: "الوحدة التي يُخزَّن ويُقيَّم بها المخزون. كل كمية تاريخية معبَّر عنها بها.",
      baseUnitLocked: "للمادة حركات مخزنية — لا يمكن تغيير الوحدة الأساسية.",
      purchaseUnit: "وحدة الشراء", salesUnit: "وحدة البيع",
      purchasable: "قابل للشراء", sellable: "قابل للبيع",
      minimumStock: "الحد الأدنى", reorderLevel: "حد إعادة الطلب",
      defaultWarehouse: "المستودع الافتراضي",
      countryOfOrigin: "بلد المنشأ (اختياري)",
      countryOfOriginHint: "من سجل الدول في النظام — لا يُكتب نصاً حراً.",
      purchaseTax: "ضريبة الشراء الافتراضية (اختياري)",
      purchaseTaxHint: "تُقترح على سطر فاتورة الشراء، والسطر يبقى صاحب القرار.",
      inventoryAccount: "حساب المخزون", cogsAccount: "حساب تكلفة الصرف",
      adjustmentAccount: "حساب التسوية",
      accountsHint: "تُترك فارغة في الغالب: الترتيب هو المادة ← المجموعة ← المستودع ← الربط المالي للشركة.",
      conversions: "تحويلات الوحدات", conversionsHint: "مثال: ١ كرتونة = ١٢ قطعة.",
      addConversion: "إضافة تحويل", unit: "الوحدة", factor: "المعامل إلى الوحدة الأساسية",
      onHand: "الرصيد الحالي", onHandValue: "قيمة المخزون", averageCost: "متوسط التكلفة",
      derivedHint: "قيم مشتقّة من دفتر المخزون — لا تُعدَّل من البطاقة، وتتغيّر بالحركات فقط.",
      types: { "1": "مادة مخزنية", "2": "مادة غير مخزنية", "3": "خدمة" },
    },
  },
  inventorySettings: {
    title: "إعدادات المخزون",
    breadcrumb: "المخزون والمستودعات / الإعدادات",
    save: "حفظ",
    saved: "حُفظت الإعدادات. تسري على الحركات القادمة فقط ولا تمسّ ما رُحِّل سابقاً.",
    saveError: "تعذّر حفظ الإعدادات",
    loadError: "تعذّر تحميل الإعدادات",
    readOnly: "لديك صلاحية عرض فقط. تعديل الإعدادات للمدير.",
    neverSaved: "لم تُحفظ الإعدادات لهذه الشركة بعد — النظام يعمل بالقيم الافتراضية الآمنة المعروضة أدناه.",
    notSet: "غير محدد",
    readiness: "جاهزية الإعداد",
    canPost: "جاهز للترحيل",
    cannotPost: "غير جاهز للترحيل",
    blocksPosting: "يمنع الترحيل",
    groups: { general: "عام", costing: "التكلفة والتقييم", count: "الجرد", purchase: "المشتريات والاستلام",
      issue: "الصرف", warehouse: "المستودعات والحركة", transfer: "التحويلات", numbering: "الترقيم" },
    allowNegativeStock: "السماح بالمخزون السالب",
    allowNegativeStockHint: "عند الإيقاف يرفض الخادم أي إخراج يجعل رصيد المادة في المستودع أقل من صفر.",
    allowFractionalQuantity: "السماح بالكسور في الكمية",
    allowFractionalQuantityHint: "وحدة القياس تبقى الفيصل: الكسر مرفوض إن كانت الوحدة نفسها لا تقبله.",
    quantityPrecision: "دقة الكمية",
    quantityPrecisionHint: "تدوير محاسبي يُطبَّق قبل التخزين والاحتساب، لا تنسيق عرض.",
    costPrecision: "دقة التكلفة",
    costPrecisionHint: "أدق من دقة العملة عمداً: تدوير تكلفة الوحدة مبكراً يُبعد التقييم عن حساب المراقبة.",
    blockInactiveItems: "منع الحركة على مادة غير فعّالة",
    blockInactiveWarehouses: "منع الحركة على مستودع غير فعّال",
    defaultWarehouse: "المستودع الافتراضي",
    costingMethod: "طريقة التكلفة",
    movingAverage: "المتوسط المرجح المتحرك",
    costingHint: "لكل مادة في كل مستودع على حدة. طريقة واحدة مطبَّقة فعلياً؛ لا تُعرض طرق غير منفَّذة.",
    allowBackdated: "السماح بحركات بتاريخ سابق",
    allowBackdatedHint: "مقيَّد دائماً بأن تكون الفترة المحاسبية مفتوحة.",
    allowBackdatedAfter: "السماح بالإدراج خلف حركات قائمة",
    allowBackdatedAfterHint: "يُنصح بإبقائه مغلقاً: المتوسط المرجح يعتمد على الترتيب، وإدراج حركة خلف حركات قائمة يُبطل كل تكلفة وقيد بعدها.",
    freezeDuringCount: "تجميد الحركة أثناء الجرد",
    freezeDuringCountHint: "يمنع أي حركة في مستودع فيه جرد مفتوح حتى إغلاقه.",
    showCostDuringCount: "إظهار التكلفة أثناء الجرد",
    showCostDuringCountHint: "عرض فقط — لا يغيّر أي احتساب.",
    createAdjustmentFromCount: "إنشاء تسوية فرق بعد اعتماد الجرد",
    createAdjustmentFromCountHint: "يُنشئ مستند تسوية **مسودّة**؛ الجرد نفسه لا يعدّل الرصيد أبداً.",
    requireSupplierInvoiceNumber: "إلزام رقم فاتورة المورد",
    requireSupplierInvoiceDate: "إلزام تاريخ فاتورة المورد",
    preventDuplicateSupplierInvoice: "منع تكرار رقم فاتورة المورد",
    preventDuplicateHint: "نفس المورد ونفس الرقم يُرفض على الخادم — أشيع سبب لسداد فاتورة مرتين.",
    requirePurchaseOrder: "إلزام مرجع أمر الشراء",
    allowPurchaseWithoutOrder: "السماح بفاتورة مباشرة بلا أمر شراء",
    exclusiveHint: "هذان الخياران متنافيان؛ تفعيل أحدهما يُلغي الآخر، والخادم يرفض حفظهما معاً.",
    allowQuantityAboveOrder: "السماح بكمية أعلى من أمر الشراء",
    quantityTolerance: "نسبة السماح في الكمية ٪",
    quantityToleranceHint: "تُقرأ فقط عند السماح بتجاوز كمية أمر الشراء.",
    defaultPurchaseWarehouse: "مستودع الشراء الافتراضي",
    requireStorageLocation: "إلزام موقع التخزين",
    requireStorageLocationHint: "عند التفعيل يجب أن تسمّي كل حركة موقعاً داخل المستودع. يبقى مطفأً ما لم تُعرَّف مواقع فعلاً.",
    warehouseRules: "قواعد مفروضة دائماً ولا تُضبط: كل حركة على مادة مخزنية تتطلب مستودعاً · التحويل يتطلب مصدراً ووجهة مختلفين · المادة والمستودع يجب أن يكونا فعّالين.",
    requireCostCenterOnIssue: "إلزام مركز التكلفة على الصرف",
    requireCostCenterHint: "عند التفعيل يُرفض أي إخراج مخزني بلا مركز تكلفة.",
    requireIssueReason: "إلزام سبب الصرف",
    allowDirectIssue: "السماح بالصرف المباشر",
    requireTransferApproval: "إلزام اعتماد التحويل",
    requireTransferApprovalHint: "يستخدم دورة مسودّة ← مدقق ← مرحّل نفسها، بلا حالات اعتماد جديدة.",
    requireTransferReceipt: "إلزام تأكيد الاستلام (تحويل بخطوتين)",
    transferReceiptUnsupported: "غير متاح: يحتاج مستودعاً وسيطاً غير مبني بعد — لو فُعِّل لاختفت الكمية من إجمالي الشركة أثناء النقل.",
    transferNoJournal: "التحويل بين المستودعات لا يُنتج قيداً محاسبياً: إجمالي الشركة كميةً وقيمةً لا يتغيّر. يُستثنى ما لو اختلف حساب المخزون بين المستودعين.",
    numberingHint: "الترقيم من محرك التسلسلات القائم نفسه: يُخصَّص الرقم مرة عند أول حفظ، ولا يتغيّر بالتعديل ولا التدقيق ولا الترحيل ولا الإلغاء. التصفير سنوي و٦ خانات.",
    documentType: "نوع المستند",
    prefix: "شكل الرقم",
    sequences: [
      { code: "MPI", name: "فاتورة مشتريات مواد" },
      { code: "MPR", name: "مردود مشتريات مواد" },
      { code: "SRC", name: "إدخال مخزني" },
      { code: "SIS", name: "إخراج مخزني" },
      { code: "STR", name: "تحويل بين المستودعات" },
      { code: "SAD", name: "تسوية مخزون" },
      { code: "SCT", name: "جرد مخزون" },
      { code: "SCA", name: "تسوية فروقات الجرد" },
      { code: "MPQ", name: "طلب شراء مواد" },
      { code: "MPO", name: "أمر شراء مواد" },
    ],
  },
  bankStatement: {
    section: "كشف البنك",
    attach: "إرفاق ملف",
    reread: "إعادة القراءة بالتعيين الجديد",
    noFile: "لم يُرفق كشف بعد. يُقبل Excel (.xlsx) وCSV وPDF.",
    fileName: "الملف",
    uploadedAt: "تاريخ الرفع",
    uploadedBy: "رفعه",
    rows: "الحركات",
    parseStatus: "حالة القراءة",
    parseStatuses: { "1": "بانتظار القراءة", "2": "تمت القراءة", "3": "لا حركات", "4": "تعذّرت القراءة" },
    mapping: "تعيين الأعمدة",
    mappingHint: "هذا ما فهمه النظام من عناوين أعمدة البنك. صحّحه إن لزم ثم اضغط «إعادة القراءة».",
    columns: {
      "0": "— تجاهل —", "1": "التاريخ", "2": "تاريخ القيمة", "3": "المرجع", "4": "رقم الشيك",
      "5": "البيان", "6": "مدين", "7": "دائن", "8": "المبلغ", "9": "الرصيد",
    },
    comparison: "المقارنة",
    runMatch: "مطابقة آلية",
    amountTolerance: "سماحية المبلغ",
    dateTolerance: "سماحية التاريخ (أيام)",
    toleranceWarning: "تنبيه: السماحية غير صفرية — الفروقات ضمنها ستُعدّ مطابقة.",
    imported: "تمت قراءة الملف. عدد الحركات:",
    manuallyMatched: "تمت المطابقة اليدوية.",
    unmatched: "أُلغيت المطابقة. لم يُحذف أي طرف.",
    ledgerRows: "سطور النظام",
    statementRows: "سطور الكشف",
    ledgerTotal: "إجمالي حركات النظام",
    statementTotal: "إجمالي حركات الكشف",
    ledgerBalance: "رصيد النظام",
    statementBalance: "رصيد كشف البنك",
    difference: "الفرق",
    counts: "التصنيف",
    status: "الحالة",
    bankDate: "تاريخ البنك",
    erpDate: "تاريخ النظام",
    reference: "المرجع",
    cheque: "الشيك",
    statementAmount: "مبلغ الكشف",
    erpAmount: "مبلغ النظام",
    gap: "الفرق",
    description: "البيان",
    action: "الإجراء",
    matchManually: "مطابقة يدوية",
    choosing: "اختر سطر النظام…",
    pairWith: "طابق مع هذا",
    unmatch: "إلغاء المطابقة",
    pairingHint: "اختر الآن سطر النظام المقابل من الجدول أدناه.",
    manualTag: "(يدوي)",
    noAutoFix: "النظام يكشف الفروقات ولا يصحّحها: لا يُنشئ قيداً ولا يعدّل حركة بسبب ملف مرفوع. القيد التصحيحي قرار المحاسب في شاشته الخاصة.",
    matchTypes: {
      "0": "غير مطابق", "1": "مطابق", "2": "فرق مبلغ", "3": "فرق تاريخ", "4": "اختلاف مرجع",
      "5": "بالنظام فقط", "6": "بالكشف فقط", "7": "احتمال تكرار", "8": "أكثر من احتمال",
    },
  },
  chequeBatch: {
    documentSection: "بيانات السند",
    searchSection: "بحث الشيكات",
    eligibleSection: "الشيكات المؤهلة",
    resultSection: "نتيجة الحفظ",
    date: "التاريخ",
    reference: "رقم الإشعار",
    referenceHint: "رقم إشعار البنك أو قسيمة الإيداع — هو هوية هذا السند.",
    notes: "البيان",
    choose: "اختر…",
    chequeNumberFrom: "من رقم شيك",
    chequeNumberTo: "إلى رقم شيك",
    chequeDateFrom: "من تاريخ شيك",
    chequeDateTo: "إلى تاريخ شيك",
    dueFrom: "من تاريخ استحقاق",
    dueTo: "إلى تاريخ استحقاق",
    amountFrom: "من مبلغ",
    amountTo: "إلى مبلغ",
    party: "العميل",
    partyHint: "جزء من اسم العميل أو رقم الشيك.",
    chequeDate: "تاريخ الشيك",
    drawerBank: "البنك المسحوب عليه",
    searchCheques: "بحث الشيكات",
    clearFilters: "مسح الفلاتر",
    documentInquiry: "استعلام السند",
    pressSearch: "اضغط «بحث الشيكات» لعرض الشيكات المؤهلة. لا تُحمَّل الشيكات تلقائياً — أنت من يختار ما يدخل في السند. أما «استعلام» في الشريط فيفتح السندات المحفوظة سابقاً.",
    noEligible: "لا توجد شيكات مؤهلة بهذه الشروط.",
    noDocuments: "لا توجد سندات محفوظة بعد.",
    select: "اختيار",
    selectAll: "اختيار الكل",
    selectedCount: "عدد الشيكات المختارة",
    selectedTotal: "إجمالي المختار",
    selectAtLeastOne: "اختر شيكاً واحداً على الأقل.",
    referenceRequired: "أدخل رقم إشعار البنك.",
    saved: "تم حفظ السند",
    chequeCount: "عدد الشيكات",
    journalVoucher: "سند القيد",
    savedSlip: "سند محفوظ",
    statusThen: "الحالة عند الحركة",
    statusNow: "الحالة الآن",
    total: "الإجمالي",
    tools: "أدوات",
    readOnly: "لديك صلاحية عرض فقط على هذه الشاشة.",
    reason: "السبب",
    reasonRequired: "أدخل السبب.",
    deliveredTo: "سُلّم إلى",
    deliveredToHint: "اسم من استلم الورقة فعلياً، إن لم يكن المستفيد نفسه.",
    newDueDate: "تاريخ الاستحقاق الجديد",
    newDueDateHint: "يُحفظ التاريخ القديم في سجل حركات الشيك ولا يُمحى.",
    newDueDateRequired: "أدخل تاريخ الاستحقاق الجديد.",
    processing: "المعالجة",
    represent: "إعادة تقديم",
    handBack: "تسليم للعميل",
    legal: "تحويل للقضايا",
    redeliver: "إعادة التسليم",
    voidCheque: "إلغاء الشيك",
    operationalNote: "هذه حركة تشغيلية ولا تُنتج قيداً محاسبياً.",
    postedNothing: "تمّت العملية ولم تُنتج قيداً: القيد المحاسبي كان قد سُجِّل في مرحلة سابقة من حياة الشيك. مثال: الشيك المدفوع الفوري يُقيَّد على البنك عند تحريره، فصرفه لا يحرّك رصيداً.",
    operation: "العملية",
    operations: {
      depositForCollection: "إيداع برسم التحصيل",
      depositImmediately: "إيداع فوري وتحصيل",
      collect: "تحصيل",
      withdraw: "سحب من التحصيل",
      returnUnpaid: "ارتجاع من البنك",
      handBack: "تسليم الشيك للعميل",
      processReturn: "معالجة شيك مرتجع",
      deliver: "تسليم الشيك",
      settle: "صرف الشيك",
      returnIssued: "ارتجاع شيك مدفوع",
      processIssuedReturn: "معالجة شيك مدفوع مرتجع",
      cancel: "إلغاء الشيك",
      changeDueDate: "تغيير تاريخ الاستحقاق",
    },
  },
  ownerNote: {
    label: "شرح الشاشة",
    dismiss: "إخفاء",
    dismissHint: "إخفاء الشرح على هذه الشاشة نهائياً",
  },
  journalEntryDialog: {
    title: "القيد المحاسبي",
    close: "إغلاق",
    noVoucher: "لا يوجد قيد لهذه الحركة بعد. احفظ الحركة أولاً.",
    voucherNumber: "رقم السند",
    date: "التاريخ",
    account: "الحساب",
    party: "الطرف",
    costCenter: "مركز التكلفة",
    debit: "مدين",
    credit: "دائن",
    total: "الإجمالي",
    balanced: "القيد متوازن: مجموع المدين = مجموع الدائن.",
    notBalanced: "القيد غير متوازن. الفرق:",
    operationalOnly: "هذه حركة تشغيلية ولا تُنتج قيداً محاسبياً: قيمة الشيك ما زالت في حساب الشيكات برسم التحصيل الخاص بالصندوق، ولا تنتقل إلى البنك إلا عند التحصيل الفعلي.",
    viewJournal: "عرض القيد",
  },
  chequeDesk: {
    chequeNumber: "رقم الشيك",
    party: "الطرف",
    drawer: "الساحب",
    beneficiary: "المستفيد",
    bank: "البنك",
    bankAccount: "الحساب البنكي",
    drawnOnAccount: "الحساب المسحوب عليه",
    cashbox: "الصندوق",
    chequeDate: "تاريخ الشيك",
    dueDate: "تاريخ الاستحقاق",
    amount: "المبلغ",
    currency: "العملة",
    status: "الحالة",
    term: "النوع",
    direction: "الاتجاه",
    daysOverdue: "أيام التأخير",
    notDueYet: "لم يستحق بعد",
    notes: "ملاحظات",
    reference: "المرجع",
    deliveredTo: "سُلّم إلى",
    deliveredDate: "تاريخ التسليم",
    returnReason: "سبب الارتجاع",
    cancellationReason: "سبب الإلغاء",
    settlementVoucher: "سند التسوية",
    search: "بحث برقم الشيك أو اسم الطرف",
    empty: "لا توجد شيكات مطابقة.",
    loading: "جارٍ التحميل…",
    totals: "الإجماليات",
    totalsHint: "الإجمالي لكل عملة على حدة. الشيكات بعملات مختلفة لا تُجمع.",
    count: "العدد",
    actions: "الإجراءات",
    history: "السجل",
    historyTitle: "حركات الشيك",
    noHistory: "لا توجد حركات مسجلة على هذا الشيك.",
    movementDate: "التاريخ",
    movement: "الحركة",
    journal: "القيد",
    noJournal: "بدون قيد",
    operationalOnly: "حركة تشغيلية — لا تُنتج قيداً محاسبياً.",
    date: "التاريخ",
    reason: "السبب",
    newDueDate: "تاريخ الاستحقاق الجديد",
    confirm: "تأكيد",
    cancelButton: "إلغاء",
    submitting: "جارٍ التنفيذ…",
    done: "تم تنفيذ الحركة.",
    required: "هذا الحقل مطلوب.",
    chooseBankAccount: "اختر الحساب البنكي",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكنك تنفيذ حركات على الشيكات.",
    noAction: "لا يوجد إجراء متاح لهذا الشيك في حالته الحالية.",
    refresh: "تحديث",
    filters: "عوامل التصفية",
    allCurrencies: "كل العملات",
    dueFrom: "الاستحقاق من",
    dueTo: "الاستحقاق إلى",
    statusUnderCollection: "برسم التحصيل",
    statusDeposited: "مودع للتحصيل",
    statusCleared: "محصّل",
    statusBounced: "مرتجع",
    statusReturned: "مُعاد للساحب",
    statusIssued: "محرر غير مسلّم",
    statusDelivered: "مُسلّم",
    statusPaid: "مصروف",
    statusReturnedUnpaid: "مرتجع من البنك",
    statusCancelled: "ملغى",
    termImmediate: "فوري",
    termPostDated: "آجل",
    directionIncoming: "مقبوض",
    directionOutgoing: "مدفوع",
    mvReceived: "استلام",
    mvDepositedForCollection: "إيداع برسم التحصيل",
    mvCollected: "تحصيل",
    mvWithdrawnFromCollection: "سحب من التحصيل",
    mvReturnedByBank: "ارتجاع من البنك",
    mvReturnedChequeProcessed: "معالجة شيك مرتجع",
    mvDepositedImmediately: "إيداع فوري",
    mvIssued: "تحرير",
    mvDelivered: "تسليم",
    mvPaid: "صرف",
    mvReturnedToUs: "ارتجاع شيك مدفوع",
    mvReturnedPaidChequeProcessed: "معالجة شيك مدفوع مرتجع",
    mvCancelled: "إلغاء",
    mvDueDateChanged: "تغيير تاريخ الاستحقاق",
    mvWithdrawnFromChequeBox: "سحب من صندوق الشيكات",
    mvHandedBackToDrawer: "إعادة للساحب",
    actDepositForCollection: "إيداع برسم التحصيل",
    actDepositImmediately: "إيداع وتحصيل",
    actCollect: "تحصيل",
    actWithdraw: "سحب من التحصيل",
    actReturnUnpaid: "تسجيل ارتجاع",
    actHandBack: "إعادة للعميل",
    actProcessReturn: "معالجة",
    actIssue: "تحرير شيك",
    actDeliver: "تسليم",
    actSettle: "تسجيل الصرف",
    actReturnIssued: "تسجيل ارتجاع",
    actProcessIssuedReturn: "معالجة",
    actCancel: "إلغاء الشيك",
    actChangeDueDate: "تغيير الاستحقاق",
    procRepresent: "إعادة تقديم",
    procRepresentHint: "يعود الشيك برسم التحصيل، ويُعكس قيد الارتجاع.",
    procLegal: "تحويل للقضايا",
    procLegalHint: "ينتقل المبلغ إلى حساب الشيكات المرتجعة (القضايا) المحدد على الصندوق.",
    procHandBack: "تسليم للعميل",
    procHandBackHint: "بدون قيد — المبلغ ما زال على العميل منذ الارتجاع.",
    procRedeliver: "إعادة التسليم",
    procRedeliverHint: "يُعاد قيد التحرير، ويصبح الشيك مُسلّماً من جديد.",
    procVoid: "إلغاء الشيك",
    procVoidHint: "بدون قيد — الارتجاع عكس قيد التحرير أصلاً.",
    screens: {
      immediateChequeDeposit: { title: "إيداع شيك فوري", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « إيداع شيك فوري", description: "شيكات فورية مقبوضة تُودع مباشرة في حساب بنكي وتُعامل كمحصّلة." },
      chequeDepositCollection: { title: "إيداع شيك برسم التحصيل", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « إيداع شيك برسم التحصيل", description: "تسليم الشيك المقبوض إلى البنك لتحصيله. حركة تشغيلية لا تُنتج قيداً." },
      collectChequeCollection: { title: "تحصيل شيك برسم التحصيل", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تحصيل شيك برسم التحصيل", description: "الشيكات المودعة لدى البنك التي وصلت قيمتها." },
      withdrawChequesCollection: { title: "سحب شيكات برسم التحصيل", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « سحب شيكات برسم التحصيل", description: "استرجاع شيك من البنك قبل تحصيله. ليس ارتجاعاً — البنك لم يرفضه." },
      returnedCheques: { title: "الشيكات المرتجعة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « الشيكات المرتجعة", description: "تسجيل أن البنك أعاد شيكاً مقبوضاً دون تحصيل." },
      processReturnedCheques: { title: "معالجة الشيكات المرتجعة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « معالجة الشيكات المرتجعة", description: "ما الذي سيحدث للشيك المرتجع: إعادة تقديم، أو تحويل للقضايا، أو تسليمه للعميل." },
      immediateChequePayment: { title: "دفع شيك فوري", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « دفع شيك فوري", description: "تحرير شيك فوري من حسابنا. يُدائن البنك فور التحرير." },
      collectPostDatedCheques: { title: "تحصيل شيكات آجلة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تحصيل شيكات آجلة", description: "الشيكات الآجلة المقبوضة التي حلّ أجلها." },
      deliverPostDatedCheque: { title: "تسليم شيك آجل", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تسليم شيك آجل", description: "تحرير شيك آجل. يُدائن حساب الشيكات الآجلة برسم الدفع، لا البنك." },
      deliverImmediatePostDated: { title: "تسليم شيكات فورية / آجلة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تسليم شيكات فورية / آجلة", description: "الشيكات المحررة التي ما زالت في الدرج ولم تُسلَّم بعد." },
      returnImmediatePostDated: { title: "إرجاع شيك فوري / آجل", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « إرجاع شيك فوري / آجل", description: "تسجيل أن البنك أعاد أحد شيكاتنا دون صرف." },
      processReturnedPaidCheques: { title: "معالجة الشيكات المدفوعة المرتجعة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « معالجة الشيكات المدفوعة المرتجعة", description: "ما الذي سيحدث لشيكنا المرتجع: إعادة تسليمه أو إلغاؤه." },
      changeChequeDueDate: { title: "تغيير تاريخ استحقاق شيك", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تغيير تاريخ استحقاق شيك", description: "نقل تاريخ الاستحقاق. لا يُنتج قيداً — تاريخ الاستحقاق ليس مبلغاً." },
      cancelCheque: { title: "إلغاء شيك", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « إلغاء شيك", description: "إلغاء أحد شيكاتنا قبل أن يصرفه البنك." },
      chequeBooks: { title: "سحب / تسليم من صندوق الشيكات", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « سحب / تسليم من صندوق الشيكات", description: "دفاتر الشيكات وأوراقها. ضبط قرطاسية لا يُنتج أي قيد." },
      dueChequesReminder: { title: "تنبيه الشيكات المستحقة", breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « تنبيه الشيكات المستحقة", description: "الشيكات التي حلّ أجلها أو يوشك، في الاتجاهين." },
    },
  },
  app: {
    name: "Business OS",
    // NOT a missing translation — do not "fix" this. The company name is a
    // trademark and is set in Latin wherever it appears, exactly as `name`
    // above already is. Arabic bidi places the run correctly inside the RTL
    // line, so it needs no wrapper.
    byline: "by Blink Connected",
    description: "منظومة متكاملة لإدارة أعمال المنشأة",
  },
  auth: {
    signInTitle: "تسجيل الدخول",
    signInSubtitle: "أدخل بيانات حسابك للمتابعة",
    userLabel: "اسم المستخدم أو البريد الإلكتروني",
    passwordLabel: "كلمة المرور",
    submit: "تسجيل الدخول",
    submitting: "جارٍ تسجيل الدخول…",
    errors: {
      userRequired: "الرجاء إدخال اسم المستخدم أو البريد الإلكتروني",
      passwordRequired: "الرجاء إدخال كلمة المرور",
      invalidCredentials: "اسم المستخدم أو كلمة المرور غير صحيحة",
      tenantNotFound: "رمز المنشأة غير صحيح",
      accountLocked: "تم إيقاف هذا الحساب. يرجى التواصل مع مسؤول النظام",
      mustChangePassword: "يجب تغيير كلمة المرور قبل متابعة الاستخدام",
      sessionExpired: "انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى",
      forbidden: "ليس لديك صلاحية للوصول إلى هذه الشاشة",
      forbiddenHint: "يرجى التواصل مع مسؤول النظام لمنحك الصلاحية اللازمة",
      network: "تعذّر الاتصال بالخادم. تحقّق من اتصالك ثم أعد المحاولة",
      unknown: "تعذّر إتمام العملية. يرجى المحاولة مرة أخرى",
    },
  },
  dataTable: {
    search: "بحث", columns: "الأعمدة", print: "طباعة", pdf: "PDF", excel: "Excel",
    previous: "السابق", next: "التالي", rowsPerPage: "لكل صفحة",
    showing: "عرض {from}–{to} من {total}",
    selected: "{count} محدد", selectAll: "تحديد كل الصفحة", selectRow: "تحديد السطر",
    emptyTitle: "لا توجد سجلات مطابقة.",
    view: "عرض", edit: "تعديل", review: "تدقيق", post: "ترحيل", cancel: "إلغاء",
    actions: "إجراءات",
  },
  shell: {
    navigation: "التنقل الرئيسي",
    breadcrumb: "مسار التنقل",
    skipToContent: "تخطَّ إلى المحتوى الرئيسي",
    openScreens: "الشاشات المفتوحة",
    closeScreen: "إغلاق الشاشة",
    closeOtherScreens: "إغلاق باقي الشاشات",
    toggleNavigation: "إظهار أو إخفاء قائمة التنقل",
    closeNavigation: "إغلاق قائمة التنقل",
    primaryNavigation: "التنقل الرئيسي",
    module: "الموديول",
    searchTheSystem: "بحث في النظام",
    collapseSidebar: "طيّ قائمة التنقل",
    expandSidebar: "توسيع قائمة التنقل",
    expandSection: "توسيع القسم",
    collapseSection: "طيّ القسم",
    search: "بحث",
    searchPlaceholder: "ابحث عن حساب أو مركز تكلفة أو عملة…",
    searchHint: "اكتب حرفين على الأقل",
    searchNoResults: "لا توجد نتائج",
    searchSearching: "جارٍ البحث…",
    notifications: "الإشعارات",
    notificationsEmpty: "لا توجد إشعارات",
    notificationsEmptyHint: "ستظهر هنا تنبيهات النظام فور تفعيلها",
    fiscalYear: "السنة المالية",
    fiscalYearUnavailable: "غير محدّدة",
    switchCompany: "تبديل الشركة",
    openMenu: "فتح القائمة",
    account: "الحساب",
    company: "الشركة",
    branch: "الفرع",
    signOut: "تسجيل الخروج",
    languageToggle: "تغيير اللغة",
    themeToggle: "تبديل المظهر",
    themeLight: "المظهر الفاتح",
    themeDark: "المظهر الداكن",
  },
  nav: {
    home: "الرئيسية",
    customerTransactionSettlement: "تسوية حركات العميل",
    dashboard: "لوحة المعلومات",
    comingSoon: "قريباً",

    financialManagement: "المحاسبة العامة",
    humanResources: "إدارة الموارد البشرية",
    hotelFrontDesk: "الاستقبال",
    systemSettings: "إعدادات النظام",

    generalLedger: "دفتر الأستاذ العام",
    cashAndBanks: "النقدية والبنوك",
    payments: "المدفوعات",
    receipts: "المقبوضات",
    fixedAssets: "الأصول الثابتة",
    inventory: "المخزون والمستودعات",

    planningAndRecruitment: "تخطيط وتوظيف",
    employeeAffairs: "شؤون موظفين",
    payrollAndWages: "رواتب وأجور",
    trainingAndDevelopment: "تدريب وتطوير",
    attendanceMonitoring: "مراقبة الدوام",

    employees: "الموظفون",

    usersAndPermissions: "المستخدمون والصلاحيات",
    controlPanel: "لوحة التحكم",

    users: "المستخدمون",
    roles: "الأدوار",
    permissions: "الصلاحيات",
    auditTrail: "سجل تدقيق النظام",
    accessMatrix: "مصفوفة صلاحيات المستخدمين",
    companies: "المنشآت",
    branches: "الفروع",

    masterData: "البيانات الأساسية",
    identityCards: "البطاقات التعريفية",
    dailyTransactions: "الحركات اليومية",
    inquiries: "الاستعلامات",
    reports: "التقارير",
    settings: "الإعدادات",

    journalEntries: "القيود اليومية",
    journalAuditPosting: "تدقيق وترحيل القيود اليومية",

    dailyTransactionsInquiry: "استعلام الحركات اليومية",
    accountMovements: "حركات الحسابات",
    costCenterAccount: "حساب مركز التكلفة",
    cashSubledgerMovements: "حركات الذمم النقدية",
    unauditedJournalNotes: "ملاحظات القيود غير المدققة",
    taxSettings: "إعدادات الضرائب",
    detailedVoucherTax: "ضريبة السندات التفصيلية",
    financialPosition: "المركز المالي",
    movementsWithoutCostCenters: "الحركات بدون مراكز تكلفة",

    reportsAccounting: "التقارير المحاسبية",
    reportsVouchers: "كشوفات السندات",
    reportsBatchVoucherPrinting: "طباعة مجموعة سندات",
    reportsSalesperson: "تقارير مندوبي المبيعات",
    reportsCostCenters: "تقارير مراكز التكلفة",
    reportsTrialBalance: "موازين المراجعة",
    reportsClosing: "تقارير الإقفال",
    reportsCheques: "تقارير الشيكات",
    reportsCashBoxes: "تقارير الصناديق",
    reportsGeneral: "التقارير العامة",

    financialStatementsSetup: "إعداد القوائم المالية",
    freezeTransactions: "تجميد الحركات",
    accountCategories: "فئات الحسابات",
    accountTypes: "أنواع الحسابات",
    accountOpeningBalances: "الأرصدة الافتتاحية للحسابات",
    voucherTypes: "أنواع السندات",
    financialIntegrationSettings: "إعدادات الربط المالي",
    generalLedgerSettings: "إعدادات النظام",
    journalEntryAuditNotes: "ملاحظات تدقيق القيود",
    costCenterGroups: "مجموعات مراكز التكلفة",

    cashboxDefinition: "تعريف الصناديق",
    bankDefinition: "تعريف البنوك",
    bankingFacilities: "التسهيلات البنكية",

    cashDeposit: "إيداع نقدي",
    cashWithdrawal: "سحب نقدي",
    cashboxTransactions: "حركات الصناديق",
    immediateChequeDeposit: "إيداع شيك فوري",
    chequeDepositForCollection: "إيداع شيك برسم التحصيل",
    collectChequeForCollection: "تحصيل شيك برسم التحصيل",
    withdrawChequesForCollection: "سحب شيكات برسم التحصيل",
    returnedCheques: "الشيكات المرتجعة",
    processReturnedCheques: "معالجة الشيكات المرتجعة",
    immediateChequePayment: "دفع شيك فوري",
    collectPostDatedCheques: "تحصيل شيكات آجلة",
    deliverPostDatedCheque: "تسليم شيك آجل",
    deliverImmediateOrPostDatedCheques: "تسليم شيكات فورية / آجلة",
    returnImmediateOrPostDatedCheque: "إرجاع شيك فوري / آجل",
    processReturnedPaidCheques: "معالجة الشيكات المدفوعة المرتجعة",
    lettersOfCredit: "الاعتمادات المستندية",
    letterOfCreditPreparationRequest: "طلب تجهيز اعتماد مستندي",
    bankReconciliation: "التسوية البنكية",
    interbankTransfer: "التحويل بين البنوك",
    bankTransactions: "حركات البنوك",
    changeChequeDueDate: "تغيير تاريخ استحقاق شيك",
    cancelCheque: "إلغاء شيك",
    withdrawDeliverFromChequeCashbox: "سحب / تسليم من صندوق الشيكات",
    calculateBankInterest: "حساب الفوائد البنكية",
    bankLoans: "القروض البنكية",
    repayLoanInstallments: "سداد أقساط القروض",
    dueChequesReminder: "تنبيه الشيكات المستحقة",

    cashboxClosingTransactions: "حركات إغلاق الصناديق",
    bankAccountCurrencyLocal: "حساب بنكي - عملة - محلي",
    bankAccount: "حساب بنكي",
    chequeMovements: "حركات الشيكات",
    paidCheques: "الشيكات المدفوعة",
    receivedCheques: "الشيكات المقبوضة",
    immediatePostDatedChequeDelivery: "تسليم الشيكات الفورية / الآجلة",
    undeliveredCheques: "الشيكات غير المسلّمة",
    cashboxMovements: "كشف حركات الصناديق",
    cashboxAccount: "حساب الصندوق",
    cashboxCount: "جرد الصندوق",
    cashboxClosingFollowUp: "متابعة إغلاق الصناديق",
    missingChequeSequences: "تسلسلات الشيكات المفقودة",

    editCashboxData: "تعديل بيانات الصناديق",
    cashboxGroups: "مجموعات الصناديق",
    cashboxCashFloat: "العهدة النقدية للصندوق",
    bankOpeningBalances: "الأرصدة الافتتاحية للبنوك",
    cashboxOpeningBalances: "الأرصدة الافتتاحية للصناديق",
    cashAndBanksSystemSettings: "إعدادات نظام النقدية والبنوك",
    letterOfCreditTypes: "أنواع الاعتمادات المستندية",
    linkUsersToCashboxes: "ربط المستخدمين بالصناديق",
    reportSignatures: "تواقيع التقارير",
    chequeReturnReasons: "أسباب إرجاع الشيكات",
    paymentMethods: "طرق الدفع",
    chequeBooks: "دفاتر الشيكات",
    expenseTypes: "أنواع المصروفات",

    suppliers: "الموردون",
    userSupplierAccess: "ربط المستخدمين بالموردين",
    coordinators: "المنسقون",
    salespersons: "مندوبو المبيعات",
    collectors: "المحصّلون",
    drivers: "السائقون",

    creditNote: "إشعار دائن",
    debitNote: "إشعار مدين",
    paymentVoucher: "سند صرف",
    paymentOrder: "أمر صرف",
    paymentBooks: "دفاتر الصرف",
    servicePurchaseInvoice: "فاتورة شراء خدمات",
    servicePurchaseReturn: "مردود شراء خدمات",
    servicePurchaseRequest: "طلب شراء خدمات",
    supplierAccountSettlement: "تسوية حساب مورد",
    supplierCreditLimitAdjustment: "تعديل سقف ائتمان المورد",
    cancelInvoicePayment: "إلغاء سداد فاتورة",
    editSupplierInformation: "تعديل بيانات المورد",
    prepaidExpenses: "المصروفات المدفوعة مقدماً",
    supplierBalanceReminder: "تذكير بأرصدة الموردين",
    transferSupplierTransactions: "ترحيل حركات المورد",
    accountReconciliation: "مطابقة الحسابات",

    paymentOrdersNotInVouchers: "أوامر الصرف غير المدرجة في سندات الصرف",
    supplierInformation: "بيانات المورد",
    supplierMovements: "حركات المورد",
    supplierAccountMovements: "حركات حساب مورد",
    supplierAccountCurrencyLocal: "حساب المورد - عملة / محلي",
    totalSupplierPayables: "إجمالي ذمم الموردين",
    totalSupplierBalancesCostCenter: "إجمالي أرصدة الموردين - مركز التكلفة",
    expenseTransactions: "حركات المصروفات",
    expenseAccount: "حساب المصروفات",
    purchaseOrdersWaitingPayment: "أوامر الشراء بانتظار الدفع",
    deliverySchedule: "جدول التسليم",
    receiptAndPaymentBooks: "دفاتر القبض والصرف",
    paymentsDailyTransactionsInquiry: "الحركات اليومية للمدفوعات",
    paymentVoucherMovements: "حركات سندات الصرف",
    servicePurchaseDocumentMovements: "حركات مستندات شراء الخدمات",
    unsettledPaymentOrders: "أوامر الصرف غير المدرجة",
    supplierInformationInquiry: "بيانات المورد",
    supplierAccountCurrency: "حساب المورد - عملة / محلي",
    supplierBalancesCostCenter: "إجمالي أرصدة الموردين - مركز التكلفة",
    purchaseOrdersAwaitingPayment: "أوامر الشراء بانتظار الدفع",
    receiptAndPaymentBooksInquiry: "دفاتر القبض والصرف",
    supplierItemTransactions: "حركات المادة - المورد",
    outstandingSupplierInvoicesInquiry: "فواتير الموردين المستحقة",
    paidUnpaidSupplierInvoices: "الفواتير المسددة وغير المسددة",
    unappliedPaymentVouchers: "سندات الصرف بدون فواتير مسددة",
    expenseMovementsInquiry: "حركات المصروفات",
    expenseAccountInquiry: "حساب المصروفات",
    expenseMovementsCostCenter: "حركات المصروفات - مركز التكلفة",
    expenseAccountCostCenterInquiry: "حساب المصروفات - مركز التكلفة",
    customerAgingInquiry: "أعمار ذمم العملاء",
    purchaseItems: "مواد الشراء",
    purchaseOrders: "أوامر الشراء",
    paymentBooksInquiry: "دفاتر الصرف",
    missingPaymentBookSequences: "التسلسلات المفقودة لدفاتر الصرف",
    itemTransactionsSupplier: "حركات المادة - المورد",
    voucherTransactions: "حركات السندات",
    journalVouchersInquiry: "سندات القيود",
    serviceVoucherTransactions: "حركات السندات - خدمات",
    outstandingSupplierInvoices: "فواتير الموردين المستحقة",
    paidAndUnpaidInvoices: "الفواتير المسددة وغير المسددة",
    expenseAccountCostCenter: "حساب المصروفات - مركز التكلفة",
    supplierMovementsByCostCenter: "حركات المورد حسب مركز التكلفة",
    agingReport: "أعمار ذمم العملاء",
    paymentVouchersWithoutSettledInvoices: "سندات الصرف بدون فواتير مسددة",
    expenseTransactionsCostCenter: "حركات المصروفات - مركز التكلفة",

    reportsPayments: "تقارير المدفوعات",

    businessActivities: "الأنشطة التجارية",
    businessNature: "طبيعة العمل",
    supplierCategories: "فئات الموردين",
    supplierOpeningBalances: "الأرصدة الافتتاحية للموردين",
    workflowProcedures: "إجراءات سير العمل",
    workflowActions: "إجراءات تدفق العمل",
    paymentsSystemSettings: "إعدادات نظام المدفوعات",
    linkUsersToSuppliers: "ربط المستخدمين بالموردين",
    discountTypes: "أنواع الخصومات",
    servicePurchaseTypes: "أنواع شراء الخدمات",
    countriesAndRegions: "الدول والمناطق",
    documentTypes: "أنواع المستندات",
    supplierEvaluationCriteria: "معايير تقييم الموردين",
    agingPeriodDefinitions: "الفترات الزمنية لعمر الذمة",
    expenseOpeningBalances: "الأرصدة الافتتاحية للمصروفات",

    customers: "العملاء",

    receiptVoucher: "سند القبض",
    receiptOrder: "أمر القبض",
    receiptBooks: "دفتر القبض",
    serviceSalesInvoice: "فاتورة المبيعات",
    serviceSalesReturn: "مردودات المبيعات",
    serviceQuotation: "عرض سعر",
    deferredRevenue: "الإيرادات المؤجلة",
    advanceReceivedRevenue: "الإيرادات المقبوضة مقدماً",
    customerBalanceReminder: "تذكير بأرصدة العملاء",
    transferCustomerTransactions: "تحويل حركات عميل",
    customerSettlement: "تسوية العميل",
    cancelInvoiceSettlement: "إلغاء تسوية فاتورة",

    customerTransactions: "الحركات اليومية",
    customerAccountCurrencyLocal: "كشف حساب العميل / عملة",
    customerAccount: "كشف حساب العميل",
    totalCustomerBalances: "إجمالي ذمم العملاء",
    totalCustomerBalancesCostCenter: "كشف إجمالي ذمم العملاء - مركز التكلفة",
    receiptVouchersWithoutInvoices: "سندات القبض بدون فواتير",
    missingReceiptBookSequences: "التسلسلات المفقودة في دفاتر القبض",
    missingReceiptVoucherSequences: "التسلسلات المفقودة في سندات القبض",
    itemTransactionsCustomer: "حركات مادة عميل",
    outstandingCustomerInvoices: "فواتير العملاء المستحقة",
    shortAgingReport: "تقرير الأعمار المختصر",
    customerAccountCostCenter: "حساب العميل - مركز التكلفة",
    detailedAgingReport: "تقرير أعمار الذمم التفصيلي",

    reportsReceipts: "تقارير المقبوضات",

    customerCategories: "فئات العملاء",
    customerClasses: "أصناف العملاء",
    customerTypes: "أنواع العملاء",
    customerOpeningBalances: "الأرصدة الافتتاحية للعملاء",
    smsSettings: "إعدادات الرسائل النصية",
    ePaymentPolicy: "سياسة الدفع الإلكتروني",
    // Named verbatim as the approved reference writes it, replacing the earlier placeholder
    // "أنواع بيع الخدمات". The single nav slot below is its only reader.
    serviceSalesTypes: "أنواع مبيعات الخدمات",
    custodyCategories: "فئات العهد",
    linkUsersToCustomers: "ربط المستخدمين بالعملاء",

    assetGroups: "مجموعات الأصول",
    assetLocations: "مواقع الأصول",
    assetCard: "بطاقة الأصل",
    insuranceCompanies: "شركات التأمين",

    custodyDeposit: "إيداع عهدة",
    custodyIssue: "صرف عهدة",
    assetPurchaseReturns: "مردودات مشتريات أصول",
    assetPurchaseInvoice: "فاتورة مشتريات أصول",
    purchaseOrder: "أمر شراء",
    quotationComparison: "مقارنة عروض الأسعار",
    quotationAward: "اعتماد عروض الأسعار",
    requestForQuotation: "طلب عروض أسعار",
    assetSale: "بيع الأصل",
    assetDepreciation: "استهلاك الأصل",
    assetRevenue: "إيرادات الأصل",
    assetExpenseReturns: "مردودات مصاريف الأصول",
    assetExpenses: "مصاريف الأصول",
    changeAssetLocation: "تعديل موقع أصل",
    assetInsurance: "تأمين الأصول",
    changeAssetCustody: "تعديل عهدة الأصل",
    releaseReturnCustody: "إخراج من العهدة / إعادة للعهدة",
    assetItemTransfer: "تحويل أصل / مادة",
    assetInventoryCount: "جرد الأصول",
    assetInventoryWorksheet: "ورقة جرد الأصول",
    assetMaintenance: "صيانة الأصول",
    assetDisposal: "إتلاف الأصل",
    assetRevaluation: "إعادة تقييم الأصول",
    assetSplit: "فصل الأصول",

    assetLifeCycle: "دورة حياة الأصل",
    assetFinancialMovements: "حركات الأصول المالية",
    assetMovements: "حركات الأصول",
    assetRecords: "سجلات الأصول",
    assetItemTransferStatus: "حالة تحويل مواد الأصول",
    assetTree: "شجرة الأصول",

    reportsFixedAssets: "تقارير الأصول الثابتة",

    assetModels: "موديلات الأصول",
    brands: "العلامات التجارية",
    assetClasses: "أصناف الأصول",
    assetCategories: "فئات الأصول",
    assetProperties: "خصائص الأصول",
    fixedAssetsSystemSettings: "إعدادات النظام",
    shippingMethods: "طرق الشحن",
    assetCodingSettings: "إعدادات ترميز الأصول",
    salesTypes: "أنواع المبيعات",
    assetDisposalReasons: "أسباب إتلاف الأصول",
    assetTypes: "أنواع الأصول",
    deliveryMethods: "طرق التسليم",
    purchaseRoutes: "مسارات المشتريات",
    quotationTerms: "شروط عروض الأسعار",
  },
  overview: {
    chooseSection: "اختر أحد الأقسام للمتابعة",
    comingSoonHint: "هذه الشاشات ضمن الهيكل المعتمد وسيتم بناؤها في مرحلة لاحقة",
    moduleComingSoon: "هذه الوحدة قيد الإنشاء",
    moduleComingSoonHint:
      "تم اعتماد هيكل هذه الوحدة ولم تُبنَ شاشاتها بعد. ستحتوي على الأقسام الخمسة القياسية نفسها.",
    standardSections: "الأقسام القياسية",
    systemSettingsEmpty: "لا توجد إعدادات عامة بعد",
    systemSettingsHint:
      "هذا القسم مخصّص لإعدادات النظام العامة فقط. إعدادات دفتر الأستاذ العام موجودة تحت المحاسبة العامة › دفتر الأستاذ العام › الإعدادات.",
    open: "فتح",
  },
  dashboard: {
    placeholder: "يجري بناء وحدات النظام؛ ستظهر مؤشرات الأداء هنا فور اكتمالها",
    title: "لوحة المعلومات",
    subtitle: "نظرة عامة على بيانات المنشأة الأساسية",
    kpi: {
      accounts: "الحسابات",
      costCenters: "مراكز التكلفة",
      currencies: "العملات",
      reports: "التقارير المتاحة",
      postable: "قابل للترحيل",
      groups: "مجموعات",
      activeOf: "{active} مفعّل من {total}",
      baseCurrency: "الأساسية",
      categories: "{count} فئات",
    },
    quickActions: "إجراءات سريعة",
    quickActionsHint: "انتقل مباشرة إلى الشاشات الأكثر استخداماً",
    activity: "آخر النشاطات",
    activityEmpty: "لا توجد نشاطات مسجّلة",
    activityEmptyHint: "سجلّ التدقيق موجود في قاعدة البيانات، ولم تُفتح له واجهة قراءة بعد",
    financialRestricted: "لا تملك صلاحية عرض الملخص المالي.",
    financialAsOf: "منذ بداية",
    financial: "الملخص المالي",
    financialEmpty: "لا توجد بيانات مالية",
    financialEmptyHint: "تظهر الأرقام بعد ترحيل أول قيد؛ شاشات القيود لم تُبنَ بعد",
    notAvailable: "—",
    metrics: {
      revenue: "الإيرادات",
      expenses: "المصروفات",
      netResult: "صافي النتيجة",
      cash: "النقدية",
    },
    banks: {
      title: "أرصدة البنوك",
      hint: "تُقرأ من حساب كل بنك في الأستاذ العام.",
      empty: "لا توجد حسابات بنكية معرّفة",
      emptyHint: "عرِّف حساباً بنكياً وحسابه في الشجرة، ويظهر رصيده هنا.",
      restricted: "لا تملك صلاحية عرض أرصدة البنوك.",
      total: "الإجمالي",
      account: "الحساب",
      balance: "الرصيد",
      main: "رئيسي",
      asOf: "كما في",
    },
    sales: {
      title: "المبيعات",
      hint: "الفواتير المرحّلة فقط. الفواتير المعكوسة غير محتسبة.",
      restricted: "لا تملك صلاحية عرض المبيعات.",
      day: "اليوم",
      material: "مبيعات المواد",
      service: "مبيعات الخدمات",
      total: "الإجمالي",
      invoices: "فاتورة",
      noSales: "لا مبيعات في هذا اليوم",
      today: "اليوم",
      yesterday: "أمس",
    },
  },
  reports: {
    title: "مركز التقارير",
    subtitle: "تصفّح فئات التقارير المتاحة في النظام",
    empty: "لا توجد فئات تقارير",
    emptyHint: "لم تُضَف أي فئات تقارير إلى هذه المنشأة بعد",
    recent: "استُخدمت مؤخراً",
    recentEmpty: "لم تفتح أي تقرير بعد",
    favorites: "المفضلة",
    favoritesEmpty: "لم تضف أي تقرير للمفضلة",
    clearRecent: "مسح السجل",
    searchAll: "البحث في كل التقارير",
    searchAllPlaceholder: "ابحث في كل التقارير…",
    inCategory: "في",
    comingSoon: "قريباً",
    reportCount: {
      zero: "لا توجد تقارير",
      one: "تقرير واحد",
      two: "تقريران",
      few: "{count} تقارير",
      many: "{count} تقريراً",
      other: "{count} تقرير",
    },
    categoryCount: {
      zero: "لا توجد فئات",
      one: "فئة واحدة",
      two: "فئتان",
      few: "{count} فئات",
      many: "{count} فئة",
      other: "{count} فئة",
    },
    category: {
      backToCenter: "مركز التقارير",
      searchLabel: "البحث في التقارير",
      searchPlaceholder: "اكتب اسم التقرير…",
      clearSearch: "مسح البحث",
      searchResults: "{shown} من {total}",
      noMatches: "لا توجد نتائج مطابقة",
      noMatchesHint: "جرّب كلمة أخرى أو امسح البحث",
      empty: "لا توجد تقارير في هذه الفئة",
      emptyHint: "سيتم إضافة تقارير هذه الفئة لاحقاً",
      notFound: "الفئة غير موجودة",
      notFoundHint: "تأكد من الرابط أو ارجع إلى مركز التقارير",
      openReport: "فتح التقرير",
      addFavorite: "إضافة إلى المفضلة",
      removeFavorite: "إزالة من المفضلة",
    },
    parameters: {
      title: "خيارات التقرير",
      pending: "شاشة الخيارات قيد الإنشاء",
      pendingHint: "سيتم بناء خيارات هذا التقرير ومخرجاته في مرحلة لاحقة",
      blocked: "يتطلب هذا التقرير تفعيل/استكمال المصدر التشغيلي المرتبط به",
      blockedReasons: {
        cancelled: "الوظيفة المرتبطة بهذا التقرير مُلغاة ولم تُفعّل.",
        noDueDate: "لا يسجّل النظام تاريخ استحقاق أو شروط سداد تاريخية للفواتير، ولا يمكن اشتقاقها من الإعدادات الحالية.",
        noExpiry: "لا يوجد تاريخ انتهاء معتمد على مستندات العملاء والموردين.",
        noSource: "لا توجد وظيفة معتمدة تسجّل هذه البيانات.",
        noBills: "وحدة الكمبيالات غير مُفعّلة، ولا يجوز اعتبار الإشعارات أو الشيكات كمبيالات.",
        ambiguous: "المعنى المحاسبي لهذا التقرير غير محدد بمصدر معتمد.",
      },
      backToCategory: "رجوع إلى الفئة",
    },
  },
  masterData: {
    currencies: "العملات",
    costCenters: "مراكز التكلفة",
    chartOfAccounts: "شجرة الحسابات",
    add: "إضافة",
    addRoot: "إضافة رئيسي",
    addChild: "إضافة فرعي",
    edit: "تعديل",
    activate: "تفعيل",
    deactivate: "إيقاف",
    delete: "حذف",
    save: "حفظ",
    saving: "جارٍ الحفظ…",
    cancel: "إلغاء",
    close: "إغلاق",
    searchLabel: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الاسم…",
    clearSearch: "مسح البحث",
    listView: "عرض قائمة",
    treeView: "عرض شجرة",
    expandAll: "توسيع الكل",
    collapseAll: "طيّ الكل",
    expandNode: "توسيع",
    collapseNode: "طيّ",
    active: "مفعّل",
    inactive: "موقوف",
    status: "الحالة",
    actions: "إجراءات",
    empty: "لا توجد سجلات",
    emptyHint: "ابدأ بإضافة أول سجل",
    noMatches: "لا توجد نتائج مطابقة",
    noMatchesHint: "جرّب كلمة أخرى أو امسح البحث",
    loadFailed: "تعذّر تحميل البيانات",
    saveFailed: "تعذّر الحفظ",
    companyRequired: "لم يتم تحديد شركة. اختر شركة للمتابعة",
    createdOk: "تمت الإضافة بنجاح",
    updatedOk: "تم الحفظ بنجاح",
    activatedOk: "تم التفعيل",
    deactivatedOk: "تم الإيقاف",
    deletedOk: "تم الحذف",
    confirmDeleteTitle: "تأكيد الحذف",
    confirmDelete: "هل أنت متأكد من حذف «{name}»؟ لا يمكن التراجع عن هذا الإجراء.",
    confirmDeactivateTitle: "تأكيد الإيقاف",
    confirmDeactivate: "سيتم إيقاف «{name}» وكل ما يندرج تحته.",
    requiredField: "هذا الحقل مطلوب",
    code: "الرمز",
    nameEn: "الاسم بالإنجليزية",
    nameAr: "الاسم بالعربية",
    notes: "ملاحظات",
    parent: "الأصل",
    noParent: "بدون أصل (رئيسي)",
    level: "المستوى",
    filterAll: "الكل",
    filterActive: "المفعّلة",
    filterInactive: "الموقوفة",
    isGroup: "مجموعة",
    isGroupHint: "المجموعة تجمّع ما تحتها ولا يمكن الترحيل عليها مباشرة",
    detail: {
      title: "التفاصيل",
      noSelection: "لم يتم اختيار عنصر",
      noSelectionHint: "اختر عنصراً من القائمة لعرض تفاصيله",
      identity: "التعريف",
      classification: "التصنيف",
      hierarchy: "التسلسل",
      flags: "الخصائص",
      audit: "سجلّ التغيير",
      createdAt: "أُنشئ في",
      updatedAt: "آخر تعديل",
      openingBalance: "الرصيد الافتتاحي",
      debit: "مدين",
      credit: "دائن",
      parentPath: "المسار",
      children: "عدد الأبناء",
      backToList: "رجوع إلى القائمة",
    },
    currency: {
      symbol: "الرمز المختصر",
      decimalPlaces: "عدد الخانات العشرية",
      exchangeRate: "سعر الصرف",
      exchangeRateHint: "يُحفظ بتاريخ اليوم دون المساس بالأسعار السابقة",
      isBase: "العملة الأساسية",
      isBaseHint: "سعر صرفها 1 دائماً، ولا يمكن إيقافها أو حذفها",
      baseBadge: "أساسية",
      inUse: "قيد الاستخدام",
      inUseHint: "مستخدمة في حسابات أو قيود، فلا يمكن حذفها",
      codeLocked: "لا يمكن تعديل رمز العملة بعد إنشائها",
      currentRate: "سعر الصرف الحالي",
      effectiveFrom: "ساري من",
      effectiveTo: "ساري حتى",
      effectiveToHint: "اتركه فارغاً ليبقى السعر مفتوحاً",
      baseFilter: "العملة الأساسية",
      baseOnly: "الأساسية فقط",
      nonBaseOnly: "غير الأساسية",
      refresh: "تحديث",
      view: "عرض",
      addRate: "إضافة سعر صرف",
      rateHistory: "سجل أسعار الصرف",
      rateHistoryEmpty: "لا توجد أسعار صرف مسجّلة",
      rateAdded: "تمت إضافة سعر الصرف",
      current: "الحالي",
      superseded: "سابق",
      future: "مستقبلي",
      openEnded: "مفتوح",
      sectionBasic: "المعلومات الأساسية",
      sectionWordsAr: "التفقيط بالعربية",
      sectionWordsEn: "التفقيط بالإنجليزية",
      sectionRate: "سعر الصرف",
      iconUrl: "أيقونة العملة",
      iconUrlHint: "رابط صورة اختياري",
      wordsSingular: "المفرد",
      wordsDual: "المثنى",
      wordsPlural: "الجمع",
      wordsFractionSingular: "مفرد الكسر",
      wordsFractionDual: "مثنى الكسر",
      wordsFractionPlural: "جمع الكسر",
      wordsPreview: "معاينة التفقيط",
      wordsPreviewHint: "مثال على المبلغ 1,234.56 بإعدادات هذه العملة",
      wordsUnset: "لم يتم إعداد التفقيط بعد",
      baseRateFixed: "سعر العملة الأساسية دائماً 1",
      baseCannotDelete: "لا يمكن حذف أو إيقاف العملة الأساسية",
      rateNotSet: "غير محدد",
      rateOptionalHint: "اتركه فارغاً إذا لم يُحدَّد سعر صرف بعد",
      makeBase: "تعيين كعملة أساسية",
      makeBaseTitle: "تغيير العملة الأساسية",
      makeBaseMessage: "سيتم اعتماد «{code}» كعملة أساسية للشركة. لن يتم إعادة احتساب أي حركة سابقة؛ تحتفظ كل الحركات بأسعار الصرف المسجّلة عليها. اكتب رمز العملة للتأكيد.",
      makeBaseConfirm: "اكتب {code} للتأكيد",
      baseChanged: "تم تغيير العملة الأساسية",
      viewTitle: "تفاصيل العملة",
      sectionStatusBase: "الحالة والعملة الأساسية",
    },
    account: {
      taxSection: "إعدادات الضريبة",
      taxHint: "أنواع الضرائب التي تُرحّل إلى هذا الحساب. الربط يأتي من تعريف الضريبة نفسه، لا من اسم الحساب.",
      taxNone: "لا يوجد نوع ضريبة مرتبط بهذا الحساب.",
      taxConfigure: "إدارة أنواع الضرائب",
      taxActive: "مفعّل", taxInactive: "غير مفعّل",
      taxSideOutput: "مخرجات", taxSideInput: "مدخلات", taxSideBoth: "مدخلات ومخرجات",
      accountType: "نوع الحساب",
      accountTypeLookup: "نوع الحساب",
      normalBalance: "طبيعة الرصيد",
      financialStatement: "القائمة المالية",
      statements: { balanceSheet: "الميزانية العمومية", incomeStatement: "قائمة الدخل" },
      statementFollowsTypeHint: "تُحدَّد القائمة المالية بنوع الحساب: الأصول والالتزامات وحقوق الملكية في الميزانية العمومية، والإيرادات وتكلفة المبيعات والمصروفات في قائمة الدخل",
      debit: "مدين",
      credit: "دائن",
      allowPosting: "السماح بالترحيل",
      allowPostingHint: "الحسابات المجمّعة لا تقبل الترحيل",
      requiresCostCenter: "يتطلب مركز تكلفة",
      requiresCostCenterHint: "يلزم اختيار مركز تكلفة عند الترحيل على هذا الحساب",
      defaultCostCenter: "مركز التكلفة الافتراضي",
      defaultCostCenterHint: "مركز التكلفة الذي يُعبأ مسبقًا عند الترحيل على هذا الحساب",
      defaultCostCenterNone: "بدون",
      defaultCostCenterRequired: "مركز التكلفة الافتراضي إلزامي لهذا الحساب",
      systemAccount: "حساب نظام",
      systemAccountHint: "حساب يعتمد عليه النظام؛ لا يمكن حذفه أو إيقافه",
      category: "فئة الحساب",
      openingDate: "تاريخ الفتح",
      referenceNumber: "الرقم المرجعي",
      closed: "مغلق",
      closedHint: "الحساب المغلق لا يقبل قيوداً جديدة، مع الاحتفاظ بأرصدته وحركاته السابقة",
      typeLockedTitle: "نوع الحساب مقفل",
      typeLockedHint: "توجد حركات على هذا الحساب، لذا لا يمكن تغيير نوعه أو طبيعته أو قائمته المالية",
      costCentersHint: "حدّد مراكز التكلفة المسموح استخدامها عند الترحيل على هذا الحساب. اتركها فارغة للسماح بجميع المراكز.",
      types: {
        asset: "أصول",
        liability: "التزامات",
        equity: "حقوق ملكية",
        revenue: "إيرادات",
        expense: "مصروفات",
      },
    },
  },
  auditPosting: {
    errorSelfAudit: "الفصل بين المهام: لا يمكن للمستخدم الذي أدخل القيد أن يدققه. يجب أن يعتمده مستخدم آخر.",
    errorSelfPost: "الفصل بين المهام: لا يمكن للمستخدم الذي أدخل القيد أن يرحّله.",
    errorAlreadyAudited: "هذا القيد مدقق مسبقاً.",
    errorAlreadyPosted: "هذا القيد مرحّل مسبقاً.",
    errorInvalidAuditTransition: "لا يمكن تدقيق هذا القيد في حالته الحالية.",
    errorInvalidPostTransition: "لا يمكن ترحيل هذا القيد في حالته الحالية.",
    errorInvalidRejectTransition: "لا يمكن إرجاع هذا القيد في حالته الحالية.",
    errorCannotRejectPosted: "القيد مرحّل ولا يمكن إرجاعه؛ يجب عكسه بقيد معاكس.",
    errorAuditRequired: "يجب اعتماد التدقيق قبل الترحيل.",
    errorUnbalanced: "القيد غير متوازن. يجب أن يتساوى المدين والدائن.",
    errorZeroValue: "لا يمكن تدقيق قيد مجموعه صفر.",
    errorInsufficientLines: "يجب أن يحتوي القيد على سطرين على الأقل.",
    errorBranchOutOfScope: "هذا القيد يخص فرعاً آخر.",
    errorForbidden: "لا تملك الصلاحية اللازمة لهذا الإجراء.",
    title: "تدقيق وترحيل القيود اليومية",
    filters: "بحث وتصفية",
    search: "بحث",
    reset: "إعادة تعيين",
    results: "نتائج البحث",
    noResults: "لا توجد قيود مطابقة",
    openReview: "فتح للمراجعة",
    inquiryTitle: "استعلام",
    inquiryDialogTitle: "استعلام القيود",
    review: "مراجعة القيد",
    selectPrompt: "اختر مستنداً من الاستعلام للبدء بالمراجعة",
    auditStatus: "حالة التدقيق",
    postingStatus: "حالة الترحيل",
    validationStatus: "حالة التحقق",
    valid: "سليم",
    invalid: "غير سليم",
    pendingAudit: "بانتظار التدقيق",
    approved: "مدقق",
    rejected: "مرفوض",
    posted: "مرحّل",
    unposted: "غير مرحّل",
    auditDecision: "قرار التدقيق",
    auditNotes: "ملاحظات التدقيق",
    auditNotesHint: "تُحفظ منفصلة عن ملاحظات القيد، ويطّلع عليها المحاسب عند الإعادة",
    approveAudit: "اعتماد التدقيق",
    rejectReturn: "رفض وإعادة للمحاسب",
    saveNotes: "حفظ ملاحظات التدقيق",
    rejectReason: "سبب الرفض",
    rejectReasonRequired: "يجب إدخال سبب الرفض ليعرف المحاسب ما يجب تصحيحه",
    postJournal: "ترحيل القيد",
    postingBlocked: "لا يمكن ترحيل القيد للأسباب التالية",
    blockers: {
      noPermission: "لا تملك صلاحية ترحيل القيود.",
      alreadyPosted: "القيد {value} مُرحَّل مسبقاً.",
      rejected: "القيد مُعاد إلى المحاسب. يجب تصحيحه وتدقيقه قبل الترحيل.",
      pendingAudit: "القيد بانتظار التدقيق. اعتمِده من «تدقيق القيد» أولاً، ثم رحِّله.",
      wrongStatus: "حالة القيد «{value}» لا تسمح بالترحيل.",
      outOfBalance: "القيد غير متوازن بفرق {value}. المدين يجب أن يساوي الدائن.",
      periodClosed: "الفترة المحاسبية مغلقة، فلا يمكن الترحيل عليها.",
    },
    postingReady: "القيد جاهز للترحيل",
    confirmPost: "تأكيد الترحيل",
    confirmPostBody: "سيتم ترحيل القيد إلى الأستاذ العام وتحديث الأرصدة. لا يمكن التراجع؛ يتطلب الإلغاء قيداً عكسياً.",
    approvedOn: "اعتُمد في",
    rejectedOn: "رُفض في",
    postedOn: "رُحّل في",
    readOnlyReview: "بيانات القيد للعرض فقط أثناء التدقيق. التصحيح يتم بإعادته للمحاسب.",
    voucherTypeFrom: "نوع السند من",
    voucherTypeTo: "نوع السند إلى",
    voucherNumberFrom: "رقم السند من",
    voucherNumberTo: "رقم السند إلى",
  },
  inquiry: {
    title: "استعلام الحركات اليومية",
    filters: "بحث وتصفية",
    account: "اسم الحساب",
    includeChildren: "شمول الحسابات الفرعية",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    fiscalYear: "السنة المالية",
    branch: "الفرع",
    reportScope: "نطاق التقرير",
    scopeCompany: "الشركة كاملة",
    scopeBranch: "فرع محدد",
    scopeAllBranches: "تفصيل جميع الفروع",
    voucherFrom: "من رقم سند",
    voucherTo: "إلى رقم سند",
    referenceNumber: "رقم المرجع",
    referenceDateFrom: "من تاريخ المرجع",
    referenceDateTo: "إلى تاريخ المرجع",
    user: "المستخدم",
    salesperson: "اسم المندوب",
    costCenter: "مركز التكلفة",
    subledger: "الذمة",
    currency: "العملة",
    auditStatus: "حالة التدقيق",
    postingStatus: "حالة الترحيل",
    descriptionSearch: "البحث في البيان",
    selectAccountPrompt: "اختر حساباً من شجرة الحسابات ثم اضغط بحث",
    noResults: "لا توجد حركات مطابقة",
    results: "الحركات",
    voucherDate: "تاريخ السند",
    voucherNumber: "رقم السند",
    voucherType: "نوع السند",
    accountNumber: "رقم الحساب",
    accountName: "اسم الحساب",
    description: "البيان",
    exchangeRate: "سعر التحويل",
    debit: "مدين",
    credit: "دائن",
    localDebit: "مدين محلي",
    localCredit: "دائن محلي",
    sourceModule: "مصدر الحركة",
    movementCount: "عدد الحركات",
    totalDebit: "مجموع المدين",
    totalCredit: "مجموع الدائن",
    totalLocalDebit: "مجموع المدين المحلي",
    totalLocalCredit: "مجموع الدائن المحلي",
    netMovement: "صافي الحركة",
    posted: "مرحّل",
    unposted: "غير مرحّل",
    invalidRange: "يجب ألا يكون تاريخ البداية بعد تاريخ النهاية",
    balancesElsewhere: "الرصيد الافتتاحي والختامي يظهران في استعلام حركة الحساب",
  },
  cashSubledger: {
    title: "استعلام حركات الذمم النقدية",
    selectPrompt: "اختر نوع الذمة والفترة ثم اضغط بحث لعرض الحركات النقدية.",
    partyType: "نوع الذمة",
    partyAll: "الكل",
    partyCustomer: "عميل",
    partySupplier: "مورد",
    partyNone: "بدون ذمة",
    noPartyHint: "حركة نقدية لم يُحدَّد لها عميل أو مورد",
    customer: "العميل",
    supplier: "المورد",
    selectParty: "اختر…",
    partyCode: "رمز الذمة",
    partyName: "اسم الذمة",
    documentNumber: "رقم المستند",
    documentType: "نوع المستند",
    documentStatus: "حالة المستند",
    documentFrom: "من رقم مستند",
    documentTo: "إلى رقم مستند",
    openDocument: "فتح المستند",
    paymentMethod: "طريقة الدفع",
    methodCash: "نقدي",
    methodBankTransfer: "حوالة بنكية",
    methodCard: "بطاقة",
    methodCheque: "شيك",
    methodImmediate: "تسوية فورية",
    methodMixed: "دفع مختلط",
    cashbox: "الصندوق",
    bankAccount: "الحساب البنكي",
    severalAccounts: "أكثر من حساب",
    netAmount: "الصافي",
    taxAmount: "الضريبة",
    totalAmount: "الإجمالي",
    paidAmount: "المدفوع",
    outstandingAmount: "المتبقي",
    localAmount: "المبلغ المحلي",
    salesperson: "المندوب",
    notAvailable: "غير متاح",
    taxUnavailableHint: "الضريبة غير متاحة: لا يفصلها أي مستند في النظام الحالي",
    outstandingWarning: "المتبقي يجب أن يكون صفرًا في هذا الاستعلام؛ أي قيمة أخرى تعني خللًا في التصنيف",
    includeCancelled: "المستندات الملغاة",
    includeCancelledHint: "إظهار الملغاة والمعكوسة (خارج الإجماليات)",
    statusCancelled: "ملغى",
    statusReversed: "معكوس",
    excludedNote: "مستندات ملغاة أو معكوسة غير مشمولة في الإجماليات",
    documentCount: "عدد المستندات",
    totalNet: "إجمالي الصافي",
    totalTax: "إجمالي الضريبة",
    totalGross: "الإجمالي الكلي",
    totalPaid: "إجمالي المدفوع",
    totalOutstanding: "إجمالي المتبقي",
    totalLocal: "الإجمالي المحلي",
    totalCash: "إجمالي النقدي",
    totalBank: "إجمالي البنكي",
    totalCard: "إجمالي البطاقات",
    totalCheque: "إجمالي الشيكات",
    totalReturns: "إجمالي المرتجعات",
    totalNoParty: "حركات بدون ذمة",
    settlementNote: "إجمالي النقدي والبنكي محسوبان على مستوى المستندات لا الأسطر",
    unpostedNote: "تشمل الإجماليات حركات غير مرحّلة",
    notConfigured: "لم يُصنَّف أي حساب على أنه صندوق أو بنك، فلا يستطيع الاستعلام تمييز التسوية النقدية. صنِّف حسابات الصناديق والبنوك من تفاصيل الحساب.",
  },
  unauditedNotes: {
    title: "استعلام ملاحظات القيود غير المدققة",
    noResults: "لا توجد قيود مطابقة — لا شيء بانتظار التدقيق ضمن هذه المعايير.",
    scope: "نطاق الاستعلام",
    scopeOutstanding: "قيد المعالجة",
    scopeUnresolved: "ملاحظات تدقيق غير معالجة",
    scopeAll: "الكل",
    statePendingAudit: "بانتظار التدقيق",
    stateReturned: "معاد للتصحيح",
    stateResubmitted: "أعيد تقديمه",
    stateAudited: "مدقق",
    statePosted: "مرحّل",
    stateCancelled: "ملغى",
    stateReversed: "معكوس",
    voucherDate: "تاريخ السند",
    createdBy: "المستخدم المنشئ",
    auditor: "المدقق",
    lastAction: "آخر إجراء تدقيق",
    lastActionAt: "تاريخ آخر إجراء",
    actionCreated: "أُنشئ",
    actionModified: "عُدّل",
    actionAudited: "تم التدقيق",
    actionReturned: "أُعيد للتصحيح",
    actionPosted: "تم الترحيل",
    actionReversed: "تم العكس",
    auditNotes: "ملاحظات التدقيق",
    journalNotes: "ملاحظات القيد",
    noNote: "لا توجد ملاحظة",
    notesHidden: "غير مصرّح",
    notesHiddenHint: "لا تملك صلاحية قراءة ملاحظات التدقيق",
    unresolved: "غير معالجة",
    returnCount: "عدد مرات الإعادة",
    resubmissionCount: "عدد مرات إعادة التقديم",
    returnedRepeatedly: "أُعيد أكثر من مرة — التصحيح السابق لم يُقبل",
    totalDebit: "مجموع المدين",
    totalCredit: "مجموع الدائن",
    difference: "الفرق",
    validationStatus: "حالة التوازن",
    balanced: "متوازن",
    unbalanced: "غير متوازن",
    unbalancedHint: "المدين لا يساوي الدائن — لا يمكن اعتماد هذا القيد",
    createdAt: "تاريخ الإنشاء",
    lastModifiedAt: "آخر تعديل",
    openForCorrection: "فتح للتصحيح",
    openReadOnly: "عرض القيد",
    viewAuditHistory: "عرض سجل التدقيق",
    referenceDateFrom: "من تاريخ المرجع",
    referenceDateTo: "إلى تاريخ المرجع",
    auditNoteSearch: "البحث في ملاحظات التدقيق",
    journalNotesSearch: "البحث في ملاحظات القيد",
    returnedMoreThanOnce: "معاد للتصحيح أكثر من مرة",
    returnedMoreThanOnceHint: "المعاد أكثر من مرة فقط",
    unbalancedOnly: "غير المتوازنة",
    unbalancedOnlyHint: "القيود غير المتوازنة فقط",
    includeCancelled: "تضمين الملغى",
    includeReversed: "تضمين المعكوس",
    entryCount: "عدد القيود",
    pendingAuditCount: "بانتظار التدقيق",
    returnedCount: "معادة للتصحيح",
    resubmittedCount: "أعيد تقديمها",
    unresolvedNotesCount: "ملاحظات غير معالجة",
    unbalancedCount: "غير متوازنة",
    totalDifference: "مجموع الفروقات",
    ownEntriesOnly: "تعرض هذه القائمة القيود التي أنشأتها أنت فقط.",
    unbalancedWarning: "يوجد قيد غير متوازن لا يمكن اعتماده قبل تصحيحه",
  },
  freezeTransactions: {
    title: "تجميد الحركات",
    pageDescription: "تعريف الفترات التي تُمنع فيها الحركات، حسب الفرع ونوع السند.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل التجميد.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « تجميد الحركات",
    phaseNotice: "هذه المرحلة لتسجيل قرار التجميد فقط؛ لم يتم تفعيل المنع على القيود بعد.",
    sectionHeader: "بيانات التجميد", sectionScope: "النطاق", sectionVoucherTypes: "أنواع السندات المحددة",
    code: "الرمز", freezeDate: "تاريخ الإغلاق", fromDate: "من تاريخ", toDate: "إلى تاريخ",
    description: "البيان", freezeDateHint: "تاريخ تسجيل قرار التجميد، وليس ضمن الفترة الممنوعة.",
    branches: "الفروع", voucherTypes: "أنواع السندات", addVoucherTypes: "إضافة أنواع سندات",
    remove: "حذف", search: "بحث", refresh: "تحديث", save: "حفظ", cancel: "إلغاء",
    newFreeze: "تجميد جديد", active: "فعال", inactive: "غير فعال",
    activate: "تفعيل", deactivate: "إيقاف", catalogue: "سجلات التجميد", period: "الفترة",
    empty: "لا توجد سجلات تجميد.", emptyVoucherTypes: "لم يتم اختيار أي نوع سند بعد.",
    noneSelected: "لم يتم الاختيار", required: "حقل إلزامي",
    created: "تم إنشاء التجميد.", saved: "تم حفظ التجميد.",
    activated: "تم تفعيل التجميد.", deactivated: "تم إيقاف التجميد.",
    loadError: "تعذّر تحميل سجلات التجميد.", saveError: "تعذّر حفظ التجميد.",
    searchPlaceholder: "ابحث بالرمز أو البيان", selectAll: "تحديد الكل", clearAll: "إلغاء التحديد",
  },
  accountCategories: {
    title: "فئات الحسابات",
    pageDescription: "تعريف الفئات التي تُصنَّف عليها حسابات دليل الحسابات.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل فئات الحسابات.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « فئات الحسابات",
    sectionCategory: "فئة الحساب", code: "الرمز", descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي", active: "فعال", inactive: "غير فعال",
    catalogue: "فئات الحسابات المعرّفة", save: "حفظ", cancel: "إلغاء", deleteAction: "حذف",
    usage: "الاستخدام", inUse: "مستخدم", searchPlaceholder: "ابحث بالرمز أو الوصف",
    search: "بحث", refresh: "تحديث", empty: "لا توجد فئات مطابقة.", required: "حقل إلزامي",
    created: "تم إنشاء الفئة.", saved: "تم حفظ الفئة.", deleted: "تم حذف الفئة.",
    activated: "تم تفعيل الفئة.", deactivated: "تم إيقاف الفئة.",
    loadError: "تعذّر تحميل فئات الحسابات.", saveError: "تعذّر حفظ الفئة.",
    deleteTitle: "حذف الفئة", deleteConfirm: "سيتم حذف الفئة. هل تريد المتابعة؟",
    close: "إغلاق", comingSoonHint: "قريباً", print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
    inactiveHint: "الفئة غير الفعالة لا تظهر للحسابات الجديدة، وتبقى ظاهرة على الحسابات التي تحملها.",
  },
  accountTypes: {
    title: "أنواع الحسابات",
    pageDescription: "تعريف أنواع الحسابات التي تُصنَّف عليها حسابات دليل الحسابات.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل أنواع الحسابات.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « أنواع الحسابات",
    sectionAccountType: "نوع الحساب", code: "الرمز", descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي", scope: "نوع الحساب", local: "محلي", international: "دولي",
    active: "فعال", inactive: "غير فعال", catalogue: "أنواع الحسابات المعرّفة",
    newType: "نوع جديد", save: "حفظ", cancel: "إلغاء", deleteAction: "حذف",
    activate: "تفعيل", deactivate: "إيقاف", usage: "الاستخدام", inUse: "مستخدم",
    searchPlaceholder: "ابحث بالرمز أو الوصف", search: "بحث", refresh: "تحديث",
    empty: "لا توجد أنواع حسابات مطابقة.", required: "حقل إلزامي",
    created: "تم إنشاء نوع الحساب.", saved: "تم حفظ نوع الحساب.", deleted: "تم حذف نوع الحساب.",
    activated: "تم تفعيل نوع الحساب.", deactivated: "تم إيقاف نوع الحساب.",
    loadError: "تعذّر تحميل أنواع الحسابات.", saveError: "تعذّر حفظ نوع الحساب.",
    deleteTitle: "حذف نوع الحساب", deleteConfirm: "سيتم حذف نوع الحساب. هل تريد المتابعة؟",
    close: "إغلاق", comingSoonHint: "قريباً", print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
  },
  serviceSalesTypes: {
    title: "أنواع مبيعات الخدمات",
    pageDescription: "تعريف أنواع الخدمات التي تبيعها الشركة، والحساب ونوع الضريبة لكل نوع.",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « أنواع مبيعات الخدمات",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل أنواع مبيعات الخدمات.",
    purposeNotice: "تعريف فقط: الحساب ونوع الضريبة يُقرآن لاحقاً عند إصدار فاتورة خدمات، ولا يُنشأ عن هذه الشاشة أي قيد.",
    sectionServiceSalesType: "أنواع مبيعات الخدمات",
    catalogue: "الأنواع المعرّفة",
    code: "الرمز", descriptionAr: "الوصف عربي", descriptionEn: "الوصف إنجليزي",
    account: "الحساب", taxType: "نوع الضريبة", taxable: "خاضع للضريبة",
    active: "فعال", inactive: "غير فعال",
    selectAccount: "اختر الحساب", selectTaxType: "اختر نوع الضريبة", noTaxType: "— بدون —",
    yes: "نعم", no: "لا", back: "رجوع", query: "إستعلام", save: "حفظ",
    saveAndNew: "حفظ + جديد", cancel: "إلغاء", refresh: "تحديث", search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الوصف أو رقم الحساب",
    statusAll: "الكل", statusActive: "الفعالة", statusInactive: "غير الفعالة",
    activate: "تفعيل", deactivate: "إيقاف",
    auditHistory: "سجل التغييرات", auditNone: "لا توجد تغييرات مسجلة.", auditFields: "الحقول",
    close: "إغلاق", empty: "لا توجد أنواع مبيعات خدمات مطابقة.", required: "حقل إلزامي",
    taxTypeRequired: "يجب تحديد نوع الضريبة عندما يكون النوع خاضعاً للضريبة.",
    created: "تم إنشاء نوع مبيعات الخدمات.", saved: "تم حفظ نوع مبيعات الخدمات.",
    activated: "تم التفعيل.", deactivated: "تم الإيقاف.",
    loadError: "تعذّر تحميل أنواع مبيعات الخدمات.", saveError: "تعذّر حفظ نوع مبيعات الخدمات.",
    noDeleteHint: "النوع المستخدَم لا يُحذف؛ يتم إيقافه بدلاً من ذلك.",
    lastChanged: "آخر تعديل",
  },
  paymentMethodsScreen: {
    title: "طرق الدفع",
    pageDescription: "تعريف الطرق التي تُسدَّد بها المبالغ: كاش، فيزا، شيكات، حوالات وغيرها.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « طرق الدفع",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل طرق الدفع.",
    purposeNotice: "طريقة الدفع تصف كيف سُدِّد المبلغ وأين يستقر: الكاش والشيكات على صندوق، والفيزا والدفع الإلكتروني والحوالات على حساب بنك. تعريف فقط، ولا يُنشأ عنه أي قيد.",
    list: "طرق الدفع", code: "الرمز", descriptionAr: "الوصف عربي", descriptionEn: "الوصف إنجليزي",
    active: "فعال", inactive: "غير فعال", usage: "الاستخدام", actions: "الإجراءات",
    newMethod: "طريقة جديدة", save: "حفظ", cancel: "إلغاء", edit: "تعديل", remove: "حذف",
    activate: "تفعيل", deactivate: "إيقاف",
    search: "بحث", searchPlaceholder: "ابحث بالرمز أو الوصف", refresh: "تحديث",
    showInactive: "إظهار غير الفعالة", empty: "لا توجد طرق دفع.", required: "مطلوب",
    saved: "حُفظت طريقة الدفع.", deleted: "حُذفت طريقة الدفع.",
    activated: "فُعّلت.", deactivated: "أُوقفت.",
    loadError: "تعذّر تحميل طرق الدفع.", saveError: "تعذّر الحفظ.",
    deleteConfirm: "سيتم حذف طريقة الدفع. هل تريد المتابعة؟",
    inUseHint: "المستخدَمة لا تُحذف؛ أوقفها بدلاً من ذلك.",
    settlement: "وجهة التسديد", settlesInto: "تُرحَّل إلى",
    targetNone: "بدون وجهة", targetCashbox: "صندوق", targetBank: "بنك",
    cashbox: "الصندوق", bank: "البنك", bankBranch: "الفرع", bankAccount: "حساب البنك",
    settlementAccount: "حساب الوجهة", accountFromCashbox: "يُؤخذ من تعريف الصندوق",
    choose: "— اختر —", branchOptional: "— الفرع (اختياري) —",
    fromBankAccounts: "من حسابات البنك المعرّفة على هذا البنك.",
    noBankAccountsHint: "هذا البنك بلا حسابات معرّفة بعد؛ الاختيار من شجرة الحسابات.",
  },
  cashDeposit: {
    title: "إيداع نقدي",
    pageDescription: "إيداع النقدية في أحد حسابات الشركة البنكية.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « إيداع نقدي",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تسجيل أو تعديل الإيداعات.",
    purposeNotice: "من ح/ الحساب البنكي إلى ح/ حساب الصندوق الذي تم الإيداع منه. تفاصيل مراكز التكلفة أدناه توزّع الجهة الدائنة، وهي اختيارية.",
    sectionSources: "تفاصيل مراكز التكلفة",
    sequence: "التسلسل", assignedOnSave: "يُعطى عند الحفظ",
    documentDate: "تاريخ السند", bank: "البنك", bankBranch: "الفرع البنكي",
    bankAccount: "الحساب البنكي",
    bankAccountHint: "من حسابات البنك المختار فقط، وفعال وقابل للترحيل.",
    currency: "العملة", branch: "الفرع", fiscalYear: "السنة المالية",
    fromDocumentDate: "تُحدَّد من تاريخ السند",
    amount: "المبلغ", exchangeRate: "سعر التحويل",
    baseRateHint: "سعر تحويل العملة الأساسية دائماً 1.",
    localAmount: "المبلغ بالمحلي", description: "البيان",
    costCenter: "مركز التكلفة",
    noCostCenters: "لم تُدرج مراكز تكلفة — الإيداع كامله يُرحَّل على حساب الصندوق بدون توزيع.",
    cashbox: "الصندوق الذي تم الإيداع منه",
    cashboxHint: "الصناديق المفتوحة والفعالة فقط. حسابه النقدي هو الجهة الدائنة.",
    creditSide: "الجهة الدائنة",
    accountNumber: "رقم الحساب",
    reachingBank: "الواصل إلى البنك", reachingBankHint: "المبلغ ناقص المصاريف.",
    panels: {
      costCenters: "مراكز التكلفة", documents: "القبوضات والفواتير",
      attachments: "الملفات المرفقة", expenses: "أنواع المصاريف",
    },
    documentNumber: "رقم المستند",
    documentsHint: "أرقام القبوضات والفواتير التي يغطيها هذا الإيداع. تُسجَّل ولا تُرحَّل.",
    noDocuments: "لم تُدرج قبوضات أو فواتير.",
    expenseAccount: "حساب المصروف",
    expensesHint: "مصاريف البنك على هذا الإيداع — تُخصم من المبلغ الواصل للبنك.",
    noExpenses: "لا مصاريف على هذا الإيداع.",
    saveBeforeAttaching: "احفظ الإيداع أولاً لتتمكن من إرفاق الملفات.",
    tools: "أدوات",
    insertAllCostCenters: "إدراج جميع مراكز التكلفة",
    insertCostCentersByGroup: "إدراج مراكز التكلفة حسب المجموعة",
    deleteRecord: "حذف السجل",
    deleteConfirm: "سيتم حذف السند كاملاً. هل تريد المتابعة؟",
    deleted: "حُذف السند.",
    postedNotDeletable: "السند المُرحَّل لا يُحذف؛ استخدم عكس القيد.",
    linesTotal: "مجموع السطور",
    unbalancedHint: "متى بدأت التوزيع، مجموع مراكز التكلفة يجب أن يساوي مبلغ الإيداع.",
    actions: "الإجراءات", addRow: "اضافة سطر جديد", remove: "حذف",
    branchOptional: "— الفرع (اختياري) —", choose: "— اختر —", newDeposit: "إيداع جديد",
    inquiry: "إستعلام", save: "حفظ", saveAndNew: "حفظ وجديد", cancel: "إلغاء",
    post: "ترحيل", print: "طباعة", attachments: "المرفقات", auditTrail: "سجل التدقيق",
    searchPlaceholder: "ابحث بالتسلسل أو البيان", empty: "لا توجد إيداعات.",
    status: "الحالة", statusDraft: "غير مُرحَّل", statusPosted: "مُرحَّل",
    alreadyPosted: "هذا الإيداع مُرحَّل مسبقاً.",
    saved: "حُفظ الإيداع", postedNotice: "رُحِّل الإيداع",
    loadError: "تعذّر تحميل الإيداعات.", saveError: "تعذّر الحفظ.",
    printError: "تعذّرت الطباعة.",
  },
  cashWithdrawal: {
    title: "سحب نقدي",
    pageDescription: "سحب النقدية من أحد حسابات الشركة البنكية.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « سحب نقدي",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تسجيل أو تعديل السحوبات.",
    purposeNotice: "من ح/ حساب الصندوق الذي تم السحب إليه إلى ح/ الحساب البنكي. تفاصيل مراكز التكلفة أدناه توزّع الجهة المدينة، وهي اختيارية.",
    sectionSources: "تفاصيل مراكز التكلفة",
    sequence: "التسلسل", assignedOnSave: "يُعطى عند الحفظ",
    documentDate: "تاريخ السند", bank: "البنك", bankBranch: "الفرع البنكي",
    bankAccount: "الحساب البنكي",
    bankAccountHint: "من حسابات البنك المختار فقط، وفعال وقابل للترحيل.",
    currency: "العملة", branch: "الفرع", fiscalYear: "السنة المالية",
    fromDocumentDate: "تُحدَّد من تاريخ السند",
    amount: "المبلغ", exchangeRate: "سعر التحويل",
    baseRateHint: "سعر تحويل العملة الأساسية دائماً 1.",
    localAmount: "المبلغ بالمحلي", description: "البيان",
    costCenter: "مركز التكلفة",
    noCostCenters: "لم تُدرج مراكز تكلفة — السحب كامله يُرحَّل على حساب الصندوق بدون توزيع.",
    cashbox: "الصندوق الذي تم السحب إليه",
    cashboxHint: "الصناديق المفتوحة والفعالة فقط. حسابه النقدي هو الجهة المدينة.",
    creditSide: "الجهة المدينة",
    accountNumber: "رقم الحساب",
    panels: {
      costCenters: "مراكز التكلفة",
      attachments: "الملفات المرفقة",
    },
    saveBeforeAttaching: "احفظ السحب أولاً لتتمكن من إرفاق الملفات.",
    tools: "أدوات",
    insertAllCostCenters: "إدراج جميع مراكز التكلفة",
    insertCostCentersByGroup: "إدراج مراكز التكلفة حسب المجموعة",
    deleteRecord: "حذف السجل",
    deleteConfirm: "سيتم حذف السند كاملاً. هل تريد المتابعة؟",
    deleted: "حُذف السند.",
    postedNotDeletable: "السند المُرحَّل لا يُحذف؛ استخدم عكس القيد.",
    linesTotal: "مجموع السطور",
    unbalancedHint: "متى بدأت التوزيع، مجموع مراكز التكلفة يجب أن يساوي مبلغ السحب.",
    actions: "الإجراءات", addRow: "اضافة سطر جديد", remove: "حذف",
    branchOptional: "— الفرع (اختياري) —", choose: "— اختر —", newDeposit: "سحب جديد",
    inquiry: "إستعلام", save: "حفظ", saveAndNew: "حفظ وجديد", cancel: "إلغاء",
    post: "ترحيل", print: "طباعة", attachments: "المرفقات", auditTrail: "سجل التدقيق",
    searchPlaceholder: "ابحث بالتسلسل أو البيان", empty: "لا توجد سحوبات.",
    status: "الحالة", statusDraft: "غير مُرحَّل", statusPosted: "مُرحَّل",
    alreadyPosted: "هذا السحب مُرحَّل مسبقاً.",
    saved: "حُفظ السحب", postedNotice: "رُحِّل السحب",
    loadError: "تعذّر تحميل السحوبات.", saveError: "تعذّر الحفظ.",
    printError: "تعذّرت الطباعة.",
  },
  cashboxTransactions: {
    title: "حركات الصناديق",
    pageDescription: "الحركات المختلفة على الصناديق: تغذية، تصفير، جرد، محاسبة مندوبين، إيداع.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « الحركات اليومية « حركات الصناديق",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تسجيل أو تعديل حركات الصناديق.",
    purposeNotice: "اتجاه النقد يحدّد أي طرف مدين: الخارج من الصندوق يُجعله دائناً، والداخل إليه يُجعله مديناً. تفاصيل مراكز التكلفة توزّع جهة الصندوق، وهي اختيارية.",
    sectionSources: "تفاصيل مراكز التكلفة",
    sequence: "التسلسل", assignedOnSave: "يُعطى عند الحفظ",
    documentDate: "تاريخ السند", bank: "البنك", bankBranch: "الفرع البنكي",
    bankAccount: "الحساب البنكي",
    bankAccountHint: "من حسابات البنك المختار فقط، وفعال وقابل للترحيل.",
    currency: "العملة", branch: "الفرع", fiscalYear: "السنة المالية",
    fromDocumentDate: "تُحدَّد من تاريخ السند",
    amount: "المبلغ", exchangeRate: "سعر التحويل",
    baseRateHint: "سعر تحويل العملة الأساسية دائماً 1.",
    localAmount: "المبلغ بالمحلي", description: "البيان",
    costCenter: "مركز التكلفة",
    noCostCenters: "لم تُدرج مراكز تكلفة — السحب كامله يُرحَّل على حساب الصندوق بدون توزيع.",
    cashbox: "الصندوق",
    cashboxHint: "الصناديق المفتوحة والفعالة فقط. حسابه النقدي هو الجهة المدينة.",
    creditSide: "الجهة المدينة",
    accountNumber: "رقم الحساب",
    panels: {
      costCenters: "مراكز التكلفة",
      attachments: "الملفات المرفقة",
    },
    saveBeforeAttaching: "احفظ الحركة أولاً لتتمكن من إرفاق الملفات.",
    movementTime: "الوقت", direction: "إتجاه النقد", directionFrom: "من", directionTo: "إلى",
    partyType: "الطرف", partyCashbox: "صندوق", partyBank: "بنك", movementType: "نوع الحركة",
    typeDeposit: "إيداع", typeZeroing: "صفر الصندوق", typeFunding: "تغذية صندوق",
    typeCount: "محضر جرد صندوق", typeRepresentative: "محاسبة مندوبين",
    counterCashbox: "الصندوق الآخر", counterAccount: "الحساب المقابل",
    counterAccountHint: "حساب فروقات الصندوق أو رصيد افتتاحي أو حساب المندوب — من شجرة الحسابات.",
    tools: "أدوات",
    insertAllCostCenters: "إدراج جميع مراكز التكلفة",
    insertCostCentersByGroup: "إدراج مراكز التكلفة حسب المجموعة",
    deleteRecord: "حذف السجل",
    deleteConfirm: "سيتم حذف السند كاملاً. هل تريد المتابعة؟",
    deleted: "حُذف السند.",
    postedNotDeletable: "السند المُرحَّل لا يُحذف؛ استخدم عكس القيد.",
    linesTotal: "مجموع السطور",
    unbalancedHint: "متى بدأت التوزيع، مجموع مراكز التكلفة يجب أن يساوي مبلغ السحب.",
    actions: "الإجراءات", addRow: "اضافة سطر جديد", remove: "حذف",
    branchOptional: "— الفرع (اختياري) —", choose: "— اختر —", newDeposit: "حركة جديدة",
    inquiry: "إستعلام", save: "حفظ", saveAndNew: "حفظ وجديد", cancel: "إلغاء",
    post: "ترحيل", print: "طباعة", attachments: "المرفقات", auditTrail: "سجل التدقيق",
    searchPlaceholder: "ابحث بالتسلسل أو البيان", empty: "لا توجد حركات.",
    status: "الحالة", statusDraft: "غير مُرحَّل", statusPosted: "مُرحَّل",
    alreadyPosted: "هذه الحركة مُرحَّلة مسبقاً.",
    saved: "حُفظت الحركة", postedNotice: "رُحِّلت الحركة",
    loadError: "تعذّر تحميل الحركات.", saveError: "تعذّر الحفظ.",
    printError: "تعذّرت الطباعة.",
  },
  bankFacilities: {
    title: "التسهيلات البنكية",
    pageDescription: "تعريف التسهيلات الممنوحة من البنوك وشروطها ومصاريفها.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « البطاقات التعريفية « التسهيلات البنكية",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل التسهيلات.",
    purposeNotice: "هذه الشاشة تعريفية فقط: لا تسحب من التسهيل ولا تُنشئ قيداً ولا تؤثر على أرصدة الأستاذ العام.",
    sectionFacility: "بيانات التسهيل", sectionLoanAccount: "بيانات حساب القرض",
    sectionCurrentAccount: "بيانات الحساب الجاري", sectionExpenses: "المصاريف",
    list: "التسهيلات",
    code: "الرمز", facilityType: "نوع التسهيل", currency: "العملة", ceiling: "سقف التسهيل",
    interestRate: "نسبة الفائدة %", loanType: "نوع القرض", graceDays: "أيام السماح",
    yearDays: "أيام السنة المعتمدة", numberOfPayments: "عدد الدفعات",
    restrictionBasis: "أساس احتساب الفائدة",
    active: "فعال", inactive: "غير فعال", ceilingExceeded: "يسمح بتجاوز السقف",
    externalParty: "جهة خارجية",
    bank: "البنك", branch: "الفرع", account: "الحساب",
    branchOptional: "— الفرع (اختياري) —", choose: "— اختر —",
    fromBankAccounts: "من حسابات البنك المعرّفة على هذا البنك.",
    noBankAccountsHint: "هذا البنك بلا حسابات معرّفة بعد؛ الاختيار من شجرة الحسابات.",
    expenseAccount: "حساب المصروف",
    expenseAccountHint: "من شجرة الحسابات: حساب مصاريف فعال وقابل للترحيل.",
    percentage: "النسبة %",
    percentageHint: "إن أدخلت نسبة، تُحتسب القيمة من السقف ويُتجاهل المبلغ المُدخل.",
    amount: "القيمة", computed: "المحتسب", paymentMethod: "طريقة الدفع",
    methodOptional: "— طريقة الدفع (اختياري) —", actions: "الإجراءات",
    addRow: "اضافة سطر جديد", edit: "تعديل", remove: "حذف",
    newFacility: "تسهيل جديد", save: "حفظ", cancel: "إلغاء",
    activate: "تفعيل", deactivate: "إيقاف",
    search: "بحث", searchPlaceholder: "ابحث بالرمز", refresh: "تحديث",
    showInactive: "إظهار غير الفعالة",
    empty: "لا توجد تسهيلات بعد.", noExpenses: "لا توجد مصاريف على هذا التسهيل.",
    saveFirstHint: "احفظ التسهيل أولاً لتتمكن من إضافة المصاريف.",
    typeLoan: "قرض", typeOverdraft: "سحب على المكشوف", typeLetterOfCredit: "اعتماد مستندي",
    typeLetterOfGuarantee: "كفالة بنكية", typeChequesForCollection: "شيكات برسم التحصيل",
    typeBillDiscounting: "حسم كمبيالات",
    loanEqual: "أقساط متساوية", loanDecreasing: "أقساط متناقصة",
    loanLumpSum: "دفعة واحدة عند الاستحقاق", loanInterestFirst: "فائدة فقط ثم أصل",
    basisOutstanding: "الرصيد القائم", basisCeiling: "السقف المعتمد",
    basisUtilised: "المبلغ المستخدم", basisDaily: "الرصيد اليومي",
    saved: "حُفظ التسهيل.", activated: "فُعّل التسهيل.", deactivated: "أُوقف التسهيل.",
    expenseSaved: "حُفظ سطر المصروف.", expenseRemoved: "حُذف سطر المصروف.",
    expenseDeleteConfirm: "سيتم حذف سطر المصروف. هل تريد المتابعة؟",
    loadError: "تعذّر تحميل التسهيلات.", saveError: "تعذّر الحفظ.",
  },
  banks: {
    title: "تعريف البنوك",
    pageDescription: "تعريف البنوك التي تتعامل معها الشركة وفروعها.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « البطاقات التعريفية « تعريف البنوك",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل البنوك.",
    purposeNotice: "هذه الشاشة تعريفية فقط: لا تنشئ قيداً ولا تؤثر على أرصدة الأستاذ العام.",
    templateNotice: "لا توجد وحدة قوالب لطباعة الشيكات بعد؛ الحقل يُخزَّن كرمز نصّي ويُربط بالقالب حين تُبنى.",
    sectionBank: "بيانات البنك", sectionBranches: "فروع البنك", list: "البنوك",
    code: "الرمز", descriptionAr: "الوصف عربي", descriptionEn: "الوصف إنجليزي",
    swiftCode: "Swift Code", bankCode: "رمز البنك",
    templateCode: "رمز نموذج البنك لطباعة الشيك",
    isDefault: "افتراضي", active: "فعال", inactive: "غير فعال",
    branchCode: "الرمز", phone: "الهاتف", fax: "فاكس", mobile: "خلوي",
    address: "العنوان", email: "البريد الإلكتروني", postalAddress: "العنوان البريدي",
    actions: "الإجراءات", branches: "فروع", templates: "قوالب",
    sectionAccounts: "حسابات البنك", noAccounts: "لا توجد حسابات لهذا البنك بعد.",
    currency: "العملة", glAccount: "حساب الأستاذ العام", accountNumber: "رقم الحساب",
    iban: "رقم الآيبان", isMain: "رئيسي", branch: "الفرع",
    branchOptional: "— الفرع (اختياري) —", choose: "— اختر —",
    accountSaved: "حُفظ حساب البنك.", accountRemoved: "حُذف حساب البنك.",
    accountRemoveHint: "الحساب المستخدَم لا يُحذف؛ أوقفه بدلاً من ذلك.",
    accountDeleteConfirm: "سيتم حذف حساب البنك. هل تريد المتابعة؟",
    newBank: "بنك جديد", inquiry: "إستعلام", save: "حفظ", saveAndNew: "حفظ وجديد",
    cancel: "إلغاء", edit: "تعديل", activate: "تفعيل", deactivate: "إيقاف", deleteBank: "حذف",
    addRow: "اضافة سطر جديد", remove: "إزالة",
    search: "بحث", searchPlaceholder: "ابحث بالرمز أو الوصف أو SWIFT", refresh: "تحديث",
    showInactive: "إظهار غير الفعالة",
    empty: "لا توجد بنوك.", noBranches: "لا توجد فروع لهذا البنك.",
    noSelection: "لم يتم اختيار بنك",
    noSelectionHint: "اختر بنكاً من القائمة لعرض بياناته وفروعه، أو أنشئ بنكاً جديداً.",
    created: "أُنشئ البنك.", saved: "حُفظ البنك.", deleted: "حُذف البنك.",
    activated: "فُعّل البنك.", deactivated: "أُوقف البنك.",
    branchSaved: "حُفظ الفرع.", branchRemoved: "أُزيل الفرع.",
    branchRemoveHint: "إزالة الفرع لا تمسّ أي حركة بنكية تاريخية؛ يُفضَّل الإيقاف إن كان مستخدماً.",
    loadError: "تعذّر تحميل البنوك.", saveError: "تعذّر الحفظ.",
    deleteConfirm: "سيتم حذف البنك. هل تريد المتابعة؟",
    branchDeleteConfirm: "سيتم حذف الفرع. هل تريد المتابعة؟",
    auditHistory: "سجل التغييرات", close: "إغلاق", required: "مطلوب",
  },
  cashboxes: {
    title: "تعريف الصناديق",
    pageDescription: "تعريف صناديق النقد وشجرتها، والحسابات التي تُكتب عليها حركاتها.",
    breadcrumb: "المحاسبة العامة « النقد والبنوك « البطاقات التعريفية « تعريف الصناديق",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الصناديق.",
    purposeNotice: "هذه الشاشة تعريفية فقط: لا تنشئ قيداً ولا تؤثر على أرصدة الأستاذ العام.",
    sectionDefinition: "تعريف الصندوق", sectionAccounts: "الحسابات المالية", tree: "شجرة الصناديق",
    parent: "الأب", noParent: "بدون أب (رئيسي)",
    code: "الرمز", descriptionAr: "الوصف العربي", descriptionEn: "الوصف الإنجليزي",
    openingCashbox: "صندوق الفتح", closingCashbox: "صندوق الإغلاق",
    cashboxGroup: "مجموعة الصندوق", groupPending: "بانتظار وحدة مجموعات الصناديق",
    cashAccount: "حساب الصندوق النقدي", chequesAccount: "حساب صندوق الشيكات",
    differencesAccount: "حساب الفروقات", exchangeAccount: "حساب التبديل",
    returnedLegalAccount: "حساب الشيكات المعادة - الدائرة القانونية",
    returnedCustomersAccount: "حساب الشيكات المعادة - عملاء",
    unusedNoOperation: "لا تستخدمه أي عملية حالياً — لا توجد عملية صرافة في النقد والبنوك. تحويل العملة في الإيداع والسحب يستعمل حسابات فروقات العملة على مستوى الشركة.",
    unusedHandBackPostsNothing: "لا يُرحَّل عليه شيء: «تسليم الشيك للساحب» لا يُنتج قيداً، لأن ارتجاع الشيك أعاد المبلغ أصلاً إلى ذمة العميل.",
    creationDate: "تاريخ الإنشاء", closingDate: "تاريخ الإغلاق",
    active: "فعال", inactive: "غير فعال", closed: "مغلق", openState: "مفتوح",
    closedByParent: "مغلق بالتبعية", inactiveByParent: "غير فعال بالتبعية",
    newCashbox: "صندوق رئيسي", newChild: "صندوق فرعي", save: "حفظ", cancel: "إلغاء", edit: "تعديل",
    activate: "تفعيل", deactivate: "إيقاف", deleteCashbox: "حذف",
    search: "بحث", searchPlaceholder: "ابحث بالرمز أو الوصف", refresh: "تحديث",
    showInactive: "إظهار غير الفعالة", pickAccount: "اختر حساباً", clear: "مسح",
    empty: "لا توجد صناديق.", noSelection: "لم يتم اختيار صندوق",
    noSelectionHint: "اختر صندوقاً من الشجرة لعرض تفاصيله، أو أنشئ صندوقاً جديداً.",
    required: "مطلوب",
    created: "أُنشئ الصندوق.", saved: "حُفظ الصندوق.", deleted: "حُذف الصندوق.",
    activated: "فُعّل الصندوق.", deactivated: "أُوقف الصندوق.",
    loadError: "تعذّر تحميل الصناديق.", saveError: "تعذّر حفظ الصندوق.",
    deleteConfirm: "سيتم حذف الصندوق. هل تريد المتابعة؟", close: "إغلاق",
    auditHistory: "سجل التغييرات", historyEmpty: "لا توجد تغييرات مسجّلة.",
  },
  costCenterGroups: {
    title: "مجموعات مراكز التكلفة",
    pageDescription: "تعريف مجموعات من مراكز التكلفة القائمة، لقراءة عدة مراكز معاً في التقارير والحركات.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « مجموعات مراكز التكلفة",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل المجموعات.",
    purposeNotice: "المجموعة تجمع مراكز تكلفة قائمة لقراءتها معاً؛ لا تنشئ مركزاً ولا تغيّر شجرة مراكز التكلفة.",
    sectionGroup: "مجموعة مراكز التكلفة", sectionCostCenters: "مراكز التكلفة",
    code: "الرمز", descriptionAr: "وصف عربي", descriptionEn: "وصف إنجليزي",
    active: "فعال", inactive: "غير فعال",
    costCenter: "مركز التكلفة", costCenterCode: "رمز مركز التكلفة",
    parent: "الأصل", path: "المسار", level: "المستوى", notes: "ملاحظات",
    noParent: "بدون أصل (رئيسي)",
    actions: "الإجراءات", members: "عدد المراكز",
    newGroup: "مجموعة جديدة", save: "حفظ", cancel: "إلغاء", edit: "تعديل",
    remove: "إزالة", deleteGroup: "حذف المجموعة",
    activate: "تفعيل", deactivate: "إيقاف",
    addCostCenter: "اضافة سطر جديد", addSelected: "إضافة المحدد",
    search: "بحث", searchPlaceholder: "ابحث بالرمز أو الوصف", refresh: "تحديث",
    query: "إستعلام", back: "رجوع",
    pickerTitle: "اختر مراكز التكلفة", pickerEmpty: "لا توجد مراكز تكلفة مطابقة.",
    alreadyInGroup: "مضاف مسبقاً", treeGroupHint: "عقدة تجميع في الشجرة — لا تحمل حركات",
    empty: "لا توجد مجموعات.", noMembers: "لم يُضف أي مركز تكلفة بعد.",
    required: "مطلوب", saveFirst: "احفظ المجموعة أولاً لتتمكن من إضافة مراكز التكلفة.",
    created: "أُنشئت المجموعة.", saved: "حُفظت المجموعة.", deleted: "حُذفت المجموعة.",
    activated: "فُعّلت المجموعة.", deactivated: "أُوقفت المجموعة.",
    memberAdded: "أُضيف إلى المجموعة.", memberRemoved: "أُزيل من المجموعة.",
    removeHint: "الإزالة تحذف العضوية فقط، ولا تحذف مركز التكلفة.",
    loadError: "تعذّر تحميل مجموعات مراكز التكلفة.",
    saveError: "تعذّر حفظ المجموعة.",
    deleteConfirm: "سيتم حذف المجموعة. هل تريد المتابعة؟", deleteTitle: "حذف المجموعة",
    close: "إغلاق", print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
    comingSoonHint: "قريباً", exporting: "جارٍ التحضير…",
  },
  openingBalances: {
    title: "الأرصدة الافتتاحية للحسابات",
    pageDescription: "تحضير الأرصدة الافتتاحية لحسابات دليل الحسابات حسب السنة المالية والحساب والعملة.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الأرصدة الافتتاحية.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « الأرصدة الافتتاحية للحسابات",
    phaseNotice: "هذه المرحلة للتحضير فقط: لا يتم إنشاء أي سند أو قيد، ولا تتأثر أرصدة الأستاذ العام.",
    accountNumber: "رقم الحساب", accountName: "اسم الحساب", debit: "مدين", credit: "دائن",
    fiscalYear: "السنة", currency: "العملة", exchangeRate: "سعر التحويل",
    localDebit: "مدين محلي", localCredit: "دائن محلي", notes: "ملاحظات",
    status: "الحالة", actions: "الإجراءات", branch: "الفرع", allBranches: "على مستوى الشركة",
    accountType: "نوع الحساب", financialStatement: "القائمة المالية",
    showZeroBalances: "إظهار الأرصدة الصفرية", showInactiveAccounts: "إظهار الحسابات غير الفعالة",
    search: "بحث", searchPlaceholder: "ابحث برقم الحساب أو الاسم",
    refresh: "تحديث", importComingSoon: "استيراد الأرصدة الافتتاحية",
    print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
    totalDebit: "إجمالي المدين", totalCredit: "إجمالي الدائن",
    totalLocalDebit: "إجمالي المدين المحلي", totalLocalCredit: "إجمالي الدائن المحلي",
    difference: "الفرق", balanced: "متوازن", unbalanced: "غير متوازن", batchStatus: "حالة الدفعة",
    stNotEntered: "غير مدخل", stZero: "صفري", stEntered: "مدخل", stInvalid: "غير صالح", stLocked: "مقفل",
    bsDraft: "مسودة", bsBalanced: "متوازن", bsUnbalanced: "غير متوازن", bsLocked: "مقفل", bsPosted: "مرحل",
    rateRequired: "يتطلب سعر تحويل معتمد",
    yearClosed: "السنة المالية مقفلة؛ الشاشة للعرض فقط.",
    empty: "لا توجد حسابات مطابقة.", loadError: "تعذّر تحميل الأرصدة الافتتاحية.",
    edit: "تحرير", comingSoonHint: "قريباً", baseCurrency: "عملة الشركة",
    saveSelected: "حفظ الأسطر المحددة", saveAll: "حفظ كل التغييرات",
    cancelChanges: "إلغاء التغييرات", resetRow: "تصفير السطر",
    selectRow: "تحديد السطر", selectAll: "تحديد الكل",
    changedCount: "سطر معدَّل", selectedCount: "سطر محدد",
    unsavedBanner: "يوجد تغييرات غير محفوظة. لن تُحفظ ما لم تضغط حفظ.",
    discardConfirm: "سيتم فقدان التغييرات غير المحفوظة. هل تريد المتابعة؟",
    noChanges: "لا توجد تغييرات لحفظها.",
    nothingSelected: "لم تحدد أي سطر معدَّل.",
    savedCreated: "سطر جديد", savedUpdated: "سطر محدَّث",
    saveError: "تعذّر حفظ الأرصدة الافتتاحية.",
    fixErrorsFirst: "صحّح الأسطر غير الصالحة قبل الحفظ.",
    errBothSides: "لا يمكن أن يكون المدين والدائن أكبر من صفر معاً.",
    errNegative: "لا يمكن أن يكون المبلغ سالباً.",
    errBaseRate: "سعر صرف العملة الأساسية يجب أن يساوي ١.",
    errRatePositive: "سعر الصرف يجب أن يكون أكبر من صفر.",
    calculated: "محتسب تلقائياً من المبلغ وسعر الصرف",
    changedRow: "سطر معدَّل غير محفوظ",
    printPreview: "معاينة الطباعة", exporting: "جارٍ التحضير…",
    exportError: "تعذّر تحضير الملف.",
    saveBeforeExport: "التصدير يُخرج الأرقام المحفوظة فقط؛ احفظ تعديلاتك أولاً.",
  },
  voucherTypes: {
    title: "أنواع السندات",
    pageDescription: "تعريف أنواع السندات التي تُدخل عليها الحركات، وطريقة ترقيمها وطباعتها.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل أنواع السندات.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « أنواع السندات",
    back: "رجوع",
    query: "إستعلام",
    save: "حفظ",
    cancel: "إلغاء",
    newType: "نوع جديد",
    sectionVoucherType: "نوع السند",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    sequenceType: "نوع التسلسل",
    taxInclusion: "ضريبة السعر",
    numberingMethod: "طريقة التسلسل",
    reportDestination: "وجهة التقرير",
    numberOfCopies: "عدد النسخ",
    effectType: "طبيعة التأثير",
    active: "فعال",
    inactive: "غير فعال",
    seqYearly: "سنوي",
    seqMonthly: "شهري",
    seqContinuous: "مستمر",
    taxIncluded: "شامل",
    taxNotIncluded: "غير شامل",
    numUnified: "موحد",
    numPerBranch: "حسب الفرع",
    numPerCompany: "حسب الشركة",
    destScreen: "الشاشة",
    destPrinter: "الطابعة",
    destPdf: "PDF",
    effectAutomatic: "آلي",
    effectManual: "يدوي",
    effectApproval: "موافقة",
    receivablesBreadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « أنواع السندات",
    cashBanksBreadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « أنواع السندات",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    statusAll: "الكل",
    statusActive: "الفعالة",
    statusInactive: "غير الفعالة",
    catalogue: "أنواع السندات المعرّفة",
    usage: "الاستخدام",
    usageTitle: "استخدام نوع السند",
    usageCount: "عدد السندات",
    usageNone: "لم يُستخدم هذا النوع في أي سند بعد.",
    usageRecent: "أحدث السندات",
    voucherNumber: "رقم السند",
    voucherDate: "التاريخ",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجّلة.",
    auditAction: "الإجراء",
    auditUser: "المستخدم",
    auditWhen: "التاريخ",
    auditFields: "الحقول",
    createdBy: "أنشئ بواسطة",
    updatedBy: "عُدّل بواسطة",
    activatedBy: "فُعّل بواسطة",
    deactivatedBy: "أُوقف بواسطة",
    print: "طباعة",
    exportPdf: "PDF",
    exportExcel: "Excel",
    activate: "تفعيل",
    deactivate: "إيقاف",
    systemType: "نوع نظام",
    systemTypeLocked: "نوع سند نظامي؛ يمكن تعديل وصفه ولكن لا يمكن إيقافه.",
    close: "إغلاق",
    required: "حقل إلزامي",
    copiesPositive: "عدد النسخ يجب أن يكون أكبر من صفر.",
    created: "تم إنشاء نوع السند.",
    saved: "تم حفظ نوع السند.",
    activated: "تم تفعيل نوع السند.",
    deactivated: "تم إيقاف نوع السند.",
    loadError: "تعذّر تحميل أنواع السندات.",
    saveError: "تعذّر حفظ نوع السند.",
    empty: "لا توجد أنواع سندات مطابقة.",
    inUse: "مستخدم",
  },
  financialStatements: {
    title: "إعداد القوائم المالية",
    pageDescription: "تعريف القوائم المالية التي تعرضها الشركة، ثم بناء هيكل كل قائمة.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل هذه القوائم.",
    breadcrumb: "المحاسبة العامة « الأستاذ العام « الإعدادات « إعداد القوائم المالية",
    back: "رجوع",
    query: "إستعلام",
    save: "حفظ",
    cancel: "إلغاء",
    deleteAll: "حذف الكل",
    sectionStatement: "القائمة المالية",
    sectionSettings: "الإعدادات",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    statementType: "نوع القائمة",
    statementActive: "قائمة فعّالة",
    required: "حقل إلزامي",
    newStatement: "قائمة جديدة",
    searchTitle: "إستعلام عن قائمة مالية",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    searchEmpty: "لا توجد قائمة مالية مطابقة.",
    searchClose: "إغلاق",
    noStatements: "لم يتم تعريف أي قائمة مالية بعد.",
    loadError: "تعذّر تحميل القوائم المالية.",
    saveError: "تعذّر حفظ القائمة المالية.",
    saved: "تم حفظ القائمة المالية.",
    created: "تم إنشاء القائمة المالية.",
    deleted: "تم حذف القائمة المالية.",
    deleteTitle: "حذف القائمة المالية",
    deleteConfirm: "سيتم حذف هذه القائمة المالية وكل ما يتبعها. هل تريد المتابعة؟",
    deleteCancel: "إلغاء",
    unsavedTitle: "لم يتم الحفظ بعد",
    structurePending: "يتم بناء هيكل القائمة (البنود والحسابات) في الخطوة التالية.",
    inactive: "غير فعّالة",
      lines: "بنود القائمة", addLine: "إضافة بند", addChild: "إضافة بند فرعي", editLine: "تحرير",
    deleteLine: "حذف", moveUp: "أعلى", moveDown: "أسفل", expandAll: "توسيع الكل",
    collapseAll: "طي الكل", lineSearch: "بحث", refreshLines: "تحديث", parent: "الأب",
    noParent: "بدون أب (بند رئيسي)", lineCode: "الرمز", lineTypeLabel: "نوع البيان",
    account: "الحساب", costCenter: "مركز التكلفة", disclosure: "الإيضاح/الشرح",
    effectNature: "طبيعة التأثير", balanceMethod: "طريقة حساب الرصيد",
    displaySequence: "تسلسل الظهور", bold: "عريض", printed: "يُطبع",
    ltHeader: "عنوان", ltAccount: "حساب", ltGroup: "مجموعة", ltTotal: "إجمالي",
    ltCalculated: "محسوب", ltSpacer: "فراغ", ltNote: "ملاحظة", ltCostCenter: "مركز تكلفة",
    enAdd: "إضافة", enSubtract: "طرح", enNoEffect: "بدون تأثير",
    bmAccountBalance: "رصيد الحساب", bmSumOfChildren: "مجموع البنود الفرعية",
    bmDebitMinusCredit: "مدين ناقص دائن", bmCreditMinusDebit: "دائن ناقص مدين",
    bmNoBalance: "بدون رصيد",
    noLines: "لا توجد بنود بعد.", lineSaved: "تم حفظ البند.", lineDeleted: "تم حذف البند.",
    lineMoved: "تم نقل البند.", deleteSubtreeTitle: "حذف البند وما تحته",
    deleteSubtreeConfirm: "هذا البند يحتوي على بنود فرعية. سيتم حذفها جميعاً. هل تريد المتابعة؟",
    selectStatementFirst: "احفظ القائمة المالية أولاً لبناء بنودها.",
  },
  financialLinks: {
    title: "إعدادات الربط المالي",
    pageDescription: "ربط حسابات النظام المعرّفة مسبقاً بحسابات دليل الحسابات، حسب مستوى الربط والعنصر.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل هذه الروابط.",
    sectionAccounts: "الحسابات",
    sectionAssignments: "الشركات والارتباطات",
    linkType: "الحساب",
    linkLevel: "مستوى الربط",
    entity: "العنصر",
    account: "الحساب المالي",
    accountNumber: "رقم الحساب",
    active: "الحالة",
    selectEntity: "اختر العنصر…",
    notLinked: "غير مرتبط",
    noRows: "لا توجد نتائج مطابقة",
    noAssignments: "لا توجد ارتباطات بعد",
    search: "بحث",
    searchPlaceholder: "البحث: كل الأعمدة النصية",
    edit: "تحرير",
    clearLink: "إلغاء الربط",
    refresh: "تحديث",
    linkedOf: "تم ربط {linked} من {total}",
    loadError: "تعذّر تحميل الروابط",
    saveError: "تعذّر حفظ الربط",
    soon: "غير متوفر",
    levelUnavailable: "هذه الوحدة غير متوفرة بعد.",
    settingsContext: "إعدادات الأستاذ العام السارية",
    openSettings: "فتح إعدادات الأستاذ العام",
    levels: {
      "1": "المستخدم",
      "2": "الشركة",
      "3": "الفرع",
      "4": "المستودع",
      "5": "مجموعة المادة",
      "6": "المادة",
      "7": "نوع الحركة",
    },
  },
  financialPosition: {
    title: "المركز المالي",
    subtitle: "كما في {date} · {currency}",
    prompt: "اختر تاريخاً ثم اضغط بحث لعرض المركز المالي",

    asOfDate: "كما في تاريخ",
    fiscalYear: "السنة المالية",
    fiscalYearHint: "تُشتق من تاريخ المركز",
    fiscalYearAuto: "تُحدَّد من التاريخ",
    branch: "الفرع",
    defaultBranch: "الفرع الافتراضي",

    levelFrom: "من مستوى",
    levelTo: "إلى مستوى",
    levelRangeInvalid: "«من مستوى» يجب ألا يكون أكبر من «إلى مستوى»",
    parentAccount: "الحساب الأب",
    accountFrom: "من حساب",
    accountTo: "إلى حساب",
    accountType: "نوع الحساب",
    allAccountTypes: "كل الأنواع",
    accountCategory: "فئة الحساب",
    costCenter: "مركز التكلفة",
    costCenterHint: "كل سطر قيد يخص مركز تكلفة واحداً بالكامل",
    currency: "العملة",
    allCurrencies: "كل العملات",
    search: "البحث برقم الحساب أو اسمه",
    searchPlaceholder: "رقم أو اسم الحساب",

    showChildren: "إظهار الأبناء",
    showChildrenHint: "عند الإيقاف تُعرض إجماليات المجموعة دون تفاصيلها",
    includeZeroBalances: "إظهار الأرصدة الصفرية",
    includeZeroBalancesHint: "الحسابات التي رصيدها صفر مخفية افتراضياً",
    includeClosedAccounts: "تضمين الحسابات المغلقة",
    includeClosedAccountsHint: "الحساب المغلق يحتفظ برصيده ويجب أن يظهر",
    includeInactiveAccounts: "تضمين الحسابات غير الفعالة",
    includeUnpostedMovements: "تضمين الحركات غير المرحلة",
    includeUnpostedMovementsHint: "المركز المالي يُبنى من الحركات المرحّلة فقط افتراضياً",
    consolidateBranches: "تجميع الفروع",
    consolidateBranchesHint: "يجمع الفروع المصرّح لك بها فقط",
    showPercentageOfTotal: "إظهار النسبة من الإجمالي",

    comparison: "وضع المقارنة",
    comparisonNone: "بدون مقارنة",
    comparisonPreviousPeriod: "الفترة السابقة",
    comparisonPreviousYear: "السنة المالية السابقة",
    comparisonCustom: "تاريخ مقارنة مخصص",
    comparisonDerivedHint: "تاريخ المقارنة يُحتسب من تاريخ المركز والسنة المالية",
    comparativeDate: "تاريخ المقارنة",
    comparativeBalance: "الرصيد المقارن",
    changeAmount: "قيمة التغير",
    changePercentage: "نسبة التغير",

    accountNumber: "رقم الحساب",
    accountName: "اسم الحساب",
    debit: "مدين",
    credit: "دائن",
    balance: "الرصيد",
    balanceNature: "طبيعة التأثير",
    accountLevel: "مستوى الحساب",
    percentageOfTotalAssets: "النسبة من إجمالي الموجودات",
    isGroupAccount: "حساب مجموعة",
    isPostingAccount: "حساب ترحيل",
    status: "الحالة",
    closed: "مغلق",

    natureDebit: "مدين",
    natureCredit: "دائن",
    natureZero: "متعادل",
    natureAll: "الكل",
    natureNormalOnly: "أرصدة طبيعية",
    natureAbnormalOnly: "أرصدة غير طبيعية",
    abnormal: "غير طبيعي",
    abnormalHint: "الطبيعة المتوقعة: {expected} — الطبيعة الفعلية: {actual}. القيمة كما هي دون تعديل.",
    systemCalculated: "محتسب آلياً",
    systemCalculatedHint: "قيمة احتسبها النظام من حسابات الإيرادات والمصروفات غير المقفلة",

    assets: "الموجودات",
    liabilities: "المطلوبات",
    equity: "حقوق الملكية",

    expand: "توسيع",
    collapse: "طي",
    expandAll: "توسيع الكل",
    collapseAll: "طي الكل",
    openMovements: "فتح استعلام حركات الحساب",

    equationTitle: "معادلة المركز المالي",
    balanced: "متوازن",
    unbalanced: "غير متوازن",
    unbalancedWarning:
      "إجمالي الموجودات لا يساوي إجمالي المطلوبات وحقوق الملكية. الفرق معروض كما هو دون أي تسوية تلقائية، ويجب مراجعة القيود.",
    difference: "الفرق",
    totalAssets: "إجمالي الموجودات",
    totalCurrentAssets: "إجمالي الموجودات المتداولة",
    totalNonCurrentAssets: "إجمالي الموجودات غير المتداولة",
    totalLiabilities: "إجمالي المطلوبات",
    totalCurrentLiabilities: "إجمالي المطلوبات المتداولة",
    totalNonCurrentLiabilities: "إجمالي المطلوبات غير المتداولة",
    totalEquity: "إجمالي حقوق الملكية",
    currentPeriodResult: "أرباح وخسائر الفترة الحالية",
    totalLiabilitiesAndEquity: "إجمالي المطلوبات وحقوق الملكية",
    displayedAccounts: "عدد الحسابات الظاهرة",
    abnormalBalances: "أرصدة غير طبيعية",

    unpostedWarning: "هذا المركز يتضمن حركات غير مرحّلة، وهو ليس المركز المحاسبي المرحّل.",
    consolidatedNotice: "مركز مالي تجميعي للفروع: {branches}",
  },
  taxTypes: {
    title: "أنواع الضرائب",
    createTitle: "نوع ضريبة جديد",
    editTitle: "تعديل نوع الضريبة",
    empty: "لا توجد أنواع ضرائب",
    emptyHint: "أضف نوع الضريبة الأول ليتمكن النظام من الرجوع إليه في الحسابات لاحقًا.",
    code: "الرمز",
    name: "الاسم",
    nameAr: "الاسم بالعربية",
    nameEn: "الاسم بالإنجليزية",
    category: "فئة الضريبة",
    allCategories: "كل الفئات",
    effectiveFrom: "ساري من",
    effectiveTo: "ساري إلى",
    effectiveToHint: "اتركه فارغًا إذا كان مفتوحًا",
    effectivePeriod: "فترة السريان",
    status: "الحالة",
    accounts: "الحسابات (مدين / دائن)",
    debitAccount: "الحساب المدين",
    creditAccount: "الحساب الدائن",
    descriptionAr: "الوصف بالعربية",
    descriptionEn: "الوصف بالإنجليزية",
    notes: "ملاحظات",
    sortOrder: "ترتيب العرض",
    isActive: "فعّال",
    isActiveHint: "غير الفعّال لا يمكن اختياره في الحركات الجديدة",
    allowManualRateOverride: "السماح بتعديل النسبة",
    allowManualRateOverrideHint: "يمكن للمستخدم إدخال نسبة مختلفة على المستند",
    isRecoverable: "قابلة للخصم",
    isRecoverableHint: "يمكن استردادها من دائرة الضريبة",
    affectsItemCost: "تؤثر على تكلفة الصنف",
    affectsItemCostHint: "الضريبة غير القابلة للخصم تُحمّل على التكلفة",
    requiresTaxRegistration: "تتطلب رقمًا ضريبيًا",
    requiresTaxRegistrationHint: "يجب أن تحمل الذمة رقمًا ضريبيًا",
    requiresNationalNumber: "تتطلب رقمًا وطنيًا",
    requiresNationalNumberHint: "يجب أن تحمل الذمة رقمًا وطنيًا",
    outOfWindow: "خارج فترة السريان",
    outOfWindowHint: "الضريبة فعّالة لكنها خارج فترة السريان اليوم",
    deleteTitle: "حذف نوع الضريبة",
    deleteHint: "الحذف نهائي. إن كانت الضريبة مستخدمة سابقًا فالأفضل تعطيلها بدل حذفها حتى تبقى المستندات القديمة مفهومة.",
    codeRequired: "الرمز مطلوب",
    nameRequired: "الاسم بالإنجليزية مطلوب",
    nameArRequired: "الاسم بالعربية مطلوب",
    effectiveFromRequired: "تاريخ بداية السريان مطلوب",
    effectiveRangeInvalid: "تاريخ النهاية يجب ألا يسبق تاريخ البداية",
    categories: {
      salesTax: "ضريبة مبيعات",
      purchaseTax: "ضريبة مشتريات",
      additionalTax: "ضريبة إضافية",
      specialTax: "ضريبة خاصة",
      zeroRated: "نسبة صفر",
      exempt: "معفى",
      nonRecoverable: "ضريبة مشتريات غير قابلة للخصم",
      withholding: "ضريبة مقتطعة",
      other: "أخرى",
    },
  },
  taxRates: {
    title: "نسب الضرائب",
    createTitle: "نسبة ضريبة جديدة",
    createHint: "النسبة الجديدة تُضاف بتاريخ سريان، ولا تُعدَّل النسبة القديمة.",
    editTitle: "تعديل نسبة الضريبة",
    editHint: "تعديل النسبة يغيّر ما تُحتسب به المستندات ضمن فترتها.",
    empty: "لا توجد نسب ضرائب",
    emptyHint: "لم تُدخل أي نسبة بعد. لا يمكن احتساب ضريبة على أي مستند قبل إدخال نسبة سارية.",

    taxType: "نوع الضريبة",
    taxTypeRequired: "اختر نوع الضريبة",
    selectTaxType: "اختر نوع الضريبة…",
    searchTaxTypes: "ابحث في أنواع الضرائب",
    noTaxTypes: "لا توجد أنواع ضرائب فعّالة",
    taxTypeInactive: "نوع الضريبة غير فعّال",
    taxTypeInactiveHint: "النسبة سارية لكن نوع الضريبة معطّل، فلا يمكن اختيارها على المستندات",

    percentage: "النسبة",
    percentageHint: "أدخل النسبة المئوية: 16 تعني 16%",
    percentageRequired: "النسبة مطلوبة",
    percentageNegative: "النسبة لا يمكن أن تكون سالبة",
    percentageTooLarge: "النسبة لا يمكن أن تتجاوز 100%",

    effectiveFrom: "بداية السريان",
    effectiveFromHint: "أول يوم تُطبَّق فيه هذه النسبة",
    effectiveFromRequired: "تاريخ بداية السريان مطلوب",
    effectiveTo: "نهاية السريان",
    effectiveToHint: "اتركه فارغًا إذا كانت النسبة مفتوحة حتى إشعار آخر",
    effectiveRangeInvalid: "تاريخ النهاية يجب ألا يسبق تاريخ البداية",
    effectivePeriod: "فترة السريان",
    openEnded: "مفتوح",

    rateStatus: "حالة النسبة",
    allStatuses: "كل الحالات",
    statusCurrent: "حالي",
    statusFuture: "مستقبلي",
    statusExpired: "منتهي",
    statusInactive: "غير فعال",

    referenceNumber: "رقم المرجع",
    referenceNumberHint: "رقم التعميم أو المرجع الصادر عن دائرة الضريبة",
    decisionNumber: "رقم القرار",
    legalReference: "المرجع القانوني",
    legalReferenceHint: "المادة أو النظام الذي استند إليه تحديد هذه النسبة",
    sortOrder: "ترتيب العرض",
    descriptionAr: "الوصف بالعربية",
    descriptionEn: "الوصف بالإنجليزية",
    notes: "ملاحظات",
    isActive: "فعّالة",
    isActiveHint: "النسبة غير الفعّالة لا تُطبَّق على أي مستند ولو كان تاريخه ضمن فترتها",

    lockedInUse:
      "هذه النسبة استُخدمت في احتساب مستند، لذا لا يمكن تعديل النسبة ولا تواريخ السريان ولا نوع الضريبة. أضف نسبة جديدة بتاريخ سريان جديد. الحقول الوصفية تبقى قابلة للتعديل.",
    lockedExpired:
      "انتهت فترة هذه النسبة، لذا لا يمكن تعديل النسبة ولا تواريخ السريان ولا نوع الضريبة. أضف نسبة جديدة بتاريخ سريان جديد. الحقول الوصفية تبقى قابلة للتعديل.",

    history: "سجل النسب",
    historyTitle: "سجل نسب الضريبة",
    historyEmpty: "لا توجد نسب مسجّلة لهذه الضريبة",
    historyView: "طريقة عرض السجل",
    tableView: "عرض جدولي",
    timelineView: "عرض زمني",
    createdBy: "أُنشئت بواسطة",
    closedBy: "أُغلقت بواسطة",

    close: "إغلاق النسبة",
    closeTitle: "إغلاق نسبة مفتوحة",
    closeHint: "حدّد آخر يوم تُطبَّق فيه هذه النسبة.",
    closeDateHint: "آخر يوم تسري فيه النسبة، شامل هذا اليوم",
    closeBeforeStart: "تاريخ الإغلاق يجب ألا يسبق تاريخ بداية السريان",
    closedOk: "تم إغلاق النسبة",
    closePredecessorTitle: "توجد نسبة مفتوحة سابقة",
    closePredecessorConfirm: "أغلق النسبة السابقة وأضف الجديدة",

    deleteTitle: "حذف نسبة الضريبة",
    deleteHint:
      "الحذف نهائي. إن كانت النسبة قد استُخدمت في احتساب أي مستند فالأفضل تعطيلها بدل حذفها حتى تبقى المبالغ المرحّلة مفهومة.",
  },
  customerReceivables: {
    title: "إجمالي ذمم العملاء",
    statementTitle: "كشف حساب العميل",
    date: "التاريخ",
    asOfDate: "لغاية تاريخ",
    currentBalance: "الرصيد الحالي",
    balanceAsOfDate: "الرصيد حتى تاريخ",
    asOfHint: "الأرصدة محسوبة من حركات الأستاذ المرحّلة حتى التاريخ المحدد.",
    currentHint: "الأرصدة الحالية كما هي مسجلة الآن.",
    customer: "العميل",
    category: "فئة العميل",
    salesRepresentative: "مندوب المبيعات",
    controlAccount: "الحساب الوسيط",
    city: "المدينة",
    country: "الدولة",
    creditLimit: "سقف الائتمان",
    includeZeroBalances: "إظهار الأرصدة الصفرية",
    balance: "الرصيد",
    balanceNature: "طبيعة الرصيد",
    natureDebit: "مدين",
    natureCredit: "دائن",
    natureBalanced: "مطابق",
    totalDebit: "إجمالي المدين",
    totalCredit: "إجمالي الدائن",
    netReceivables: "صافي الذمم",
    customerCount: "عدد العملاء",
    baseCurrency: "عملة الشركة",
    totalsCoverAll: "المجاميع تشمل كامل نتيجة البحث.",
    openStatement: "عرض كشف الحساب",
    selectCustomer: "اختر عميلاً لعرض كشف حسابه.",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    openingBalance: "الرصيد الافتتاحي",
    closingBalance: "الرصيد الختامي",
    runningBalance: "الرصيد التراكمي",
    movementCount: "عدد الحركات",
    voucherType: "نوع السند",
    voucherNumber: "رقم السند",
    reference: "المرجع",
    description: "البيان",
    debit: "مدين",
    credit: "دائن",
    documentCurrency: "عملة المستند",
    noDrillDown: "لا توجد شاشة مرتبطة بهذا المستند.",
    allCustomers: "كل العملاء",
    all: "الكل",
  },
  customerCostCenterReceivables: {
    title: "كشف إجمالي ذمم العملاء - مركز التكلفة",
    costCenter: "مركز التكلفة",
    costCenterCode: "رمز مركز التكلفة",
    noCostCenter: "بدون مركز تكلفة",
    costCenterCount: "عدد مراكز التكلفة",
    includeChildCostCenters: "شمول مراكز التكلفة الفرعية",
    onlyWithoutCostCenter: "الحركات بدون مركز تكلفة فقط",
    unassigned: "بدون مركز تكلفة",
    unassignedNote: "حركات مرحّلة دون مركز تكلفة — معروضة كي يطابق المجموع رصيد العميل.",
    ledgerOnlyHint: "الأرصدة محسوبة دائماً من حركات الأستاذ المرحّلة، لأن توزيع مراكز التكلفة لا يتوفر في أرصدة العملاء المخزّنة.",
    currentScope: "حتى الآن",
    asOfScope: "لغاية تاريخ",
  },
  customerCurrencyStatement: {
    title: "كشف حساب العميل / عملة",
    currency: "العملة",
    selectCustomerFirst: "اختر عميلاً ثم العملة لعرض كشف الحساب.",
    selectCurrency: "اختر العملة",
    noLedgerCurrencies: "لا توجد حركات مرحّلة لهذا العميل بأي عملة.",
    exchangeRate: "سعر الصرف",
    transactionCurrencyNote: "جميع المبالغ بعملة الحركة المحددة، كما رُحّلت — دون أي تحويل.",
    baseCurrencyBadge: "عملة الشركة",
    movementsInCurrency: "حركة",
  },
  customerTransactions: {
    title: "الحركات اليومية",
    voucherDate: "تاريخ السند",
    customerNumber: "رقم العميل",
    accountNumber: "رقم الحساب",
    accountName: "اسم الحساب",
    currency: "العملة",
    rate: "سعر التحويل",
    currencyDebit: "مدين عملة",
    currencyCredit: "دائن عملة",
    paymentMethod: "طريقة الدفع",
    notes: "الملاحظات",
    branch: "الفرع",
    year: "السنة",
    netMovement: "صافي الحركة",
    mixedCurrencies: "النتيجة تشمل أكثر من عملة، لذلك لم يتم جمع أعمدة العملة.",
    noPaymentMethod: "لا تحمل طريقة دفع",
    reversed: "معكوس",
    mappingNote: "مدين ودائن بعملة الشركة، ومدين عملة ودائن عملة بعملة الحركة.",
  },
  serviceVouchers: {
    title: "حركات السندات - خدمات",
    voucherDate: "تاريخ السند",
    paymentMethod: "طريقة الدفع",
    partyCode: "رقم العميل/المورد",
    partyName: "اسم العميل/المورد",
    subtotal: "المجموع",
    discount: "الخصم",
    tax: "الضريبة",
    finalTotal: "الإجمالي النهائي",
    currency: "العملة",
    supplierInvoiceNumber: "رقم فاتورة المورد",
    project: "المشروع",
    notes: "ملاحظات",
    branch: "الفرع",
    userName: "اسم المستخدم",
    year: "السنة",
    status: "الحالة",
    documents: "عدد السندات",
    documentType: "نوع السند",
    totalsByCurrency: "المجاميع مجمّعة حسب العملة — لا يوجد إجمالي موحّد عبر العملات.",
    returnNote: "مردودات المبيعات تُعرض بإشارة سالبة لأنها عكس بيع.",
    noProjectModel: "لا يوجد نموذج مشاريع في النظام حالياً، لذلك عمود المشروع فارغ.",
    noPurchaseModule: "سندات المشتريات - خدمات غير منفَّذة في النظام حالياً، لذلك لا تظهر هنا.",
    openDocument: "فتح السند",
  },
  customerItems: {
    title: "حركات مادة عميل",
    voucherDate: "تاريخ السند",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    itemCode: "رقم المادة / الخدمة",
    itemName: "اسم المادة / الخدمة",
    itemType: "نوع المادة",
    uom: "الوحدة",
    quantity: "الكمية",
    unitPrice: "السعر",
    discount: "الخصم",
    netUnitPrice: "السعر بعد الخصم",
    tax: "الضريبة",
    lineTotal: "الإجمالي",
    currency: "العملة",
    rate: "سعر التحويل",
    branch: "الفرع",
    costCenter: "مركز التكلفة",
    salesRep: "مندوب المبيعات",
    notes: "الملاحظات",
    nature: "نوع الحركة",
    sale: "بيع",
    returned: "مردود",
    customers: "عدد العملاء",
    documents: "عدد السندات",
    lines: "عدد السطور",
    netSales: "صافي المبيعات",
    totalsByCurrency: "المجاميع مجمّعة حسب العملة — لا يوجد إجمالي موحّد عبر العملات.",
    returnNote: "المردودات تُعرض بكمية ومبلغ سالبين؛ السعر يبقى سعر البيع الأصلي.",
    servicesOnly: "لا يوجد نموذج مواد أو أصناف في النظام حالياً، لذلك جميع الحركات من نوع خدمة.",
    postedOnly: "تشمل النتيجة السندات المرحّلة فقط — المسودات والملغاة والمعكوسة ليست مبيعات فعلية.",
    allCustomers: "كل العملاء",
    openDocument: "فتح السند",
  },
  paidUnpaid: {
    title: "الفواتير المسددة وغير المسددة",
    invoiceDate: "تاريخ الفاتورة",
    invoiceNumber: "رقم الفاتورة",
    invoiceType: "نوع الفاتورة",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    currency: "العملة",
    invoiceTotal: "إجمالي الفاتورة",
    paid: "المبلغ المسدد",
    remaining: "المبلغ المتبقي",
    status: "حالة السداد",
    dueDate: "تاريخ الاستحقاق",
    daysOverdue: "أيام التأخير",
    paymentMethod: "طريقة الدفع",
    settledBy: "طريقة التسديد",
    salesRep: "مندوب المبيعات",
    costCenter: "مركز التكلفة",
    branch: "الفرع",
    reference: "المرجع",
    notes: "الملاحظات",
    statusPaid: "مسددة",
    statusPartial: "مسددة جزئياً",
    statusUnpaid: "غير مسددة",
    multiple: "متعدد",
    invoices: "عدد الفواتير",
    overSettled: "تسديد زائد",
    overSettledWarning: "فواتير تجاوز تسديدها قيمتها — يُعرض الخلل ولا يُصحّح هنا.",
    totalsByCurrency: "المجاميع مجمّعة حسب العملة — لا يوجد إجمالي موحّد عبر العملات.",
    noDueDateNote: "لا يحتفظ النظام بتاريخ استحقاق للفواتير، لذلك عمودا الاستحقاق وأيام التأخير فارغان.",
    postedOnly: "تشمل النتيجة الفواتير المرحّلة فقط — المسودات والملغاة والمعكوسة ليست ذمماً قابلة للتحصيل.",
    settlementDetails: "تفاصيل التسديد",
    settlementDate: "تاريخ التسوية",
    settlementNumber: "رقم التسوية",
    receiptNumber: "رقم سند القبض",
    amountApplied: "المبلغ المطبّق",
    settlementStatus: "الحالة",
    createdBy: "أنشأها",
    active: "سارية",
    cancelled: "ملغاة",
    cancelledNote: "التسويات الملغاة معروضة للسجل ولا تُحتسب ضمن المبلغ المسدد.",
    noSettlements: "لا توجد تسويات على هذه الفاتورة.",
    close: "إغلاق",
    allCustomers: "كل العملاء",
    openInvoice: "فتح الفاتورة",
  },
  customerAging: {
    title: "أعمار ذمم العملاء",
    asOfDate: "لغاية تاريخ",
    basis: "أساس احتساب العمر",
    basisDue: "تاريخ الاستحقاق",
    basisDocument: "تاريخ الفاتورة",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    salesRep: "مندوب المبيعات",
    accountNumber: "رقم حساب الذمم",
    accountName: "حساب الذمم",
    notDue: "غير مستحق",
    unclassified: "غير مصنف",
    totalOutstanding: "إجمالي الذمم",
    unappliedCredits: "أرصدة دائنة غير مطابقة",
    creditLimit: "سقف الائتمان",
    maxAge: "أقصى عمر",
    customers: "عدد العملاء",
    totalOverdue: "إجمالي المتأخر",
    overdueOnly: "المتأخر فقط",
    minimumOutstanding: "الحد الأدنى للرصيد",
    country: "الدولة",
    city: "المدينة",
    agingDetails: "تفاصيل أعمار الذمة",
    documentDate: "تاريخ السند",
    dueDate: "تاريخ الاستحقاق",
    voucherNumber: "رقم السند",
    documentType: "نوع السند",
    reference: "المرجع",
    originalAmount: "المبلغ الأصلي",
    settledAmount: "المسدد لغاية التاريخ",
    remainingAmount: "المتبقي لغاية التاريخ",
    ageDays: "عمر الذمة (أيام)",
    agingPeriod: "الفترة",
    currency: "العملة",
    rate: "سعر التحويل",
    branch: "الفرع",
    fiscalYear: "السنة",
    noOpenItems: "لا توجد ذمم قائمة لهذا العميل حتى التاريخ المحدد.",
    detailsReconcile: "مجموع المتبقي في التفاصيل يساوي إجمالي ذمم العميل لنفس التاريخ والأساس.",
    close: "إغلاق",
    allCustomers: "كل العملاء",
    openItems: "عدد الذمم القائمة",
    configurePeriods: "تُقرأ الفترات من شاشة «الفترات الزمنية لعمر الذمة» فقط — لا يمكن تعديلها من هنا.",
    noCostCentreFilter: "لا يمكن توزيع المتبقي على مراكز التكلفة، لأن سطور الذمم لا تحمل مركز تكلفة؛ لذلك لا يوجد مرشّح لمركز التكلفة هنا.",
  },
  detailedAging: {
    title: "تقرير أعمار الذمم التفصيلي",
    documentDate: "تاريخ السند",
    documentDateFrom: "تاريخ السند من",
    documentDateTo: "تاريخ السند إلى",
    originalBase: "المبلغ الأصلي",
    settledBase: "المسدد لغاية التاريخ",
    remainingBase: "المتبقي لغاية التاريخ",
    originalTransaction: "المبلغ بعملة الحركة",
    ageDays: "عمر الذمة (أيام)",
    agingPeriod: "الفترة",
    dueStatus: "حالة الاستحقاق",
    statusNotDue: "غير مستحق",
    statusOverdue: "مستحق",
    statusPaid: "مسدد",
    unclassified: "غير مصنف",
    openItems: "عدد الذمم القائمة",
    customers: "عدد العملاء",
    totalOverdue: "إجمالي المتأخر",
    unappliedCredits: "أرصدة دائنة غير مطابقة",
    showFullySettled: "إظهار المسدّدة بالكامل",
    minimumRemaining: "الحد الأدنى للمتبقي",
    maximumRemaining: "الحد الأعلى للمتبقي",
    settlementDetails: "تفاصيل التسديد",
    settlementDate: "تاريخ التسوية",
    settlementNumber: "رقم التسوية",
    receiptNumber: "رقم سند القبض",
    amountApplied: "المبلغ المطبّق",
    paymentMethod: "طريقة الدفع",
    createdBy: "أنشأها",
    cancelledLater: "ألغيت لاحقاً",
    noSettlements: "لا توجد تسويات سارية على هذه الذمة حتى التاريخ المحدد.",
    settlementAsOfNote: "التسويات معروضة كما كانت في التاريخ المحدد؛ ما أُلغي بعده يبقى سارياً هنا.",
    close: "إغلاق",
    summaryLink: "هذا التقرير التفصيلي يطابق «أعمار ذمم العملاء» لنفس التاريخ والأساس.",
  },
  receiptsReportsExtra: {
    title: {
      openingBalances: "كشف الأرصدة الافتتاحية للعملاء",
      trialBalance: "ميزان مراجعة مختصر - العملاء والموردون",
      withoutActivity: "العملاء بدون حركة خلال الفترة",
      byCurrency: "إجمالي العملاء حسب العملة",
      byAccount: "حساب العميل - الحسابات",
      receiptsSummary: "كشف المقبوضات",
      receiptOrders: "كشف أوامر القبض والصرف",
      collector: "كشف حركات المحصّلين",
      deferredRevenue: "كشف الإيرادات المؤجلة المستحقة",
      invoicesByType: "كشف فواتير بيع الخدمات حسب النوع",
      consolidated: "الرصيد المجمّع للعملاء النشطين",
      infoChanges: "كشف تغييرات بيانات العملاء والموردين",
      parentAccount: "حساب العميل الأب",
    },
    note: {
      openingBalances: "الرصيد الافتتاحي محسوب من حركات الأستاذ الفعّالة قبل التاريخ المحدد، وليس من الرصيد الحالي.",
      trialBalance: "العملاء والموردون في كشف واحد دون دمج؛ كل سطر يحمل نوع الطرف الخاص به.",
      withoutActivity: "«الحركة» تعني حركة محاسبية فعّالة على حساب العميل، وليست تاريخ تعديل البيانات.",
      byCurrency: "سطر واحد لكل عميل وعملة. المبالغ بعملة الحركة كما رُحّلت، ولا يتم جمع العملات.",
      byAccount: "سطر واحد لكل حركة محاسبية بالحساب الذي رُحّلت إليه. هذا ليس بُعد مركز التكلفة.",
      receiptsSummary: "سند القبض هو المال المستلم فعلاً. أمر القبض ليس تحصيلاً ولا يظهر هنا.",
      receiptOrders: "أوامر القبض فقط؛ أوامر الصرف غير مُفعّلة في النظام.",
      collector: "المحصل مقروء من أمر القبض ولا يُستنتج من مندوب المبيعات. إجماليات الأوامر والسندات منفصلة.",
      deferredRevenue: "كشف للقراءة فقط من جدول الاعتراف المعتمد. فترة العقد هي الأساس، وليس تاريخ التحصيل.",
      invoicesByType: "نوع الخدمة مسجّل على سطور الفاتورة، لذا تظهر «متعدد» عند تعدد الأنواع. الإجماليات كما رُحّلت.",
      consolidated: "التجميع يعتمد على حقل العميل الأب المسجّل فقط، ولا يُستنتج من الفئة أو الحساب أو التشابه.",
      infoChanges: "من سجل التدقيق فقط؛ لا يُعاد بناء أي تغيير لا يحفظه السجل.",
      parentAccount: "الأبناء مقروؤون من حقل العميل الأب المسجّل فقط، ولمستوى واحد مباشر.",
    },
    empty: {
      generic: "لا توجد بيانات.",
      openingBalances: "لا توجد أرصدة افتتاحية.",
      withoutActivity: "كل العملاء لديهم حركة خلال الفترة.",
      collector: "لا توجد حركات محصّلين.",
    },
    partyTypeLabel: (v) => (v === "Supplier" ? "مورّد" : v === "Customer" ? "عميل" : v),
    natureLabel: (v) => (v === "Debit" ? "مدين" : v === "Credit" ? "دائن" : "متعادل"),
    statusLabel: (v) => ({ Draft: "مسودة", Audited: "مدققة", Posted: "مرحّلة",
      Rejected: "مرفوضة", Cancelled: "ملغاة", Reversed: "معكوسة" })[v] ?? v,
    documentTypeLabel: (v) => (v === "ReceiptVoucher" ? "سند قبض" : "أمر قبض"),
    recognitionStatus: (v) => ({ Due: "مستحق", NotYetDue: "غير مستحق بعد", Recognised: "معترف به" })[v] ?? v,
    auditAction: (v) => ({ Created: "إنشاء", Updated: "تعديل", Deleted: "حذف" })[v] ?? v,
    dateFrom: "من تاريخ", dateTo: "إلى تاريخ", asOf: "لغاية تاريخ", openingBefore: "الرصيد الافتتاحي قبل",
    partyType: "نوع الطرف", partyCode: "رقم الطرف", partyName: "اسم الطرف", category: "الفئة",
    controlAccount: "الحساب الرئيسي", currency: "العملة", lastActivity: "آخر حركة", never: "لا يوجد حركة",
    openingDebit: "مدين افتتاحي", openingCredit: "دائن افتتاحي",
    periodDebit: "مدين الفترة", periodCredit: "دائن الفترة",
    closingDebit: "مدين ختامي", closingCredit: "دائن ختامي", nature: "طبيعة الرصيد", net: "الصافي",
    parties: "عدد الأطراف", customers: "عدد العملاء", customer: "عميل", supplier: "مورّد",
    bothTypes: "الكل", rows: "عدد السطور", date: "التاريخ", voucherType: "نوع السند",
    voucherNumber: "رقم السند", accountNumber: "رقم الحساب", accountName: "اسم الحساب",
    description: "البيان", debit: "مدين", credit: "دائن", balance: "الرصيد",
    runningBalance: "الرصيد الجاري", openingBalance: "الرصيد الافتتاحي",
    closingBalance: "الرصيد الختامي", accountSummary: "ملخص حسب الحساب",
    costCenter: "مركز التكلفة", branch: "الفرع", customerRequired: "العميل مطلوب لهذا التقرير.",
    receiptNumber: "رقم السند", amount: "المبلغ", paymentMethod: "طريقة الدفع",
    destination: "الصندوق / البنك", collector: "المحصل", salesRep: "مندوب المبيعات",
    reference: "المرجع", status: "الحالة", createdBy: "المستخدم", multiple: "متعدد",
    receipts: "عدد السندات", total: "الإجمالي",
    orderNumber: "رقم الأمر", requested: "المبلغ المطلوب", collected: "المحصّل",
    remaining: "المتبقي", expectedDate: "التاريخ المتوقع", orders: "عدد الأوامر", notes: "الملاحظات",
    documentType: "نوع المستند", documentNumber: "رقم المستند", receiptVoucher: "سند قبض",
    receiptOrder: "أمر قبض", bothDocuments: "الكل", documents: "عدد المستندات",
    collectors: "عدد المحصّلين", collectedReceipts: "إجمالي السندات المحصّلة",
    orderedAmount: "إجمالي أوامر القبض",
    recognitionDate: "تاريخ الاعتراف", invoiceNumber: "رقم الفاتورة", contractPeriod: "فترة العقد",
    periodNumber: "رقم الفترة", scheduled: "المجدول", recognised: "المعترف به", due: "المستحق",
    notYetDue: "غير مستحق بعد", schedules: "عدد الجداول", periods: "عدد الفترات", allStatuses: "الكل",
    serviceType: "نوع الخدمة", saleNature: "طبيعة البيع", subtotal: "المجموع", discount: "الخصم",
    tax: "الضريبة", netTotal: "الإجمالي النهائي", invoices: "عدد الفواتير",
    byTypeSummary: "ملخص حسب نوع الخدمة",
    isParent: "التجميع", parent: "أب", child: "ابن", standalone: "مستقل", parents: "عدد الآباء",
    ownBalance: "الرصيد الذاتي", childrenBalance: "رصيد الأبناء", consolidated: "الرصيد المجمّع",
    children: "عدد الأبناء", parentCustomer: "العميل الأب", selectParent: "اختر العميل الأب",
    parentRequired: "العميل الأب مطلوب لهذا التقرير.", role: "الدور", movements: "عدد الحركات",
    changedAt: "التاريخ والوقت", field: "الحقل", oldValue: "القيمة السابقة",
    newValue: "القيمة الجديدة", action: "الإجراء", changedBy: "المستخدم",
    fieldChanges: "عدد التغييرات", auditEvents: "عدد الأحداث",
  },
  customerMasterReports: {
    title: (kind) =>
      kind === "customers-exceeded-credit-limit" ? "العملاء المتجاوزون سقف الائتمان"
      : kind === "total-customers" ? "كشف إجمالي العملاء"
      : kind === "customer-information" ? "كشف بيانات العملاء"
      : kind === "customer-blacklist" ? "كشف العملاء المحظورين"
      : "كشف سقف ائتمان العملاء",
    note: (kind) =>
      kind === "customers-exceeded-credit-limit"
        ? "يظهر العميل هنا فقط عندما يتجاوز رصيده المدين سقف الائتمان. الأرصدة الدائنة لا تُعد تجاوزاً مهما كبرت."
      : kind === "customer-blacklist"
        ? "يعتمد هذا الكشف على حقل الحظر المسجّل فقط، ولا يُستنتج من حالة العميل أو رصيده أو ملاحظاته."
      : "الرصيد الحالي مأخوذ من المصدر المعتمد نفسه الذي يقرأه كشف إجمالي ذمم العملاء.",
    noRows: (kind) =>
      kind === "customers-exceeded-credit-limit" ? "لا يوجد عملاء متجاوزون لسقف الائتمان."
      : kind === "customer-blacklist" ? "لا يوجد عملاء محظورون."
      : "لا توجد بيانات.",
    natureLabel: (nature) =>
      nature === "Debit" ? "مدين" : nature === "Credit" ? "دائن" : "متعادل",
    code: "رقم العميل", name: "اسم العميل", nameEn: "الاسم بالإنجليزية", category: "الفئة",
    status: "الحالة", active: "نشط", inactive: "غير نشط", customer: "العميل",
    salesRep: "مندوب المبيعات", controlAccount: "الحساب الرئيسي", creditLimit: "سقف الائتمان",
    balance: "الرصيد الحالي", nature: "طبيعة الرصيد", exceeded: "المبلغ المتجاوز",
    utilisation: "نسبة الاستخدام %", available: "الائتمان المتاح", blacklistReason: "سبب الحظر",
    city: "المدينة", country: "الدولة", taxNumber: "الرقم الضريبي", registration: "السجل التجاري",
    phone: "الهاتف", mobile: "الجوال", email: "البريد الإلكتروني", address: "العنوان",
    contactPerson: "جهة الاتصال", paymentMethod: "طريقة الدفع", parentCustomer: "العميل الأب",
    customers: "عدد العملاء", totalLimit: "إجمالي سقوف الائتمان",
    totalBalance: "إجمالي الأرصدة", totalExceeded: "إجمالي المتجاوز",
  },
  agingGrouped: {
    title: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "الأعمار حسب العملة" : "الأعمار حسب الفئة",
    groupHeader: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "العملة" : "الفئة",
    unassigned: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "بدون عملة" : "بدون فئة",
    asOf: "لغاية تاريخ",
    customers: "عدد العملاء",
    openItems: "عدد البنود",
    notDue: "غير مستحق",
    unclassified: "غير مصنف",
    total: "إجمالي المستحق",
    grandTotal: "الإجمالي العام",
    groups: "عدد المجموعات",
    noRows: "لا توجد بنود مستحقة.",
    basisNote:
      "المبالغ بعملة الشركة، لأنها الأساس الوحيد الذي يحتسب عليه محرك الأعمار المتبقي. الأعمدة تأتي من فترات الأعمار المُعرّفة، ولا يتم أي تحويل بسعر اليوم.",
  },
  reportsReceipts: {
    currencyLocalTitle: "حساب العميل - عملة / محلي",
    currencyLocalNote:
      "تُعرض مبالغ العملة الأصلية وعملة الشركة جنباً إلى جنب، كل منهما على أساسه، وبسعر التحويل المُرحّل. لا يتم أي تحويل بسعر اليوم.",
    transactionBasis: "بعملة الحركة",
    localBasis: "بعملة الشركة",
  },
  customerAccountCostCenter: {
    title: "حساب العميل - مركز التكلفة",
    date: "التاريخ",
    voucherType: "نوع السند",
    voucherNumber: "رقم السند",
    reference: "المرجع",
    description: "البيان",
    costCenterCode: "رمز مركز التكلفة",
    costCenterName: "مركز التكلفة",
    debit: "مدين",
    credit: "دائن",
    runningBalance: "الرصيد الجاري",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    currencyDebit: "مدين عملة",
    currencyCredit: "دائن عملة",
    branch: "الفرع",
    customer: "العميل",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    customerCategory: "فئة العميل",
    controlAccount: "الحساب الرئيسي",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    openingBalance: "الرصيد الافتتاحي",
    totalDebit: "إجمالي المدين",
    totalCredit: "إجمالي الدائن",
    closingBalance: "الرصيد الختامي",
    costCenterFilter: "مركز التكلفة",
    allCostCenters: "كل مراكز التكلفة",
    noCostCenter: "بدون مركز تكلفة",
    summary: "ملخص حسب مركز التكلفة",
    movements: "عدد الحركات",
    coverage: "حركات بمركز تكلفة",
    noRows: "لا توجد حركات.",
    selectCustomer: "اختر العميل لعرض حسابه.",
    customerRequired: "العميل مطلوب لهذا الاستعلام.",
    grainNote:
      "سطر واحد لكل حركة محاسبية فعّالة بمركز التكلفة الذي رُحّلت به. الحركات بدون مركز تكلفة تظهر كما هي ولا تُنسب لأي مركز.",
    runningBalanceNote:
      "الرصيد الجاري يُحسب في الخادم بالترتيب المحاسبي، ولا يتغيّر بترتيب العرض.",
    openDocument: "فتح المستند",
  },
  voucherTransactionsInquiry: {
    title: "حركات السندات",
    voucherDate: "تاريخ السند",
    voucherNumber: "رقم السند",
    voucherType: "نوع السند",
    status: "الحالة",
    accountingEffect: "الأثر المحاسبي",
    partyType: "نوع الطرف",
    partyCode: "رقم العميل / الطرف",
    partyName: "اسم العميل / الطرف",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    voucherAmount: "قيمة السند",
    voucherAmountLocal: "قيمة السند - عملة الشركة",
    nature: "طبيعة السند",
    paymentMethod: "طريقة الدفع",
    branch: "الفرع",
    costCenter: "مركز التكلفة",
    reference: "المرجع",
    notes: "الملاحظات",
    createdBy: "المستخدم",
    createdAt: "تاريخ الإنشاء",
    reversalReference: "رقم سند العكس / المرجع",
    counterparty: "الطرف الآخر",
    customer: "عميل",
    allTypes: "كل الأنواع",
    natureDebit: "مدين",
    natureCredit: "دائن",
    natureNeutral: "محايد",
    effectPosted: "مرحّل",
    effectNotPosted: "غير مرحّل",
    effectReversed: "معكوس",
    effectNone: "بدون قيد محاسبي",
    statusDraft: "مسودة",
    statusAudited: "مدققة",
    statusPosted: "مرحّلة",
    statusRejected: "مرفوضة",
    statusCancelled: "ملغاة",
    statusReversed: "معكوسة",
    multiple: "متعدد",
    voucherCount: "عدد السندات",
    postedCount: "مرحّلة",
    unpostedCount: "غير مرحّلة",
    reversedCount: "معكوسة",
    partyCount: "عدد الأطراف",
    totalDebit: "إجمالي سندات المدين",
    totalCredit: "إجمالي سندات الدائن",
    totalNeutral: "إجمالي السندات المحايدة",
    effectiveDebit: "المدين الفعّال",
    effectiveCredit: "الدائن الفعّال",
    effectiveNet: "الصافي الفعّال - عملة الشركة",
    historicalNet: "الصافي التاريخي - عملة الشركة",
    countsByType: "عدد السندات حسب النوع",
    noRows: "لا توجد سندات.",
    documentGrainNote:
      "سطر واحد لكل سند/مستند - وليس لكل قيد محاسبي. القيود المحاسبية تظهر في «الحركات اليومية».",
    reversalNote:
      "السندات المعكوسة تبقى ظاهرة تاريخياً ولا تدخل في الإجماليات الفعّالة.",
    journalUnavailable: "لا يوجد مسار معتمد لعرض القيد المحاسبي لسند محدد.",
    details: "تفاصيل السند",
    openDocument: "فتح المستند",
    close: "إغلاق",
  },
  outstandingInvoices: {
    title: "فواتير العملاء المستحقة",
    invoiceDate: "تاريخ الفاتورة",
    invoiceNumber: "رقم الفاتورة",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    customerCategory: "فئة العميل",
    currency: "العملة",
    invoiceTotal: "إجمالي الفاتورة",
    settledAmount: "المبلغ المسدد",
    remainingAmount: "المبلغ المتبقي",
    paymentStatus: "حالة السداد",
    dueDate: "تاريخ الاستحقاق",
    paymentMethod: "طريقة الدفع",
    salesRepresentative: "مندوب المبيعات",
    branch: "الفرع",
    reference: "المرجع",
    notes: "الملاحظات",
    asOf: "لغاية تاريخ",
    allOutstanding: "كل المستحقة",
    unpaid: "غير مسددة",
    partiallyPaid: "مسددة جزئياً",
    invoiceCount: "عدد الفواتير المستحقة",
    unpaidCount: "غير مسددة",
    partiallyCount: "مسددة جزئياً",
    customerCount: "عدد العملاء",
    totalInvoice: "إجمالي قيمة الفواتير",
    totalSettled: "إجمالي المسدد",
    totalRemaining: "إجمالي المتبقي",
    totalRemainingLocal: "إجمالي المتبقي - عملة الشركة",
    minRemaining: "أقل مبلغ متبقي",
    maxRemaining: "أعلى مبلغ متبقي",
    noRows: "لا توجد فواتير مستحقة.",
    flatListNote:
      "سطر واحد لكل فاتورة مستحقة، لكل العملاء معاً. الفواتير المسددة بالكامل غير مشمولة.",
    dueDateNote:
      "لا يسجل النظام تاريخ استحقاق للفواتير، لذا «مستحقة» تعني وجود رصيد غير محصّل.",
    settlementDetails: "تفاصيل التسديد",
    openInvoice: "فتح الفاتورة",
  },
  receiptsWithoutInvoices: {
    title: "سندات القبض بدون فواتير",
    receiptDate: "تاريخ سند القبض",
    receiptNumber: "رقم سند القبض",
    customerCode: "رقم العميل",
    customerName: "اسم العميل",
    customerCategory: "فئة العميل",
    currency: "العملة",
    receiptAmount: "قيمة سند القبض",
    allocatedAmount: "المبلغ المطابق",
    unallocatedAmount: "المبلغ غير المطابق",
    matchingStatus: "حالة المطابقة",
    paymentMethod: "طريقة الدفع",
    destination: "الصندوق / البنك",
    reference: "رقم المرجع",
    branch: "الفرع",
    costCenter: "مركز التكلفة",
    collector: "المحصل",
    salesRepresentative: "مندوب المبيعات",
    notes: "الملاحظات",
    createdBy: "المستخدم",
    createdAt: "تاريخ الإنشاء",
    unallocated: "غير مطابق",
    partiallyAllocated: "مطابق جزئياً",
    fullyAllocated: "مطابق بالكامل",
    multiple: "متعدد",
    showFullyAllocated: "إظهار السندات المطابقة بالكامل",
    minUnallocated: "أقل مبلغ غير مطابق",
    maxUnallocated: "أعلى مبلغ غير مطابق",
    receiptCount: "عدد السندات",
    unallocatedCount: "غير مطابقة كلياً",
    partiallyCount: "مطابقة جزئياً",
    fullyCount: "مطابقة بالكامل",
    totalReceipt: "إجمالي قيمة السندات",
    totalAllocated: "إجمالي المطابق",
    totalUnallocated: "إجمالي غير المطابق",
    totalUnallocatedLocal: "إجمالي غير المطابق - عملة الشركة",
    overAllocated: "مطابقة زائدة",
    overAllocatedWarning: "التسويات تتجاوز قيمة السند - خلل في سلامة البيانات.",
    noRows: "لا توجد سندات قبض غير مطابقة.",
    note: "المبلغ المطابق مأخوذ من تسويات الحساب الفعّالة فقط؛ التسويات الملغاة لا تُحتسب.",
    readOnlyNote:
      "شاشة استعلام للقراءة فقط - لا تُطابق سنداً بفاتورة ولا تُنشئ تسوية. المطابقة تتم من شاشة تسوية الحسابات.",
    details: "تفاصيل السند",
    payments: "تفاصيل الدفع",
    allocations: "التسويات",
    settlementDate: "تاريخ التسوية",
    settlementNumber: "رقم التسوية",
    documentNumber: "رقم المستند",
    documentType: "نوع المستند",
    documentDate: "تاريخ المستند",
    status: "الحالة",
    active: "فعّالة",
    cancelled: "ملغاة",
    cancellationReason: "سبب الإلغاء",
    noAllocations: "لا توجد تسويات على هذا السند.",
    line: "السطر",
    amount: "المبلغ",
    chequeNumber: "رقم الشيك",
    chequeDueDate: "تاريخ استحقاق الشيك",
    fromReceiptOrder: "من أمر قبض",
    close: "إغلاق",
  },
  missingVoucherSequences: {
    title: "التسلسلات المفقودة في سندات القبض",
    missingNumber: "الرقم المفقود",
    sequence: "التسلسل",
    year: "السنة",
    missingFrom: "من رقم",
    missingTo: "إلى رقم",
    missingCount: "عدد الأرقام المفقودة",
    gapCount: "عدد الفجوات",
    firstMissing: "أول رقم مفقود",
    lastMissing: "آخر رقم مفقود",
    previousNumber: "الرقم السابق",
    nextNumber: "الرقم اللاحق",
    previousDate: "تاريخ السند السابق",
    nextDate: "تاريخ السند اللاحق",
    previousStatus: "حالة السند السابق",
    nextStatus: "حالة السند اللاحق",
    gapSize: "حجم الفجوة",
    scopes: "نطاقات الترقيم",
    resetPolicy: "سياسة التصفير",
    startNumber: "بداية التسلسل",
    lastIssued: "آخر رقم مُصدر",
    issuedCount: "عدد الأرقام المُصدرة",
    duplicates: "أرقام مكررة",
    duplicateSerial: "الرقم",
    duplicateVouchers: "السندات",
    noGaps: "لا توجد أرقام مفقودة في تسلسل سندات القبض.",
    note: "السندات الملغاة والمعكوسة والمحذوفة استهلكت أرقامها، فهي ليست مفقودة.",
    auditOnly: "هذه شاشة تدقيق للقراءة فقط — لا تُنشئ سنداً ولا تُصلح ترقيماً ولا تُصفّر تسلسلاً.",
    viewDetails: "عرض التفاصيل",
    groupedGaps: "الفجوات المجمّعة",
    close: "إغلاق",
    numberFrom: "الرقم المفقود من",
    numberTo: "الرقم المفقود إلى",
  },
  costCenterAccount: {
    title: "استعلام حساب مركز التكلفة",
    costCenter: "مركز التكلفة",
    costCenterCode: "رمز مركز التكلفة",
    costCenterName: "اسم مركز التكلفة",
    includeChildCostCenters: "شمول مراكز التكلفة الفرعية",
    allocationPercentage: "نسبة التحميل",
    allocatedDebit: "مدين محمّل",
    allocatedCredit: "دائن محمّل",
    allocatedLocalDebit: "مدين محلي محمّل",
    allocatedLocalCredit: "دائن محلي محمّل",
    totalAllocatedDebit: "مجموع المدين المحمّل",
    totalAllocatedCredit: "مجموع الدائن المحمّل",
    selectPrompt: "اختر مركز تكلفة وحساباً ثم اضغط بحث",
    rolledUp: "النتيجة تجميع لعدة مراكز تكلفة",
    wholeLineNote:
      "كل سطر قيد يُحمّل على مركز تكلفة واحد بالكامل، لذا نسبة التحميل 100% دائماً في النظام الحالي",
  },
  accountMovements: {
    title: "استعلام حركات الحساب",
    movementDate: "تاريخ الحركة",
    sourceReference: "مرجع المصدر",
    runningBalance: "الرصيد الجاري",
    balanceType: "نوع الرصيد",
    actions: "الإجراءات",
    open: "فتح",
    openingBalance: "الرصيد الافتتاحي",
    closingBalance: "الرصيد الختامي",
    includeOpeningBalance: "شمول الرصيد الافتتاحي",
    netMovement: "صافي الحركة",
    advancedFilters: "عوامل تصفية إضافية",
    partyType: "نوع الذمة",
    party: "الذمة",
    customer: "عميل",
    supplier: "مورّد",
    employee: "موظف",
    voucherKind: "نوع السند",
    postingStatusPosted: "المرحّلة فقط",
    postingStatusUnposted: "غير المرحّلة فقط",
    postingStatusAll: "الكل",
    zero: "متعادل",
    runningBalanceUnavailable:
      "الرصيد الجاري يظهر فقط عند الترتيب حسب تسلسل الحركة وللحركات المرحّلة",
    unpostedNote: "الحركات غير المرحّلة معروضة وغير مشمولة في الأرصدة",
    balancesNote: "الرصيد الافتتاحي والختامي يشملان الحركات المرحّلة فقط",
    modules: {
      manual: "قيد يدوي",
      receipts: "المقبوضات",
      payments: "المدفوعات",
      cashBanks: "النقدية والبنوك",
      payroll: "الرواتب",
      fixedAssets: "الأصول الثابتة",
      sales: "المبيعات",
      purchases: "المشتريات",
      openingBalance: "رصيد افتتاحي",
      closing: "قيود الإقفال",
      system: "مُولّد آلياً",
    },
  },
  /**
   * المخزون والمستودعات › الاستعلامات — the words the twenty-four screens share.
   *
   * One vocabulary for the module, so «المتبقية» never means outstanding-to-receive on
   * one screen and outstanding-to-invoice on the next.
   */
  inventoryInquiries: {
    itemTypes: { "1": "مادة مخزنية", "2": "مادة غير مخزنية", "3": "خدمة" },
    register: {
      title: "سجل أصناف المواد", breadcrumb: "المخزون والمستودعات / الاستعلامات",
      searchPlaceholder: "بحث بالرمز أو الاسم أو الباركود…",
      expandAll: "توسيع الكل", collapseAll: "طي الكل", columnChooser: "اختيار الأعمدة",
      clear: "مسح", summary: "{items} صنف · {groups} مجموعة",
      itemsCount: "{direct} مباشرة · {all} إجمالاً",
      loadError: "تعذّر تحميل السجل.", empty: "لا توجد نتائج مطابقة.",
      notStockNote: "هذا سجل بطاقات، لا رصيد مخزون. الكميات تأتي من دفتر الحركات وحده.",
      level: "المستوى", code: "رمز المادة", name: "اسم المادة", nameAr: "الاسم بالعربية",
      group: "المجموعة", class1: "تصنيف مادة 1", class2: "تصنيف مادة 2", class3: "تصنيف مادة 3",
      unit: "الوحدة", type: "نوع المادة", country: "بلد المنشأ", tax: "ضريبة الشراء",
      warehouse: "المستودع الافتراضي", barcode: "الباركود", price: "سعر المستهلك",
      supplier: "المورّد المفضل", min: "الحد الأدنى", reorder: "حد إعادة الطلب",
      conversions: "تحويلات الوحدة", movement: "عليها حركة", status: "الحالة", open: "فتح",
      active: "فعّالة", inactive: "معطّلة", yes: "نعم", no: "لا", neverMoved: "بلا حركة",
    },
    noRows: "لا توجد حركات مطابقة لمعايير البحث.",
    noItemCards: "لا توجد بطاقات مواد مطابقة لمعايير البحث.",
    unspecified: "غير محدد",
    cards: "عدد البطاقات",
    conversions: "تحويلات الوحدة",
    hasMovements: "عليها حركة",
    incomplete: "بطاقات ناقصة",
    incompleteYes: "ناقصة",
    incompleteOnly: "الناقصة فقط",
    neverMovedOnly: "بلا أي حركة",
    activeOnly: "الفعّالة فقط",
    inactiveOnly: "المعطّلة فقط",
    withGroup: "لها مجموعة",
    withCategory: "لها تصنيف",
    withCountry: "لها بلد منشأ",
    withPurchaseTax: "لها ضريبة شراء",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    asOfDate: "الرصيد كما في تاريخ",
    item: "المادة",
    itemCode: "رمز المادة",
    itemName: "اسم المادة",
    warehouse: "المستودع",
    toWarehouse: "المستودع المستلم",
    group: "المجموعة",
    category: "التصنيف",
    supplier: "المورّد",
    costCenter: "مركز التكلفة",
    branch: "الفرع",
    reason: "السبب",
    unit: "الوحدة",
    search: "بحث",
    documentNumber: "رقم المستند",
    documentType: "نوع الحركة",
    document: "المستند",
    date: "التاريخ",
    status: "الحالة",
    direction: "الاتجاه",
    directionIn: "وارد",
    directionOut: "صادر",
    quantity: "الكمية",
    quantityIn: "وارد",
    quantityOut: "صادر",
    valueIn: "قيمة واردة",
    valueOut: "قيمة صادرة",
    runningQuantity: "الرصيد",
    runningValue: "رصيد القيمة",
    openingQuantity: "الرصيد الافتتاحي",
    closingQuantity: "الرصيد الختامي",
    unitCost: "تكلفة الوحدة",
    averageCost: "متوسط التكلفة",
    inventoryValue: "قيمة المخزون",
    available: "المتاح",
    minimumStock: "الحد الأدنى",
    reorderLevel: "حد إعادة الطلب",
    shortage: "النقص",
    lastMovement: "آخر حركة",
    daysSince: "الأيام منذ آخر حركة",
    days: "عدد الأيام",
    orderNumber: "أمر الشراء",
    orderDate: "تاريخ أمر الشراء",
    expectedDate: "تاريخ التسليم المتوقع",
    ordered: "المطلوبة",
    received: "المستلمة",
    remaining: "المتبقية",
    invoiced: "المفوترة",
    uninvoiced: "غير المفوترة",
    uninvoicedValue: "قيمة غير المفوترة",
    returned: "المرتجعة",
    progress: "التقدّم",
    lastReceipt: "آخر استلام",
    receiptNumber: "سند الاستلام",
    receiptDate: "تاريخ الاستلام",
    receiptValue: "قيمة الاستلام",
    comparison: "حالة المطابقة",
    difference: "الفرق",
    differenceValue: "قيمة الفرق",
    invoiceNumber: "رقم الفاتورة",
    supplierInvoice: "رقم فاتورة المورّد",
    dueDate: "تاريخ الاستحقاق",
    subTotal: "الصافي",
    tax: "الضريبة",
    total: "الإجمالي",
    paid: "المسدد",
    outstanding: "المتبقي",
    paymentStatus: "حالة السداد",
    returnNumber: "رقم المردود",
    returnValue: "قيمة المردود",
    taxReversal: "عكس الضريبة",
    countNumber: "رقم الجرد",
    countDate: "تاريخ الجرد",
    systemQuantity: "كمية النظام",
    countedQuantity: "الكمية الفعلية",
    adjustment: "التسوية",
    adjustmentStatus: "حالة التسوية",
    responsible: "المسؤول",
    unadjustedOnly: "غير المسوى فقط",
    positiveOnly: "الفروقات الموجبة",
    negativeOnly: "الفروقات السالبة",
    includeDraft: "تضمين المسودات",
    outstandingOnly: "غير المكتمل فقط",
    overdueOnly: "المتأخر فقط",
    uninvoicedOnly: "غير المفوتر فقط",
    balanceSign: "نوع الرصيد",
    positive: "موجب",
    zero: "صفر",
    negative: "سالب",
    all: "الكل",
    openSource: "فتح المصدر",
    runningQty: "الرصيد التراكمي",
    movements: "عدد الحركات",
    summedQuantity: "مجموع الحركات",
    checkedPairs: "الأزواج المفحوصة",
    mismatches: "عدد الفروقات",
    mismatchesOnly: "الفروقات فقط",
    itemsCount: "عدد المواد",
    minimumAgeDays: "أقدم من (يوم)",
    againstMinimum: "المقارنة بالحد الأدنى",
    screens: {
      itemBalance: "استعلام رصيد مادة",
      stockByWarehouse: "أرصدة المواد حسب المستودع",
      itemMovements: "حركات المادة",
      itemLedgerCard: "بطاقة حركة المادة",
      warehouseMovements: "حركات المستودع",
      stockAsOf: "رصيد المخزون بتاريخ",
      belowReorder: "المواد تحت حد إعادة الطلب",
      nonMoving: "المواد بدون حركة",
      purchaseOrders: "استعلام أوامر الشراء",
      orderReceipts: "متابعة استلام أوامر الشراء",
      goodsReceipts: "استعلام سندات الاستلام",
      receivedNotInvoiced: "المواد المستلمة غير المفوترة",
      purchaseInvoices: "متابعة فواتير مشتريات المواد",
      purchaseReturns: "متابعة مردود مشتريات المواد",
      stockReceipts: "حركات الإدخال المخزني",
      stockIssues: "حركات الإخراج المخزني",
      transfers: "التحويلات بين المستودعات",
      adjustments: "تسويات المخزون",
      stockCounts: "استعلام الجرد",
      countDifferences: "فروقات الجرد",
      itemBySupplier: "حركة مادة حسب المورد",
      itemRegister: "سجل أصناف المواد",
      itemsByGroup: "المواد حسب المجموعة والتصنيف",
      stockLedger: "سجل حركة المخزون",
      reconciliation: "فحص مطابقة دفتر المخزون",
    },
  },
  inventoryReports: {
    customer: "Customer",
    purchaseOrder: "Purchase order",
    stockCount: "Stock count",
    movementType: "Movement type",
    adjustmentReason: "Adjustment reason",
    storageLocation: "Storage location",
    top: "Top",
    topAll: "All",
    marginBelow: "Margin below %",
    valueAbove: "Value above",
    activeOnly: "Active items only",
    costsHidden:
      "Cost, value and profit are withheld: you do not hold the inventory report costs permission.",
  },
  inventoryTransactions: {
    tools: "أدوات",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "عكس",
    cancelDocument: "إلغاء المستند",
    open: "فتح",
    noJournalYet: "لم يُرحَّل هذا المستند بعد، فلا يوجد قيد لعرضه.",
    noJournalEver: "هذه الحركة لا تُنشئ قيداً محاسبياً: الكمية والقيمة لم تتغيّرا على مستوى الشركة.",
    noRows: "لا توجد مستندات مطابقة",
    saveFailed: "تعذّر تنفيذ العملية.",
    filterSearch: "بحث",
    filterFrom: "من تاريخ",
    filterTo: "إلى تاريخ",
    filterApply: "عرض",
    saved: "تم الحفظ كمسودة. لم تتأثر الكميات ولا الذمم ولا القيود.",
    posted: "تم الترحيل.",
    audited: "تم التدقيق.",
    cancelled: "تم الإلغاء.",
    reversed: "تم العكس بقيد معاكس، والمستند الأصلي محفوظ كما هو.",
    addLine: "إضافة سطر",
    removeLine: "حذف",
    date: "التاريخ",
    warehouse: "المستودع",
    toWarehouse: "المستودع المستلم",
    storageLocation: "موقع التخزين",
    supplier: "المورّد",
    reference: "المرجع",
    notes: "ملاحظات",
    reason: "السبب",
    costCenter: "مركز التكلفة",
    item: "المادة",
    unit: "الوحدة",
    quantity: "الكمية",
    unitCost: "تكلفة الوحدة",
    unitPrice: "سعر الوحدة",
    lineValue: "قيمة السطر",
    available: "المتاح",
    averageCost: "متوسط التكلفة",
    total: "الإجمالي",
    status: "الحالة",
    number: "الرقم",
    branch: "الفرع",
    reversalDate: "تاريخ العكس",
    selectOrder: "اختيار أمر شراء",
    selectReceipt: "اختيار سند استلام",
    selectInvoice: "اختيار فاتورة",
    selectCount: "اختيار جرد",
    orderNumber: "رقم أمر الشراء",
    orderDate: "تاريخ أمر الشراء",
    receiptNumber: "رقم سند الاستلام",
    invoiceNumber: "رقم الفاتورة",
    deliveryNote: "رقم إشعار التسليم",
    supplierInvoiceNumber: "رقم فاتورة المورّد",
    supplierInvoiceDate: "تاريخ فاتورة المورّد",
    dueDate: "تاريخ الاستحقاق",
    ordered: "الكمية المطلوبة",
    previouslyReceived: "المستلمة سابقاً",
    remaining: "المتبقية",
    receivedNow: "المستلمة الآن",
    difference: "الفرق",
    comparison: "حالة المطابقة",
    receivedDescription: "الوصف كما ورد من المورّد",
    orderPrice: "سعر أمر الشراء",
    invoicePrice: "سعر فاتورة المورّد",
    priceDifference: "فرق السعر",
    invoiced: "المفوترة",
    remainingToInvoice: "المتبقي للفوترة",
    returned: "المرتجعة",
    remainingToReturn: "المتبقي القابل للإرجاع",
    systemQuantity: "كمية النظام",
    countedQuantity: "الكمية الفعلية",
    differenceValue: "قيمة الفرق",
    responsible: "المسؤول",
    loadWarehouseItems: "تحميل مواد المستودع",
    createAdjustment: "إنشاء تسوية الفروقات",
    requiredDate: "التاريخ المطلوب",
    requestedBy: "الجهة الطالبة",
    department: "القسم الطالب",
    approve: "اعتماد",
    close: "إغلاق",
    conversion: "التحويل",
    progress: "التقدّم",
    groups: {
      purchases: "المشتريات",
      sales: "المبيعات",
    },
    cards: {
      requests: "طلب شراء مواد",
      orders: "أمر شراء مواد",
      receipts: "سند استلام مواد",
      invoices: "فاتورة مشتريات مواد",
      returns: "مردود مشتريات مواد",
      stockReceipt: "إدخال مخزني",
      stockIssue: "إخراج مخزني",
      transfer: "تحويل بين المستودعات",
      adjustment: "تسوية مخزون",
      count: "جرد مخزون",
      countAdjustment: "تسوية فروقات الجرد",
      opening: "الرصيد الافتتاحي للمخزون",
    },
    hints: {
      requests: "طلب داخلي بلا مورّد وبلا سعر. لا يؤثر على المخزون ولا على الذمم ولا على القيود.",
      orders: "التزام تجاه مورّد بأسعار متفق عليها. لا يدخل مخزون ولا يُنشئ ذمة.",
      receipts: "هنا تدخل البضاعة المشتراة إلى المستودع، مقابل حساب البضاعة المستلمة غير المفوترة.",
      invoices: "فاتورة المورّد لبضاعة دخلت المستودع سابقاً: تُنشئ الذمة والضريبة ولا تزيد المخزون إطلاقاً.",
      returns: "إرجاع بضاعة إلى المورّد بتكلفة الاستلام الأصلية وبنسبة الضريبة الأصلية.",
      stockReceipt: "إدخال داخلي غير مرتبط بالشراء. لا يُستخدم لتجاوز دورة أمر الشراء والاستلام والفوترة.",
      stockIssue: "صرف مخزني بتكلفة المتوسط المرجح القائم وقت الصرف، وليس بتكلفة يكتبها المستخدم.",
      transfer: "نقل بين مستودعين: الكمية والقيمة على مستوى الشركة لا تتغيّران، ولا يُنشأ قيد.",
      adjustment: "تصحيح مقصود للكمية أو القيمة، مقابل حساب أرباح أو خسائر التسوية المُعرَّف.",
      count: "الجرد يسجّل الواقع فقط. لا يغيّر رصيداً ولا يُنشئ قيداً؛ الفروقات تُعالَج بمستند مستقل.",
      countAdjustment: "تسوية فروقات جرد معتمد. تُنشأ كمسودة دائماً، ولا تُرحَّل تلقائياً.",
      opening: "بضاعة كانت موجودة قبل النظام. كل سطر يحمل تكلفة وحدته — الترحيل يُرفض بدونها، والطرف المقابل حساب أرصدة افتتاحية في حقوق الملكية لا أرباح جرد.",
    },
  },
  inventorySales: {
    indivisibleOfferClause: "هذا العرض جزء لا يتجزأ، وأي تخفيض في الكميات يغيّر الأسعار.",
    indivisibleOffer: "العرض جزء لا يتجزأ", yes: "نعم", no: "لا",
    deliveryPeriod: "مدة التسليم", origin: "بلد المنشأ",
    insertFromOffer: "إدراج من عرض بيع", insertFromDelivery: "إدراج من إذن تسليم",
    autoDeliveryOnPost: "يُنشأ تلقائياً عند الترحيل",
    orderDateBeforeToday: "لا يمكن أن يكون تاريخ عرض البيع قبل اليوم.",
    orderNumber: "رقم عرض البيع",
    deliveryNumber: "رقم إذن التسليم",
    invoiceNumber: "رقم الفاتورة",
    returnNumber: "رقم المردود",
    customer: "العميل",
    customerCode: "رمز العميل",
    customerPo: "رقم أمر شراء العميل",
    customerPoDate: "تاريخ أمر شراء العميل",
    customerReference: "مرجع العميل",
    billingAddress: "عنوان الفاتورة",
    deliveryAddress: "عنوان التسليم",
    contactPerson: "الشخص المسؤول",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    salesperson: "مندوب المبيعات",
    driver: "السائق",
    vehicle: "رقم المركبة",
    deliveryReference: "مرجع التسليم",
    paymentMethod: "طريقة الدفع",
    paymentTerms: "شروط الدفع",
    returnReason: "سبب الإرجاع",
    internalNotes: "ملاحظات داخلية",
    selectOrder: "اختيار عرض بيع",
    selectDelivery: "اختيار إذن تسليم",
    selectInvoice: "اختيار فاتورة",
    ordered: "الكمية المطلوبة",
    deliveredNow: "الكمية المسلّمة الآن",
    previouslyDelivered: "سُلّم سابقاً",
    remainingToDeliver: "المتبقي للتسليم",
    delivered: "الكمية المسلّمة",
    previouslyInvoiced: "فُوتر سابقاً",
    remainingToInvoice: "المتبقي للفوترة",
    invoiceNow: "الكمية المفوترة الآن",
    sold: "الكمية المباعة",
    previouslyReturned: "أُرجع سابقاً",
    maxReturnable: "الحد الأقصى للإرجاع",
    returnNow: "الكمية المرتجعة الآن",
    available: "الرصيد المتاح",
    shortStock: "الكمية المطلوبة تتجاوز الرصيد المتاح",
    issueCost: "تكلفة الإخراج",
    costTotal: "إجمالي التكلفة",
    grossBeforeDiscount: "إجمالي قبل الخصم",
    discountTotal: "إجمالي الخصم",
    netBeforeTax: "الصافي قبل الضريبة",
    taxTotal: "إجمالي الضريبة",
    grandTotal: "الإجمالي النهائي",
    baseCurrencyTotal: "الإجمالي بالعملة الأساسية",
    exchangeRate: "سعر الصرف",
    discountType: "نوع الخصم",
    discountValue: "قيمة الخصم",
    taxType: "نوع الضريبة",
    fulfilment: "حالة التنفيذ",
    closeOrder: "إغلاق العرض",
    reopenOrder: "إعادة فتح العرض",
    approve: "اعتماد",
    noStockEffect: "هذه الفاتورة لا تحرّك المخزون: الكمية والتكلفة سُجّلتا على إذن التسليم.",
    stockOutHere: "ترحيل إذن التسليم يُخرج الكمية من المستودع ويحمّل تكلفة البضاعة المباعة بالمتوسط المرجح المتحرك.",
    cards: {
      orders: "عرض بيع",
      deliveries: "إذن تسليم",
      invoices: "فاتورة مبيعات مواد",
      returns: "مردود مبيعات مواد",
    },
    hints: {
      orders: "التزام تجاري فقط: لا يحرّك مخزوناً ولا يُنشئ قيداً ولا يمسّ رصيد العميل.",
      deliveries: "خروج البضاعة من المستودع، وتحميل تكلفة البضاعة المباعة بالتكلفة الفعلية لحظة التسليم.",
      invoices: "مطالبة العميل بقيمة البضاعة. الفاتورة المباشرة تُنشئ إذن التسليم تلقائياً عند الترحيل، والمبنية على إذن تسليم لا تحرّك المخزون.",
      returns: "إعادة بضاعة مباعة إلى المستودع بتكلفتها الأصلية، وقيد دائن على ذمة العميل.",
    },
  },
  dailyTransactionKit: {
    inquiry: "استعلام",
    newDocument: "جديد",
    save: "حفظ",
    saveAndNew: "حفظ جديد",
    saveIncomplete: "الحفظ متاح لمسودة مكتملة: الطرف، والمادة، والكمية",
    print: "طباعة",
    cancel: "إلغاء",
    lookupTitle: "الحركات السابقة — اختر حركة لتحميلها",
    registerTitle: "الحركات السابقة",
    emptyTitle: "لا توجد حركات سابقة",
    emptyHint: "لم يتم إدخال أي حركة على هذه الشاشة بعد. اضغط «جديد» لإنشاء حركة.",
  },
  sameModuleInquiries: {
    noRows: "لا توجد بيانات مطابقة",
    open: "فتح",
    receiptOrders: {
      title: "أوامر القبض",
      breadcrumb: "المحاسبة العامة « المقبوضات « الاستعلامات « أوامر القبض",
      notice: "سجل أوامر القبض كما هي — استعلام للقراءة فقط، لا يُنشئ أي قيد.",
      sourceNotice: "المصدر: أوامر القبض نفسها. المبلغ المقبوض والمتبقي محسوبان من سندات القبض المُرحَّلة، وغير مخزَّنين.",
      orderNumber: "رقم الأمر", date: "التاريخ", customer: "العميل", amount: "المبلغ المطلوب",
      collected: "المقبوض", remaining: "المتبقي", currency: "العملة", status: "الحالة",
      branch: "الفرع", reference: "المرجع", collector: "المُحصِّل",
      totalOrdered: "إجمالي المطلوب", totalCollected: "إجمالي المقبوض",
      totalRemaining: "إجمالي المتبقي", count: "عدد الأوامر",
    },
    reminderHistory: {
      title: "سجل تذكيرات أرصدة العملاء",
      breadcrumb: "المحاسبة العامة « المقبوضات « الاستعلامات « سجل تذكيرات أرصدة العملاء",
      notice: "التذكيرات التي أُنشئت فعلاً — وليس العملاء المؤهلين للتذكير.",
      sourceNotice: "المصدر: دفعات التذكير المحفوظة. لا يوجد جدول ثانٍ ولا نسخة للاستعلام.",
      batchNumber: "رقم الدفعة", date: "التاريخ", channel: "القناة", status: "الحالة",
      recipients: "المستلمون", sent: "أُرسلت", skipped: "متجاوَزة", failed: "فاشلة",
      totalBalance: "إجمالي الأرصدة", createdBy: "أنشأها", count: "عدد الدفعات",
    },
    bankReconciliation: {
      title: "التسوية البنكية",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الاستعلامات « التسوية البنكية",
      notice: "سجل التسويات البنكية — للقراءة فقط. المطابقة تتم من شاشة التسوية نفسها.",
      sourceNotice: "المصدر: التسويات البنكية وبنودها، نفس المصدر الذي يقرأه تقرير البنوك.",
      number: "رقم التسوية", bank: "البنك", account: "الحساب البنكي", period: "الفترة",
      statementDate: "تاريخ الكشف", statementBalance: "رصيد الكشف", ledgerBalance: "رصيد الدفاتر",
      reconciled: "المُطابَق", difference: "الفرق", status: "الحالة", count: "عدد التسويات", statusDraft: "مسودة", statusCompleted: "مكتملة",
    },
  },
  inquiryKit: {
    search: "بحث",
    reset: "إعادة تعيين",
    refresh: "تحديث",
    print: "طباعة",
    printPreview: "معاينة الطباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    columns: "الأعمدة",
    columnSettings: "إعدادات الأعمدة",
    columnSettingsHint: "اختر الأعمدة التي تظهر على الشاشة. لا يتأثر التصدير بهذا الاختيار.",
    columnLocked: "ظاهر دائماً",
    restoreDefaults: "استعادة الافتراضي",
    done: "تم",
    resultInfo: "عرض {from}–{to} من {total}",
    rowsPerPage: "عدد السجلات",
    pageOf: "صفحة {page} من {pages}",
    firstPage: "الصفحة الأولى",
    previousPage: "الصفحة السابقة",
    nextPage: "الصفحة التالية",
    lastPage: "الصفحة الأخيرة",
    sortAscending: "ترتيب تصاعدي",
    sortDescending: "ترتيب تنازلي",
    printFailed: "تعذّرت الطباعة، وتم فتح المستند للطباعة يدوياً.",
  },
  journal: {
    title: "القيود اليومية",
    voucherType: "نوع السند",
    voucherInformation: "معلومات السند",
    referenceInformation: "معلومات المرجع",
    tabAttachments: "المرفقات",
    tabPrintOptions: "خيارات الطباعة",
    tabAuditHistory: "سجل التعديلات",

    linkedDocuments: "السندات المرتبطة",
    additionalInformation: "معلومات إضافية",
    reverses: "يعكس السند",
    reversedBy: "عُكس بالسند",
    noLinkedDocuments: "لا توجد سندات مرتبطة",
    openDocument: "فتح",
    saveBeforeAttaching: "احفظ القيد أولاً لإضافة المرفقات",
    printDialogTitle: "طباعة القيد",
    withCostCenters: "مع مراكز التكلفة",
    withoutCostCenters: "بدون مراكز التكلفة",
    auditHistoryEmpty: "لا توجد تعديلات مسجّلة",
    auditWhen: "التاريخ والوقت",
    auditHistoryUnsaved: "يظهر سجل التعديلات بعد حفظ القيد",
    createdBy: "أنشئ بواسطة",
    createdOn: "تاريخ الإنشاء",
    modifiedBy: "آخر تعديل بواسطة",
    modifiedOn: "تاريخ آخر تعديل",
    auditCategories: { header: "رأس السند", line: "الأسطر", attachment: "المرفقات" },
    auditActions: {
      created: "إنشاء",
      updated: "تعديل",
      deleted: "حذف",
      audited: "تم التدقيق",
      posted: "تم الترحيل",
      returned: "أُعيد للتصحيح",
      reversed: "تم العكس",
      reopened: "فُتح للتعديل",
      other: "إجراء",
    },
    dropFilesHere: "أفلت الملفات هنا أو اخترها",
    accountBalanceHint: "رصيد الحساب",
    selectCostCenter: "اختر مركز تكلفة",
    searchCostCenters: "بحث في مراكز التكلفة…",
    noCostCenters: "لا توجد مراكز تكلفة مطابقة",
    groupCostCenter: "مركز تجميعي",
    groupAccount: "حساب تجميعي",
    statusDraft: "مسودة",
    statusNotAudited: "غير مدقق",
    delete: "حذف",
    journalVoucher: "سند قيد",
    status: "حالة السند",
    statuses: {
      unposted: "غير مرحّل",
      posted: "مرحّل",
      audited: "مدقّق",
      rejected: "مُعاد",
      cancelled: "ملغى",
      reversed: "معكوس",
    },
    sequence: "التسلسل",
    voucherDate: "تاريخ السند",
    fiscalYear: "السنة",
    period: "الفترة",
    referenceNumber: "رقم المرجع",
    referenceDate: "تاريخ المرجع",
    referenceSequence: "تسلسل المرجع",
    salesperson: "اسم المندوب",
    user: "المستخدم",
    branch: "الفرع",
    notes: "ملاحظات",
    notesHint: "تُنسخ الملاحظات إلى بيان كل سطر جديد، ويمكن تعديل كل سطر على حدة",
    save: "حفظ",
    saveAndNew: "حفظ + جديد",
    inquiry: "استعلام",
    print: "طباعة",
    cancel: "إلغاء",
    newEntry: "قيد جديد",
    printOptions: "خيارات الطباعة",
    printSubledger: "طباعة الذمة",
    showCostCenters: "إظهار مراكز التكلفة في الطباعة",
    showCurrency: "إظهار العملة في الطباعة",
    printStamps: "طباعة الأختام والتواقيع",
    lines: "تفاصيل القيد",
    addLine: "إضافة سطر",
    removeLine: "حذف السطر",
    accountNumber: "رقم الحساب",
    accountName: "اسم الحساب",
    subledger: "الذمة",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    debit: "مدين",
    credit: "دائن",
    localDebit: "مدين محلي",
    localCredit: "دائن محلي",
    balance: "الرصيد",
    balanceType: "نوع الرصيد",
    description: "البيان",
    costCenter: "مراكز التكلفة",
    actions: "إجراءات",
    totalDebit: "مجموع مدين",
    totalCredit: "مجموع دائن",
    totalLocalDebit: "مجموع مدين محلي",
    totalLocalCredit: "مجموع دائن محلي",
    difference: "الفرق",
    balanced: "متوازن",
    unbalanced: "غير متوازن",
    attachments: "المرفقات",
    uploadFile: "إرفاق ملف",
    allowedTypes: "PDF، Excel، Word، صور — بحد أقصى ٢٥ ميجابايت",
    noAttachments: "لا توجد مرفقات",
    attachmentsPending: "بانتظار الحفظ",
    attachmentPendingNote: "تُرفع الملفات المحددة عند حفظ القيد بنجاح",
    attachmentStatuses: {
      pending: "بانتظار الرفع",
      uploading: "جارٍ الرفع",
      uploaded: "تم الرفع",
      failed: "فشل الرفع",
    },
    attachmentFileType: "النوع",
    attachmentFileSize: "الحجم",
    attachmentTypeRejected: "الملف «{name}» غير مقبول. الأنواع المسموحة: {types}",
    attachmentTooLarge: "الملف «{name}» يتجاوز الحد الأقصى {max}",
    attachmentEmpty: "الملف «{name}» فارغ",
    attachmentUploadFailed: "تم حفظ القيد، لكن تعذّر رفع بعض المرفقات. يمكنك إعادة المحاولة.",
    attachmentRetry: "إعادة المحاولة",
    attachmentRemove: "إزالة",
    attachmentsLinked: "تم ربط {count} مرفقاً بالقيد",
    download: "تنزيل",
    saveFirst: "احفظ القيد أولاً لتتمكن من إرفاق الملفات",
    selectAccount: "اختر حساباً",
    searchAccounts: "ابحث برقم الحساب أو اسمه",
    searchParties: "ابحث عن الذمة",
    noAccounts: "لا توجد حسابات مطابقة",
    noParties: "لا توجد ذمم مطابقة",
    partyRequired: "هذا الحساب يتطلب اختيار ذمة",
    costCenterRequired: "هذا الحساب يتطلب مركز تكلفة",
    notesRequired: "الملاحظات إجبارية على شاشة القيود اليومية",
    rateOverridden: "تم تعديل سعر التحويل يدوياً",
    rateMissing: "لا يوجد سعر تحويل ساري في تاريخ السند",
    readOnlyPosted: "هذا القيد مرحّل ولا يمكن تعديله",
    discardChanges: "إلغاء المدخلات",
    discardBody: "سيتم مسح محتويات النموذج غير المحفوظة فقط. لن يُحذف أي قيد محفوظ مسبقاً.",
    savedAs: "تم الحفظ برقم",
    filters: "عوامل التصفية",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    apply: "تطبيق",
    clear: "مسح",
    results: "النتائج",
    noResults: "لا توجد قيود مطابقة",
    open: "فتح",
    edit: "تعديل",
    view: "عرض",
    deleteEntry: "حذف القيد",
    deleteBody: "سيتم حذف هذا القيد غير المرحّل. لا يمكن التراجع.",
    lineCount: "عدد الأسطر",
    backToEntry: "رجوع إلى القيد",
    balanceInfo: "رصيد الحساب (للاطلاع فقط)",
    reopen: "فتح للتعديل",
    reopenTitle: "فتح القيد المرحّل للتعديل",
    reopenWarning:
      "سيُزال أثر هذا القيد من دفتر الأستاذ والأرصدة ويعود مسودة. بعد التعديل يجب تدقيقه وترحيله من جديد. يُسجَّل الإجراء وسببه واسم من نفّذه في سجل التدقيق.",
    reopenReason: "سبب الفتح للتعديل",
    reopenReasonHint: "مطلوب — حتى 500 حرف",
    reopenConfirm: "فتح للتعديل",
    reopened: "تم فتح القيد للتعديل وعاد مسودة",
  },
  glSettings: {
    title: "إعدادات النظام",
    pageDescription: "إعدادات محاسبية على مستوى الشركة لدليل الحسابات والقيود اليومية والذمم الفرعية ومراكز التكلفة.",
    descriptionColumn: "الوصف",
    valueColumn: "القيمة",

    requireDefaultCostCenter: "القيمة الافتراضية لمركز التكلفة إجبارية على الحسابات",
    requireDefaultCostCenterHint:
      "عند التفعيل، يجب أن يحمل كل حساب ترحيل قابل للترحيل مركز تكلفة افتراضي. الحسابات الحالية دون مركز تكلفة تبقى كما هي حتى تعديلها.",
    requireJournalEntryNotes: "الملاحظات إجبارية على شاشة القيود اليومية",
    requireJournalEntryNotesHint:
      "عند التفعيل، تصبح الملاحظات إلزامية على القيود اليومية الجديدة وغير المرحّلة القابلة للتعديل. القيود المرحّلة سابقاً لا تتأثر.",
    aggregateChildSubledger: "تجميع قيمة الرصيد الافتتاحي للذمم الفرعية في حساب الذمم الرئيسي",
    aggregateChildSubledgerHint:
      "عند التفعيل، تُجمَّع الأرصدة الافتتاحية للذمم الفرعية الفرعية مع حساب الذمم الرئيسي لأغراض الاستعلام والتقارير فقط، دون أي قيود محاسبية مكررة.",
    costPercentage: "نسبة التكلفة",
    costPercentageHint: "قيمة عشرية أكبر من أو تساوي صفر.",
    manualCostCenterEntryMethod: "الطريقة المعتمدة في الإدخال اليدوي لمراكز التكلفة",
    manualCostCenterEntryMethodHint: "الطريقة المعتمدة لإدخال توزيع مراكز التكلفة يدوياً على سطر القيد.",

    yes: "نعم",
    no: "لا",
    methodAmount: "قيمة",
    methodPercentage: "نسبة",

    restoreDefaults: "استعادة القيم الافتراضية",
    viewAuditHistory: "عرض سجل التعديلات",

    savedOk: "تم حفظ الإعدادات بنجاح.",
    saveError: "تعذّر حفظ الإعدادات.",
    loadError: "تعذّر تحميل الإعدادات.",
    unsavedChangesWarning: "لديك تغييرات غير محفوظة. هل تريد المغادرة دون حفظها؟",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكنك تعديل هذه الإعدادات.",

    restoreDefaultsConfirmTitle: "استعادة القيم الافتراضية",
    restoreDefaultsConfirmMessage:
      "سيتم إرجاع جميع إعدادات الأستاذ العام إلى القيم الافتراضية المعتمدة لهذه الشركة. لا يمكن التراجع عن هذا الإجراء.",
    restoreDefaultsConfirmLabel: "استعادة القيم الافتراضية",
    restoredOk: "تمت استعادة القيم الافتراضية بنجاح.",

    enableConfirmTitle: "تفعيل مركز التكلفة الافتراضي الإجباري",
    enableConfirmLabel: "تفعيل على أي حال",

    auditHistoryTitle: "سجل تعديلات إعدادات الأستاذ العام",
    auditHistoryEmpty: "لا يوجد سجل تعديلات لهذه الإعدادات بعد.",
    auditHistoryLoadError: "تعذّر تحميل سجل التعديلات.",
    auditHistoryWhen: "التاريخ والوقت",
    auditHistoryUser: "بواسطة",
    auditHistorySetting: "الإعداد",
    auditHistoryOldValue: "القيمة السابقة",
    auditHistoryNewValue: "القيمة الجديدة",
    auditHistoryCreated: "تهيئة الإعدادات بالقيم الافتراضية المعتمدة",
    auditHistorySystem: "النظام",
  },
  customerOpeningBalances: {
    title: "الأرصدة الافتتاحية للعملاء",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « الأرصدة الافتتاحية للعملاء",
    purposeNotice:
      "ما كان على العميل — أو له — لحظة فتح الدفاتر، بستة تصنيفات مستقلة. اختر التصنيف من البطاقات ثم أدخل الأرصدة.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن إدخال أو تعديل الأرصدة.",
    preparationNotice:
      "تحضير فقط: الحفظ لا ينشئ قيداً ولا يحرّك أي رصيد. الترحيل خطوة لاحقة منفصلة.",
    types: {
      customerReceivables: "ذمم العملاء",
      unbilledReceivables: "ذمم غير مفوترة",
      advancePayments: "ذمم مدفوعة مقدماً",
      promissoryNotes: "ذمم كمبيالات",
      supplierReceivables: "ذمم الموردين",
      chequesInCollection: "ذمم عملاء - شيكات برسم التحصيل",
    },
    category: "الفئة",
    allCategories: "— كل الفئات —",
    fiscalYear: "السنة",
    currency: "العملة",
    allCurrencies: "— كل العملات —",
    status: "الحالة",
    allStatuses: "— كل الحالات —",
    statusNotEntered: "لم يُدخل",
    statusZero: "صفر",
    statusEntered: "مُدخل",
    statusInvalid: "غير صالح",
    statusLocked: "مقفل",
    customerNumber: "رقم العميل",
    customer: "العميل",
    exchangeRate: "سعر التحويل",
    debit: "مدين",
    credit: "دائن",
    localDebit: "مدين محلي",
    localCredit: "دائن محلي",
    controlAccount: "الحساب المالي",
    noControlAccount: "لا يوجد حساب مالي مرتبط — الحفظ سيُرفض.",
    inactiveCustomer: "عميل غير فعال — رصيده السابق ظاهر ولا يقبل إدخالاً جديداً.",
    search: "بحث",
    searchPlaceholder: "ابحث برقم العميل أو الاسم",
    refresh: "تحديث",
    print: "طباعة",
    pdf: "PDF",
    excel: "Excel",
    save: "حفظ التغييرات",
    cancelChanges: "إلغاء التغييرات",
    resetRow: "تصفير السطر",
    unsavedOne: "سطر واحد غير محفوظ",
    unsavedMany: "{count} سطر غير محفوظ",
    leaveWarning: "لديك تغييرات غير محفوظة. مغادرة الشاشة ستفقدها.",
    totals: "الإجماليات",
    totalDebit: "مجموع المدين",
    totalCredit: "مجموع الدائن",
    totalLocalDebit: "مجموع المدين المحلي",
    totalLocalCredit: "مجموع الدائن المحلي",
    difference: "الفرق",
    balanced: "متوازن",
    entered: "مُدخل",
    zero: "صفر",
    notEntered: "لم يُدخل",
    empty: "لا يوجد عملاء بهذا التصنيف.",
    rows: "السطور",
    saved: "حُفظت الأرصدة",
    nothingToSave: "لا توجد تغييرات للحفظ.",
    loadError: "تعذّر تحميل الأرصدة.",
    saveError: "تعذّر الحفظ.",
  },
  serviceQuotations: {
    title: "عرض أسعار الخدمات",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « عرض أسعار الخدمات",
    purposeNotice: "عرض سعر يُقدَّم للعميل. يُحفظ ويُعدَّل ويُلغى، ولا يُرحَّل.",
    noAccountingNotice:
      "لا أثر محاسبي لهذا المستند إطلاقاً: لا قيد يومية، ولا ذمة على العميل، ولا حركة نقدية، ولا ترحيل ضريبي.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    dependencyNotice:
      "بنود العرض تحتاج أنواع مبيعات خدمات معرَّفة، والضريبة تحتاج نسبة سارية بتاريخ العرض.",
    headerTitle: "بيانات العرض",
    linesTitle: "البنود",
    listTitle: "عروض الأسعار",
    newRecord: "عرض جديد",
    number: "رقم العرض",
    date: "تاريخ العرض",
    status: "حالة السند",
    draft: "غير مرحّل",
    cancelled: "ملغى",
    customer: "العميل",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    paymentMethod: "طريقة الدفع",
    cash: "نقدي",
    credit: "ذمم",
    paymentInstrument: "وسيلة الدفع",
    salesperson: "المندوب",
    costCenter: "مركز التكلفة",
    notes: "ملاحظات",
    service: "الخدمة",
    description: "البيان",
    quantity: "الكمية",
    unitPrice: "سعر الوحدة",
    discountPercent: "الخصم %",
    taxPercent: "الضريبة %",
    gross: "الإجمالي",
    discount: "الخصم",
    tax: "الضريبة",
    net: "الصافي",
    taxable: "الأساس الخاضع",
    pricingMode: "ضريبة السعر",
    taxExclusive: "غير شامل",
    taxInclusive: "شامل",
    exemptCustomer: "العميل معفى من الضريبة، فلا ضريبة على أي بند.",
    addLine: "إضافة بند",
    removeLine: "حذف",
    noLines: "لا توجد بنود. أضف بنداً واحداً على الأقل.",
    subtotal: "الإجمالي قبل الخصم",
    discountTotal: "إجمالي الخصم",
    taxTotal: "إجمالي الضريبة",
    netTotal: "صافي العرض",
    save: "حفظ",
    saveNew: "حفظ + جديد",
    cancelEdit: "إلغاء",
    cancelDoc: "إلغاء العرض",
    cancelConfirm: "إلغاء عرض الأسعار؟ لا يمكن تعديله بعد ذلك.",
    edit: "تعديل",
    refresh: "تحديث",
    search: "بحث",
    noRows: "لا توجد عروض أسعار",
    count: "العدد",
    choose: "— اختر —",
    none: "بدون",
    actions: "إجراءات",
    view: "عرض",
    back: "رجوع",
    saved: "تم الحفظ",
    cancelledOk: "تم إلغاء العرض",
    loadError: "تعذر تحميل عروض الأسعار",
    saveError: "تعذر حفظ العرض",
    taxType: "نوع الضريبة",
    branch: "الفرع",
    fiscalYear: "السنة",
    taxTypeFromService: "— حسب الخدمة —",
    duplicateLine: "تكرار البند",
    discountType: "نوع الخصم",
    discountValue: "قيمة الخصم",
    discountTypeNone: "— بدون نوع —",
    discountAsPercent: "نسبة %",
    discountAsAmount: "مبلغ",
    discountTypeHint:
      "نوع الخصم يحدد كيف تُقرأ القيمة: نسبة أم مبلغ ثابت. بدون نوع، يُستخدم حقل الخصم % كما كان.",
    customerInfoTitle: "معلومات العميل",
    customerInfoEmpty: "اختر عميلاً لعرض بياناته.",
    customerCode: "رمز العميل",
    customerNameLabel: "اسم العميل",
    phone: "الهاتف",
    mobile: "الجوال",
    email: "البريد الإلكتروني",
    taxNumber: "الرقم الضريبي",
    addressLabel: "العنوان",
    customerCategory: "فئة العميل",
    creditLimit: "سقف الائتمان",
    taxTreatment: "المعاملة الضريبية",
    treatmentInclusive: "شامل الضريبة",
    treatmentExclusive: "غير شامل الضريبة",
    treatmentExempt: "معفى",
    attachmentsTitle: "الملفات المرفقة",
    attach: "إرفاق ملف",
    attachHint: "المسموح: PDF، Excel، Word، صور. الحد الأقصى ٢٥ ميجابايت للملف.",
    noAttachments: "لا توجد ملفات مرفقة",
    attachmentSaveFirst: "احفظ العرض أولاً، ثم أرفق الملفات.",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentUploadedBy: "أرفقه",
    attachmentUploadedAt: "تاريخ الإرفاق",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "حذف الملف المرفق؟",
    attachmentUploadFailed: "تعذر رفع الملف",
    attachmentOpenFailed: "تعذر فتح الملف المرفق",
    attachmentUploaded: "تم إرفاق الملف",
    attachmentRemoved: "تم حذف الملف المرفق",
    documentActions: "المستند",
    printAction: "طباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    exportSaveFirst: "احفظ العرض أولاً، فالطباعة تقرأ المستند المحفوظ.",
    exportFailed: "تعذر إخراج المستند",
  },
  serviceSalesInvoices: {
    settlementTitle: "معلومات التسوية",
    settlementInvoiceTotal: "إجمالي الفاتورة",
    settlementSettled: "المبلغ المسدد",
    settlementOutstanding: "الرصيد المستحق",
    settlementPaymentNo: "رقم سند القبض",
    settlementDate: "التاريخ",
    settlementAmount: "المبلغ المسدد",
    settlementStatus: "الحالة",
    settlementAction: "إجراء",
    settlementCancel: "إلغاء التسوية",
    settlementReason: "سبب إلغاء التسوية",
    settlementConfirm: "تأكيد إلغاء التسوية",
    settlementDismiss: "إلغاء",
    settlementCancelled: "تم إلغاء التسوية. الفاتورة وسند القبض ما زالا مرحّلين دون تغيير.",
    settlementNone: "لا توجد تسويات على هذه الفاتورة.",
    settlementActive: "سارية",
    settlementCancelledLabel: "ملغاة",
    settlementNotice: "إلغاء التسوية يفك ربط الدفعة عن الفاتورة فقط: لا يُلغي الفاتورة، ولا يُلغي سند القبض، ولا يُنشئ أو يحذف أي قيد.",
    settlementPaid: "مدفوع",
    settlementPartiallyPaid: "مدفوع جزئياً",
    settlementUnpaid: "غير مدفوع",
    title: "فاتورة مبيعات الخدمات",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « فاتورة مبيعات الخدمات",
    purposeNotice: "فاتورة خدمات تُصدر للعميل. طريقة الدفع تُورَّث من بطاقة العميل ولا تُختار هنا.",
    lifecycleNotice: "دورة المستند: مسودّة ← مدقّقة ← مرحّلة. لا أثر محاسبي إلا عند الترحيل، ولا يجوز لمن أنشأ الفاتورة أن يدقّقها.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    postedNotice: "الفاتورة مرحّلة: القيد في الأستاذ العام ولا تقبل التعديل. التصحيح يكون بعكسها.",
    headerTitle: "بيانات الفاتورة",
    linesTitle: "البنود",
    listTitle: "الفواتير",
    customerInfoTitle: "معلومات العميل",
    customerInfoEmpty: "اختر عميلاً لعرض بياناته.",
    attachmentsTitle: "الملفات المرفقة",
    newRecord: "فاتورة جديدة",
    number: "رقم الفاتورة",
    date: "تاريخ الفاتورة",
    status: "حالة السند",
    statusDraft: "مسودّة",
    statusAudited: "مدقّقة",
    statusPosted: "مرحّلة",
    statusReversed: "معكوسة",
    customer: "العميل",
    paymentMethod: "طريقة الدفع",
    paymentFromCustomer: "— من بطاقة العميل —",
    saleNature: "نوع البيع",
    saleOneTime: "بيع لمرة واحدة",
    saleContract: "عقد",
    sourceQuotation: "عرض السعر المصدر",
    noQuotation: "— بدون عرض سعر —",
    imported: "تم إدراج بيانات عرض السعر {number}",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    branch: "الفرع",
    fiscalYear: "السنة",
    notes: "ملاحظات",
    taxType: "نوع الضريبة",
    taxTypeFromService: "— حسب الخدمة —",
    service: "الخدمة",
    description: "البيان",
    quantity: "الكمية",
    unitPrice: "سعر الوحدة",
    duplicateLine: "تكرار البند",
    discountType: "نوع الخصم",
    discountValue: "قيمة الخصم",
    discountTypeNone: "— بدون نوع —",
    discountPercent: "الخصم %",
    taxPercent: "الضريبة %",
    taxable: "الأساس الخاضع",
    tax: "الضريبة",
    gross: "الإجمالي",
    addLine: "إضافة بند",
    removeLine: "حذف",
    noLines: "لا توجد بنود. أضف بنداً واحداً على الأقل.",
    subtotal: "الإجمالي قبل الخصم",
    discountTotal: "إجمالي الخصم",
    grandTotal: "صافي الفاتورة",
    saveDraft: "حفظ",
    cancelEdit: "إلغاء",
    auditAction: "تدقيق",
    postAction: "ترحيل",
    reverseAction: "عكس القيد",
    reverseConfirm: "عكس الفاتورة المرحّلة؟ يُنشأ قيد عكسي ولا يُحذف التاريخ.",
    reverseReason: "عكس فاتورة مبيعات خدمات",
    printAction: "طباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    exportSaveFirst: "احفظ الفاتورة أولاً، فالطباعة تقرأ المستند المحفوظ.",
    exportFailed: "تعذر إخراج المستند",
    attach: "إرفاق ملف",
    attachHint: "المسموح: PDF، Excel، Word، صور. الحد الأقصى ٢٥ ميجابايت للملف.",
    noAttachments: "لا توجد ملفات مرفقة",
    attachmentSaveFirst: "احفظ الفاتورة أولاً، ثم أرفق الملفات.",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentUploadedBy: "أرفقه",
    attachmentUploadedAt: "تاريخ الإرفاق",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "حذف الملف المرفق؟",
    attachmentUploaded: "تم إرفاق الملف",
    attachmentRemoved: "تم حذف الملف المرفق",
    attachmentUploadFailed: "تعذر رفع الملف",
    attachmentOpenFailed: "تعذر فتح الملف المرفق",
    customerCode: "رمز العميل",
    customerNameLabel: "اسم العميل",
    phone: "الهاتف",
    mobile: "الجوال",
    email: "البريد الإلكتروني",
    taxNumber: "الرقم الضريبي",
    addressLabel: "العنوان",
    creditLimit: "سقف الائتمان",
    edit: "تعديل",
    view: "عرض",
    refresh: "تحديث",
    search: "بحث",
    noRows: "لا توجد فواتير",
    count: "العدد",
    choose: "— اختر —",
    none: "بدون",
    actions: "إجراءات",
    saved: "تم الحفظ",
    audited: "تم التدقيق",
    posted: "تم الترحيل",
    reversed: "تم عكس القيد",
    loadError: "تعذر تحميل الفواتير",
    saveError: "تعذر حفظ الفاتورة",
    actionError: "تعذر تنفيذ الإجراء",
  },
  debitNotes: {
    title: "إشعار مدين",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « إشعار مدين",
    purposeNotice: "مستند تسوية يزيد المبلغ المستحق على العميل. كل بند يحدّد بنفسه الحساب المقابل الذي يُقيَّد عليه الطرف الآخر.",
    lifecycleNotice: "الدورة: مسودّة ← مدقّق ← مرحّل. التدقيق من مستخدم آخر: لا يمكن للمستخدم تدقيق إشعاره بنفسه.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    postedNotice: "الإشعار مرحّل: البيانات للعرض فقط. لا يمكن التعديل إلا بعكس القيد.",
    sourceInvoiceNotice: "الفاتورة المرجعية للتوثيق فقط: لا يتم تعديلها ولا إعادة فتحها ولا تسوية أي مبلغ عليها.",
    headerTitle: "بيانات الإشعار",
    linesTitle: "بنود الإشعار",
    attachmentsTitle: "الملفات المرفقة",
    listTitle: "إشعارات المدين",
    filtersTitle: "تصفية",
    newRecord: "إشعار جديد",
    number: "رقم الإشعار",
    date: "التاريخ",
    status: "الحالة",
    branch: "الفرع",
    createdBy: "أنشأه",
    customer: "العميل",
    customerInfo: "بيانات العميل",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    reference: "رقم المرجع",
    notes: "ملاحظات",
    sourceInvoice: "الفاتورة المرجعية",
    sourceInvoiceNone: "بدون فاتورة مرجعية",
    taxInclusive: "ضريبة المبلغ",
    inclusive: "شامل",
    exclusive: "غير شامل",
    costCenter: "مركز الكلفة",
    lineDescription: "البيان",
    offsetAccount: "الحساب المقابل",
    amount: "المبلغ",
    taxType: "نوع الضريبة",
    taxRate: "النسبة",
    lineNet: "الصافي",
    lineTax: "الضريبة",
    lineTotal: "الإجمالي",
    rowNotes: "بيان إضافي",
    addRow: "إضافة بند",
    duplicateRow: "تكرار",
    removeRow: "حذف",
    totalNet: "إجمالي الصافي",
    totalTax: "إجمالي الضريبة",
    grandTotal: "إجمالي الإشعار",
    actions: "إجراءات",
    choose: "اختر",
    none: "—",
    count: "العدد",
    search: "بحث برقم الإشعار أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد إشعارات مدين",
    edit: "تعديل",
    view: "عرض",
    save: "حفظ",
    cancelEdit: "إلغاء",
    auditAction: "تدقيق",
    postAction: "ترحيل",
    reverseAction: "عكس القيد",
    reverseConfirm: "سيتم عكس إشعار المدين بقيد عكسي، وتُعاد ذمّة العميل بكامل قيمة الإشعار. هل تريد المتابعة؟",
    reverseReason: "عكس إشعار مدين",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "احفظ الإشعار أولاً",
    exportFailed: "تعذر إنشاء الملف",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    allStatuses: "كل الحالات",
    apply: "تطبيق",
    reset: "مسح",
    attachmentAdd: "إضافة مرفق",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "سيتم حذف المرفق نهائياً. هل تريد المتابعة؟",
    attachmentUploaded: "تم رفع المرفق",
    attachmentRemoved: "تم حذف المرفق",
    attachmentUploadFailed: "تعذر رفع المرفق",
    attachmentDraftOnly: "لا يمكن تعديل المرفقات إلا والإشعار مسودّة.",
    attachmentOpenFailed: "تعذر فتح المرفق",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentBy: "رفعه",
    attachmentAt: "تاريخ الرفع",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    noAttachments: "لا توجد مرفقات",
    attachmentPolicy: "الحد الأقصى ٢٥ ميجابايت للملف الواحد.",
    statusDraft: "مسودّة",
    statusAudited: "مدقّق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
    saved: "تم حفظ الإشعار",
    audited: "تم تدقيق الإشعار",
    posted: "تم ترحيل الإشعار",
    reversed: "تم عكس الإشعار",
    loadError: "تعذر تحميل إشعارات المدين",
    saveError: "تعذر حفظ الإشعار",
    actionError: "تعذر تنفيذ الإجراء",
  },

  creditNotes: {
    title: "إشعار دائن",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « إشعار دائن",
    purposeNotice: "مستند تسوية يُنقص المبلغ المستحق على العميل. كل بند يحدّد بنفسه الحساب المقابل الذي يُقيَّد عليه الطرف الآخر.",
    lifecycleNotice: "الدورة: مسودّة ← مدقّق ← مرحّل. التدقيق من مستخدم آخر: لا يمكن للمستخدم تدقيق إشعاره بنفسه.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    postedNotice: "الإشعار مرحّل: البيانات للعرض فقط. لا يمكن التعديل إلا بعكس القيد.",
    sourceInvoiceNotice: "الفاتورة المصدر للتوثيق فقط: لا يتم تعديلها ولا إعادة فتحها ولا تسوية أي مبلغ عليها.",
    headerTitle: "بيانات الإشعار",
    linesTitle: "بنود الإشعار",
    attachmentsTitle: "الملفات المرفقة",
    listTitle: "إشعارات الدائن",
    filtersTitle: "تصفية",
    newRecord: "إشعار جديد",
    number: "رقم الإشعار",
    date: "التاريخ",
    status: "الحالة",
    branch: "الفرع",
    createdBy: "أنشأه",
    customer: "العميل",
    customerInfo: "بيانات العميل",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    reference: "المرجع",
    notes: "ملاحظات",
    sourceInvoice: "الفاتورة المصدر",
    sourceInvoiceNone: "بدون فاتورة مصدر",
    taxInclusive: "ضريبة المبلغ",
    inclusive: "شامل",
    exclusive: "غير شامل",
    costCenter: "مركز التكلفة",
    lineDescription: "الوصف",
    offsetAccount: "الحساب المقابل",
    amount: "المبلغ",
    taxType: "نوع الضريبة",
    taxRate: "النسبة",
    lineNet: "صافي المبلغ",
    lineTax: "الضريبة",
    lineTotal: "الإجمالي",
    rowNotes: "بيان إضافي",
    addRow: "إضافة بند",
    duplicateRow: "تكرار",
    removeRow: "حذف",
    totalNet: "صافي المبلغ",
    totalTax: "إجمالي الضريبة",
    grandTotal: "إجمالي الإشعار",
    actions: "إجراءات",
    choose: "اختر",
    none: "—",
    count: "العدد",
    search: "بحث برقم الإشعار أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد إشعارات دائن",
    edit: "تعديل",
    view: "عرض",
    save: "حفظ",
    cancelEdit: "إلغاء",
    auditAction: "تدقيق",
    postAction: "ترحيل",
    reverseAction: "عكس القيد",
    reverseConfirm: "سيتم عكس إشعار الدائن بقيد عكسي، وتُعاد ذمّة العميل بكامل قيمة الإشعار. هل تريد المتابعة؟",
    reverseReason: "عكس إشعار دائن",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "احفظ الإشعار أولاً",
    exportFailed: "تعذر إنشاء الملف",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    allStatuses: "كل الحالات",
    apply: "تطبيق",
    reset: "مسح",
    attachmentAdd: "إضافة مرفق",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "سيتم حذف المرفق نهائياً. هل تريد المتابعة؟",
    attachmentUploaded: "تم رفع المرفق",
    attachmentRemoved: "تم حذف المرفق",
    attachmentUploadFailed: "تعذر رفع المرفق",
    attachmentDraftOnly: "لا يمكن تعديل المرفقات إلا والإشعار مسودّة.",
    attachmentOpenFailed: "تعذر فتح المرفق",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentBy: "رفعه",
    attachmentAt: "تاريخ الرفع",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    noAttachments: "لا توجد مرفقات",
    attachmentPolicy: "الحد الأقصى ٢٥ ميجابايت للملف الواحد.",
    statusDraft: "مسودّة",
    statusAudited: "مدقّق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
    saved: "تم حفظ الإشعار",
    audited: "تم تدقيق الإشعار",
    posted: "تم ترحيل الإشعار",
    reversed: "تم عكس الإشعار",
    loadError: "تعذر تحميل إشعارات الدائن",
    saveError: "تعذر حفظ الإشعار",
    actionError: "تعذر تنفيذ الإجراء",
  },
  serviceSalesReturns: {
    title: "مردود بيع خدمات",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « مردود بيع خدمات",
    purposeNotice: "إرجاع خدمات سبق بيعها. يبدأ المستند باختيار فاتورة مبيعات خدمات مرحّلة، ثم اختيار البنود والكميات المرتجعة منها.",
    lifecycleNotice: "الدورة: مسودّة ← مدقّق ← مرحّل. الترحيل وحده هو ما يجعل المردود نافذاً ويستهلك الكمية المتاحة للإرجاع.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    postedNotice: "المردود مرحّل: البيانات للعرض فقط. لا يمكن التعديل إلا بعكس القيد.",
    sourceRequiredNotice: "لا يمكن إنشاء مردود بدون فاتورة مبيعات خدمات مرحّلة. البنود تأتي من الفاتورة ولا تُدخل يدوياً.",
    noInventoryNotice: "هذا مردود خدمات: أثره مالي فقط، ولا توجد حركة مخزون.",
    pickInvoiceTitle: "فاتورة مبيعات الخدمات",
    headerTitle: "بيانات المردود",
    linesTitle: "بنود الفاتورة القابلة للإرجاع",
    attachmentsTitle: "الملفات المرفقة",
    listTitle: "مردودات بيع الخدمات",
    filtersTitle: "تصفية",
    newRecord: "مردود جديد",
    number: "رقم المردود",
    date: "التاريخ",
    status: "الحالة",
    branch: "الفرع",
    createdBy: "أنشأه",
    sourceInvoice: "الفاتورة المصدر",
    invoiceNumber: "رقم الفاتورة",
    invoiceDate: "تاريخ الفاتورة",
    customer: "العميل",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    costCenter: "مركز التكلفة",
    reference: "المرجع",
    notes: "ملاحظات",
    changeInvoice: "تغيير الفاتورة",
    chooseInvoice: "اختر فاتورة مرحّلة للإرجاع منها",
    noInvoices: "لا توجد فواتير مرحّلة قابلة للإرجاع",
    invoiceSearch: "بحث برقم الفاتورة",
    fullyReturned: "مرتجعة بالكامل",
    service: "الخدمة",
    lineDescription: "الوصف",
    originalQty: "الكمية الأصلية",
    previouslyReturned: "المرتجع سابقاً",
    availableQty: "المتاح للإرجاع",
    returnQty: "الكمية المرتجعة",
    unitPrice: "سعر الوحدة",
    taxType: "نوع الضريبة",
    taxRate: "النسبة",
    lineNet: "صافي المبلغ",
    lineTax: "الضريبة",
    lineTotal: "الإجمالي",
    nothingSelected: "أدخل كمية مرتجعة في بند واحد على الأقل",
    totalNet: "صافي المبلغ",
    totalTax: "الضريبة",
    grandTotal: "إجمالي المردود",
    actions: "إجراءات",
    choose: "اختر",
    none: "—",
    count: "العدد",
    search: "بحث برقم المردود أو الفاتورة",
    refresh: "تحديث",
    noRows: "لا توجد مردودات",
    edit: "تعديل",
    view: "عرض",
    save: "حفظ",
    cancelEdit: "إلغاء",
    auditAction: "تدقيق",
    postAction: "ترحيل",
    reverseAction: "عكس القيد",
    reverseConfirm: "سيتم عكس المردود بقيد عكسي، وتعود الكمية متاحة للإرجاع مرة أخرى. هل تريد المتابعة؟",
    reverseReason: "عكس مردود بيع خدمات",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "احفظ المردود أولاً",
    exportFailed: "تعذر إنشاء الملف",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    allStatuses: "كل الحالات",
    apply: "تطبيق",
    reset: "مسح",
    attachmentAdd: "إضافة مرفق",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "سيتم حذف المرفق نهائياً. هل تريد المتابعة؟",
    attachmentUploaded: "تم رفع المرفق",
    attachmentRemoved: "تم حذف المرفق",
    attachmentUploadFailed: "تعذر رفع المرفق",
    attachmentDraftOnly: "لا يمكن تعديل المرفقات إلا والمردود مسودّة.",
    attachmentOpenFailed: "تعذر فتح المرفق",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentBy: "رفعه",
    attachmentAt: "تاريخ الرفع",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    noAttachments: "لا توجد مرفقات",
    attachmentPolicy: "الحد الأقصى ٢٥ ميجابايت للملف الواحد.",
    statusDraft: "مسودّة",
    statusAudited: "مدقّق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
    saved: "تم حفظ المردود",
    audited: "تم تدقيق المردود",
    posted: "تم ترحيل المردود",
    reversed: "تم عكس المردود",
    loadError: "تعذر تحميل المردودات",
    saveError: "تعذر حفظ المردود",
    actionError: "تعذر تنفيذ الإجراء",
  },
  receiptVouchers: {
    title: "سند قبض",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « سند القبض",
    purposeNotice: "تسجيل المبالغ المقبوضة فعلياً من العملاء أو على حساب عام. كل سطر قبض يحدّد طريقة القبض والجهة التي وصل إليها المال.",
    lifecycleNotice: "الدورة: مسودّة ← مدقّق ← مرحّل. التدقيق من مستخدم آخر: لا يمكن للمستخدم تدقيق سنده بنفسه.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    postedNotice: "السند مرحّل: البيانات للعرض فقط. لا يمكن التعديل إلا بعكس القيد.",
    tabVouchers: "سندات القبض",
    tabCheques: "متابعة الشيكات",
    headerTitle: "بيانات السند",
    paymentsTitle: "تفاصيل القبض",
    chequesTitle: "الشيكات المقبوضة",
    attachmentsTitle: "الملفات المرفقة",
    listTitle: "سندات القبض",
    newRecord: "سند جديد",
    number: "رقم السند",
    date: "التاريخ",
    status: "الحالة",
    branch: "الفرع",
    createdBy: "أنشأه",
    sourceType: "نوع مصدر القبض",
    sourceCustomer: "عميل",
    sourceAccount: "حساب عام",
    customer: "العميل",
    customerInfo: "بيانات العميل",
    glAccount: "الحساب",
    glAccountHint: "اختر حساباً قابلاً للترحيل",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    referenceNumber: "رقم المرجع",
    totalAmount: "إجمالي المبلغ",
    notes: "ملاحظات",
    method: "طريقة القبض",
    amount: "المبلغ",
    destination: "الصندوق / الحساب البنكي",
    destinationFromMethod: "يتحدّد من طريقة القبض",
    cashbox: "الصندوق",
    bankAccount: "الحساب البنكي",
    bankAccountHint: "حسب طريقة القبض",
    chequeNumber: "رقم الشيك",
    drawerBank: "البنك المسحوب عليه",
    drawerBranch: "فرع البنك",
    chequeDate: "تاريخ الشيك",
    dueDate: "تاريخ الاستحقاق",
    rowNotes: "بيان السطر",
    addRow: "إضافة سطر",
    duplicateRow: "تكرار",
    removeRow: "حذف",
    actions: "إجراءات",
    choose: "اختر",
    none: "—",
    count: "العدد",
    search: "بحث برقم السند أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد سندات قبض",
    edit: "تعديل",
    view: "عرض",
    save: "حفظ",
    cancelEdit: "إلغاء",
    auditAction: "تدقيق",
    postAction: "ترحيل",
    reverseAction: "عكس القيد",
    reverseConfirm: "سيتم عكس سند القبض بالكامل بقيد عكسي. هل تريد المتابعة؟",
    reverseReason: "عكس سند قبض",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "احفظ السند أولاً",
    attachmentAdd: "إضافة مرفق",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "سيتم حذف المرفق نهائياً. هل تريد المتابعة؟",
    attachmentUploaded: "تم رفع المرفق",
    attachmentRemoved: "تم حذف المرفق",
    attachmentUploadFailed: "تعذر رفع المرفق",
    attachmentDraftOnly: "لا يمكن تعديل المرفقات إلا والسند مسودّة.",
    attachmentSize: "الحجم",
    attachmentBy: "رفعه",
    attachmentFile: "الملف",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    attachmentAt: "تاريخ الرفع",
    noAttachments: "لا توجد مرفقات",
    attachmentOpenFailed: "تعذر فتح المرفق",
    attachmentPolicy: "الحد الأقصى ٢٥ ميجابايت للملف الواحد.",
    exportFailed: "تعذر إنشاء الملف",
    statusDraft: "مسودّة",
    statusAudited: "مدقّق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
    chequeUnderCollection: "برسم التحصيل",
    chequeDeposited: "مودع للتحصيل",
    chequeCleared: "محصل",
    chequeBounced: "مرتجع",
    chequeReturned: "مُعاد للعميل",
    saved: "تم حفظ السند",
    audited: "تم تدقيق السند",
    posted: "تم ترحيل السند",
    reversed: "تم عكس السند",
    loadError: "تعذر تحميل سندات القبض",
    saveError: "تعذر حفظ السند",
    actionError: "تعذر تنفيذ الإجراء",
  },
  receiptOrders: {
    title: "أمر القبض",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « أمر القبض",
    purposeNotice: "تعليمات بتحصيل مبلغ من العميل. المبلغ يُطلب هنا، ويُقبض فعلياً عبر سند القبض.",
    noAccountingNotice: "لا أثر محاسبي لأمر القبض إطلاقاً: لا قيد يومية، ولا ذمم، ولا صندوق، ولا بنك، ولا شيكات. الأثر المحاسبي يحدث عند ترحيل سند القبض فقط.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    cancelledNotice: "الأمر ملغى: البيانات للعرض فقط.",
    headerTitle: "بيانات أمر القبض",
    amountsTitle: "المبالغ",
    linkedTitle: "سندات القبض المرتبطة",
    attachmentsTitle: "الملفات المرفقة",
    listTitle: "أوامر القبض",
    filtersTitle: "تصفية",
    newRecord: "أمر جديد",
    number: "رقم أمر القبض",
    date: "التاريخ",
    status: "الحالة",
    branch: "الفرع",
    createdBy: "أنشأه",
    customer: "العميل",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    collector: "المحصل",
    salesRepresentative: "مندوب المبيعات",
    expectedDate: "تاريخ التحصيل المتوقع",
    expectedPaymentMethod: "طريقة القبض المتوقعة",
    reference: "المرجع",
    notes: "ملاحظات",
    cancellationReason: "سبب الإلغاء",
    orderAmount: "المبلغ المطلوب قبضه",
    collectedAmount: "المبلغ المقبوض",
    remainingAmount: "المبلغ المتبقي",
    derivedHint: "المبلغ المقبوض والمتبقي محسوبان من سندات القبض المرحّلة فقط.",
    approveAction: "اعتماد",
    cancelAction: "إلغاء الأمر",
    cancelConfirm: "سيتم إلغاء أمر القبض. هل تريد المتابعة؟",
    cancelReason: "سبب الإلغاء",
    createReceiptAction: "إنشاء سند قبض",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "احفظ الأمر أولاً",
    exportFailed: "تعذر إنشاء الملف",
    receiptNumber: "رقم سند القبض",
    receiptDate: "التاريخ",
    receiptAmount: "المبلغ",
    receiptStatus: "الحالة",
    paymentSummary: "طرق القبض",
    counted: "محتسب",
    notCounted: "غير محتسب",
    noLinked: "لا توجد سندات قبض مرتبطة",
    openReceipt: "فتح",
    statusDraft: "مسودّة",
    statusOpen: "معتمد",
    statusCancelled: "ملغى",
    fullyCollected: "محصّل بالكامل",
    partiallyCollected: "محصّل جزئياً",
    actions: "إجراءات",
    choose: "اختر",
    none: "—",
    count: "العدد",
    search: "بحث برقم الأمر أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد أوامر قبض",
    edit: "تعديل",
    view: "عرض",
    save: "حفظ",
    cancelEdit: "إلغاء",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    allStatuses: "كل الحالات",
    apply: "تطبيق",
    reset: "مسح",
    attachmentAdd: "إضافة مرفق",
    attachmentRemove: "حذف",
    attachmentRemoveConfirm: "سيتم حذف المرفق نهائياً. هل تريد المتابعة؟",
    attachmentUploaded: "تم رفع المرفق",
    attachmentRemoved: "تم حذف المرفق",
    attachmentUploadFailed: "تعذر رفع المرفق",
    attachmentOpenFailed: "تعذر فتح المرفق",
    attachmentCancelledOnly: "لا يمكن تعديل مرفقات أمر ملغى.",
    attachmentFile: "الملف",
    attachmentSize: "الحجم",
    attachmentBy: "رفعه",
    attachmentAt: "تاريخ الرفع",
    attachmentView: "عرض",
    attachmentDownload: "تنزيل",
    noAttachments: "لا توجد مرفقات",
    attachmentPolicy: "الحد الأقصى ٢٥ ميجابايت للملف الواحد.",
    saved: "تم حفظ الأمر",
    approved: "تم اعتماد الأمر",
    cancelled: "تم إلغاء الأمر",
    loadError: "تعذر تحميل أوامر القبض",
    saveError: "تعذر حفظ الأمر",
    actionError: "تعذر تنفيذ الإجراء",
  },
  advanceReceivedRevenues: {
    title: "الإيرادات المقبوضة مقدماً",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « الإيرادات المقبوضة مقدماً",
    purposeNotice: "الاعتراف بإيراد العقود المقبوضة مقدماً على مدى فترة الخدمة، شهراً بشهر.",
    contractRuleNotice: "الاعتراف بالإيراد يقوم على فترة العقد وحدها: تاريخ بداية العقد وتاريخ نهايته. لا تاريخ الفاتورة، ولا تاريخ القبض، ولا تاريخ إنشاء الجدول.",
    collectionNotice: "التحصيل شأن آخر تماماً: سند القبض يحرّك الصندوق وذمة العميل فقط. لو حُصّل العقد بالكامل في 31/03 يبقى الباقي إيراداً مؤجلاً حتى تُقدَّم الخدمة.",
    noSchedulerNotice: "لا يوجد مجدول آلي في النظام: توليد الجدول لا يُنشئ أي قيد، والترحيل يتم بأمر المستخدم لكل فترة مستحقة.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    eligibleTitle: "فواتير العقود القابلة للجدولة",
    schedulesTitle: "جداول الاعتراف بالإيراد",
    scheduleTitle: "الجدول",
    periodsTitle: "فترات الاعتراف",
    recognitionsTitle: "قيود الاعتراف",
    invoice: "الفاتورة المصدر",
    invoiceDate: "تاريخ الفاتورة",
    customer: "العميل",
    currency: "العملة",
    contractStart: "تاريخ بداية العقد",
    contractEnd: "تاريخ نهاية العقد",
    periodCount: "عدد الفترات",
    originalDeferred: "إجمالي الإيراد المؤجل",
    recognized: "الإيراد المعترف به",
    returned: "قيمة المردود",
    remaining: "الإيراد المؤجل المتبقي",
    duePending: "المستحق للترحيل",
    period: "الفترة",
    periodStart: "من",
    periodEnd: "إلى",
    recognitionDate: "تاريخ الاعتراف",
    scheduledAmount: "المبلغ المجدول",
    recognizedAmount: "المعترف به",
    periodStatus: "الحالة",
    recognitionNumber: "رقم القيد",
    fiscalYear: "السنة المالية",
    amount: "المبلغ",
    revenueAccount: "حساب الإيراد",
    service: "الخدمة",
    asOfDate: "حتى تاريخ",
    generate: "توليد الجدول",
    activate: "تفعيل",
    remove: "حذف الجدول",
    recognizeDue: "ترحيل الفترات المستحقة",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "عكس",
    open: "فتح",
    back: "رجوع",
    search: "بحث",
    notEligible: "غير قابلة للجدولة",
    noSchedules: "لا توجد جداول.",
    noEligible: "لا توجد فواتير عقود مرحّلة بلا جدول.",
    noRecognitions: "لا توجد قيود اعتراف.",
    generated: "تم توليد الجدول. لا أثر محاسبي.",
    activated: "تم تفعيل الجدول.",
    removed: "تم حذف الجدول.",
    recognizedToast: "تم إنشاء مسودات الاعتراف. لم يُكتب أي قيد بعد.",
    auditedToast: "تم التدقيق.",
    postedToast: "تم الترحيل إلى دفتر الأستاذ.",
    reversedToast: "تم عكس القيد.",
    confirmRecognize: "سيتم إنشاء مسودة اعتراف لكل فترة مستحقة حتى هذا التاريخ. متابعة؟",
    confirmPost: "سيُكتب هذا القيد في دفتر الأستاذ. متابعة؟",
    confirmReverse: "سيتم عكس هذا الاعتراف بقيد مضاد. متابعة؟",
    statusDraft: "مسودة",
    statusActive: "فعّال",
    statusCompleted: "مكتمل",
    periodPending: "لم تُستحق",
    periodDue: "مستحقة",
    periodDrafted: "مسودة",
    periodRecognized: "معترف به",
    periodReduced: "مخفّضة بمردود",
    entryNotice: "القيد: من ح/ الإيرادات المؤجلة إلى ح/ إيراد الخدمة. بلا ضريبة، وبلا ذمة عميل، وبلا صندوق أو بنك.",
  },
  customerTransactionTransfers: {
    title: "تحويل حركات عميل",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « تحويل حركات عميل",
    purposeNotice: "نقل حركات مالية مرحّلة سابقاً من عميل إلى عميل آخر ضمن فترة تاريخية محددة.",
    historyNotice: "لا يُعدَّل التاريخ المحاسبي إطلاقاً: لا يُغيَّر العميل على الفاتورة أو السند الأصلي، ولا يُعاد توجيه أي قيد مرحّل. يُنشَأ سند إعادة تصنيف جديد، وتبقى المستندات الأصلية كما صدرت.",
    entryNotice: "القيد: من ح/ ذمم العميل الجديد إلى ح/ ذمم العميل القديم. إذا كان الحسابان الوسيطان واحداً يبقى صافي حركة الحساب صفراً وتنتقل الملكية في الأستاذ المساعد.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    selectionTitle: "الحركات القابلة للتحويل",
    transfersTitle: "التحويلات",
    detailTitle: "تفاصيل التحويل",
    transferDate: "تاريخ التحويل",
    oldCustomer: "العميل القديم",
    newCustomer: "العميل الجديد",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    statement: "البيان",
    search: "بحث",
    load: "عرض الحركات",
    select: "اختيار",
    documentType: "نوع المستند",
    documentNumber: "رقم المستند",
    documentDate: "تاريخ المستند",
    reference: "المرجع",
    description: "الوصف",
    currency: "العملة",
    debit: "مدين",
    credit: "دائن",
    netEffect: "صافي أثر العميل",
    currentCustomer: "العميل الحالي",
    movementStatus: "الحالة",
    lastTransfer: "آخر تحويل",
    transferNumber: "رقم التحويل",
    movementCount: "عدد الحركات",
    totalEffect: "إجمالي الأثر",
    sameControlAccount: "نفس الحساب الوسيط",
    differentControlAccounts: "حسابان مختلفان",
    fiscalYear: "السنة المالية",
    status: "الحالة",
    execute: "تنفيذ التحويل",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "عكس",
    open: "فتح",
    back: "رجوع",
    notSelectable: "غير قابلة للتحويل",
    sourceReversed: "الحركة الأصلية عُكست لاحقاً",
    noMovements: "لا توجد حركات قابلة للتحويل لهذا العميل ضمن الفترة.",
    noTransfers: "لا توجد تحويلات.",
    saved: "تم حفظ التحويل كمسودة. لا أثر محاسبي بعد.",
    auditedToast: "تم التدقيق.",
    postedToast: "تم الترحيل. انتقلت ملكية الحركات.",
    reversedToast: "تم عكس التحويل وعادت الملكية.",
    confirmExecute: "سيتم إنشاء مسودة تحويل بالحركات المختارة. متابعة؟",
    confirmPost: "سيُرحَّل القيد وتنتقل ملكية الحركات إلى العميل الجديد. متابعة؟",
    confirmReverse: "سيتم عكس هذا التحويل بقيد مضاد وتعود الملكية. متابعة؟",
    statusDraft: "مسودة",
    statusAudited: "مدقق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
  },
  supplierTransactionTransfers: {
    title: "تحويل حركات مورّد",
    breadcrumb: "المحاسبة العامة « المدفوعات « الحركات اليومية « تحويل حركات مورّد",
    purposeNotice: "نقل حركات مالية مرحّلة سابقاً من مورّد إلى مورّد آخر ضمن فترة تاريخية محددة.",
    historyNotice: "لا يُعدَّل التاريخ المحاسبي إطلاقاً: لا يُغيَّر المورّد على الفاتورة أو السند الأصلي، ولا يُعاد توجيه أي قيد مرحّل. يُنشَأ سند إعادة تصنيف جديد، وتبقى المستندات الأصلية كما صدرت.",
    entryNotice: "القيد للفاتورة غير المسدّدة: من ح/ ذمم المورّد القديم إلى ح/ ذمم المورّد الجديد — فما نَدين به للقديم ينقص وللجديد يزيد. أما الحركة التي كانت مدينة على المورّد القديم (دفعة أو إشعار مدين) فتنتقل بالاتجاه المعاكس. الاتجاه يُقرأ من أثر الحركة نفسها لا من نوع المستند. وإذا كان الحسابان الوسيطان واحداً يبقى صافي حركة الحساب صفراً وتنتقل الملكية في الأستاذ المساعد.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    selectionTitle: "الحركات القابلة للتحويل",
    transfersTitle: "التحويلات",
    detailTitle: "تفاصيل التحويل",
    transferDate: "تاريخ التحويل",
    oldSupplier: "المورّد القديم",
    newSupplier: "المورّد الجديد",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    statement: "البيان",
    search: "بحث",
    load: "عرض الحركات",
    select: "اختيار",
    documentType: "نوع المستند",
    documentNumber: "رقم المستند",
    documentDate: "تاريخ المستند",
    reference: "المرجع",
    description: "الوصف",
    currency: "العملة",
    debit: "مدين",
    credit: "دائن",
    netEffect: "صافي أثر المورّد",
    currentSupplier: "المورّد الحالي",
    movementStatus: "الحالة",
    lastTransfer: "آخر تحويل",
    transferNumber: "رقم التحويل",
    movementCount: "عدد الحركات",
    totalEffect: "إجمالي الأثر",
    sameControlAccount: "نفس الحساب الوسيط",
    differentControlAccounts: "حسابان مختلفان",
    fiscalYear: "السنة المالية",
    status: "الحالة",
    execute: "تنفيذ التحويل",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "عكس",
    open: "فتح",
    back: "رجوع",
    notSelectable: "غير قابلة للتحويل",
    sourceReversed: "الحركة الأصلية عُكست لاحقاً",
    noMovements: "لا توجد حركات قابلة للتحويل لهذا المورّد ضمن الفترة.",
    noTransfers: "لا توجد تحويلات.",
    saved: "تم حفظ التحويل كمسودة. لا أثر محاسبي بعد.",
    auditedToast: "تم التدقيق.",
    postedToast: "تم الترحيل. انتقلت ملكية الحركات.",
    reversedToast: "تم عكس التحويل وعادت الملكية.",
    confirmExecute: "سيتم إنشاء مسودة تحويل بالحركات المختارة. متابعة؟",
    confirmPost: "سيُرحَّل القيد وتنتقل ملكية الحركات إلى المورّد الجديد. متابعة؟",
    confirmReverse: "سيتم عكس هذا التحويل بقيد مضاد وتعود الملكية. متابعة؟",
    statusDraft: "مسودة",
    statusAudited: "مدقق",
    statusPosted: "مرحّل",
    statusReversed: "معكوس",
  },
  batchPrint: {
    select: "تحديد",
    selectAll: "تحديد الكل",
    clear: "إلغاء التحديد",
    selected: "المحدد",
    printSelected: "طباعة المحدد",
    nothingSelected: "اختر مستنداً واحداً على الأقل.",
    preparing: "جارٍ تحضير الطباعة…",
    failed: "تعذّرت الطباعة.",
    hint: "يُطبع كل مستند كاملاً بقالبه الأصلي، وكل مستند يبدأ في صفحة جديدة.",
  },
  insuranceCompanies: {
    title: "شركات التأمين",
    breadcrumb: "المحاسبة العامة « الأصول الثابتة « البيانات الأساسية « شركات التأمين",
    code: "الرمز",
    name: "الاسم",
    phone: "الهاتف",
    mobile: "الخلوي",
    email: "البريد الإلكتروني",
    address: "العنوان",
    notes: "ملاحظات",
    status: "الحالة",
    active: "فعّالة",
    inactive: "غير فعّالة",
    policies: "عدد الوثائق",
    openCard: "بطاقة الطرف",
    noRows: "لا توجد شركات تأمين.",
    total: "الإجمالي",
    search: "بحث",
    searchHint: "الرمز أو الاسم",
    refresh: "تحديث",
    sectorHint: "تُدرَج الشركة هنا عندما تكون طبيعة عملها «قطاع التأمين»، أو عندما تكون مؤمِّناً على وثيقة قائمة.",
  },
  cancelInvoiceSettlementScreen: {
    noParties: "لا توجد أطراف متاحة لك.",
    party: "الطرف",
    customer: "العميل",
    supplier: "المورّد",
    choose: "اختر",
    settlements: "التسويات",
    settlementNo: "رقم المطابقة",
    settledDate: "تاريخ المطابقة",
    invoice: "المستند المستحق",
    creditDocument: "مستند الدفع",
    amount: "المبلغ المطبَّق",
    currency: "العملة",
    state: "الحالة",
    active: "سارية",
    cancelled: "ملغاة",
    cancelAction: "إلغاء التسوية",
    reasonPrompt: "سبب إلغاء التسوية",
    reasonRequired: "سبب الإلغاء مطلوب.",
    confirm: "سيعود المستند مستحقاً بكامل المبلغ المطبَّق. هل تريد المتابعة؟",
    cancelled_ok: "أُلغيت التسوية، وعاد المستند مستحقاً.",
    noRows: "لا توجد تسويات لهذا الطرف.",
    pickParty: "اختر الطرف لعرض تسوياته.",
    cancelledBy: "أُلغيت بواسطة",
    reason: "السبب",
    refresh: "تحديث",
    title: "إلغاء تسوية فاتورة",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « إلغاء تسوية فاتورة",
    scaffoldNotice: "هذه الشاشة معرّفة فقط في هذه المرحلة. لم تُنفَّذ بعد أي وظيفة أعمال.",
  },
  accountReconciliations: {
    selectColumn: "اختيار",
    ctsTitle: "تسوية حركات العميل",
    ctsBreadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « تسوية حركات العميل",
    ctsNotice: "تسوية بين حركات العميل المدينة والدائنة المرحّلة. لا تُنشئ قيداً ولا حركة على دفتر الأستاذ.",
    ctsBalanceNotice: "لا يمكن الحفظ إلا إذا تساوى إجمالي التسوية المدين مع إجمالي التسوية الدائن بالعملة المحلية.",
    sequence: "التسلسل", branch: "الفرع", year: "السنة", date: "التاريخ",
    user: "المستخدم", notesLabel: "ملاحظات",
    upToDate: "لغاية التاريخ", category: "الفئة", inquiry: "استعلام",
    saveAction: "حفظ", cancelAction: "إلغاء",
    debitSection: "تفاصيل الحركات المدينة", creditSection: "تفاصيل الحركات الدائنة",
    txnNumber: "رقم الحركة", txnDate: "تاريخ الحركة", txnType: "نوع الحركة", txnBranch: "فرع الحركة",
    debitCurrencyCol: "مدين عملة", creditCurrencyCol: "دائن عملة", rate: "سعر التحويل",
    prevSettleCurrency: "قيمة التسويات السابقة عملة", prevSettleLocal: "قيمة التسويات السابقة محلي",
    thisSettleCurrency: "قيمة التسوية عملة", thisSettleLocal: "قيمة التسوية محلي",
    remaining: "المتبقي",
    totalCurrency: "الإجمالي من التسوية عملة", totalLocal: "الإجمالي من التسوية محلي",
    balanced: "متوازن", unbalanced: "غير متوازن",
    savedToast: "تم حفظ التسوية. لم يُنشأ أي قيد.",
    pickCustomerFirst: "اختر العميل ثم اضغط «استعلام».",
    customerTitle: "تسوية العميل",
    customerBreadcrumb: "المحاسبة العامة « المقبوضات « تسوية العميل",
    showSettled: "إظهار المسدَّدة بالكامل",
    noDueDateNotice: "لا يوجد في هذا النظام مفهوم تاريخ استحقاق أو شروط سداد على الفاتورة، لذلك يبقى عمود «تاريخ الاستحقاق» فارغاً.",
    settlementAmount: "مبلغ التسوية",
    settle: "تسوية",
    title: "مطابقة الحساب",
    breadcrumb: "المحاسبة العامة « مطابقة الحساب",
    purposeNotice: "مطابقة المستندات المستحقة على الطرف مع دفعاته وإشعاراته الدائنة.",
    noAccountingNotice: "لا أثر محاسبي: الطرفان مرحّلان أصلاً في دفتر الأستاذ. المطابقة تسجّل علاقة تسوية فقط — بلا سند قيد، وبلا حركة على الأرصدة، وبلا تعديل على أي مستند مرحّل.",
    cancelNotice: "إلغاء التسوية لا يُلغي الفاتورة. الفاتورة وسند القبض وقيودهما تبقى مرحّلة كما هي، ويعود المستند مستحقاً وتعود الدفعة متاحة.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    partyType: "نوع الطرف", customer: "العميل", supplier: "المورد", party: "الطرف",
    currency: "العملة", fromDate: "من تاريخ", toDate: "إلى تاريخ", status: "الحالة",
    statusAll: "الكل", statusUnreconciled: "غير مطابق", statusPartial: "مطابق جزئياً",
    statusFull: "مطابق بالكامل",
    load: "عرض", outstandingTitle: "المستندات المستحقة", creditsTitle: "الدفعات المتاحة",
    documentType: "نوع المستند", documentNumber: "رقم المستند", documentDate: "تاريخ المستند",
    dueDate: "تاريخ الاستحقاق",
    originalAmount: "القيمة الأصلية", settledAmount: "القيمة المسددة",
    outstandingAmount: "الرصيد المستحق", allocatedAmount: "المخصص سابقاً",
    availableAmount: "المبلغ المتاح", allocate: "المبلغ المطبق",
    reconcile: "مطابقة", reconciled: "تمت المطابقة", notes: "ملاحظات",
    totalOutstanding: "إجمالي المستحق", totalAvailable: "إجمالي المتاح",
    totalAllocating: "إجمالي التخصيص",
    historyTitle: "سجل المطابقات", action: "الإجراء",
    actionReconciled: "تمت المطابقة", actionCancelled: "تم إلغاء التسوية",
    cancelSettlement: "إلغاء التسوية", cancellationReason: "سبب إلغاء التسوية",
    cancelConfirm: "سيتم إلغاء هذه التسوية فقط. الفاتورة والسند يبقيان مرحّلين دون تغيير. متابعة؟",
    cancelled: "تم إلغاء التسوية.",
    invoiceTotal: "إجمالي الفاتورة", paymentInfoTitle: "معلومات السداد",
    paymentNo: "رقم الدفعة", paymentDate: "تاريخ الدفعة",
    paid: "مدفوع", partiallyPaid: "مدفوع جزئياً", unpaid: "غير مدفوع",
    by: "بواسطة", at: "التاريخ", reason: "السبب",
    noRows: "لا توجد مستندات مطابقة للتصفية.", noHistory: "لا يوجد سجل مطابقات.",
    pickParty: "اختر الطرف ثم اضغط «عرض».", save: "حفظ", close: "إغلاق",
  },
  customerBalanceReminders: {
    title: "تذكير بأرصدة العملاء",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « تذكير بأرصدة العملاء",
    purposeNotice: "إرسال تذكير للعملاء بأرصدتهم المستحقة. الأرصدة تُقرأ من دفتر الأستاذ المساعد ولا تتغير من هذه الشاشة.",
    noAccountingNotice: "لا أثر محاسبي لهذه الشاشة إطلاقاً: لا قيد يومية، ولا حركة ذمم، ولا سند قبض، ولا أمر قبض. إرسال التذكير لا يغيّر رصيد العميل.",
    transportNotice: "ملاحظة: في هذه النسخة يتم تسليم الرسالة إلى خدمة الإشعارات وتُسجّل في سجل النظام، ولا يوجد مزوّد بريد خارجي مُهيّأ بعد. لا توجد قناة رسائل نصية في النظام.",
    localModeNotice: "تمت المعالجة في وضع الإشعارات المحلي — لم يتم إرسال بريد إلكتروني فعلياً.",
    localModeBadge: "وضع الإشعارات المحلي",
    print: "طباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    exportNotice: "التصدير معلوماتي فقط: لا يُرسل تذكيراً ولا يُنشئ قيداً، وليس كشف حساب.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    filtersTitle: "تصفية",
    customersTitle: "العملاء",
    reminderTitle: "الرسالة",
    summaryTitle: "الملخص",
    historyTitle: "سجل التذكيرات",
    search: "بحث",
    customer: "العميل",
    currency: "العملة",
    collector: "المحصل",
    salesRepresentative: "مندوب المبيعات",
    category: "فئة العميل",
    positiveOnly: "الأرصدة المدينة فقط",
    minimumBalance: "الحد الأدنى للرصيد",
    apply: "تطبيق",
    reset: "مسح",
    select: "اختيار",
    selectAll: "تحديد الكل",
    code: "رقم العميل",
    name: "اسم العميل",
    email: "البريد الإلكتروني",
    mobile: "رقم الهاتف",
    balance: "الرصيد",
    lastReminder: "آخر تذكير",
    status: "الحالة",
    noEmail: "لا يوجد بريد إلكتروني",
    noRows: "لا يوجد عملاء مطابقون",
    count: "العدد",
    channel: "قناة الإرسال",
    channelEmail: "البريد الإلكتروني",
    template: "نموذج الرسالة",
    subject: "موضوع الرسالة",
    message: "نص الرسالة",
    preview: "معاينة",
    previewTitle: "معاينة الرسالة",
    previewFor: "معاينة لـ",
    placeholders: "المتغيرات المتاحة",
    loadTemplate: "تحميل النموذج المعتمد",
    selectedCount: "عدد العملاء المحددين",
    selectedBalance: "إجمالي الأرصدة المحددة",
    sendAction: "إرسال التذكير",
    sendConfirm: "سيتم إرسال تذكير إلى عدد {count} من العملاء. هل تريد المتابعة؟",
    sending: "جارٍ الإرسال…",
    batchNumber: "رقم التذكير",
    batchDate: "التاريخ",
    recipients: "المستلمون",
    sent: "تمت المعالجة",
    skipped: "تم التخطي",
    failed: "فشل",
    totalBalance: "إجمالي الأرصدة",
    createdBy: "نفّذه",
    createdAt: "وقت التنفيذ",
    statusPrepared: "مُعدّ",
    statusCompleted: "مكتمل",
    statusCompletedWithErrors: "مكتمل مع استثناءات",
    statusFailed: "فشل",
    deliverySent: "تمت المعالجة محلياً",
    deliverySkipped: "تم التخطي",
    deliveryFailed: "فشل",
    reason: "السبب",
    destination: "الوجهة",
    snapshot: "الرصيد وقت الإرسال",
    viewBatch: "عرض",
    noBatches: "لا توجد تذكيرات سابقة",
    back: "رجوع",
    refresh: "تحديث",
    none: "—",
    actions: "إجراءات",
    sendSuccess: "تم تنفيذ التذكير",
    loadError: "تعذر تحميل البيانات",
    sendError: "تعذر إرسال التذكير",
    previewError: "تعذر إنشاء المعاينة",
  },
  supplierBalanceReminders: {
    title: "تذكير بأرصدة المورّدين",
    breadcrumb: "المحاسبة العامة « المدفوعات « الحركات اليومية « تذكير بأرصدة المورّدين",
    purposeNotice: "مصادقة أرصدة: إرسال ما تُظهره دفاترنا من مبالغ مستحقة للمورّد ليؤكّدها. الرصيد يُقرأ من الأستاذ المساعد ويُعرض من جهة الشركة — الموجب يعني أن الشركة مدينة للمورّد — ولا يتغيّر من هذه الشاشة.",
    noAccountingNotice: "لا أثر محاسبي لهذه الشاشة إطلاقاً: لا قيد يومية، ولا حركة ذمم، ولا سند صرف، ولا أمر صرف. إرسال التذكير لا يغيّر رصيد المورّد.",
    transportNotice: "ملاحظة: في هذه النسخة يتم تسليم الرسالة إلى خدمة الإشعارات وتُسجّل في سجل النظام، ولا يوجد مزوّد بريد خارجي مُهيّأ بعد. لا توجد قناة رسائل نصية في النظام.",
    localModeNotice: "تمت المعالجة في وضع الإشعارات المحلي — لم يتم إرسال بريد إلكتروني فعلياً.",
    localModeBadge: "وضع الإشعارات المحلي",
    print: "طباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    exportNotice: "التصدير معلوماتي فقط: لا يُرسل تذكيراً ولا يُنشئ قيداً، وليس كشف حساب.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    filtersTitle: "تصفية",
    suppliersTitle: "المورّدين",
    reminderTitle: "الرسالة",
    summaryTitle: "الملخص",
    historyTitle: "سجل التذكيرات",
    search: "بحث",
    supplier: "المورّد",
    currency: "العملة",
    collector: "المحصل",
    salesRepresentative: "مندوب المبيعات",
    category: "فئة المورّد",
    positiveOnly: "المورّدون المستحق لهم فقط",
    minimumBalance: "الحد الأدنى للرصيد",
    apply: "تطبيق",
    reset: "مسح",
    select: "اختيار",
    selectAll: "تحديد الكل",
    code: "رقم المورّد",
    name: "اسم المورّد",
    email: "البريد الإلكتروني",
    mobile: "رقم الهاتف",
    balance: "الرصيد",
    lastReminder: "آخر تذكير",
    status: "الحالة",
    noEmail: "لا يوجد بريد إلكتروني",
    noRows: "لا يوجد مورّدين مطابقون",
    count: "العدد",
    channel: "قناة الإرسال",
    channelEmail: "البريد الإلكتروني",
    template: "نموذج الرسالة",
    subject: "موضوع الرسالة",
    message: "نص الرسالة",
    preview: "معاينة",
    previewTitle: "معاينة الرسالة",
    previewFor: "معاينة لـ",
    placeholders: "المتغيرات المتاحة",
    loadTemplate: "تحميل النموذج المعتمد",
    selectedCount: "عدد المورّدين المحددين",
    selectedBalance: "إجمالي الأرصدة المحددة",
    sendAction: "إرسال التذكير",
    sendConfirm: "سيتم إرسال تذكير إلى عدد {count} من المورّدين. هل تريد المتابعة؟",
    sending: "جارٍ الإرسال…",
    batchNumber: "رقم التذكير",
    batchDate: "التاريخ",
    recipients: "المستلمون",
    sent: "تمت المعالجة",
    skipped: "تم التخطي",
    failed: "فشل",
    totalBalance: "إجمالي الأرصدة",
    createdBy: "نفّذه",
    createdAt: "وقت التنفيذ",
    statusPrepared: "مُعدّ",
    statusCompleted: "مكتمل",
    statusCompletedWithErrors: "مكتمل مع استثناءات",
    statusFailed: "فشل",
    deliverySent: "تمت المعالجة محلياً",
    deliverySkipped: "تم التخطي",
    deliveryFailed: "فشل",
    reason: "السبب",
    destination: "الوجهة",
    snapshot: "الرصيد وقت الإرسال",
    viewBatch: "عرض",
    noBatches: "لا توجد تذكيرات سابقة",
    back: "رجوع",
    refresh: "تحديث",
    none: "—",
    actions: "إجراءات",
    sendSuccess: "تم تنفيذ التذكير",
    loadError: "تعذر تحميل البيانات",
    sendError: "تعذر إرسال التذكير",
    previewError: "تعذر إنشاء المعاينة",
  },
  prepaidExpenses: {
    title: "المصروفات المدفوعة مقدماً",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "أصل دُفع مقدماً يتحوّل إلى مصروف على فترات: تأمين سنوي، إيجار، رخصة. الجدول يُبنى على بند من فاتورة مشتريات مرحّلة، ويحدّد متى يصبح الأصل تكلفة.",
    postingNotice: "إنشاء الجدول لا يُنشئ أي قيد. الاستهلاك الدوري فقط هو ما يُرحَّل: من ح/ المصروف، إلى ح/ المدفوع مقدماً — سند مستقل لكل فترة بتاريخ نهايتها. الفترات المستقبلية لا تُستهلك، والتصحيح بقيد عكسي لا بالحذف.",
    noPayableNotice: "لا يُحمَّل المورّد مرة ثانية: فاتورة المشتريات هي التي أنشأت الذمة، ولا يمسّ الاستهلاك رصيد المورّد إطلاقاً.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope: "لا يوجد مورّد ضمن صلاحيتك للحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← صلاحية المستخدمين على المورّدين.",
    listTitle: "الجداول",
    formTitle: "بيانات الجدول",
    periodsTitle: "الفترات",
    newRecord: "جدول جديد",
    search: "بحث",
    searchHint: "رقم الجدول أو الفاتورة أو المورّد",
    refresh: "تحديث",
    noRows: "لا توجد جداول.",
    stateFilter: "الحالة",
    allStates: "كل الحالات",
    totalCount: "العدد",
    totalOriginal: "إجمالي الأصلي",
    totalRecognized: "إجمالي المستهلك",
    number: "رقم الجدول",
    date: "التاريخ",
    supplier: "المورّد",
    sourceInvoice: "فاتورة المصدر",
    chooseInvoice: "اختر فاتورة مشتريات مرحّلة",
    sourceLine: "بند المصدر",
    chooseLine: "اختر البند",
    lineNet: "صافي البند",
    available: "المتاح للجدولة",
    alreadyScheduled: "مجدول سابقاً",
    returned: "مردود",
    amount: "المبلغ المجدول",
    prepaidAccount: "حساب المدفوع مقدماً",
    expenseAccount: "حساب المصروف",
    chooseAccount: "اختر الحساب",
    costCenter: "مركز التكلفة",
    none: "بدون",
    coverageStart: "بداية التغطية",
    coverageEnd: "نهاية التغطية",
    basis: "أساس الاستهلاك",
    basisMonthly: "شهري",
    periodCount: "عدد الفترات",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    notes: "ملاحظات",
    original: "المبلغ الأصلي",
    recognized: "المستهلك",
    remaining: "المتبقي",
    state: "الحالة",
    draft: "مسودة",
    active: "نشط",
    partiallyRecognized: "مستهلك جزئياً",
    fullyRecognized: "مستهلك بالكامل",
    cancelled: "ملغى",
    periodNumber: "الفترة",
    periodFrom: "من",
    periodTo: "إلى",
    recognitionDate: "تاريخ الاستهلاك",
    scheduled: "المجدول",
    periodRecognized: "المستهلك",
    periodRemaining: "المتبقي",
    voucher: "السند",
    periodState: "الحالة",
    due: "مستحق",
    notDue: "غير مستحق",
    reversedLabel: "معكوس",
    save: "حفظ",
    cancelEdit: "إلغاء",
    edit: "تعديل",
    view: "عرض",
    activate: "تفعيل",
    recognize: "استهلاك الفترات المستحقة",
    recognizeOne: "استهلاك",
    reverse: "قيد عكسي",
    cancelSchedule: "إلغاء الجدول",
    cancelReason: "سبب الإلغاء",
    confirmCancel: "تأكيد الإلغاء",
    reversalDate: "تاريخ القيد العكسي",
    reversalReason: "سبب القيد العكسي",
    confirmReverse: "تأكيد العكس",
    close: "إغلاق",
    details: "تفاصيل الجدول",
    createdBy: "أنشأه",
    createdAt: "تاريخ الإنشاء",
    cancelledBy: "ألغاه",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "تم حفظ الجدول.",
    activatedOk: "تم تفعيل الجدول.",
    recognizedOk: "تم استهلاك الفترات المستحقة.",
    reversedOk: "تم إنشاء القيد العكسي.",
    cancelledOk: "تم إلغاء الجدول.",
    skippedLabel: "فترات لم تُستهلك",
    loadError: "تعذّر تحميل الجداول.",
    saveError: "تعذّر حفظ الجدول.",
    activateError: "تعذّر تفعيل الجدول.",
    recognizeError: "تعذّر استهلاك الفترات.",
    reverseError: "تعذّر إنشاء القيد العكسي.",
    cancelError: "تعذّر إلغاء الجدول.",
    exportFailed: "تعذّر إنشاء الملف.",
  },
  supplierMaster: {
    infoTitle: "تعديل بيانات المورد",
    infoBreadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    infoPurpose: "تعديل مُدقَّق لبيانات المورّد: القيمة الحالية، القيمة المقترحة، السبب، ومَن عدّل ومتى.",
    infoNoAccounting: "لا أثر محاسبي: لا قيد يومية، ولا حركة ذمم. ولا يُعاد كتابة أي مستند سابق — الفاتورة الصادرة العام الماضي تحتفظ بالعنوان الذي صدرت به.",
    infoDelegation: "التحقق كله من البطاقة الأصلية للمورّد: تكرار الرقم الضريبي، وحساب الذمم الذي يجب أن يكون قابلاً للترحيل، وغيرها. هذه الشاشة لا تملك قواعد خاصة بها. وسقف الائتمان لا يُعدَّل من هنا إطلاقاً — له شاشته وصلاحيته.",
    limitTitle: "تعديل سقف ائتمان المورد",
    limitBreadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    limitPurpose: "تعديل سقف الائتمان مع سجل دائم لكل تعديل: القيمة السابقة، الجديدة، الفرق، السبب، ومَن عدّل.",
    limitNoAccounting: "لا أثر محاسبي، ولا تقييم ائتماني، ولا حجب شراء. الشاشة تسجّل سقفاً ومَن حدّده فقط.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope: "لا يوجد مورّد ضمن صلاحيتك للحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← صلاحية المستخدمين على المورّدين.",
    supplier: "المورّد",
    chooseSupplier: "اختر المورّد",
    currentValue: "القيمة الحالية",
    proposedValue: "القيمة المقترحة",
    reason: "السبب",
    apply: "تطبيق التعديل",
    refresh: "تحديث",
    noChanges: "لم تُدخل أي قيمة جديدة.",
    historyTitle: "سجل التعديلات",
    field: "الحقل",
    oldValue: "قبل",
    newValue: "بعد",
    changedBy: "بواسطة",
    changedAt: "التاريخ",
    noHistory: "لا توجد تعديلات مسجّلة.",
    name: "الاسم",
    nameAr: "الاسم بالعربية",
    startDate: "تاريخ بداية التعامل",
    controlAccount: "حساب الذمم",
    currency: "العملة",
    taxTreatment: "المعاملة الضريبية",
    paymentTerm: "طريقة الدفع",
    phone: "الهاتف",
    mobile: "الجوال",
    fax: "الفاكس",
    email: "البريد الإلكتروني",
    website: "الموقع",
    contactPerson: "الشخص المسؤول",
    taxNumber: "الرقم الضريبي",
    commercialRegistration: "السجل التجاري",
    nationalIdCompany: "الرقم الوطني للمنشأة",
    referenceNumber: "رقم المرجع",
    country: "الدولة",
    city: "المدينة",
    area: "المنطقة",
    street: "الشارع",
    building: "المبنى",
    postalCode: "الرمز البريدي",
    poBox: "صندوق البريد",
    address: "العنوان",
    addressNotes: "ملاحظات العنوان",
    notes: "ملاحظات",
    isActive: "فعّال",
    isBlacklisted: "قائمة سوداء",
    blacklistReason: "سبب القائمة السوداء",
    creditLimitReadOnly: "سقف الائتمان (يُعدَّل من شاشته الخاصة)",
    currentLimit: "السقف الحالي",
    newLimit: "السقف الجديد",
    changeAmount: "الفرق",
    balance: "الرصيد",
    balanceExceeds: "الرصيد يتجاوز السقف",
    changeLimit: "تعديل السقف",
    limitHistory: "سجل تعديلات السقف",
    previousLimit: "السقف السابق",
    keep: "بدون تغيير",
    clear: "تفريغ",
    unchanged: "بدون تغيير",
    savedFields: "حقل تم تعديله",
    limitChanged: "تم تعديل سقف الائتمان.",
    loadError: "تعذّر تحميل بيانات المورّد.",
    saveError: "تعذّر تطبيق التعديل.",
    limitError: "تعذّر تعديل سقف الائتمان.",
  },
  cheques: {
    title: "الشيكات المقبوضة",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « الشيكات المقبوضة",
    purposeNotice: "متابعة الشيكات الواردة من سندات القبض من لحظة استلامها حتى تحصيلها أو رجوعها.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    noActionNotice: "لا يوجد إجراء مالي متاح على هذا الشيك.",
    filtersTitle: "تصفية",
    listTitle: "الشيكات",
    detailTitle: "بيانات الشيك",
    chequeNumber: "رقم الشيك",
    drawer: "العميل / الساحب",
    drawerBank: "البنك المسحوب عليه",
    bankBranch: "فرع البنك",
    chequeDate: "تاريخ الشيك",
    dueDate: "تاريخ الاستحقاق",
    dueFrom: "استحقاق من",
    dueTo: "استحقاق إلى",
    amount: "المبلغ",
    currency: "العملة",
    cashbox: "الصندوق",
    status: "الحالة",
    sourceVoucher: "سند القبض المصدر",
    clearedTo: "حُصّل إلى",
    clearedDate: "تاريخ التحصيل",
    settlementVoucher: "قيد التسوية",
    notes: "ملاحظات",
    allStatuses: "كل الحالات",
    statusUnderCollection: "برسم التحصيل",
    statusDeposited: "مودع للتحصيل",
    statusCleared: "محصل",
    statusBounced: "مرتجع",
    statusReturned: "مُعاد للعميل",
    depositAction: "إيداع للتحصيل",
    clearAction: "تحصيل",
    bounceAction: "ترجيع الشيك",
    returnAction: "إعادة الشيك للعميل",
    depositTitle: "إيداع الشيك للتحصيل",
    clearTitle: "تحصيل الشيك",
    bounceTitle: "ترجيع الشيك",
    returnTitle: "إعادة الشيك للعميل",
    depositConfirm: "سيتم تسجيل إيداع الشيك لدى البنك للتحصيل. إجراء تشغيلي فقط: لا يُنشأ أي قيد محاسبي لأن قيمة الشيك ما زالت في حساب الشيكات برسم التحصيل.",
    clearConfirm: "سيتم تحصيل الشيك وإدخال قيمته إلى الحساب البنكي المحدّد، بقيد: من ح/ البنك إلى ح/ الشيكات برسم التحصيل.",
    bounceConfirm: "رجع الشيك من البنك دون تحصيل. ستتم إعادة ذمّة العميل بقيمة هذا الشيك فقط. لن يُعكس سند القبض.",
    returnConfirm: "سيتم إعادة الشيك للعميل دون تحصيله. ستتم إعادة ذمّة العميل بقيمة هذا الشيك فقط. الإجراء نهائي ولا يمكن التراجع عنه.",
    bankAccount: "الحساب البنكي",
    chooseBankAccount: "اختر الحساب البنكي المستلم",
    clearedOn: "تاريخ التحصيل",
    actionNotes: "ملاحظات",
    confirm: "تأكيد",
    cancel: "إلغاء",
    deposited: "تم إيداع الشيك للتحصيل",
    cleared: "تم تحصيل الشيك",
    bounced: "تم ترجيع الشيك وإعادة ذمّة العميل",
    returned: "تمت إعادة الشيك للعميل وإعادة ذمّته",
    apply: "تطبيق",
    reset: "مسح",
    refresh: "تحديث",
    search: "رقم الشيك",
    noRows: "لا توجد شيكات",
    count: "العدد",
    choose: "الكل",
    none: "—",
    actions: "إجراءات",
    view: "عرض",
    close: "إغلاق",
    loadError: "تعذر تحميل الشيكات",
    actionError: "تعذر تنفيذ الإجراء",
  },
  receiptBooks: {
    title: "دفاتر القبض",
    breadcrumb: "المحاسبة العامة « المقبوضات « الحركات اليومية « دفاتر القبض",
    purposeNotice: "ضبط دفاتر سندات القبض الورقية: استلامها، تسليمها لموظف، واستعادتها.",
    noAccountingNotice: "لا أثر محاسبي لهذه الشاشة إطلاقاً: لا قيد يومية، ولا حركة نقدية، ولا ذمم. المال يأتي مع سند القبض وهو مستند آخر.",
    readOnlyNotice: "لديك صلاحية العرض فقط.",
    closedNotice: "الدفتر مغلق ولا يقبل التسليم.",
    noHoldersNotice: "لا يوجد ملف رئيسي لهذه الجهة في النظام، فلا توجد أسماء لعرضها. التسليم متاح للموظفين فقط حالياً.",
    bookTitle: "بيانات الدفتر",
    handoverTitle: "التسليم والاستلام",
    historyTitle: "حركات الدفتر",
    listTitle: "الدفاتر",
    newBook: "دفتر جديد",
    bookNumber: "رقم الدفتر",
    fromNumber: "من رقم",
    toNumber: "إلى رقم",
    receiptCount: "عدد السندات",
    receivedDate: "تاريخ الاستلام",
    status: "الحالة",
    statusAvailable: "متاح",
    statusAssigned: "مُسلَّم",
    statusClosed: "مغلق",
    allStatuses: "— كل الحالات —",
    currentHolder: "المستلم الحالي",
    holderType: "نوع المستلم",
    holder: "المستلم",
    holderEmployee: "موظف",
    holderCollector: "محصل",
    holderSalesRep: "مندوب",
    assignmentDate: "تاريخ التسليم",
    branch: "الفرع",
    notes: "ملاحظات",
    lastMovement: "آخر حركة",
    assignAction: "تسليم دفتر",
    receiveAction: "استلام دفتر",
    closeAction: "إغلاق الدفتر",
    closeConfirm: "إغلاق الدفتر؟ لا يمكن تسليمه بعد ذلك.",
    save: "حفظ",
    moveAction: "الحركة",
    moveFrom: "من",
    moveTo: "إلى",
    moveDate: "التاريخ",
    movePerformedBy: "نفّذها",
    moveReceived: "استلام دفتر جديد",
    moveAssigned: "تسليم",
    moveReturned: "استلام",
    moveClosed: "إغلاق",
    company: "الشركة",
    openBook: "فتح",
    refresh: "تحديث",
    search: "بحث",
    noRows: "لا توجد دفاتر",
    count: "العدد",
    choose: "— اختر —",
    none: "بدون",
    actions: "إجراءات",
    saved: "تم الحفظ",
    assigned: "تم تسليم الدفتر",
    received: "تم استلام الدفتر",
    closed: "تم إغلاق الدفتر",
    loadError: "تعذر تحميل الدفاتر",
    saveError: "تعذر حفظ الدفتر",
    actionError: "تعذر تنفيذ الإجراء",
  },
  salesPurchaseTax: {
    title: "أنواع الضرائب للمبيعات والمشتريات",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « أنواع الضرائب للمبيعات والمشتريات",
    purposeNotice: "كتالوج الضرائب المعتمد للمبيعات والمشتريات، وكل نوع بنسبته السارية.",
    reuseNotice:
      "هذه الشاشة تقرأ وتكتب في كتالوج الضرائب القائم نفسه، ولا يوجد محرك ضرائب ثانٍ. الصلاحيات هي صلاحيات الضرائب القائمة.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
    formTitle: "نوع الضريبة",
    gridTitle: "أنواع الضرائب للمبيعات والمشتريات",
    newRecord: "نوع جديد",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    rate: "النسبة %",
    category: "فئة الضريبة",
    side: "جهة الضريبة",
    sideFromCategory: "— حسب فئة الضريبة —",
    sideSales: "مبيعات",
    sidePurchase: "مشتريات",
    account: "الحساب",
    debitAccount: "الحساب المدين",
    creditAccount: "الحساب الدائن",
    active: "فعال",
    defaultForSales: "إفتراضي مبيعات",
    actions: "إجراءات",
    edit: "تعديل",
    activate: "تفعيل",
    deactivate: "إلغاء التفعيل",
    search: "بحث",
    refresh: "تحديث",
    showInactive: "إظهار غير الفعال",
    noRows: "لا توجد أنواع ضرائب",
    counts: "الإجمالي / الفعال",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    choose: "— اختر —",
    none: "بدون",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    saved: "تم الحفظ",
    activated: "تم التفعيل",
    deactivated: "تم إلغاء التفعيل",
    defaultHint: "نوع واحد فقط يكون الافتراضي للمبيعات على مستوى الشركة.",
    loadError: "تعذر تحميل أنواع الضرائب",
    saveError: "تعذر الحفظ",
    auditError: "تعذر تحميل سجل التغييرات",
  },
  agingPeriods: {
    title: "الفترات الزمنية لعمر الذمة",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « الفترات الزمنية لعمر الذمة",
    purposeNotice: "تعريف الفترات الزمنية التي تُوزَّع عليها أعمار الذمم. الشاشة للتعريف فقط.",
    emptyNotice:
      "لا توجد فترات معرَّفة، ولم تُزرع أي قيم افتراضية. الفترات تُدخَل من هذه الشاشة فقط.",
    notCalculatedNotice:
      "لا يوجد في هذا الإصدار محرّك احتساب أعمار. التعريفات تُحفظ ولا يُحتسب شيء بعد.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
    gridTitle: "الفترات",
    formTitle: "بيانات الفترة",
    newRow: "فترة جديدة",
    sequence: "تسلسل الفترة",
    fromDay: "من يوم",
    toDay: "إلى يوم",
    actions: "إجراءات",
    edit: "تعديل",
    remove: "حذف",
    removeConfirm: "حذف هذه الفترة؟",
    refresh: "تحديث",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    noRows: "لا توجد فترات معرَّفة",
    count: "عدد الفترات",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    saved: "تم الحفظ",
    removed: "تم الحذف",
    loadError: "تعذر تحميل الفترات",
    saveError: "تعذر الحفظ",
    auditError: "تعذر تحميل سجل التغييرات",
  },
  paymentOrders: {
    title: "أمر صرف", breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "تعليمات بدفع مبلغ لمورّد. مستند تشغيلي لا أثر محاسبي له.",
    derivedNotice:
      "المدفوع والمتبقي محسوبان من سندات الصرف المُرحّلة فعلاً، ولا يُخزَّنان. عكس سند صرف يعيد المبلغ متاحاً على الأمر تلقائياً، لأن المجموع يُقرأ من جديد في كل مرة.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope:
      "لا يوجد أي مورّد ضمن صلاحيتك في الحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← ربط المستخدمين بالموردين.",
    listTitle: "الأوامر", formTitle: "بيانات الأمر", newRecord: "أمر جديد",
    search: "بحث", searchHint: "رقم الأمر أو المرجع أو المورّد", refresh: "تحديث",
    noRows: "لا توجد أوامر.", statusFilter: "الحالة", allStatuses: "كل الحالات",
    payableOnly: "القابلة للدفع فقط",
    number: "رقم الأمر", date: "التاريخ", supplier: "المورّد", chooseSupplier: "اختر المورّد",
    requestedAmount: "المبلغ المطلوب", paidAmount: "المدفوع", remainingAmount: "المتبقي",
    currency: "العملة", exchangeRate: "سعر التحويل", branch: "الفرع",
    expectedDate: "تاريخ الدفع المتوقع", expectedMethod: "طريقة الدفع المفضّلة",
    responsible: "المسؤول", reference: "المرجع", notes: "ملاحظات", none: "بدون",
    status: "الحالة", open: "مفتوح", cancelled: "ملغى",
    progress: "حالة السداد", unpaid: "غير مدفوع", partiallyPaid: "مدفوع جزئياً", paid: "مدفوع",
    save: "حفظ", cancelEdit: "إلغاء التعديل", edit: "تعديل", view: "عرض",
    cancelOrder: "إلغاء الأمر", cancelReason: "سبب الإلغاء", confirmCancel: "تأكيد الإلغاء",
    close: "إغلاق", details: "تفاصيل الأمر", createdAt: "تاريخ الإنشاء",
    vouchers: "سندات الصرف المرتبطة", voucherNumber: "رقم السند", voucherDate: "التاريخ",
    voucherStatus: "الحالة", voucherAmount: "المبلغ",
    effective: "محتسب", notEffective: "غير محتسب", noVouchers: "لا توجد سندات مرتبطة.",
    createVoucher: "إنشاء سند صرف",
    saved: "تم حفظ الأمر.", cancelledOk: "تم إلغاء الأمر.",
    loadError: "تعذّر تحميل الأوامر.", saveError: "تعذّر حفظ الأمر.",
    cancelError: "تعذّر إلغاء الأمر.", totalCount: "عدد الأوامر",
  },
  paymentVouchers: {
    settlementTitle: "تسديد الفواتير",
    settlementHint: "اختر الفواتير التي يسدّدها هذا السند.",
    settlementInvoice: "رقم القيد",
    settlementSupplierInvoice: "رقم فاتورة المورد",
    settlementDate: "تاريخ الفاتورة",
    settlementDue: "تاريخ الاستحقاق",
    settlementOriginal: "قيمة الفاتورة",
    settlementSettled: "المسدّد سابقاً",
    settlementOutstanding: "المتبقي",
    settlementAmount: "المبلغ المسدّد",
    settlementAllocated: "إجمالي التسديد",
    settlementUnapplied: "غير مخصّص",
    settlementNone: "لا توجد فواتير مفتوحة لهذا المورّد.",
    settlementOverPayment: "مجموع التسديدات يتجاوز قيمة السند.",
    settlementEffectiveOnPost: "يسري التسديد عند ترحيل السند.",
    settlementLoading: "جارٍ تحميل الفواتير المفتوحة…",
    title: "سند صرف", breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "المستند المحاسبي لخروج المال.",
    postingNotice:
      "القيد: من ح/ ذمم المورّد الدائنة (أو الحساب المختار في الدفع المباشر)، إلى ح/ الصندوق أو البنك حسب طريقة الدفع. الدورة: مسودة ← مدقّق ← مُرحّل، ولا يجوز لمن أدخل السند أن يدقّقه. الإلغاء بقيد عكسي رسمي فقط.",
    chequeNotice:
      "الشيكات الصادرة غير مدعومة في هذه النسخة: سجل الشيكات الحالي خاص بالشيكات الواردة. اختيار طريقة دفع بشيك يُرفض صراحةً ولا يُرحَّل كأنه نقد أو حوالة.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope:
      "لا يوجد أي مورّد ضمن صلاحيتك في الحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← ربط المستخدمين بالموردين.",
    listTitle: "السندات", formTitle: "بيانات السند", linesTitle: "مصادر الدفع",
    newRecord: "سند جديد", search: "بحث", searchHint: "رقم السند أو المرجع أو المورّد",
    refresh: "تحديث", noRows: "لا توجد سندات.", statusFilter: "الحالة", allStatuses: "كل الحالات",
    number: "رقم السند", date: "التاريخ", mode: "نوع الدفع",
    modeSupplier: "دفع لمورّد", modeDirect: "دفع مباشر",
    supplier: "المورّد", chooseSupplier: "اختر المورّد",
    directAccount: "الحساب", chooseAccount: "اختر الحساب",
    paymentOrder: "أمر الصرف", noOrder: "بدون أمر", currency: "العملة",
    exchangeRate: "سعر التحويل", branch: "الفرع", costCenter: "مركز التكلفة",
    reference: "المرجع", notes: "ملاحظات", none: "بدون", amount: "المبلغ",
    paymentMethod: "طريقة الدفع", chooseMethod: "اختر الطريقة", lineAmount: "المبلغ",
    lineNotes: "ملاحظات", addLine: "إضافة مصدر", removeLine: "حذف", total: "الإجمالي",
    status: "الحالة", draft: "مسودة", audited: "مدقّق", posted: "مُرحّل", reversed: "معكوس",
    save: "حفظ", cancelEdit: "إلغاء التعديل", edit: "تعديل", view: "عرض",
    audit: "تدقيق", post: "ترحيل", reverse: "قيد عكسي", auditNotes: "ملاحظات التدقيق",
    reversalDate: "تاريخ القيد العكسي", reversalReason: "سبب العكس",
    confirmReverse: "تأكيد العكس", close: "إغلاق",
    details: "تفاصيل السند", createdAt: "تاريخ الإنشاء", remainingOnOrder: "المتبقي على الأمر",
    saved: "تم حفظ السند.", auditedOk: "تم التدقيق.", postedOk: "تم الترحيل.",
    reversedOk: "تم إصدار القيد العكسي.",
    loadError: "تعذّر تحميل السندات.", saveError: "تعذّر حفظ السند.",
    auditError: "تعذّر التدقيق.", postError: "تعذّر الترحيل.",
    reverseError: "تعذّر إصدار القيد العكسي.",
    totalCount: "عدد السندات", totalPosted: "إجمالي المُرحّل",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    exportFailed: "تعذّر إنشاء الملف.",
  },
  supplierDebitNotes: {
    title: "إشعار مدين للمورّد",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "تسوية تُحمَّل على المورّد فتُخفّض المبلغ المستحق له: خصم متفق عليه، أو غرامة، أو تصحيح مبالغة في الفوترة.",
    postingNotice: "القيد: من ح/ ذمم المورّد الدائنة، إلى ح/ الحساب المقابل لكل بند، وإلى ح/ ضريبة المدخلات لردّ ما سبق تحميله. الدورة: مسودة ← تدقيق ← ترحيل. لا أثر في الدفاتر قبل الترحيل، والتصحيح بقيد عكسي لا بالحذف.",
    lineageNotice: "ربط الإشعار بفاتورة مشتريات مرحّلة هو للعلم فقط: لا يُعاد فتح الفاتورة، ولا يُعدَّل قيدها، ولا يُخصم من رصيد المردودات المتاح عليها. ويجوز ربط أكثر من إشعار بالفاتورة نفسها.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope: "لا يوجد مورّد ضمن صلاحيتك للحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← صلاحية المستخدمين على المورّدين.",
    listTitle: "الإشعارات",
    formTitle: "بيانات الإشعار",
    linesTitle: "البنود",
    newRecord: "إشعار جديد",
    search: "بحث",
    searchHint: "رقم الإشعار أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد إشعارات.",
    statusFilter: "الحالة",
    allStatuses: "كل الحالات",
    totalCount: "العدد",
    totalPosted: "إجمالي المُرحّل",
    number: "رقم الإشعار",
    date: "التاريخ",
    supplier: "المورّد",
    chooseSupplier: "اختر المورّد",
    sourceInvoice: "فاتورة المصدر (للعلم فقط)",
    noSourceInvoice: "بدون فاتورة",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    branch: "الفرع",
    costCenter: "مركز التكلفة",
    reference: "رقم المرجع",
    notes: "ملاحظات",
    none: "بدون",
    taxInclusive: "المبالغ شاملة الضريبة",
    description: "البيان",
    offsetAccount: "الحساب المقابل",
    chooseAccount: "اختر الحساب",
    amount: "المبلغ",
    taxType: "نوع الضريبة",
    noTax: "بدون ضريبة",
    taxPercent: "النسبة",
    tax: "الضريبة",
    lineTotal: "إجمالي البند",
    lineNotes: "ملاحظات البند",
    addLine: "إضافة بند",
    removeLine: "حذف",
    net: "الصافي",
    total: "الإجمالي",
    status: "الحالة",
    draft: "مسودة",
    audited: "مدقّق",
    posted: "مُرحّل",
    reversed: "معكوس",
    save: "حفظ",
    cancelEdit: "إلغاء",
    edit: "تعديل",
    view: "عرض",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "قيد عكسي",
    auditNotes: "ملاحظات التدقيق",
    reversalDate: "تاريخ القيد العكسي",
    reversalReason: "سبب القيد العكسي",
    confirmReverse: "تأكيد العكس",
    close: "إغلاق",
    details: "تفاصيل الإشعار",
    createdAt: "تاريخ الإنشاء",
    createdBy: "أنشأه",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "تم حفظ الإشعار.",
    auditedOk: "تم تدقيق الإشعار.",
    postedOk: "تم ترحيل الإشعار.",
    reversedOk: "تم إنشاء القيد العكسي.",
    loadError: "تعذّر تحميل الإشعارات.",
    saveError: "تعذّر حفظ الإشعار.",
    auditError: "تعذّر تدقيق الإشعار.",
    postError: "تعذّر ترحيل الإشعار.",
    reverseError: "تعذّر إنشاء القيد العكسي.",
    exportFailed: "تعذّر إنشاء الملف.",
  },
  supplierCreditNotes: {
    title: "إشعار دائن للمورّد",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "تسوية تُقيَّد لصالح المورّد فتزيد المبلغ المستحق له: تصحيح نقص في الفوترة، أو زيادة سعر متفق عليها، أو رسوم شحن أو مناولة تُطالَب بها لاحقاً.",
    postingNotice: "القيد: من ح/ الحساب المقابل لكل بند، ومن ح/ ضريبة المدخلات، إلى ح/ ذمم المورّد الدائنة. عكس إشعار المدين تماماً. الدورة: مسودة ← تدقيق ← ترحيل. لا أثر في الدفاتر قبل الترحيل، والتصحيح بقيد عكسي لا بالحذف.",
    lineageNotice: "ربط الإشعار بفاتورة مشتريات مرحّلة هو للعلم فقط: لا يُعاد فتح الفاتورة، ولا يُعدَّل قيدها، ولا يُضاف إلى رصيد المردودات المتاح عليها — وإلا أمكن ردّ المبلغ نفسه مرتين. ويجوز ربط أكثر من إشعار بالفاتورة نفسها.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    noSuppliersInScope: "لا يوجد مورّد ضمن صلاحيتك للحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← صلاحية المستخدمين على المورّدين.",
    listTitle: "الإشعارات",
    formTitle: "بيانات الإشعار",
    linesTitle: "البنود",
    newRecord: "إشعار جديد",
    search: "بحث",
    searchHint: "رقم الإشعار أو المرجع",
    refresh: "تحديث",
    noRows: "لا توجد إشعارات.",
    statusFilter: "الحالة",
    allStatuses: "كل الحالات",
    totalCount: "العدد",
    totalPosted: "إجمالي المُرحّل",
    number: "رقم الإشعار",
    date: "التاريخ",
    supplier: "المورّد",
    chooseSupplier: "اختر المورّد",
    sourceInvoice: "فاتورة المصدر (للعلم فقط)",
    noSourceInvoice: "بدون فاتورة",
    currency: "العملة",
    exchangeRate: "سعر الصرف",
    branch: "الفرع",
    costCenter: "مركز التكلفة",
    reference: "رقم المرجع",
    notes: "ملاحظات",
    none: "بدون",
    taxInclusive: "المبالغ شاملة الضريبة",
    description: "البيان",
    offsetAccount: "الحساب المقابل",
    chooseAccount: "اختر الحساب",
    amount: "المبلغ",
    taxType: "نوع الضريبة",
    noTax: "بدون ضريبة",
    taxPercent: "النسبة",
    tax: "الضريبة",
    lineTotal: "إجمالي البند",
    lineNotes: "ملاحظات البند",
    addLine: "إضافة بند",
    removeLine: "حذف",
    net: "الصافي",
    total: "الإجمالي",
    status: "الحالة",
    draft: "مسودة",
    audited: "مدقّق",
    posted: "مُرحّل",
    reversed: "معكوس",
    save: "حفظ",
    cancelEdit: "إلغاء",
    edit: "تعديل",
    view: "عرض",
    audit: "تدقيق",
    post: "ترحيل",
    reverse: "قيد عكسي",
    auditNotes: "ملاحظات التدقيق",
    reversalDate: "تاريخ القيد العكسي",
    reversalReason: "سبب القيد العكسي",
    confirmReverse: "تأكيد العكس",
    close: "إغلاق",
    details: "تفاصيل الإشعار",
    createdAt: "تاريخ الإنشاء",
    createdBy: "أنشأه",
    printAction: "طباعة",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "تم حفظ الإشعار.",
    auditedOk: "تم تدقيق الإشعار.",
    postedOk: "تم ترحيل الإشعار.",
    reversedOk: "تم إنشاء القيد العكسي.",
    loadError: "تعذّر تحميل الإشعارات.",
    saveError: "تعذّر حفظ الإشعار.",
    auditError: "تعذّر تدقيق الإشعار.",
    postError: "تعذّر ترحيل الإشعار.",
    reverseError: "تعذّر إنشاء القيد العكسي.",
    exportFailed: "تعذّر إنشاء الملف.",
  },
  supplierAccountSettlement: {
    title: "تسوية حساب مورّد",
    breadcrumb: "المحاسبة العامة « المدفوعات « الحركات اليومية « تسوية حساب مورّد",
    notice: "مطابقة بين فواتير المورّد المستحقة وما دُفع له من سندات صرف وإشعارات. لا تُنشئ قيداً ولا حركة على دفتر الأستاذ.",
    partyLabel: "المورّد",
    pickPartyFirst: "اختر المورّد ثم اضغط «استعلام».",
    polarityNotice: "على جانب المدفوعات ينعكس الاتجاه: فاتورة المشتريات تُقيَّد دائنة للمورّد وهي مع ذلك ما يُسدَّد، وسند الصرف يُقيَّد مديناً وهو ما يُسدِّد. المحرّك يقرأ الاتجاه من نوع الطرف نفسه.",
    noSuppliersInScope: "لا يوجد مورّد ضمن صلاحيتك للحركات اليومية. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← صلاحية المستخدمين على المورّدين.",
    cancelTitle: "إلغاء تسوية فاتورة مشتريات",
    cancelBreadcrumb: "المحاسبة العامة « المدفوعات « الحركات اليومية « إلغاء تسوية فاتورة",
    cancelNotice: "إلغاء تسوية سابقة يعيد فتح المبلغ على الفاتورة وعلى الدفعة معاً. لا يُحذف السطر: يبقى مسجّلاً مع سبب الإلغاء ومَن ألغاه.",
  },
  paymentBooks: {
    title: "دفاتر الصرف",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "سجل دفاتر سندات الصرف المطبوعة مسبقاً: المدى، ومن يحمل الدفتر، ومتى.",
    noAccountingNotice:
      "لا أثر محاسبي في أي مرحلة. الدفتر قرطاسية تحت الرقابة؛ المال يخرج بسند صرف، وهو مستند آخر. ولا يسحب سند الصرف رقمه من الدفتر — الترقيم من المولّد المشترك حسب نوع السند.",
    historyNotice:
      "سجل الحركات لا يُعدّل ولا يُحذف: تصحيح تسليم خاطئ يكون بتسجيل الاستلام، لا بمحو التسليم. ولا يوجد حذف للدفتر — يتم الإغلاق فقط.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    listTitle: "الدفاتر", formTitle: "بيانات الدفتر", newRecord: "دفتر جديد",
    search: "بحث", searchHint: "رقم الدفتر أو الملاحظات", refresh: "تحديث",
    noRows: "لا توجد دفاتر.",
    statusFilter: "الحالة", allStatuses: "كل الحالات",
    counts: "الإجمالي / المتاح / المُسلَّم", numbers: "عدد الأرقام",
    bookNumber: "رقم الدفتر", fromNumber: "من رقم", toNumber: "إلى رقم", count: "العدد",
    branch: "الفرع", none: "بدون", receivedDate: "تاريخ الاستلام",
    assignedDate: "تاريخ التسليم", returnedDate: "تاريخ الإرجاع",
    holder: "الحامل", chooseHolder: "اختر المستلم", notes: "ملاحظات",
    status: "الحالة", available: "متاح", assigned: "مُسلَّم", closed: "مغلق",
    actions: "إجراءات", edit: "تعديل", view: "عرض",
    assign: "تسليم", takeBack: "استلام", close: "إغلاق",
    save: "حفظ", cancelEdit: "إلغاء", movementDate: "تاريخ الحركة",
    movementNotes: "ملاحظات الحركة", confirm: "تأكيد",
    history: "سجل الحركات", historyAction: "الحركة", historyFrom: "من",
    historyTo: "إلى", historyDate: "التاريخ", historyBy: "بواسطة", historyNotes: "ملاحظات",
    actionReceived: "استلام دفتر جديد", actionAssigned: "تسليم",
    actionReturned: "استلام من الحامل", actionClosed: "إغلاق",
    details: "تفاصيل الدفتر",
    noHolders: "لا توجد سجلات أفراد فعّالة لتسليم الدفتر لها. تُضاف من: المدفوعات ← البيانات الأساسية.",
    saved: "تم حفظ الدفتر.", movedOk: "تم تسجيل الحركة.",
    loadError: "تعذّر تحميل الدفاتر.", saveError: "تعذّر حفظ الدفتر.",
    moveError: "تعذّر تسجيل الحركة.", totalCount: "عدد الدفاتر",
  },
  servicePurchaseReturn: {
    title: "مردود شراء خدمات",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "إرجاع مالي على فاتورة شراء خدمات مُرحّلة.",
    postingNotice:
      "القيد عكس قيد الفاتورة تماماً: من ح/ ذمم المورّد الدائنة، إلى ح/ المصروف وإلى ح/ ضريبة المدخلات. دورة المستند: مسودة ← مدقّق ← مُرحّل، والإلغاء بقيد عكسي رسمي.",
    boundNotice:
      "المردود مرتبط بالفاتورة: المورّد والعملة وسعر التحويل والحسابات والضريبة تُقرأ من سطور الفاتورة الأصلية، ولا تُختار. أنت تختار أي البنود وكم منها فقط. عكس المردود يُعيد الكمية متاحة للإرجاع من جديد.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    listTitle: "المردودات", formTitle: "بيانات المردود", linesTitle: "البنود القابلة للإرجاع",
    newRecord: "مردود جديد", search: "بحث", searchHint: "رقم المردود أو المرجع أو المورّد",
    refresh: "تحديث", noRows: "لا توجد مردودات.",
    dateFrom: "من تاريخ", dateTo: "إلى تاريخ", statusFilter: "الحالة", allStatuses: "كل الحالات",
    number: "رقم المردود", date: "التاريخ", sourceInvoice: "فاتورة الشراء",
    chooseInvoice: "اختر فاتورة مُرحّلة", supplier: "المورّد", currency: "العملة",
    reference: "المرجع", notes: "ملاحظات", costCenter: "مركز التكلفة", none: "بدون",
    branch: "الفرع",
    lineNo: "#", expenseAccount: "حساب المصروف", lineDescription: "الوصف",
    originalQuantity: "الكمية الأصلية", returnedQuantity: "المُرجع سابقاً",
    remainingQuantity: "المتبقي", returnQuantity: "كمية الإرجاع",
    unitPrice: "السعر", taxPercent: "نسبة الضريبة",
    netAmount: "الصافي", taxAmount: "الضريبة", totalAmount: "الإجمالي",
    status: "الحالة", draft: "مسودة", audited: "مدقّق", posted: "مُرحّل", reversed: "معكوس",
    save: "حفظ", cancelEdit: "إلغاء التعديل", edit: "تعديل", view: "عرض",
    audit: "تدقيق", post: "ترحيل", reverse: "قيد عكسي", auditNotes: "ملاحظات التدقيق",
    reversalDate: "تاريخ القيد العكسي", reversalReason: "سبب العكس",
    confirmReverse: "تأكيد العكس", close: "إغلاق",
    details: "تفاصيل المردود", createdAt: "تاريخ الإنشاء",
    saved: "تم حفظ المردود.", auditedOk: "تم تدقيق المردود.",
    postedOk: "تم ترحيل المردود.", reversedOk: "تم إصدار القيد العكسي.",
    loadError: "تعذّر تحميل المردودات.", saveError: "تعذّر حفظ المردود.",
    auditError: "تعذّر تدقيق المردود.", postError: "تعذّر ترحيل المردود.",
    reverseError: "تعذّر إصدار القيد العكسي.", totalCount: "عدد المردودات",
    print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
    exportError: "تعذّر إنشاء الملف.",
    nothingReturnable: "لا يوجد في هذه الفاتورة ما يمكن إرجاعه.",
    invoiceNotPosted: "لا يمكن الإرجاع إلا على فاتورة مُرحّلة.",
    pickInvoiceFirst: "اختر فاتورة شراء مُرحّلة لعرض البنود القابلة للإرجاع.",
  },
  servicePurchaseInvoice: {
    title: "فاتورة شراء خدمات",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "فاتورة شراء خدمة من مورّد. مستند محاسبي كامل.",
    postingNotice:
      "دورة المستند: مسودة ← مدقّقة ← مُرحّلة. لا أثر على الأستاذ العام قبل الترحيل، ولا يجوز لمن أدخل المستند أن يدقّقه. الإلغاء يتم بقيد عكسي رسمي؛ لا يُحذف مستند مُرحّل أبداً.",
    accountNotice:
      "القيد: من ح/ المصروف (لكل بند) ومن ح/ ضريبة المدخلات، إلى ح/ ذمم المورّد الدائنة (الحساب الوسيط للمورّد). يختار كل بند حساب المصروف من دليل الحسابات — لا يُستخدم حساب إيراد ولا حساب ثابت.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    scopeNotice: "تظهر فقط فواتير الموردين المصرّح لك بهم في الحركات اليومية.",
    noSuppliersInScope:
      "لا يوجد أي مورّد ضمن صلاحيتك في الحركات اليومية، لذلك لا يمكن إنشاء فاتورة. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← ربط المستخدمين بالموردين.",
    listTitle: "الفواتير", formTitle: "بيانات الفاتورة", linesTitle: "البنود",
    newRecord: "فاتورة جديدة", search: "بحث",
    searchHint: "رقم الفاتورة أو رقم فاتورة المورّد أو المورّد",
    refresh: "تحديث", noRows: "لا توجد فواتير.",
    dateFrom: "من تاريخ", dateTo: "إلى تاريخ", statusFilter: "الحالة", allStatuses: "كل الحالات",
    number: "رقم الفاتورة", date: "التاريخ", supplier: "المورّد", chooseSupplier: "اختر المورّد",
    supplierInvoiceNumber: "رقم فاتورة المورّد", sourceRequest: "طلب الشراء",
    currency: "العملة", exchangeRate: "سعر التحويل", branch: "الفرع",
    paymentMethod: "طريقة الدفع", costCenter: "مركز التكلفة", none: "بدون",
    notes: "ملاحظات", pricing: "طريقة التسعير",
    taxInclusive: "السعر شامل الضريبة", taxExclusive: "السعر غير شامل الضريبة",
    expenseAccount: "حساب المصروف", chooseAccount: "اختر الحساب",
    lineDescription: "الوصف", unit: "الوحدة", quantity: "الكمية", unitPrice: "السعر",
    discountPercent: "نسبة الخصم %", taxType: "نوع الضريبة",
    lineGross: "الإجمالي", lineDiscount: "الخصم", lineTax: "الضريبة", lineNet: "الصافي",
    addLine: "إضافة بند", removeLine: "حذف",
    taxComputedOnSave: "الضريبة تُحتسب على الخادم عند الحفظ.",
    subtotal: "الإجمالي قبل الخصم", discountTotal: "إجمالي الخصم",
    taxTotal: "إجمالي الضريبة", netTotal: "الصافي",
    status: "الحالة", draft: "مسودة", audited: "مدقّقة", posted: "مُرحّلة",
    rejected: "مُعادة", reversed: "معكوسة",
    save: "حفظ", cancelEdit: "إلغاء التعديل", edit: "تعديل", view: "عرض",
    audit: "تدقيق", post: "ترحيل", reverse: "قيد عكسي", auditNotes: "ملاحظات التدقيق",
    reversalDate: "تاريخ القيد العكسي", reversalReason: "سبب العكس",
    confirmReverse: "تأكيد العكس", close: "إغلاق",
    details: "تفاصيل الفاتورة", createdAt: "تاريخ الإنشاء", settled: "المسدّد",
    saved: "تم حفظ الفاتورة.", auditedOk: "تم تدقيق الفاتورة.",
    postedOk: "تم ترحيل الفاتورة.", reversedOk: "تم إصدار القيد العكسي.",
    loadError: "تعذّر تحميل الفواتير.", saveError: "تعذّر حفظ الفاتورة.",
    auditError: "تعذّر تدقيق الفاتورة.", postError: "تعذّر ترحيل الفاتورة.",
    reverseError: "تعذّر إصدار القيد العكسي.", totalCount: "عدد الفواتير",
    print: "طباعة", exportPdf: "PDF", exportExcel: "Excel",
    exportError: "تعذّر إنشاء الملف.",
  },
  servicePurchaseRequest: {
    title: "طلب شراء خدمات",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice:
      "طلب شراء خدمة من مورّد. مستند تشغيلي سابق للمحاسبة: يسجّل ما هو مطلوب وبتقدير سعره، ولا يُلزم أحداً بشيء.",
    noAccountingNotice:
      "لا أثر محاسبي في أي مرحلة: لا قيد ولا ذمة دائنة ولا ضريبة مُرحّلة. الطلب يصبح مالاً فقط عند إصدار فاتورة شراء الخدمات، ولا يوجد تحويل آلي من الطلب إلى الفاتورة في هذه النسخة.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    scopeNotice: "تظهر فقط الطلبات الخاصة بالموردين المصرّح لك بهم في الحركات اليومية.",
    listTitle: "الطلبات",
    formTitle: "بيانات الطلب",
    linesTitle: "البنود",
    newRecord: "طلب جديد",
    search: "بحث",
    searchHint: "رقم الطلب أو المرجع أو المورّد",
    refresh: "تحديث",
    noRows: "لا توجد طلبات.",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    statusFilter: "الحالة",
    allStatuses: "كل الحالات",
    number: "رقم الطلب",
    date: "التاريخ",
    requiredDate: "التاريخ المطلوب",
    supplier: "المورّد",
    chooseSupplier: "اختر المورّد",
    currency: "العملة",
    exchangeRate: "سعر التحويل",
    branch: "الفرع",
    paymentMethod: "طريقة الدفع",
    requestedBy: "الجهة الطالبة",
    costCenter: "مركز التكلفة",
    none: "بدون",
    reference: "المرجع",
    notes: "ملاحظات",
    taxInclusive: "السعر شامل الضريبة",
    taxExclusive: "السعر غير شامل الضريبة",
    pricing: "طريقة التسعير",
    lineDescription: "الوصف",
    unit: "الوحدة",
    quantity: "الكمية",
    unitPrice: "السعر التقديري",
    discountPercent: "نسبة الخصم %",
    discountType: "نوع الخصم",
    discountValue: "قيمة الخصم",
    taxType: "نوع الضريبة",
    lineGross: "الإجمالي",
    lineDiscount: "الخصم",
    lineTax: "الضريبة",
    lineNet: "الصافي",
    addLine: "إضافة بند",
    removeLine: "حذف",
    subtotal: "الإجمالي قبل الخصم",
    discountTotal: "إجمالي الخصم",
    taxTotal: "إجمالي الضريبة",
    netTotal: "الصافي",
    status: "الحالة",
    draft: "مسودة",
    cancelled: "ملغى",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancelEdit: "إلغاء التعديل",
    edit: "تعديل",
    view: "عرض",
    cancelRequest: "إلغاء الطلب",
    cancelReason: "سبب الإلغاء",
    confirmCancel: "تأكيد الإلغاء",
    close: "إغلاق",
    details: "تفاصيل الطلب",
    createdBy: "أُنشئ بواسطة",
    createdAt: "تاريخ الإنشاء",
    attachments: "المرفقات",
    saved: "تم حفظ الطلب.",
    cancelledOk: "تم إلغاء الطلب.",
    loadError: "تعذّر تحميل الطلبات.",
    saveError: "تعذّر حفظ الطلب.",
    cancelError: "تعذّر إلغاء الطلب.",
    linesRequired: "يجب إدخال بند واحد على الأقل.",
    totalCount: "عدد الطلبات",
    taxComputedOnSave: "الضريبة تُحتسب على الخادم عند الحفظ.",
    noSuppliersInScope:
      "لا يوجد أي مورّد ضمن صلاحيتك في الحركات اليومية، لذلك لا يمكن إنشاء طلب. تُمنح الصلاحية من: المدفوعات ← البيانات الأساسية ← ربط المستخدمين بالموردين.",
  },
  personnel: {
    collectorsTitle: "المحصّلون",
    driversTitle: "السائقون",
    coordinatorsTitle: "المنسقون",
    salesRepsTitle: "مندوبو المبيعات",
    collectorsPurpose:
      "قائمة المحصّلين المعتمدين. المحصّل المسجّل في سندات وأوامر القبض هو نفسه مستخدم النظام المرتبط هنا، ولم يُستحدث كيان محصّل ثانٍ.",
    driversPurpose:
      "بيانات السائقين التشغيلية فقط. لا مركبات ولا خطوط سير ولا جداول تسليم ولا أي معالجة محاسبية.",
    coordinatorsPurpose:
      "بيانات المنسقين التشغيلية فقط. لا مهام سير عمل ولا معالجة محاسبية غير معرّفة مسبقاً.",
    salesRepsPurpose:
      "قائمة مندوبي المبيعات المعتمدين. المندوب المسجّل في بطاقة العميل وفئات العملاء وأوامر القبض هو نفسه مستخدم النظام المرتبط هنا.",
    breadcrumb: "المحاسبة العامة / المدفوعات / البيانات الأساسية",
    identityNotice:
      "الربط بمستخدم النظام اختياري: مَن لا يملك حساب دخول يُسجَّل بالاسم فقط. وحيث يوجد ربط فهو نفس المستخدم الذي تشير إليه المستندات القائمة.",
    noAccountingNotice:
      "بيانات تشغيلية فقط: لا حساب محاسبي ولا رصيد ولا عمولة ولا قيود. لا يوجد حذف — يتم التعطيل والتفعيل فقط حفاظاً على المستندات التاريخية.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط.",
    listTitle: "القائمة",
    formTitle: "البيانات",
    newRecord: "سجل جديد",
    search: "بحث",
    searchHint: "الرمز أو الاسم أو الجوال أو الهاتف أو البريد",
    refresh: "تحديث",
    showInactive: "إظهار غير الفعّال",
    noRows: "لا توجد سجلات.",
    branchFilter: "الفرع",
    allBranches: "كل الفروع",
    linkFilter: "الربط بمستخدم",
    anyLink: "الكل",
    linkedOnly: "مرتبط بمستخدم",
    unlinkedOnly: "غير مرتبط",
    code: "الرمز",
    nameAr: "الاسم بالعربي",
    nameEn: "الاسم بالإنجليزي",
    user: "مستخدم النظام",
    noUser: "بدون مستخدم",
    userHint: "من مستخدمي النظام الفعالين.",
    mobile: "الجوال",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    branch: "الفرع",
    noBranch: "بدون فرع",
    notes: "ملاحظات",
    active: "فعّال",
    actions: "إجراءات",
    edit: "تعديل",
    view: "عرض",
    activate: "تفعيل",
    deactivate: "تعطيل",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    details: "تفاصيل السجل",
    updatedAt: "آخر تحديث",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجّلة.",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    saved: "تم الحفظ.",
    activated: "تم التفعيل.",
    deactivated: "تم التعطيل.",
    counts: "الإجمالي / الفعّال",
    matched: "المطابق",
    loadError: "تعذّر تحميل القائمة.",
    saveError: "تعذّر الحفظ.",
    auditError: "تعذّر تحميل سجل التغييرات.",
  },
  fixedAssetsMasterData: {
    common: {
      code: "الرمز", nameAr: "الاسم بالعربي", nameEn: "الاسم بالإنجليزي",
      active: "مفعّل", notes: "ملاحظات", actions: "إجراءات",
      edit: "تعديل", activate: "تفعيل", deactivate: "إيقاف", delete: "حذف",
      save: "حفظ", cancel: "إلغاء", search: "بحث", refresh: "تحديث",
      showInactive: "إظهار غير المفعّل", noRows: "لا توجد سجلات.",
      readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
      saved: "تم الحفظ.", activated: "تم التفعيل.", deactivated: "تم الإيقاف.",
      deleted: "تم الحذف.",
      loadError: "تعذّر تحميل البيانات.", saveError: "تعذّر الحفظ.",
      parent: "السجل الأب", noParent: "— بدون أب (مستوى رئيسي) —", level: "المستوى",
      assets: "عدد الأصول", inactiveByParent: "موقوف بسبب الأب",
      deleteConfirm: "حذف هذا السجل نهائياً؟ الحذف متاح فقط لسجل لم يُستخدم إطلاقاً.",
      none: "— بدون —", required: "مطلوب",
    },
    groups: {
      title: "مجموعات الأصول الثابتة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « البيانات الأساسية « مجموعات الأصول الثابتة",
      purposeNotice: "تصنيف هرمي للأصول الثابتة، وهو المكان الذي تُحدَّد فيه الحسابات التي ترحّل إليها الأصول التابعة له.",
      scopeNotice: "الحسابات تُختار من شجرة حسابات الشركة ولا يوجد أي حساب مثبّت في الكود. المجموعة الفرعية ترث حسابات أقرب أب يحمل حساباً، والوراثة تُحسب عند القراءة ولا تُنسخ.",
      treeTitle: "شجرة المجموعات", formTitle: "بيانات المجموعة",
      newRoot: "مجموعة رئيسية", newChild: "مجموعة فرعية",
      accountsSection: "الحسابات المحاسبية", depreciationSection: "افتراضيات الإهلاك",
      assetAccount: "حساب الأصل", accumulatedAccount: "مجمع الإهلاك",
      expenseAccount: "مصروف الإهلاك",
      disposalGainAccount: "أرباح الاستبعاد", disposalLossAccount: "خسائر الاستبعاد",
      inheritedFrom: "موروث من", effective: "الحساب الفعلي", notMapped: "غير محدد",
      chooseAccount: "— اختر الحساب —",
      method: "طريقة الإهلاك", straightLine: "القسط الثابت", decliningBalance: "القسط المتناقص",
      usefulLifeMonths: "العمر الإنتاجي (شهر)", ratePercent: "نسبة الإهلاك السنوية %",
      salvagePercent: "القيمة التخريدية %",
      defaultsNotice: "هذه افتراضيات تُنسخ إلى بطاقة الأصل عند إنشائها فقط. تعديلها لاحقاً لا يغيّر أي بطاقة قائمة ولا أي إهلاك مرحّل.",
    },
    locations: {
      title: "مواقع الأصول الثابتة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « البيانات الأساسية « مواقع الأصول الثابتة",
      purposeNotice: "هيكل هرمي لمواقع الأصول بعدد مستويات غير محدود: المبنى ثم الطابق ثم القسم.",
      scopeNotice: "الموقع ليس مركز تكلفة. المكان الذي يوجد فيه الأصل والجهة التي تتحمّل كلفته سؤالان مختلفان، ولا يُشتق أحدهما من الآخر.",
      treeTitle: "شجرة المواقع", formTitle: "بيانات الموقع",
      newRoot: "موقع رئيسي", newChild: "موقع فرعي",
      branch: "الفرع",
    },
    card: {
      title: "بطاقة الأصل",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « البيانات الأساسية « بطاقة الأصل",
      purposeNotice: "السجل المرجعي لكل أصل ثابت على حدة. كل حركات الأصول الثابتة تشير إلى هذه البطاقة ولا تعيد وصف الأصل.",
      scopeNotice: "هذه الشاشة بيانات أساسية فقط: لا تبيع ولا تستبعد ولا تُهلك. حالات البيع والاستبعاد والشطب تُحدَّد من خلال الحركة المحاسبية الخاصة بها.",
      listTitle: "سجل الأصول", newAsset: "أصل جديد", backToList: "رجوع للقائمة",
      tabGeneral: "بيانات عامة", tabAcquisition: "الاقتناء", tabDepreciation: "الإهلاك",
      tabLocation: "الموقع والعهدة", tabAccounting: "الحسابات", tabAttachments: "المرفقات",
      assetNumber: "رقم الأصل", group: "المجموعة", location: "الموقع", branch: "الفرع",
      status: "الحالة", description: "الوصف",
      statusDraft: "مسودة", statusInService: "في الخدمة", statusSuspended: "موقوف",
      statusFullyDepreciated: "مهلك بالكامل", statusSold: "مباع",
      statusDisposed: "مستبعد", statusWrittenOff: "مشطوب",
      statusOperationOnly: "تُحدَّد بالحركة",
      acquisitionSource: "مصدر الاقتناء", sourcePurchase: "شراء",
      sourceOpeningBalance: "رصيد افتتاحي",
      supplier: "المورد", purchaseDate: "تاريخ الشراء", inServiceDate: "تاريخ التشغيل",
      documentReference: "مرجع مستند الشراء",
      documentReferenceHint: "نص حر: فاتورة شراء الأصول الثابتة حركة يومية غير مبنية بعد، وربط مفتاح بجدول فارغ وعد لا يمكن الوفاء به.",
      acquisitionCost: "تكلفة الاقتناء", currency: "العملة", exchangeRate: "سعر الصرف",
      localCost: "التكلفة بالعملة المحلية",
      serialNumber: "الرقم التسلسلي", manufacturer: "الشركة الصانعة", model: "الموديل",
      warrantyExpiry: "انتهاء الكفالة",
      method: "طريقة الإهلاك", usefulLifeMonths: "العمر الإنتاجي (شهر)",
      ratePercent: "نسبة الإهلاك السنوية %", salvageValue: "القيمة التخريدية",
      depreciationStart: "تاريخ بدء الإهلاك",
      openingAccumulated: "مجمع الإهلاك الافتتاحي",
      openingAccumulatedHint: "يخص الأصول المُدخلة كرصيد افتتاحي فقط. الأصل المشترى عبر النظام يأتي مجمع إهلاكه من دفتر الأستاذ.",
      depreciableAmount: "المبلغ القابل للإهلاك", accumulated: "مجمع الإهلاك",
      bookValue: "القيمة الدفترية",
      derivedNotice: "مجمع الإهلاك والقيمة الدفترية قيم محسوبة على الخادم ولا تُدخل يدوياً.",
      noEngineNotice: "لم يُرحَّل أي إهلاك بعد، لذلك القيمة الدفترية تساوي التكلفة ناقصاً المجمع الافتتاحي فقط.",
      inheritFromGroup: "أخذ افتراضيات الإهلاك من المجموعة",
      inheritFromGroupHint: "عند الإنشاء فقط: تُنسخ القيم مرة واحدة ثم تصبح ملكاً للبطاقة.",
      custodian: "أمين العهدة", department: "القسم / الإدارة",
      accountingNotice: "هذه الحسابات تأتي من مجموعة الأصل ولا تُعدَّل من البطاقة. لتغييرها عدّل المجموعة.",
      attachmentsHint: "الفاتورة، الكفالة، سند الملكية، صورة الأصل. الصورة اختيارية ولا تُشترط لإنشاء البطاقة.",
      upload: "إرفاق ملف", uploading: "جارٍ الرفع…", download: "تنزيل",
      fileName: "اسم الملف", fileSize: "الحجم", uploadedAt: "تاريخ الرفع",
      noAttachments: "لا توجد مرفقات.",
      closedNotice: "هذا الأصل خرج من دفاتر الشركة. يمكن تعديل البيانات الوصفية فقط.",
      filterGroup: "المجموعة", filterLocation: "الموقع", filterSupplier: "المورد",
      filterStatus: "الحالة",
      totalCost: "إجمالي التكلفة", totalBookValue: "إجمالي القيمة الدفترية", count: "العدد",
    },
  },
  fixedAssetsTransactions: {
    common: {
      asset: "الأصل", selectAsset: "اختر الأصل", date: "التاريخ", notes: "ملاحظات",
      description: "البيان", amount: "المبلغ", currency: "العملة", exchangeRate: "سعر الصرف",
      localAmount: "المبلغ بالعملة المحلية",
      status: "الحالة", statusDraft: "مسودة", statusApproved: "معتمد", statusPosted: "مُرحّل",
      statusCancelled: "ملغي", statusReversed: "معكوس",
      approve: "اعتماد", post: "ترحيل", cancel: "إلغاء", reverse: "عكس القيد",
      save: "حفظ", saveNew: "حفظ جديد", newDocument: "مستند جديد", edit: "تعديل",
      close: "إغلاق", refresh: "تحديث", search: "بحث", none: "بدون", actions: "إجراءات",
      number: "الرقم", noRows: "لا توجد بيانات",
      readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكنك إنشاء أو تعديل أو ترحيل أي مستند هنا.",
      loadError: "تعذّر تحميل البيانات.", saveError: "تعذّر حفظ المستند.",
      saved: "تم الحفظ.", approvedMsg: "تم الاعتماد.", postedMsg: "تم الترحيل إلى دفتر الأستاذ.",
      cancelledMsg: "تم الإلغاء.", reversedMsg: "تم عكس القيد بقيد مقابل.",
      required: "حقل مطلوب",
      reasonPrompt: "السبب (اختياري)",
      confirmPost: "سيتم إنشاء قيد محاسبي فعلي. هل تريد المتابعة؟",
      confirmReverse: "سيتم إنشاء قيد عكسي. المستند الأصلي لن يُعدّل. هل تريد المتابعة؟",
      confirmCancel: "سيتم إلغاء المستند قبل الترحيل. هل تريد المتابعة؟",
      journalVoucher: "سند القيد", notPosted: "غير مُرحّل",
      postedNotice: "هذا المستند مُرحّل. لا يمكن تعديله؛ التصحيح يتم بعكس القيد فقط.",
      condition: "الحالة الفنية", conditionGood: "جيدة", conditionFair: "مقبولة",
      conditionDamaged: "تالفة", conditionOutOfService: "خارج الخدمة",
      settlement: "طريقة السداد", settlementSupplier: "على حساب المورد",
      settlementOnAccount: "على الحساب",
      settlementCashOrBank: "نقدًا / بنك", settlementAccount: "حساب الصندوق أو البنك",
      supplier: "المورد", customer: "العميل", costCenter: "مركز التكلفة", branch: "الفرع",
      location: "الموقع", total: "الإجمالي", count: "العدد", all: "الكل",
      valuationTitle: "القيمة الحالية للأصل",
      valuationCost: "التكلفة", valuationAccumulated: "مجمع الاستهلاك",
      valuationNetBookValue: "القيمة الدفترية", valuationSalvage: "قيمة الخردة",
      valuationDepreciable: "المبلغ القابل للاستهلاك",
      valuationNotice: "هذه القيم محسوبة على الخادم من المستندات المُرحّلة فقط، ولا تُدخل يدويًا.",
    },
    purchaseOrders: {
      title: "أمر شراء الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « أمر شراء الأصول",
      purposeNotice: "سجل أوامر شراء الأصول القادمة من دورة المشتريات. الأمر لا يُنشئ قيدًا محاسبيًا؛ الفاتورة هي التي تُرحّل.",
      scopeNotice: "هذه الشاشة تعرض الأوامر ولا تُنشئها. أوامر الشراء تُنشأ في دورة المشتريات ثم تظهر هنا للتحويل إلى فاتورة أصول.",
      registerTitle: "أوامر الشراء", orderNumber: "رقم الأمر", orderDate: "تاريخ الأمر",
      expectedDate: "التاريخ المتوقع", netTotal: "الإجمالي",
      availableOnly: "غير المفوترة فقط", invoiced: "مفوتر", available: "متاح للفوترة",
      totalOnOrder: "إجمالي تحت الأمر", availableCount: "متاح للفوترة",
      createInvoice: "إنشاء فاتورة", orderStatus: "حالة الأمر",
    },
    purchaseInvoices: {
      title: "فاتورة مشتريات الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « فاتورة مشتريات الأصول",
      purposeNotice: "مستند اقتناء الأصل. كل سطر يُنشئ بطاقة أصل مستقلة برقمها الخاص، ويُرحّل الأصل إلى حساب المجموعة مقابل المورد أو الصندوق.",
      scopeNotice: "سطر واحد لكل أصل — لا توجد كمية. عشرة أجهزة تعني عشرة أسطر وعشر بطاقات، لأن لكل أصل رقمًا تسلسليًا وعهدة وموقعًا مستقلًا.",
      registerTitle: "فواتير الشراء", formTitle: "بيانات الفاتورة",
      invoiceNumber: "رقم الفاتورة", invoiceDate: "تاريخ الفاتورة",
      supplierInvoiceNumber: "رقم فاتورة المورد", purchaseOrder: "أمر الشراء",
      noPurchaseOrder: "بدون أمر شراء",
      linesTitle: "الأصول المشتراة", addLine: "إضافة أصل", removeLine: "حذف",
      assetName: "اسم الأصل (إنجليزي)", assetNameAr: "اسم الأصل (عربي)", group: "المجموعة",
      serialNumber: "الرقم التسلسلي", manufacturer: "الشركة المصنّعة", model: "الطراز",
      unitCost: "التكلفة", lineTotal: "إجمالي السطر", subtotal: "الإجمالي",
      totalAcquired: "إجمالي المقتنيات", assetCount: "عدد الأصول",
      createdCards: "بطاقات الأصول المُنشأة",
      linesRequired: "يجب إضافة سطر واحد على الأقل.",
      postNotice: "الترحيل يُنشئ القيد ويُدخل بطاقات الأصول إلى الخدمة في آنٍ واحد.",
    },
    custodyIssue: {
      title: "صرف عهدة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « صرف عهدة",
      purposeNotice: "تسليم الأصل إلى موظف كعهدة شخصية. حركة تشغيلية لا تُنشئ أي قيد محاسبي؛ ملكية الأصل لم تتغيّر.",
      scopeNotice: "لا يمكن صرف أصل عليه عهدة مفتوحة. يجب إرجاعه أولًا أو استخدام «تعديل عهدة الأصل» لنقله مباشرة.",
      registerTitle: "سجل العهد", formTitle: "صرف عهدة جديدة",
      custodian: "المستلم", issuedOn: "تاريخ الصرف", expectedReturnOn: "تاريخ الإرجاع المتوقع",
      conditionOnIssue: "الحالة عند الصرف", issue: "صرف العهدة", issued: "تم صرف العهدة.",
      openOnly: "المفتوحة فقط", openCount: "عهد مفتوحة", overdueCount: "متأخرة",
      overdue: "متأخرة", returnedOn: "تاريخ الإرجاع", conditionOnReturn: "الحالة عند الإرجاع",
      open: "مفتوحة", closed: "مغلقة",
    },
    custodyReturn: {
      title: "إخراج من العهدة / إعادة للعهدة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « إخراج من العهدة",
      purposeNotice: "إرجاع الأصل من الموظف وإغلاق العهدة المفتوحة، مع تسجيل حالته الفنية عند الإرجاع.",
      scopeNotice: "الإرجاع يتطلب عهدة مفتوحة على الأصل. إعادة الأصل إلى موقع مختلف تُسجَّل كحركة موقع مستقلة بتاريخها الخاص.",
      formTitle: "إرجاع عهدة", returnedOn: "تاريخ الإرجاع",
      conditionOnReturn: "الحالة عند الإرجاع", returnToLocation: "إعادة إلى موقع",
      keepLocation: "إبقاء الموقع الحالي", returnAction: "إرجاع العهدة",
      returned: "تم إرجاع العهدة.",
      openCustodyTitle: "العهدة المفتوحة", noOpenCustody: "لا توجد عهدة مفتوحة على هذا الأصل.",
      heldBy: "بعهدة", heldSince: "منذ",
    },
    custodyTransfer: {
      title: "تعديل عهدة الأصل",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « تعديل عهدة الأصل",
      purposeNotice: "نقل العهدة من موظف إلى آخر مباشرة: تُغلق عهدة الأول وتُفتح عهدة الثاني في حركة واحدة.",
      scopeNotice: "النقل يتطلب عهدة مفتوحة، ولا يمكن النقل إلى الموظف نفسه. الحركتان تبقيان في السجل ولا تُحذف أي منهما.",
      formTitle: "نقل العهدة", fromCustodian: "من", toCustodian: "إلى",
      transferDate: "تاريخ النقل", transfer: "نقل العهدة", transferred: "تم نقل العهدة.",
      historyTitle: "سجل حركات العهدة", movementType: "نوع الحركة",
      movementIssue: "صرف", movementReturn: "إرجاع", movementTransfer: "نقل",
      from: "من", to: "إلى", recordedAt: "وقت التسجيل",
    },
    locationMovements: {
      title: "تعديل موقع الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « تعديل موقع الأصول",
      purposeNotice: "نقل الأصل من موقع إلى آخر مع الاحتفاظ بتاريخ كامل لكل نقلة. حركة تشغيلية بلا قيد محاسبي.",
      scopeNotice: "تغيير الموقع لا يغيّر المجموعة ولا الحسابات ولا العهدة. النقل إلى الموقع نفسه مرفوض.",
      registerTitle: "سجل حركات المواقع", formTitle: "نقل أصل",
      fromLocation: "من موقع", toLocation: "إلى موقع", movementDate: "تاريخ النقل",
      reason: "السبب", move: "تنفيذ النقل", moved: "تم نقل الأصل.",
      currentLocation: "الموقع الحالي", noLocation: "بدون موقع", from: "من", to: "إلى",
    },
    insurance: {
      title: "تأمين الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « تأمين الأصول",
      purposeNotice: "وثائق التأمين على الأصول: شركة التأمين، رقم الوثيقة، مدة التغطية، القيمة المؤمّنة وقسط التأمين.",
      scopeNotice: "هذه الشاشة تسجّل الوثيقة ولا تُرحّل القسط. قسط التأمين يُصرف من شاشة «مصاريف الأصول» ثم يُربط بالوثيقة هنا.",
      registerTitle: "وثائق التأمين", formTitle: "بيانات الوثيقة",
      insurer: "شركة التأمين", policyNumber: "رقم الوثيقة", startDate: "بداية التغطية",
      endDate: "نهاية التغطية", insuredValue: "القيمة المؤمّنة", premiumAmount: "قسط التأمين",
      coverageType: "نوع التغطية", premiumExpense: "مصروف القسط",
      noPremiumExpense: "غير مربوط بمصروف",
      policyStatus: "حالة الوثيقة", statusActive: "سارية", statusExpired: "منتهية",
      statusCancelled: "ملغاة", expired: "منتهية", daysToExpiry: "أيام حتى الانتهاء",
      activeCount: "وثائق سارية", expiringSoon: "تنتهي قريبًا",
      totalInsuredValue: "إجمالي القيمة المؤمّنة",
      premiumNotice: "ربط الوثيقة بمصروف مُرحّل يجعل الأثر المحاسبي للقسط ظاهرًا من الوثيقة نفسها.",
    },
    maintenance: {
      title: "صيانة الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « صيانة الأصول",
      purposeNotice: "سجل صيانة مستقل لكل أصل: نوع الصيانة، تاريخها، الجهة المنفّذة، التكلفة، وقراءة العداد والحالة بعد الصيانة.",
      scopeNotice: "الصيانة سجل تشغيلي بتاريخها الخاص ولا تُرحّل بنفسها. تكلفة الصيانة تُصرف من شاشة «مصاريف الأصول» ثم تُربط هنا.",
      registerTitle: "سجل الصيانة", formTitle: "بيانات الصيانة",
      maintenanceNumber: "رقم الصيانة", maintenanceType: "نوع الصيانة",
      typePreventive: "وقائية", typeCorrective: "تصحيحية", typeEmergency: "طارئة",
      typeImprovement: "تحسينية", maintenanceDate: "تاريخ الصيانة",
      serviceProvider: "الجهة المنفّذة", cost: "التكلفة", costExpense: "مصروف التكلفة",
      noCostExpense: "غير مربوط بمصروف", nextMaintenanceDate: "موعد الصيانة القادمة",
      meterReading: "قراءة العداد", conditionAfter: "الحالة بعد الصيانة",
      due: "مستحقة", dueCount: "صيانات مستحقة", totalCost: "إجمالي التكلفة",
      costNotice: "الصيانة التحسينية غالبًا تُرسمل على تكلفة الأصل؛ اختر المعالجة عند صرف المصروف لا هنا.",
    },
    inventoryCounts: {
      title: "جرد الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « جرد الأصول",
      purposeNotice: "عملية جرد مستقلة لها رقمها وتاريخها: يُسحب كشف بكل أصل متوقع في الموقع، ثم يُسجَّل ما وُجد فعلًا.",
      scopeNotice: "الجرد يسجّل الواقع ولا يغيّره: لا ينقل أصلًا ولا يعدّل بطاقة ولا يُنشئ قيدًا. معالجة الفروقات تتم بحركات مستقلة.",
      registerTitle: "عمليات الجرد", sheetTitle: "كشف الجرد",
      countNumber: "رقم الجرد", countDate: "تاريخ الجرد", responsible: "المسؤول",
      openCount: "فتح جرد جديد", opened: "تم فتح الجرد وسحب الكشف.",
      complete: "إنهاء الجرد", completed: "تم إنهاء الجرد.",
      countStatus: "حالة الجرد", statusDraft: "جارٍ", statusCompleted: "منتهٍ",
      statusCancelled: "ملغي",
      lineNumber: "م", expectedLocation: "الموقع المتوقع", actualLocation: "الموقع الفعلي",
      result: "النتيجة", resultNotCounted: "لم يُجرد", resultFound: "موجود",
      resultFoundElsewhere: "وُجد في موقع آخر", resultMissing: "مفقود",
      discrepancy: "فرق", resolved: "مُعالج", markResolved: "تمت المعالجة",
      recordLine: "حفظ السطر", recorded: "تم تسجيل نتيجة الجرد.",
      totalLines: "إجمالي الأسطر", countedLines: "تم جردها", foundCount: "موجود",
      foundElsewhereCount: "في موقع آخر", missingCount: "مفقود",
      unresolved: "فروقات غير معالجة",
      backToRegister: "رجوع إلى القائمة", allLocations: "كل المواقع",
      changesNothingNotice: "«الموقع المتوقع» لقطة محفوظة وقت سحب الكشف، حتى يبقى الكشف شاهدًا صحيحًا حتى لو نُقل الأصل بعد ذلك.",
      cancelCount: "إلغاء الجرد",
    },
    depreciation: {
      title: "استهلاك الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « استهلاك الأصول",
      purposeNotice: "دورة استهلاك شهرية: يُحتسب قسط الشهر لكل أصل مؤهّل كمسودة للمراجعة، ثم تُعتمد وتُرحّل بقيد واحد.",
      scopeNotice: "لا يمكن ترحيل الفترة نفسها مرتين للمجموعة نفسها. القسط محسوب من التكلفة الحالية ولا يُدخل يدويًا.",
      registerTitle: "دورات الاستهلاك", runTitle: "تفاصيل الدورة",
      runNumber: "رقم الدورة", period: "الفترة", periodYear: "السنة", periodMonth: "الشهر",
      runDate: "تاريخ الدورة", group: "المجموعة", allGroups: "كل المجموعات",
      prepare: "احتساب الدورة", prepared: "تم احتساب الدورة كمسودة.",
      totalDepreciation: "إجمالي الاستهلاك", assetCount: "عدد الأصول",
      postedCount: "دورات مُرحّلة", totalPosted: "إجمالي المُرحّل",
      lineNumber: "م", costBasis: "التكلفة", salvageValue: "قيمة الخردة",
      method: "الطريقة", methodStraightLine: "القسط الثابت",
      methodDecliningBalance: "القسط المتناقص", usefulLifeMonths: "العمر (شهر)",
      ratePercent: "النسبة %", openingAccumulated: "مجمع أول المدة",
      charge: "قسط الشهر", closingAccumulated: "مجمع آخر المدة",
      closingBookValue: "القيمة الدفترية", capped: "مُقيّد",
      cappedNotice: "القسط المؤشّر بـ«مُقيّد» خُفّض حتى لا يتجاوز مجمع الاستهلاك المبلغ القابل للاستهلاك.",
      backToRegister: "رجوع إلى القائمة", noLines: "لا يوجد أصل مؤهّل للاستهلاك في هذه الفترة.",
      preparedNotice: "المسودة قابلة للمراجعة والإلغاء. لا يُكتب أي قيد قبل الترحيل.",
    },
    annualDepreciation: {
      title: "احتساب الاهتلاك السنوي", periodYear: "السنة", fromMonth: "من شهر", toMonth: "إلى شهر",
      preview: "معاينة", execute: "إنشاء وترحيل السند",
      previewNotice: "معاينة فقط: لم يُنشأ أي سند ولم يُرحّل أي قيد بعد.",
      voucherDate: "تاريخ السند", runCount: "عدد الأشهر", monthsCharged: "أشهر الاحتساب",
      category: "الفئة", voucherLines: "قيد الاهتلاك المقترح", account: "الحساب",
      debit: "مدين", credit: "دائن", total: "المجموع",
      confirmExecute: "سيُنشأ ويُرحّل سند اهتلاك واحد بمبلغ {total} بتاريخ {date}. متابعة؟",
      executed: "تم إنشاء وترحيل السند {voucher}.",
    },
    sales: {
      title: "بيع الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « بيع الأصول",
      purposeNotice: "بيع الأصل وإخراجه من الدفاتر: يُقفل مجمع الاستهلاك وتكلفة الأصل، ويُعترف بالربح أو الخسارة من الفرق مع الثمن.",
      scopeNotice: "البيع حركة مستقلة عن الإتلاف وعن الصيانة، ولها دورة حياة وتاريخ خاص بها. بعد الترحيل يخرج الأصل من الخدمة ولا يقبل أي حركة جديدة.",
      registerTitle: "عمليات البيع", formTitle: "بيانات البيع",
      saleNumber: "رقم البيع", saleDate: "تاريخ البيع", saleAmount: "ثمن البيع",
      costAtSale: "التكلفة عند البيع", accumulatedAtSale: "مجمع الاستهلاك عند البيع",
      bookValueAtSale: "القيمة الدفترية عند البيع", gainOrLoss: "الربح / الخسارة",
      gain: "ربح", loss: "خسارة",
      totalProceeds: "إجمالي المتحصلات", totalGainOrLoss: "صافي الربح / الخسارة",
      postedCount: "مُرحّلة",
      snapshotNotice: "التكلفة ومجمع الاستهلاك يُلتقطان لحظة الترحيل ويُحفظان على المستند، حتى تبقى قيمة الربح قابلة للتفسير لاحقًا.",
    },
    disposals: {
      title: "إتلاف الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « إتلاف الأصول",
      purposeNotice: "إخراج الأصل من الدفاتر بلا متحصلات: تُقفل التكلفة ومجمع الاستهلاك وتُحمّل القيمة الدفترية المتبقية كخسارة.",
      scopeNotice: "الإتلاف حركة مستقلة عن البيع بدورة حياة وتاريخ خاص. أصل بقيمة دفترية صفر يُتلف أيضًا، والقيد يقفل التكلفة والمجمع دون خسارة.",
      registerTitle: "عمليات الإتلاف", formTitle: "بيانات الإتلاف",
      disposalNumber: "رقم الإتلاف", disposalDate: "تاريخ الإتلاف", reason: "سبب الإتلاف",
      reasonDamaged: "تلف", reasonObsolete: "تقادم", reasonLost: "فقدان",
      reasonStolen: "سرقة", reasonDonated: "تبرع", reasonOther: "أخرى",
      costAtDisposal: "التكلفة عند الإتلاف",
      accumulatedAtDisposal: "مجمع الاستهلاك عند الإتلاف",
      bookValueAtDisposal: "القيمة الدفترية المُعدمة",
      totalWrittenOff: "إجمالي المُعدم", postedCount: "مُرحّلة",
      reasonMasterNotice: "«أسباب الإتلاف» ليست جدول بيانات أساسية في هذا الإصدار، لذلك الأسباب قائمة ثابتة وليست قابلة للإضافة.",
    },
    expenses: {
      title: "مصاريف الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « مصاريف الأصول",
      purposeNotice: "تحميل تكلفة على أصل معيّن: إما تُحمّل على الأرباح والخسائر مصروفًا، أو تُرسمل فتزيد تكلفة الأصل.",
      scopeNotice: "المعالجة المرسملة تزيد أساس الاستهلاك في الدورات القادمة ولا تُعيد احتساب دورات مُرحّلة سابقًا.",
      registerTitle: "المصاريف", formTitle: "بيانات المصروف",
      expenseNumber: "رقم المصروف", expenseDate: "تاريخ المصروف",
      expenseAccount: "حساب المصروف", treatment: "المعالجة",
      treatmentExpensed: "تحميل على المصروفات", treatmentCapitalised: "رسملة على الأصل",
      totalExpensed: "إجمالي المُحمّل", totalCapitalised: "إجمالي المُرسمل",
      postedCount: "مُرحّلة", allTreatments: "كل المعالجات",
      treatmentNotice: "اختيار «رسملة» يجعل هذا المستند جزءًا من تكلفة الأصل، ويظهر أثره في كل احتساب لاحق للقيمة.",
    },
    revenues: {
      title: "إيرادات الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « إيرادات الأصول",
      purposeNotice: "إيراد ناتج عن استخدام الأصل — إيجار أو تشغيل أو ما شابه — مع بقاء الأصل في ملكية الشركة.",
      scopeNotice: "هذه ليست شاشة بيع: الإيراد لا يمس تكلفة الأصل ولا مجمع استهلاكه ولا يُخرجه من الخدمة.",
      registerTitle: "الإيرادات", formTitle: "بيانات الإيراد",
      revenueNumber: "رقم الإيراد", revenueDate: "تاريخ الإيراد",
      revenueAccount: "حساب الإيراد", totalRevenue: "إجمالي الإيرادات",
      postedCount: "مُرحّلة",
      notASaleNotice: "لبيع الأصل نفسه استخدم شاشة «بيع الأصول»؛ فهي وحدها التي تُخرج الأصل من الدفاتر.",
    },
    revaluations: {
      title: "إعادة تقييم الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « إعادة تقييم الأصول",
      purposeNotice: "حركة مستقلة تُعيد تحديد القيمة الدفترية للأصل: الزيادة تُرحّل إلى فائض إعادة التقييم، والنقص يُحمّل خسارةً.",
      scopeNotice: "إعادة التقييم ليست تعديلًا على بطاقة الأصل: لها مستند ورقم وتاريخ ودورة اعتماد وترحيل، وتُصحّح بعكس القيد لا بالتعديل.",
      registerTitle: "عمليات إعادة التقييم", formTitle: "بيانات إعادة التقييم",
      revaluationNumber: "رقم إعادة التقييم", revaluationDate: "تاريخ إعادة التقييم",
      previousCarryingAmount: "القيمة الدفترية قبل التقييم",
      revaluedAmount: "القيمة بعد إعادة التقييم", difference: "الفرق",
      upward: "زيادة", downward: "نقص", reason: "سبب إعادة التقييم",
      totalUpward: "إجمالي الزيادة", totalDownward: "إجمالي النقص", postedCount: "مُرحّلة",
      carryingNotice: "القيمة قبل التقييم محسوبة على الخادم من المستندات المُرحّلة، وتُحدَّث فور اختيار الأصل.",
      carryingUnavailable: "اختر أصلًا لعرض قيمته الدفترية الحالية.",
    },
    splits: {
      title: "فصل الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « فصل الأصول",
      purposeNotice: "تقسيم أصل واحد إلى عدة أصول: تُوزَّع تكلفة الأصل الأم ومجمع استهلاكه على الأبناء، ثم يُقفل الأصل الأم.",
      scopeNotice: "الشركة تملك بعد الفصل ما كانت تملكه قبله بالضبط: مجموع تكلفة الأبناء ومجموع مجمع استهلاكهم يجب أن يساويا الأم تمامًا، بلا هامش سماح.",
      registerTitle: "عمليات الفصل", formTitle: "بيانات الفصل",
      splitNumber: "رقم الفصل", splitDate: "تاريخ الفصل", parentAsset: "الأصل الأم",
      postedCount: "منفّذة",
      parentCost: "تكلفة الأم", parentAccumulated: "مجمع استهلاك الأم",
      parentBookValue: "القيمة الدفترية للأم", reason: "سبب الفصل",
      childCount: "عدد الأبناء",
      linesTitle: "الأصول الناتجة", addLine: "إضافة أصل ابن", removeLine: "حذف",
      childAssetNumber: "رقم الأصل", childName: "الاسم (إنجليزي)",
      childNameAr: "الاسم (عربي)", group: "المجموعة",
      allocatedCost: "التكلفة الموزّعة", allocatedAccumulated: "المجمع الموزّع",
      allocatedBookValue: "القيمة الدفترية",
      allocatedTotals: "إجمالي التوزيع", balanced: "التوزيع مطابق",
      notBalanced: "التوزيع غير مطابق",
      balanceNotice: "لا يمكن تنفيذ الفصل ما لم يطابق التوزيع الأصل الأم في التكلفة والمجمع معًا.",
      complete: "تنفيذ الفصل", completed: "تم الفصل وإنشاء الأصول الناتجة.",
      minimumLines: "الفصل يتطلب أصلين ناتجين على الأقل.",
      backToRegister: "رجوع إلى القائمة",
    },
  },
  fixedAssetsProcurement: {
    custodyDeposit: {
      title: "إيداع عهدة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « إيداع عهدة",
      purposeNotice: "إدخال الأصل في عهدة أمين مخزن للحفظ، مع تحديد موقع الإيداع. يفتح عهدة قائمة تماماً كما يفعل صرف العهدة.",
      scopeNotice: "الإيداع عكس «صرف عهدة» وليس عكس «إخراج من العهدة»: الصرف يُخرج الأصل للاستخدام، والإيداع يُدخله للحفظ، وكلاهما يفتح عهدة. أما الإخراج فيُغلق عهدة قائمة. لا يوجد أي قيد محاسبي.",
      formTitle: "إيداع عهدة جديدة", registerTitle: "العهد القائمة",
      keeper: "أمين العهدة", depositedOn: "تاريخ الإيداع",
      depositLocation: "موقع الإيداع", keepLocation: "إبقاء الموقع الحالي",
      conditionOnDeposit: "الحالة عند الإيداع", expectedReturnOn: "تاريخ الإخراج المتوقع",
      deposit: "تنفيذ الإيداع", deposited: "تم إيداع الأصل في العهدة.",
      movementIssue: "صرف", movementReturn: "إرجاع", movementTransfer: "نقل",
      movementDeposit: "إيداع", movementType: "نوع الحركة",
      heldNotice: "لا يمكن إيداع أصل عليه عهدة قائمة. يجب إخراجه من العهدة أولاً.",
      openCount: "عهد قائمة",
    },
    purchaseReturns: {
      title: "مردودات مشتريات أصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « مردودات مشتريات أصول",
      purposeNotice: "رد أصل مقتنى إلى المورد على فاتورة مشتريات مُرحّلة: يُعكس أثر الاقتناء، وتخرج بطاقة الأصل من الدفاتر بحالة «مرتجع للمورد».",
      scopeNotice: "المردود ليس حذفاً للفاتورة ولا للبطاقة: الشركة اشترت الأصل فعلاً ثم ردّته، والسجل يحفظ الواقعتين. المورد والعملة وسعر الصرف والتكلفة كلها من الفاتورة الأصلية ولا تُدخل يدوياً.",
      registerTitle: "المردودات", formTitle: "بيانات المردود",
      returnNumber: "رقم المردود", returnDate: "تاريخ المردود",
      sourceInvoice: "الفاتورة الأصلية", selectInvoice: "اختر فاتورة مُرحّلة",
      supplierInvoice: "المورد",
      reason: "سبب الرد", reasonDefective: "معيب",
      reasonNotAsSpecified: "غير مطابق للمواصفات", reasonOverDelivered: "تسليم زائد",
      reasonPurchaseCancelled: "إلغاء الشراء", reasonOther: "أخرى",
      referenceNumber: "رقم إشعار المورد", returnAmount: "قيمة المردود",
      originalCost: "التكلفة الأصلية",
      assetsTitle: "الأصول المرتجعة", returnable: "قابل للرد", notReturnable: "غير قابل للرد",
      blockedReason: "السبب", selectAll: "تحديد الكل",
      assetCount: "عدد الأصول", totalReturned: "إجمالي المردود", postedCount: "مُرحّلة",
      eligibilityNotice: "الأصل المُباع أو المُتلف أو المفصول أو الذي عليه استهلاك مُرحّل لا يمكن رده. عند وجود استهلاك يجب عكس دفعة الاستهلاك أولاً — ولا يتم أي تصحيح خفي.",
      postNotice: "الترحيل يُنشئ قيد المردود ويُخرج بطاقات الأصول من الدفاتر بحالة «مرتجع للمورد».",
      nothingReturnable: "لا يوجد على هذه الفاتورة أصل قابل للرد.",
      returnedStatus: "مرتجع للمورد",
    },
    expenseReturns: {
      title: "مردودات مصاريف الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « مردودات مصاريف الأصول",
      purposeNotice: "رد مبلغ مصروف أصل مُرحّل، كلياً أو جزئياً. جهة الرد تتبع معالجة المصروف الأصلي: المُحمّل يُرد لحساب المصروف، والمُرسمل يُرد لحساب الأصل.",
      scopeNotice: "المعالجة تُنسخ من المصروف الأصلي ولا تُختار من جديد، ومجموع المردودات لا يتجاوز قيمة المصروف. المستند الأصلي لا يُعدّل ولا يُحذف.",
      registerTitle: "مردودات المصاريف", formTitle: "بيانات المردود",
      returnNumber: "رقم المردود", returnDate: "تاريخ المردود",
      sourceExpense: "المصروف الأصلي", selectExpense: "اختر مصروفاً مُرحّلاً",
      sourceAmount: "قيمة المصروف", alreadyReturned: "المردود سابقاً",
      remainingReturnable: "المتبقي القابل للرد", returnAmount: "قيمة المردود",
      treatment: "معالجة المصروف الأصلي", treatmentExpensed: "محمّل على المصروفات",
      treatmentCapitalised: "مُرسمل على الأصل",
      reason: "سبب الرد", totalReturned: "إجمالي المردود",
      totalCapitalisedReturned: "إجمالي المُرسمل المردود", postedCount: "مُرحّلة",
      capitalisedNotice: "هذا المصروف كان مُرسملاً، لذلك يُقيَّد الرد على حساب الأصل نفسه وتنخفض تكلفة الأصل تلقائياً دون تعديل أي حقل في البطاقة.",
      treatmentLockedNotice: "المعالجة مأخوذة من المصروف الأصلي ولا يمكن تغييرها هنا.",
      noneReturnable: "لا يوجد مصروف مُرحّل عليه رصيد قابل للرد.",
    },
    transfers: {
      title: "تحويل أصل / مادة",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « تحويل أصل / مادة",
      purposeNotice: "مستند تحويل له طرفان: إرسال واستلام. ينقل الأصل بين المواقع والفروع، ويمكنه أيضاً تغيير تصنيف الأصل (المجموعة) مع حفظ القديم والجديد.",
      scopeNotice: "التحويل مستند بحالة، و«تعديل موقع أصل» خطوة واحدة فورية — الفرق أن الأصل هنا يبقى «قيد التحويل» بين الإرسال والاستلام. الاستلام هو ما ينقل الأصل فعلاً، وهو وحده ما قد يُنشئ قيد إعادة تصنيف.",
      registerTitle: "التحويلات", formTitle: "بيانات التحويل",
      transferNumber: "رقم التحويل", transferDate: "تاريخ التحويل",
      fromSide: "من", toSide: "إلى",
      fromLocation: "الموقع الحالي", toLocation: "الموقع الجديد",
      fromBranch: "الفرع الحالي", toBranch: "الفرع الجديد",
      fromGroup: "المجموعة الحالية", toGroup: "المجموعة الجديدة",
      fromCustodian: "العهدة الحالية", toCustodian: "العهدة الجديدة",
      keepGroup: "إبقاء المجموعة", keepLocation: "إبقاء الموقع",
      keepBranch: "إبقاء الفرع", keepCustodian: "إبقاء العهدة",
      reason: "سبب التحويل",
      transferStatus: "حالة التحويل", statusDraft: "مسودة", statusApproved: "معتمد",
      statusDispatched: "مُرسل", statusReceived: "مُستلم", statusCancelled: "ملغي",
      dispatch: "إرسال", dispatched: "تم إرسال الأصل.",
      receive: "استلام", received: "تم استلام الأصل وتنفيذ التحويل.",
      dispatchedOn: "تاريخ الإرسال", receivedOn: "تاريخ الاستلام",
      conditionOnDispatch: "الحالة عند الإرسال", conditionOnReceipt: "الحالة عند الاستلام",
      inTransit: "قيد التحويل", inTransitCount: "قيد التحويل",
      receivedCount: "مُستلمة", reclassified: "أُعيد تصنيفه",
      reclassifiedCount: "إعادة تصنيف",
      willReclassify: "سيُنشئ الاستلام قيد إعادة تصنيف لاختلاف الحسابات.",
      willNotReclassify: "لن يُنشئ الاستلام أي قيد: الحسابات واحدة قبل التحويل وبعده.",
      costAtTransfer: "التكلفة عند التحويل",
      accumulatedAtTransfer: "مجمع الاستهلاك عند التحويل",
      reclassificationNotice: "إعادة التصنيف تنقل التكلفة ومجمع الاستهلاك معاً، فلا تتغير القيمة الدفترية ولا يُنشأ ربح أو خسارة.",
      receiptNotice: "الاستلام ينفّذ النقل: يُسجّل حركة موقع في السجل نفسه الذي تستخدمه شاشة «تعديل موقع أصل»، ويحدّث الفرع والمجموعة والعهدة.",
      dispatchNotice: "بعد الإرسال لا يمكن إلغاء التحويل: الأصل غادر مكانه فعلاً.",
    },
    worksheet: {
      title: "ورقة جرد الأصول",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « ورقة جرد الأصول",
      purposeNotice: "ورقة نتيجة عملية جرد قائمة: الأصول المتوقعة، وما وُجد فعلاً، والفروقات — للعرض والطباعة.",
      scopeNotice: "شاشة قراءة فقط. لا تُنشئ جرداً جديداً ولا تُعدّل سطراً ولا تُحرّك أصلاً؛ الجرد نفسه يُدار من شاشة «جرد الأصول».",
      selectCount: "اختر عملية الجرد", sheetTitle: "ورقة الجرد",
      countNumber: "رقم الجرد", countDate: "تاريخ الجرد", responsible: "المسؤول",
      discrepanciesOnly: "الفروقات فقط", print: "طباعة",
      lineNumber: "م", serialNumber: "الرقم التسلسلي", group: "المجموعة",
      custodian: "العهدة",
      expectedLocation: "الموقع المتوقع", actualLocation: "الموقع الفعلي",
      result: "النتيجة", difference: "الفرق",
      notCounted: "لم يُجرد", counted: "تم جرده", discrepancies: "فروقات",
      unresolved: "غير معالجة", resolved: "مُعالج",
      readOnlyNotice: "هذه الورقة للعرض والطباعة فقط ولا تُغيّر أي بيانات.",
      noCountSelected: "اختر عملية جرد لعرض ورقتها.",
    },
    rfq: {
      title: "طلب عروض أسعار",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « طلب عروض أسعار",
      purposeNotice: "طلب عروض أسعار من عدة موردين قبل الشراء. مستند مشتريات مشترك على مستوى النظام، مع تحديد الغرض عند كونه لاقتناء أصل ثابت.",
      scopeNotice: "لا يوجد أي أثر محاسبي في أي مرحلة: الطلب سؤال يُوجَّه للموردين ولا يُلزم الشركة بشيء. الالتزام يبدأ بأمر الشراء وينشأ الدين بفاتورة المورد.",
      registerTitle: "طلبات عروض الأسعار", formTitle: "بيانات الطلب",
      requestNumber: "رقم الطلب", requestDate: "تاريخ الطلب",
      requiredDate: "التاريخ المطلوب", closingDate: "آخر موعد للعروض",
      subject: "موضوع الطلب", terms: "الشروط",
      purpose: "الغرض", purposeGeneral: "مشتريات عامة", purposeFixedAsset: "اقتناء أصل ثابت",
      assetGroup: "مجموعة الأصول", intendedLocation: "الموقع المقصود",
      requestStatus: "حالة الطلب", statusDraft: "مسودة", statusIssued: "مُرسل",
      statusAwarded: "معتمد", statusCancelled: "ملغي",
      linesTitle: "بنود الطلب", addLine: "إضافة بند", removeLine: "حذف",
      description: "البيان", specification: "المواصفات", unit: "الوحدة", quantity: "الكمية",
      suppliersTitle: "الموردون المدعوون", addSupplier: "إضافة مورد",
      invitedOn: "تاريخ الدعوة", responded: "قدّم عرضاً", notResponded: "لم يقدّم عرضاً",
      supplierCount: "الموردون", quotationCount: "العروض",
      issue: "إرسال للموردين", issued: "تم إرسال الطلب للموردين.",
      issueNotice: "بعد الإرسال لا يمكن تعديل الطلب: ما سُئل عنه الموردون لا يتغيّر من تحتهم.",
      draftCount: "مسودات", issuedCount: "مُرسلة", awardedCount: "معتمدة",
      noAccountingNotice: "لا قيد محاسبي.",
      awardedTo: "اعتُمد لصالح",
    },
    quotations: {
      title: "عروض أسعار الموردين", sectionTitle: "العروض المستلمة",
      formTitle: "تسجيل عرض مورد",
      supplier: "المورد", supplierReference: "مرجع المورد",
      quotationDate: "تاريخ العرض", validUntil: "صالح حتى", lapsed: "منتهي الصلاحية",
      deliveryDays: "مدة التوريد (يوم)", paymentTermDays: "مهلة السداد (يوم)",
      commercialNotes: "ملاحظات تجارية", technicalNotes: "ملاحظات فنية",
      unitPrice: "سعر الوحدة", discount: "الخصم", taxPercent: "الضريبة %",
      lineTotal: "إجمالي السطر", netTotal: "صافي العرض",
      record: "حفظ العرض", recorded: "تم تسجيل العرض.",
      withdraw: "سحب العرض", withdrawn: "تم سحب العرض.",
      quotationStatus: "حالة العرض", statusReceived: "مستلم", statusWithdrawn: "مسحوب",
      notInvitedNotice: "لا يمكن تسجيل عرض من مورد غير مدعو لهذا الطلب.",
      issueFirstNotice: "لا تُسجَّل العروض إلا على طلب مُرسل.",
      noQuotations: "لم يُسجَّل أي عرض على هذا الطلب بعد.",
      liveCount: "عروض قائمة", answersLine: "يقابل البند",
    },
    comparison: {
      title: "مقارنة عروض الأسعار",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « مقارنة عروض الأسعار",
      purposeNotice: "مصفوفة تقارن العروض المسجّلة فعلاً على طلب واحد: السعر والإجمالي والعملة ومدة التوريد وشروط السداد والصلاحية.",
      scopeNotice: "شاشة قراءة لا تحفظ شيئاً ولا تخترع رقماً: كل قيمة هنا مأخوذة من عرض مورد مسجّل. التحويل بين العملات يتم بسعر الصرف المسجّل على العرض نفسه، وما لا يمكن تحويله يُعرض بعملته ويُستثنى من مؤشرات «الأقل سعراً».",
      matrixTitle: "مصفوفة المقارنة", selectRequest: "اختر طلب عروض الأسعار",
      noRequestSelected: "اختر طلباً لعرض مقارنة عروضه.",
      requestedLine: "البند المطلوب", lowest: "الأقل", lowestOverall: "الأقل إجمالاً",
      awarded: "معتمد", didNotQuote: "لم يقدّم سعراً", partialOffer: "عرض جزئي",
      invited: "مدعوون", responded: "قدّموا عروضاً",
      currencyWarning: "العروض بعملات مختلفة. المقارنة تستخدم سعر الصرف المسجّل على كل عرض.",
      notComparable: "غير قابل للمقارنة", ownCurrency: "بعملة العرض",
      comparableTotal: "الإجمالي المقارن", delivery: "التوريد",
      paymentTerms: "السداد", validity: "الصلاحية", print: "طباعة",
      noQuotationsNotice: "لا يوجد عرض قائم على هذا الطلب لمقارنته.",
      readOnlyNotice: "لا قيد محاسبي ولا تعديل بيانات من هذه الشاشة.",
    },
    award: {
      title: "اعتماد عروض الأسعار",
      breadcrumb: "المحاسبة العامة « الأصول الثابتة « الحركات اليومية « اعتماد عروض الأسعار",
      purposeNotice: "اختيار العرض الفائز على طلب عروض الأسعار، مع حفظ العرض المعتمد والمعتمِد وتاريخ الاعتماد وسببه.",
      scopeNotice: "الاعتماد قرار مُسجَّل وليس مجرد علامة عرض. لا يُنشئ أي قيد محاسبي؛ الالتزام يبدأ بأمر الشراء الذي يليه.",
      selectRequest: "اختر طلب عروض الأسعار",
      wholeAward: "اعتماد عرض كامل", splitAward: "اعتماد موزّع على البنود",
      awardMode: "نمط الاعتماد",
      awardDate: "تاريخ الاعتماد", awardReason: "سبب الاعتماد",
      award: "تنفيذ الاعتماد", awarded: "تم اعتماد العرض.",
      chooseQuotation: "العرض الفائز", chooseePerLine: "العرض الفائز لهذا البند",
      awardedQuantity: "الكمية المعتمدة",
      alreadyAwardedNotice: "تم اعتماد هذا الطلب مسبقاً ولا يمكن اعتماده مرة أخرى.",
      noAccountingNotice: "لا قيد محاسبي.",
      awardedBy: "اعتمده", awardedOn: "بتاريخ", unawarded: "بدون اعتماد",
      confirmAward: "سيتم تسجيل قرار الاعتماد نهائياً. هل تريد المتابعة؟",
      feedsOrderNotice: "الطلب المعتمد يُغذّي أمر شراء الأصول ثم فاتورة مشتريات الأصول.",
    },
  },
    hrEmployeeReports: { category: "تقارير سجل الموظفين", documentsCategory: "تقارير الوثائق والعقود" },
    hrInquiries: {
      title: "استعلامات شؤون الموظفين",
      description: "كل استعلام يجيب بقائمة، وكل مرشّح فيه اختياري — بما في ذلك الموظف.",
      filters: "المرشّحات", all: "الكل", none: "—",
      apply: "عرض", reset: "إعادة تعيين", refresh: "تحديث", search: "بحث",
      loadFailed: "تعذّر تحميل النتائج", noResults: "لا توجد نتائج",
      employee: "الموظف", employeeNumber: "رقم الموظف", fromDate: "من تاريخ", toDate: "إلى تاريخ",
      department: "القسم", status: "الحالة", jobTitle: "المسمى الوظيفي", hireDate: "تاريخ التعيين",
      responsibleEntity: "الجهة المسؤولة", activeOnly: "على رأس العمل",
      number: "الرقم", documentTitle: "اسم الوثيقة", documentType: "نوع الوثيقة",
      documentNumber: "رقم الوثيقة", issueDate: "تاريخ الإصدار", expiryDate: "تاريخ الانتهاء",
      daysToExpiry: "المتبقي (يوم)",
      expiryWindow: "المدى", alreadyExpired: "المنتهية", withinDays: "خلال {n} يوماً",
      noExpiry: "بلا انتهاء", expiredSince: "منتهية منذ {n}",
      effectiveDate: "تاريخ السريان", changeKind: "نوع التغيير",
      sourceDocument: "المستند المصدر", notes: "ملاحظات",
      transactionKind: "نوع الحركة",
      assetNumber: "رمز الأصل", assetName: "الأصل", handedOver: "تاريخ التسليم",
      dueBack: "الإعادة المتوقعة", returned: "تاريخ الإعادة",
      custodyState: "الحالة", stillOut: "لم تُعَد",
      branch: "الفرع", asOfDate: "كما في تاريخ", withinDaysFilter: "خلال (يوم)", totals: "الإجماليات",
      print: "طباعة PDF", exportExcel: "تصدير Excel",
      pageOf: "صفحة {page} من {pages}", nextPage: "التالي", previousPage: "السابق",
      activeEmployeesOnly: "الموظفون الفعّالون فقط",
      contractType: "نوع العقد", contractStatus: "حالة العقد",
      expiryFrom: "انتهاء من", expiryTo: "انتهاء إلى",
      contractStatuses: {
        1: "لم يبدأ", 2: "ساري", 3: "منتهٍ", 4: "مفسوخ", 5: "مُجدَّد",
      },
      changeKinds: {
        1: "الفرع", 2: "القسم", 3: "المسمى الوظيفي", 4: "الدرجة",
        5: "التصنيف الفني", 6: "المدير المباشر", 7: "المشرف", 8: "حالة التوظيف",
        9: "مركز التكلفة", 10: "العقد", 11: "المسمى الثانوي", 12: "تصنيف الموظف",
      },
      employmentStatuses: {
        1: "على رأس العمل", 2: "في إجازة", 3: "موقوف", 4: "منتهية خدمته", 5: "مستقيل",
      },
      screens: {
        jobTitleChanges: {
          title: "المسمى الوظيفي",
          description: "كل تغيير مسمى وظيفي: من أي مسمى، إلى أي مسمى، ومتى سرى.",
        },
        departureRequests: {
          title: "طلبات المغادرات",
          description: "طلبات المغادرة بكل حالاتها — مسودة، مُرسلة، معتمدة، مرفوضة.",
        },
        departureAmendments: {
          title: "حركات المغادرات",
          description: "تعديلات المغادرات المعتمدة: الإلغاء والتمديد والقطع.",
        },
        iqamaMovements: {
          title: "حركات الإقامات",
          description: "حركات وثائق الموظفين مُرشَّحة على نوع «إقامة». لا يوجد سجل منفصل للإقامات — الإقامة نوع وثيقة.",
        },
        healthCertificates: {
          title: "حركات الشهادات الصحية",
          description: "نفس سجل حركات وثائق الموظفين، مُرشَّحاً على نوع «شهادة صحية».",
        },
        disciplinaryActions: {
          title: "العقوبات والجزاءات",
          description: "المخالفات والإجراءات التأديبية المسجّلة على الموظفين.",
        },
        promotions: {
          title: "ترقية موظف",
          description: "الترقيات: الدرجة والمسمى قبل الترقية وبعدها، وتاريخ السريان.",
        },
        transfers: {
          title: "نقل موظف",
          description: "نقل الموظفين بين الفروع والأقسام ومراكز التكلفة.",
        },
        overtimeRequests: {
          title: "طلبات العمل الاضافي",
          description: "طلبات العمل الإضافي وحالتها.",
        },
        companyDocuments: {
          title: "وثائق الشركة المنتهية",
          description: "السجل التجاري والرخص والشهادات — ما انتهى منها وما قارب. تفتح على «المنتهية»، ويمكن توسيع المدى.",
        },
        employeeDirectory: {
          title: "معلومات الموظفين الادارية",
          description: "الرقم والاسم والقسم والمسمى والجهة وتاريخ التعيين والحالة. لا يعرض شيئاً شخصياً — لا هوية ولا راتب ولا سجل طبي.",
        },
        employeeActions: {
          title: "إجراءات الموظفين",
          description: "سجل ما تغيّر على الموظف ومتى وبأي مستند — نقل أو ترقية أو تغيير مسمى، كلها على صف واحد الشكل.",
        },
        employeeCustody: {
          title: "عهدة الموظف",
          description: "الأصول المسلّمة للموظفين وما لم يُعَد منها. مصدرها سجل عهد الأصول، لا جدول في شؤون الموظفين.",
        },
        dailyRegister: {
          title: "الحركات اليومية",
          description: "كل حركات شؤون الموظفين على قائمة واحدة. تعرض فقط الأنواع التي يملك المستخدم صلاحية رؤيتها.",
        },
      },
    },
    hrSettings: {
      title: "إعدادات شؤون الموظفين",
      description: "المفردات التي تختار منها بطاقة الموظف وحركاته اليومية، وقواعد الإجازات. لا شيء منها مُعبّأ مسبقاً.",
      cardGroup: "مفردات بطاقة الموظف",
      dailyGroup: "مفردات الحركات اليومية",
      rulesGroup: "القواعد",
      add: "إضافة", edit: "تعديل", save: "حفظ", saving: "جارٍ الحفظ…", cancel: "إلغاء",
      search: "بحث", code: "الرمز", nameAr: "الاسم بالعربية", nameEn: "الاسم بالإنجليزية",
      status: "الحالة", active: "فعال", retired: "موقوف",
      activate: "إعادة تفعيل", deactivate: "إيقاف",
      newEntry: "بند جديد", editEntry: "تعديل البند",
      emptyList: "لا توجد بنود بعد",
      noPermission: "لا تملك صلاحية التعديل",
      saved: "تم الحفظ", activated: "أُعيد التفعيل", deactivated: "تم الإيقاف",
      loadFailed: "تعذّر تحميل القائمة", saveFailed: "تعذّر الحفظ",
      groupIdentity: "التعريف",
      groupNature: "طبيعة الإجازة",
      groupDurations: "المدد",
      groupEntitlement: "الرصيد السنوي",
      groupEntitlementHint: "اتركه فارغاً إذا كانت الإجازة لا تُرصَّد سنوياً. الفراغ ليس صفراً.",
      screens: {
        leaveTypes: {
          title: "إعدادات الإجازات",
          description: "قواعد كل نوع إجازة: الرصيد السنوي، أدنى وأقصى مدة، مدة الإشعار، وجوب الوثيقة، والترحيل للسنة التالية. هنا — لا في الكود — يُذكر نظام العمل الذي تعمل به الشركة.",
        },
        religions: {
          title: "الديانات",
          description: "الديانة كما تُدوَّن على بطاقة الموظف.",
        },
        departments: {
          title: "الأقسام",
          description: "الأقسام التي يتبع لها الموظفون. تقارير الإجازات وأرصدتها تُجمَّع عليها.",
        },
        jobTitles: {
          title: "المسميات الوظيفية",
          description: "المسمى الوظيفي الأساسي والثانوي على بطاقة الموظف.",
        },
        employmentTypes: {
          title: "أنواع التوظيف",
          description: "طبيعة التوظيف — دوام كامل، جزئي، مؤقت… كما تسمّيها الشركة.",
        },
        technicalClassifications: {
          title: "التصنيفات الفنية",
          description: "التصنيف الفني للموظف.",
        },
        grades: {
          title: "الدرجات",
          description: "الدرجة الوظيفية.",
        },
        employeeClassifications: {
          title: "تصنيفات الموظفين",
          description: "تصنيف الموظف كما تعتمده الشركة.",
        },
        contractTypes: {
          title: "أنواع العقود",
          description: "نوع العقد الذي يُبرَم مع الموظف.",
        },
        socialInsurancePolicies: {
          title: "سياسات التأمينات الاجتماعية",
          description: "سياسة التأمينات التي يُسجَّل عليها الموظف. لا يُحتسب منها أي استقطاع — لا يوجد محرّك رواتب بعد.",
        },
        healthInsurancePolicies: {
          title: "سياسات التأمين الصحي",
          description: "سياسة التأمين الصحي التي يُسجَّل عليها الموظف.",
        },
        departureTypes: {
          title: "أنواع المغادرات",
          description: "أنواع المغادرة — شخصية، رسمية، مرضية… تختارها حركة المغادرة اليومية.",
        },
        terminationReasons: {
          title: "أسباب إنهاء الخدمة",
          description: "سبب إنهاء الخدمة — استقالة، فسخ عقد، انتهاء مدة…",
        },
        violationTypes: {
          title: "أنواع المخالفات",
          description: "أنواع المخالفات التي تُسجَّل على الموظف.",
        },
        disciplinaryActionTypes: {
          title: "أنواع الإجراءات التأديبية",
          description: "الإجراء التأديبي — إنذار، خصم، إيقاف…",
        },
        employeeDocumentTypes: {
          title: "أنواع وثائق الموظفين",
          description: "أنواع وثائق الموظف — إقامة، جواز، رخصة، شهادة صحية…",
        },
        letterTypes: {
          title: "أنواع الكتب الإدارية",
          description: "أنواع الكتب الإدارية التي تصدرها الموارد البشرية.",
        },
        injuryTypes: {
          title: "أنواع الإصابات",
          description: "أنواع إصابات العمل المسجَّلة في الملف الصحي.",
        },
        medicalRecordTypes: {
          title: "أنواع السجلات الطبية",
          description: "أنواع السجلات الطبية للموظف.",
        },
        companyDocumentTypes: {
          title: "أنواع وثائق الشركة",
          description: "أنواع وثائق الشركة التي تتابع الموارد البشرية تواريخ انتهائها.",
        },
        policyCategories: {
          title: "تصنيفات الأنظمة والتشريعات",
          description: "تصنيف الأنظمة واللوائح الداخلية المنشورة على الموظفين.",
        },
        clearanceDepartments: {
          title: "جهات براءة الذمة",
          description: "الجهة المسؤولة عن بند براءة الذمة — موارد بشرية، مالية، تقنية معلومات…",
        },
      },
    },
    fixedAssetsSettings: {
      search: "بحث", add: "إضافة", edit: "تعديل", save: "حفظ", saving: "جارٍ الحفظ…",
      cancel: "إلغاء", choose: "اختر…", none: "بدون", active: "مفعّل", inactive: "غير مفعّل",
      inUse: "مستخدم في مستندات", activate: "تفعيل", deactivate: "إيقاف",
      activated: "تم التفعيل.", deactivated: "تم الإيقاف. لن يظهر في المستندات الجديدة، ولم يتغيّر أي مستند قائم.",
      saved: "تم الحفظ.",
      emptyList: "لا توجد سجلات بعد. أضف أول سجل عندما تحتاجه — لا تُنشأ قيم افتراضية تلقائياً.",
      noPermission: "لا تملك صلاحية تعديل إعدادات الأصول الثابتة.",
      readOnlyNotice: "لديك صلاحية العرض فقط؛ الحفظ متاح لمن يملك صلاحية إدارة الإعدادات.",
      newEntry: "سجل جديد", editEntry: "تعديل السجل",
      saveFailed: "تعذّر إتمام العملية. راجع القيم وحاول مرة أخرى.",
      treatmentHint: "تحدد ما إذا كان المصروف يُحمّل على المصروفات أم يُرسمل على الأصل. المستند يحتفظ بنسخته الخاصة عند الحفظ.",
      classificationHint: "التصنيف المحاسبي الذي يقابل هذا السبب. تسمية الشركة تظهر على المستند، والتصنيف هو ما تقرأه المحاسبة.",
      defaultAccountHint: "حساب افتراضي يُقترح على المستند، ويمكن تغييره. ليس قاعدة ترحيل.",
      textHint: "نص الشرط. يُنسخ إلى الطلب عند اختياره، فيبقى الطلب حاملاً الصياغة التي صدر بها.",
      columns: {
        code: "الرمز", name: "الاسم بالإنجليزي", nameAr: "الاسم بالعربي",
        treatment: "المعالجة", classification: "التصنيف", defaultAccount: "الحساب الافتراضي",
        text: "النص", notes: "ملاحظات", status: "الحالة",
      },
      treatment: { expensed: "محمّل على المصروفات", capitalised: "مُرسمل على الأصل" },
      disposalReason: {
        1: "تالف", 2: "متقادم", 3: "مفقود", 4: "مسروق", 5: "متبرَّع به", 99: "أخرى",
      },
      frequency: { monthly: "شهري" },
      groups: { depreciation: "الاستهلاك", documents: "المستندات", numbering: "الترقيم" },
      fields: {
        frequency: "دورية الاستهلاك",
        frequencyHint: "محرك الاستهلاك يحتسب شهرياً، والقاعدة المثبتة هي قيد استهلاك واحد لكل أصل في كل شهر. لا تُدعم دورية أخرى.",
        defaultUsefulLife: "العمر الإنتاجي الافتراضي (شهر)",
        defaultUsefulLifeHint: "يُستخدم عند إنشاء بطاقة أصل لا تحدد مجموعتها ولا أي مجموعة أعلى منها عمراً إنتاجياً.",
        defaultSalvage: "نسبة القيمة التخريدية الافتراضية %",
        defaultSalvageHint: "على القاعدة نفسها: المجموعة أقرب، وهذه هي الشبكة الأخيرة.",
        requireDisposalReason: "إلزام اختيار سبب إتلاف معرّف",
        requireDisposalReasonHint: "عند التفعيل، يُرفض أي إتلاف لا يحدد سبباً من القائمة — يُتحقق منه على الخادم.",
        requireExpenseType: "إلزام اختيار نوع مصروف معرّف",
        requireExpenseTypeHint: "عند التفعيل، يُرفض أي مصروف أصل لا يحدد نوعاً من القائمة.",
        autoNumber: "ترقيم تلقائي للأصول",
        autoNumberHint: "عند التفعيل، تُعطى البطاقة الجديدة التي تُترك بلا رقم الرقمَ التالي في التسلسل، ويتقدّم العدّاد ضمن عملية الحفظ نفسها.",
        prefix: "البادئة", padding: "عدد الخانات",
        paddingHint: "بين 1 و 12.",
        nextValue: "الرقم التالي",
        nextValueHint: "يُحفظ عدّاداً، لا يُشتق من أكبر رقم موجود.",
        preview: "شكل الرقم التالي",
      },
      screens: {
        assetModels: { title: "موديلات الأصول", description: "قائمة الموديلات التي تختار منها بطاقة الأصل." },
        brands: { title: "العلامات التجارية", description: "قائمة العلامات التي تختار منها بطاقة الأصل." },
        assetClasses: { title: "أصناف الأصول", description: "تصنيف وصفي تحمله بطاقة الأصل." },
        assetCategories: { title: "فئات الأصول", description: "تبويب وصفي تحمله بطاقة الأصل." },
        assetProperties: { title: "خصائص الأصول", description: "مفردات الخصائص التي تصف بها الشركة أصولها." },
        shippingMethods: { title: "طرق الشحن", description: "تُقرأ في مستندات المشتريات." },
        expenseTypes: { title: "أنواع المصروفات", description: "لكل نوع معالجة — محمّل أو مُرسمل — وحساب افتراضي، ويقرأهما مستند مصروف الأصل." },
        salesTypes: { title: "أنواع المبيعات", description: "تُقرأ في مستند بيع الأصل. مستقلة تماماً عن إيرادات الأصول." },
        disposalReasons: { title: "أسباب إتلاف الأصول", description: "تسمية الشركة لسبب الإتلاف، ولكل سبب تصنيف محاسبي هو ما تقرأه القيود." },
        assetTypes: { title: "أنواع الأصول", description: "تصنيف وصفي تحمله بطاقة الأصل." },
        deliveryMethods: { title: "طرق التسليم", description: "تُقرأ في مستندات المشتريات." },
        purchaseRoutes: { title: "مسارات المشتريات", description: "تُقرأ في مستندات المشتريات." },
        quotationTerms: { title: "شروط عروض الأسعار", description: "نص الشرط يُنسخ إلى طلب عرض السعر عند اختياره." },
        system: { title: "إعدادات النظام", description: "سلوك موديول الأصول الثابتة لهذه الشركة. كل حقل هنا يقرأه شيء فعلي، ومكتوب بجانبه ما يقرأه." },
        coding: { title: "إعدادات ترميز الأصول", description: "كيف تُرقَّم بطاقات الأصول الجديدة." },
      },
    },
  fixedAssetsInquiries: {
    readOnly: "شاشة عرض فقط: لا تُنشئ ولا تُعدّل ولا تحذف أي بيان أو قيد.",
    noRows: "لا توجد نتائج ضمن النطاق المحدد.",
    noCostCentre: "بدون مركز تكلفة",
    voucherSeriesMismatchHint: "سلسلة السند تختلف عن نوع المستند المصدر. السجل معروض كما رُحّل.",
    screens: {
      lifeCycle: {
        title: "دورة حياة الأصل",
        description: "كل ما حدث لأصل واحد منذ اقتنائه: العهدة والمواقع والتحويلات والتأمين والصيانة والجرد والاستهلاك والمصاريف والإيرادات وإعادة التقييم والبيع والإتلاف والفصل.",
        assetRequired: "اختر أصلاً لعرض دورة حياته.",
      },
      financialMovements: {
        title: "حركات الأصول المالية",
        description: "الأثر المحاسبي على الأصول مستنداً بمستند، مع تغيّر التكلفة والاستهلاك والقيمة الدفترية المتحركة وسند القيد.",
      },
      movements: {
        title: "حركات الأصول",
        description: "الحركات التشغيلية: العهدة والمواقع والتحويلات والجرد. لا تُنشئ أي قيد محاسبي.",
      },
      expenses: {
        title: "مصاريف الأصول",
        description: "المصروف على الأصول صافياً بعد المردودات، مفصولاً بين المحمّل على المصروفات والمُرسمل على الأصل حسب المعالجة المسجّلة.",
      },
      records: {
        title: "سجلات الأصول",
        description: "سجل الأصول كما في تاريخ محدد: التكلفة والإضافات المرسملة وإعادة التقييم ومجمع الاستهلاك والقيمة الدفترية والحالة — كلها مُعاد بناؤها من المستندات المُرحّلة.",
      },
      transferStatus: {
        title: "حالة تحويل مواد الأصول",
        description: "موقف كل تحويل: مسودة أو معتمد أو مُرسل أو مُستلم، وكم يوماً بقي الأصل قيد النقل.",
      },
      tree: {
        title: "شجرة الأصول",
        description: "شجرة مجموعات الأصول بأرقام كل مجموعة وأرقامها المجمّعة مع فروعها، بحيث يُحتسب كل أصل مرة واحدة فقط.",
      },
      dailyTransactions: {
        title: "الحركات اليومية",
        description: "دفتر يومية الأصول الثابتة: كل ما سجّله الموديول من مستندات وحركات، الأحدث أولاً.",
      },
    },
    filters: {
      dateFrom: "من تاريخ", dateTo: "إلى تاريخ", asOfDate: "كما في تاريخ",
      asset: "الأصل", chooseAsset: "اختر الأصل", allAssets: "كل الأصول",
      group: "المجموعة", allGroups: "كل المجموعات", wholeTree: "الشجرة كاملة",
      location: "الموقع", allLocations: "كل المواقع",
      supplier: "المورد", allSuppliers: "كل الموردين",
      branch: "الفرع", allBranches: "كل الفروع",
      custodian: "أمين العهدة", allCustodians: "الكل",
      costCentre: "مركز التكلفة", allCostCentres: "كل مراكز التكلفة",
      status: "الحالة", allStatuses: "كل الحالات",
      transferStatus: "حالة التحويل",
      treatment: "المعالجة", allTreatments: "كل المعالجات",
      event: "نوع الحركة", allEvents: "كل الحركات",
      search: "بحث",
      includeUnposted: "إظهار غير المُرحّل", postedOnly: "المُرحّل فقط",
      periodFrom: "من فترة", periodTo: "إلى فترة",
      includeChildGroups: "شمول المجموعات الفرعية",
      includeChildLocations: "شمول المواقع الفرعية",
      detailedView: "عرض تفصيلي",
      includeClosed: "إظهار الأصول الخارجة", includeEmptyGroups: "إظهار المجموعات الفارغة",
      inTransitOnly: "قيد التحويل فقط",
    },
    columns: {
      date: "التاريخ", event: "الحركة", document: "المستند", asset: "الأصل", description: "البيان",
      from: "من", to: "إلى", status: "الحالة",
      debit: "مدين", credit: "دائن", costChange: "تغيّر التكلفة", depreciation: "الاستهلاك",
      bookValue: "القيمة الدفترية", voucher: "سند القيد",
      assetNumber: "رقم الأصل", assetName: "اسم الأصل", serialNumber: "الرقم التسلسلي",
      group: "المجموعة", location: "الموقع", custodian: "العهدة", supplier: "المورد",
      acquired: "تاريخ الاقتناء", acquisitionCost: "تكلفة الاقتناء",
      additions: "الإضافات المرسملة", revaluation: "إعادة التقييم",
      cost: "التكلفة", accumulated: "مجمع الاستهلاك",
      code: "الرمز", level: "المستوى", ownAssets: "أصول المجموعة", ownCost: "تكلفة المجموعة",
      totalAssets: "إجمالي الأصول", totalCost: "إجمالي التكلفة",
      treatment: "المعالجة", expenseAccount: "حساب المصروف", costCentre: "مركز التكلفة",
      original: "المبلغ الأصلي", returned: "المردود", net: "الصافي",
      fromGroup: "من مجموعة", toGroup: "إلى مجموعة",
      dispatched: "تاريخ الإرسال", received: "تاريخ الاستلام", daysInTransit: "أيام النقل",
    },
    figures: {
      events: "عدد الحركات", transactions: "عدد الحركات", assets: "عدد الأصول",
      groups: "عدد المجموعات", expenses: "عدد المصاريف", transfers: "عدد التحويلات",
      acquired: "تاريخ الاقتناء", closed: "تاريخ الخروج", daysHeld: "أيام الحيازة",
      cost: "التكلفة", accumulated: "مجمع الاستهلاك", bookValue: "القيمة الدفترية",
      acquisitionCost: "تكلفة الاقتناء", additions: "الإضافات المرسملة",
      revaluation: "إعادة التقييم",
      totalDebit: "إجمالي المدين", totalCredit: "إجمالي الدائن",
      costIncrease: "زيادة التكلفة", costDecrease: "نقص التكلفة",
      totalDepreciation: "إجمالي الاستهلاك", closingBookValue: "القيمة الدفترية الختامية",
      custodyEvents: "حركات العهدة", locationEvents: "حركات المواقع",
      transferEvents: "التحويلات", inventoryEvents: "الجرد",
      posted: "مُرحّلة", unposted: "غير مُرحّلة", operational: "تشغيلية",
      original: "المبلغ الأصلي", returned: "المردود",
      operating: "المحمّل على المصروفات", capitalised: "المُرسمل على الأصول", net: "الصافي",
      reclassified: "إعادة تصنيف", costMoved: "التكلفة المنقولة",
    },
    kinds: {
      acquisition: "اقتناء", purchaseReturn: "مردود مشتريات",
      custodyIssue: "صرف عهدة", custodyDeposit: "إيداع عهدة",
      custodyReturn: "إرجاع عهدة", custodyTransfer: "نقل عهدة",
      locationMovement: "تغيير موقع", transfer: "تحويل",
      insurance: "تأمين", maintenance: "صيانة", inventoryCount: "جرد",
      depreciation: "استهلاك", expense: "مصروف", expenseReturn: "مردود مصروف",
      revenue: "إيراد", revaluation: "إعادة تقييم", sale: "بيع", disposal: "إتلاف",
      split: "فصل",
    },
    assetStatus: {
      draft: "مسودة", inService: "في الخدمة", suspended: "موقوف",
      fullyDepreciated: "مهلك بالكامل", sold: "مباع", disposed: "مستبعد",
      writtenOff: "مشطوب", returned: "مرتجع للمورد",
    },
    transferStatus: {
      draft: "مسودة", approved: "معتمد", dispatched: "مُرسل",
      received: "مُستلم", cancelled: "ملغي",
    },
    treatment: { expensed: "محمّل على المصروفات", capitalised: "مُرسمل على الأصل" },
  },
  cashBanksSettings: {
    scoped: {
      financialIntegrationTitle: "إعدادات الربط المالي — النقد والبنوك",
      financialIntegrationBreadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « إعدادات الربط المالي",
      financialIntegrationNotice: "حسابات الربط الخاصة بالنقد والبنوك فقط. الحسابات تُختار من شجرة حسابات الشركة ولا يوجد أي حساب مثبّت في الكود.",
      reportSignaturesTitle: "تواقيع التقارير — النقد والبنوك",
      reportSignaturesBreadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « تواقيع التقارير",
      reportSignaturesNotice: "تواقيع تقارير النقد والبنوك. نفس السجل المشترك في النظام، معروضاً على نطاق هذه الوحدة.",
      bankOpeningTitle: "الأرصدة الافتتاحية للبنوك",
      bankOpeningBreadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « الأرصدة الافتتاحية للبنوك",
      bankOpeningNotice: "الأرصدة الافتتاحية لحسابات البنوك، من نفس كشف الأرصدة الافتتاحية المعتمد في الأستاذ العام — لا يوجد جدول أرصدة ثانٍ.",
      cashboxOpeningTitle: "الأرصدة الافتتاحية للصناديق",
      cashboxOpeningBreadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « الأرصدة الافتتاحية للصناديق",
      cashboxOpeningNotice: "الأرصدة الافتتاحية لحسابات الصناديق، من نفس كشف الأرصدة الافتتاحية المعتمد في الأستاذ العام — لا يوجد جدول أرصدة ثانٍ.",
      sharedScreenNotice: "هذه الشاشة هي نفسها الشاشة المعتمدة في الوحدة المالكة لها، وليست نسخة منها: التعديل هنا يعدّل السجل نفسه.",
    },
    common: {
      code: "الرمز", nameAr: "الاسم بالعربي", nameEn: "الاسم بالإنجليزي",
      displayOrder: "الترتيب", active: "مفعّل", notes: "ملاحظات", actions: "إجراءات",
      edit: "تعديل", activate: "تفعيل", deactivate: "إيقاف",
      save: "حفظ", saveNew: "حفظ وجديد", cancel: "إلغاء", newRecord: "سجل جديد",
      search: "بحث", refresh: "تحديث", showInactive: "إظهار غير المفعّل",
      noRows: "لا توجد سجلات.", counts: "الإجمالي / المفعّل", usage: "الاستخدام",
      readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
      saved: "تم الحفظ.", activated: "تم التفعيل.", deactivated: "تم الإيقاف.",
      loadError: "تعذّر تحميل البيانات.", saveError: "تعذّر الحفظ.",
      formTitle: "البيانات", listTitle: "السجلات",
    },
    cashboxGroups: {
      title: "مجموعات الصناديق",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « مجموعات الصناديق",
      purposeNotice: "تصنيف للصناديق لأغراض الإدارة والتقارير. المجموعة لا تحمل حساباً ولا رصيداً ولا تؤثر على أي قيد.",
      scopeNotice: "المجموعة ليست الشجرة: الشجرة تحدد الصندوق الأب، والمجموعة تجمع صناديق قد تكون في فروع مختلفة. لا يوجد حذف — يتم الإيقاف بدل الحذف.",
      listTitle: "المجموعات", newRecord: "مجموعة جديدة",
      assignedCashboxes: "عدد الصناديق",
    },
    cashboxCashFloat: {
      title: "العهدة النقدية للصندوق",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « العهدة النقدية للصندوق",
      purposeNotice: "السقف المعتمد لما يحتفظ به الصندوق نقداً، لكل عملة. رقم إداري لا يُنتج أي قيد.",
      scopeNotice: "«المعتمد» قرار من الشركة، و«الرصيد» ما يقوله حساب الصندوق فعلياً. الرقمان مستقلان، ولا يمنع تجاوز السقف أي ترحيل بذاته.",
      listTitle: "العهد المعتمدة", formTitle: "البيانات", newRecord: "عهدة جديدة",
      cashbox: "الصندوق", selectCashbox: "— اختر الصندوق —",
      currency: "العملة", selectCurrency: "— اختر العملة —",
      floatAmount: "المبلغ المعتمد", effectiveFrom: "ساري من",
      custodianAr: "أمين العهدة (عربي)", custodianEn: "أمين العهدة (إنجليزي)",
      currentBalance: "الرصيد الحالي", variance: "الفرق",
      noAccount: "لا يوجد حساب صندوق مرتبط", overFloat: "تجاوز السقف",
      totalAuthorised: "إجمالي المعتمد", overFloatCount: "متجاوز السقف",
    },
    letterOfCreditTypes: {
      title: "أنواع الاعتمادات المستندية",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « أنواع الاعتمادات المستندية",
      purposeNotice: "تصنيف الشركة للاعتمادات التي تفتحها — اطلاعي، دفع مؤجل، دوّار — حسب عرفها التجاري.",
      scopeNotice: "تصنيف فقط ولا يمس دورة حياة الاعتماد: حالة الاعتماد تبقى مرجعها الوحيد. الاعتمادات المفتوحة قبل تعريف القائمة لا يُعاد تصنيفها.",
      listTitle: "الأنواع", newRecord: "نوع جديد",
      defaultMargin: "نسبة التأمين الافتراضية %",
    },
    chequeReturnReasons: {
      title: "أسباب إرجاع الشيكات",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « أسباب إرجاع الشيكات",
      purposeNotice: "قائمة أسباب رفض البنك للشيك، لتصبح قابلة للتجميع في التقارير بدل نص حر.",
      scopeNotice: "السبب النصي المسجّل على الشيكات المرتجعة سابقاً يبقى كما هو ولا يُعاد كتابته. القائمة تخص الإرجاعات الجديدة.",
      listTitle: "الأسباب", newRecord: "سبب جديد",
      drawerFault: "خطأ الساحب", drawerFaultHint: "تمييز إداري للمتابعة فقط ولا يغيّر القيد.",
    },
    userCashboxAccess: {
      title: "ربط المستخدمين بالصناديق",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « ربط المستخدمين بالصناديق",
      purposeNotice: "تحديد الصناديق التي يعمل عليها كل مستخدم. القيد يُطبّق على الخادم في كل الشاشات والاستعلامات وليس بإخفاء عناصر الواجهة.",
      enforcementOffNotice: "التفعيل غير مفعّل حالياً في إعدادات نظام النقدية والبنوك، لذلك هذه الروابط لا تقيّد أحداً بعد.",
      enforcementOnNotice: "التفعيل مفعّل. المستخدم الذي لا يملك أي ربط يبقى غير مقيّد — حتى لا يُقفل النظام على شركة لم تربط أحداً بعد.",
      readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
      userSection: "المستخدم", user: "المستخدم", selectUser: "— اختر المستخدم —",
      userInactive: "الحساب غير مفعّل", searchUsers: "بحث في المستخدمين",
      grantsSection: "الصناديق", cashboxCode: "الرمز", cashboxName: "الصندوق",
      allowed: "مسموح", grantedAt: "تاريخ الربط", cashboxInactive: "الصندوق مغلق أو غير مفعّل",
      grantAll: "منح الكل", revokeAll: "سحب الكل",
      noUser: "اختر مستخدماً لعرض صناديقه.", noRows: "لا توجد صناديق معرّفة.",
      grantedCount: "عدد الصناديق المربوطة",
      saved: "تم الحفظ.", loadError: "تعذّر تحميل البيانات.", saveError: "تعذّر الحفظ.",
    },
    system: {
      title: "إعدادات نظام النقدية والبنوك",
      breadcrumb: "المحاسبة العامة « النقد والبنوك « الإعدادات « إعدادات نظام النقدية والبنوك",
      purposeNotice: "القواعد التي تعمل بها وحدة النقد والبنوك على مستوى الشركة. كل إعداد هنا تقرأه عملية فعلية.",
      prospectiveNotice: "كل الإعدادات تسري على ما هو قادم فقط: لا يُعاد فحص أو تعديل أي سند مرحّل أو شيك مسجّل أو تسوية مكتملة.",
      readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
      cashboxSection: "الصناديق", chequeSection: "الشيكات", bankSection: "البنوك",
      preventNegative: "منع رصيد الصندوق بالسالب",
      preventNegativeHint: "يُفحص عند ترحيل السحب النقدي فقط، ولا يُعاد فحص ما رُحّل سابقاً.",
      enforceAccess: "تفعيل ربط المستخدمين بالصناديق",
      enforceAccessHint: "عند التفعيل تُحصر الصناديق على المرتبطة بالمستخدم في كل الشاشات والاستعلامات.",
      enforceAccessEmpty: "لا يوجد أي مستخدم مربوط بصندوق حتى الآن، لذلك التفعيل لن يقيّد أحداً.",
      defaultCashbox: "الصندوق الافتراضي", defaultBankAccount: "الحساب البنكي الافتراضي",
      none: "— بدون —",
      requireLeaf: "إلزام سحب الشيك الصادر من دفتر مسجّل",
      requireLeafHint: "يُفحص عند تحرير الشيك الصادر فقط.",
      requireReason: "إلزام اختيار سبب إرجاع الشيك",
      requireReasonHint: "يُفحص عند تسجيل ارتجاع الشيك الوارد والصادر معاً.",
      requireReasonEmpty: "لم يتم تعريف أي سبب إرجاع بعد. تفعيل هذا الخيار سيمنع تسجيل الارتجاعات.",
      defaultTerm: "نوع الشيك الافتراضي",
      termImmediate: "فوري", termPostDated: "آجل",
      save: "حفظ", restoreDefaults: "استعادة الافتراضي",
      restoreConfirm: "استعادة كل الإعدادات إلى الوضع الافتراضي؟",
      saved: "تم حفظ الإعدادات.", restored: "تمت استعادة الإعدادات الافتراضية.",
      loadError: "تعذّر تحميل الإعدادات.", saveError: "تعذّر حفظ الإعدادات.",
    },
  },
  custodyCategories: {
    title: "فئات العهد",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « فئات العهد",
    purposeNotice: "المفردات المعتمدة لفئات العهد. الشاشة للتعريف فقط.",
    scopeNotice:
      "لا تحمل الفئة حساباً ولا سقفاً ولا قواعد ترحيل: لا توجد في هذا الإصدار عملية عهد تستهلكها بعد.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
    listTitle: "الفئات",
    formTitle: "البيانات",
    newRecord: "فئة جديدة",
    search: "بحث",
    refresh: "تحديث",
    showInactive: "إظهار غير المفعّل",
    noRows: "لا توجد فئات بعد",
    code: "الرمز",
    descriptionAr: "الوصف بالعربي",
    descriptionEn: "الوصف بالإنجليزي",
    active: "مفعّل",
    actions: "إجراءات",
    edit: "تعديل",
    activate: "تفعيل",
    deactivate: "إلغاء التفعيل",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    saved: "تم الحفظ",
    activated: "تم التفعيل",
    deactivated: "تم إلغاء التفعيل",
    counts: "الإجمالي / المفعّل",
    loadError: "تعذر تحميل فئات العهد",
    saveError: "تعذر الحفظ",
    auditError: "تعذر تحميل سجل التغييرات",
  },
  countryRegions: {
    title: "البلدان والمناطق",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « البلدان والمناطق",
    purposeNotice:
      "شجرة البلدان وما تحتها من مناطق ومدن. السجل الجذر بلد، وكل سجل تابع منطقة أو مدينة تحته.",
    timeZoneNotice:
      "لا يوجد في هذا النظام جدول مناطق زمنية. القائمة مأخوذة من قاعدة المناطق الزمنية للمنصّة نفسها، وهي المصدر الذي يتحقق منه الحفظ.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
    treeTitle: "الشجرة",
    formTitle: "البيانات",
    newRoot: "بلد جديد",
    newChild: "سجل تابع",
    search: "بحث",
    refresh: "تحديث",
    showInactive: "إظهار غير المفعّل",
    noRows: "لا توجد سجلات",
    parent: "الأصل",
    noParent: "بلا أصل (بلد)",
    code: "الرمز",
    descriptionAr: "الوصف بالعربي",
    descriptionEn: "الوصف بالإنجليزي",
    nationalityAr: "الجنسية بالعربي",
    nationalityEn: "الجنسية بالإنجليزي",
    currency: "العملة",
    timeZone: "المنطقة الزمنية",
    shortCode: "الرمز المختصر",
    internationalCode: "الرمز الدولي",
    mobileDigits: "عدد خانات الجوال",
    documentDigits: "عدد خانات المستند",
    isChild: "سجل تابع",
    active: "مفعّل",
    none: "بدون",
    flagSection: "علم البلد",
    flagUpload: "رفع صورة",
    flagReplace: "استبدال",
    flagRemove: "حذف",
    flagNone: "لا توجد صورة",
    flagHint: "PNG أو JPEG أو GIF أو WEBP أو SVG، بحد أقصى ١ ميجابايت.",
    flagRemoveConfirm: "حذف علم هذا البلد؟",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    deactivate: "إلغاء التفعيل",
    activate: "تفعيل",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    country: "بلد",
    region: "منطقة",
    level: "المستوى",
    children: "التوابع",
    saved: "تم الحفظ",
    activated: "تم التفعيل",
    deactivated: "تم إلغاء التفعيل",
    flagSaved: "تم حفظ العلم",
    flagRemoved: "تم حذف العلم",
    loadError: "تعذر تحميل البلدان والمناطق",
    saveError: "تعذر الحفظ",
    auditError: "تعذر تحميل سجل التغييرات",
    flagError: "تعذر معالجة صورة العلم",
  },
  userSupplierAccess: {
    title: "ربط المستخدمين بالموردين",
    breadcrumb: "المحاسبة العامة « المدفوعات « البيانات التعريفية « ربط المستخدمين بالموردين",
    purposeNotice:
      "يحدد مدير النظام هنا المورّدين الذين يجوز لكل مستخدم العمل معهم، على ثلاثة مستويات مستقلة.",
    denyByDefaultNotice:
      "المستخدم الذي لا يملك ربطاً في مستوى ما لا يرى أي مورّد في ذلك المستوى. الغياب يعني المنع لا الإطلاق.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الربط.",
    notIntegratedNotice:
      "المطبَّق فعلياً اليوم هو مستوى الحركات اليومية. الاستعلامات والتقارير محفوظة وجاهزة، وتُفعَّل عند بناء شاشات المدفوعات الخاصة بها.",
    userSection: "المستخدم",
    user: "المستخدم",
    selectUser: "اختر مستخدماً",
    userInactive: "هذا المستخدم غير مفعّل. الربط القائم للعرض فقط، ولا يُقبل ربط جديد.",
    dailySection: "ربط الموردين مع الحركات اليومية",
    inquirySection: "ربط الموردين مع الاستعلامات",
    reportSection: "ربط الموردين مع التقارير",
    partnerCode: "رمز المورد",
    partnerName: "اسم المورد",
    allowed: "مسموح",
    actions: "إجراءات",
    add: "إضافة مورد",
    remove: "حذف",
    search: "بحث",
    refresh: "تحديث",
    noRows: "لا يوجد مورد مرتبط على هذا المستوى",
    noUser: "اختر مستخدماً لعرض المورّدين المرتبطين به",
    partnerInactive: "مورد غير مفعّل",
    pickerTitle: "إضافة مورد",
    pickerSearch: "ابحث بالرمز أو الاسم",
    pickerNone: "لا يوجد مورد متاح للإضافة على هذا المستوى",
    cancel: "إلغاء",
    confirmRemove: "حذف ربط هذا المورد على هذا المستوى؟",
    granted: "تمت الإضافة",
    removed: "تم الحذف",
    toggled: "تم التحديث",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    auditFields: "الحقول",
    close: "إغلاق",
    loadError: "تعذر تحميل الربط",
    saveError: "تعذر حفظ التغيير",
    auditError: "تعذر تحميل سجل التغييرات",
  },
  userCustomerAccess: {
    title: "ربط المستخدمين بالعملاء",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « ربط المستخدمين بالعملاء",
    purposeNotice:
      "يحدد مدير النظام هنا العملاء الذين يجوز لكل مستخدم العمل معهم، على ثلاثة مستويات مستقلة.",
    denyByDefaultNotice:
      "المستخدم الذي لا يملك ربطاً في مستوى ما لا يرى أي عميل في ذلك المستوى. الغياب يعني المنع لا الإطلاق.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الربط.",
    notIntegratedNotice:
      "المطبَّق فعلياً اليوم هو مستوى الحركات اليومية. الاستعلامات والتقارير محفوظة وجاهزة، وتُفعَّل عند بناء شاشات المقبوضات الخاصة بها.",
    userSection: "المستخدم",
    user: "المستخدم",
    selectUser: "اختر مستخدماً",
    userInactive: "هذا المستخدم غير مفعّل. الربط القائم للعرض فقط، ولا يُقبل ربط جديد.",
    dailySection: "ربط العملاء مع الحركات اليومية",
    inquirySection: "ربط العملاء مع الاستعلامات",
    reportSection: "ربط العملاء مع التقارير",
    partnerCode: "رمز العميل",
    partnerName: "اسم العميل",
    allowed: "مسموح",
    actions: "إجراءات",
    add: "إضافة عميل",
    remove: "حذف",
    search: "بحث",
    refresh: "تحديث",
    noRows: "لا يوجد عميل مرتبط على هذا المستوى",
    noUser: "اختر مستخدماً لعرض العملاء المرتبطين به",
    partnerInactive: "عميل غير مفعّل",
    pickerTitle: "إضافة عميل",
    pickerSearch: "ابحث بالرمز أو الاسم",
    pickerNone: "لا يوجد عميل متاح للإضافة على هذا المستوى",
    cancel: "إلغاء",
    confirmRemove: "حذف ربط هذا العميل على هذا المستوى؟",
    granted: "تمت الإضافة",
    removed: "تم الحذف",
    toggled: "تم التحديث",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجلة",
    auditAction: "الإجراء",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    auditFields: "الحقول",
    close: "إغلاق",
    loadError: "تعذر تحميل الربط",
    saveError: "تعذر حفظ التغيير",
    auditError: "تعذر تحميل سجل التغييرات",
  },
  smsSettings: {
    title: "إعدادات الرسائل النصية",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « إعدادات الرسائل النصية",
    purposeNotice: "سياسة تذكير العملاء برسالة نصية: موعد الإرسال الشهري، وفترة الإرسال، ونص الرسالة.",
    unavailableNotice:
      "لا توجد في هذا الإصدار قناة إرسال رسائل نصية ولا مجدول مهام. الإعدادات تُحفظ ولا يُرسل شيء تلقائياً بعد.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الإعدادات.",
    shortMonthNotice:
      "اليوم يُحفظ كما أدخلته. سلوك الأشهر التي لا تحتوي اليوم المختار (29 · 30 · 31) قرار يخص جهة الإرسال، ولم يُعتمد بعد.",
    scheduleSection: "موعد الإرسال الشهري",
    day: "اليوم",
    hour: "الساعة",
    minute: "الدقيقة",
    windowSection: "فترة الإرسال",
    startMonth: "من شهر",
    endMonth: "إلى شهر",
    messageSection: "نص الرسالة",
    messageText: "النص المرسل للعميل",
    messageHint: "مطلوب عند تفعيل الإرسال التلقائي.",
    automaticSending: "إرسال تلقائي",
    active: "فعال",
    save: "حفظ",
    cancel: "إلغاء",
    restoreDefaults: "استعادة الافتراضي",
    restoreConfirm: "سيتم إرجاع كل الإعدادات إلى قيمها الافتراضية. هل تريد المتابعة؟",
    auditHistory: "سجل التدقيق",
    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditChangedBy: "المستخدم",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    lastChanged: "آخر تعديل",
    saved: "حُفظت الإعدادات",
    restored: "أُعيدت الإعدادات إلى الافتراضي",
    loadError: "تعذّر تحميل الإعدادات.",
    saveError: "تعذّر الحفظ.",
    auditError: "تعذّر تحميل سجل التدقيق.",
  },
  ePaymentPolicy: {
    title: "سياسة الدفع الإلكتروني",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « سياسة الدفع الإلكتروني",
    purposeNotice:
      "تعريف أنواع بطاقات الدفع، ثم سياسة العمولة لكل نوع على مستوى الفرع بشرائح القيم.",
    configurationNotice:
      "تهيئة فقط: لا يُنشأ أي قيد ولا يتغيّر أي رصيد. تقرأها حركات الدفع بالبطاقة لاحقاً.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن التعديل.",
    sections: "اعدادات بطاقات الدفع",
    cardTypesSection: "أنواع بطاقات الدفع",
    commissionSection: "سياسة العمولات",
    newCardType: "نوع بطاقة جديد",
    edit: "تعديل",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    active: "فعال",
    inactive: "غير فعال",
    usage: "السياسات",
    list: "أنواع البطاقات",
    search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    showInactive: "إظهار غير الفعال",
    refresh: "تحديث",
    save: "حفظ",
    saveAndNew: "حفظ وجديد",
    cancel: "إلغاء",
    activate: "تفعيل",
    deactivate: "إيقاف",
    actions: "الإجراءات",
    empty: "لا توجد أنواع بطاقات معرَّفة.",
    cardType: "نوع بطاقة الدفع",
    branch: "الفرع",
    chooseCardType: "— اختر نوع البطاقة —",
    chooseBranch: "— اختر الفرع —",
    ranges: "شرائح العمولة",
    fromAmount: "من مبلغ",
    toAmount: "إلى مبلغ",
    openEnded: "مفتوح",
    commissionPercent: "نسبة العمولة %",
    commissionAmount: "مبلغ العمولة",
    addRange: "إضافة شريحة",
    removeRange: "حذف",
    policyList: "السياسات المعرَّفة",
    noPolicies: "لا توجد سياسات معرَّفة.",
    rangesCount: "شريحة",
    savePolicy: "حفظ السياسة",
    editPolicy: "تعديل السياسة",
    newPolicy: "سياسة جديدة",
    saved: "حُفظ نوع البطاقة",
    policySaved: "حُفظت السياسة",
    activated: "فُعِّل النوع",
    deactivated: "أُوقف النوع",
    loadError: "تعذّر التحميل.",
    saveError: "تعذّر الحفظ.",
  },
  discountTypesScreen: {
    title: "أنواع الخصومات",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « أنواع الخصومات",
    purposeNotice:
      "أنواع الخصم المعتمدة، وكل نوع يُسمّي الحساب الذي يُرحَّل عليه خصمه لاحقاً.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن إضافة أو تعديل الأنواع.",
    newType: "نوع جديد",
    edit: "تعديل",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    calculationType: "نوع الخصم",
    percentage: "نسبة",
    fixedAmount: "مبلغ ثابت",
    account: "الحساب",
    accountHint: "حساب فرعي قابل للترحيل وفعال من دليل الحسابات.",
    isDefault: "افتراضي",
    active: "فعال",
    inactive: "غير فعال",
    list: "الأنواع المعرَّفة",
    search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    showInactive: "إظهار غير الفعال",
    refresh: "تحديث",
    save: "حفظ",
    saveAndNew: "حفظ وجديد",
    cancel: "إلغاء",
    activate: "تفعيل",
    deactivate: "إيقاف",
    actions: "الإجراءات",
    empty: "لا توجد أنواع خصم معرَّفة.",
    auditHistory: "سجل التدقيق",
    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditAction: "الإجراء",
    auditChangedBy: "المستخدم",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    lastChanged: "آخر تعديل",
    yes: "نعم",
    no: "لا",
    saved: "حُفظ النوع",
    activated: "فُعِّل النوع",
    deactivated: "أُوقف النوع",
    loadError: "تعذّر تحميل الأنواع.",
    saveError: "تعذّر الحفظ.",
    auditError: "تعذّر تحميل سجل التدقيق.",
  },
  workflowActions: {
    title: "إجراءات تدفق العمل",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « إجراءات تدفق العمل",
    purposeNotice:
      "الخطوات المعتمدة التي تسير عليها دورة العمل حسب سياسة المؤسسة. الخطوة المعلَّمة إجبارية هي التي يجب أن يعملها المستخدم أثناء إنشاء الحركات اليومية.",
    scopeNotice:
      "تعريف فقط في هذه المرحلة. لا يوجد بعد نموذج يربط الخطوة بحركة معيّنة، فلا يُفرض شيء على أي حركة اليوم. دورة السند (غير مُرحَّل ← مدقَّق ← مُرحَّل) ثابتة ولا تُدار من هنا.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن إضافة أو تعديل الإجراءات.",
    newAction: "إجراء جديد",
    edit: "تعديل",
    code: "الرمز",
    descriptionAr: "الوصف عربي",
    descriptionEn: "الوصف إنجليزي",
    sequence: "الترتيب",
    mandatory: "إجباري",
    active: "فعال",
    inactive: "غير فعال",
    list: "الإجراءات المعرَّفة",
    search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    showInactive: "إظهار غير الفعال",
    refresh: "تحديث",
    save: "حفظ",
    saveAndNew: "حفظ وجديد",
    cancel: "إلغاء",
    activate: "تفعيل",
    deactivate: "إيقاف",
    actions: "الإجراءات",
    empty: "لا توجد إجراءات معرَّفة.",
    auditHistory: "سجل التدقيق",
    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditAction: "الإجراء",
    auditChangedBy: "المستخدم",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    lastChanged: "آخر تعديل",
    yes: "نعم",
    no: "لا",
    saved: "حُفظ الإجراء",
    activated: "فُعِّل الإجراء",
    deactivated: "أُوقف الإجراء",
    loadError: "تعذّر تحميل الإجراءات.",
    saveError: "تعذّر الحفظ.",
    auditError: "تعذّر تحميل سجل التدقيق.",
  },
  receivablesLinks: {
    title: "إعدادات الربط المالي",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « إعدادات الربط المالي",
    purposeNotice:
      "تسع خانات ربط معرّفة مسبقاً تقرأها حركات المقبوضات لاحقاً. لا تُسجَّل هنا أي قيود ولا تتغير أي أرصدة.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الربط.",
    unlinkedNotice:
      "الخانة غير المربوطة تبقى ظاهرة. إزالة الربط تُفرغ الحساب ولا تحذف الخانة ولا سجلها.",
    accountsSection: "الحسابات",
    account: "الحساب",
    linkLevel: "مستوى الربط",
    detailPreview: "معاينة التفاصيل",
    actions: "الإجراءات",
    companyLevel: "الشركة",
    companyLevelFixed: "مستوى الربط لهذه الخانات هو الشركة دائماً.",
    linked: "مربوط",
    notLinked: "غير مربوط",
    linkedOf: "{linked} من {total} مربوطة",
    edit: "تحرير",
    clearLink: "إزالة الربط",
    clearConfirm: "سيتم إفراغ الحساب من هذه الخانة مع بقاء الخانة وسجلها. هل تريد المتابعة؟",
    save: "حفظ",
    cancel: "إلغاء",
    search: "بحث",
    searchPlaceholder: "ابحث باسم الخانة أو رقم الحساب",
    refresh: "تحديث",
    print: "طباعة",
    auditHistory: "سجل التدقيق",
    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditAction: "الإجراء",
    auditChangedBy: "المستخدم",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    chooseAccount: "الحساب المالي",
    accountHint: "حساب فرعي قابل للترحيل وفعال من دليل الحسابات، ويخص هذه الشركة.",
    requiredTypeHint: "هذه الخانة تقبل حسابات من نوع: ",
    detailsTitle: "تفاصيل الربط",
    detailLinkType: "نوع الربط",
    detailLinkLevel: "مستوى الربط",
    detailCompany: "الشركة",
    detailAccountNumber: "رقم الحساب",
    detailAccountNameAr: "اسم الحساب بالعربية",
    detailAccountNameEn: "اسم الحساب بالإنجليزية",
    detailStatus: "الحالة",
    detailChangedBy: "آخر تعديل بواسطة",
    detailChangedAt: "تاريخ آخر تعديل",
    active: "فعال",
    inactive: "غير فعال",
    saved: "حُفظ الربط",
    cleared: "أُزيل الربط",
    loadError: "تعذّر تحميل خانات الربط.",
    saveError: "تعذّر الحفظ.",
    printError: "تعذّرت الطباعة.",
    auditError: "تعذّر تحميل سجل التدقيق.",
    typeExpense: "مصروفات",
    typeRevenue: "إيرادات",
  },
  receivablesSettings: {
    title: "إعدادات النظام",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « إعدادات النظام",
    purposeNotice:
      "إعدادات تخص هذه الشركة وحدها. الإعدادات الإلزامية تُطبَّق عند حفظ البطاقة من الآن فصاعداً؛ البطاقات المسجّلة سابقاً تبقى كما هي إلى أن تُعدَّل.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن تعديل الإعدادات.",
    pendingModulesNotice:
      "بعض الإعدادات تخص وحدات لم تُبنَ بعد (سندات القبض، التمويل، الكمبيالات، أعمار الذمم). تُحفظ الآن لتقرأها تلك الوحدات لاحقاً، ولا يوجد سلوك يقرأها اليوم.",
    description: "الوصف",
    value: "القيمة",
    sectionCustomerCard: "بطاقة العميل",
    sectionReceipts: "المقبوضات والتحصيل",
    sectionAging: "أعمار الذمم",
    sectionFinancing: "التمويل",
    sectionPaymentTax: "طرق الدفع والضريبة",
    sectionCheques: "الشيكات والكمبيالات",
    save: "حفظ",
    cancel: "إلغاء",
    restoreDefaults: "استعادة الافتراضي",
    restoreConfirm: "سيتم إرجاع كل الإعدادات إلى قيمها الافتراضية. هل تريد المتابعة؟",
    auditHistory: "سجل التدقيق",
    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditSetting: "الإعداد",
    auditOldValue: "القيمة السابقة",
    auditNewValue: "القيمة الجديدة",
    auditChangedBy: "المستخدم",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    yes: "نعم",
    no: "لا",
    choose: "— اختر —",
    none: "— لا يوجد —",
    lastChanged: "آخر تعديل",
    saved: "حُفظت الإعدادات",
    restored: "أُعيدت الإعدادات إلى الافتراضي",
    loadError: "تعذّر تحميل الإعدادات.",
    saveError: "تعذّر الحفظ.",
    auditError: "تعذّر تحميل سجل التدقيق.",

    requireNationalOrTaxNumber: "الرقم الوطني أو الرقم الضريبي إجبارية في بطاقة المورد والعميل",
    requireCustomerCategory: "فئة العميل إجبارية في بطاقة العميل",
    requireAddressAndMobile: "العنوان و الخلوي إجبارية في بطاقة العميل",
    requireCustomerTypeClassAndNature: "نوع العميل و صنف العميل وطبيعة العمل إجبارية في بطاقة العميل",
    defaultCustomerCreditLimit: "القيمة الافتراضية لسقف العميل (مركبات، قطع غيار، صيانة، ذمم)",
    defaultCustomerPaymentMethod: "القيمة الافتراضية لطريقة الدفع لبطاقة العميل",
    defaultCustomerPriceTaxTreatment: "القيمة الافتراضية لضريبة السعر للعملاء",
    hideCollectorFromVouchers: "إخفاء المحصل عن السندات",
    linkReferenceWithCollector: "ربط المرجع مع المحصل",
    electronicPaymentSalesRepresentative: "مندوب المبيعات الخاص بالدفع الإلكتروني",
    enableReceiptCommissionEntry: "تفعيل قيد العمولة على سند القبض",
    allowedSettlementDifference: "قيمة الفرق المسموحة بين قيمة القبض وقيمة الفاتورة عند التسديد",
    maximumDeliveriesToSamePerson: "الحد الأعلى لعدد مرات التسليم لنفس الشخص",
    minimumCustomerResponsePercentage: "الحد الادني لنسبة استجابة العملاء",
    legalSettlementAlertDays: "عدد ايام التنبيه قبل فترة سداد تسويات القضايا",
    paymentMethodEnforcementSource: "اعتماد طريقة الدفع للعميل",
    createCustomerReceivablesForChequesInCollection: "إنشاء ذمم عملاء - شيكات برسم التحصيل",
    customerAgingGracePeriodDays: "فترة السماح للعملاء في استعلام عمر الذمم المخصص",
    receivablesOpeningBalanceDate: "تاريخ الرصيد الافتتاحي (لاعمار الذمم)",
    useOpeningBalanceDateAsAgingStart: "إعتماد تاريخ الرصيد الافتتاحي (لاعمار الذمم) كبداية للتعمير",
    agingInvoiceDateBasis: "آلية إحتساب تواريخ الفواتير في عمر الذمة",
    showNotYetDueAndAdvancePaymentsInAging: "إظهار الرصيد غير المستحق والدفعات المقدمة في استعلام عمر الذمة",
    customerFinancingType: "نوع التمويل للعملاء",
    accountingFinancingType: "نوع التمويل - محاسبيا",
    financingCostBasis: "نوع احتساب كلفة التمويل",
    deferredInterestBasis: "آلية احتساب قيمة الفائدة المؤجلة والمجدولة",
    decliningInterestMultiplier: "معامل ضرب نسبة الفائدة المتناقصة الخاصة بإصدار طلب التمويل",
    decliningInterestOnBalanceBeforePayment:
      "احتساب قيمة الفائدة المتناقصة في جدولة التمويل محاسبياً بناءً على الرصيد قبل الفائدة",
    createIntermediateAccountInFinancingRequest: "انشاء حساب وسيط في سند طلب التمويل (كمبيالات، شيكات)",
    promissoryNoteDelayPenalty: "غرامة التأخير الخاصة بإصدار الكمبيالات",
    hideCommissionFieldsInScheduling: "اخفاء حقول العمولة في شاشة جدولة الكمبيالات والشيكات",

    financingFixed: "ثابت",
    financingDeclining: "متناقص",
    costBasisCurrencies: "العملات",
    interestMonthly: "شهري",
    enforcementCustomerCard: "بطاقة العميل",
    agingByInvoiceDate: "تاريخ الفاتورة",
    agingByDueDate: "تاريخ الحق",
    paymentCash: "نقدي",
    paymentCredit: "ذمم",
    taxInclusive: "شامل",
    taxExclusive: "غير شامل",
    taxExempt: "معفى",
  },
  partnerLookups: {
    title: "تصنيفات العملاء والموردين",
    breadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « تصنيفات العملاء والموردين",
    lockedBreadcrumb: "المحاسبة العامة « المقبوضات « الإعدادات « {list}",
    purposeNotice:
      "قوائم ثابتة تُستخدم في بطاقة العميل والمورد. الرمز والوصف العربي فريدان داخل القائمة الواحدة فقط، فيمكن استخدام الرمز نفسه في قائمة أخرى.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن إضافة أو تعديل التصنيفات.",
    vocabulary: "القائمة",
    allVocabularies: "— كل القوائم —",
    newValue: "قيمة جديدة",
    edit: "تعديل",
    code: "الرمز",
    descriptionAr: "الوصف بالعربية",
    descriptionEn: "الوصف بالإنجليزية",
    active: "فعال",
    inactive: "غير فعال",
    collector: "المحصل",
    noCollector: "— لا يوجد —",
    taxCode: "الرمز الضريبي",
    usage: "الاستخدام",
    list: "القيم المعرَّفة",
    search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الوصف",
    showInactive: "إظهار غير الفعال",
    refresh: "تحديث",
    save: "حفظ",
    cancel: "إلغاء",
    activate: "تفعيل",
    deactivate: "إيقاف",
    actions: "الإجراءات",
    empty: "لا توجد قيم معرَّفة.",
    inUseHint: "القيمة المستخدمة في بطاقات لا تُحذف؛ أوقفها بدل ذلك.",
    saved: "حُفظت القيمة",
    activated: "فُعِّلت القيمة",
    deactivated: "أُوقفت القيمة",
    loadError: "تعذّر تحميل التصنيفات.",
    saveError: "تعذّر الحفظ.",
    types: {
      category: "الفئة",
      importance: "الأهمية",
      partnerType: "نوع العميل",
      class: "أصناف العملاء",
      title: "اللقب",
      nature: "طبيعة العمل",
      source: "مصدر التعامل",
      nationality: "الجنسية",
      parentClassification: "التصنيف الرئيسي",
      reportTemplate: "نموذج التقرير",
      salePriceType: "نوع سعر البيع",
      commercialActivity: "الأنشطة التجارية",
      documentType: "أنواع المستندات",
    },
  },
  partners: {
    delete: "حذف", deleteConfirm: "حذف بطاقة «{name}» نهائياً؟ يُرفض الحذف إن كان أي مستند يسمّيها.",
    deleted: "حُذفت البطاقة.", deleteError: "تعذّر الحذف.",
    settlementActiveLabel: "سارية",
    settlementCancelledOnCard: "ملغاة",
    settlementsTitle: "التسويات",
    settlementInvoice: "رقم الفاتورة",
    settlementReceipt: "رقم سند القبض",
    settlementAmountCol: "المبلغ المسدد",
    settlementDateCol: "تاريخ التسوية",
    settlementStatusCol: "الحالة",
    settlementNoneOnCard: "لا توجد تسويات لهذا العميل.",
    settlementReadOnly: "للعرض فقط. تُنفَّذ التسوية وإلغاؤها من شاشة تسوية العميل.",
    creditSectionTitle: "المعلومات الائتمانية",
    creditPickCustomer: "اختر عميلاً من القائمة أدناه (زر «تعديل») لعرض سقف ائتمانه وتعديله.",
    creditLimitLocked: "لا يُعدَّل سقف الائتمان من هذه الشاشة. استخدم إجراء «تعديل سقف الائتمان» — فهو يتطلب صلاحية مستقلة وسبباً ويُسجَّل في السجل.",
    creditNoEnforcement: "ملاحظة: لا يوجد حالياً أي مستند يمنع البيع عند تجاوز السقف. السقف معلومة اعتماد فقط في هذه النسخة.",
    currentBalance: "الرصيد الحالي",
    balanceExceedsLimit: "الرصيد الحالي للعميل يتجاوز سقف الائتمان الجديد.",
    changeCreditLimit: "تعديل سقف الائتمان",
    newCreditLimit: "السقف الجديد",
    changeReason: "سبب التعديل",
    creditHistoryTitle: "سجل تعديلات السقف",
    previousLimit: "السقف السابق",
    newLimit: "السقف الجديد",
    changedBy: "بواسطة",
    changedAt: "التاريخ",
    balanceAtChange: "الرصيد وقت التعديل",
    noCreditHistory: "لا توجد تعديلات على السقف.",
    creditLimitChanged: "تم تعديل سقف الائتمان. لم يتأثر الرصيد المحاسبي.",
    confirmCreditChange: "سيتم تعديل سقف ائتمان العميل. هذا التعديل لا يؤثر على الرصيد المحاسبي الحالي للعميل.",
    creditZeroAccounting: "تعديل السقف لا يُنشئ أي قيد ولا يغيّر رصيد العميل.",
    customersTitle: "العملاء",
    suppliersTitle: "الموردون",
    customersBreadcrumb: "المحاسبة العامة « المقبوضات « البيانات الأساسية « العملاء",
    suppliersBreadcrumb: "المحاسبة العامة « المدفوعات « البيانات الأساسية « الموردون",
    customerSingular: "عميل",
    supplierSingular: "مورد",
    purposeNotice:
      "البطاقة تُعرِّف الطرف وترتبط بحسابه في شجرة الحسابات. الأرصدة والحركات تُقرأ من الحساب المرتبط، ولا تُسجَّل هنا.",
    readOnlyNotice: "لديك صلاحية العرض فقط. لا يمكن إضافة أو تعديل البطاقات.",

    sectionBasic: "المعلومات الأساسية",
    sectionFinancial: "المعلومات المالية",
    sectionContact: "معلومات الاتصال",
    sectionLink: "إعدادات الربط المالي",
    sectionAddress: "معلومات العنوان التفصيلية",

    code: "الرمز",
    nameAr: "الاسم بالعربية",
    nameEn: "الاسم بالإنجليزية",
    startDate: "تاريخ بداية التعامل",
    category: "الفئة",
    importance: "الأهمية",
    partnerTypeLookup: "نوع العميل",
    class: "صنف العميل",

    // The SAME two lookups, worded for the card being read. A supplier card that asks for
    // "نوع العميل" is asking the right question in the wrong language; nothing beneath these
    // labels differs, and no second vocabulary exists.
    supplierTypeLookup: "نوع المورد",
    supplierClass: "صنف المورد",
    title: "اللقب",
    nature: "طبيعة العمل",
    source: "مصدر التعامل",
    nationality: "الجنسية",
    parentClassification: "التصنيف الرئيسي",
    reportTemplate: "نموذج التقرير",
    parentPartner: "التابع لـ",
    noParent: "— لا يوجد —",
    nationalIdIndividual: "الرقم الوطني للأفراد",
    nationalIdCompany: "الرقم الوطني للمنشآت",
    commercialRegistration: "السجل التجاري",
    referenceNumber: "الرقم المرجعي",
    notes: "ملاحظات",

    controlAccount: "الحساب المالي",
    controlAccountHint: "حساب فرعي قابل للترحيل من شجرة الحسابات — عليه تُرحَّل حركات هذا الطرف.",
    currency: "العملة",
    creditLimit: "سقف الائتمان",
    taxTreatment: "المعالجة الضريبية",
    taxInclusive: "شامل الضريبة",
    taxExclusive: "غير شامل الضريبة",
    taxExempt: "معفى",
    paymentMethod: "طريقة الدفع",
    paymentCash: "نقدي",
    paymentCredit: "آجل",
    defaultPaymentMethod: "طريقة الدفع الافتراضية",
    defaultPaymentMethodHint: "تُنسخ إلى الحركة عند اختيار العميل، ويمكن تغييرها في الحركة دون أن يتغير هذا الحقل.",
    salePriceType: "نوع سعر البيع",
    taxNumber: "الرقم الضريبي",
    turnoverRate: "معدل الدوران",
    turnoverRateHint: "عدد الأيام المتوقع للسداد.",

    salesRepresentative: "المندوب",
    marketer: "المسوّق",
    collector: "المحصّل",
    employee: "الموظف المسؤول",
    owner: "المالك",
    staffHint: "من مستخدمي النظام الفعالين.",

    phone: "الهاتف",
    mobile: "الخلوي",
    fax: "الفاكس",
    email: "البريد الإلكتروني",
    website: "الموقع الإلكتروني",
    contactPerson: "الشخص المسؤول",

    country: "الدولة",
    city: "المدينة",
    area: "المنطقة",
    street: "الشارع",
    building: "المبنى",
    postalCode: "الرمز البريدي",
    poBox: "صندوق البريد",
    address: "العنوان",
    addressNotes: "ملاحظات العنوان",
    latitude: "خط العرض",
    longitude: "خط الطول",
    coordinatesHint: "إحداثيات الموقع للخرائط، اختيارية.",

    active: "فعال",
    inactive: "غير فعال",
    blacklisted: "قائمة سوداء",
    blacklistReason: "سبب الإدراج بالقائمة السوداء",
    blacklistHint: "الطرف المدرج بالقائمة السوداء لا يظهر في الحركات الجديدة، وحركاته السابقة تبقى كما هي.",

    newRecord: "بطاقة جديدة",
    edit: "تعديل",
    save: "حفظ",
    saveAndNew: "حفظ وجديد",
    cancel: "إلغاء",
    print: "طباعة",
    auditHistory: "سجل التدقيق",
    activate: "تفعيل",
    deactivate: "إيقاف",
    list: "السجلات",
    inquiry: "استعلام", inquiryTitle: "السجلات المعرَّفة",
    inquiryEmpty: "لا توجد بطاقة تطابق البحث.",
    search: "بحث",
    searchPlaceholder: "ابحث بالرمز أو الاسم أو الهاتف",
    showInactive: "إظهار غير الفعال",
    refresh: "تحديث",
    actions: "الإجراءات",
    empty: "لا توجد سجلات.",
    status: "الحالة",
    choose: "— اختر —",
    chooseCountryFirst: "اختر البلد أولاً",
    manageLookups: "إدارة التصنيفات",

    auditNone: "لا توجد تغييرات مسجلة.",
    auditFields: "الحقول",
    auditCreated: "إنشاء",
    auditUpdated: "تعديل",
    auditDeleted: "حذف",
    close: "إغلاق",

    saved: "حُفظت البطاقة",
    activated: "فُعِّلت البطاقة",
    deactivated: "أُوقفت البطاقة",
    loadError: "تعذّر تحميل السجلات.",
    saveError: "تعذّر الحفظ.",
    printError: "تعذّرت الطباعة.",
    auditError: "تعذّر تحميل سجل التدقيق.",

    identificationRequired: "الرقم الوطني أو الرقم الضريبي مطلوب.",
    categoryRequired: "فئة العميل مطلوبة.",
    addressAndMobileRequired: "العنوان والخلوي مطلوبان.",
    typeClassNatureRequired: "نوع العميل وصنف العميل وطبيعة العمل مطلوبة.",
  },
  common: {
    loading: "جارٍ التحميل…",
    notFound: "الصفحة غير موجودة",
    notFoundHint: "قد يكون الرابط غير صحيح أو أن هذه الشاشة لا تنتمي إلى هذه الوحدة",
    retry: "إعادة المحاولة",
    error: "حدث خطأ غير متوقع",
    errorTitle: "خطأ",
    dismiss: "إغلاق",
    optional: "اختياري",
    cancel: "إلغاء",
  },
  paymentsReports: {
    supplierBlacklist: "كشف الموردين المحظورين", blacklisted: "محظور", notBlacklisted: "غير محظور",
    reason: "السبب", includeAllowed: "عرض غير المحظورين أيضاً",
    noBlacklisted: "لا يوجد موردون محظورون",
    flagNote: "المورّد يظهر هنا لأن علامة الحظر مُسجّلة على بطاقته — لا استنتاج من الرصيد أو النشاط.",
    expiringDocuments: "مستندات العملاء والموردين التي قاربت على الانتهاء",
    party: "الطرف", partyType: "نوع الطرف", customer: "عميل", supplier: "مورّد",
    documentType: "نوع المستند", documentNumber: "رقم المستند", issuedBy: "جهة الإصدار",
    issued: "تاريخ الإصدار", expires: "تاريخ الانتهاء", daysLeft: "الأيام المتبقية",
    withinDays: "خلال (يوم)", includeExpired: "تضمين المنتهية", expired: "منتهٍ",
    expiringSoon: "قارب على الانتهاء", noDocuments: "لا توجد مستندات قاربت على الانتهاء",
    expiryNote: "المستندات بلا تاريخ انتهاء مسجّل لا تظهر: غياب التاريخ ليس قرباً من الانتهاء.",
    infoChanges: "كشف تغييرات بيانات العملاء والموردين", whenChanged: "التاريخ والوقت",
    action: "الإجراء", changedColumns: "الحقول المتغيّرة", user: "المستخدم",
    created: "إنشاء", updated: "تعديل", parties: "عدد الأطراف",
    noChanges: "لا توجد تغييرات في هذا النطاق",
    auditNote: "مقروء من سجل التدقيق نفسه — لا يُستنتج بمقارنة البطاقة الحالية بشيء.",
    ageing: "تقرير الأعمار", currencyAgeing: "تقرير الأعمار بالعملة", current: "جارٍ",
    over120: "أكثر من 120", undated: "بلا تاريخ استحقاق", ageByDueDate: "الاحتساب من تاريخ الاستحقاق",
    byCurrency: "تفصيل حسب العملة", oldestDocument: "أقدم مستند", documentsCount: "عدد المستندات",
    grandTotal: "الإجمالي", noOutstanding: "لا توجد أرصدة مستحقة",
    bucketNote: "الفترات ثابتة 30/60/90/120 فأكثر، ومحسوبة من المتبقي على كل مستند بعد التسويات الفعلية.",
    trialBalance: "ميزان مراجعة مختصر - العملاء والموردون", opening: "الرصيد الافتتاحي",
    periodDebit: "مدين الفترة", periodCredit: "دائن الفترة", closingDebit: "ختامي مدين",
    closingCredit: "ختامي دائن", customers: "العملاء", suppliers: "الموردون",
    noBalances: "لا توجد أرصدة في هذا النطاق",
    ledgerNote: "الأرصدة من قيود الأستاذ المرحّلة على الحساب الوسيط لكل طرف.",
    orders: "كشف أوامر القبض والصرف", side: "النوع", receiptOrder: "أمر قبض",
    paymentOrder: "أمر صرف", orderNumber: "رقم الأمر", orderAmount: "قيمة الأمر",
    settled: "المنفّذ", remaining: "المتبقي", outstandingOnly: "غير المنفّذة فقط",
    noOrders: "لا توجد أوامر في هذا النطاق",
    ordersNote: "سجلان منفصلان بترقيم منفصل — يُعرضان معاً ولا يُدمجان. المنفّذ من السندات المرحّلة فقط.",
    guarantees: "كشف الكفالات", facilityCode: "رمز التسهيل", bank: "البنك",
    ceiling: "سقف الكفالة", rate: "نسبة الفائدة", ceilingExceedable: "يسمح بتجاوز السقف",
    includeAllFacilities: "عرض كل أنواع التسهيلات", noGuarantees: "لا توجد كفالات مسجّلة",
    guaranteeNote: "الكفالة مسجّلة كتسهيل بنكي في هذا النظام، لذلك يقرأ الكشف سجل التسهيلات.",
    invoicesByType: "كشف فواتير شراء الخدمات حسب النوع", groupBy: "التجميع حسب",
    byItem: "مادة الشراء", byAccount: "حساب المصروف", invoices: "عدد الفواتير",
    lines: "عدد الأسطر", sharePercent: "النسبة %",
    noInvoices: "لا توجد فواتير في هذا النطاق",
    byTypeNote: "«النوع» يُقرأ بطريقتين: المادة المشتراة، أو الحساب الذي رُحّلت إليه — والاختيار لك.",
    capitalInvoices: "فواتير الخدمات بانتظار الربط بالأصول", assetAccount: "حساب الأصل",
    noCapitalInvoices: "لا توجد فواتير رأسمالية في هذا النطاق",
    capitalNote: "السطر مُدرج لأنه رُحّل إلى حساب أصول. لا يوجد سجل أصول ثابتة في النظام بعد، فلا يمكن أن يكون أي منها مرتبطاً بأصل.",
    byAccountStatement: "كشف حساب مورد - الحسابات", accountNumber: "رقم الحساب",
    accountName: "اسم الحساب", movements: "عدد الحركات", bySupplier: "تفصيل حسب المورّد",
    accounts: "عدد الحسابات", noMovements: "لا توجد حركات في هذا النطاق",
    accountsNote: "يقرأ نفس الأستاذ الذي يقرأه كشف حساب المورّد، مجمّعاً حسب الحساب — فالصافي يطابق صافي الكشف.",
  },
  paymentsSettings: {
    signaturesTitle: "تواقيع التقارير",
    signaturesBreadcrumb: "المحاسبة العامة / المدفوعات / الإعدادات",
    signaturesNotice: "كتل التواقيع التي تُطبع أسفل التقارير. إعداد عرض فقط — لا أثر محاسبي، وطباعة التوقيع لا تُثبت شيئاً عن الأرقام فوقه. لا يوجد حذف: التوقيع الذي طُبع على تقارير صادرة يُوقَف ولا يُمحى.",
    criteriaTitle: "معايير تقييم الموردين",
    criteriaBreadcrumb: "المحاسبة العامة / المدفوعات / الإعدادات",
    criteriaNotice: "المعايير التي يُقاس عليها المورّد ووزن كل معيار. هذه الشاشة تُعرّف المسطرة فقط ولا تُسجّل درجات: تقييم مورّد عملية تشغيلية لها مسارها الخاص.",
    weightNotice: "مجموع الأوزان معروض ولا يُفرض أن يساوي 100 — وإلا تعذّر حفظ أول معيار قبل تعريف البقية.",
    code: "الرمز", title: "الصفة", titleAr: "الصفة بالعربية",
    signatoryName: "اسم الموقّع", signatoryNameAr: "اسم الموقّع بالعربية",
    module: "الوحدة", allModules: "كل الوحدات", displayOrder: "ترتيب العرض", notes: "ملاحظات",
    name: "الاسم", nameAr: "الاسم بالعربية", weight: "الوزن %", maximumScore: "أعلى درجة",
    description: "الوصف", descriptionAr: "الوصف بالعربية", totalWeight: "مجموع الأوزان النشطة",
    newRecord: "سجل جديد", editRecord: "تعديل السجل", save: "حفظ", cancel: "إلغاء",
    edit: "تعديل", activate: "تفعيل", deactivate: "إيقاف", actions: "إجراءات",
    status: "الحالة", active: "نشط", inactive: "موقوف",
    search: "بحث", searchHint: "الرمز أو الاسم", refresh: "تحديث",
    noSignatures: "لا توجد تواقيع معرّفة.", noCriteria: "لا توجد معايير تقييم معرّفة.",
    saved: "تم الحفظ.", showInactive: "عرض الموقوفة",
  },
  purchaseOrders: {
    title: "أوامر الشراء",
    breadcrumb: "المحاسبة العامة / المدفوعات / الحركات اليومية",
    purposeNotice: "التزام تجاه مورّد: ما ستأخذه الشركة وبأي سعر. غير أمر الصرف، الذي هو تعليمات بالدفع.",
    noAccountingNotice: "لا أثر محاسبي في أي مرحلة. المال يتحرك عند ترحيل فاتورة المورّد، وهي مستند آخر. «المفوتر» و«المدفوع» يُقرآن لحظة العرض من الفواتير التي تشير إلى الأمر ومن محرك التسويات — ولا يُخزَّنان.",
    newRecord: "أمر شراء جديد", editRecord: "تعديل أمر الشراء",
    searchHint: "رقم الأمر أو المرجع", noRows: "لا توجد أوامر شراء.", details: "تفاصيل أمر الشراء",
    orderNumber: "رقم الأمر", orderDate: "تاريخ الأمر", supplier: "المورّد",
    chooseSupplier: "اختر المورّد", branch: "الفرع", currency: "العملة",
    expectedDate: "التاريخ المتوقع", paymentTerm: "مدة السداد (يوم)", costCenter: "مركز التكلفة",
    supplierReference: "مرجع المورّد", taxInclusive: "الضريبة",
    taxInclusiveHint: "الأسعار شاملة الضريبة",
    lines: "البنود", addLine: "إضافة بند", removeLine: "حذف", item: "المادة",
    description: "البيان", quantity: "الكمية", unitPrice: "سعر الوحدة", discount: "الخصم",
    tax: "الضريبة", lineNet: "صافي البند", subtotal: "المجموع", netTotal: "الإجمالي",
    invoiced: "المفوتر", paid: "المدفوع", remaining: "المتبقي",
    beforeTax: "قبل الضريبة", taxComputedNote: "الضريبة تُحتسب على الخادم بنسبة السريان في تاريخ الأمر.",
    status: "الحالة", draft: "مسودة", approved: "معتمد", closed: "مغلق", cancelled: "ملغى",
    actions: "إجراءات", view: "عرض", edit: "تعديل", approve: "اعتماد", close: "إغلاق",
    cancel: "إلغاء", save: "حفظ", none: "بدون",
    saved: "تم حفظ أمر الشراء.", approvedOk: "تم اعتماد أمر الشراء.",
    closedOk: "تم إغلاق أمر الشراء.", cancelledOk: "تم إلغاء أمر الشراء.",
  },
  purchaseItems: {
    title: "مواد الشراء",
    breadcrumb: "المحاسبة العامة / المدفوعات / البيانات الأساسية",
    purposeNotice: "كتالوج ما تشتريه الشركة. مستقل عن أنواع مبيعات الخدمات: الشركة تشتري أشياء لا تبيعها.",
    defaultsNotice: "لا أثر محاسبي هنا. الحساب والضريبة ومركز التكلفة قيم افتراضية يَنسخها المستند مرة واحدة ثم يملكها — تغييرها لاحقاً لا يُحرّك رقماً مُرحّلاً. ولا يوجد حذف: المادة المستخدمة تُوقَف ولا تُمحى.",
    newRecord: "مادة جديدة", editRecord: "تعديل المادة",
    searchHint: "الرمز أو الاسم", noRows: "لا توجد مواد شراء.",
    code: "الرمز", name: "الاسم", nameAr: "الاسم بالعربية", itemType: "نوع المادة",
    service: "خدمة", goods: "بضاعة", unit: "الوحدة",
    defaultExpenseAccount: "حساب المصروف الافتراضي", defaultTaxType: "الضريبة الافتراضية",
    defaultCostCenter: "مركز التكلفة الافتراضي", usageCount: "مرات الاستخدام",
    status: "الحالة", active: "نشط", actions: "إجراءات", edit: "تعديل",
    activate: "تفعيل", deactivate: "إيقاف", save: "حفظ", notes: "ملاحظات",
    none: "بدون", saved: "تم حفظ المادة.",
  },
  paymentsInquiries: {
    title: "استعلامات المدفوعات",
    subtitle: "تسع شاشات للقراءة فقط على جانب الذمم الدائنة. لا تُنشئ قيدًا ولا تعدّل مستندًا.",
    readOnly: "شاشة قراءة فقط — لا تُنشئ أو تعدّل أي قيد أو مستند.",
    scopeNote: "تُعرض الموردون المسموح لك بالاستعلام عنهم فقط.",
    supplier: "المورّد",
    allSuppliers: "كل الموردين",
    chooseSupplier: "اختر المورّد",
    supplierRequired: "يجب اختيار مورّد لعرض كشف الحساب.",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    branch: "الفرع",
    currency: "العملة",
    costCenter: "مركز التكلفة",
    account: "الحساب",
    documentType: "نوع المستند",
    documentNumber: "رقم المستند",
    documentDate: "تاريخ المستند",
    reference: "المرجع",
    description: "البيان",
    amountFrom: "المبلغ من",
    amountTo: "المبلغ إلى",
    debit: "مدين",
    credit: "دائن",
    netMovement: "صافي الحركة",
    balance: "الرصيد",
    status: "الحالة",
    postedOnly: "المرحّلة فقط",
    includeReversed: "تضمين المعكوسة",
    reversed: "معكوس",
    statusDraft: "مسودة",
    statusAudited: "مدققة",
    statusPosted: "مرحّلة",
    statusRejected: "مرفوضة",
    statusCancelled: "ملغاة",
    statusReversed: "معكوسة",
    paymentMethod: "طريقة الدفع",
    user: "المستخدم",
    all: "الكل",
    yes: "نعم",
    no: "لا",
    none: "—",
    totalDebit: "إجمالي المدين",
    totalCredit: "إجمالي الدائن",
    rowCount: "عدد الأسطر",
    side: "الطرف",
    sideDebit: "المدين فقط",
    sideCredit: "الدائن فقط",
    open: "فتح",
  },
  supplierPayables: {
    title: "إجمالي ذمم الموردين",
    description: "سطر واحد لكل مورد: الحركة، الرصيد، الذمم المستحقة والدفعات المقدمة.",
    code: "الرمز",
    name: "اسم المورّد",
    controlAccount: "الحساب الوسيط",
    accountingBalance: "الرصيد المحاسبي",
    payable: "ذمم مستحقة",
    advance: "دفعات مقدمة",
    balanceType: "نوع الرصيد",
    payableOnly: "المستحق فقط",
    advanceOnly: "الدفعات المقدمة فقط",
    zeroOnly: "الأرصدة الصفرية فقط",
    minimumBalance: "أدنى رصيد",
    maximumBalance: "أعلى رصيد",
    openDocuments: "المستندات المفتوحة",
    withOpenOnly: "من لديه مستندات مفتوحة",
    withoutOpenOnly: "من ليس لديه مستندات مفتوحة",
    unsettledInvoices: "فواتير غير مسددة",
    unappliedPayments: "دفعات غير مخصّصة",
    lastMovement: "آخر حركة",
    creditLimit: "سقف الائتمان",
    availableCredit: "المتاح من السقف",
    supplierCount: "عدد الموردين",
    totalPayable: "إجمالي الذمم",
    totalAdvance: "إجمالي الدفعات المقدمة",
    activeOnly: "النشطون فقط",
    inactiveOnly: "غير النشطين فقط",
    noSuppliers: "لا يوجد موردون مطابقون",
    ledgerNote: "الأرصدة محسوبة من قيود الأستاذ المرحّلة على الحساب الوسيط لكل مورّد.",
  },
  supplierAccountMovements: {
    title: "حركات حساب مورد",
    description: "كشف حساب مورد واحد برصيد افتتاحي ورصيد متحرك.",
    openingBalance: "الرصيد الافتتاحي",
    closingBalance: "الرصيد الختامي",
    runningBalance: "الرصيد المتحرك",
    pickSupplier: "اختر مورّدًا لعرض كشف حسابه.",
    noMovements: "لا توجد حركات في هذا النطاق",
    balanceNote: "الرصيد الافتتاحي هو صافي الحركة قبل «من تاريخ»، والرصيد المتحرك يتراكم عليه.",
    supplierInvoiceNumber: "رقم فاتورة المورد",
    supplierInvoiceDate: "تاريخ فاتورة المورد",
    dueDate: "تاريخ الاستحقاق",
    natureDebit: "مدين",
    natureCredit: "دائن",
  },
  supplierMovements: {
    title: "حركات المورد",
    description: "نفس الحركات عبر جميع الموردين المسموح لك بالاستعلام عنهم.",
    noMovements: "لا توجد حركات في هذا النطاق",
    everySupplier: "بلا رصيد متحرك — الرصيد المتحرك بلا معنى عبر أكثر من مورّد.",
  },
  paymentsDailyTransactions: {
    title: "الحركات اليومية للمدفوعات",
    description: "كل مستندات الذمم الدائنة الصادرة في فترة، المحاسبية وغير المحاسبية.",
    documentFamily: "عائلة المستند",
    nature: "الطبيعة",
    accounting: "محاسبي",
    nonAccounting: "غير محاسبي",
    accountingCount: "مستندات محاسبية",
    nonAccountingCount: "مستندات غير محاسبية",
    totalAccountingAmount: "إجمالي المبالغ المحاسبية",
    createdAt: "تاريخ الإنشاء",
    noDocuments: "لا توجد مستندات في هذا النطاق",
    natureNote: "المستندات غير المحاسبية — مثل أمر الدفع — لا تُحرّك الأستاذ، ولا تدخل في الإجمالي المحاسبي.",
    families: {
      ServicePurchaseRequest: "طلب شراء خدمات",
      ServicePurchaseInvoice: "فاتورة شراء خدمات",
      ServicePurchaseReturn: "مردود شراء خدمات",
      PaymentOrder: "أمر صرف",
      PaymentVoucher: "سند صرف",
      SupplierDebitNote: "إشعار مدين لمورّد",
      SupplierCreditNote: "إشعار دائن لمورّد",
      SupplierTransactionTransfer: "نقل حركات مورّد",
      PrepaidExpenseSchedule: "جدول مصروف مدفوع مقدمًا",
      PrepaidExpenseRecognition: "اعتراف بمصروف مقدم",
      SupplierSettlement: "تسوية مورّد",
      SettlementCancellation: "إلغاء تسوية",
      SupplierBalanceReminder: "تذكير برصيد مورّد",
      SupplierInformationAdjustment: "تعديل بيانات مورّد",
      SupplierCreditLimitAdjustment: "تعديل سقف ائتمان مورّد",
      PaymentBook: "دفتر صرف",
    },
  },
  paymentVoucherMovements: {
    title: "حركات سندات الصرف",
    description: "سندات الصرف مع توزيعها الفعلي على طرق الدفع.",
    voucherNumber: "رقم السند",
    voucherDate: "تاريخ السند",
    paidTo: "المدفوع له",
    directAccount: "حساب مباشر",
    paymentOrder: "أمر الدفع",
    components: "مكوّنات الدفع",
    componentCount: "عدد المكوّنات",
    cashbox: "الصندوق",
    bankAccount: "الحساب البنكي",
    localAmount: "بالعملة المحلية",
    includeDirect: "تضمين الدفعات المباشرة",
    postedCount: "المرحّلة",
    reversedCount: "المعكوسة",
    totalAmount: "الإجمالي",
    noVouchers: "لا توجد سندات صرف في هذا النطاق",
    splitNote: "يُعرض التوزيع كما سُجّل على السند؛ لا تُختلق طريقة دفع واحدة للسندات المقسّمة.",
    viewComponents: "عرض المكوّنات",
    hideComponents: "إخفاء المكوّنات",
  },
  servicePurchaseDocuments: {
    title: "حركات مستندات شراء الخدمات",
    description: "الطلب ثم الفاتورة ثم المردود، في سلسلة واحدة.",
    request: "طلب شراء",
    invoice: "فاتورة",
    documentReturn: "مردود",
    supplierReference: "مرجع المورّد",
    sourceDocument: "المستند المصدر",
    netAmount: "الصافي",
    taxAmount: "الضريبة",
    totalAmount: "الإجمالي",
    requestCount: "الطلبات",
    invoiceCount: "الفواتير",
    returnCount: "المردودات",
    totalNet: "إجمالي الصافي",
    totalTax: "إجمالي الضريبة",
    includeLines: "تضمين الأسطر",
    lineDescription: "وصف السطر",
    quantity: "الكمية",
    unitPrice: "سعر الوحدة",
    taxPercent: "نسبة الضريبة",
    expenseAccount: "حساب المصروف",
    noDocuments: "لا توجد مستندات في هذا النطاق",
    historicalNote: "القيم والنسب معروضة كما سُجّلت على المستند، لا كما هي في البيانات الأساسية اليوم.",
    viewLines: "عرض الأسطر",
    hideLines: "إخفاء الأسطر",
  },
  supplierCostCenterMovements: {
    title: "حركات المورد حسب مركز التكلفة",
    description: "قيود الموردين موزعة على مركز التكلفة الذي يحمله كل قيد فعليًا.",
    includeChildren: "تضمين المراكز الفرعية",
    costCenterOnly: "الحركات ذات مركز التكلفة فقط",
    uncosted: "بلا مركز تكلفة",
    uncostedCount: "حركات بلا مركز تكلفة",
    parentCostCenter: "المركز الأب",
    bySupplier: "حسب المورّد",
    byCostCenter: "حسب مركز التكلفة",
    byAccount: "حسب الحساب",
    subtotals: "المجاميع الفرعية",
    noMovements: "لا توجد حركات في هذا النطاق",
    authoritativeNote: "مركز التكلفة يُؤخذ من القيد نفسه فقط. لا يُستنتج من بطاقة المورّد ولا من الفرع ولا من أي مستند آخر.",
  },
  paymentBooksInquiry: {
    title: "دفاتر الصرف",
    description: "عهدة دفاتر الصرف: النطاقات، الحائزون، وحركات التسليم والاستلام.",
    bookNumber: "رقم الدفتر",
    fromNumber: "من رقم",
    toNumber: "إلى رقم",
    voucherCount: "عدد السندات",
    holder: "الحائز الحالي",
    holderType: "نوع الحائز",
    receivedDate: "تاريخ الاستلام",
    assignedDate: "تاريخ التسليم",
    returnedDate: "تاريخ الإرجاع",
    assignedFrom: "سُلّم من تاريخ",
    assignedTo: "سُلّم إلى تاريخ",
    lastMovement: "آخر حركة",
    movements: "حركات العهدة",
    viewMovements: "عرض حركات العهدة",
    hideMovements: "إخفاء حركات العهدة",
    totalBooks: "عدد الدفاتر",
    totalVouchers: "الأرقام المسجّلة",
    noBooks: "لا توجد دفاتر صرف مطابقة",
    custodyNote: "هذه الشاشة لا ترتبط بمورّد، فلا يسري عليها نطاق الموردين — ويسري عليها عزل الشركة والفرع وصلاحية الاستعلام.",
  },
  paymentBookRangeGaps: {
    title: "التسلسلات المفقودة لدفاتر الصرف",
    description: "الفجوات بين نطاقات دفاتر الصرف المسجّلة — أرقام لا ينتمي أي منها إلى دفتر مسجّل.",
    previousBook: "الدفتر السابق",
    previousEnd: "ينتهي عند",
    nextBook: "الدفتر التالي",
    nextStart: "يبدأ من",
    gapFrom: "الفجوة من",
    gapTo: "الفجوة إلى",
    gapSize: "عدد الأرقام",
    gapCount: "عدد الفجوات",
    unregisteredNumbers: "إجمالي الأرقام غير المسجّلة",
    booksExamined: "الدفاتر المفحوصة",
    rangeFrom: "النطاق من",
    rangeTo: "النطاق إلى",
    noGaps: "نطاقات دفاتر الصرف المسجّلة متصلة بلا فجوات",
    detectionNote: "دفاتر الدفع لا تستهلك أرقام سندات الدفع في هذا النظام، لذلك تُبلّغ هذه الشاشة عن نطاقات غير مسجّلة لأي دفتر فقط. لا تُحدّد سندات مفقودة أو ضائعة أو غير مستخدمة.",
  },
  expenseInquiries: {
    movements: "حركات المصروفات",
    account: "حساب المصروفات",
    movementsByCostCenter: "حركات المصروفات - مركز التكلفة",
    accountByCostCenter: "حساب المصروفات - مركز التكلفة",
    glNote: "هذه شاشة الأستاذ العام نفسها، والمنتقي محصور بحسابات المصروفات. لا يوجد محرك مصروفات منفصل في النظام.",
  },
  unsettledPaymentOrders: {
    title: "أوامر الصرف غير المدرجة في سندات الصرف",
    description: "أوامر صرف صدرت ولم تُسدَّد بالكامل بسندات صرف مُرحّلة.",
    orderNumber: "رقم الأمر", orderDate: "تاريخ الأمر",
    orderAmount: "قيمة الأمر", paidAmount: "المدفوع", remainingAmount: "المتبقي",
    settlementState: "حالة السداد", expectedPaymentDate: "تاريخ الدفع المتوقع",
    voucherCount: "عدد السندات", includeFullyPaid: "تضمين المسددة بالكامل",
    includeCancelled: "تضمين الملغاة", minimumRemaining: "أدنى متبقٍ",
    totalOrdered: "إجمالي الأوامر", unpaidCount: "غير مدفوعة",
    partiallyPaidCount: "مدفوعة جزئياً", unpaid: "غير مدفوع",
    partiallyPaid: "مدفوع جزئياً", paid: "مدفوع",
    noOrders: "لا توجد أوامر صرف غير مسددة",
    derivedNote: "المدفوع محسوب من سندات الصرف المُرحّلة فعلاً؛ عكس السند يخرجه من الحساب تلقائياً.",
  },
  supplierInformationInquiry: {
    title: "بيانات المورد",
    description: "بطاقة المورّد للقراءة فقط، ضمن نطاق استعلامك.",
    taxNumber: "الرقم الضريبي", commercialRegistration: "السجل التجاري",
    nationalId: "الرقم الوطني", phone: "الهاتف", mobile: "الجوال", email: "البريد الإلكتروني",
    address: "العنوان", city: "المدينة", country: "الدولة", creditLimit: "سقف الائتمان",
    activeCount: "النشطون", inactiveCount: "غير النشطين", active: "نشط", inactive: "غير نشط",
    noSuppliers: "لا يوجد موردون مطابقون",
    readOnlyNote: "شاشة عرض فقط. التعديل من البيانات الأساسية، والتسوية من مستند تعديل بيانات المورّد.",
  },
  supplierCurrencyStatement: {
    title: "حساب المورد - عملة / محلي",
    description: "كشف حساب مورّد بعملة الحركة وبالعملة المحلية معاً.",
    transactionCurrency: "عملة الحركة", exchangeRate: "سعر التحويل",
    foreignDebit: "مدين (عملة)", foreignCredit: "دائن (عملة)", foreignBalance: "الرصيد (عملة)",
    localDebit: "مدين (محلي)", localCredit: "دائن (محلي)", localBalance: "الرصيد (محلي)",
    openingForeign: "الافتتاحي (عملة)", openingLocal: "الافتتاحي (محلي)",
    closingForeign: "الختامي (عملة)", closingLocal: "الختامي (محلي)",
    ledgerCurrencies: "عملات الحركة", movements: "عدد الحركات",
    pickSupplier: "اختر مورّداً لعرض كشف حسابه بالعملات.",
    noMovements: "لا توجد حركات في هذا النطاق",
    rateNote: "سعر التحويل مقروء من القيد كما رُحّل، ولا يُعاد احتسابه من أسعار اليوم.",
  },
  supplierBalancesByCostCenter: {
    title: "إجمالي أرصدة الموردين - مركز التكلفة",
    description: "أرصدة الحساب الوسيط للموردين موزّعة على مركز التكلفة الذي يحمله كل قيد.",
    unassigned: "غير موزّع", unassignedAmount: "المبلغ غير الموزّع",
    includeUnassigned: "عرض غير الموزّع", includeZeroBalances: "عرض الأرصدة الصفرية",
    upToDate: "لغاية تاريخ", costCenterCount: "عدد مراكز التكلفة",
    noBalances: "لا توجد أرصدة في هذا النطاق",
    reconcileNote: "المجموع يطابق الحساب الوسيط تماماً لأن القيود بلا مركز تكلفة تظهر في سطر «غير موزّع» ولا تُستبعد.",
  },
  purchaseOrdersAwaitingPayment: {
    title: "أوامر الشراء بانتظار الدفع",
    description: "أوامر شراء معتمدة لم يُسدَّد التزامها بالكامل بعد.",
    orderNumber: "رقم أمر الشراء", committed: "قيمة الالتزام", invoiced: "المفوتر",
    uninvoiced: "غير مفوتر", expectedDate: "التاريخ المتوقع", paymentTerm: "مدة السداد (يوم)",
    includeSettled: "تضمين المسددة", includeNonOutstanding: "تضمين غير المعتمدة",
    totalCommitted: "إجمالي الالتزام", totalInvoiced: "إجمالي المفوتر",
    totalUninvoiced: "إجمالي غير المفوتر",
    noOrders: "لا توجد أوامر شراء بانتظار الدفع",
    genuineNote: "أوامر شراء حقيقية — وليست أوامر صرف. المفوتر مقروء من الفواتير التي تشير إلى الأمر، والمدفوع من محرك التسويات.",
  },
  combinedBooks: {
    title: "دفاتر القبض والصرف",
    description: "سجلا دفاتر القبض ودفاتر الصرف معاً، للقراءة فقط.",
    bookType: "نوع الدفتر", receiptBook: "دفتر قبض", paymentBook: "دفتر صرف",
    allBooks: "كل الدفاتر", holder: "الحائز", movementCount: "عدد الحركات",
    receiptBookCount: "دفاتر القبض", paymentBookCount: "دفاتر الصرف",
    registeredNumbers: "الأرقام المسجّلة",
    noBooks: "لا توجد دفاتر مطابقة",
    separateNote: "السجلان منفصلان بترقيم منفصل وحركات عهدة منفصلة — تُعرض معاً ولا تُدمج، وكل سطر يفتح سجله الأصلي.",
  },
  supplierItemTransactions: {
    title: "حركات المادة - المورد",
    description: "أسطر الشراء حسب مادة الشراء، بقيمها كما سُجّلت.",
    item: "المادة", allItems: "كل المواد", itemType: "نوع المادة",
    service: "خدمة", goods: "بضاعة", quantity: "الكمية", unit: "الوحدة",
    unitPrice: "سعر الوحدة", discount: "الخصم", net: "الصافي",
    uncatalogued: "بلا مادة مصنّفة", includeUncatalogued: "تضمين الأسطر بلا مادة",
    documentType: "نوع المستند", purchaseOrder: "أمر شراء", purchaseRequest: "طلب شراء",
    purchaseInvoice: "فاتورة شراء", purchaseReturn: "مردود شراء",
    totalQuantity: "إجمالي الكمية", itemCount: "عدد المواد",
    noTransactions: "لا توجد حركات مواد في هذا النطاق",
    historicalNote: "المواد من كتالوج الشراء وليس من أنواع مبيعات الخدمات. القيم كما سُجّلت على السطر، والمردودات بكمية سالبة.",
  },
  outstandingSupplierInvoices: {
    title: "فواتير الموردين المستحقة",
    description: "ما زال مستحقاً للموردين، فاتورة بفاتورة.",
    invoiceNumber: "رقم الفاتورة", invoiceDate: "تاريخ الفاتورة", dueDate: "تاريخ الاستحقاق",
    originalAmount: "القيمة الأصلية", appliedAmount: "المسدد", outstandingAmount: "المستحق",
    daysOutstanding: "عمر الفاتورة", daysOverdue: "أيام التأخير",
    overdueOnly: "المتأخرة فقط", outstandingOnly: "المستحقة فقط",
    minimumOutstanding: "أدنى مبلغ مستحق", asOfDate: "لغاية تاريخ",
    overdueCount: "عدد المتأخرة", overdueAmount: "المبلغ المتأخر",
    totalOriginal: "إجمالي القيمة", totalApplied: "إجمالي المسدد",
    totalOutstanding: "إجمالي المستحق",
    noInvoices: "لا توجد فواتير مستحقة",
    allocationNote: "الاستحقاق محسوب من التسويات الفعلية في محرك المطابقة، وليس من حالة الفاتورة.",
  },
  paidUnpaidSupplierInvoices: {
    title: "الفواتير المسددة وغير المسددة",
    description: "كل فاتورة مورّد مصنّفة حسب ما خُصّص لها فعلاً.",
    paymentState: "حالة السداد", paid: "مسددة", partiallyPaid: "مسددة جزئياً",
    unpaid: "غير مسددة", invoiceAmount: "قيمة الفاتورة", remainingAmount: "المتبقي",
    totalInvoiced: "إجمالي الفواتير", totalPaid: "إجمالي المسدد",
    totalRemaining: "إجمالي المتبقي",
    noInvoices: "لا توجد فواتير في هذا النطاق",
    engineNote: "التصنيف من محرك التسويات: «مسددة» تعني ألا يتبقى شيء، لا أن الفاتورة مُرحّلة.",
  },
  unappliedPaymentVouchers: {
    title: "سندات الصرف بدون فواتير مسددة",
    description: "مبالغ مدفوعة للمورّد لم تُخصَّص لأي فاتورة.",
    voucherAmount: "قيمة السند", appliedAmount: "المخصّص", unappliedAmount: "غير المخصّص",
    cashboxOrBank: "الصندوق / البنك", minimumUnapplied: "أدنى مبلغ غير مخصّص",
    includeFullyApplied: "تضمين المخصّصة بالكامل",
    totalVoucherAmount: "إجمالي السندات", totalUnapplied: "إجمالي غير المخصّص",
    noVouchers: "لا توجد سندات صرف غير مخصّصة",
    noGuessNote: "الربط بالتخصيص الفعلي فقط — لا مطابقة بالرقم أو بالمرجع أو بتساوي المبالغ.",
  },

  employees: {
    title: "بطاقة الموظف",
    breadcrumb: "إدارة الموارد البشرية « شؤون موظفين « البيانات الأساسية « بطاقة الموظف",
    purposeNotice: "سجل من توظّفهم الشركة: الرمز والاسم والوظيفة والقسم والفرع وبيانات الاتصال.",
    identityNotice:
      "مستخدم النظام المرتبط هو نفسه المستخدم الذي يسمّيه باقي النظام، وليس هوية ثانية. الحقل اختياري: معظم الموظفين لا يملكون حساب دخول.",
    noPayrollNotice:
      "بيانات أساسية فقط — لا راتب ولا قيمة عقد ولا رصيد إجازات ولا دوام ولا أي قيد محاسبي. لا يوجد حذف: سحب الموظف يُعطّله ويُبقي سجله مقروءاً.",
    vocabulariesEmpty:
      "قوائم الوظائف والأقسام وأنواع العقود فارغة. البطاقة تقرأ هذه القوائم ولا تنشئها — تُضاف لاحقاً من إعدادات الموارد البشرية.",
    readOnlyNotice: "لديك صلاحية الاطلاع فقط؛ لا يمكنك الحفظ أو التعديل.",
    listTitle: "الموظفون",
    formTitle: "بيانات الموظف",
    newRecord: "سجل جديد",
    search: "بحث",
    searchHint: "الرمز أو الاسم أو الهوية أو الجوال أو البريد",
    refresh: "تحديث",
    showInactive: "إظهار المعطّلين",
    noRows: "لا يوجد موظفون",
    branchFilter: "الفرع",
    allBranches: "كل الفروع",
    allDepartments: "كل الأقسام",
    linkFilter: "الارتباط بمستخدم",
    anyLink: "الكل",
    linkedOnly: "المرتبطون فقط",
    unlinkedOnly: "غير المرتبطين فقط",
    code: "رمز الموظف",
    nameAr: "الاسم بالعربي",
    nameEn: "الاسم بالإنجليزي",
    user: "مستخدم النظام",
    noUser: "بدون مستخدم",
    userHint: "نفس معرّف المستخدم المستخدم في باقي النظام.",
    jobTitle: "الوظيفة",
    noJobTitle: "بدون وظيفة",
    department: "القسم",
    noDepartment: "بدون قسم",
    contractType: "نوع العقد",
    noContractType: "بدون نوع عقد",
    branch: "الفرع",
    noBranch: "بدون فرع",
    hireDate: "تاريخ التعيين",
    nationalId: "رقم الهوية",
    mobile: "الجوال",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    notes: "ملاحظات",
    active: "نشط",
    actions: "إجراءات",
    edit: "تعديل",
    view: "عرض",
    activate: "تفعيل",
    deactivate: "تعطيل",
    save: "حفظ",
    saveNew: "حفظ وجديد",
    cancel: "إلغاء",
    details: "تفاصيل الموظف",
    updatedAt: "آخر تحديث",
    auditHistory: "سجل التغييرات",
    auditNone: "لا توجد تغييرات مسجّلة",
    auditAction: "الإجراء",
    auditFields: "الحقول",
    auditChangedBy: "بواسطة",
    auditChangedAt: "التاريخ",
    close: "إغلاق",
    saved: "تم الحفظ",
    activated: "تم التفعيل",
    deactivated: "تم التعطيل",
    counts: "الإجمالي / النشط",
    matched: "المطابق",
    loadError: "تعذّر تحميل الموظفين",
    saveError: "تعذّر الحفظ",
    auditError: "تعذّر تحميل سجل التغييرات",
  },
  settingsExport: {
    print: "طباعة",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    working: "جارٍ…",
    failed: "تعذّر التصدير",
  },
  hr: {
    employeeCard: "بطاقة الموظف", responsibleEntities: "الجهات المسؤولة", employeeRegister: "سجل الموظفين",
    inquiry: "استعلام", newRecord: "جديد", save: "حفظ", saveAndNew: "حفظ + جديد", cancel: "إلغاء",
    close: "إغلاق", edit: "تعديل", delete: "حذف", refresh: "تحديث", search: "بحث",
    searchAndFilter: "بحث وتصفية", all: "الكل", none: "—", actions: "إجراءات", status: "الحالة",
    active: "نشط", inactive: "غير نشط", activeRecord: "سجل فعّال", unsaved: "سجل جديد",
    saved: "تم الحفظ", deleted: "تم الحذف", loadFailed: "تعذّر تحميل البيانات",
    saveFailed: "تعذّر الحفظ", deleteFailed: "تعذّر الحذف",
    confirmDeleteTitle: "تأكيد الحذف", deleteEntityConfirm: "سيتم حذف الجهة «{name}».",
    code: "الرمز", nameAr: "الوصف عربي", nameEn: "الوصف إنجليزي", notes: "ملاحظات", employees: "الموظفون",
    newEntity: "جهة جديدة", editEntity: "تعديل الجهة", noEntities: "لا توجد جهات مطابقة",
    noEmployees: "لا يوجد موظفون مطابقون",
    selectEmployee: "اختر موظفاً من السجل أو ابدأ بطاقة جديدة",
    identification: "بيانات التعريف", serial: "التسلسل", employeeNumber: "رقم الموظف",
    firstNameAr: "الاسم الأول", secondNameAr: "الاسم الثاني", thirdNameAr: "الاسم الثالث",
    familyNameAr: "اسم العائلة",
    firstNameEn: "الاسم الأول إنجليزي", secondNameEn: "الاسم الثاني إنجليزي",
    thirdNameEn: "الاسم الثالث إنجليزي", familyNameEn: "اسم العائلة إنجليزي",
    photo: "صورة الموظف", uploadPhoto: "رفع صورة", removePhoto: "إزالة",
    photoSaved: "تم حفظ الصورة", photoFailed: "تعذّر رفع الصورة",
    personalInfo: "المعلومات الشخصية", nationality: "الجنسية", religion: "الديانة",
    gender: "الجنس", male: "ذكر", female: "أنثى",
    maritalStatus: "الحالة الاجتماعية", single: "أعزب", married: "متزوج",
    divorced: "مطلّق", widowed: "أرمل",
    nationalId: "الرقم الوطني", birthDate: "تاريخ الميلاد", birthDateHijri: "تاريخ الميلاد هجري",
    birthPlace: "مكان الولادة", motherName: "اسم الأم", specialNeeds: "من ذوي الاحتياجات الخاصة",
    address: "العنوان", country: "الدولة", governorate: "المحافظة", city: "المدينة", area: "المنطقة",
    addressLine: "العنوان التفصيلي", phone: "الهاتف", altPhone: "هاتف بديل", email: "البريد الإلكتروني",
    employmentInfo: "المعلومات الوظيفية", hireDate: "تاريخ التعيين", department: "القسم",
    jobTitle: "المسمى الوظيفي", employmentType: "نوع التوظيف", responsibleEntity: "الجهة المسؤولة",
    branch: "الفرع", costCenter: "مركز التكلفة", employmentStatus: "الحالة الوظيفية",
    statusActive: "على رأس عمله", statusOnLeave: "في إجازة", statusSuspended: "موقوف",
    statusTerminated: "منتهية خدمته", statusResigned: "مستقيل",
    jobDetails: "تفاصيل الوظيفة",
    contractSection: "العقد",
    insuranceSection: "الاشتراكات والتأمينات",
    secondaryJobTitle: "المسمى الوظيفي الثانوي",
    technicalClassification: "التصنيف الفني",
    grade: "الدرجة",
    employeeClassification: "تصنيف الموظف",
    directManager: "المدير المباشر",
    supervisor: "المشرف",
    dailyWorkingHours: "عدد ساعات العمل",
    probationPeriodDays: "مدة التجربة (يوم)",
    employmentStatusDate: "تاريخ الحالة",
    isHazardousOccupation: "مهن خطرة",
    allowsOvertime: "يسمح بالعمل الإضافي",
    spouseIsEmployed: "الزوج / الزوجة تعمل",
    isFamilyProvider: "معيل",
    payFrequency: "طبيعة تعاطي الأجر",
    payMonthly: "شهري",
    payDaily: "يومي",
    payHourly: "بالساعة",
    contractType: "نوع العقد",
    contractStart: "تاريخ بداية العقد الحالي",
    contractEnd: "تاريخ انتهاء العقد الحالي",
    isFixedTermContract: "محدد المدة",
    renewalMethod: "آلية احتساب تجديد العقد",
    renewalDaily: "بالأيام",
    renewalMonthly: "بالأشهر",
    renewalCalendar: "حسب التقويم",
    leaveCategory: "فئة الإجازة",
    isGosiRegistered: "مشترك في التأمينات الاجتماعية",
    gosiRegistrationDate: "تاريخ الاشتراك في التأمينات",
    gosiNumber: "رقم التأمينات الاجتماعية",
    gosiPolicy: "سياسة التأمينات الاجتماعية",
    hasHealthInsurance: "مشترك في التأمين الصحي",
    healthInsurancePolicy: "سياسة التأمين الصحي",
  },
  hrLeave: {
    leave: "الإجازات",
    leaveTypes: "أنواع الإجازات",
    leaveRequests: "طلبات الإجازات",
    leaveAmendments: "تعديل الإجازات",
    leaveBalanceAdjustments: "تعديل أرصدة الإجازات",
    leaveMovements: "حركات الإجازات",
    leaveBalances: "أرصدة إجازات الموظفين",
    new: "جديد",
    save: "حفظ",
    edit: "تعديل",
    delete: "حذف",
    cancel: "إلغاء",
    close: "إغلاق",
    refresh: "تحديث",
    search: "بحث",
    actions: "إجراءات",
    status: "الحالة",
    all: "الكل",
    none: "—",
    saved: "تم الحفظ",
    deleted: "تم الحذف",
    loadFailed: "تعذّر تحميل البيانات",
    saveFailed: "تعذّر الحفظ",
    deleteFailed: "تعذّر الحذف",
    noResults: "لا توجد نتائج",
    required: "حقل مطلوب",
    confirmDeleteTitle: "تأكيد الحذف",
    confirmDelete: "هل تريد حذف هذا السجل؟",
    yes: "نعم",
    no: "لا",
    days: "يوم",
    day: "يوم",
    typeCode: "الرمز",
    typeNameAr: "الاسم بالعربية",
    typeNameEn: "الاسم بالإنجليزية",
    isActive: "مفعّل",
    isPaid: "مدفوعة الأجر",
    requiresBalance: "تعتمد على رصيد",
    allowsNegativeBalance: "تسمح برصيد سالب",
    requiresAttachment: "تتطلب مرفقاً",
    minimumDays: "أقل مدة (يوم)",
    maximumDays: "أقصى مدة (يوم)",
    noticeDays: "مهلة الإشعار (يوم)",
    annualEntitlementDays: "الاستحقاق السنوي (يوم)",
    carryForwardAllowed: "يسمح بترحيل الرصيد",
    maximumCarryForwardDays: "أقصى رصيد مُرحّل (يوم)",
    notes: "ملاحظات",
    newLeaveType: "نوع إجازة جديد",
    editLeaveType: "تعديل نوع الإجازة",
    noLeaveTypes: "لم يُعرَّف أي نوع إجازة بعد",
    typeInUse: "مستخدم",
    deactivateInstead: "لا يمكن حذف نوع مستخدم — عطّله بدل ذلك.",
    policy: "السياسة",
    paid: "مدفوعة",
    unpaid: "غير مدفوعة",
    balanceDriven: "برصيد",
    noBalance: "بدون رصيد",
    number: "الرقم",
    employee: "الموظف",
    leaveType: "نوع الإجازة",
    requestDate: "تاريخ الطلب",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    returnDate: "تاريخ المباشرة",
    requestedDays: "الأيام المطلوبة",
    approvedDays: "الأيام المعتمدة",
    replacement: "البديل",
    branch: "الفرع",
    department: "القسم",
    reason: "السبب",
    newRequest: "طلب إجازة جديد",
    noRequests: "لا توجد طلبات",
    submit: "إرسال للاعتماد",
    approve: "اعتماد",
    reject: "رفض",
    cancelRequest: "إلغاء الطلب",
    rejectionReason: "سبب الرفض",
    submitted: "مُرسل",
    approved: "معتمد",
    rejected: "مرفوض",
    cancelledStatus: "ملغى",
    draft: "مسودة",
    available: "الرصيد المتاح",
    upcoming: "أيام قادمة",
    attachments: "المرفقات",
    addAttachment: "إضافة مرفق",
    noAttachments: "لا توجد مرفقات",
    amendments: "التعديلات",
    noAmendments: "لا توجد تعديلات",
    amend: "تعديل الإجازة",
    amendAction: "نوع التعديل",
    actionCancel: "إلغاء",
    actionExtend: "تمديد",
    actionInterrupt: "قطع",
    actionChangeCategory: "تغيير الفئة",
    effectiveDate: "تاريخ السريان",
    newToDate: "تاريخ النهاية الجديد",
    newLeaveType2: "نوع الإجازة الجديد",
    daysDifference: "فرق الأيام",
    oldDays: "الأيام قبل",
    newDays: "الأيام بعد",
    submittedAt: "أُرسل في",
    approvedAt: "اعتُمد في",
    rejectedAt: "رُفض في",
    cancelledAt: "أُلغي في",
    by: "بواسطة",
    adjustment: "تعديل رصيد",
    newAdjustment: "تعديل رصيد جديد",
    direction: "الاتجاه",
    increase: "صرف",
    decrease: "خصم",
    isOpeningBalance: "رصيد افتتاحي",
    adjustmentDays: "عدد الأيام",
    adjustmentReason: "سبب التعديل",
    noAdjustments: "لا توجد تعديلات رصيد",
    movementType: "نوع الحركة",
    movementOpeningBalance: "رصيد افتتاحي",
    movementEntitlement: "استحقاق",
    movementUsage: "استخدام",
    movementAdjustment: "تعديل",
    movementReversal: "عكس",
    balanceBefore: "الرصيد قبل",
    balanceAfter: "الرصيد بعد",
    sourceDocument: "المستند",
    entitled: "المستحق",
    used: "المستخدم",
    adjusted: "التعديلات",
    noMovements: "لا توجد حركات",
    noBalances: "لا توجد أرصدة",
    activeEmployeesOnly: "الموظفون الفعّالون فقط",
    asOf: "كما في تاريخ",
    filters: "التصفية",
    apply: "عرض",
    reset: "مسح",
    rows: "سجل",
    print: "طباعة",
    exportExcel: "تصدير Excel",
    allowSelfApproval: "يسمح باعتماد الطلب من مُرسِله",
    employmentStatus: "حالة التوظيف",
    groupBy: "التجميع حسب",
    groupByLeaveType: "نوع الإجازة",
    groupByEmployee: "الموظف",
    groupByDepartment: "القسم",
    groupByBranch: "الفرع",
    groupByMonth: "الشهر",
    statusOnLeave: "في إجازة",
    statusSuspended: "موقوف",
    statusTerminated: "منتهية خدمته",
    statusResigned: "مستقيل",
    statusActiveEmployee: "على رأس العمل",
  },
  hotelFrontDesk: {
    title: "الاستقبال",
    roomBoardTitle: "لوحة الغرف",
    masterDataTitle: "البيانات الأساسية",
    settingsTitle: "إعدادات الفندق",
    roomTypesTitle: "أنواع الغرف",
    roomsTitle: "الغرف",

    statusVacant: "شاغرة",
    statusOccupiedFirstNight: "ساكنة — الليلة الأولى",
    statusOccupiedContinuing: "ساكنة — إقامة مستمرة",
    statusOccupiedCheckoutToday: "ساكنة — مغادرة اليوم",
    statusVacantDirtyToday: "طالعة اليوم — لم تُنظف",
    statusVacantDirtyStale: "طالعة من قبل — لم تُنظف",
    statusReserved: "محجوزة (لم يسكن بعد)",
    statusOutOfService: "خارج الخدمة",
    needsCleaningBadge: "تحتاج تنظيف",

    book: "حجز",
    checkIn: "تسجيل دخول",
    checkOut: "تسجيل مغادرة",
    markCleaned: "تم التنظيف",
    flagNeedsCleaning: "تحتاج تنظيف",
    setOutOfService: "خارج الخدمة (صيانة)",
    returnToService: "إعادة للخدمة",
    outOfServiceReasonPrompt: "سبب الخروج من الخدمة",
    outOfServiceReasonHint: "اختياري — مثال: صيانة كهرباء، تسريب مويه",
    outOfServiceBadge: "صيانة",

    guestName: "اسم النزيل",
    guestPhone: "هاتف النزيل",
    findOrAddGuest: "بحث عن نزيل أو إضافة جديد",
    existingGuestFound: "تم العثور على نزيل سابق بنفس الرقم",
    newGuestWillBeCreated: "سيتم إنشاء بطاقة نزيل جديدة",

    checkInDate: "تاريخ الدخول",
    checkOutDate: "تاريخ الخروج",
    actualCheckOutDate: "تاريخ المغادرة الفعلي",
    nights: "عدد الليالي",
    nightlyRate: "سعر الليلة",
    discountAmount: "قيمة الخصم",
    paymentMethod: "طريقة الدفع",
    cash: "نقدي",
    visa: "فيزا",
    passportNumber: "رقم جواز السفر",
    totalPax: "عدد النزلاء",
    notes: "ملاحظات (الجنسية، إلخ)",

    code: "الرمز",
    name: "الاسم",
    nameAr: "الاسم بالعربي",
    pricePerNight: "سعر الليلة",
    serviceSalesType: "نوع مبيعات الخدمة",
    roomNumber: "رقم الغرفة",
    floor: "الطابق",
    roomType: "نوع الغرفة",
    outOfService: "خارج الخدمة",
    outOfServiceReason: "سبب الخروج من الخدمة",
    active: "فعال",

    defaultCostCenter: "مركز التكلفة الافتراضي",
    defaultGuestControlAccount: "حساب ذمم النزلاء الافتراضي",
    discountType: "نوع الخصم (قيمة ثابتة)",
    none: "بلا",
    cleaningDueTime: "موعد استحقاق التنظيف",

    save: "حفظ",
    cancel: "إلغاء",
    close: "إغلاق",
    confirm: "تأكيد",
    newRoomType: "نوع غرفة جديد",
    newRoom: "غرفة جديدة",

    invoiceCreatedNotice: "تم إنشاء مسودة فاتورة. بانتظار تدقيق واعتماد المحاسب.",
    loadFailed: "تعذر تحميل البيانات",
    saveFailed: "تعذر الحفظ",
    empty: "لا توجد بيانات",
  },
  hrDaily: {
    inquiry: "استعلام",
    new: "جديد",
    save: "حفظ",
    saveAndNew: "حفظ + جديد",
    print: "طباعة",
    cancel: "إلغاء",
    submit: "إرسال",
    approve: "اعتماد",
    reject: "رفض",
    execute: "تنفيذ",
    send: "إرسال",
    sent: "تم الإرسال",
    cancelDocument: "إلغاء المستند",
    close: "إغلاق",
    delete: "حذف",
    refresh: "تحديث",
    apply: "عرض",
    all: "الكل",
    none: "—",
    actions: "إجراءات",
    status: "الحالة",
    draft: "مسودة",
    submitted: "مُرسل",
    approved: "معتمد",
    rejected: "مرفوض",
    cancelled: "ملغى",
    executed: "منفّذ",
    number: "الرقم",
    employee: "الموظف",
    employeeNumber: "رقم الموظف",
    documentDate: "تاريخ المستند",
    fromDate: "من تاريخ",
    toDate: "إلى تاريخ",
    branch: "الفرع",
    department: "القسم",
    attachments: "المرفقات",
    addAttachment: "إضافة مرفق",
    noAttachments: "لا توجد مرفقات",
    audit: "سجل الإجراءات",
    submittedAt: "أُرسل",
    approvedAt: "اعتُمد",
    rejectedAt: "رُفض",
    cancelledAt: "أُلغي",
    executedAt: "نُفّذ",
    rejectionReason: "سبب الرفض",
    noResults: "لا توجد بيانات مطابقة لمعايير البحث",
    loadFailed: "تعذّر تحميل البيانات",
    saveFailed: "تعذّر الحفظ",
    saved: "تم الحفظ",
    openDocument: "فتح",
    remove: "إزالة",
    dailyTransactions: "الحركات اليومية",
    sectionLeave: "الإجازات",
    sectionDepartures: "المغادرات",
    sectionJobMovement: "الحركة الوظيفية",
    sectionContracts: "العقود والخدمة",
    sectionDocuments: "الوثائق",
    sectionTimeTravel: "العمل الإضافي والسفر",
    sectionConductHealth: "الإجراءات والصحة",
    sectionCustody: "العهد",
    sectionAdministration: "الإدارة والاتصالات",
    custodyNote: "عهد الموظفين تُدار في الأصول الثابتة. هذه الشاشة تعرض العهد المرتبطة بالموظفين ولا تنشئ سجلاً موازياً.",
    iqamaMovements: "حركات الإقامات",
    healthCertificates: "حركات الشهادات الصحية",
    employeeCustody: "حركات عهد الموظفين",
  },
  glGuide: {
    subtitleTree: "كل موديول، قسماً قسماً وشاشةً شاشة — مقروءاً من الموديول نفسه لا من قائمة منفصلة.",
    module: "الموديول",
    coverage: "موثّق {written} من {total} شاشة في هذا الموديول.",
    notWritten: "لم يُوثَّق بعد — الشاشة موجودة وشرحها لم يُكتب.",
    notBuilt: "قسم ضمن الهيكل المعتمد، لم يُبنَ بعد.",
    title: "دليل الحسابات",
    subtitle: "{count} شاشة — وظيفة كل واحدة، وكيف تُستخدم، وأين تظهر في الحسابات.",
    search: "ابحث في الدليل",
    noResults: "لا توجد نتائج مطابقة",
    openScreen: "فتح الشاشة ←",
    purpose: "الوظيفة",
    usage: "طريقة الاستخدام",
    accounting: "الأثر المحاسبي",
    caution: "انتبه",
  },
  systemSettings: {
    search: "بحث",
    add: "إضافة",
    edit: "تعديل",
    save: "حفظ",
    cancel: "إلغاء",
    close: "إغلاق",
    delete: "حذف",
    remove: "إزالة",
    retry: "إعادة المحاولة",
    saved: "تم الحفظ",
    deleted: "تم الحذف",
    actions: "إجراءات",
    status: "الحالة",
    active: "نشط",
    inactive: "غير نشط",
    all: "الكل",
    none: "لا يوجد",
    yes: "نعم",
    no: "لا",
    noResults: "لا توجد نتائج",
    showing: "عرض {from}–{to} من {total}",
    previous: "السابق",
    next: "التالي",
    code: "الرمز",
    name: "الاسم",
    nameAr: "الاسم بالعربية",
    description: "الوصف",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    address: "العنوان",
    city: "المدينة",
    country: "الدولة",
    loadError: "تعذّر تحميل البيانات",
    saveError: "تعذّر الحفظ",
    deleteError: "تعذّر الحذف",
    confirmDeleteTitle: "تأكيد الحذف",

    users: "المستخدمون",
    newUser: "مستخدم جديد",
    editUser: "تعديل المستخدم",
    userName: "اسم المستخدم",
    fullName: "الاسم الكامل",
    fullNameAr: "الاسم الكامل بالعربية",
    password: "كلمة المرور",
    newPassword: "كلمة المرور الجديدة",
    showPassword: "إظهار",
    hidePassword: "إخفاء",
    confirmPassword: "تأكيد كلمة المرور",
    passwordMismatch: "كلمتا المرور غير متطابقتين",
    language: "لغة الواجهة",
    english: "الإنجليزية",
    arabic: "العربية",
    userRoles: "الأدوار",
    companyAccess: "الشركات المسموح بها",
    allCompanies: "كل شركات المنشأة",
    defaultCompany: "الشركة الافتراضية",
    lastLogin: "آخر دخول",
    neverSignedIn: "لم يدخل بعد",
    mustChangePassword: "تغيير كلمة المرور عند الدخول",
    systemAdministrator: "مدير النظام",
    resetPassword: "تعيين كلمة مرور",
    passwordWasReset: "تم تعيين كلمة المرور",
    assignRoles: "إسناد الأدوار",
    rolesAssigned: "تم تحديث الأدوار",
    deleteUserConfirm: "سيتم حذف المستخدم «{name}».",
    statusPending: "قيد التفعيل",
    statusActive: "نشط",
    statusSuspended: "موقوف",
    statusLocked: "مقفل",
    statusDisabled: "معطّل",
    noUsers: "لا يوجد مستخدمون مطابقون",

    roles: "الأدوار",
    newRole: "دور جديد",
    editRole: "تعديل الدور",
    systemRole: "دور نظام",
    userCount: "المستخدمون",
    permissionCount: "الصلاحيات",
    editPermissions: "تعديل الصلاحيات",
    permissionsSaved: "تم تحديث الصلاحيات",
    deleteRoleConfirm: "سيتم حذف الدور «{name}».",
    selectAll: "تحديد الكل",
    clearAll: "إلغاء التحديد",
    notGrantable: "لا تملك هذه الصلاحية فلا يمكنك منحها",
    granted: "ممنوحة: {count}",
    noRoles: "لا توجد أدوار مطابقة",

    permissions: "الصلاحيات",
    permissionKey: "المفتاح",
    module: "الوحدة",
    grantedToRoles: "ممنوحة للأدوار",
    grantedToNoRole: "غير ممنوحة لأي دور",
    catalogueCounts: "{permissions} صلاحية في {modules} وحدة",

    auditTrail: "سجل تدقيق النظام",
    occurredAt: "التاريخ والوقت",
    user: "المستخدم",
    action: "الإجراء",
    entity: "الكيان",
    record: "السجل",
    summary: "الوصف",
    changedFields: "الحقول المتغيّرة",
    ipAddress: "عنوان IP",
    correlationId: "معرّف الطلب",
    viewDetails: "التفاصيل",
    valuesBefore: "القيم قبل",
    valuesAfter: "القيم بعد",
    dateFrom: "من تاريخ",
    dateTo: "إلى تاريخ",
    anyAction: "كل الإجراءات",
    anyEntity: "كل الكيانات",
    noAuditEntries: "لا توجد حركات مطابقة",
    actionCreated: "إنشاء",
    actionUpdated: "تعديل",
    actionDeleted: "حذف",
    actionLogin: "دخول",
    actionLoginFailed: "محاولة دخول فاشلة",
    actionLogout: "خروج",
    actionPermissionDenied: "رفض صلاحية",
    actionJournalAudited: "تدقيق قيد",
    actionJournalPosted: "ترحيل قيد",
    actionJournalRejected: "رفض قيد",
    actionJournalReversed: "عكس قيد",
    actionJournalReopened: "فتح قيد مرحّل للتعديل",
    actionExported: "تصدير",

    accessMatrix: "مصفوفة صلاحيات المستخدمين",
    effectivePermissions: "الصلاحيات الفعلية",
    viaRoles: "عبر الأدوار",
    holdsPermission: "يملك الصلاحية",
    anyPermission: "كل الصلاحيات",
    noAccess: "بلا أي دور",

    companies: "المنشآت",
    newCompany: "منشأة جديدة",
    editCompany: "تعديل المنشأة",
    legalName: "الاسم القانوني",
    taxNumber: "الرقم الضريبي",
    registrationNumber: "رقم السجل التجاري",
    website: "الموقع الإلكتروني",
    localCurrency: "عملة التقارير",
    fiscalYearStart: "بداية السنة المالية",
    branchCount: "الفروع",
    deleteCompanyConfirm: "سيتم حذف المنشأة «{name}».",
    noCompanies: "لا توجد منشآت مطابقة",
    branches: "الفروع",
    newBranch: "فرع جديد",
    editBranch: "تعديل الفرع",
    company: "المنشأة",
    anyCompany: "كل المنشآت",
    headOffice: "المركز الرئيسي",
    deleteBranchConfirm: "سيتم حذف الفرع «{name}».",
    noBranches: "لا توجد فروع مطابقة",
    query: "استعلام",
    newRecord: "جديد",
    saveAndNew: "حفظ + جديد",
    userInformation: "بيانات المستخدم",
    userRegister: "سجل المستخدمين",
    loginName: "رمز الدخول",
    arabicDescription: "الوصف عربي",
    englishDescription: "الوصف إنجليزي",
    defaultLanguage: "اللغة الافتراضية",
    createdAt: "تاريخ الإنشاء",
    passwordChangedAt: "آخر تغيير لكلمة المرور",
    lockedOutUntil: "مقفل حتى",
    failedAttempts: "محاولات دخول فاشلة",
    notSaved: "غير محفوظ",
    unsavedRecord: "سجل جديد",
    selectUser: "اختر مستخدماً من السجل أو ابدأ سجلاً جديداً",
    changePassword: "تغيير كلمة المرور",
    passwordOnCreateOnly: "تُحدَّد كلمة المرور عند الإنشاء، ثم تُغيَّر من زر تغيير كلمة المرور",
    defaultCompanyBranch: "الشركة والفرع الافتراضيان",
    defaultBranch: "الفرع الافتراضي",
    tabPermissions: "الصلاحيات",
    tabCompanies: "الشركات والفروع",
    tabRestricted: "الوصول المقيّد",
    assigned: "مُسند",
    permissionsFromRoles: "الصلاحيات الفعلية مشتقّة من الأدوار المسندة",
    noPermissions: "لا توجد صلاحيات",
    grantedByRole: "عبر",
    restrictedSuppliers: "الموردون المسموح بهم",
    restrictedCustomers: "العملاء المسموح بهم",
    restrictedCashboxes: "الصناديق المسموح بها",
    openManager: "فتح الشاشة",
    accessRows: "{count} سجل",
    unrestricted: "بلا تقييد",
    notSupported: "غير مدعوم في هذا النظام",
    fiscalYearAccess: "صلاحيات السنوات المالية",
    blockedAccounts: "الحسابات المحجوبة",
    reminderNotifications: "إشعارات التذكير",
    interactiveScreens: "الشاشات التفاعلية",
    specialPermissions: "الصلاحيات الخاصة",
    twoFactor: "التحقق بخطوتين",
    cloudUser: "مستخدم Cloud",
    userCode: "الرمز",
    identificationCode: "كود التعريف",
    expiryDate: "تاريخ الانتهاء",
    months: [
      "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
      "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
    ],
  },
};

const en: Dictionary = {
  cashBanksInquiries: {
    readOnly: "A read-only inquiry. It creates and changes nothing.",
    date: "Date",
    dateFrom: "From date",
    dateTo: "To date",
    asOfDate: "As of date",
    asOfHint: "The balance is taken from postings up to this date and no later.",
    branch: "Branch",
    currency: "Currency",
    costCenter: "Cost centre",
    cashbox: "Cashbox",
    bank: "Bank",
    bankAccount: "Bank account",
    party: "Party",
    search: "Search",
    all: "All",
    unassigned: "Unassigned",
    voucher: "Voucher",
    account: "Account",
    description: "Description",
    reference: "Reference",
    status: "Status",
    debit: "Debit",
    credit: "Credit",
    amount: "Amount",
    localAmount: "In base currency",
    exchangeRate: "Rate",
    runningBalance: "Running balance",
    openingBalance: "Opening balance",
    closingBalance: "Closing balance",
    balance: "Balance",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    count: "Count",
    noRows: "No results match these filters.",
    perCurrency: "Totals are shown per currency.",
    movementType: "Movement type",
    direction: "Direction",
    counterparty: "Counterparty",
    cashIn: "In",
    cashOut: "Out",
    netMovement: "Net movement",
    mtDeposit: "Deposit",
    mtZeroing: "Zeroing",
    mtFunding: "Cashbox funding",
    mtCount: "Count sheet",
    mtRepresentative: "Representative settlement",
    dirFrom: "Out of the cashbox",
    dirTo: "Into the cashbox",
    code: "Code",
    inClosingRoutine: "In closing routine",
    zeroings: "Zeroings",
    lastZeroing: "Last zeroing",
    lastCount: "Last count",
    closingOnly: "Closing cashboxes only",
    outstandingOnly: "Still holding money only",
    zeroedBoxes: "Zeroed",
    outstandingBoxes: "Still holding money",
    totalOutstanding: "Total outstanding",
    chequeNumber: "Cheque number",
    chequeDate: "Cheque date",
    dueDate: "Due date",
    dueFrom: "Due from",
    dueTo: "Due to",
    term: "Term",
    daysOverdue: "Days overdue",
    daysUnderCollection: "Days with the bank",
    depositedDate: "Deposited on",
    deliveredDate: "Delivered on",
    deliveredTo: "Delivered to",
    returnedDate: "Returned on",
    returnReason: "Return reason",
    clearedTo: "Collected into",
    sourceDocument: "Source document",
    settlementVoucher: "Settlement voucher",
    movements: "Movements",
    chequeTotal: "Total cheques",
    movement: "Movement",
    fromStatus: "From",
    toStatus: "To",
    journal: "Journal",
    noJournal: "No journal",
    recordedBy: "Recorded by",
    recordedAt: "Recorded at",
    postedOnly: "Movements with a journal only",
    postedMovements: "Posted",
    operationalMovements: "Operational",
    finding: "Finding",
    book: "Book",
    fromNumber: "From",
    toNumber: "To",
    leaves: "Leaves",
    leafStatus: "Leaf status",
    skippedLeaf: "Skipped leaf",
    unregisteredRange: "Unregistered range",
    gapsHint: "This screen never claims a cheque was written and lost. It reports a leaf that was skipped, or a range of numbers no registered book covers.",
    leavesAffected: "Leaves affected",
    documentKind: "Document kind",
    from: "From",
    to: "To",
    dkCashDeposit: "Cash deposit",
    dkCashWithdrawal: "Cash withdrawal",
    dkCashboxTransaction: "Cashbox movement",
    dkBankTransaction: "Bank transaction",
    dkBankTransfer: "Bank transfer",
    posted: "Posted",
    unposted: "Unposted",
    documents: "Documents",
    creditNumber: "Credit number",
    beneficiary: "Beneficiary",
    facility: "Facility",
    facilityCeiling: "Facility ceiling",
    facilityCommitted: "Facility committed",
    facilityRemaining: "Facility remaining",
    creditAmount: "Credit value",
    margin: "Cash margin",
    openedDate: "Opened",
    expiryDate: "Expiry",
    daysToExpiry: "Days to expiry",
    openOnly: "Open only",
    expiredOnly: "Expired only",
    openCredits: "Open",
    expiredCredits: "Expired",
    totalMargin: "Total margin",
    loanNumber: "Loan number",
    principal: "Principal",
    interestRate: "Rate %",
    installments: "Instalments",
    paidInstallments: "Paid",
    remainingInstallments: "Remaining",
    overdueInstallments: "Overdue",
    paidPrincipal: "Principal paid",
    outstandingPrincipal: "Principal outstanding",
    scheduledInterest: "Scheduled interest",
    nextDue: "Next due",
    overdueOnly: "With overdue instalments only",
    activeLoans: "Active",
    overdueLoans: "Overdue",
    screens: {
      cashboxClosing: { title: "Cashbox Closing Transactions", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cashbox Closing Transactions", description: "The صفر الصندوق documents — the closing routine as the system records it." },
      bankAccountCurrency: { title: "Bank Account — Currency — Local", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Bank Account — Currency — Local", description: "Each bank account's balance in its own currency and in the base-currency equivalent." },
      bankAccount: { title: "Bank Account", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Bank Account", description: "One bank account's ledger, with a server-side running balance." },
      accountMovements: { title: "Account Movements", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Account Movements", description: "Cash and bank account movements together." },
      dailyTransactions: { title: "Daily Transactions", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Daily Transactions", description: "Every Cash & Banks document raised in the period, whatever its kind." },
      chequeMovements: { title: "Cheque Movements", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cheque Movements", description: "The cheque history as it was written, never reconstructed from current state." },
      paidCheques: { title: "Paid Cheques", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Paid Cheques", description: "Cheques the company wrote." },
      receivedCheques: { title: "Received Cheques", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Received Cheques", description: "Cheques received from customers, and where they are in collection." },
      lettersOfCredit: { title: "Letters of Credit", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Letters of Credit", description: "The credit register, with facility utilisation." },
      chequeDelivery: { title: "Immediate / Post-Dated Cheque Delivery", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Immediate / Post-Dated Cheque Delivery", description: "Cheques we wrote that have actually been handed over." },
      undeliveredCheques: { title: "Undelivered Cheques", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Undelivered Cheques", description: "Cheques written and still in the drawer." },
      cashboxMovements: { title: "Cashbox Movements", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cashbox Movements", description: "Every cashbox movement, whatever its kind." },
      cashboxAccount: { title: "Cashbox Account", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cashbox Account", description: "A cashbox's ledger, with a server-side running balance." },
      cashboxCount: { title: "Cashbox Count", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cashbox Count", description: "The cashbox count sheets as they were recorded." },
      cashboxClosingFollowUp: { title: "Cashbox Closing Follow-up", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Cashbox Closing Follow-up", description: "Which cashboxes have been zeroed, and which still hold money." },
      missingChequeSequences: { title: "Missing Cheque Sequences", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Missing Cheque Sequences", description: "Gaps in the registered cheque numbering." },
      bankLoans: { title: "Bank Loans", breadcrumb: "Financial Management › Cash & Banks › Inquiries › Bank Loans", description: "The loan register with its schedule figures." },
    },
  },
  issueCheque: {
    bankAccount: "Drawn on account",
    beneficiary: "Beneficiary",
    chequeNumber: "Cheque number",
    chequeNumberHint: "The bank's number printed on the paper, never an ERP voucher number.",
    fromChequeBook: "From a chequebook",
    chooseLeaf: "Choose a cheque leaf",
    leafHint: "Choosing a leaf fills the number and consumes the leaf on save. Used leaves never appear.",
    manualNumber: "Type the number",
    chequeDate: "Cheque date",
    dueDate: "Due date",
    amount: "Amount",
    currency: "Currency",
    notes: "Notes",
    term: "Cheque term",
    issue: "Issue the cheque",
    issuing: "Issuing…",
    issued: "Cheque issued.",
    recent: "Cheques issued",
    empty: "No cheques written yet.",
    loading: "Loading…",
    required: "This field is required.",
    readOnlyNotice: "You have view access only. You cannot write cheques.",
    clear: "Clear",
    cancel: "Cancel",
    chooseAccount: "Choose the bank account",
    chooseBeneficiary: "Choose the beneficiary",
    chooseCurrency: "Choose the currency",
    immediateAccounting: "Entry: Dr the beneficiary, Cr the bank. The bank is credited the moment the cheque is written.",
    deferredAccounting: "Entry: Dr the beneficiary, Cr deferred cheques payable. The bank is not credited until the cheque is paid.",
    accountingTitle: "Accounting effect",
  },
  chequeBooksScreen: {
    title: "Chequebooks",
    breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Withdraw / Deliver from Cheque Cashbox",
    description: "Books of blank cheque leaves. Stationery control — it posts no journal.",
    numberingHint: "A cheque leaf number is the bank's printed number. It never becomes an ERP voucher number.",
    bankAccount: "Bank account",
    bookNumber: "Book number",
    fromChequeNumber: "First cheque number",
    toChequeNumber: "Last cheque number",
    rangeHint: "The leaves are derived from the range, keeping any leading zeros.",
    leafCount: "Leaves",
    receivedDate: "Received on",
    cashbox: "Cheque cashbox",
    isActive: "Active",
    notes: "Notes",
    leaves: "Leaves",
    leafNumber: "Leaf number",
    leafStatus: "Status",
    leafUnused: "Unused",
    leafWithdrawn: "Withdrawn",
    leafUsed: "Used",
    leafVoid: "Spoiled",
    withdrawnBy: "Withdrawn by",
    withdrawnDate: "Withdrawn on",
    withdraw: "Withdraw",
    withdrawHint: "The step between receiving a book and writing a cheque. A blank leaf is worth nothing until it is written.",
    counts: "Leaf status",
    newBook: "New book",
    save: "Save",
    cancel: "Cancel",
    search: "Search by book or cheque number",
    empty: "No chequebooks registered.",
    noLeaves: "No leaves.",
    loading: "Loading…",
    saving: "Saving…",
    saved: "Saved.",
    withdrawn: "Leaf withdrawn.",
    required: "This field is required.",
    readOnlyNotice: "You have view access only.",
    actions: "Actions",
    refresh: "Refresh",
    rangeLocked: "A registered book's range cannot change — its leaves exist and some carry real cheques.",
  },
  bankOps: {
    voucherNumber: "Voucher number",
    documentDate: "Document date",
    valueDate: "Bank value date",
    branch: "Branch",
    description: "Description",
    bank: "Bank",
    bankAccount: "Bank account",
    counterAccount: "Counter account",
    counterAccountHint: "The account the charge or credit belongs to. Chosen per movement, never assumed.",
    costCenter: "Cost centre",
    facility: "Facility",
    currency: "Currency",
    amount: "Amount",
    exchangeRate: "Exchange rate",
    localAmount: "Local amount",
    bankReference: "Bank reference",
    notes: "Notes",
    kind: "Movement kind",
    direction: "Direction",
    directionIncrease: "Account increases",
    directionDecrease: "Account decreases",
    directionHint: "Stated from our books' point of view, not the bank statement's.",
    kindCharges: "Bank charges and commissions",
    kindInterestEarned: "Interest earned",
    kindInterestCharged: "Interest charged",
    kindOtherCredit: "Other increase",
    kindOtherDebit: "Other decrease",
    status: "Status",
    statusDraft: "Draft",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    balanced: "The entry balances",
    save: "Save",
    post: "Post",
    reverse: "Reverse",
    reverseHint: "A mirrored counter-entry is created. The original is never edited.",
    reversalDate: "Reversal date",
    reason: "Reason",
    delete: "Delete",
    deleteConfirm: "This document and its voucher will be removed. Continue?",
    newRecord: "New movement",
    search: "Search by voucher number or reference",
    empty: "No movements recorded.",
    loading: "Loading…",
    saving: "Saving…",
    saved: "Saved.",
    posted: "Posted to the General Ledger.",
    readOnlyNotice: "You have view access only.",
    required: "This field is required.",
    cancel: "Cancel",
    actions: "Actions",
    refresh: "Refresh",
    fromAccount: "From account",
    toAccount: "To account",
    sentAmount: "Amount sent",
    receivedAmount: "Amount received",
    fee: "Fee",
    feeAccount: "Fee account",
    feeBorneBySource: "Fee borne by the sending account",
    transferHint: "Sent = received + fee. One document and one journal, never two loose movements.",
    fxHint: "A cross-currency transfer uses the bank's two rates; any difference goes to the currency-difference account.",
    reconciliationNumber: "Reconciliation number",
    period: "Period",
    fromDate: "From",
    toDate: "To",
    statementDate: "Statement date",
    statementBalance: "Balance per bank statement",
    ledgerBalance: "Balance per our books",
    reconciledBalance: "Reconciled balance",
    difference: "Difference",
    differenceHint: "A non-zero difference is a finding, not an error to hide. A reconciliation explains a difference; it never creates one.",
    ledgerLines: "Our ledger movements",
    statementLines: "Bank statement movements",
    ledgerLinesHint: "Read automatically from posted movements on the account. Never typed in.",
    matched: "Matched",
    unmatched: "Unmatched",
    outstandingDeposits: "Deposits in transit",
    outstandingPayments: "Payments not presented",
    unrecorded: "Bank movements not in our books",
    unrecordedHint: "Each of these is a missing document, entered as the real document — never fixed by ticking it.",
    addStatementLine: "Add a statement line",
    chequeNumber: "Cheque number",
    complete: "Complete",
    completeHint: "A completed reconciliation is read-only from then on.",
    completed: "Reconciliation completed.",
    nonAccounting: "A bank reconciliation posts no journal at all.",
    creditNumber: "Credit number",
    beneficiary: "Beneficiary",
    creditAmount: "Credit value",
    margin: "Cash margin",
    marginAccount: "Cash margin account",
    marginPaidFrom: "Margin paid from",
    requestDate: "Request date",
    openedDate: "Opened on",
    expiryDate: "Expiry date",
    latestShipmentDate: "Latest shipment date",
    goodsDescription: "Goods description",
    daysToExpiry: "Days to expiry",
    lcStatusRequested: "Requested",
    lcStatusOpened: "Opened",
    lcStatusSettled: "Settled",
    lcStatusCancelled: "Cancelled",
    openCredit: "Open the credit",
    settleCredit: "Settle the credit",
    cancelCredit: "Cancel the credit",
    lcAccountingHint: "Opening records the cash margin only. The goods and the payable belong to the purchase document, not to the credit.",
    loanNumber: "Loan number",
    principal: "Principal",
    interestRate: "Interest rate %",
    interestAccount: "Interest account",
    loanAccount: "Loan account",
    repaymentType: "Repayment type",
    numberOfPayments: "Number of instalments",
    yearDays: "Approved year days",
    drawdownDate: "Drawdown date",
    firstInstallmentDate: "First instalment",
    schedule: "Repayment schedule",
    installment: "Instalment",
    principalPart: "Principal",
    interestPart: "Interest",
    installmentTotal: "Instalment total",
    paid: "Paid",
    unpaid: "Unpaid",
    paidDate: "Paid on",
    paidFrom: "Paid from",
    outstandingPrincipal: "Outstanding principal",
    scheduledInterest: "Total scheduled interest",
    drawdown: "Draw down",
    drawdownHint: "The money arrives and the debt goes on the books. Interest is not booked here — it is charged with each instalment.",
    repay: "Repay",
    repayHint: "Principal and interest in one entry, because the bank takes one payment.",
    loanStatusDraft: "Draft",
    loanStatusActive: "Active",
    loanStatusSettled: "Settled",
    repayEqual: "Equal instalments",
    repayDecreasing: "Decreasing instalments",
    repayLumpSum: "Lump sum at maturity",
    repayInterestOnly: "Interest only, then principal",
    scheduleHint: "The whole schedule is built on save, and the principal column sums to exactly the loan amount.",
    screens: {
      bankTransactions: { title: "Bank Transactions", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Bank Transactions", description: "Charges, interest, and whatever appears on a statement that no other document explains." },
      interbankTransfer: { title: "Interbank Transfer", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Interbank Transfer", description: "Moving money between two of our own accounts. One document, one journal." },
      calculateBankInterest: { title: "Calculate Bank Interest", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Calculate Bank Interest", description: "Interest the bank credited or charged on the account." },
      bankReconciliation: { title: "Bank Reconciliation", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Bank Reconciliation", description: "Matching the bank statement against our books. It posts no journal." },
      lettersOfCredit: { title: "Letters of Credit", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Letters of Credit", description: "The register of credits: opening, settlement and cancellation." },
      letterOfCreditRequest: { title: "Letter of Credit Request", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Letter of Credit Request", description: "Credit requests not yet opened with the bank. They post nothing." },
      bankLoans: { title: "Bank Loans", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Bank Loans", description: "Loans drawn against loan facilities, and their repayment schedules." },
      repayLoanInstallments: { title: "Repay Loan Installments", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Repay Loan Installments", description: "Instalments falling due across every active loan." },
    },
  },
  inventoryLinks: {
    title: "Inventory financial links",
    breadcrumb: "Financial Management / Inventory & Warehouses / Settings",
    notice: "Four accounts: inventory, the two adjustment sides, and the cost of what is issued. Posting resolves item → group → warehouse → this link, and anything unmapped here refuses to post with account_not_configured.",
  },
  inventoryMaster: {
    printError: "Could not print or export.",
    inquiry: "Inquiry", inquiryTitle: "Existing records", newRecord: "New", save: "Save",
    saveAndNew: "Save + New", print: "Print", cancel: "Cancel", delete: "Delete", close: "Close",
    search: "Search",
    saved: "Saved.", deleted: "Deleted.",
    saveError: "Could not save", deleteError: "Could not delete", loadError: "Could not load",
    readOnly: "You have view-only access to this card.",
    editing: "You are editing an existing record. Press New to start a blank one.",
    noRecords: "No matching records.", none: "— none —",
    typeToSearch: "Type to search…", noMatch: "No matches",
    code: "Code", nameAr: "Arabic name", nameEn: "English name",
    active: "Active", notes: "Notes",
    cards: {
      items: "Item Card", itemGroups: "Item Groups", itemCategories: "Item Categories",
      units: "Units of Measure", conversions: "UOM Conversions", warehouses: "Warehouses",
      locations: "Storage Locations", reasons: "Inventory Movement Reasons",
    },
    units: {
      decimalAllowed: "Allows fractions",
      decimalAllowedHint: "A kilogram divides; a piece does not. Enforced on the server for every quantity.",
      itemCount: "Items",
    },
    groups: {
      parent: "Parent group",
      parentHint: "Changing the parent rebases the paths of every descendant automatically.",
      path: "Path", itemCount: "Items",
    },
    warehouses: {
      branch: "Branch (optional)",
      branchHint: "A warehouse can be defined now and placed in its branch later — leave it empty until then.",
      custodian: "Custodian", account: "Inventory account (optional)",
      accountHint: "Usually left empty: the company financial link answers for every warehouse.",
      hasMovements: "Has movements",
    },
    commercial: {
      prices: "Prices", pricesHint: "A SELLING price, never a cost. Inventory valuation comes from the stock ledger alone, and nothing here writes it.",
      priceList: "Price list", price: "Price", currency: "Currency",
      from: "Effective from", to: "Effective to", status: "Status", openEnded: "Open",
      active: "In force", ended: "Ended", noPrices: "No price on this item yet.", addPrice: "Add price",
      suppliers: "Suppliers", suppliersHint: "From the one business partner register. «Last purchase price» is a negotiating note, never inventory valuation.",
      supplier: "Supplier", supplierItemCode: "Supplier's item code",
      preferred: "Preferred", leadTime: "Lead time (days)", moq: "Min. order qty",
      lastPurchasePrice: "Last purchase price",
      noSuppliers: "No supplier linked to this item.", addSupplier: "Add supplier",
      choose: "Choose…", remove: "Remove",
      saveFirst: "Save the item card first — prices and suppliers appear then.",
      saveFailed: "Could not save.",
    },
    categories: {
      level: "Level",
      levelHint: "1, 2 or 3 — which source classification column it belongs in. Not a tree, and not the item group.",
    },
    locations: {
      warehouse: "Warehouse",
      warehouseHint: "A location belongs to one warehouse, and its parent must be in the same one.",
      parent: "Parent location", path: "Path",
    },
    reasons: {
      direction: "Direction", scope: "Scope", requiresNotes: "Requires a note",
      requiresNotesHint: "Like «damaged»: the code says what happened, the note says what actually broke.",
      requiresApproval: "Requires approval", account: "Account (optional)",
      directions: { "0": "Increase and decrease", "1": "Increase only", "2": "Decrease only" },
      scopes: { "1": "Stock adjustment", "2": "Stock issue", "3": "Stock receipt", "9": "Any document" },
    },
    item: {
      barcode: "Barcode", barcodeHint: "Optional, and never shared by two active items.",
      category1: "Classification 1", category2: "Classification 2", category3: "Classification 3",
      previous: "Previous", next: "Next", backToRegister: "Back to register",
      categoryHint: "Three independent source classifications, not a tree — and separate from the item group.",
      group: "Group", category: "Category", type: "Item type",
      baseUnit: "Base unit",
      baseUnitHint: "The unit stock is held and valued in. Every historical quantity is expressed in it.",
      baseUnitLocked: "This item has stock movements — the base unit can no longer change.",
      purchaseUnit: "Purchase unit", salesUnit: "Sales unit",
      purchasable: "Purchasable", sellable: "Sellable",
      minimumStock: "Minimum stock", reorderLevel: "Reorder level",
      defaultWarehouse: "Default warehouse",
      countryOfOrigin: "Country of origin (optional)",
      countryOfOriginHint: "From the company country register — never free text.",
      purchaseTax: "Default purchase tax (optional)",
      purchaseTaxHint: "Offered on a purchase invoice line; the line still decides.",
      inventoryAccount: "Inventory account", cogsAccount: "Cost of issue account",
      adjustmentAccount: "Adjustment account",
      accountsHint: "Usually left empty: resolution is item → group → warehouse → company financial link.",
      conversions: "Unit conversions", conversionsHint: "For example: 1 carton = 12 pieces.",
      addConversion: "Add a conversion", unit: "Unit", factor: "Factor to the base unit",
      onHand: "On hand", onHandValue: "Stock value", averageCost: "Average cost",
      derivedHint: "Derived from the stock ledger — not editable here, and changed only by movements.",
      types: { "1": "Stock item", "2": "Non-stock item", "3": "Service" },
    },
  },
  inventorySettings: {
    title: "Inventory settings",
    breadcrumb: "Financial Management / Inventory & Warehouses / Settings",
    save: "Save",
    saved: "Settings saved. They apply to future documents and never restate what is already posted.",
    saveError: "Could not save the settings",
    loadError: "Could not load the settings",
    readOnly: "You have view-only access. Changing settings is an administrator right.",
    neverSaved: "These settings have never been saved for this company — the safe defaults shown below are in force.",
    notSet: "Not set",
    readiness: "Configuration readiness",
    canPost: "Ready to post",
    cannotPost: "Not ready to post",
    blocksPosting: "blocks posting",
    groups: { general: "General", costing: "Costing & valuation", count: "Stock count",
      purchase: "Purchasing & receipt", issue: "Issues", warehouse: "Warehouses & movement",
      transfer: "Transfers", numbering: "Numbering" },
    allowNegativeStock: "Allow negative stock",
    allowNegativeStockHint: "When off, the server refuses any issue that would take an item below zero in a warehouse.",
    allowFractionalQuantity: "Allow fractional quantities",
    allowFractionalQuantityHint: "The unit still decides: a fraction is refused if the unit itself does not divide.",
    quantityPrecision: "Quantity precision",
    quantityPrecisionHint: "Accounting rounding applied before storing and costing — not a display format.",
    costPrecision: "Cost precision",
    costPrecisionHint: "Deliberately finer than the currency: rounding a unit cost early drifts the valuation off the control account.",
    blockInactiveItems: "Block movements on inactive items",
    blockInactiveWarehouses: "Block movements on inactive warehouses",
    defaultWarehouse: "Default warehouse",
    costingMethod: "Costing method",
    movingAverage: "Moving weighted average",
    costingHint: "Per item and per warehouse. One method is genuinely implemented; unimplemented ones are not offered.",
    allowBackdated: "Allow back-dated documents",
    allowBackdatedHint: "Always bounded by the fiscal period being open.",
    allowBackdatedAfter: "Allow inserting behind existing movements",
    allowBackdatedAfterHint: "Best left off: a moving average is order-dependent, so inserting behind existing movements invalidates every later cost and journal.",
    freezeDuringCount: "Freeze movements during a count",
    freezeDuringCountHint: "Blocks movements in a warehouse with an open count until it closes.",
    showCostDuringCount: "Show cost during a count",
    showCostDuringCountHint: "Presentation only — it changes no calculation.",
    createAdjustmentFromCount: "Create the difference adjustment on approval",
    createAdjustmentFromCountHint: "Creates a DRAFT adjustment; the count itself never changes a balance.",
    requireSupplierInvoiceNumber: "Require the supplier's invoice number",
    requireSupplierInvoiceDate: "Require the supplier's invoice date",
    preventDuplicateSupplierInvoice: "Prevent duplicate supplier invoice numbers",
    preventDuplicateHint: "Same supplier and same number is refused on the server — the commonest way a payable gets paid twice.",
    requirePurchaseOrder: "Require a purchase order reference",
    allowPurchaseWithoutOrder: "Allow a direct invoice with no purchase order",
    exclusiveHint: "These two are mutually exclusive; turning one on clears the other, and the server refuses to store both.",
    allowQuantityAboveOrder: "Allow a quantity above the purchase order",
    quantityTolerance: "Quantity tolerance %",
    quantityToleranceHint: "Read only when exceeding the ordered quantity is allowed.",
    defaultPurchaseWarehouse: "Default purchase warehouse",
    requireStorageLocation: "Require a storage location",
    requireStorageLocationHint: "When on, every movement must name a location inside the warehouse. Left off unless locations are actually defined.",
    warehouseRules: "Always enforced and not configurable: every stock-item movement needs a warehouse · a transfer needs a different source and destination · the item and the warehouse must both be active.",
    requireCostCenterOnIssue: "Require a cost centre on issues",
    requireCostCenterHint: "When on, a stock issue with no cost centre is refused.",
    requireIssueReason: "Require an issue reason",
    allowDirectIssue: "Allow direct issues",
    requireTransferApproval: "Require transfer approval",
    requireTransferApprovalHint: "Uses the same Draft → Audited → Posted lifecycle; no new approval states.",
    requireTransferReceipt: "Require receiving confirmation (two-step transfer)",
    transferReceiptUnsupported: "Unavailable: it needs a transit location that does not exist yet — switching it on would make stock vanish from the company total while in motion.",
    transferNoJournal: "A warehouse transfer posts no journal: the company total, in quantity and in value, does not change. The exception is two warehouses whose inventory accounts differ.",
    numberingHint: "Numbering comes from the existing sequence engine: assigned once at first save and never changed by an edit, an audit, a posting or a cancellation. Yearly reset, six digits.",
    documentType: "Document type",
    prefix: "Number shape",
    sequences: [
      { code: "MPI", name: "Material Purchase Invoice" },
      { code: "MPR", name: "Material Purchase Return" },
      { code: "SRC", name: "Stock Receipt" },
      { code: "SIS", name: "Stock Issue" },
      { code: "STR", name: "Warehouse Transfer" },
      { code: "SAD", name: "Stock Adjustment" },
      { code: "SCT", name: "Stock Count" },
      { code: "SCA", name: "Count Difference Adjustment" },
      { code: "MPQ", name: "Material Purchase Request" },
      { code: "MPO", name: "Material Purchase Order" },
    ],
  },
  bankStatement: {
    section: "Bank statement",
    attach: "Attach file",
    reread: "Re-read with this mapping",
    noFile: "No statement attached yet. Excel (.xlsx), CSV and PDF are accepted.",
    fileName: "File",
    uploadedAt: "Uploaded",
    uploadedBy: "By",
    rows: "Rows",
    parseStatus: "Read status",
    parseStatuses: { "1": "Awaiting read", "2": "Read", "3": "No transactions", "4": "Could not read" },
    mapping: "Column mapping",
    mappingHint: "This is how the bank's captions were understood. Correct them if needed, then Re-read.",
    columns: {
      "0": "— ignore —", "1": "Date", "2": "Value date", "3": "Reference", "4": "Cheque number",
      "5": "Description", "6": "Debit", "7": "Credit", "8": "Amount", "9": "Balance",
    },
    comparison: "Comparison",
    runMatch: "Match automatically",
    amountTolerance: "Amount tolerance",
    dateTolerance: "Date tolerance (days)",
    toleranceWarning: "Warning: tolerance is not zero — differences within it will read as matched.",
    imported: "File read. Transactions:",
    manuallyMatched: "Matched by hand.",
    unmatched: "Match removed. Neither side was deleted.",
    ledgerRows: "ERP rows",
    statementRows: "Statement rows",
    ledgerTotal: "ERP movements total",
    statementTotal: "Statement movements total",
    ledgerBalance: "ERP balance",
    statementBalance: "Bank statement balance",
    difference: "Difference",
    counts: "Classification",
    status: "Status",
    bankDate: "Bank date",
    erpDate: "ERP date",
    reference: "Reference",
    cheque: "Cheque",
    statementAmount: "Statement amount",
    erpAmount: "ERP amount",
    gap: "Difference",
    description: "Description",
    action: "Action",
    matchManually: "Match by hand",
    choosing: "Choose the ERP row…",
    pairWith: "Match with this",
    unmatch: "Unmatch",
    pairingHint: "Now pick the matching ERP row from the table below.",
    manualTag: "(manual)",
    noAutoFix: "The system reports differences and does not correct them: no entry is created and no transaction is changed because a file was uploaded. The correcting document is the accountant's decision, on its own screen.",
    matchTypes: {
      "0": "Unmatched", "1": "Matched", "2": "Amount difference", "3": "Date difference",
      "4": "Reference difference", "5": "ERP only", "6": "Statement only",
      "7": "Possible duplicate", "8": "Ambiguous",
    },
  },
  chequeBatch: {
    documentSection: "Document",
    searchSection: "Find cheques",
    eligibleSection: "Eligible cheques",
    resultSection: "Saved result",
    date: "Date",
    reference: "Advice number",
    referenceHint: "The bank's deposit slip or advice number — it is this document's identity.",
    notes: "Description",
    choose: "Choose…",
    chequeNumberFrom: "Cheque number from",
    chequeNumberTo: "Cheque number to",
    chequeDateFrom: "Cheque date from",
    chequeDateTo: "Cheque date to",
    dueFrom: "Due date from",
    dueTo: "Due date to",
    amountFrom: "Amount from",
    amountTo: "Amount to",
    party: "Customer",
    partyHint: "Part of the customer's name, or a cheque number.",
    chequeDate: "Cheque date",
    drawerBank: "Drawn on",
    searchCheques: "Find cheques",
    clearFilters: "Clear filters",
    documentInquiry: "Find documents",
    pressSearch: "Press Find cheques to list what is eligible. Nothing loads on its own — you choose what goes on the slip. The toolbar's Inquiry opens previously saved documents instead.",
    noEligible: "No cheques match those conditions.",
    noDocuments: "No documents saved yet.",
    select: "Select",
    selectAll: "Select all",
    selectedCount: "Cheques selected",
    selectedTotal: "Selected total",
    selectAtLeastOne: "Select at least one cheque.",
    referenceRequired: "Enter the bank's advice number.",
    saved: "Document saved",
    chequeCount: "Cheques",
    journalVoucher: "Journal voucher",
    savedSlip: "Saved document",
    statusThen: "Status then",
    statusNow: "Status now",
    total: "Total",
    tools: "Tools",
    readOnly: "You have view-only access to this screen.",
    reason: "Reason",
    reasonRequired: "Enter the reason.",
    deliveredTo: "Delivered to",
    deliveredToHint: "Who physically took the paper, when that is not the beneficiary.",
    newDueDate: "New due date",
    newDueDateHint: "The old date is kept in the cheque's movement history, never overwritten.",
    newDueDateRequired: "Enter the new due date.",
    processing: "Processing",
    represent: "Re-present",
    handBack: "Hand back to customer",
    legal: "Transfer to legal",
    redeliver: "Re-deliver",
    voidCheque: "Void the cheque",
    operationalNote: "This is an operational movement and posts no journal entry.",
    postedNothing: "Done, and it posted nothing: the entry was already made earlier in this cheque's life. An immediate outgoing cheque credits the bank when it is issued, so cashing it moves no balance.",
    operation: "Operation",
    operations: {
      depositForCollection: "Deposit for collection",
      depositImmediately: "Immediate deposit and collect",
      collect: "Collect",
      withdraw: "Withdraw from collection",
      returnUnpaid: "Returned by the bank",
      handBack: "Hand back to the customer",
      processReturn: "Process a returned cheque",
      deliver: "Deliver the cheque",
      settle: "Cash the cheque",
      returnIssued: "Return a paid cheque",
      processIssuedReturn: "Process a returned paid cheque",
      cancel: "Cancel the cheque",
      changeDueDate: "Change the due date",
    },
  },
  ownerNote: {
    label: "About this screen",
    dismiss: "Hide",
    dismissHint: "Hide this note on this screen for good",
  },
  journalEntryDialog: {
    title: "Journal entry",
    close: "Close",
    noVoucher: "This transaction has no entry yet. Save it first.",
    voucherNumber: "Voucher number",
    date: "Date",
    account: "Account",
    party: "Party",
    costCenter: "Cost center",
    debit: "Debit",
    credit: "Credit",
    total: "Total",
    balanced: "Balanced: total debit equals total credit.",
    notBalanced: "Not balanced. Difference:",
    operationalOnly: "This is an operational movement and posts no journal entry: the cheque's value is still in the cashbox's cheques-in-collection account, and only reaches the bank when the money actually arrives.",
    viewJournal: "View journal entry",
  },
  chequeDesk: {
    chequeNumber: "Cheque number",
    party: "Party",
    drawer: "Drawer",
    beneficiary: "Beneficiary",
    bank: "Bank",
    bankAccount: "Bank account",
    drawnOnAccount: "Drawn on account",
    cashbox: "Cashbox",
    chequeDate: "Cheque date",
    dueDate: "Due date",
    amount: "Amount",
    currency: "Currency",
    status: "Status",
    term: "Term",
    direction: "Direction",
    daysOverdue: "Days overdue",
    notDueYet: "Not due yet",
    notes: "Notes",
    reference: "Reference",
    deliveredTo: "Delivered to",
    deliveredDate: "Delivered on",
    returnReason: "Return reason",
    cancellationReason: "Cancellation reason",
    settlementVoucher: "Settlement voucher",
    search: "Search by cheque number or party",
    empty: "No cheques match.",
    loading: "Loading…",
    totals: "Totals",
    totalsHint: "One total per currency. Cheques in different currencies do not add up.",
    count: "Count",
    actions: "Actions",
    history: "History",
    historyTitle: "Cheque movements",
    noHistory: "No movements recorded for this cheque.",
    movementDate: "Date",
    movement: "Movement",
    journal: "Journal",
    noJournal: "No journal",
    operationalOnly: "Operational only — this posts no journal.",
    date: "Date",
    reason: "Reason",
    newDueDate: "New due date",
    confirm: "Confirm",
    cancelButton: "Cancel",
    submitting: "Working…",
    done: "Done.",
    required: "This field is required.",
    chooseBankAccount: "Choose the bank account",
    readOnlyNotice: "You have view access only. You cannot act on cheques.",
    noAction: "No action is available for this cheque in its current state.",
    refresh: "Refresh",
    filters: "Filters",
    allCurrencies: "All currencies",
    dueFrom: "Due from",
    dueTo: "Due to",
    statusUnderCollection: "Under collection",
    statusDeposited: "Deposited",
    statusCleared: "Collected",
    statusBounced: "Returned unpaid",
    statusReturned: "Handed back",
    statusIssued: "Issued",
    statusDelivered: "Delivered",
    statusPaid: "Paid",
    statusReturnedUnpaid: "Returned by bank",
    statusCancelled: "Cancelled",
    termImmediate: "Immediate",
    termPostDated: "Post-dated",
    directionIncoming: "Received",
    directionOutgoing: "Issued by us",
    mvReceived: "Received",
    mvDepositedForCollection: "Deposited for collection",
    mvCollected: "Collected",
    mvWithdrawnFromCollection: "Withdrawn from collection",
    mvReturnedByBank: "Returned by bank",
    mvReturnedChequeProcessed: "Returned cheque processed",
    mvDepositedImmediately: "Deposited immediately",
    mvIssued: "Issued",
    mvDelivered: "Delivered",
    mvPaid: "Paid",
    mvReturnedToUs: "Issued cheque returned",
    mvReturnedPaidChequeProcessed: "Returned issued cheque processed",
    mvCancelled: "Cancelled",
    mvDueDateChanged: "Due date changed",
    mvWithdrawnFromChequeBox: "Withdrawn from cheque cashbox",
    mvHandedBackToDrawer: "Handed back to drawer",
    actDepositForCollection: "Deposit for collection",
    actDepositImmediately: "Deposit and collect",
    actCollect: "Collect",
    actWithdraw: "Withdraw from collection",
    actReturnUnpaid: "Record return",
    actHandBack: "Hand back",
    actProcessReturn: "Process",
    actIssue: "Issue cheque",
    actDeliver: "Deliver",
    actSettle: "Record payment",
    actReturnIssued: "Record return",
    actProcessIssuedReturn: "Process",
    actCancel: "Cancel cheque",
    actChangeDueDate: "Change due date",
    procRepresent: "Re-present",
    procRepresentHint: "The cheque goes back into collection and the return entry is reversed.",
    procLegal: "Transfer to legal",
    procLegalHint: "The amount moves to the returned-cheques legal account configured on the cashbox.",
    procHandBack: "Hand back to the customer",
    procHandBackHint: "No journal — the customer has owed it since the cheque came back.",
    procRedeliver: "Re-deliver",
    procRedeliverHint: "The issue entry is posted again and the cheque is delivered once more.",
    procVoid: "Void the cheque",
    procVoidHint: "No journal — the return already reversed the issue.",
    screens: {
      immediateChequeDeposit: { title: "Immediate Cheque Deposit", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Immediate Cheque Deposit", description: "Received immediate cheques paid straight into a bank account and treated as collected." },
      chequeDepositCollection: { title: "Cheque Deposit for Collection", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Cheque Deposit for Collection", description: "Lodging a received cheque with a bank. Operational — it posts no journal." },
      collectChequeCollection: { title: "Collect Cheque for Collection", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Collect Cheque for Collection", description: "Cheques lodged with a bank whose money has now arrived." },
      withdrawChequesCollection: { title: "Withdraw Cheques for Collection", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Withdraw Cheques for Collection", description: "Taking a cheque back off a bank before it cleared. Not a return — the bank did not refuse it." },
      returnedCheques: { title: "Returned Cheques", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Returned Cheques", description: "Recording that the bank sent a received cheque back unpaid." },
      processReturnedCheques: { title: "Process Returned Cheques", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Process Returned Cheques", description: "What happens to a returned cheque: re-present it, send it to legal, or give it back." },
      immediateChequePayment: { title: "Immediate Cheque Payment", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Immediate Cheque Payment", description: "Writing an immediate cheque on our account. The bank is credited at once." },
      collectPostDatedCheques: { title: "Collect Post-Dated Cheques", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Collect Post-Dated Cheques", description: "Received post-dated cheques that have now fallen due." },
      deliverPostDatedCheque: { title: "Deliver Post-Dated Cheque", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Deliver Post-Dated Cheque", description: "Writing a post-dated cheque. It credits deferred cheques payable, not the bank." },
      deliverImmediatePostDated: { title: "Deliver Immediate / Post-Dated Cheques", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Deliver Immediate / Post-Dated Cheques", description: "Cheques we have written that are still in the drawer, undelivered." },
      returnImmediatePostDated: { title: "Return Immediate / Post-Dated Cheque", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Return Immediate / Post-Dated Cheque", description: "Recording that the bank sent one of our own cheques back unpaid." },
      processReturnedPaidCheques: { title: "Process Returned Paid Cheques", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Process Returned Paid Cheques", description: "What happens to one of our returned cheques: re-deliver it or void it." },
      changeChequeDueDate: { title: "Change Cheque Due Date", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Change Cheque Due Date", description: "Moving a due date. It posts nothing — a due date is not an amount." },
      cancelCheque: { title: "Cancel Cheque", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Cancel Cheque", description: "Voiding one of our own cheques before the bank has paid it." },
      chequeBooks: { title: "Withdraw / Deliver from Cheque Cashbox", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Withdraw / Deliver from Cheque Cashbox", description: "Chequebooks and their leaves. Stationery control — it posts nothing." },
      dueChequesReminder: { title: "Due Cheques Reminder", breadcrumb: "Financial Management › Cash & Banks › Daily Transactions › Due Cheques Reminder", description: "Cheques that have fallen due or are about to, in both directions." },
    },
  },
  app: {
    name: "Business OS",
    byline: "by Blink Connected",
    description: "The integrated platform for running your business",
  },
  auth: {
    signInTitle: "Sign in",
    signInSubtitle: "Enter your credentials to continue",
    userLabel: "Username or email",
    passwordLabel: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    errors: {
      userRequired: "Enter your username or email",
      passwordRequired: "Enter your password",
      invalidCredentials: "Incorrect username or password",
      tenantNotFound: "Unknown tenant code",
      accountLocked: "This account is locked. Contact your administrator",
      mustChangePassword: "You must change your password before continuing",
      sessionExpired: "Your session has expired. Please sign in again",
      forbidden: "You do not have access to this screen",
      forbiddenHint: "Contact your administrator to request the required permission",
      network: "Can't reach the server. Check your connection and try again",
      unknown: "Something went wrong. Please try again",
    },
  },
  dataTable: {
    search: "Search", columns: "Columns", print: "Print", pdf: "PDF", excel: "Excel",
    previous: "Previous", next: "Next", rowsPerPage: "Per page",
    showing: "Showing {from}\u2013{to} of {total}",
    selected: "{count} selected", selectAll: "Select the page", selectRow: "Select row",
    emptyTitle: "Nothing matches these filters.",
    view: "View", edit: "Edit", review: "Review", post: "Post", cancel: "Cancel",
    actions: "Actions",
  },
  shell: {
    navigation: "Main navigation",
    breadcrumb: "Breadcrumb",
    skipToContent: "Skip to main content",
    openScreens: "Open screens",
    closeScreen: "Close screen",
    closeOtherScreens: "Close other screens",
    toggleNavigation: "Show or hide navigation",
    closeNavigation: "Close navigation",
    primaryNavigation: "Primary navigation",
    module: "Module",
    searchTheSystem: "Search the system",
    collapseSidebar: "Collapse navigation",
    expandSidebar: "Expand navigation",
    expandSection: "Expand section",
    collapseSection: "Collapse section",
    search: "Search",
    searchPlaceholder: "Search accounts, cost centers, currencies…",
    searchHint: "Type at least two characters",
    searchNoResults: "No results",
    searchSearching: "Searching…",
    notifications: "Notifications",
    notificationsEmpty: "No notifications",
    notificationsEmptyHint: "System alerts will appear here once enabled",
    fiscalYear: "Fiscal year",
    fiscalYearUnavailable: "Not set",
    switchCompany: "Switch company",
    openMenu: "Open menu",
    account: "Account",
    company: "Company",
    branch: "Branch",
    signOut: "Sign out",
    languageToggle: "Change language",
    themeToggle: "Toggle theme",
    themeLight: "Light",
    themeDark: "Dark",
  },
  nav: {
    home: "Home",
    customerTransactionSettlement: "Customer Transaction Settlement",
    dashboard: "Dashboard",
    comingSoon: "Coming Soon",

    financialManagement: "General Accounting",
    humanResources: "Human Resources Management",
    hotelFrontDesk: "Front Desk",
    systemSettings: "System Settings",

    generalLedger: "General Ledger",
    cashAndBanks: "Cash and Banks",
    payments: "Payments",
    receipts: "Receipts",
    fixedAssets: "Fixed Assets",
    inventory: "Inventory & Warehouses",

    planningAndRecruitment: "Planning & Recruitment",
    employeeAffairs: "Employee Affairs",
    payrollAndWages: "Payroll & Wages",
    trainingAndDevelopment: "Training & Development",
    attendanceMonitoring: "Attendance Monitoring",

    employees: "Employees",

    usersAndPermissions: "Users & Permissions",
    controlPanel: "Control Panel",

    users: "Users",
    roles: "Roles",
    permissions: "Permissions",
    auditTrail: "System Audit Trail",
    accessMatrix: "User Access Matrix",
    companies: "Companies",
    branches: "Branches",

    masterData: "Master Data",
    identityCards: "Master Data",
    dailyTransactions: "Daily Transactions",
    inquiries: "Inquiries",
    reports: "Reports",
    settings: "Settings",

    journalEntries: "Journal Entries",
    journalAuditPosting: "Journal Audit & Posting",

    dailyTransactionsInquiry: "Daily Transactions Inquiry",
    accountMovements: "Account Movements",
    costCenterAccount: "Cost Center Account",
    cashSubledgerMovements: "Cash Subledger Movements",
    unauditedJournalNotes: "Unaudited Journal Notes Inquiry",
    taxSettings: "Tax Settings",
    detailedVoucherTax: "Detailed Voucher Tax",
    financialPosition: "Financial Position",
    movementsWithoutCostCenters: "Movements Without Cost Centers",

    reportsAccounting: "Accounting Reports",
    reportsVouchers: "Voucher Reports",
    reportsBatchVoucherPrinting: "Batch Voucher Printing",
    reportsSalesperson: "Salesperson Reports",
    reportsCostCenters: "Cost Center Reports",
    reportsTrialBalance: "Trial Balance Reports",
    reportsClosing: "Closing Reports",
    reportsCheques: "Cheque Reports",
    reportsCashBoxes: "Cashbox Reports",
    reportsGeneral: "General Reports",

    financialStatementsSetup: "Financial Statements Setup",
    freezeTransactions: "Freeze Transactions",
    accountCategories: "Account Categories",
    accountTypes: "Account Types",
    accountOpeningBalances: "Account Opening Balances",
    voucherTypes: "Voucher Types",
    financialIntegrationSettings: "Financial Integration Settings",
    generalLedgerSettings: "General Ledger System Settings",
    journalEntryAuditNotes: "Journal Entry Audit Notes",
    costCenterGroups: "Cost Center Groups",

    cashboxDefinition: "Cashbox Definition",
    bankDefinition: "Bank Definition",
    bankingFacilities: "Banking Facilities",

    cashDeposit: "Cash Deposit",
    cashWithdrawal: "Cash Withdrawal",
    cashboxTransactions: "Cashbox Transactions",
    immediateChequeDeposit: "Immediate Cheque Deposit",
    chequeDepositForCollection: "Cheque Deposit for Collection",
    collectChequeForCollection: "Collect Cheque for Collection",
    withdrawChequesForCollection: "Withdraw Cheques for Collection",
    returnedCheques: "Returned Cheques",
    processReturnedCheques: "Process Returned Cheques",
    immediateChequePayment: "Immediate Cheque Payment",
    collectPostDatedCheques: "Collect Post-Dated Cheques",
    deliverPostDatedCheque: "Deliver Post-Dated Cheque",
    deliverImmediateOrPostDatedCheques: "Deliver Immediate / Post-Dated Cheques",
    returnImmediateOrPostDatedCheque: "Return Immediate / Post-Dated Cheque",
    processReturnedPaidCheques: "Process Returned Paid Cheques",
    lettersOfCredit: "Letters of Credit",
    letterOfCreditPreparationRequest: "Letter of Credit Preparation Request",
    bankReconciliation: "Bank Reconciliation",
    interbankTransfer: "Interbank Transfer",
    bankTransactions: "Bank Transactions",
    changeChequeDueDate: "Change Cheque Due Date",
    cancelCheque: "Cancel Cheque",
    withdrawDeliverFromChequeCashbox: "Withdraw / Deliver from Cheque Cashbox",
    calculateBankInterest: "Calculate Bank Interest",
    bankLoans: "Bank Loans",
    repayLoanInstallments: "Repay Loan Installments",
    dueChequesReminder: "Due Cheques Reminder",

    cashboxClosingTransactions: "Cashbox Closing Transactions",
    bankAccountCurrencyLocal: "Bank Account - Currency - Local",
    bankAccount: "Bank Account",
    chequeMovements: "Cheque Movements",
    paidCheques: "Paid Cheques",
    receivedCheques: "Received Cheques",
    immediatePostDatedChequeDelivery: "Immediate / Post-Dated Cheque Delivery",
    undeliveredCheques: "Undelivered Cheques",
    cashboxMovements: "Cashbox Movements",
    cashboxAccount: "Cashbox Account",
    cashboxCount: "Cashbox Count",
    cashboxClosingFollowUp: "Cashbox Closing Follow-up",
    missingChequeSequences: "Missing Cheque Sequences",

    editCashboxData: "Edit Cashbox Data",
    cashboxGroups: "Cashbox Groups",
    cashboxCashFloat: "Cashbox Cash Float",
    bankOpeningBalances: "Bank Opening Balances",
    cashboxOpeningBalances: "Cashbox Opening Balances",
    cashAndBanksSystemSettings: "Cash and Banks System Settings",
    letterOfCreditTypes: "Letter of Credit Types",
    linkUsersToCashboxes: "Link Users to Cashboxes",
    reportSignatures: "Report Signatures",
    chequeReturnReasons: "Cheque Return Reasons",
    paymentMethods: "Payment Methods",
    chequeBooks: "Cheque Books",
    expenseTypes: "Expense Types",

    suppliers: "Suppliers",
    userSupplierAccess: "User-Supplier Access",
    coordinators: "Coordinators",
    salespersons: "Sales Representatives",
    collectors: "Collectors",
    drivers: "Drivers",

    creditNote: "Credit Note",
    debitNote: "Debit Note",
    paymentVoucher: "Payment Voucher",
    paymentOrder: "Payment Order",
    paymentBooks: "Payment Books",
    servicePurchaseInvoice: "Service Purchase Invoice",
    servicePurchaseReturn: "Service Purchase Return",
    servicePurchaseRequest: "Service Purchase Request",
    supplierAccountSettlement: "Supplier Account Settlement",
    supplierCreditLimitAdjustment: "Supplier Credit Limit Adjustment",
    cancelInvoicePayment: "Cancel Invoice Payment",
    editSupplierInformation: "Edit Supplier Information",
    prepaidExpenses: "Prepaid Expenses",
    supplierBalanceReminder: "Supplier Balance Reminder",
    transferSupplierTransactions: "Transfer Supplier Transactions",
    accountReconciliation: "Account Reconciliation",

    paymentOrdersNotInVouchers: "Payment Orders Not Included in Payment Vouchers",
    supplierInformation: "Supplier Information",
    supplierMovements: "Supplier Movements",
    supplierAccountMovements: "Supplier Account Movements",
    supplierAccountCurrencyLocal: "Supplier Account - Currency / Local",
    totalSupplierPayables: "Total Supplier Payables",
    totalSupplierBalancesCostCenter: "Total Supplier Balances - Cost Center",
    expenseTransactions: "Expense Transactions",
    expenseAccount: "Expense Account",
    purchaseOrdersWaitingPayment: "Purchase Orders Waiting for Payment",
    deliverySchedule: "Delivery Schedule",
    receiptAndPaymentBooks: "Receipt and Payment Books",
    paymentsDailyTransactionsInquiry: "Payments Daily Transactions",
    paymentVoucherMovements: "Payment Voucher Movements",
    servicePurchaseDocumentMovements: "Service Purchase Document Movements",
    unsettledPaymentOrders: "Payment Orders Not Settled",
    supplierInformationInquiry: "Supplier Information",
    supplierAccountCurrency: "Supplier Account - Currency / Local",
    supplierBalancesCostCenter: "Total Supplier Balances by Cost Center",
    purchaseOrdersAwaitingPayment: "Purchase Orders Awaiting Payment",
    receiptAndPaymentBooksInquiry: "Receipt and Payment Books",
    supplierItemTransactions: "Supplier Item Transactions",
    outstandingSupplierInvoicesInquiry: "Outstanding Supplier Invoices",
    paidUnpaidSupplierInvoices: "Paid & Unpaid Supplier Invoices",
    unappliedPaymentVouchers: "Payment Vouchers Not Applied",
    expenseMovementsInquiry: "Expense Movements",
    expenseAccountInquiry: "Expense Account Statement",
    expenseMovementsCostCenter: "Expense Movements by Cost Center",
    expenseAccountCostCenterInquiry: "Expense Account by Cost Center",
    customerAgingInquiry: "Customer Receivables Aging",
    purchaseItems: "Purchase Items",
    purchaseOrders: "Purchase Orders",
    paymentBooksInquiry: "Payment Books Inquiry",
    missingPaymentBookSequences: "Missing Payment Book Sequences",
    itemTransactionsSupplier: "Item Transactions - Supplier",
    voucherTransactions: "Voucher Transactions",
    journalVouchersInquiry: "Journal Vouchers",
    serviceVoucherTransactions: "Service Voucher Transactions",
    outstandingSupplierInvoices: "Outstanding Supplier Invoices",
    paidAndUnpaidInvoices: "Paid & Unpaid Invoices",
    expenseAccountCostCenter: "Expense Account - Cost Center",
    supplierMovementsByCostCenter: "Supplier Movements by Cost Center",
    agingReport: "Customer Receivables Aging",
    paymentVouchersWithoutSettledInvoices: "Payment Vouchers Without Settled Invoices",
    expenseTransactionsCostCenter: "Expense Transactions - Cost Center",

    reportsPayments: "Payments Reports",

    businessActivities: "Business Activities",
    businessNature: "Business Nature",
    supplierCategories: "Supplier Categories",
    supplierOpeningBalances: "Supplier Opening Balances",
    workflowProcedures: "Workflow Procedures",
    workflowActions: "Workflow Actions",
    paymentsSystemSettings: "Payments System Settings",
    linkUsersToSuppliers: "Link Users to Suppliers",
    discountTypes: "Discount Types",
    servicePurchaseTypes: "Service Purchase Types",
    countriesAndRegions: "Countries & Regions",
    documentTypes: "Document Types",
    supplierEvaluationCriteria: "Supplier Evaluation Criteria",
    agingPeriodDefinitions: "Receivables Aging Periods",
    expenseOpeningBalances: "Expense Opening Balances",

    customers: "Customers",

    receiptVoucher: "Receipt Voucher",
    receiptOrder: "Receipt Order",
    receiptBooks: "Receipt Book",
    serviceSalesInvoice: "Sales Invoice",
    serviceSalesReturn: "Sales Return",
    serviceQuotation: "Quotation",
    deferredRevenue: "Deferred Revenue",
    advanceReceivedRevenue: "Advance Received Revenue",
    customerBalanceReminder: "Customer Balance Reminder",
    transferCustomerTransactions: "Customer Transaction Transfer",
    customerSettlement: "Customer Settlement",
    cancelInvoiceSettlement: "Cancel Invoice Settlement",

    customerTransactions: "Daily Transactions",
    customerAccountCurrencyLocal: "Customer Statement by Currency",
    customerAccount: "Customer Statement",
    totalCustomerBalances: "Customer Receivables Summary",
    totalCustomerBalancesCostCenter: "Customer Receivables by Cost Center",
    receiptVouchersWithoutInvoices: "Receipt Vouchers Without Invoices",
    missingReceiptBookSequences: "Missing Receipt Book Sequences",
    missingReceiptVoucherSequences: "Missing Receipt Voucher Sequences",
    itemTransactionsCustomer: "Customer Item Transactions",
    outstandingCustomerInvoices: "Outstanding Customer Invoices",
    shortAgingReport: "Short Aging Report",
    customerAccountCostCenter: "Customer Account - Cost Center",
    detailedAgingReport: "Detailed Receivables Aging Report",

    reportsReceipts: "Receipts Reports",

    customerCategories: "Customer Categories",
    customerClasses: "Customer Classes",
    customerTypes: "Customer Types",
    customerOpeningBalances: "Customer Opening Balances",
    smsSettings: "SMS Settings",
    ePaymentPolicy: "E-Payment Policy",
    serviceSalesTypes: "Service Sales Types",
    custodyCategories: "Custody Categories",
    linkUsersToCustomers: "Link Users to Customers",

    assetGroups: "Asset Groups",
    assetLocations: "Asset Locations",
    assetCard: "Asset Card",
    insuranceCompanies: "Insurance Companies",

    custodyDeposit: "Custody Deposit",
    custodyIssue: "Custody Issue",
    assetPurchaseReturns: "Asset Purchase Returns",
    assetPurchaseInvoice: "Asset Purchase Invoice",
    purchaseOrder: "Purchase Order",
    quotationComparison: "Quotation Comparison",
    quotationAward: "Quotation Award",
    requestForQuotation: "Request for Quotation",
    assetSale: "Asset Sale",
    assetDepreciation: "Asset Depreciation",
    assetRevenue: "Asset Revenue",
    assetExpenseReturns: "Asset Expense Returns",
    assetExpenses: "Asset Expenses",
    changeAssetLocation: "Change Asset Location",
    assetInsurance: "Asset Insurance",
    changeAssetCustody: "Change Asset Custody",
    releaseReturnCustody: "Release from Custody / Return to Custody",
    assetItemTransfer: "Asset / Item Transfer",
    assetInventoryCount: "Asset Inventory Count",
    assetInventoryWorksheet: "Asset Inventory Worksheet",
    assetMaintenance: "Asset Maintenance",
    assetDisposal: "Asset Disposal",
    assetRevaluation: "Asset Revaluation",
    assetSplit: "Asset Split",

    assetLifeCycle: "Asset Life Cycle",
    assetFinancialMovements: "Asset Financial Movements",
    assetMovements: "Asset Movements",
    assetRecords: "Asset Records",
    assetItemTransferStatus: "Asset Item Transfer Status",
    assetTree: "Asset Tree",

    reportsFixedAssets: "Fixed Assets Reports",

    assetModels: "Asset Models",
    brands: "Brands",
    assetClasses: "Asset Classes",
    assetCategories: "Asset Categories",
    assetProperties: "Asset Properties",
    fixedAssetsSystemSettings: "Fixed Assets System Settings",
    shippingMethods: "Shipping Methods",
    assetCodingSettings: "Asset Coding Settings",
    salesTypes: "Sales Types",
    assetDisposalReasons: "Asset Disposal Reasons",
    assetTypes: "Asset Types",
    deliveryMethods: "Delivery Methods",
    purchaseRoutes: "Purchase Routes",
    quotationTerms: "Quotation Terms",
  },
  overview: {
    chooseSection: "Choose a section to continue",
    comingSoonHint: "These screens are part of the approved structure and will be built in a later stage",
    moduleComingSoon: "This module is being built",
    moduleComingSoonHint:
      "Its structure is approved but none of its screens exist yet. It will carry the same five standard sections.",
    standardSections: "Standard sections",
    systemSettingsEmpty: "No global settings yet",
    systemSettingsHint:
      "This section holds global system settings only. General Ledger settings live under Financial Management › General Ledger › Settings.",
    open: "Open",
  },
  dashboard: {
    placeholder: "The modules are still being built; indicators will appear here as they land",
    title: "Dashboard",
    subtitle: "An overview of your organisation's master data",
    kpi: {
      accounts: "Accounts",
      costCenters: "Cost centers",
      currencies: "Currencies",
      reports: "Available reports",
      postable: "postable",
      groups: "groups",
      activeOf: "{active} active of {total}",
      baseCurrency: "base",
      categories: "{count} categories",
    },
    quickActions: "Quick actions",
    quickActionsHint: "Jump straight to the screens you use most",
    activity: "Recent activity",
    activityEmpty: "No recorded activity",
    activityEmptyHint: "The audit trail exists in the database; no read endpoint is exposed yet",
    financialRestricted: "You do not have permission to view the financial summary.",
    financialAsOf: "Since the start of",
    financial: "Financial summary",
    financialEmpty: "No financial data",
    financialEmptyHint: "Figures appear once the first journal is posted; the journal screens are not built yet",
    notAvailable: "—",
    metrics: {
      revenue: "Revenue",
      expenses: "Expenses",
      netResult: "Net result",
      cash: "Cash",
    },
    banks: {
      title: "Bank balances",
      hint: "Read from each account's general ledger account.",
      empty: "No bank accounts defined",
      emptyHint: "Define a bank account and its ledger account, and its balance appears here.",
      restricted: "You do not have permission to view bank balances.",
      total: "Total",
      account: "Account",
      balance: "Balance",
      main: "Main",
      asOf: "As at",
    },
    sales: {
      title: "Sales",
      hint: "Posted invoices only. Reversed invoices are excluded.",
      restricted: "You do not have permission to view sales.",
      day: "Day",
      material: "Material sales",
      service: "Service sales",
      total: "Total",
      invoices: "invoices",
      noSales: "No sales on this day",
      today: "Today",
      yesterday: "Yesterday",
    },
  },
  reports: {
    title: "Reports Center",
    subtitle: "Browse the report categories available in the system",
    empty: "No report categories",
    emptyHint: "No report categories have been added for this tenant yet",
    recent: "Recently used",
    recentEmpty: "You have not opened any report yet",
    favorites: "Favorites",
    favoritesEmpty: "No reports marked as favorite",
    clearRecent: "Clear history",
    searchAll: "Search all reports",
    searchAllPlaceholder: "Search all reports…",
    inCategory: "in",
    comingSoon: "Coming soon",
    reportCount: {
      zero: "No reports",
      one: "1 report",
      other: "{count} reports",
    },
    categoryCount: {
      zero: "No categories",
      one: "1 category",
      other: "{count} categories",
    },
    category: {
      backToCenter: "Reports Center",
      searchLabel: "Search reports",
      searchPlaceholder: "Type a report name…",
      clearSearch: "Clear search",
      searchResults: "{shown} of {total}",
      noMatches: "No matching reports",
      noMatchesHint: "Try a different term or clear the search",
      empty: "No reports in this category",
      emptyHint: "Reports for this category will be added later",
      notFound: "Category not found",
      notFoundHint: "Check the link, or go back to the Reports Center",
      openReport: "Open report",
      addFavorite: "Add to favorites",
      removeFavorite: "Remove from favorites",
    },
    parameters: {
      title: "Report options",
      pending: "The options screen is not built yet",
      pendingHint: "This report's options and output will be built in a later stage",
      blocked: "This report needs its underlying business source to be enabled or completed",
      blockedReasons: {
        cancelled: "The business feature behind this report was cancelled and is not enabled.",
        noDueDate:
          "This system records no invoice due date or historical payment terms, and they cannot be derived from current settings.",
        noExpiry: "No authoritative expiry date exists on customer or supplier documents.",
        noSource: "No approved feature records this data.",
        noBills:
          "The promissory notes module is not enabled, and notes or cheques must not be treated as bills.",
        ambiguous: "This report's accounting meaning is not defined by an authoritative source.",
      },
      backToCategory: "Back to category",
    },
  },
  masterData: {
    currencies: "Currencies",
    costCenters: "Cost Centers",
    chartOfAccounts: "Chart of Accounts",
    add: "Add",
    addRoot: "Add root",
    addChild: "Add child",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    delete: "Delete",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    searchLabel: "Search",
    searchPlaceholder: "Search by code or name…",
    clearSearch: "Clear search",
    listView: "List view",
    treeView: "Tree view",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    expandNode: "Expand",
    collapseNode: "Collapse",
    active: "Active",
    inactive: "Inactive",
    status: "Status",
    actions: "Actions",
    empty: "No records yet",
    emptyHint: "Start by adding the first record",
    noMatches: "No matching records",
    noMatchesHint: "Try a different term or clear the search",
    loadFailed: "Could not load the data",
    saveFailed: "Could not save",
    companyRequired: "No company selected. Choose a company to continue",
    createdOk: "Created successfully",
    updatedOk: "Saved successfully",
    activatedOk: "Activated",
    deactivatedOk: "Deactivated",
    deletedOk: "Deleted",
    confirmDeleteTitle: "Confirm deletion",
    confirmDelete: "Delete “{name}”? This cannot be undone.",
    confirmDeactivateTitle: "Confirm deactivation",
    confirmDeactivate: "“{name}” and everything under it will be deactivated.",
    requiredField: "This field is required",
    code: "Code",
    nameEn: "English name",
    nameAr: "Arabic name",
    notes: "Notes",
    parent: "Parent",
    noParent: "No parent (root)",
    level: "Level",
    filterAll: "All",
    filterActive: "Active",
    filterInactive: "Inactive",
    isGroup: "Group",
    isGroupHint: "A group aggregates its children and cannot be posted to",
    detail: {
      title: "Details",
      noSelection: "Nothing selected",
      noSelectionHint: "Pick an item from the list to see its details",
      identity: "Identity",
      classification: "Classification",
      hierarchy: "Hierarchy",
      flags: "Properties",
      audit: "Change history",
      createdAt: "Created",
      updatedAt: "Last modified",
      openingBalance: "Opening balance",
      debit: "Debit",
      credit: "Credit",
      parentPath: "Path",
      children: "Children",
      backToList: "Back to list",
    },
    currency: {
      symbol: "Symbol",
      decimalPlaces: "Decimal places",
      exchangeRate: "Exchange rate",
      exchangeRateHint: "Saved with today's date; earlier rates are left untouched",
      isBase: "Base currency",
      isBaseHint: "Its rate is always 1, and it cannot be deactivated or deleted",
      baseBadge: "Base",
      inUse: "In use",
      inUseHint: "Referenced by accounts or postings, so it cannot be deleted",
      codeLocked: "The currency code cannot be changed after creation",
      currentRate: "Current Rate",
      effectiveFrom: "Effective From",
      effectiveTo: "Effective To",
      effectiveToHint: "Leave empty to keep the rate open-ended",
      baseFilter: "Base currency",
      baseOnly: "Base only",
      nonBaseOnly: "Non-base",
      refresh: "Refresh",
      view: "View",
      addRate: "Add Exchange Rate",
      rateHistory: "Exchange Rate History",
      rateHistoryEmpty: "No exchange rates recorded",
      rateAdded: "Exchange rate added",
      current: "Current",
      superseded: "Superseded",
      future: "Future",
      openEnded: "Open",
      sectionBasic: "Basic Information",
      sectionWordsAr: "Arabic Number-to-Words",
      sectionWordsEn: "English Number-to-Words",
      sectionRate: "Exchange Rate",
      iconUrl: "Currency icon",
      iconUrlHint: "Optional image URL",
      wordsSingular: "Singular",
      wordsDual: "Dual",
      wordsPlural: "Plural",
      wordsFractionSingular: "Fraction singular",
      wordsFractionDual: "Fraction dual",
      wordsFractionPlural: "Fraction plural",
      wordsPreview: "Words preview",
      wordsPreviewHint: "1,234.56 spelled with this currency's configuration",
      wordsUnset: "Number-to-words is not configured yet",
      baseRateFixed: "The base currency rate is always 1",
      baseCannotDelete: "The base currency cannot be deleted or deactivated",
      rateNotSet: "Not Set",
      rateOptionalHint: "Leave empty if no rate has been set yet",
      makeBase: "Make base currency",
      makeBaseTitle: "Change base currency",
      makeBaseMessage: "“{code}” becomes the company's base currency. No existing transaction is recalculated — every posting keeps the rate stored on it. Type the currency code to confirm.",
      makeBaseConfirm: "Type {code} to confirm",
      baseChanged: "Base currency changed",
      viewTitle: "Currency details",
      sectionStatusBase: "Status and Base Currency",
    },
    account: {
      taxSection: "Tax configuration",
      taxHint: "The tax types that post to this account. The link comes from the tax definition itself, never from the account's name.",
      taxNone: "No tax type is linked to this account.",
      taxConfigure: "Manage tax types",
      taxActive: "Active", taxInactive: "Inactive",
      taxSideOutput: "Output", taxSideInput: "Input", taxSideBoth: "Input and output",
      accountType: "Account type",
      accountTypeLookup: "Account Type",
      normalBalance: "Normal balance",
      financialStatement: "Financial statement",
      statements: { balanceSheet: "Balance Sheet", incomeStatement: "Income Statement" },
      statementFollowsTypeHint: "Set by the account type: assets, liabilities and equity are reported on the balance sheet; revenue, cost of sales and expenses on the income statement",
      debit: "Debit",
      credit: "Credit",
      allowPosting: "Allow posting",
      allowPostingHint: "Group accounts cannot be posted to",
      requiresCostCenter: "Cost center required",
      requiresCostCenterHint: "A cost center must be chosen when posting to this account",
      defaultCostCenter: "Default Cost Center",
      defaultCostCenterHint: "The cost center pre-filled when this account is posted to",
      defaultCostCenterNone: "None",
      defaultCostCenterRequired: "A Default Cost Center is required for this account",
      systemAccount: "System account",
      systemAccountHint: "Relied on by the system; it cannot be deleted or deactivated",
      category: "Account Category",
      openingDate: "Opening Date",
      referenceNumber: "Reference Number",
      closed: "Closed",
      closedHint: "A closed account rejects new journal entries but keeps its balances and history",
      typeLockedTitle: "Account type is locked",
      typeLockedHint: "This account already carries transactions, so its type, nature and financial statement cannot be changed",
      costCentersHint: "Choose which cost centers may be used when posting to this account. Leave empty to allow all.",
      types: {
        asset: "Assets",
        liability: "Liabilities",
        equity: "Equity",
        revenue: "Revenue",
        expense: "Expenses",
      },
    },
  },
  auditPosting: {
    errorSelfAudit: "Segregation of duties: the user who entered this entry cannot audit it. Another user must approve it.",
    errorSelfPost: "Segregation of duties: the user who entered this entry cannot post it.",
    errorAlreadyAudited: "This entry has already been audited.",
    errorAlreadyPosted: "This entry has already been posted.",
    errorInvalidAuditTransition: "This entry cannot be audited in its current status.",
    errorInvalidPostTransition: "This entry cannot be posted in its current status.",
    errorInvalidRejectTransition: "This entry cannot be returned in its current status.",
    errorCannotRejectPosted: "This entry is posted and must be reversed rather than returned.",
    errorAuditRequired: "The entry must be approved before it can be posted.",
    errorUnbalanced: "The entry is out of balance. Debit and credit must be equal.",
    errorZeroValue: "An entry with a zero total cannot be audited.",
    errorInsufficientLines: "An entry must contain at least two lines.",
    errorBranchOutOfScope: "This entry belongs to another branch.",
    errorForbidden: "You do not have permission to perform this action.",
    title: "Journal Audit & Posting",
    filters: "Search and filters",
    search: "Search",
    reset: "Reset filters",
    results: "Results",
    noResults: "No journal entries match these filters",
    openReview: "Open review",
    inquiryTitle: "Inquiry",
    inquiryDialogTitle: "Journal inquiry",
    review: "Journal review",
    selectPrompt: "Pick a document from the inquiry to start reviewing",
    auditStatus: "Audit status",
    postingStatus: "Posting status",
    validationStatus: "Validation",
    valid: "Valid",
    invalid: "Invalid",
    pendingAudit: "Pending audit",
    approved: "Audited",
    rejected: "Rejected",
    posted: "Posted",
    unposted: "Unposted",
    auditDecision: "Audit decision",
    auditNotes: "Audit notes",
    auditNotesHint: "Stored separately from the entry's own notes; the accountant sees them if it is returned",
    approveAudit: "Approve audit",
    rejectReturn: "Reject and return to accountant",
    saveNotes: "Save audit notes",
    rejectReason: "Reason for rejection",
    rejectReasonRequired: "A reason is required so the accountant knows what to correct",
    postJournal: "Post journal",
    postingBlocked: "This entry cannot be posted yet",
    blockers: {
      noPermission: "You do not have permission to post journal entries.",
      alreadyPosted: "Voucher {value} has already been posted.",
      rejected: "The entry was returned to the accountant. It must be corrected and audited before posting.",
      pendingAudit: "The entry is pending audit. Approve it under «Audit entry» first, then post it.",
      wrongStatus: "An entry with status «{value}» cannot be posted.",
      outOfBalance: "The entry is out of balance by {value}. Debit must equal credit.",
      periodClosed: "The accounting period is closed, so nothing can be posted into it.",
    },
    postingReady: "Ready to post",
    confirmPost: "Confirm posting",
    confirmPostBody: "This posts the entry to the General Ledger and updates balances. It cannot be undone; reversing requires a counter-entry.",
    approvedOn: "Approved on",
    rejectedOn: "Rejected on",
    postedOn: "Posted on",
    readOnlyReview: "Accounting data is read-only during audit. Corrections go back to the accountant.",
    voucherTypeFrom: "Voucher type from",
    voucherTypeTo: "Voucher type to",
    voucherNumberFrom: "Voucher number from",
    voucherNumberTo: "Voucher number to",
  },
  inquiry: {
    title: "Daily Transactions Inquiry",
    filters: "Search and filters",
    account: "Account",
    includeChildren: "Include child accounts",
    dateFrom: "Date from",
    dateTo: "Date to",
    fiscalYear: "Fiscal year",
    branch: "Branch",
    reportScope: "Report scope",
    scopeCompany: "Whole company",
    scopeBranch: "One branch",
    scopeAllBranches: "All branches, itemised",
    voucherFrom: "Voucher no. from",
    voucherTo: "Voucher no. to",
    referenceNumber: "Reference number",
    referenceDateFrom: "Reference date from",
    referenceDateTo: "Reference date to",
    user: "User",
    salesperson: "Salesperson",
    costCenter: "Cost center",
    subledger: "Subledger",
    currency: "Currency",
    auditStatus: "Audit status",
    postingStatus: "Posting status",
    descriptionSearch: "Search description",
    selectAccountPrompt: "Choose an account from the chart of accounts, then press Search",
    noResults: "No movements match these filters",
    results: "Movements",
    voucherDate: "Voucher date",
    voucherNumber: "Voucher no.",
    voucherType: "Voucher type",
    accountNumber: "Account no.",
    accountName: "Account name",
    description: "Description",
    exchangeRate: "Rate",
    debit: "Debit",
    credit: "Credit",
    localDebit: "Local debit",
    localCredit: "Local credit",
    sourceModule: "Source module",
    movementCount: "Movements",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    totalLocalDebit: "Total local debit",
    totalLocalCredit: "Total local credit",
    netMovement: "Net movement",
    posted: "Posted",
    unposted: "Unposted",
    invalidRange: "The 'date from' must not be later than the 'date to'",
    balancesElsewhere: "Opening and closing balances belong to the Account Movements inquiry",
  },
  cashSubledger: {
    title: "Cash Subledger Movements Inquiry",
    selectPrompt: "Choose a party type and a period, then press Search to list cash movements.",
    partyType: "Party type",
    partyAll: "All",
    partyCustomer: "Customer",
    partySupplier: "Supplier",
    partyNone: "No Party",
    noPartyHint: "A cash movement raised without a customer or supplier",
    customer: "Customer",
    supplier: "Supplier",
    selectParty: "Select…",
    partyCode: "Party code",
    partyName: "Party name",
    documentNumber: "Document no.",
    documentType: "Document type",
    documentStatus: "Document status",
    documentFrom: "Document no. from",
    documentTo: "Document no. to",
    openDocument: "Open document",
    paymentMethod: "Payment method",
    methodCash: "Cash",
    methodBankTransfer: "Bank Transfer",
    methodCard: "Card",
    methodCheque: "Cheque",
    methodImmediate: "Immediate Settlement",
    methodMixed: "Mixed Payment",
    cashbox: "Cash box",
    bankAccount: "Bank account",
    severalAccounts: "Several",
    netAmount: "Net amount",
    taxAmount: "Tax",
    totalAmount: "Total",
    paidAmount: "Paid",
    outstandingAmount: "Outstanding",
    localAmount: "Local amount",
    salesperson: "Salesperson",
    notAvailable: "n/a",
    taxUnavailableHint: "Tax is not available: no document in this system separates it",
    outstandingWarning: "Outstanding must be zero on this inquiry; anything else is a classification defect",
    includeCancelled: "Cancelled documents",
    includeCancelledHint: "Show cancelled and reversed (kept out of the totals)",
    statusCancelled: "Cancelled",
    statusReversed: "Reversed",
    excludedNote: "Cancelled or reversed documents, not included in the totals",
    documentCount: "Documents",
    totalNet: "Total net",
    totalTax: "Total tax",
    totalGross: "Total gross",
    totalPaid: "Total paid",
    totalOutstanding: "Total outstanding",
    totalLocal: "Total local",
    totalCash: "Total cash",
    totalBank: "Total bank",
    totalCard: "Total card",
    totalCheque: "Total cheque",
    totalReturns: "Total returns",
    totalNoParty: "No Party movements",
    settlementNote: "Total cash and bank are document-level figures, not row sums",
    unpostedNote: "The totals include unposted movements",
    notConfigured: "No account is classified as Cash or Bank, so the inquiry cannot recognise a cash settlement. Classify your cash box and bank accounts from the account details screen.",
  },
  unauditedNotes: {
    title: "Unaudited Journal Notes Inquiry",
    noResults: "No matching entries — nothing is waiting on an audit under these filters.",
    scope: "Scope",
    scopeOutstanding: "Outstanding",
    scopeUnresolved: "Unresolved audit notes",
    scopeAll: "All",
    statePendingAudit: "Pending Audit",
    stateReturned: "Returned for Correction",
    stateResubmitted: "Resubmitted",
    stateAudited: "Audited",
    statePosted: "Posted",
    stateCancelled: "Cancelled",
    stateReversed: "Reversed",
    voucherDate: "Voucher date",
    createdBy: "Created by",
    auditor: "Auditor",
    lastAction: "Last audit action",
    lastActionAt: "Last action at",
    actionCreated: "Created",
    actionModified: "Modified",
    actionAudited: "Audited",
    actionReturned: "Returned for correction",
    actionPosted: "Posted",
    actionReversed: "Reversed",
    auditNotes: "Audit notes",
    journalNotes: "Journal notes",
    noNote: "No note",
    notesHidden: "Not permitted",
    notesHiddenHint: "You do not hold the permission to read audit notes",
    unresolved: "unresolved",
    returnCount: "Returns",
    resubmissionCount: "Resubmissions",
    returnedRepeatedly: "Returned more than once — the earlier correction was not accepted",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    difference: "Difference",
    validationStatus: "Validation",
    balanced: "Balanced",
    unbalanced: "Unbalanced",
    unbalancedHint: "Debit does not equal credit — this entry cannot be approved",
    createdAt: "Created at",
    lastModifiedAt: "Last modified",
    openForCorrection: "Open for correction",
    openReadOnly: "View entry",
    viewAuditHistory: "View audit history",
    referenceDateFrom: "Reference date from",
    referenceDateTo: "Reference date to",
    auditNoteSearch: "Audit note search",
    journalNotesSearch: "Journal note search",
    returnedMoreThanOnce: "Returned more than once",
    returnedMoreThanOnceHint: "Only entries sent back repeatedly",
    unbalancedOnly: "Unbalanced",
    unbalancedOnlyHint: "Only entries that do not balance",
    includeCancelled: "Include cancelled",
    includeReversed: "Include reversed",
    entryCount: "Entries",
    pendingAuditCount: "Pending audit",
    returnedCount: "Returned",
    resubmittedCount: "Resubmitted",
    unresolvedNotesCount: "Unresolved notes",
    unbalancedCount: "Unbalanced",
    totalDifference: "Total difference",
    ownEntriesOnly: "This list shows only entries you created.",
    unbalancedWarning: "An unbalanced entry cannot be approved until it is corrected",
  },
  freezeTransactions: {
    title: "Freeze Transactions",
    pageDescription: "Define the periods in which transactions are blocked, by branch and voucher type.",
    readOnlyNotice: "You have view-only access. Freezes cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Freeze Transactions",
    phaseNotice:
      "This phase records the freeze decision only; blocking is not yet applied to journal entries.",
    sectionHeader: "Freeze details", sectionScope: "Scope", sectionVoucherTypes: "Selected voucher types",
    code: "Code", freezeDate: "Freeze Date", fromDate: "From Date", toDate: "To Date",
    description: "Description",
    freezeDateHint: "The date the freeze decision was recorded — not part of the blocked period.",
    branches: "Branches", voucherTypes: "Voucher Types", addVoucherTypes: "Add Voucher Types",
    remove: "Remove", search: "Search", refresh: "Refresh", save: "Save", cancel: "Cancel",
    newFreeze: "New freeze", active: "Active", inactive: "Inactive",
    activate: "Activate", deactivate: "Deactivate", catalogue: "Freeze records", period: "Period",
    empty: "No freeze records.", emptyVoucherTypes: "No voucher type selected yet.",
    noneSelected: "None selected", required: "Required",
    created: "Freeze created.", saved: "Freeze saved.",
    activated: "Freeze activated.", deactivated: "Freeze deactivated.",
    loadError: "Could not load freeze records.", saveError: "Could not save the freeze.",
    searchPlaceholder: "Search by code or description", selectAll: "Select all", clearAll: "Clear",
  },
  accountCategories: {
    title: "Account Categories",
    pageDescription: "Define the categories that Chart of Accounts accounts are classified under.",
    readOnlyNotice: "You have view-only access. Account categories cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Account Categories",
    sectionCategory: "Account category", code: "Code", descriptionAr: "Arabic description",
    descriptionEn: "English description", active: "Active", inactive: "Inactive",
    catalogue: "Defined account categories", save: "Save", cancel: "Cancel", deleteAction: "Delete",
    usage: "Usage", inUse: "In use", searchPlaceholder: "Search by code or description",
    search: "Search", refresh: "Refresh", empty: "No account category matches.",
    required: "Required", created: "Account category created.", saved: "Account category saved.",
    deleted: "Account category deleted.", activated: "Account category activated.",
    deactivated: "Account category deactivated.",
    loadError: "Could not load account categories.", saveError: "Could not save the account category.",
    deleteTitle: "Delete account category", deleteConfirm: "This deletes the account category. Continue?",
    close: "Close", comingSoonHint: "Coming soon", print: "Print", exportPdf: "PDF", exportExcel: "Excel",
    inactiveHint:
      "An inactive category is hidden from new accounts and stays visible on the accounts that already carry it.",
  },
  accountTypes: {
    title: "Account Types",
    pageDescription: "Define the account types that Chart of Accounts accounts are classified under.",
    readOnlyNotice: "You have view-only access. Account types cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Account Types",
    sectionAccountType: "Account type", code: "Code", descriptionAr: "Arabic description",
    descriptionEn: "English description", scope: "Account Type", local: "Local",
    international: "International", active: "Active", inactive: "Inactive",
    catalogue: "Defined account types", newType: "New type", save: "Save", cancel: "Cancel",
    deleteAction: "Delete", activate: "Activate", deactivate: "Deactivate", usage: "Usage",
    inUse: "In use", searchPlaceholder: "Search by code or description", search: "Search",
    refresh: "Refresh", empty: "No account type matches.", required: "Required",
    created: "Account type created.", saved: "Account type saved.", deleted: "Account type deleted.",
    activated: "Account type activated.", deactivated: "Account type deactivated.",
    loadError: "Could not load account types.", saveError: "Could not save the account type.",
    deleteTitle: "Delete account type", deleteConfirm: "This deletes the account type. Continue?",
    close: "Close", comingSoonHint: "Coming soon", print: "Print", exportPdf: "PDF", exportExcel: "Excel",
  },
  serviceSalesTypes: {
    title: "Service Sales Types",
    pageDescription:
      "Define the kinds of service the company sells, and the account and tax type each one carries.",
    breadcrumb: "Financial Management » Receivables » Settings » Service Sales Types",
    readOnlyNotice: "You have view-only access. Service sales types cannot be edited.",
    purposeNotice:
      "Configuration only: the account and tax type are read later when a service invoice is raised. Nothing here posts.",
    sectionServiceSalesType: "Service sales type",
    catalogue: "Defined types",
    code: "Code", descriptionAr: "Arabic Description", descriptionEn: "English Description",
    account: "Account", taxType: "Tax Type", taxable: "Taxable",
    active: "Active", inactive: "Inactive",
    selectAccount: "Select account", selectTaxType: "Select tax type", noTaxType: "— None —",
    yes: "Yes", no: "No", back: "Back", query: "Inquiry", save: "Save",
    saveAndNew: "Save + New", cancel: "Cancel", refresh: "Refresh", search: "Search",
    searchPlaceholder: "Search by code, description or account number",
    statusAll: "All", statusActive: "Active", statusInactive: "Inactive",
    activate: "Activate", deactivate: "Deactivate",
    auditHistory: "Change history", auditNone: "No change recorded.", auditFields: "Fields",
    close: "Close", empty: "No service sales type matches.", required: "Required",
    taxTypeRequired: "A taxable service sales type must name a tax type.",
    created: "Service sales type created.", saved: "Service sales type saved.",
    activated: "Activated.", deactivated: "Deactivated.",
    loadError: "Could not load service sales types.",
    saveError: "Could not save the service sales type.",
    noDeleteHint: "A type in use is never deleted; deactivate it instead.",
    lastChanged: "Last changed",
  },
  paymentMethodsScreen: {
    title: "Payment Methods",
    pageDescription: "Define how amounts are settled: cash, card, cheque, transfer and the rest.",
    breadcrumb: "Financial Management » Cash & Banks » Settings » Payment Methods",
    readOnlyNotice: "You have view-only access. Payment methods cannot be edited.",
    purposeNotice: "A payment method says how an amount was settled and where it lands: cash and cheques reach a cashbox, cards, e-payments and transfers reach a bank account. Definitions only — nothing here posts.",
    list: "Payment Methods", code: "Code", descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    active: "Active", inactive: "Inactive", usage: "In use", actions: "Actions",
    newMethod: "New Method", save: "Save", cancel: "Cancel", edit: "Edit", remove: "Delete",
    activate: "Activate", deactivate: "Deactivate",
    search: "Search", searchPlaceholder: "Search by code or description", refresh: "Refresh",
    showInactive: "Show inactive", empty: "No payment method yet.", required: "Required",
    saved: "Payment method saved.", deleted: "Payment method deleted.",
    activated: "Activated.", deactivated: "Deactivated.",
    loadError: "Could not load payment methods.", saveError: "Could not save.",
    deleteConfirm: "This deletes the payment method. Continue?",
    inUseHint: "A method in use cannot be deleted; deactivate it instead.",
    settlement: "Settlement destination", settlesInto: "Settles into",
    targetNone: "No destination", targetCashbox: "Cashbox", targetBank: "Bank",
    cashbox: "Cashbox", bank: "Bank", bankBranch: "Branch", bankAccount: "Bank Account",
    settlementAccount: "Destination account", accountFromCashbox: "Taken from the cashbox definition",
    choose: "— Choose —", branchOptional: "— Branch (optional) —",
    fromBankAccounts: "From the accounts defined on this bank.",
    noBankAccountsHint: "This bank has no accounts defined yet; choosing from the Chart of Accounts.",
  },
  cashDeposit: {
    title: "Cash Deposit",
    pageDescription: "Cash paid into one of the company's bank accounts.",
    breadcrumb: "Financial Management » Cash & Banks » Daily Transactions » Cash Deposit",
    readOnlyNotice: "You have view-only access. Deposits cannot be recorded or amended.",
    purposeNotice: "The bank account is debited against the cash account of the cashbox the money was paid in from. The cost-centre detail below splits that credit, and is optional.",
    sectionSources: "Cost centre detail",
    sequence: "Sequence", assignedOnSave: "Assigned on save",
    documentDate: "Document Date", bank: "Bank", bankBranch: "Bank Branch",
    bankAccount: "Bank Account",
    bankAccountHint: "Only the selected bank's own accounts, active and postable.",
    currency: "Currency", branch: "Branch", fiscalYear: "Fiscal Year",
    fromDocumentDate: "Resolved from the document date",
    amount: "Amount", exchangeRate: "Exchange Rate",
    baseRateHint: "The base currency's rate is always 1.",
    localAmount: "Local Amount", description: "Description",
    costCenter: "Cost Center",
    noCostCenters: "No cost centres listed — the whole deposit posts to the cashbox account undistributed.",
    cashbox: "Cashbox paid in from",
    cashboxHint: "Open, active cashboxes only. Its cash account is the credit side.",
    creditSide: "Credit side",
    accountNumber: "Account Number",
    reachingBank: "Reaching the bank", reachingBankHint: "The amount less the charges.",
    panels: {
      costCenters: "Cost Centers", documents: "Receipts and Invoices",
      attachments: "Attachments", expenses: "Expenses",
    },
    documentNumber: "Document Number",
    documentsHint: "The receipts and invoices this deposit covers. Recorded, never posted.",
    noDocuments: "No receipt or invoice listed.",
    expenseAccount: "Expense Account",
    expensesHint: "What the bank charged on this deposit — deducted from what reaches the bank.",
    noExpenses: "No charge on this deposit.",
    saveBeforeAttaching: "Save the deposit first, then files can be attached.",
    tools: "Tools",
    insertAllCostCenters: "Insert all cost centres",
    insertCostCentersByGroup: "Insert cost centres by group",
    deleteRecord: "Delete Record",
    deleteConfirm: "This deletes the whole voucher. Continue?",
    deleted: "Voucher deleted.",
    postedNotDeletable: "A posted voucher cannot be deleted; reverse it instead.",
    linesTotal: "Lines total",
    unbalancedHint: "Once you distribute at all, the cost-centre lines must total the deposit amount.",
    actions: "Actions", addRow: "Add Row", remove: "Delete",
    branchOptional: "— Branch (optional) —", choose: "— Choose —", newDeposit: "New Deposit",
    inquiry: "Inquiry", save: "Save", saveAndNew: "Save and New", cancel: "Cancel",
    post: "Post", print: "Print", attachments: "Attachments", auditTrail: "Audit Trail",
    searchPlaceholder: "Search by sequence or description", empty: "No deposit yet.",
    status: "Status", statusDraft: "Unposted", statusPosted: "Posted",
    alreadyPosted: "This deposit has already been posted.",
    saved: "Deposit saved", postedNotice: "Deposit posted",
    loadError: "Could not load deposits.", saveError: "Could not save.",
    printError: "Could not print.",
  },
  cashWithdrawal: {
    title: "Cash Withdrawal",
    pageDescription: "Cash drawn out of one of the company's bank accounts.",
    breadcrumb: "Financial Management » Cash & Banks » Daily Transactions » Cash Withdrawal",
    readOnlyNotice: "You have view-only access. Withdrawals cannot be recorded or amended.",
    purposeNotice: "The cash account of the cashbox the money was drawn into is debited against the bank account. The cost-centre detail below splits that debit, and is optional.",
    sectionSources: "Cost centre detail",
    sequence: "Sequence", assignedOnSave: "Assigned on save",
    documentDate: "Document Date", bank: "Bank", bankBranch: "Bank Branch",
    bankAccount: "Bank Account",
    bankAccountHint: "Only the selected bank's own accounts, active and postable.",
    currency: "Currency", branch: "Branch", fiscalYear: "Fiscal Year",
    fromDocumentDate: "Resolved from the document date",
    amount: "Amount", exchangeRate: "Exchange Rate",
    baseRateHint: "The base currency's rate is always 1.",
    localAmount: "Local Amount", description: "Description",
    costCenter: "Cost Center",
    noCostCenters: "No cost centres listed — the whole withdrawal posts to the cashbox account undistributed.",
    cashbox: "Cashbox drawn into",
    cashboxHint: "Open, active cashboxes only. Its cash account is the debit side.",
    creditSide: "Debit side",
    accountNumber: "Account Number",
    panels: {
      costCenters: "Cost Centers",
      attachments: "Attachments",
    },
    saveBeforeAttaching: "Save the withdrawal first, then files can be attached.",
    tools: "Tools",
    insertAllCostCenters: "Insert all cost centres",
    insertCostCentersByGroup: "Insert cost centres by group",
    deleteRecord: "Delete Record",
    deleteConfirm: "This deletes the whole voucher. Continue?",
    deleted: "Voucher deleted.",
    postedNotDeletable: "A posted voucher cannot be deleted; reverse it instead.",
    linesTotal: "Lines total",
    unbalancedHint: "Once you distribute at all, the cost-centre lines must total the withdrawal amount.",
    actions: "Actions", addRow: "Add Row", remove: "Delete",
    branchOptional: "— Branch (optional) —", choose: "— Choose —", newDeposit: "New Withdrawal",
    inquiry: "Inquiry", save: "Save", saveAndNew: "Save and New", cancel: "Cancel",
    post: "Post", print: "Print", attachments: "Attachments", auditTrail: "Audit Trail",
    searchPlaceholder: "Search by sequence or description", empty: "No withdrawal yet.",
    status: "Status", statusDraft: "Unposted", statusPosted: "Posted",
    alreadyPosted: "This withdrawal has already been posted.",
    saved: "Withdrawal saved", postedNotice: "Withdrawal posted",
    loadError: "Could not load withdrawals.", saveError: "Could not save.",
    printError: "Could not print.",
  },
  cashboxTransactions: {
    title: "Cashbox Transactions",
    pageDescription: "Movements on a cashbox: funding, zeroing, counting, settling a representative, depositing.",
    breadcrumb: "Financial Management » Cash & Banks » Daily Transactions » Cashbox Transactions",
    readOnlyNotice: "You have view-only access. Cashbox movements cannot be recorded or amended.",
    purposeNotice: "The direction decides which side the cashbox sits on: money leaving it credits it, money arriving debits it. The cost-centre detail splits the cashbox side, and is optional.",
    sectionSources: "Cost centre detail",
    sequence: "Sequence", assignedOnSave: "Assigned on save",
    documentDate: "Document Date", bank: "Bank", bankBranch: "Bank Branch",
    bankAccount: "Bank Account",
    bankAccountHint: "Only the selected bank's own accounts, active and postable.",
    currency: "Currency", branch: "Branch", fiscalYear: "Fiscal Year",
    fromDocumentDate: "Resolved from the document date",
    amount: "Amount", exchangeRate: "Exchange Rate",
    baseRateHint: "The base currency's rate is always 1.",
    localAmount: "Local Amount", description: "Description",
    costCenter: "Cost Center",
    noCostCenters: "No cost centres listed — the whole withdrawal posts to the cashbox account undistributed.",
    cashbox: "Cashbox",
    cashboxHint: "Open, active cashboxes only. Its cash account is the debit side.",
    creditSide: "Debit side",
    accountNumber: "Account Number",
    panels: {
      costCenters: "Cost Centers",
      attachments: "Attachments",
    },
    saveBeforeAttaching: "Save the movement first, then files can be attached.",
    movementTime: "Time", direction: "Cash direction", directionFrom: "From", directionTo: "To",
    partyType: "Party", partyCashbox: "Cashbox", partyBank: "Bank", movementType: "Movement type",
    typeDeposit: "Deposit", typeZeroing: "Cashbox zeroing", typeFunding: "Cashbox funding",
    typeCount: "Cashbox count minutes", typeRepresentative: "Representative settlement",
    counterCashbox: "The other cashbox", counterAccount: "Counter account",
    counterAccountHint: "The cashbox's differences account, an opening balance, or the representative's — from the Chart of Accounts.",
    tools: "Tools",
    insertAllCostCenters: "Insert all cost centres",
    insertCostCentersByGroup: "Insert cost centres by group",
    deleteRecord: "Delete Record",
    deleteConfirm: "This deletes the whole voucher. Continue?",
    deleted: "Voucher deleted.",
    postedNotDeletable: "A posted voucher cannot be deleted; reverse it instead.",
    linesTotal: "Lines total",
    unbalancedHint: "Once you distribute at all, the cost-centre lines must total the withdrawal amount.",
    actions: "Actions", addRow: "Add Row", remove: "Delete",
    branchOptional: "— Branch (optional) —", choose: "— Choose —", newDeposit: "New Movement",
    inquiry: "Inquiry", save: "Save", saveAndNew: "Save and New", cancel: "Cancel",
    post: "Post", print: "Print", attachments: "Attachments", auditTrail: "Audit Trail",
    searchPlaceholder: "Search by sequence or description", empty: "No movement yet.",
    status: "Status", statusDraft: "Unposted", statusPosted: "Posted",
    alreadyPosted: "This movement has already been posted.",
    saved: "Movement saved", postedNotice: "Movement posted",
    loadError: "Could not load movements.", saveError: "Could not save.",
    printError: "Could not print.",
  },
  bankFacilities: {
    title: "Bank Facilities",
    pageDescription: "Define the facilities banks have granted, their terms and their charges.",
    breadcrumb: "Financial Management » Cash & Banks » Basic Data » Bank Facilities",
    readOnlyNotice: "You have view-only access. Facilities cannot be edited.",
    purposeNotice: "Definitions only: this screen draws nothing on the facility, creates no journal entry and does not affect General Ledger balances.",
    sectionFacility: "Facility", sectionLoanAccount: "Loan Account Data",
    sectionCurrentAccount: "Current Account Data", sectionExpenses: "Expenses",
    list: "Facilities",
    code: "Code", facilityType: "Facility Type", currency: "Currency", ceiling: "Facility Ceiling",
    interestRate: "Interest Rate %", loanType: "Loan Type", graceDays: "Grace Days",
    yearDays: "Approved Year Days", numberOfPayments: "Number of Payments",
    restrictionBasis: "Interest Restriction Basis",
    active: "Active", inactive: "Inactive", ceilingExceeded: "Ceiling may be exceeded",
    externalParty: "External party",
    bank: "Bank", branch: "Branch", account: "Account",
    branchOptional: "— Branch (optional) —", choose: "— Choose —",
    fromBankAccounts: "From the accounts defined on this bank.",
    noBankAccountsHint: "This bank has no accounts defined yet; choosing from the Chart of Accounts.",
    expenseAccount: "Expense Account",
    expenseAccountHint: "From the Chart of Accounts: an active, postable expense account.",
    percentage: "Percentage %",
    percentageHint: "With a percentage, the value is computed from the ceiling and the keyed amount is ignored.",
    amount: "Amount", computed: "Computed", paymentMethod: "Payment Method",
    methodOptional: "— Payment method (optional) —", actions: "Actions",
    addRow: "Add Row", edit: "Edit", remove: "Delete",
    newFacility: "New Facility", save: "Save", cancel: "Cancel",
    activate: "Activate", deactivate: "Deactivate",
    search: "Search", searchPlaceholder: "Search by code", refresh: "Refresh",
    showInactive: "Show inactive",
    empty: "No facility yet.", noExpenses: "No expense on this facility.",
    saveFirstHint: "Save the facility first, then its expenses can be added.",
    typeLoan: "Loan", typeOverdraft: "Overdraft", typeLetterOfCredit: "Letter of Credit",
    typeLetterOfGuarantee: "Letter of Guarantee", typeChequesForCollection: "Cheques for Collection",
    typeBillDiscounting: "Bill Discounting",
    loanEqual: "Equal instalments", loanDecreasing: "Decreasing instalments",
    loanLumpSum: "Lump sum at maturity", loanInterestFirst: "Interest only, then principal",
    basisOutstanding: "Outstanding balance", basisCeiling: "Approved ceiling",
    basisUtilised: "Utilised amount", basisDaily: "Daily balance",
    saved: "Facility saved.", activated: "Facility activated.", deactivated: "Facility deactivated.",
    expenseSaved: "Expense line saved.", expenseRemoved: "Expense line deleted.",
    expenseDeleteConfirm: "This deletes the expense line. Continue?",
    loadError: "Could not load facilities.", saveError: "Could not save.",
  },
  banks: {
    title: "Bank Definition",
    pageDescription: "Define the banks the company deals with, and their branches.",
    breadcrumb: "Financial Management » Cash & Banks » Basic Data » Bank Definition",
    readOnlyNotice: "You have view-only access. Banks cannot be edited.",
    purposeNotice: "Definitions only: this screen creates no journal entry and does not affect General Ledger balances.",
    templateNotice: "No cheque-printing template module exists yet; the field is stored as a code and will be linked once one is built.",
    sectionBank: "Bank", sectionBranches: "Bank Branches", list: "Banks",
    code: "Code", descriptionAr: "Arabic Description", descriptionEn: "English Description",
    swiftCode: "Swift Code", bankCode: "Bank Code",
    templateCode: "Cheque Printing Template Code",
    isDefault: "Default", active: "Active", inactive: "Inactive",
    branchCode: "Code", phone: "Phone", fax: "Fax", mobile: "Mobile",
    address: "Address", email: "Email", postalAddress: "Postal Address / PO Box",
    actions: "Actions", branches: "Branches", templates: "Templates",
    sectionAccounts: "Bank Accounts", noAccounts: "No account defined for this bank yet.",
    currency: "Currency", glAccount: "GL Account", accountNumber: "Account Number",
    iban: "IBAN", isMain: "Main", branch: "Branch",
    branchOptional: "— Branch (optional) —", choose: "— Choose —",
    accountSaved: "Bank account saved.", accountRemoved: "Bank account deleted.",
    accountRemoveHint: "An account in use cannot be deleted; deactivate it instead.",
    accountDeleteConfirm: "This deletes the bank account. Continue?",
    newBank: "New Bank", inquiry: "Inquiry", save: "Save", saveAndNew: "Save and New",
    cancel: "Cancel", edit: "Edit", activate: "Activate", deactivate: "Deactivate", deleteBank: "Delete",
    addRow: "Add Row", remove: "Remove",
    search: "Search", searchPlaceholder: "Search by code, description or SWIFT", refresh: "Refresh",
    showInactive: "Show inactive",
    empty: "No bank yet.", noBranches: "No branch for this bank.",
    noSelection: "No bank selected",
    noSelectionHint: "Pick a bank from the list to see its details and branches, or create a new one.",
    created: "Bank created.", saved: "Bank saved.", deleted: "Bank deleted.",
    activated: "Bank activated.", deactivated: "Bank deactivated.",
    branchSaved: "Branch saved.", branchRemoved: "Branch removed.",
    branchRemoveHint: "Removing a branch does not affect any historical banking transaction; prefer deactivating one already in use.",
    loadError: "Could not load banks.", saveError: "Could not save.",
    deleteConfirm: "This deletes the bank. Continue?",
    branchDeleteConfirm: "This deletes the branch. Continue?",
    auditHistory: "Audit History", close: "Close", required: "Required",
  },
  cashboxes: {
    title: "Cashbox Definition",
    pageDescription: "Define cashboxes and their hierarchy, and the accounts their movements are written to.",
    breadcrumb: "Financial Management » Cash & Banks » Basic Data » Cashbox Definition",
    readOnlyNotice: "You have view-only access. Cashboxes cannot be edited.",
    purposeNotice: "Definitions only: this screen creates no journal entry and does not affect General Ledger balances.",
    sectionDefinition: "Cashbox Definition", sectionAccounts: "Financial Accounts", tree: "Cashbox tree",
    parent: "Parent Cashbox", noParent: "No parent (root)",
    code: "Code", descriptionAr: "Arabic Description", descriptionEn: "English Description",
    openingCashbox: "Opening Cashbox", closingCashbox: "Closing Cashbox",
    cashboxGroup: "Cashbox Group", groupPending: "Awaiting the Cashbox Groups module",
    cashAccount: "Cash Account", chequesAccount: "Cheques-in-Collection Account",
    differencesAccount: "Differences Account", exchangeAccount: "Exchange / Conversion Account",
    returnedLegalAccount: "Returned Cheques - Legal Department Account",
    returnedCustomersAccount: "Returned Cheques - Customers Account",
    unusedNoOperation: "No operation uses this yet — Cash & Banks has no exchange transaction. Currency conversion on deposits and withdrawals uses the company-wide FX difference accounts.",
    unusedHandBackPostsNothing: "Nothing posts here: handing a cheque back to the drawer writes no journal, because the bounce already returned the amount to the customer\u2019s receivable.",
    creationDate: "Creation Date", closingDate: "Closing Date",
    active: "Active", inactive: "Inactive", closed: "Closed", openState: "Open",
    closedByParent: "Closed by parent", inactiveByParent: "Inactive by parent",
    newCashbox: "New Root", newChild: "New Child", save: "Save", cancel: "Cancel", edit: "Edit",
    activate: "Activate", deactivate: "Deactivate", deleteCashbox: "Delete",
    search: "Search", searchPlaceholder: "Search by code or description", refresh: "Refresh",
    showInactive: "Show inactive", pickAccount: "Select an account", clear: "Clear",
    empty: "No cashbox yet.", noSelection: "No cashbox selected",
    noSelectionHint: "Pick a cashbox from the tree to see its details, or create a new one.",
    required: "Required",
    created: "Cashbox created.", saved: "Cashbox saved.", deleted: "Cashbox deleted.",
    activated: "Cashbox activated.", deactivated: "Cashbox deactivated.",
    loadError: "Could not load cashboxes.", saveError: "Could not save the cashbox.",
    deleteConfirm: "This deletes the cashbox. Continue?", close: "Close",
    auditHistory: "Audit History", historyEmpty: "No recorded change.",
  },
  costCenterGroups: {
    title: "Cost Center Groups",
    pageDescription: "Define groups of existing cost centers, so several can be read together in reports and entries.",
    breadcrumb: "Financial Management » General Ledger » Settings » Cost Center Groups",
    readOnlyNotice: "You have view-only access. Groups cannot be edited.",
    purposeNotice: "A group gathers existing cost centers to be read together. It creates no cost center and does not change the cost center tree.",
    sectionGroup: "Cost Center Group", sectionCostCenters: "Cost Centers",
    code: "Code", descriptionAr: "Arabic description", descriptionEn: "English description",
    active: "Active", inactive: "Inactive",
    costCenter: "Cost Center", costCenterCode: "Cost Center Code",
    parent: "Parent", path: "Path", level: "Level", notes: "Notes",
    noParent: "No parent (root)",
    actions: "Actions", members: "Cost centers",
    newGroup: "New Group", save: "Save", cancel: "Cancel", edit: "Edit",
    remove: "Remove", deleteGroup: "Delete Group",
    activate: "Activate", deactivate: "Deactivate",
    addCostCenter: "Add Row", addSelected: "Add Selected",
    search: "Search", searchPlaceholder: "Search by code or description", refresh: "Refresh",
    query: "Query", back: "Back",
    pickerTitle: "Select cost centers", pickerEmpty: "No cost center matches.",
    alreadyInGroup: "Already added", treeGroupHint: "A grouping node in the tree - carries no transactions",
    empty: "No group yet.", noMembers: "No cost center added yet.",
    required: "Required", saveFirst: "Save the group first to add cost centers.",
    created: "Cost center group created.", saved: "Cost center group saved.",
    deleted: "Cost center group deleted.",
    activated: "Cost center group activated.", deactivated: "Cost center group deactivated.",
    memberAdded: "Added to the group.", memberRemoved: "Removed from the group.",
    removeHint: "Removing deletes the membership only. The cost center itself is kept.",
    loadError: "Could not load cost center groups.",
    saveError: "Could not save the cost center group.",
    deleteConfirm: "This deletes the group. Continue?", deleteTitle: "Delete group",
    close: "Close", print: "Print", exportPdf: "PDF", exportExcel: "Excel",
    comingSoonHint: "Coming soon", exporting: "Preparing…",
  },
  openingBalances: {
    title: "Account Opening Balances",
    pageDescription:
      "Prepare opening balances for Chart of Accounts posting accounts by fiscal year, account and currency.",
    readOnlyNotice: "You have view-only access. Opening balances cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Account Opening Balances",
    phaseNotice:
      "Preparation phase only: no voucher or journal entry is created, and General Ledger balances are not affected.",
    accountNumber: "Account Number", accountName: "Account Name", debit: "Debit", credit: "Credit",
    fiscalYear: "Fiscal Year", currency: "Currency", exchangeRate: "Exchange Rate",
    localDebit: "Local Debit", localCredit: "Local Credit", notes: "Notes",
    status: "Status", actions: "Actions", branch: "Branch", allBranches: "Company level",
    accountType: "Account Type", financialStatement: "Financial Statement",
    showZeroBalances: "Show zero balances", showInactiveAccounts: "Show inactive accounts",
    search: "Search", searchPlaceholder: "Search by account number or name",
    refresh: "Refresh", importComingSoon: "Import Opening Balances",
    print: "Print", exportPdf: "PDF", exportExcel: "Excel",
    totalDebit: "Total Debit", totalCredit: "Total Credit",
    totalLocalDebit: "Total Local Debit", totalLocalCredit: "Total Local Credit",
    difference: "Difference", balanced: "Balanced", unbalanced: "Unbalanced", batchStatus: "Batch status",
    stNotEntered: "Not Entered", stZero: "Zero", stEntered: "Entered", stInvalid: "Invalid", stLocked: "Locked",
    bsDraft: "Draft", bsBalanced: "Balanced", bsUnbalanced: "Unbalanced", bsLocked: "Locked", bsPosted: "Posted",
    rateRequired: "An approved rate is required",
    yearClosed: "The fiscal year is closed; this screen is read-only.",
    empty: "No account matches.", loadError: "Could not load opening balances.",
    edit: "Edit", comingSoonHint: "Coming soon", baseCurrency: "Company currency",
    saveSelected: "Save Selected Rows", saveAll: "Save All Changes",
    cancelChanges: "Cancel Changes", resetRow: "Reset to Zero",
    selectRow: "Select row", selectAll: "Select all",
    changedCount: "changed", selectedCount: "selected",
    unsavedBanner: "You have unsaved changes. They are not saved until you press Save.",
    discardConfirm: "Unsaved changes will be lost. Continue?",
    noChanges: "There is nothing to save.",
    nothingSelected: "No changed row is selected.",
    savedCreated: "new", savedUpdated: "updated",
    saveError: "Could not save the opening balances.",
    fixErrorsFirst: "Correct the invalid rows before saving.",
    errBothSides: "Debit and credit cannot both be greater than zero.",
    errNegative: "The amount cannot be negative.",
    errBaseRate: "The base-currency rate must be exactly 1.",
    errRatePositive: "The exchange rate must be greater than zero.",
    calculated: "Calculated from the amount and the exchange rate",
    changedRow: "Changed, not yet saved",
    printPreview: "Print Preview", exporting: "Preparing…",
    exportError: "Could not prepare the file.",
    saveBeforeExport: "An export carries the saved figures only; save your changes first.",
  },
  voucherTypes: {
    title: "Voucher Types",
    pageDescription:
      "Define the voucher types transactions are keyed against, and how each is numbered and printed.",
    readOnlyNotice: "You have view-only access. Voucher types cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Voucher Types",
    back: "Back",
    query: "Query",
    save: "Save",
    cancel: "Cancel",
    newType: "New type",
    sectionVoucherType: "Voucher type",
    code: "Code",
    descriptionAr: "Arabic description",
    descriptionEn: "English description",
    sequenceType: "Sequence type",
    taxInclusion: "Tax inclusion",
    numberingMethod: "Numbering method",
    reportDestination: "Report destination",
    numberOfCopies: "Number of copies",
    effectType: "Effect type",
    active: "Active",
    inactive: "Inactive",
    seqYearly: "Yearly",
    seqMonthly: "Monthly",
    seqContinuous: "Continuous",
    taxIncluded: "Included",
    taxNotIncluded: "Not Included",
    numUnified: "Unified",
    numPerBranch: "Per Branch",
    numPerCompany: "Per Company",
    destScreen: "Screen",
    destPrinter: "Printer",
    destPdf: "PDF",
    effectAutomatic: "Automatic",
    effectManual: "Manual",
    effectApproval: "Approval",
    receivablesBreadcrumb: "Financial Management › Receipts › Settings › Voucher Types",
    cashBanksBreadcrumb: "Financial Management › Cash and Banks › Settings › Voucher Types",
    searchPlaceholder: "Search by code or description",
    statusAll: "All",
    statusActive: "Active",
    statusInactive: "Inactive",
    catalogue: "Defined voucher types",
    usage: "Usage",
    usageTitle: "Voucher type usage",
    usageCount: "Vouchers",
    usageNone: "This type has not been used on any voucher yet.",
    usageRecent: "Most recent vouchers",
    voucherNumber: "Voucher no.",
    voucherDate: "Date",
    auditHistory: "Change history",
    auditNone: "No changes recorded.",
    auditAction: "Action",
    auditUser: "User",
    auditWhen: "When",
    auditFields: "Fields",
    createdBy: "Created by",
    updatedBy: "Modified by",
    activatedBy: "Activated by",
    deactivatedBy: "Deactivated by",
    print: "Print",
    exportPdf: "PDF",
    exportExcel: "Excel",
    activate: "Activate",
    deactivate: "Deactivate",
    systemType: "System type",
    systemTypeLocked: "A system voucher type: it can be renamed but not deactivated.",
    close: "Close",
    required: "Required",
    copiesPositive: "Number of copies must be greater than zero.",
    created: "Voucher type created.",
    saved: "Voucher type saved.",
    activated: "Voucher type activated.",
    deactivated: "Voucher type deactivated.",
    loadError: "Could not load voucher types.",
    saveError: "Could not save the voucher type.",
    empty: "No voucher type matches.",
    inUse: "In use",
  },
  financialStatements: {
    title: "Financial Statement Setup",
    pageDescription:
      "Define the financial statements this company presents, then build the structure of each.",
    readOnlyNotice: "You have view-only access. These statements cannot be edited.",
    breadcrumb: "Financial Management » General Ledger » Settings » Financial Statement Setup",
    back: "Back",
    query: "Query",
    save: "Save",
    cancel: "Cancel",
    deleteAll: "Delete All",
    sectionStatement: "Financial statement",
    sectionSettings: "Settings",
    code: "Code",
    descriptionAr: "Arabic description",
    descriptionEn: "English description",
    statementType: "Statement type",
    statementActive: "Statement is active",
    required: "Required",
    newStatement: "New statement",
    searchTitle: "Query financial statements",
    searchPlaceholder: "Search by code or description",
    searchEmpty: "No financial statement matches that search.",
    searchClose: "Close",
    noStatements: "No financial statement has been defined yet.",
    loadError: "Could not load financial statements.",
    saveError: "Could not save the financial statement.",
    saved: "Financial statement saved.",
    created: "Financial statement created.",
    deleted: "Financial statement deleted.",
    deleteTitle: "Delete financial statement",
    deleteConfirm:
      "This deletes the financial statement and everything beneath it. Continue?",
    deleteCancel: "Cancel",
    unsavedTitle: "Not saved yet",
    structurePending: "The statement structure — lines and accounts — is built in the next step.",
    inactive: "Inactive",
      lines: "Statement lines", addLine: "Add Line", addChild: "Add Child", editLine: "Edit",
    deleteLine: "Delete", moveUp: "Move Up", moveDown: "Move Down", expandAll: "Expand All",
    collapseAll: "Collapse All", lineSearch: "Search", refreshLines: "Refresh", parent: "Parent",
    noParent: "No parent (root line)", lineCode: "Line Code", lineTypeLabel: "Statement Line Type",
    account: "Account", costCenter: "Cost Center", disclosure: "Disclosure / Explanation",
    effectNature: "Effect Nature", balanceMethod: "Balance Calculation Method",
    displaySequence: "Display Sequence", bold: "Bold", printed: "Print",
    ltHeader: "Header", ltAccount: "Account", ltGroup: "Group", ltTotal: "Total",
    ltCalculated: "Calculated", ltSpacer: "Spacer", ltNote: "Note", ltCostCenter: "Cost Center",
    enAdd: "Add", enSubtract: "Subtract", enNoEffect: "No Effect",
    bmAccountBalance: "Account Balance", bmSumOfChildren: "Sum of Children",
    bmDebitMinusCredit: "Debit Minus Credit", bmCreditMinusDebit: "Credit Minus Debit",
    bmNoBalance: "No Balance",
    noLines: "No lines yet.", lineSaved: "Line saved.", lineDeleted: "Line deleted.",
    lineMoved: "Line moved.", deleteSubtreeTitle: "Delete line and its children",
    deleteSubtreeConfirm: "This line has children. All of them will be deleted. Continue?",
    selectStatementFirst: "Save the financial statement first to build its lines.",
  },
  financialLinks: {
    title: "Financial Link Settings",
    pageDescription:
      "Map the predefined system accounts to Chart of Accounts accounts, by link level and entity.",
    readOnlyNotice: "You have view-only access. These links cannot be edited.",
    sectionAccounts: "Accounts",
    sectionAssignments: "Companies and assignments",
    linkType: "Account",
    linkLevel: "Link level",
    entity: "Entity",
    account: "Financial account",
    accountNumber: "Account no.",
    active: "Active",
    selectEntity: "Select an entity…",
    notLinked: "Not linked",
    noRows: "No matching rows",
    noAssignments: "No assignments yet",
    search: "Search",
    searchPlaceholder: "Search: all text columns",
    edit: "Edit",
    clearLink: "Clear link",
    refresh: "Refresh",
    linkedOf: "{linked} of {total} linked",
    loadError: "Could not load the links",
    saveError: "Could not save the link",
    soon: "not available",
    levelUnavailable: "This module is not available yet.",
    settingsContext: "Active General Ledger settings",
    openSettings: "Open General Ledger Settings",
    levels: {
      "1": "User",
      "2": "Company",
      "3": "Branch",
      "4": "Warehouse",
      "5": "Item group",
      "6": "Item",
      "7": "Inventory transaction type",
    },
  },
  financialPosition: {
    title: "Financial Position Inquiry",
    subtitle: "As of {date} · {currency}",
    prompt: "Choose a date and press Search to show the financial position",

    asOfDate: "As of date",
    fiscalYear: "Fiscal year",
    fiscalYearHint: "Derived from the position date",
    fiscalYearAuto: "Derived from the date",
    branch: "Branch",
    defaultBranch: "Default branch",

    levelFrom: "Level from",
    levelTo: "Level to",
    levelRangeInvalid: "'Level from' must not be greater than 'level to'",
    parentAccount: "Parent account",
    accountFrom: "Account from",
    accountTo: "Account to",
    accountType: "Account type",
    allAccountTypes: "All types",
    accountCategory: "Account category",
    costCenter: "Cost center",
    costCenterHint: "A journal line belongs to one cost center in full",
    currency: "Currency",
    allCurrencies: "All currencies",
    search: "Search by account number or name",
    searchPlaceholder: "Account number or name",

    showChildren: "Show children",
    showChildrenHint: "When off, a group is shown as one total rather than expanded",
    includeZeroBalances: "Include zero balances",
    includeZeroBalancesHint: "Accounts that net to zero are hidden by default",
    includeClosedAccounts: "Include closed accounts",
    includeClosedAccountsHint: "A closed account keeps its balance and belongs on the sheet",
    includeInactiveAccounts: "Include inactive accounts",
    includeUnpostedMovements: "Include unposted movements",
    includeUnpostedMovementsHint: "A financial position is built from posted movements by default",
    consolidateBranches: "Consolidate branches",
    consolidateBranchesHint: "Combines only the branches you are permitted to see",
    showPercentageOfTotal: "Show percentage of total",

    comparison: "Comparison",
    comparisonNone: "No comparison",
    comparisonPreviousPeriod: "Previous period",
    comparisonPreviousYear: "Previous fiscal year",
    comparisonCustom: "Custom comparative date",
    comparisonDerivedHint: "The comparative date is derived from the position date and fiscal year",
    comparativeDate: "Comparative date",
    comparativeBalance: "Comparative balance",
    changeAmount: "Change",
    changePercentage: "Change %",

    accountNumber: "Account number",
    accountName: "Account name",
    debit: "Debit",
    credit: "Credit",
    balance: "Balance",
    balanceNature: "Balance nature",
    accountLevel: "Account level",
    percentageOfTotalAssets: "% of total assets",
    isGroupAccount: "Group account",
    isPostingAccount: "Posting account",
    status: "Status",
    closed: "Closed",

    natureDebit: "Debit",
    natureCredit: "Credit",
    natureZero: "Zero",
    natureAll: "All",
    natureNormalOnly: "Normal balances",
    natureAbnormalOnly: "Abnormal balances",
    abnormal: "Abnormal",
    abnormalHint: "Expected {expected}, found {actual}. The value is shown exactly as calculated.",
    systemCalculated: "System-calculated",
    systemCalculatedHint: "Derived from revenue and expense accounts that have not been closed yet",

    assets: "Assets",
    liabilities: "Liabilities",
    equity: "Equity",

    expand: "Expand",
    collapse: "Collapse",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    openMovements: "Open Account Movements Inquiry",

    equationTitle: "Financial position equation",
    balanced: "Balanced",
    unbalanced: "Unbalanced",
    unbalancedWarning:
      "Total assets do not equal total liabilities and equity. The difference is shown exactly as calculated, with no automatic adjustment — the entries need investigating.",
    difference: "Difference",
    totalAssets: "Total assets",
    totalCurrentAssets: "Total current assets",
    totalNonCurrentAssets: "Total non-current assets",
    totalLiabilities: "Total liabilities",
    totalCurrentLiabilities: "Total current liabilities",
    totalNonCurrentLiabilities: "Total non-current liabilities",
    totalEquity: "Total equity",
    currentPeriodResult: "Current period profit or loss",
    totalLiabilitiesAndEquity: "Total liabilities and equity",
    displayedAccounts: "Displayed accounts",
    abnormalBalances: "Abnormal balances",

    unpostedWarning: "This position includes unposted movements. It is not the posted accounting position.",
    consolidatedNotice: "Consolidated financial position across branches: {branches}",
  },
  taxTypes: {
    title: "Tax Types",
    createTitle: "New tax type",
    editTitle: "Edit tax type",
    empty: "No tax types yet",
    emptyHint: "Add the first tax type so the rest of the system has something to reference.",
    code: "Code",
    name: "Name",
    nameAr: "Arabic name",
    nameEn: "English name",
    category: "Tax category",
    allCategories: "All categories",
    effectiveFrom: "Effective from",
    effectiveTo: "Effective to",
    effectiveToHint: "Leave empty for open-ended",
    effectivePeriod: "Effective period",
    status: "Status",
    accounts: "Accounts (debit / credit)",
    debitAccount: "Debit account",
    creditAccount: "Credit account",
    descriptionAr: "Arabic description",
    descriptionEn: "English description",
    notes: "Notes",
    sortOrder: "Sort order",
    isActive: "Active",
    isActiveHint: "Inactive taxes cannot be chosen on new transactions",
    allowManualRateOverride: "Allow manual rate override",
    allowManualRateOverrideHint: "A user may enter a different rate on a document",
    isRecoverable: "Recoverable",
    isRecoverableHint: "Can be reclaimed from the tax authority",
    affectsItemCost: "Affects item cost",
    affectsItemCostHint: "Non-recoverable tax is capitalised into the item cost",
    requiresTaxRegistration: "Requires tax registration",
    requiresTaxRegistrationHint: "The party must carry a tax registration number",
    requiresNationalNumber: "Requires national number",
    requiresNationalNumberHint: "The party must carry a national number",
    outOfWindow: "Outside effective period",
    outOfWindowHint: "Active, but outside its effective window today",
    deleteTitle: "Delete tax type",
    deleteHint: "Deleting is permanent. If this tax has ever been used, deactivate it instead so historical documents stay readable.",
    codeRequired: "A code is required",
    nameRequired: "An English name is required",
    nameArRequired: "An Arabic name is required",
    effectiveFromRequired: "An effective-from date is required",
    effectiveRangeInvalid: "The 'to' date must not be earlier than the 'from' date",
    categories: {
      salesTax: "Sales Tax",
      purchaseTax: "Purchase Tax",
      additionalTax: "Additional Tax",
      specialTax: "Special Tax",
      zeroRated: "Zero Rated",
      exempt: "Exempt",
      nonRecoverable: "Non Recoverable Purchase Tax",
      withholding: "Withholding Tax",
      other: "Other",
    },
  },
  taxRates: {
    title: "Tax Rates",
    createTitle: "New tax rate",
    createHint: "A new rate is added with its own effective date. The previous rate is never edited.",
    editTitle: "Edit tax rate",
    editHint: "Changing a rate changes what documents inside its period are charged.",
    empty: "No tax rates yet",
    emptyHint: "No rate has been entered. Nothing can be taxed until a rate is in force.",

    taxType: "Tax type",
    taxTypeRequired: "Select a tax type",
    selectTaxType: "Select a tax type…",
    searchTaxTypes: "Search tax types",
    noTaxTypes: "No active tax types",
    taxTypeInactive: "Tax type inactive",
    taxTypeInactiveHint: "The rate is in force but its tax type is switched off, so it cannot be chosen on documents",

    percentage: "Rate",
    percentageHint: "A percentage: 16 means 16%",
    percentageRequired: "A rate is required",
    percentageNegative: "The rate cannot be negative",
    percentageTooLarge: "The rate cannot exceed 100%",

    effectiveFrom: "Effective from",
    effectiveFromHint: "The first day this rate applies",
    effectiveFromRequired: "An effective-from date is required",
    effectiveTo: "Effective to",
    effectiveToHint: "Leave empty for a rate that runs until further notice",
    effectiveRangeInvalid: "The 'to' date must not be earlier than the 'from' date",
    effectivePeriod: "Effective period",
    openEnded: "Open",

    rateStatus: "Rate status",
    allStatuses: "All statuses",
    statusCurrent: "Current",
    statusFuture: "Future",
    statusExpired: "Expired",
    statusInactive: "Inactive",

    referenceNumber: "Reference",
    referenceNumberHint: "The circular or reference number issued by the tax authority",
    decisionNumber: "Decision no.",
    legalReference: "Legal reference",
    legalReferenceHint: "The article or regulation this rate rests on",
    sortOrder: "Sort order",
    descriptionAr: "Arabic description",
    descriptionEn: "English description",
    notes: "Notes",
    isActive: "Active",
    isActiveHint: "An inactive rate applies to nothing, even to documents dated inside its period",

    lockedInUse:
      "This rate has valued a document, so its percentage, dates and tax type can no longer be changed. Add a new effective-dated rate instead. The descriptive fields stay editable.",
    lockedExpired:
      "This rate's period has ended, so its percentage, dates and tax type can no longer be changed. Add a new effective-dated rate instead. The descriptive fields stay editable.",

    history: "Rate history",
    historyTitle: "Tax rate history",
    historyEmpty: "No rates recorded for this tax",
    historyView: "History view",
    tableView: "Table view",
    timelineView: "Timeline view",
    createdBy: "Created by",
    closedBy: "Closed by",

    close: "Close rate",
    closeTitle: "Close an open-ended rate",
    closeHint: "Set the last day this rate applies.",
    closeDateHint: "The last day the rate applies, inclusive",
    closeBeforeStart: "The end date cannot be earlier than the rate's start date",
    closedOk: "Rate closed",
    closePredecessorTitle: "An open-ended rate is already in force",
    closePredecessorConfirm: "Close the previous rate and add this one",

    deleteTitle: "Delete tax rate",
    deleteHint:
      "Deleting is permanent. If this rate has valued any document, deactivate it instead so posted figures stay explicable.",
  },
  customerReceivables: {
    title: "Customer Receivables Summary",
    statementTitle: "Customer Statement",
    date: "Date",
    asOfDate: "As of Date",
    currentBalance: "Current Balance",
    balanceAsOfDate: "Balance as of Date",
    asOfHint: "Balances are read from posted ledger movements up to the selected date.",
    currentHint: "Balances as they stand right now.",
    customer: "Customer",
    category: "Customer Category",
    salesRepresentative: "Sales Representative",
    controlAccount: "Control Account",
    city: "City",
    country: "Country",
    creditLimit: "Credit Limit",
    includeZeroBalances: "Show zero balances",
    balance: "Balance",
    balanceNature: "Nature",
    natureDebit: "Debit",
    natureCredit: "Credit",
    natureBalanced: "Balanced",
    totalDebit: "Total Debit",
    totalCredit: "Total Credit",
    netReceivables: "Net Receivables",
    customerCount: "Customers",
    baseCurrency: "Company Base Currency",
    totalsCoverAll: "Totals cover the entire filtered result.",
    openStatement: "Open statement",
    selectCustomer: "Select a customer to view their statement.",
    dateFrom: "Date From",
    dateTo: "Date To",
    openingBalance: "Opening Balance",
    closingBalance: "Closing Balance",
    runningBalance: "Running Balance",
    movementCount: "Movements",
    voucherType: "Voucher Type",
    voucherNumber: "Voucher Number",
    reference: "Reference",
    description: "Description",
    debit: "Debit",
    credit: "Credit",
    documentCurrency: "Document Currency",
    noDrillDown: "No screen is linked to this document.",
    allCustomers: "All customers",
    all: "All",
  },
  customerCostCenterReceivables: {
    title: "Customer Receivables by Cost Center",
    costCenter: "Cost Center",
    costCenterCode: "Cost Center Code",
    noCostCenter: "No Cost Center",
    costCenterCount: "Cost Centers",
    includeChildCostCenters: "Include child cost centers",
    onlyWithoutCostCenter: "Only movements without a cost center",
    unassigned: "No Cost Center",
    unassignedNote: "Movements posted without a cost center — shown so the grouped total still reconciles to the customer balance.",
    ledgerOnlyHint: "Balances always come from posted ledger movements, because the stored customer balances carry no cost-center split.",
    currentScope: "Up to now",
    asOfScope: "As of date",
  },
  customerCurrencyStatement: {
    title: "Customer Statement by Currency",
    currency: "Currency",
    selectCustomerFirst: "Select a customer, then a currency, to view the statement.",
    selectCurrency: "Select a currency",
    noLedgerCurrencies: "This customer has no posted movements in any currency.",
    exchangeRate: "Exchange Rate",
    transactionCurrencyNote: "All amounts are in the selected transaction currency, exactly as posted — nothing is converted.",
    baseCurrencyBadge: "Company currency",
    movementsInCurrency: "movements",
  },
  customerTransactions: {
    title: "Daily Transactions",
    voucherDate: "Voucher Date",
    customerNumber: "Customer Number",
    accountNumber: "Account Number",
    accountName: "Account Name",
    currency: "Currency",
    rate: "Exchange Rate",
    currencyDebit: "Debit (Currency)",
    currencyCredit: "Credit (Currency)",
    paymentMethod: "Payment Method",
    notes: "Notes",
    branch: "Branch",
    year: "Year",
    netMovement: "Net Movement",
    mixedCurrencies: "The result spans more than one currency, so the currency columns are not totalled.",
    noPaymentMethod: "carries no payment method",
    reversed: "Reversed",
    mappingNote: "Debit and Credit are in company currency; Debit (Currency) and Credit (Currency) are in the transaction currency.",
  },
  serviceVouchers: {
    title: "Service Voucher Transactions",
    voucherDate: "Voucher Date",
    paymentMethod: "Payment Method",
    partyCode: "Customer / Supplier Code",
    partyName: "Customer / Supplier Name",
    subtotal: "Subtotal",
    discount: "Discount",
    tax: "Tax",
    finalTotal: "Final Total",
    currency: "Currency",
    supplierInvoiceNumber: "Supplier Invoice Number",
    project: "Project",
    notes: "Notes",
    branch: "Branch",
    userName: "User Name",
    year: "Year",
    status: "Status",
    documents: "Documents",
    documentType: "Voucher Type",
    totalsByCurrency: "Totals are grouped by currency — there is no single total across currencies.",
    returnNote: "Sales returns are shown negative because they reverse a sale.",
    noProjectModel: "This system has no project model, so the Project column is empty.",
    noPurchaseModule: "Service purchase documents are not implemented in Business OS yet, so none appear here.",
    openDocument: "Open document",
  },
  customerItems: {
    title: "Customer Item Transactions",
    voucherDate: "Voucher Date",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    itemCode: "Item / Service Code",
    itemName: "Item / Service Name",
    itemType: "Item Type",
    uom: "UOM",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    discount: "Discount",
    netUnitPrice: "Net Unit Price",
    tax: "Tax",
    lineTotal: "Line Total",
    currency: "Currency",
    rate: "Exchange Rate",
    branch: "Branch",
    costCenter: "Cost Center",
    salesRep: "Sales Representative",
    notes: "Notes",
    nature: "Transaction Nature",
    sale: "Sale",
    returned: "Return",
    customers: "Customers",
    documents: "Documents",
    lines: "Lines",
    netSales: "Net Sales",
    totalsByCurrency: "Totals are grouped by currency — there is no single total across currencies.",
    returnNote: "Returns show negative quantity and amount; the unit price stays the price it sold at.",
    servicesOnly: "This system has no item or product master, so every transaction is a Service.",
    postedOnly: "Only posted documents are included — drafts, cancelled and reversed documents are not actual sales.",
    allCustomers: "All customers",
    openDocument: "Open document",
  },
  paidUnpaid: {
    title: "Paid & Unpaid Invoices",
    invoiceDate: "Invoice Date",
    invoiceNumber: "Invoice Number",
    invoiceType: "Invoice Type",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    currency: "Currency",
    invoiceTotal: "Invoice Total",
    paid: "Paid Amount",
    remaining: "Remaining Amount",
    status: "Payment Status",
    dueDate: "Due Date",
    daysOverdue: "Days Overdue",
    paymentMethod: "Payment Method",
    settledBy: "Settled By",
    salesRep: "Sales Representative",
    costCenter: "Cost Center",
    branch: "Branch",
    reference: "Reference",
    notes: "Notes",
    statusPaid: "Paid",
    statusPartial: "Partially Paid",
    statusUnpaid: "Unpaid",
    multiple: "Multiple",
    invoices: "Invoices",
    overSettled: "Over-settled",
    overSettledWarning: "Invoices settled beyond their total — the integrity issue is shown, not repaired here.",
    totalsByCurrency: "Totals are grouped by currency — there is no single total across currencies.",
    noDueDateNote: "This system records no invoice due date, so Due Date and Days Overdue are empty.",
    postedOnly: "Only posted invoices are included — drafts, cancelled and reversed invoices are not collectible receivables.",
    settlementDetails: "Settlement Details",
    settlementDate: "Settlement Date",
    settlementNumber: "Settlement Number",
    receiptNumber: "Receipt Number",
    amountApplied: "Amount Applied",
    settlementStatus: "Status",
    createdBy: "Created By",
    active: "Active",
    cancelled: "Cancelled",
    cancelledNote: "Cancelled settlements are shown for the record and do not count toward the paid amount.",
    noSettlements: "This invoice has no settlements.",
    close: "Close",
    allCustomers: "All customers",
    openInvoice: "Open invoice",
  },
  customerAging: {
    title: "Customer Receivables Aging",
    asOfDate: "As of Date",
    basis: "Aging Basis",
    basisDue: "Due Date",
    basisDocument: "Invoice Date",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    salesRep: "Sales Representative",
    accountNumber: "Control Account Number",
    accountName: "Control Account",
    notDue: "Not Due",
    unclassified: "Unclassified",
    totalOutstanding: "Total Outstanding",
    unappliedCredits: "Unapplied Credits",
    creditLimit: "Credit Limit",
    maxAge: "Maximum Age Days",
    customers: "Customers",
    totalOverdue: "Total Overdue",
    overdueOnly: "Overdue only",
    minimumOutstanding: "Minimum Outstanding",
    country: "Country",
    city: "City",
    agingDetails: "Aging Details",
    documentDate: "Document Date",
    dueDate: "Due Date",
    voucherNumber: "Voucher Number",
    documentType: "Voucher Type",
    reference: "Reference",
    originalAmount: "Original Amount",
    settledAmount: "Settled as of Date",
    remainingAmount: "Remaining as of Date",
    ageDays: "Age Days",
    agingPeriod: "Aging Period",
    currency: "Currency",
    rate: "Exchange Rate",
    branch: "Branch",
    fiscalYear: "Year",
    noOpenItems: "This customer has no open receivables as of the selected date.",
    detailsReconcile: "The detail remaining amounts sum to the customer's Total Outstanding for the same date and basis.",
    close: "Close",
    allCustomers: "All customers",
    openItems: "Open Items",
    configurePeriods: "Periods are read from the Aging Periods settings screen only — they cannot be edited here.",
    noCostCentreFilter: "Remaining amounts cannot be allocated to cost centres, because receivable lines carry none — so there is no cost-centre filter here.",
  },
  detailedAging: {
    title: "Detailed Receivables Aging Report",
    documentDate: "Document Date",
    documentDateFrom: "Document Date From",
    documentDateTo: "Document Date To",
    originalBase: "Original Base Amount",
    settledBase: "Settled as of Date",
    remainingBase: "Remaining as of Date",
    originalTransaction: "Original Transaction Amount",
    ageDays: "Age Days",
    agingPeriod: "Aging Period",
    dueStatus: "Due Status",
    statusNotDue: "Not Due",
    statusOverdue: "Overdue",
    statusPaid: "Paid",
    unclassified: "Unclassified",
    openItems: "Open Items",
    customers: "Customers",
    totalOverdue: "Total Overdue",
    unappliedCredits: "Unapplied Credits",
    showFullySettled: "Show fully settled",
    minimumRemaining: "Minimum Remaining",
    maximumRemaining: "Maximum Remaining",
    settlementDetails: "Settlement Details",
    settlementDate: "Settlement Date",
    settlementNumber: "Settlement Number",
    receiptNumber: "Receipt Number",
    amountApplied: "Amount Applied",
    paymentMethod: "Payment Method",
    createdBy: "Created By",
    cancelledLater: "Cancelled later",
    noSettlements: "This item has no settlements effective as of the selected date.",
    settlementAsOfNote: "Settlements are shown as they stood on the selected date; one cancelled after it still counts here.",
    close: "Close",
    summaryLink: "This detailed report reconciles to Customer Receivables Aging for the same date and basis.",
  },
  receiptsReportsExtra: {
    title: {
      openingBalances: "Customer Opening Balances",
      trialBalance: "Short Trial Balance - Customers & Suppliers",
      withoutActivity: "Customers Without Activity",
      byCurrency: "Total Customers by Currency",
      byAccount: "Customer Account - Accounts",
      receiptsSummary: "Receipts Report",
      receiptOrders: "Receipt / Payment Orders",
      collector: "Collector Transactions",
      deferredRevenue: "Due Deferred Revenue",
      invoicesByType: "Service Sales Invoices by Type",
      consolidated: "Active Customers Consolidated Balance",
      infoChanges: "Customer / Supplier Information Changes",
      parentAccount: "Parent Customer Account",
    },
    note: {
      openingBalances: "The opening balance is computed from effective ledger movements before the selected date, never from the current balance.",
      trialBalance: "Customers and suppliers appear on one report without being merged; every row carries its own party type.",
      withoutActivity: "\"Activity\" means an effective accounting movement on the customer's account, not a data-edit timestamp.",
      byCurrency: "One row per customer and currency. Amounts are in the currency they were posted in, and currencies are never added together.",
      byAccount: "One row per accounting movement, with the account it was posted to. This is not the cost-centre dimension.",
      receiptsSummary: "A receipt voucher is money actually received. A receipt order is not a collection and does not appear here.",
      receiptOrders: "Receipt Orders only; Payment Orders are not implemented in this system.",
      collector: "The collector is read from the receipt order and is never inferred from the sales representative. Order and receipt totals are kept apart.",
      deferredRevenue: "A read-only view of the approved recognition schedule. The contract period is the basis, not the collection date.",
      invoicesByType: "Service type is recorded on invoice lines, so an invoice spanning several types shows Multiple. Totals are as posted.",
      consolidated: "Consolidation uses the stored parent-customer field only; it is never inferred from category, control account or similar names.",
      infoChanges: "From the audit log only; no change is reconstructed beyond what the log stores.",
      parentAccount: "Children are read from the stored parent-customer field only, one direct level deep.",
    },
    empty: {
      generic: "No data.",
      openingBalances: "No opening balances.",
      withoutActivity: "Every customer had activity during the period.",
      collector: "No collector transactions.",
    },
    partyTypeLabel: (v) => v,
    natureLabel: (v) => (v === "Debit" ? "Debit" : v === "Credit" ? "Credit" : "Zero"),
    statusLabel: (v) => v,
    documentTypeLabel: (v) => (v === "ReceiptVoucher" ? "Receipt Voucher" : "Receipt Order"),
    recognitionStatus: (v) => ({ Due: "Due", NotYetDue: "Not Yet Due", Recognised: "Recognised" })[v] ?? v,
    auditAction: (v) => v,
    dateFrom: "Date From", dateTo: "Date To", asOf: "As of Date", openingBefore: "Opening balance before",
    partyType: "Party Type", partyCode: "Party Code", partyName: "Party Name", category: "Category",
    controlAccount: "Control Account", currency: "Currency", lastActivity: "Last Activity", never: "Never",
    openingDebit: "Opening Debit", openingCredit: "Opening Credit",
    periodDebit: "Period Debit", periodCredit: "Period Credit",
    closingDebit: "Closing Debit", closingCredit: "Closing Credit", nature: "Balance Nature", net: "Net",
    parties: "Parties", customers: "Customers", customer: "Customer", supplier: "Supplier",
    bothTypes: "All", rows: "Rows", date: "Date", voucherType: "Voucher Type",
    voucherNumber: "Voucher Number", accountNumber: "Account Number", accountName: "Account Name",
    description: "Description", debit: "Debit", credit: "Credit", balance: "Balance",
    runningBalance: "Running Balance", openingBalance: "Opening Balance",
    closingBalance: "Closing Balance", accountSummary: "Summary by Account",
    costCenter: "Cost Center", branch: "Branch", customerRequired: "Customer is required for this report.",
    receiptNumber: "Receipt Number", amount: "Amount", paymentMethod: "Payment Method",
    destination: "Cashbox / Bank", collector: "Collector", salesRep: "Sales Representative",
    reference: "Reference", status: "Status", createdBy: "Created By", multiple: "Multiple",
    receipts: "Receipts", total: "Total",
    orderNumber: "Order Number", requested: "Requested Amount", collected: "Collected",
    remaining: "Remaining", expectedDate: "Expected Date", orders: "Orders", notes: "Notes",
    documentType: "Document Type", documentNumber: "Document Number", receiptVoucher: "Receipt Voucher",
    receiptOrder: "Receipt Order", bothDocuments: "All", documents: "Documents",
    collectors: "Collectors", collectedReceipts: "Total Collected Receipts",
    orderedAmount: "Total Ordered Amount",
    recognitionDate: "Recognition Date", invoiceNumber: "Invoice Number", contractPeriod: "Contract Period",
    periodNumber: "Period", scheduled: "Scheduled", recognised: "Recognised", due: "Due",
    notYetDue: "Not Yet Due", schedules: "Schedules", periods: "Periods", allStatuses: "All",
    serviceType: "Service Type", saleNature: "Sale Nature", subtotal: "Subtotal", discount: "Discount",
    tax: "Tax", netTotal: "Net Total", invoices: "Invoices",
    byTypeSummary: "Summary by Service Type",
    isParent: "Consolidation", parent: "Parent", child: "Child", standalone: "Standalone",
    parents: "Parents", ownBalance: "Own Balance", childrenBalance: "Children Balance",
    consolidated: "Consolidated Balance", children: "Children", parentCustomer: "Parent Customer",
    selectParent: "Choose the parent customer", parentRequired: "Parent customer is required for this report.",
    role: "Role", movements: "Movements",
    changedAt: "Changed At", field: "Field", oldValue: "Old Value",
    newValue: "New Value", action: "Action", changedBy: "Changed By",
    fieldChanges: "Field Changes", auditEvents: "Audit Events",
  },
  customerMasterReports: {
    title: (kind) =>
      kind === "customers-exceeded-credit-limit" ? "Customers Exceeded Credit Limit"
      : kind === "total-customers" ? "Total Customers"
      : kind === "customer-information" ? "Customer Information"
      : kind === "customer-blacklist" ? "Customer Blacklist"
      : "Customer Credit Limit",
    note: (kind) =>
      kind === "customers-exceeded-credit-limit"
        ? "A customer appears only when their debit balance exceeds the credit limit. A credit balance is never treated as exceeded, however large."
      : kind === "customer-blacklist"
        ? "This report uses the stored blacklist flag only. It is never inferred from status, balance or notes."
      : "The current balance comes from the same approved source Customer Receivables Summary reads.",
    noRows: (kind) =>
      kind === "customers-exceeded-credit-limit" ? "No customer has exceeded their credit limit."
      : kind === "customer-blacklist" ? "No customer is blacklisted."
      : "No data.",
    natureLabel: (nature) =>
      nature === "Debit" ? "Debit" : nature === "Credit" ? "Credit" : "Zero",
    code: "Customer Code", name: "Customer Name", nameEn: "English Name", category: "Category",
    status: "Status", active: "Active", inactive: "Inactive", customer: "Customer",
    salesRep: "Sales Representative", controlAccount: "Control Account",
    creditLimit: "Credit Limit", balance: "Current Balance", nature: "Balance Nature",
    exceeded: "Exceeded Amount", utilisation: "Credit Utilization %",
    available: "Available Credit", blacklistReason: "Blacklist Reason",
    city: "City", country: "Country", taxNumber: "Tax Number",
    registration: "Commercial Registration", phone: "Phone", mobile: "Mobile",
    email: "Email", address: "Address", contactPerson: "Contact Person",
    paymentMethod: "Payment Method", parentCustomer: "Parent Customer",
    customers: "Customers", totalLimit: "Total Credit Limit",
    totalBalance: "Total Balance", totalExceeded: "Total Exceeded",
  },
  agingGrouped: {
    title: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "Aging by Currency" : "Aging by Category",
    groupHeader: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "Currency" : "Category",
    unassigned: (dimension: "Category" | "Currency") =>
      dimension === "Currency" ? "No Currency" : "No Category",
    asOf: "As of Date",
    customers: "Customers",
    openItems: "Open Items",
    notDue: "Not Due",
    unclassified: "Unclassified",
    total: "Total Outstanding",
    grandTotal: "Grand Total",
    groups: "Groups",
    noRows: "No outstanding items.",
    basisNote:
      "Amounts are in company currency, the only basis on which the aging engine computes a remaining figure. Columns come from the configured aging periods, and nothing is converted at today's rate.",
  },
  reportsReceipts: {
    currencyLocalTitle: "Customer Account - Currency / Local",
    currencyLocalNote:
      "Transaction-currency and company-currency amounts are shown side by side, each on its own basis, at the rate the movement was posted at. Nothing is converted at today's rate.",
    transactionBasis: "Transaction currency",
    localBasis: "Company currency",
  },
  customerAccountCostCenter: {
    title: "Customer Account by Cost Center",
    date: "Date",
    voucherType: "Voucher Type",
    voucherNumber: "Voucher Number",
    reference: "Reference",
    description: "Description",
    costCenterCode: "Cost Center Code",
    costCenterName: "Cost Center",
    debit: "Debit",
    credit: "Credit",
    runningBalance: "Running Balance",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    currencyDebit: "Currency Debit",
    currencyCredit: "Currency Credit",
    branch: "Branch",
    customer: "Customer",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    customerCategory: "Customer Category",
    controlAccount: "Control Account",
    dateFrom: "Date From",
    dateTo: "Date To",
    openingBalance: "Opening Balance",
    totalDebit: "Total Debit",
    totalCredit: "Total Credit",
    closingBalance: "Closing Balance",
    costCenterFilter: "Cost Center",
    allCostCenters: "All Cost Centers",
    noCostCenter: "No Cost Center",
    summary: "Summary by Cost Center",
    movements: "Movements",
    coverage: "Movements with a cost center",
    noRows: "No movements.",
    selectCustomer: "Choose a customer to see their account.",
    customerRequired: "Customer is required for this inquiry.",
    grainNote:
      "One row per effective accounting movement, with the cost center it was posted with. Movements without a cost center are shown as they are and never attributed to one.",
    runningBalanceNote:
      "The running balance is computed on the server in accounting order and does not change with display sorting.",
    openDocument: "Open document",
  },
  voucherTransactionsInquiry: {
    title: "Voucher Transactions",
    voucherDate: "Voucher Date",
    voucherNumber: "Voucher Number",
    voucherType: "Voucher Type",
    status: "Status",
    accountingEffect: "Accounting Effect",
    partyType: "Party Type",
    partyCode: "Party Code",
    partyName: "Party Name",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    voucherAmount: "Voucher Amount",
    voucherAmountLocal: "Voucher Amount - Company Currency",
    nature: "Voucher Nature",
    paymentMethod: "Payment Method",
    branch: "Branch",
    costCenter: "Cost Center",
    reference: "Reference",
    notes: "Notes",
    createdBy: "Created By",
    createdAt: "Created At",
    reversalReference: "Reversal Reference",
    counterparty: "Counterparty",
    customer: "Customer",
    allTypes: "All Types",
    natureDebit: "Debit",
    natureCredit: "Credit",
    natureNeutral: "Neutral",
    effectPosted: "Posted",
    effectNotPosted: "Not Posted",
    effectReversed: "Reversed",
    effectNone: "No Accounting Entry",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusRejected: "Rejected",
    statusCancelled: "Cancelled",
    statusReversed: "Reversed",
    multiple: "Multiple",
    voucherCount: "Total Voucher Count",
    postedCount: "Posted",
    unpostedCount: "Not Posted",
    reversedCount: "Reversed",
    partyCount: "Parties",
    totalDebit: "Total Debit-Nature Vouchers",
    totalCredit: "Total Credit-Nature Vouchers",
    totalNeutral: "Total Neutral Vouchers",
    effectiveDebit: "Effective Debit",
    effectiveCredit: "Effective Credit",
    effectiveNet: "Effective Net - Company Currency",
    historicalNet: "Historical Net - Company Currency",
    countsByType: "Voucher Count by Type",
    noRows: "No vouchers.",
    documentGrainNote:
      "One row per voucher/document — not per accounting line. Accounting lines appear in Daily Transactions.",
    reversalNote:
      "Reversed vouchers stay visible historically and are excluded from the effective totals.",
    journalUnavailable: "No approved route exists to view a specific voucher's journal entry.",
    details: "Voucher Details",
    openDocument: "Open document",
    close: "Close",
  },
  outstandingInvoices: {
    title: "Outstanding Customer Invoices",
    invoiceDate: "Invoice Date",
    invoiceNumber: "Invoice Number",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    customerCategory: "Customer Category",
    currency: "Currency",
    invoiceTotal: "Invoice Total",
    settledAmount: "Settled Amount",
    remainingAmount: "Remaining Amount",
    paymentStatus: "Payment Status",
    dueDate: "Due Date",
    paymentMethod: "Payment Method",
    salesRepresentative: "Sales Representative",
    branch: "Branch",
    reference: "Reference",
    notes: "Notes",
    asOf: "As of Date",
    allOutstanding: "All Outstanding",
    unpaid: "Unpaid",
    partiallyPaid: "Partially Paid",
    invoiceCount: "Outstanding Invoices",
    unpaidCount: "Completely Unpaid",
    partiallyCount: "Partially Paid",
    customerCount: "Customers Represented",
    totalInvoice: "Total Invoice Amount",
    totalSettled: "Total Settled",
    totalRemaining: "Total Remaining",
    totalRemainingLocal: "Total Remaining - Company Currency",
    minRemaining: "Minimum Remaining",
    maxRemaining: "Maximum Remaining",
    noRows: "No outstanding invoices.",
    flatListNote:
      "One row per outstanding invoice, across all customers together. Fully paid invoices are not included.",
    dueDateNote:
      "This system records no invoice due date, so \"outstanding\" means an uncollected balance.",
    settlementDetails: "Settlement Details",
    openInvoice: "Open invoice",
  },
  receiptsWithoutInvoices: {
    title: "Receipt Vouchers Without Invoices",
    receiptDate: "Receipt Date",
    receiptNumber: "Receipt Voucher Number",
    customerCode: "Customer Code",
    customerName: "Customer Name",
    customerCategory: "Customer Category",
    currency: "Currency",
    receiptAmount: "Receipt Amount",
    allocatedAmount: "Allocated Amount",
    unallocatedAmount: "Unallocated Amount",
    matchingStatus: "Matching Status",
    paymentMethod: "Payment Method",
    destination: "Cashbox / Bank",
    reference: "Reference",
    branch: "Branch",
    costCenter: "Cost Center",
    collector: "Collector",
    salesRepresentative: "Sales Representative",
    notes: "Notes",
    createdBy: "Created By",
    createdAt: "Created At",
    unallocated: "Unallocated",
    partiallyAllocated: "Partially Allocated",
    fullyAllocated: "Fully Allocated",
    multiple: "Multiple",
    showFullyAllocated: "Show Fully Allocated",
    minUnallocated: "Minimum Unallocated",
    maxUnallocated: "Maximum Unallocated",
    receiptCount: "Receipt Count",
    unallocatedCount: "Fully Unallocated",
    partiallyCount: "Partially Allocated",
    fullyCount: "Fully Allocated",
    totalReceipt: "Total Receipt Amount",
    totalAllocated: "Total Allocated",
    totalUnallocated: "Total Unallocated",
    totalUnallocatedLocal: "Total Unallocated - Company Currency",
    overAllocated: "Over-Allocated",
    overAllocatedWarning: "Allocations exceed the receipt - a data integrity problem.",
    noRows: "No receipt vouchers with an unallocated amount.",
    note: "Allocated amounts come from ACTIVE account settlements only; cancelled settlements do not count.",
    readOnlyNote:
      "A read-only inquiry - it matches no receipt to an invoice and creates no settlement. Matching belongs to the Account Settlement screen.",
    details: "Receipt Details",
    payments: "Payment Splits",
    allocations: "Allocations",
    settlementDate: "Settlement Date",
    settlementNumber: "Settlement Number",
    documentNumber: "Document Number",
    documentType: "Document Type",
    documentDate: "Document Date",
    status: "Status",
    active: "Active",
    cancelled: "Cancelled",
    cancellationReason: "Cancellation Reason",
    noAllocations: "This receipt has no allocations.",
    line: "Line",
    amount: "Amount",
    chequeNumber: "Cheque Number",
    chequeDueDate: "Cheque Due Date",
    fromReceiptOrder: "From Receipt Order",
    close: "Close",
  },
  missingVoucherSequences: {
    title: "Missing Receipt Voucher Sequences",
    missingNumber: "Missing Voucher Number",
    sequence: "Sequence",
    year: "Year",
    missingFrom: "Missing From",
    missingTo: "Missing To",
    missingCount: "Missing Count",
    gapCount: "Gap Count",
    firstMissing: "First Missing Number",
    lastMissing: "Last Missing Number",
    previousNumber: "Previous Existing Number",
    nextNumber: "Next Existing Number",
    previousDate: "Previous Voucher Date",
    nextDate: "Next Voucher Date",
    previousStatus: "Previous Voucher Status",
    nextStatus: "Next Voucher Status",
    gapSize: "Gap Size",
    scopes: "Numbering Scopes",
    resetPolicy: "Reset Policy",
    startNumber: "Start Number",
    lastIssued: "Last Issued Number",
    issuedCount: "Issued Numbers",
    duplicates: "Duplicate Numbers",
    duplicateSerial: "Number",
    duplicateVouchers: "Vouchers",
    noGaps: "No missing numbers in the Receipt Voucher sequence.",
    note: "Cancelled, reversed and deleted vouchers consumed their numbers and are not missing.",
    auditOnly: "This is a read-only audit screen — it creates no voucher, repairs no numbering and resets no sequence.",
    viewDetails: "View Details",
    groupedGaps: "Grouped Gaps",
    close: "Close",
    numberFrom: "Missing Number From",
    numberTo: "Missing Number To",
  },
  costCenterAccount: {
    title: "Cost Center Account Inquiry",
    costCenter: "Cost center",
    costCenterCode: "Cost center code",
    costCenterName: "Cost center name",
    includeChildCostCenters: "Include child cost centers",
    allocationPercentage: "Allocation %",
    allocatedDebit: "Allocated debit",
    allocatedCredit: "Allocated credit",
    allocatedLocalDebit: "Allocated local debit",
    allocatedLocalCredit: "Allocated local credit",
    totalAllocatedDebit: "Total allocated debit",
    totalAllocatedCredit: "Total allocated credit",
    selectPrompt: "Choose a cost center and an account, then press Search",
    rolledUp: "These figures roll up several cost centers",
    wholeLineNote:
      "A journal line is charged to one cost center in full, so the allocation is always 100% in the current data model",
  },
  accountMovements: {
    title: "Account Movements Inquiry",
    movementDate: "Movement date",
    sourceReference: "Source reference",
    runningBalance: "Running balance",
    balanceType: "Balance type",
    actions: "Actions",
    open: "Open",
    openingBalance: "Opening balance",
    closingBalance: "Closing balance",
    includeOpeningBalance: "Include opening balance",
    netMovement: "Net movement",
    advancedFilters: "More filters",
    partyType: "Subledger type",
    party: "Subledger",
    customer: "Customer",
    supplier: "Supplier",
    employee: "Employee",
    voucherKind: "Voucher type",
    postingStatusPosted: "Posted only",
    postingStatusUnposted: "Unposted only",
    postingStatusAll: "All",
    zero: "Zero",
    runningBalanceUnavailable:
      "The running balance is shown only in movement order, for posted movements",
    unpostedNote: "Unposted movements are listed but excluded from the balances",
    balancesNote: "Opening and closing balances cover posted movements only",
    modules: {
      manual: "Manual Journal",
      receipts: "Receipts",
      payments: "Payments",
      cashBanks: "Cash & Banks",
      payroll: "Payroll",
      fixedAssets: "Fixed Assets",
      sales: "Sales",
      purchases: "Purchases",
      openingBalance: "Opening Balance",
      closing: "Closing",
      system: "System Generated",
    },
  },
  /**
   * المخزون والمستودعات › الاستعلامات — the words the twenty-four screens share.
   *
   * One vocabulary for the module, so «المتبقية» never means outstanding-to-receive on
   * one screen and outstanding-to-invoice on the next.
   */
  inventoryInquiries: {
    itemTypes: { "1": "Stock material", "2": "Non-stock material", "3": "Service" },
    register: {
      title: "Item register", breadcrumb: "Inventory / Inquiries",
      searchPlaceholder: "Search by code, name or barcode…",
      expandAll: "Expand all", collapseAll: "Collapse all", columnChooser: "Choose columns",
      clear: "Clear", summary: "{items} item(s) · {groups} group(s)",
      itemsCount: "{direct} direct · {all} in total",
      loadError: "Could not load the register.", empty: "Nothing matches.",
      notStockNote: "This is a card register, not a stock balance. Quantities come from the ledger alone.",
      level: "Level", code: "Item code", name: "Item name", nameAr: "Arabic name",
      group: "Group", class1: "Classification 1", class2: "Classification 2", class3: "Classification 3",
      unit: "UOM", type: "Item type", country: "Country of origin", tax: "Purchase tax",
      warehouse: "Default warehouse", barcode: "Barcode", price: "Consumer price",
      supplier: "Preferred supplier", min: "Minimum stock", reorder: "Reorder level",
      conversions: "Unit conversions", movement: "Has movement", status: "Status", open: "Open",
      active: "Active", inactive: "Inactive", yes: "Yes", no: "No", neverMoved: "Never moved",
    },
    noRows: "No records match these filters.",
    noItemCards: "No item cards match these filters.",
    unspecified: "Unspecified",
    cards: "Cards",
    conversions: "Unit conversions",
    hasMovements: "Has movements",
    incomplete: "Incomplete cards",
    incompleteYes: "Incomplete",
    incompleteOnly: "Incomplete only",
    neverMovedOnly: "Never moved",
    activeOnly: "Active only",
    inactiveOnly: "Inactive only",
    withGroup: "With group",
    withCategory: "With category",
    withCountry: "With origin",
    withPurchaseTax: "With purchase tax",
    dateFrom: "From",
    dateTo: "To",
    asOfDate: "As of date",
    item: "Item",
    itemCode: "Item code",
    itemName: "Item name",
    warehouse: "Warehouse",
    toWarehouse: "Destination warehouse",
    group: "Group",
    category: "Category",
    supplier: "Supplier",
    costCenter: "Cost centre",
    branch: "Branch",
    reason: "Reason",
    unit: "Unit",
    search: "Search",
    documentNumber: "Document number",
    documentType: "Movement type",
    document: "Document",
    date: "Date",
    status: "Status",
    direction: "Direction",
    directionIn: "In",
    directionOut: "Out",
    quantity: "Quantity",
    quantityIn: "Qty in",
    quantityOut: "Qty out",
    valueIn: "Value in",
    valueOut: "Value out",
    runningQuantity: "Running qty",
    runningValue: "Running value",
    openingQuantity: "Opening quantity",
    closingQuantity: "Closing quantity",
    unitCost: "Unit cost",
    averageCost: "Average cost",
    inventoryValue: "Inventory value",
    available: "Available",
    minimumStock: "Minimum",
    reorderLevel: "Reorder level",
    shortage: "Shortage",
    lastMovement: "Last movement",
    daysSince: "Days since",
    days: "Days",
    orderNumber: "Purchase order",
    orderDate: "Order date",
    expectedDate: "Expected date",
    ordered: "Ordered",
    received: "Received",
    remaining: "Remaining",
    invoiced: "Invoiced",
    uninvoiced: "Uninvoiced",
    uninvoicedValue: "Uninvoiced value",
    returned: "Returned",
    progress: "Progress",
    lastReceipt: "Last receipt",
    receiptNumber: "Receipt",
    receiptDate: "Receipt date",
    receiptValue: "Receipt value",
    comparison: "Comparison",
    difference: "Difference",
    differenceValue: "Difference value",
    invoiceNumber: "Invoice",
    supplierInvoice: "Supplier invoice",
    dueDate: "Due date",
    subTotal: "Subtotal",
    tax: "Tax",
    total: "Total",
    paid: "Paid",
    outstanding: "Outstanding",
    paymentStatus: "Payment status",
    returnNumber: "Return",
    returnValue: "Return value",
    taxReversal: "Tax reversal",
    countNumber: "Count",
    countDate: "Count date",
    systemQuantity: "System quantity",
    countedQuantity: "Counted quantity",
    adjustment: "Adjustment",
    adjustmentStatus: "Adjustment status",
    responsible: "Responsible",
    unadjustedOnly: "Unadjusted only",
    positiveOnly: "Positive only",
    negativeOnly: "Negative only",
    includeDraft: "Include drafts",
    outstandingOnly: "Outstanding only",
    overdueOnly: "Overdue only",
    uninvoicedOnly: "Uninvoiced only",
    balanceSign: "Balance",
    positive: "Positive",
    zero: "Zero",
    negative: "Negative",
    all: "All",
    openSource: "Open source",
    runningQty: "Running balance",
    movements: "Movements",
    summedQuantity: "Summed quantity",
    checkedPairs: "Pairs checked",
    mismatches: "Mismatches",
    mismatchesOnly: "Mismatches only",
    itemsCount: "Items",
    minimumAgeDays: "Older than (days)",
    againstMinimum: "Against minimum",
    screens: {
      itemBalance: "Item balance",
      stockByWarehouse: "Stock by warehouse",
      itemMovements: "Item movements",
      itemLedgerCard: "Item ledger card",
      warehouseMovements: "Warehouse movements",
      stockAsOf: "Stock as of date",
      belowReorder: "Items below reorder level",
      nonMoving: "Non-moving items",
      purchaseOrders: "Purchase orders",
      orderReceipts: "Purchase order receipt follow-up",
      goodsReceipts: "Goods receipts",
      receivedNotInvoiced: "Received not invoiced",
      purchaseInvoices: "Material purchase invoices",
      purchaseReturns: "Material purchase returns",
      stockReceipts: "Stock receipts",
      stockIssues: "Stock issues",
      transfers: "Warehouse transfers",
      adjustments: "Stock adjustments",
      stockCounts: "Stock counts",
      countDifferences: "Count differences",
      itemBySupplier: "Item movement by supplier",
      itemRegister: "Item register",
      itemsByGroup: "Items by group and category",
      stockLedger: "Stock ledger",
      reconciliation: "Stock ledger reconciliation",
    },
  },
  inventoryReports: {
    customer: "العميل",
    purchaseOrder: "أمر الشراء",
    stockCount: "الجرد",
    movementType: "نوع الحركة",
    adjustmentReason: "سبب التسوية",
    storageLocation: "موقع التخزين",
    top: "أعلى",
    topAll: "الكل",
    marginBelow: "الهامش أقل من %",
    valueAbove: "القيمة أعلى من",
    activeOnly: "المواد النشطة فقط",
    costsHidden:
      "التكلفة والقيمة والربح غير معروضة: لا تملك صلاحية عرض تكاليف وأرباح تقارير المخزون.",
  },
  inventoryTransactions: {
    tools: "Tools",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    cancelDocument: "Cancel document",
    open: "Open",
    noJournalYet: "This document has not been posted, so there is no entry to show yet.",
    noJournalEver: "This movement writes no journal: company quantity and value are unchanged.",
    noRows: "No matching documents",
    saveFailed: "The operation could not be completed.",
    filterSearch: "Search",
    filterFrom: "From",
    filterTo: "To",
    filterApply: "Show",
    saved: "Saved as a draft. Stock, payables, tax and the ledger are untouched.",
    posted: "Posted.",
    audited: "Audited.",
    cancelled: "Cancelled.",
    reversed: "Reversed by a counter-entry; the original is kept exactly as posted.",
    addLine: "Add line",
    removeLine: "Remove",
    date: "Date",
    warehouse: "Warehouse",
    toWarehouse: "Destination warehouse",
    storageLocation: "Storage location",
    supplier: "Supplier",
    reference: "Reference",
    notes: "Notes",
    reason: "Reason",
    costCenter: "Cost centre",
    item: "Item",
    unit: "Unit",
    quantity: "Quantity",
    unitCost: "Unit cost",
    unitPrice: "Unit price",
    lineValue: "Line value",
    available: "Available",
    averageCost: "Average cost",
    total: "Total",
    status: "Status",
    number: "Number",
    branch: "Branch",
    reversalDate: "Reversal date",
    selectOrder: "Choose a purchase order",
    selectReceipt: "Choose a goods receipt",
    selectInvoice: "Choose an invoice",
    selectCount: "Choose a stock count",
    orderNumber: "Purchase order",
    orderDate: "Order date",
    receiptNumber: "Receipt number",
    invoiceNumber: "Invoice number",
    deliveryNote: "Delivery note",
    supplierInvoiceNumber: "Supplier invoice number",
    supplierInvoiceDate: "Supplier invoice date",
    dueDate: "Due date",
    ordered: "Ordered",
    previouslyReceived: "Previously received",
    remaining: "Remaining",
    receivedNow: "Received now",
    difference: "Difference",
    comparison: "Comparison",
    receivedDescription: "Description as delivered",
    orderPrice: "Order price",
    invoicePrice: "Invoice price",
    priceDifference: "Price difference",
    invoiced: "Invoiced",
    remainingToInvoice: "Remaining to invoice",
    returned: "Returned",
    remainingToReturn: "Remaining returnable",
    systemQuantity: "System quantity",
    countedQuantity: "Counted quantity",
    differenceValue: "Difference value",
    responsible: "Responsible",
    loadWarehouseItems: "Load warehouse items",
    createAdjustment: "Create difference adjustment",
    requiredDate: "Required date",
    requestedBy: "Requested by",
    department: "Department",
    approve: "Approve",
    close: "Close",
    conversion: "Conversion",
    progress: "Progress",
    groups: {
      purchases: "Purchases",
      sales: "Sales",
    },
    cards: {
      requests: "Material purchase request",
      orders: "Material purchase order",
      receipts: "Goods receipt note",
      invoices: "Material purchase invoice",
      returns: "Material purchase return",
      stockReceipt: "Stock receipt",
      stockIssue: "Stock issue",
      transfer: "Warehouse transfer",
      adjustment: "Stock adjustment",
      count: "Stock count",
      countAdjustment: "Count difference adjustment",
      opening: "Opening inventory balance",
    },
    hints: {
      requests: "An internal requisition: no supplier, no price, no stock, no accounting.",
      orders: "A commitment to a supplier at agreed prices. No stock and no payable.",
      receipts: "Where purchased stock enters the warehouse, against goods received not invoiced.",
      invoices: "The supplier's bill for goods already received. It raises the payable and adds NO stock.",
      returns: "Goods back to the supplier at the original receipt cost and the original tax rate.",
      stockReceipt: "An internal, non-purchase receipt. Never a way around order → receipt → invoice.",
      stockIssue: "Costed at the moving average standing when it happened, never at a typed figure.",
      transfer: "Between two warehouses: company quantity and value are unchanged, and no journal is written.",
      adjustment: "A deliberate correction against the configured gain or loss account.",
      count: "A count records reality only. It moves no stock; differences are a separate document.",
      countAdjustment: "The difference from an approved count. Always created as a draft, never auto-posted.",
      opening: "Stock that existed before the system. Every line carries its unit cost — posting is refused without one, and the counterpart is an opening-balance equity account, never an inventory gain.",
    },
  },
  inventorySales: {
    indivisibleOfferClause: "This quotation is indivisible; any reduction in quantities changes the prices.",
    indivisibleOffer: "Quotation is indivisible", yes: "Yes", no: "No",
    deliveryPeriod: "Delivery period", origin: "Country of origin",
    insertFromOffer: "Insert from quotation", insertFromDelivery: "Insert from delivery note",
    autoDeliveryOnPost: "Created automatically on posting",
    orderDateBeforeToday: "A quotation cannot be dated before today.",
    orderNumber: "Quotation number",
    deliveryNumber: "Delivery note number",
    invoiceNumber: "Invoice number",
    returnNumber: "Return number",
    customer: "Customer",
    customerCode: "Customer code",
    customerPo: "Customer PO number",
    customerPoDate: "Customer PO date",
    customerReference: "Customer reference",
    billingAddress: "Billing address",
    deliveryAddress: "Delivery address",
    contactPerson: "Contact person",
    phone: "Phone",
    email: "Email",
    salesperson: "Salesperson",
    driver: "Driver",
    vehicle: "Vehicle number",
    deliveryReference: "Delivery reference",
    paymentMethod: "Payment method",
    paymentTerms: "Payment terms",
    returnReason: "Return reason",
    internalNotes: "Internal notes",
    selectOrder: "Select quotation",
    selectDelivery: "Select delivery note",
    selectInvoice: "Select invoice",
    ordered: "Ordered",
    deliveredNow: "Delivering now",
    previouslyDelivered: "Previously delivered",
    remainingToDeliver: "Remaining to deliver",
    delivered: "Delivered",
    previouslyInvoiced: "Previously invoiced",
    remainingToInvoice: "Remaining to invoice",
    invoiceNow: "Invoicing now",
    sold: "Sold",
    previouslyReturned: "Previously returned",
    maxReturnable: "Maximum returnable",
    returnNow: "Returning now",
    available: "Available",
    shortStock: "The quantity exceeds what is in stock",
    issueCost: "Issue cost",
    costTotal: "Total cost",
    grossBeforeDiscount: "Gross before discount",
    discountTotal: "Total discount",
    netBeforeTax: "Net before tax",
    taxTotal: "Total tax",
    grandTotal: "Grand total",
    baseCurrencyTotal: "Total in base currency",
    exchangeRate: "Exchange rate",
    discountType: "Discount type",
    discountValue: "Discount value",
    taxType: "Tax type",
    fulfilment: "Fulfilment",
    closeOrder: "Close quotation",
    reopenOrder: "Reopen quotation",
    approve: "Approve",
    noStockEffect: "This invoice moves no stock: the quantity and its cost were recorded on the delivery note.",
    stockOutHere: "Posting a delivery note issues the stock and charges cost of goods sold at the moving weighted average.",
    cards: {
      orders: "Quotation",
      deliveries: "Delivery Note",
      invoices: "Material Sales Invoice",
      returns: "Material Sales Return",
    },
    hints: {
      orders: "A commercial commitment only: no stock, no journal, no customer balance.",
      deliveries: "Goods leave the warehouse, and cost of goods sold is charged at the actual cost at that moment.",
      invoices: "Bills the customer for goods. A direct invoice raises its delivery note on posting; one billed against a delivery moves no stock.",
      returns: "Sold goods come back into the warehouse at their original cost, and the customer is credited.",
    },
  },
  dailyTransactionKit: {
    inquiry: "Inquiry",
    newDocument: "New",
    save: "Save",
    saveAndNew: "Save & New",
    saveIncomplete: "Saving needs a complete draft: party, item and quantity",
    print: "Print",
    cancel: "Cancel",
    lookupTitle: "Previous transactions — choose one to load",
    registerTitle: "Previous transactions",
    emptyTitle: "No previous transactions",
    emptyHint: "Nothing has been entered on this screen yet. Choose New to create one.",
  },
  sameModuleInquiries: {
    noRows: "No matching rows",
    open: "Open",
    receiptOrders: {
      title: "Receipt Orders",
      breadcrumb: "Financial Management › Receivables › Inquiries › Receipt Orders",
      notice: "The receipt order documents themselves — read-only, and it posts nothing.",
      sourceNotice: "Source: the receipt orders. Collected and Remaining are derived from posted receipt vouchers, never stored.",
      orderNumber: "Order No.", date: "Date", customer: "Customer", amount: "Ordered",
      collected: "Collected", remaining: "Remaining", currency: "Currency", status: "Status",
      branch: "Branch", reference: "Reference", collector: "Collector",
      totalOrdered: "Total ordered", totalCollected: "Total collected",
      totalRemaining: "Total remaining", count: "Orders",
    },
    reminderHistory: {
      title: "Customer Balance Reminder History",
      breadcrumb: "Financial Management › Receivables › Inquiries › Customer Balance Reminder History",
      notice: "Reminders that were actually created — not customers merely eligible for one.",
      sourceNotice: "Source: the stored reminder batches. No second table and no copy for display.",
      batchNumber: "Batch No.", date: "Date", channel: "Channel", status: "Status",
      recipients: "Recipients", sent: "Sent", skipped: "Skipped", failed: "Failed",
      totalBalance: "Total balance", createdBy: "Created by", count: "Batches",
    },
    bankReconciliation: {
      title: "Bank Reconciliation",
      breadcrumb: "Financial Management › Cash & Banks › Inquiries › Bank Reconciliation",
      notice: "The reconciliation register — read-only. Matching happens on the reconciliation screen.",
      sourceNotice: "Source: the reconciliations and their lines, the same source the bank report reads.",
      number: "Reconciliation No.", bank: "Bank", account: "Bank account", period: "Period",
      statementDate: "Statement date", statementBalance: "Statement balance",
      ledgerBalance: "Book balance", reconciled: "Reconciled", difference: "Difference",
      status: "Status", count: "Reconciliations", statusDraft: "Draft", statusCompleted: "Completed",
    },
  },
  inquiryKit: {
    search: "Search",
    reset: "Reset",
    refresh: "Refresh",
    print: "Print",
    printPreview: "Print preview",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    columns: "Columns",
    columnSettings: "Column settings",
    columnSettingsHint: "Choose the columns shown on screen. Exports are not affected by this choice.",
    columnLocked: "Always shown",
    restoreDefaults: "Restore defaults",
    done: "Done",
    resultInfo: "Showing {from}–{to} of {total}",
    rowsPerPage: "Rows",
    pageOf: "Page {page} of {pages}",
    firstPage: "First page",
    previousPage: "Previous page",
    nextPage: "Next page",
    lastPage: "Last page",
    sortAscending: "Sort ascending",
    sortDescending: "Sort descending",
    printFailed: "The document could not be sent to the printer, so it was opened for printing instead.",
  },
  journal: {
    title: "Journal Entries",
    voucherType: "Voucher type",
    voucherInformation: "Voucher information",
    referenceInformation: "Reference information",
    tabAttachments: "Attachments",
    tabPrintOptions: "Print options",
    tabAuditHistory: "Audit history",

    linkedDocuments: "Linked documents",
    additionalInformation: "Additional information",
    reverses: "Reverses",
    reversedBy: "Reversed by",
    noLinkedDocuments: "No linked documents",
    openDocument: "Open",
    saveBeforeAttaching: "Save the entry before attaching files",
    printDialogTitle: "Print entry",
    withCostCenters: "With cost centres",
    withoutCostCenters: "Without cost centres",
    auditHistoryEmpty: "No changes recorded",
    auditWhen: "When",
    auditHistoryUnsaved: "Audit history appears once the entry is saved",
    createdBy: "Created by",
    createdOn: "Created on",
    modifiedBy: "Last modified by",
    modifiedOn: "Last modified on",
    auditCategories: { header: "Header", line: "Lines", attachment: "Attachments" },
    auditActions: {
      created: "Created",
      updated: "Updated",
      deleted: "Deleted",
      audited: "Audited",
      posted: "Posted",
      returned: "Returned for correction",
      reversed: "Reversed",
      reopened: "Reopened for editing",
      other: "Action",
    },
    dropFilesHere: "Drop files here or browse",
    accountBalanceHint: "Account balance",
    selectCostCenter: "Select a cost center",
    searchCostCenters: "Search cost centers…",
    noCostCenters: "No matching cost centers",
    groupCostCenter: "Group",
    groupAccount: "Group",
    statusDraft: "Draft",
    statusNotAudited: "Not audited",
    delete: "Delete",
    journalVoucher: "Journal Voucher",
    status: "Status",
    statuses: {
      unposted: "Unposted",
      posted: "Posted",
      audited: "Audited",
      rejected: "Rejected",
      cancelled: "Cancelled",
      reversed: "Reversed",
    },
    sequence: "Sequence",
    voucherDate: "Voucher date",
    fiscalYear: "Fiscal year",
    period: "Period",
    referenceNumber: "Reference number",
    referenceDate: "Reference date",
    referenceSequence: "Reference sequence",
    salesperson: "Salesperson",
    user: "User",
    branch: "Branch",
    notes: "Notes",
    notesHint: "Notes are copied into each new line description; every line stays independently editable",
    save: "Save",
    saveAndNew: "Save and new",
    inquiry: "Inquiry",
    print: "Print",
    cancel: "Cancel",
    newEntry: "New entry",
    printOptions: "Print options",
    printSubledger: "Print subledger",
    showCostCenters: "Show cost centers in print",
    showCurrency: "Show currency in print",
    printStamps: "Print stamps and signatures",
    lines: "Journal details",
    addLine: "Add line",
    removeLine: "Remove line",
    accountNumber: "Account no.",
    accountName: "Account name",
    subledger: "Subledger",
    currency: "Currency",
    exchangeRate: "Rate",
    debit: "Debit",
    credit: "Credit",
    localDebit: "Local debit",
    localCredit: "Local credit",
    balance: "Balance",
    balanceType: "Balance type",
    description: "Description",
    costCenter: "Cost center",
    actions: "Actions",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    totalLocalDebit: "Total local debit",
    totalLocalCredit: "Total local credit",
    difference: "Difference",
    balanced: "Balanced",
    unbalanced: "Out of balance",
    attachments: "Attachments",
    uploadFile: "Attach file",
    allowedTypes: "PDF, Excel, Word, images — 25 MB maximum",
    noAttachments: "No attachments",
    attachmentsPending: "Pending save",
    attachmentPendingNote: "Selected files are uploaded when the entry saves successfully",
    attachmentStatuses: {
      pending: "Pending",
      uploading: "Uploading",
      uploaded: "Uploaded",
      failed: "Failed",
    },
    attachmentFileType: "Type",
    attachmentFileSize: "Size",
    attachmentTypeRejected: "\u201C{name}\u201D is not an accepted file type. Allowed: {types}",
    attachmentTooLarge: "\u201C{name}\u201D exceeds the {max} limit",
    attachmentEmpty: "\u201C{name}\u201D is empty",
    attachmentUploadFailed: "The entry was saved, but some attachments failed to upload. You can retry them.",
    attachmentRetry: "Retry",
    attachmentRemove: "Remove",
    attachmentsLinked: "{count} attachment(s) linked to the entry",
    download: "Download",
    saveFirst: "Save the entry before attaching files",
    selectAccount: "Select an account",
    searchAccounts: "Search by account number or name",
    searchParties: "Search parties",
    noAccounts: "No matching accounts",
    noParties: "No matching parties",
    partyRequired: "This account requires a subledger party",
    costCenterRequired: "This account requires a cost center",
    notesRequired: "Notes are required on this journal entry",
    rateOverridden: "Exchange rate overridden manually",
    rateMissing: "No exchange rate is effective on the voucher date",
    readOnlyPosted: "This entry has been posted and can no longer be edited",
    discardChanges: "Clear the form",
    discardBody: "This clears the unsaved contents of this form only. It does not delete or reverse any saved journal entry.",
    savedAs: "Saved as",
    filters: "Filters",
    dateFrom: "Date from",
    dateTo: "Date to",
    apply: "Apply",
    clear: "Clear",
    results: "Results",
    noResults: "No journal entries match these filters",
    open: "Open",
    edit: "Edit",
    view: "View",
    deleteEntry: "Delete entry",
    deleteBody: "This unposted journal entry will be deleted. This cannot be undone.",
    lineCount: "Lines",
    backToEntry: "Back to entry",
    balanceInfo: "Account balance (information only)",
    reopen: "Reopen for editing",
    reopenTitle: "Reopen this posted entry",
    reopenWarning:
      "This entry's effect is removed from the General Ledger and the balances, and it returns to Draft. After editing it must be audited and posted again. The action, its reason and who did it are recorded in the audit trail.",
    reopenReason: "Reason for reopening",
    reopenReasonHint: "Required — up to 500 characters",
    reopenConfirm: "Reopen for editing",
    reopened: "The entry was reopened and is a draft again",
  },
  glSettings: {
    title: "General Ledger System Settings",
    pageDescription:
      "Company-level accounting behavior for Chart of Accounts, Journal Entries, Subledgers and Cost Centers.",
    descriptionColumn: "Description",
    valueColumn: "Value",

    requireDefaultCostCenter: "Require Default Cost Center on Accounts",
    requireDefaultCostCenterHint:
      "When enabled, every postable account must have a Default Cost Center. Existing accounts without one are left unchanged until next edited.",
    requireJournalEntryNotes: "Require Notes on Journal Entries",
    requireJournalEntryNotesHint:
      "When enabled, Notes becomes mandatory on new and editable unposted Journal Entries. Posted historical entries are never affected.",
    aggregateChildSubledger: "Aggregate Child Subledger Opening Balances into the Main Subledger Account",
    aggregateChildSubledgerHint:
      "When enabled, child subledger opening balances are aggregated into the main subledger account for inquiry and reporting only, with no duplicate accounting entries.",
    costPercentage: "Cost Percentage",
    costPercentageHint: "A decimal value greater than or equal to zero.",
    manualCostCenterEntryMethod: "Manual Cost Center Entry Method",
    manualCostCenterEntryMethodHint: "The approved method for manually distributing Cost Centers on a journal line.",

    yes: "Yes",
    no: "No",
    methodAmount: "Amount",
    methodPercentage: "Percentage",

    restoreDefaults: "Restore Defaults",
    viewAuditHistory: "View Audit History",

    savedOk: "Settings saved successfully.",
    saveError: "Failed to save settings.",
    loadError: "Failed to load settings.",
    unsavedChangesWarning: "You have unsaved changes. Leave without saving?",
    readOnlyNotice: "You have view-only access. These settings cannot be edited.",

    restoreDefaultsConfirmTitle: "Restore Defaults",
    restoreDefaultsConfirmMessage:
      "Every General Ledger Setting for this company will be reset to its approved default. This cannot be undone.",
    restoreDefaultsConfirmLabel: "Restore Defaults",
    restoredOk: "Defaults restored successfully.",

    enableConfirmTitle: "Enable Required Default Cost Center",
    enableConfirmLabel: "Enable Anyway",

    auditHistoryTitle: "General Ledger Settings Audit History",
    auditHistoryEmpty: "No changes have been recorded for these settings yet.",
    auditHistoryLoadError: "Failed to load the audit history.",
    auditHistoryWhen: "Date & Time",
    auditHistoryUser: "Changed By",
    auditHistorySetting: "Setting",
    auditHistoryOldValue: "Old Value",
    auditHistoryNewValue: "New Value",
    auditHistoryCreated: "Settings initialized with approved defaults",
    auditHistorySystem: "System",
  },
  customerOpeningBalances: {
    title: "Customer Opening Balances",
    breadcrumb: "Financial Management › Receipts › Settings › Customer Opening Balances",
    purposeNotice:
      "What each customer owed — or was owed — when the books opened, in six distinct classifications. Pick one from the cards, then declare the balances.",
    readOnlyNotice: "You have view-only access. Balances cannot be declared or edited.",
    preparationNotice:
      "Preparation only: saving writes no journal voucher and moves no balance. Posting is a separate later step.",
    types: {
      customerReceivables: "Customer Receivables",
      unbilledReceivables: "Unbilled Receivables",
      advancePayments: "Customer Advance Payments",
      promissoryNotes: "Promissory Note Receivables",
      supplierReceivables: "Supplier Receivables",
      chequesInCollection: "Customer Receivables – Cheques in Collection",
    },
    category: "Category",
    allCategories: "— All categories —",
    fiscalYear: "Fiscal Year",
    currency: "Currency",
    allCurrencies: "— All currencies —",
    status: "Status",
    allStatuses: "— All statuses —",
    statusNotEntered: "Not entered",
    statusZero: "Zero",
    statusEntered: "Entered",
    statusInvalid: "Invalid",
    statusLocked: "Locked",
    customerNumber: "Customer Number",
    customer: "Customer",
    exchangeRate: "Exchange Rate",
    debit: "Debit",
    credit: "Credit",
    localDebit: "Local Debit",
    localCredit: "Local Credit",
    controlAccount: "Control Account",
    noControlAccount: "No control account is linked — a save will be refused.",
    inactiveCustomer: "Inactive customer — an existing balance stays visible but no new one may be entered.",
    search: "Search",
    searchPlaceholder: "Search by customer number or name",
    refresh: "Refresh",
    print: "Print",
    pdf: "PDF",
    excel: "Excel",
    save: "Save changes",
    cancelChanges: "Cancel changes",
    resetRow: "Reset row to zero",
    unsavedOne: "1 unsaved row",
    unsavedMany: "{count} unsaved rows",
    leaveWarning: "You have unsaved changes. Leaving this screen will lose them.",
    totals: "Totals",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    totalLocalDebit: "Total local debit",
    totalLocalCredit: "Total local credit",
    difference: "Difference",
    balanced: "Balanced",
    entered: "Entered",
    zero: "Zero",
    notEntered: "Not entered",
    empty: "No customers in this classification.",
    rows: "Rows",
    saved: "Balances saved",
    nothingToSave: "There are no changes to save.",
    loadError: "Could not load the balances.",
    saveError: "Could not save.",
  },
  serviceQuotations: {
    title: "Service Sales Quotation",
    breadcrumb: "Financial Management › Receivables › Daily Transactions › Service Sales Quotation",
    purposeNotice: "A price offered to a customer. It is saved, edited and cancelled — never posted.",
    noAccountingNotice:
      "This document has NO accounting impact at all: no journal, no customer receivable, no cash movement, no tax posting.",
    readOnlyNotice: "You have view-only access.",
    dependencyNotice:
      "Lines need defined Service Sales Types, and tax needs a rate in force on the quotation date.",
    headerTitle: "Quotation details",
    linesTitle: "Lines",
    listTitle: "Quotations",
    newRecord: "New Quotation",
    number: "Quotation No.",
    date: "Quotation Date",
    status: "Status",
    draft: "Draft",
    cancelled: "Cancelled",
    customer: "Customer",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    paymentMethod: "Payment Method",
    cash: "Cash",
    credit: "Credit",
    paymentInstrument: "Payment Instrument",
    salesperson: "Salesperson",
    costCenter: "Cost Center",
    notes: "Notes",
    service: "Service",
    description: "Description",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    discountPercent: "Discount %",
    taxPercent: "Tax %",
    gross: "Gross",
    discount: "Discount",
    tax: "Tax",
    net: "Net",
    taxable: "Taxable",
    pricingMode: "Price tax",
    taxExclusive: "Exclusive",
    taxInclusive: "Inclusive",
    exemptCustomer: "This customer is tax exempt, so no line carries tax.",
    addLine: "Add Line",
    removeLine: "Remove",
    noLines: "No lines yet. Add at least one.",
    subtotal: "Subtotal",
    discountTotal: "Discount Total",
    taxTotal: "Tax Total",
    netTotal: "Quotation Total",
    save: "Save",
    saveNew: "Save + New",
    cancelEdit: "Cancel",
    cancelDoc: "Cancel Quotation",
    cancelConfirm: "Cancel this quotation? It cannot be edited afterwards.",
    edit: "Edit",
    refresh: "Refresh",
    search: "Search",
    noRows: "No quotations",
    count: "Count",
    choose: "— Select —",
    none: "None",
    actions: "Actions",
    view: "View",
    back: "Back",
    saved: "Saved",
    cancelledOk: "Quotation cancelled",
    loadError: "The quotations could not be loaded",
    saveError: "The quotation could not be saved",
    taxType: "Tax Type",
    branch: "Branch",
    fiscalYear: "Year",
    taxTypeFromService: "— From service —",
    duplicateLine: "Duplicate Line",
    discountType: "Discount Type",
    discountValue: "Discount Value",
    discountTypeNone: "— No type —",
    discountAsPercent: "percent %",
    discountAsAmount: "amount",
    discountTypeHint:
      "The discount type decides how the value is read — a percentage or a flat amount. With no type, the Discount % column is used exactly as before.",
    customerInfoTitle: "Customer Information",
    customerInfoEmpty: "Select a customer to see their details.",
    customerCode: "Customer Code",
    customerNameLabel: "Customer Name",
    phone: "Phone",
    mobile: "Mobile",
    email: "Email",
    taxNumber: "Tax Number",
    addressLabel: "Address",
    customerCategory: "Customer Category",
    creditLimit: "Credit Limit",
    taxTreatment: "Tax Treatment",
    treatmentInclusive: "Tax inclusive",
    treatmentExclusive: "Tax exclusive",
    treatmentExempt: "Exempt",
    attachmentsTitle: "Attachments",
    attach: "Attach File",
    attachHint: "Allowed: PDF, Excel, Word, images. Up to 25 MB per file.",
    noAttachments: "No files attached",
    attachmentSaveFirst: "Save the quotation first, then attach files to it.",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentUploadedBy: "Attached By",
    attachmentUploadedAt: "Attached On",
    attachmentView: "View",
    attachmentDownload: "Download",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "Remove this attachment?",
    attachmentUploadFailed: "The file could not be uploaded",
    attachmentOpenFailed: "The attachment could not be opened",
    attachmentUploaded: "File attached",
    attachmentRemoved: "Attachment removed",
    documentActions: "Document",
    printAction: "Print",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    exportSaveFirst: "Save the quotation first — printing reads the saved document.",
    exportFailed: "The document could not be produced",
  },
  serviceSalesInvoices: {
    settlementTitle: "Settlement information",
    settlementInvoiceTotal: "Invoice total",
    settlementSettled: "Settled amount",
    settlementOutstanding: "Outstanding amount",
    settlementPaymentNo: "Payment number",
    settlementDate: "Date",
    settlementAmount: "Settled amount",
    settlementStatus: "Status",
    settlementAction: "Action",
    settlementCancel: "Cancel settlement",
    settlementReason: "Cancellation reason",
    settlementConfirm: "Confirm cancellation",
    settlementDismiss: "Cancel",
    settlementCancelled: "Settlement cancelled. The invoice and the receipt remain posted and unchanged.",
    settlementNone: "This invoice has no settlements.",
    settlementActive: "Active",
    settlementCancelledLabel: "Cancelled",
    settlementNotice: "Cancelling a settlement only releases the payment from the invoice: it does not cancel the invoice, does not cancel the receipt, and creates or deletes no accounting entry.",
    settlementPaid: "Paid",
    settlementPartiallyPaid: "Partially paid",
    settlementUnpaid: "Unpaid",
    title: "Service Sales Invoice",
    breadcrumb: "Financial Management › Receivables › Daily Transactions › Service Sales Invoice",
    purposeNotice: "A service invoice issued to a customer. The payment method is inherited from the customer card and is not chosen here.",
    lifecycleNotice: "Lifecycle: Draft → Audited → Posted. Nothing reaches the ledger before posting, and whoever raised an invoice may not approve it.",
    readOnlyNotice: "You have view-only access.",
    postedNotice: "This invoice is posted: the entry is in the ledger and cannot be amended. Correct it by reversing.",
    headerTitle: "Invoice details",
    linesTitle: "Lines",
    listTitle: "Invoices",
    customerInfoTitle: "Customer Information",
    customerInfoEmpty: "Select a customer to see their details.",
    attachmentsTitle: "Attachments",
    newRecord: "New Invoice",
    number: "Invoice No.",
    date: "Invoice Date",
    status: "Status",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    customer: "Customer",
    paymentMethod: "Payment Method",
    paymentFromCustomer: "— From customer card —",
    saleNature: "Sale Nature",
    saleOneTime: "One-Time Sale",
    saleContract: "Contract",
    sourceQuotation: "Source Quotation",
    noQuotation: "— No quotation —",
    imported: "Imported quotation {number}",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    branch: "Branch",
    fiscalYear: "Year",
    notes: "Notes",
    taxType: "Tax Type",
    taxTypeFromService: "— From service —",
    service: "Service",
    description: "Description",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    duplicateLine: "Duplicate Line",
    discountType: "Discount Type",
    discountValue: "Discount Value",
    discountTypeNone: "— No type —",
    discountPercent: "Discount %",
    taxPercent: "Tax %",
    taxable: "Taxable",
    tax: "Tax",
    gross: "Gross",
    addLine: "Add Line",
    removeLine: "Remove",
    noLines: "No lines. Add at least one.",
    subtotal: "Subtotal",
    discountTotal: "Total Discount",
    grandTotal: "Grand Total",
    saveDraft: "Save",
    cancelEdit: "Cancel",
    auditAction: "Audit",
    postAction: "Post",
    reverseAction: "Reverse",
    reverseConfirm: "Reverse this posted invoice? A reversing entry is created; nothing is deleted.",
    reverseReason: "Service sales invoice reversal",
    printAction: "Print",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    exportSaveFirst: "Save the invoice first — printing reads the saved document.",
    exportFailed: "The document could not be produced",
    attach: "Attach File",
    attachHint: "Allowed: PDF, Excel, Word, images. Up to 25 MB per file.",
    noAttachments: "No files attached",
    attachmentSaveFirst: "Save the invoice first, then attach files to it.",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentUploadedBy: "Attached By",
    attachmentUploadedAt: "Attached On",
    attachmentView: "View",
    attachmentDownload: "Download",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "Remove this attachment?",
    attachmentUploaded: "File attached",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "The file could not be uploaded",
    attachmentOpenFailed: "The attachment could not be opened",
    customerCode: "Customer Code",
    customerNameLabel: "Customer Name",
    phone: "Phone",
    mobile: "Mobile",
    email: "Email",
    taxNumber: "Tax Number",
    addressLabel: "Address",
    creditLimit: "Credit Limit",
    edit: "Edit",
    view: "View",
    refresh: "Refresh",
    search: "Search",
    noRows: "No invoices",
    count: "Count",
    choose: "— Select —",
    none: "None",
    actions: "Actions",
    saved: "Saved",
    audited: "Audited",
    posted: "Posted",
    reversed: "Reversed",
    loadError: "The invoices could not be loaded",
    saveError: "The invoice could not be saved",
    actionError: "The action could not be completed",
  },
  debitNotes: {
    title: "Debit Note",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Debit Note",
    purposeNotice: "An adjustment that increases what a customer owes. Each line names the offset account its other half is credited to.",
    lifecycleNotice: "Lifecycle: Draft → Audited → Posted. Audit is a second pair of eyes: nobody audits their own note.",
    readOnlyNotice: "You have view-only access.",
    postedNotice: "This note is posted and read-only. It can only be undone by reversing it.",
    sourceInvoiceNotice: "The source invoice is a reference only: it is never altered, reopened, or settled against.",
    headerTitle: "Note Details",
    linesTitle: "Adjustment Lines",
    attachmentsTitle: "Attachments",
    listTitle: "Debit Notes",
    filtersTitle: "Filters",
    newRecord: "New Note",
    number: "Note No.",
    date: "Date",
    status: "Status",
    branch: "Branch",
    createdBy: "Created By",
    customer: "Customer",
    customerInfo: "Customer",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    reference: "Reference",
    notes: "Notes",
    sourceInvoice: "Source Invoice",
    sourceInvoiceNone: "No source invoice",
    taxInclusive: "Amount tax",
    inclusive: "Inclusive",
    exclusive: "Exclusive",
    costCenter: "Cost Center",
    lineDescription: "Description",
    offsetAccount: "Offset Account",
    amount: "Amount",
    taxType: "Tax Type",
    taxRate: "Rate",
    lineNet: "Net",
    lineTax: "Tax",
    lineTotal: "Total",
    rowNotes: "Row Notes",
    addRow: "Add Line",
    duplicateRow: "Duplicate",
    removeRow: "Remove",
    totalNet: "Net",
    totalTax: "Tax",
    grandTotal: "Grand Total",
    actions: "Actions",
    choose: "Choose",
    none: "—",
    count: "Count",
    search: "Search by note or reference number",
    refresh: "Refresh",
    noRows: "No debit notes",
    edit: "Edit",
    view: "View",
    save: "Save",
    cancelEdit: "Cancel",
    auditAction: "Audit",
    postAction: "Post",
    reverseAction: "Reverse",
    reverseConfirm: "The debit note will be reversed with a contra entry, restoring the customer receivable by its full amount. Continue?",
    reverseReason: "Debit note reversal",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "Save the note first",
    exportFailed: "Could not produce the file",
    fromDate: "From",
    toDate: "To",
    allStatuses: "All statuses",
    apply: "Apply",
    reset: "Clear",
    attachmentAdd: "Add Attachment",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "This attachment will be deleted permanently. Continue?",
    attachmentUploaded: "Attachment uploaded",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "Could not upload the attachment",
    attachmentDraftOnly: "Attachments can only be changed while the note is a draft.",
    attachmentOpenFailed: "Could not open the attachment",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentBy: "Uploaded by",
    attachmentAt: "Uploaded",
    attachmentView: "View",
    attachmentDownload: "Download",
    noAttachments: "No attachments",
    attachmentPolicy: "Up to 25 MB per file.",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    saved: "Note saved",
    audited: "Note audited",
    posted: "Note posted",
    reversed: "Note reversed",
    loadError: "Could not load debit notes",
    saveError: "Could not save the note",
    actionError: "Could not complete the action",
  },

  creditNotes: {
    title: "Credit Note",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Credit Note",
    purposeNotice: "An adjustment that decreases what a customer owes. Each line names the offset account its other half is debited to.",
    lifecycleNotice: "Lifecycle: Draft → Audited → Posted. Audit is a second pair of eyes: nobody audits their own note.",
    readOnlyNotice: "You have view-only access.",
    postedNotice: "This note is posted and read-only. It can only be undone by reversing it.",
    sourceInvoiceNotice: "The source invoice is a reference only: it is never altered, reopened, or settled against.",
    headerTitle: "Note Details",
    linesTitle: "Adjustment Lines",
    attachmentsTitle: "Attachments",
    listTitle: "Credit Notes",
    filtersTitle: "Filters",
    newRecord: "New Note",
    number: "Note No.",
    date: "Date",
    status: "Status",
    branch: "Branch",
    createdBy: "Created By",
    customer: "Customer",
    customerInfo: "Customer",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    reference: "Reference",
    notes: "Notes",
    sourceInvoice: "Source Invoice",
    sourceInvoiceNone: "No source invoice",
    taxInclusive: "Amount tax",
    inclusive: "Inclusive",
    exclusive: "Exclusive",
    costCenter: "Cost Center",
    lineDescription: "Description",
    offsetAccount: "Offset Account",
    amount: "Amount",
    taxType: "Tax Type",
    taxRate: "Rate",
    lineNet: "Net",
    lineTax: "Tax",
    lineTotal: "Total",
    rowNotes: "Row Notes",
    addRow: "Add Line",
    duplicateRow: "Duplicate",
    removeRow: "Remove",
    totalNet: "Net",
    totalTax: "Tax",
    grandTotal: "Grand Total",
    actions: "Actions",
    choose: "Choose",
    none: "—",
    count: "Count",
    search: "Search by note or reference number",
    refresh: "Refresh",
    noRows: "No credit notes",
    edit: "Edit",
    view: "View",
    save: "Save",
    cancelEdit: "Cancel",
    auditAction: "Audit",
    postAction: "Post",
    reverseAction: "Reverse",
    reverseConfirm: "The credit note will be reversed with a contra entry, restoring the customer receivable by its full amount. Continue?",
    reverseReason: "Credit note reversal",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "Save the note first",
    exportFailed: "Could not produce the file",
    fromDate: "From",
    toDate: "To",
    allStatuses: "All statuses",
    apply: "Apply",
    reset: "Clear",
    attachmentAdd: "Add Attachment",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "This attachment will be deleted permanently. Continue?",
    attachmentUploaded: "Attachment uploaded",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "Could not upload the attachment",
    attachmentDraftOnly: "Attachments can only be changed while the note is a draft.",
    attachmentOpenFailed: "Could not open the attachment",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentBy: "Uploaded by",
    attachmentAt: "Uploaded",
    attachmentView: "View",
    attachmentDownload: "Download",
    noAttachments: "No attachments",
    attachmentPolicy: "Up to 25 MB per file.",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    saved: "Note saved",
    audited: "Note audited",
    posted: "Note posted",
    reversed: "Note reversed",
    loadError: "Could not load credit notes",
    saveError: "Could not save the note",
    actionError: "Could not complete the action",
  },
  serviceSalesReturns: {
    title: "Service Sales Return",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Service Sales Return",
    purposeNotice: "Returns services already invoiced. The document starts by choosing a POSTED service sales invoice, then selecting which of its lines to return and how much.",
    lifecycleNotice: "Lifecycle: Draft → Audited → Posted. Only posting makes a return effective and spends the available return quantity.",
    readOnlyNotice: "You have view-only access.",
    postedNotice: "This return is posted and read-only. It can only be undone by reversing it.",
    sourceRequiredNotice: "A return cannot be created without a posted service sales invoice. Lines come from the invoice and are never entered by hand.",
    noInventoryNotice: "This is a service return: financial effect only, with no stock movement.",
    pickInvoiceTitle: "Service Sales Invoice",
    headerTitle: "Return Details",
    linesTitle: "Returnable Invoice Lines",
    attachmentsTitle: "Attachments",
    listTitle: "Service Sales Returns",
    filtersTitle: "Filters",
    newRecord: "New Return",
    number: "Return No.",
    date: "Date",
    status: "Status",
    branch: "Branch",
    createdBy: "Created By",
    sourceInvoice: "Source Invoice",
    invoiceNumber: "Invoice No.",
    invoiceDate: "Invoice Date",
    customer: "Customer",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    costCenter: "Cost Center",
    reference: "Reference",
    notes: "Notes",
    changeInvoice: "Change Invoice",
    chooseInvoice: "Choose a posted invoice to return against",
    noInvoices: "No posted invoices are available to return",
    invoiceSearch: "Search by invoice number",
    fullyReturned: "Fully returned",
    service: "Service",
    lineDescription: "Description",
    originalQty: "Original Qty",
    previouslyReturned: "Previously Returned",
    availableQty: "Available to Return",
    returnQty: "Return Qty",
    unitPrice: "Unit Price",
    taxType: "Tax Type",
    taxRate: "Rate",
    lineNet: "Net",
    lineTax: "Tax",
    lineTotal: "Total",
    nothingSelected: "Enter a return quantity on at least one line",
    totalNet: "Net",
    totalTax: "Tax",
    grandTotal: "Grand Total",
    actions: "Actions",
    choose: "Choose",
    none: "—",
    count: "Count",
    search: "Search by return or invoice number",
    refresh: "Refresh",
    noRows: "No service sales returns",
    edit: "Edit",
    view: "View",
    save: "Save",
    cancelEdit: "Cancel",
    auditAction: "Audit",
    postAction: "Post",
    reverseAction: "Reverse",
    reverseConfirm: "The return will be reversed with a contra entry, and its quantity becomes available to return again. Continue?",
    reverseReason: "Service sales return reversal",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "Save the return first",
    exportFailed: "Could not produce the file",
    fromDate: "From",
    toDate: "To",
    allStatuses: "All statuses",
    apply: "Apply",
    reset: "Clear",
    attachmentAdd: "Add Attachment",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "This attachment will be deleted permanently. Continue?",
    attachmentUploaded: "Attachment uploaded",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "Could not upload the attachment",
    attachmentDraftOnly: "Attachments can only be changed while the return is a draft.",
    attachmentOpenFailed: "Could not open the attachment",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentBy: "Uploaded by",
    attachmentAt: "Uploaded",
    attachmentView: "View",
    attachmentDownload: "Download",
    noAttachments: "No attachments",
    attachmentPolicy: "Up to 25 MB per file.",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    saved: "Return saved",
    audited: "Return audited",
    posted: "Return posted",
    reversed: "Return reversed",
    loadError: "Could not load service sales returns",
    saveError: "Could not save the return",
    actionError: "Could not complete the action",
  },
  receiptVouchers: {
    title: "Receipt Voucher",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Receipt Voucher",
    purposeNotice: "Records money actually received from a customer or into a general account. Each payment row states how it was received and where it landed.",
    lifecycleNotice: "Lifecycle: Draft → Audited → Posted. Audit is a second pair of eyes: nobody audits their own voucher.",
    readOnlyNotice: "You have view-only access.",
    postedNotice: "This voucher is posted and read-only. It can only be undone by reversing it.",
    tabVouchers: "Receipt Vouchers",
    tabCheques: "Cheque Follow-up",
    headerTitle: "Voucher Details",
    paymentsTitle: "Payment Details",
    chequesTitle: "Received Cheques",
    attachmentsTitle: "Attachments",
    listTitle: "Receipt Vouchers",
    newRecord: "New Voucher",
    number: "Voucher No.",
    date: "Date",
    status: "Status",
    branch: "Branch",
    createdBy: "Created By",
    sourceType: "Receipt Source Type",
    sourceCustomer: "Customer",
    sourceAccount: "General Ledger Account",
    customer: "Customer",
    customerInfo: "Customer",
    glAccount: "Account",
    glAccountHint: "Choose a postable account",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    referenceNumber: "Reference",
    totalAmount: "Total Amount",
    notes: "Notes",
    method: "Payment Method",
    amount: "Amount",
    destination: "Cashbox / Bank Account",
    destinationFromMethod: "Set by the payment method",
    cashbox: "Cashbox",
    bankAccount: "Bank Account",
    bankAccountHint: "From the payment method",
    chequeNumber: "Cheque No.",
    drawerBank: "Drawer Bank",
    drawerBranch: "Bank Branch",
    chequeDate: "Cheque Date",
    dueDate: "Due Date",
    rowNotes: "Row Notes",
    addRow: "Add Row",
    duplicateRow: "Duplicate",
    removeRow: "Remove",
    actions: "Actions",
    choose: "Choose",
    none: "—",
    count: "Count",
    search: "Search by voucher or reference number",
    refresh: "Refresh",
    noRows: "No receipt vouchers",
    edit: "Edit",
    view: "View",
    save: "Save",
    cancelEdit: "Cancel",
    auditAction: "Audit",
    postAction: "Post",
    reverseAction: "Reverse",
    reverseConfirm: "The whole receipt voucher will be reversed with a contra entry. Continue?",
    reverseReason: "Receipt voucher reversal",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "Save the voucher first",
    attachmentAdd: "Add Attachment",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "This attachment will be deleted permanently. Continue?",
    attachmentUploaded: "Attachment uploaded",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "Could not upload the attachment",
    attachmentDraftOnly: "Attachments can only be changed while the voucher is a draft.",
    attachmentSize: "Size",
    attachmentBy: "Uploaded by",
    attachmentFile: "File",
    attachmentView: "View",
    attachmentDownload: "Download",
    attachmentAt: "Uploaded",
    noAttachments: "No attachments",
    attachmentOpenFailed: "Could not open the attachment",
    attachmentPolicy: "Up to 25 MB per file.",
    exportFailed: "Could not produce the file",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
    chequeUnderCollection: "Under Collection",
    chequeDeposited: "Deposited",
    chequeCleared: "Cleared",
    chequeBounced: "Bounced",
    chequeReturned: "Returned",
    saved: "Voucher saved",
    audited: "Voucher audited",
    posted: "Voucher posted",
    reversed: "Voucher reversed",
    loadError: "Could not load receipt vouchers",
    saveError: "Could not save the voucher",
    actionError: "Could not complete the action",
  },
  receiptOrders: {
    title: "Receipt Order",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Receipt Order",
    purposeNotice: "An instruction to collect an amount from a customer. The amount is requested here; it is actually received on a Receipt Voucher.",
    noAccountingNotice: "A receipt order has NO accounting effect at all: no journal, no receivable, no cashbox, no bank, no cheque. Accounting happens only when a Receipt Voucher is posted.",
    readOnlyNotice: "You have view-only access.",
    cancelledNotice: "This order is cancelled and read-only.",
    headerTitle: "Order Details",
    amountsTitle: "Amounts",
    linkedTitle: "Linked Receipt Vouchers",
    attachmentsTitle: "Attachments",
    listTitle: "Receipt Orders",
    filtersTitle: "Filters",
    newRecord: "New Order",
    number: "Receipt Order No.",
    date: "Date",
    status: "Status",
    branch: "Branch",
    createdBy: "Created By",
    customer: "Customer",
    currency: "Currency",
    exchangeRate: "Exchange Rate",
    collector: "Collector",
    salesRepresentative: "Sales Representative",
    expectedDate: "Expected Collection Date",
    expectedPaymentMethod: "Expected Payment Method",
    reference: "Reference",
    notes: "Notes",
    cancellationReason: "Cancellation Reason",
    orderAmount: "Amount to Collect",
    collectedAmount: "Collected Amount",
    remainingAmount: "Remaining Amount",
    derivedHint: "Collected and remaining are derived from POSTED receipt vouchers only.",
    approveAction: "Approve",
    cancelAction: "Cancel Order",
    cancelConfirm: "This receipt order will be cancelled. Continue?",
    cancelReason: "Cancellation reason",
    createReceiptAction: "Create Receipt Voucher",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saveFirst: "Save the order first",
    exportFailed: "Could not produce the file",
    receiptNumber: "Receipt No.",
    receiptDate: "Date",
    receiptAmount: "Amount",
    receiptStatus: "Status",
    paymentSummary: "Payment Methods",
    counted: "Counted",
    notCounted: "Not counted",
    noLinked: "No linked receipt vouchers",
    openReceipt: "Open",
    statusDraft: "Draft",
    statusOpen: "Open",
    statusCancelled: "Cancelled",
    fullyCollected: "Fully collected",
    partiallyCollected: "Partially collected",
    actions: "Actions",
    choose: "Choose",
    none: "—",
    count: "Count",
    search: "Search by order or reference number",
    refresh: "Refresh",
    noRows: "No receipt orders",
    edit: "Edit",
    view: "View",
    save: "Save",
    cancelEdit: "Cancel",
    fromDate: "From",
    toDate: "To",
    allStatuses: "All statuses",
    apply: "Apply",
    reset: "Clear",
    attachmentAdd: "Add Attachment",
    attachmentRemove: "Remove",
    attachmentRemoveConfirm: "This attachment will be deleted permanently. Continue?",
    attachmentUploaded: "Attachment uploaded",
    attachmentRemoved: "Attachment removed",
    attachmentUploadFailed: "Could not upload the attachment",
    attachmentOpenFailed: "Could not open the attachment",
    attachmentCancelledOnly: "A cancelled order's attachments can no longer be changed.",
    attachmentFile: "File",
    attachmentSize: "Size",
    attachmentBy: "Uploaded by",
    attachmentAt: "Uploaded",
    attachmentView: "View",
    attachmentDownload: "Download",
    noAttachments: "No attachments",
    attachmentPolicy: "Up to 25 MB per file.",
    saved: "Order saved",
    approved: "Order approved",
    cancelled: "Order cancelled",
    loadError: "Could not load receipt orders",
    saveError: "Could not save the order",
    actionError: "Could not complete the action",
  },
  advanceReceivedRevenues: {
    title: "Advance Received Revenue",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Advance Received Revenue",
    purposeNotice: "Earns the revenue of prepaid contracts across the service period, month by month.",
    contractRuleNotice: "Revenue recognition follows the CONTRACT service period and nothing else: the contract start and end dates. Not the invoice date, not the collection date, not the date this schedule was created.",
    collectionNotice: "Collection is a separate matter entirely: a receipt voucher moves cash and the customer's balance only. A contract collected in full on 31/03 still defers the rest until the service is delivered.",
    noSchedulerNotice: "There is no scheduler in this system: generating the plan writes no journal, and each due period is recognized when a user asks for it.",
    readOnlyNotice: "You have view-only access.",
    eligibleTitle: "Contract invoices ready to schedule",
    schedulesTitle: "Recognition schedules",
    scheduleTitle: "Schedule",
    periodsTitle: "Recognition periods",
    recognitionsTitle: "Recognition entries",
    invoice: "Source invoice",
    invoiceDate: "Invoice date",
    customer: "Customer",
    currency: "Currency",
    contractStart: "Contract start date",
    contractEnd: "Contract end date",
    periodCount: "Periods",
    originalDeferred: "Original deferred revenue",
    recognized: "Recognized revenue",
    returned: "Returned amount",
    remaining: "Remaining deferred revenue",
    duePending: "Due to recognize",
    period: "Period",
    periodStart: "From",
    periodEnd: "To",
    recognitionDate: "Recognition date",
    scheduledAmount: "Scheduled",
    recognizedAmount: "Recognized",
    periodStatus: "Status",
    recognitionNumber: "Entry number",
    fiscalYear: "Fiscal year",
    amount: "Amount",
    revenueAccount: "Revenue account",
    service: "Service",
    asOfDate: "As at",
    generate: "Generate schedule",
    activate: "Activate",
    remove: "Delete schedule",
    recognizeDue: "Recognize due periods",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    open: "Open",
    back: "Back",
    search: "Search",
    notEligible: "Not schedulable",
    noSchedules: "No schedules yet.",
    noEligible: "No posted contract invoice is waiting for a schedule.",
    noRecognitions: "No recognition entries.",
    generated: "Schedule generated. No accounting effect.",
    activated: "Schedule activated.",
    removed: "Schedule deleted.",
    recognizedToast: "Draft recognitions raised. Nothing has been written to the ledger yet.",
    auditedToast: "Audited.",
    postedToast: "Posted to the general ledger.",
    reversedToast: "Recognition reversed.",
    confirmRecognize: "A draft recognition will be raised for every period due as at this date. Continue?",
    confirmPost: "This entry will be written to the general ledger. Continue?",
    confirmReverse: "This recognition will be undone by a counter-entry. Continue?",
    statusDraft: "Draft",
    statusActive: "Active",
    statusCompleted: "Completed",
    periodPending: "Not due",
    periodDue: "Due",
    periodDrafted: "Drafted",
    periodRecognized: "Recognized",
    periodReduced: "Reduced by a return",
    entryNotice: "The entry is Dr deferred service revenue / Cr service revenue. No tax, no customer balance, no cash or bank.",
  },
  customerTransactionTransfers: {
    title: "Customer Transaction Transfer",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Customer Transaction Transfer",
    purposeNotice: "Moves previously posted financial movements from one customer to another within a chosen historical period.",
    historyNotice: "Accounting history is never rewritten: no source document's customer is changed and no posted ledger row is repointed. A NEW reclassification voucher is posted, and the original documents stay exactly as they were issued.",
    entryNotice: "The entry is Dr the new customer's receivable / Cr the old customer's. When both share one control account its own net movement stays zero and only the subledger ownership moves.",
    readOnlyNotice: "You have view-only access.",
    selectionTitle: "Transferable movements",
    transfersTitle: "Transfers",
    detailTitle: "Transfer detail",
    transferDate: "Transfer date",
    oldCustomer: "Old customer",
    newCustomer: "New customer",
    fromDate: "From date",
    toDate: "To date",
    statement: "Statement",
    search: "Search",
    load: "Load movements",
    select: "Select",
    documentType: "Document type",
    documentNumber: "Document number",
    documentDate: "Document date",
    reference: "Reference",
    description: "Description",
    currency: "Currency",
    debit: "Debit",
    credit: "Credit",
    netEffect: "Net customer effect",
    currentCustomer: "Current customer",
    movementStatus: "Status",
    lastTransfer: "Last transfer",
    transferNumber: "Transfer number",
    movementCount: "Movements",
    totalEffect: "Total effect",
    sameControlAccount: "Same control account",
    differentControlAccounts: "Different control accounts",
    fiscalYear: "Fiscal year",
    status: "Status",
    execute: "Execute transfer",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    open: "Open",
    back: "Back",
    notSelectable: "Not transferable",
    sourceReversed: "The source movement was reversed later",
    noMovements: "This customer owns no transferable movement in that period.",
    noTransfers: "No transfers yet.",
    saved: "Transfer saved as a draft. No accounting effect yet.",
    auditedToast: "Audited.",
    postedToast: "Posted. Ownership of the movements has moved.",
    reversedToast: "Transfer reversed and ownership returned.",
    confirmExecute: "A draft transfer will be created for the selected movements. Continue?",
    confirmPost: "The entry will post and ownership will move to the new customer. Continue?",
    confirmReverse: "This transfer will be undone by a counter-entry and ownership returned. Continue?",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
  },
  supplierTransactionTransfers: {
    title: "Supplier Transaction Transfer",
    breadcrumb: "Financial Management » Payables » Daily Transactions » Supplier Transaction Transfer",
    purposeNotice: "Moves previously posted financial movements from one supplier to another within a chosen historical period.",
    historyNotice: "Accounting history is never rewritten: no source document's supplier is changed and no posted ledger row is repointed. A NEW reclassification voucher is posted, and the original documents stay exactly as they were issued.",
    entryNotice: "For an unpaid invoice the entry is Dr the OLD supplier's payable / Cr the NEW supplier's — what is owed to the old one goes down and to the new one goes up. A movement that DEBITED the old supplier, such as a payment already made, moves the other way. The direction is read from the movement's own posted effect, never from the document type. When both share one control account its own net movement stays zero and only the subledger ownership moves.",
    readOnlyNotice: "You have view-only access.",
    selectionTitle: "Transferable movements",
    transfersTitle: "Transfers",
    detailTitle: "Transfer detail",
    transferDate: "Transfer date",
    oldSupplier: "Old supplier",
    newSupplier: "New supplier",
    fromDate: "From date",
    toDate: "To date",
    statement: "Statement",
    search: "Search",
    load: "Load movements",
    select: "Select",
    documentType: "Document type",
    documentNumber: "Document number",
    documentDate: "Document date",
    reference: "Reference",
    description: "Description",
    currency: "Currency",
    debit: "Debit",
    credit: "Credit",
    netEffect: "Net supplier effect",
    currentSupplier: "Current supplier",
    movementStatus: "Status",
    lastTransfer: "Last transfer",
    transferNumber: "Transfer number",
    movementCount: "Movements",
    totalEffect: "Total effect",
    sameControlAccount: "Same control account",
    differentControlAccounts: "Different control accounts",
    fiscalYear: "Fiscal year",
    status: "Status",
    execute: "Execute transfer",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    open: "Open",
    back: "Back",
    notSelectable: "Not transferable",
    sourceReversed: "The source movement was reversed later",
    noMovements: "This supplier owns no transferable movement in that period.",
    noTransfers: "No transfers yet.",
    saved: "Transfer saved as a draft. No accounting effect yet.",
    auditedToast: "Audited.",
    postedToast: "Posted. Ownership of the movements has moved.",
    reversedToast: "Transfer reversed and ownership returned.",
    confirmExecute: "A draft transfer will be created for the selected movements. Continue?",
    confirmPost: "The entry will post and ownership will move to the new supplier. Continue?",
    confirmReverse: "This transfer will be undone by a counter-entry and ownership returned. Continue?",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusReversed: "Reversed",
  },
  batchPrint: {
    select: "Select",
    selectAll: "Select all",
    clear: "Clear",
    selected: "Selected",
    printSelected: "Print selected",
    nothingSelected: "Choose at least one document.",
    preparing: "Preparing the print…",
    failed: "The print could not be produced.",
    hint: "Each document prints in full using its own template, starting on a new page.",
  },
  insuranceCompanies: {
    title: "Insurance Companies",
    breadcrumb: "Financial Management » Fixed Assets » Master Data » Insurance Companies",
    code: "Code",
    name: "Name",
    phone: "Phone",
    mobile: "Mobile",
    email: "Email",
    address: "Address",
    notes: "Notes",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    policies: "Policies",
    openCard: "Partner card",
    noRows: "No insurance companies.",
    total: "Total",
    search: "Search",
    searchHint: "Code or name",
    refresh: "Refresh",
    sectorHint: "A company is listed here when its business nature is the insurance sector, or when it insures an existing policy.",
  },
  cancelInvoiceSettlementScreen: {
    noParties: "No parties are available to you.",
    party: "Party",
    customer: "Customer",
    supplier: "Supplier",
    choose: "Choose",
    settlements: "Settlements",
    settlementNo: "Reconciliation No.",
    settledDate: "Date",
    invoice: "Outstanding Document",
    creditDocument: "Credit Document",
    amount: "Applied",
    currency: "Currency",
    state: "State",
    active: "Active",
    cancelled: "Cancelled",
    cancelAction: "Cancel Settlement",
    reasonPrompt: "Reason for cancelling the settlement",
    reasonRequired: "A cancellation reason is required.",
    confirm: "The document becomes outstanding again for the full applied amount. Continue?",
    cancelled_ok: "Settlement cancelled — the document is outstanding again.",
    noRows: "This party has no settlements.",
    pickParty: "Choose a party to see its settlements.",
    cancelledBy: "Cancelled by",
    reason: "Reason",
    refresh: "Refresh",
    title: "Cancel Invoice Settlement",
    breadcrumb: "Financial Management » Receipts » Daily Transactions » Cancel Invoice Settlement",
    scaffoldNotice: "This screen is defined only at this stage. No business behaviour has been implemented yet.",
  },
  accountReconciliations: {
    selectColumn: "Select",
    ctsTitle: "Customer Transaction Settlement",
    ctsBreadcrumb: "Financial Management » Receipts » Daily Transactions » Customer Transaction Settlement",
    ctsNotice: "Settles a customer's posted debit movements against their posted credit movements. It creates no journal entry and no ledger movement.",
    ctsBalanceNotice: "Saving requires the total debit settlement to equal the total credit settlement in local currency.",
    sequence: "Sequence", branch: "Branch", year: "Year", date: "Date",
    user: "User", notesLabel: "Notes",
    upToDate: "Up to date", category: "Category", inquiry: "Inquiry",
    saveAction: "Save", cancelAction: "Cancel",
    debitSection: "Debit Transaction Details", creditSection: "Credit Transaction Details",
    txnNumber: "Transaction number", txnDate: "Transaction date", txnType: "Transaction type",
    txnBranch: "Transaction branch",
    debitCurrencyCol: "Debit amount", creditCurrencyCol: "Credit amount", rate: "Exchange rate",
    prevSettleCurrency: "Previous settlement currency", prevSettleLocal: "Previous settlement local",
    thisSettleCurrency: "Current settlement currency", thisSettleLocal: "Current settlement local",
    remaining: "Remaining",
    totalCurrency: "Total settlement currency", totalLocal: "Total settlement local",
    balanced: "Balanced", unbalanced: "Not balanced",
    savedToast: "Settlement saved. No accounting entry was created.",
    pickCustomerFirst: "Choose a customer and press Inquiry.",
    customerTitle: "Customer Settlement",
    customerBreadcrumb: "Financial Management » Receivables » Customer Settlement",
    showSettled: "Show fully settled",
    noDueDateNotice: "This system has no due-date or payment-terms concept on an invoice, so the Due date column stays blank.",
    settlementAmount: "Settlement amount",
    settle: "Settle",
    title: "Account Reconciliation",
    breadcrumb: "Financial Management » Account Reconciliation",
    purposeNotice: "Match a party's outstanding documents against their payments and credit notes.",
    noAccountingNotice: "No accounting effect: both sides are already posted in the ledger. Reconciling records a settlement relationship only — no journal voucher, no balance movement, and no change to any posted document.",
    cancelNotice: "Cancelling a settlement does NOT cancel the invoice. The invoice, the receipt and their entries stay posted exactly as they are; the document simply becomes outstanding again and the payment becomes available again.",
    readOnlyNotice: "You have view-only access.",
    partyType: "Party type", customer: "Customer", supplier: "Supplier", party: "Party",
    currency: "Currency", fromDate: "Date from", toDate: "Date to", status: "Status",
    statusAll: "All", statusUnreconciled: "Unreconciled", statusPartial: "Partially reconciled",
    statusFull: "Fully reconciled",
    load: "Load", outstandingTitle: "Outstanding documents", creditsTitle: "Available payments",
    documentType: "Document type", documentNumber: "Document number", documentDate: "Document date",
    dueDate: "Due date",
    originalAmount: "Original amount", settledAmount: "Settled amount",
    outstandingAmount: "Outstanding amount", allocatedAmount: "Previously allocated",
    availableAmount: "Available amount", allocate: "Allocate",
    reconcile: "Reconcile", reconciled: "Reconciled", notes: "Notes",
    totalOutstanding: "Total outstanding", totalAvailable: "Total available",
    totalAllocating: "Total being allocated",
    historyTitle: "Reconciliation history", action: "Action",
    actionReconciled: "Reconciled", actionCancelled: "Settlement cancelled",
    cancelSettlement: "Cancel settlement", cancellationReason: "Cancellation reason",
    cancelConfirm: "Only this settlement will be cancelled. The invoice and the payment remain posted and unchanged. Continue?",
    cancelled: "Settlement cancelled.",
    invoiceTotal: "Invoice total", paymentInfoTitle: "Payment / settlement information",
    paymentNo: "Payment no.", paymentDate: "Payment date",
    paid: "Paid", partiallyPaid: "Partially paid", unpaid: "Unpaid",
    by: "By", at: "Date", reason: "Reason",
    noRows: "No documents match the filter.", noHistory: "No reconciliation history.",
    pickParty: "Choose a party and press Load.", save: "Save", close: "Close",
  },
  customerBalanceReminders: {
    title: "Customer Balance Reminder",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Customer Balance Reminder",
    purposeNotice: "Reminds customers of their outstanding balances. Balances are read from the subledger and are never changed by this screen.",
    noAccountingNotice: "This screen has NO accounting effect at all: no journal, no payable movement, no payment voucher, no payment order. Sending a reminder does not change what a supplier is owed.",
    transportNotice: "Note: in this build a message is handed to the notification service and written to the system log — no external mail provider is configured yet. There is no SMS channel in this system.",
    localModeNotice: "Processed in local notification mode — no external email was delivered.",
    localModeBadge: "Local notification mode",
    print: "Print",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    exportNotice: "Export is informational only: it sends no reminder, posts nothing, and is not a statement of account.",
    readOnlyNotice: "You have view-only access.",
    filtersTitle: "Filters",
    customersTitle: "Customers",
    reminderTitle: "Reminder",
    summaryTitle: "Summary",
    historyTitle: "Reminder History",
    search: "Search",
    customer: "Customer",
    currency: "Currency",
    collector: "Collector",
    salesRepresentative: "Sales Representative",
    category: "Customer Category",
    positiveOnly: "Owing customers only",
    minimumBalance: "Minimum Balance",
    apply: "Apply",
    reset: "Clear",
    select: "Select",
    selectAll: "Select all",
    code: "Customer Code",
    name: "Customer Name",
    email: "Email",
    mobile: "Mobile",
    balance: "Balance",
    lastReminder: "Last Reminder",
    status: "Status",
    noEmail: "No email address",
    noRows: "No customers match",
    count: "Count",
    channel: "Delivery Channel",
    channelEmail: "Email",
    template: "Message Template",
    subject: "Subject",
    message: "Message",
    preview: "Preview",
    previewTitle: "Message Preview",
    previewFor: "Preview for",
    placeholders: "Available variables",
    loadTemplate: "Load approved template",
    selectedCount: "Selected Customers",
    selectedBalance: "Total Selected Balance",
    sendAction: "Send Reminder",
    sendConfirm: "A reminder will be sent to {count} customers. Continue?",
    sending: "Sending…",
    batchNumber: "Reminder No.",
    batchDate: "Date",
    recipients: "Recipients",
    sent: "Processed",
    skipped: "Skipped",
    failed: "Failed",
    totalBalance: "Total Balance",
    createdBy: "Sent by",
    createdAt: "Sent at",
    statusPrepared: "Prepared",
    statusCompleted: "Completed",
    statusCompletedWithErrors: "Completed with exceptions",
    statusFailed: "Failed",
    deliverySent: "Processed locally",
    deliverySkipped: "Skipped",
    deliveryFailed: "Failed",
    reason: "Reason",
    destination: "Destination",
    snapshot: "Balance at send",
    viewBatch: "View",
    noBatches: "No previous reminders",
    back: "Back",
    refresh: "Refresh",
    none: "—",
    actions: "Actions",
    sendSuccess: "Reminder run completed",
    loadError: "Could not load the data",
    sendError: "Could not send the reminder",
    previewError: "Could not build the preview",
  },
  supplierBalanceReminders: {
    title: "Supplier Balance Reminder",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Supplier Balance Reminder",
    purposeNotice: "A balance confirmation: it sends the supplier what our books show as owed to them, for them to confirm. The balance is read from the subledger and stated from the COMPANY'S side — positive means the company owes the supplier — and is never changed by this screen.",
    noAccountingNotice: "This screen has NO accounting effect at all: no journal, no payable movement, no payment voucher, no payment order. Sending a reminder does not change what the company owes the supplier.",
    transportNotice: "Note: in this build a message is handed to the notification service and written to the system log — no external mail provider is configured yet. There is no SMS channel in this system.",
    localModeNotice: "Processed in local notification mode — no external email was delivered.",
    localModeBadge: "Local notification mode",
    print: "Print",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    exportNotice: "Export is informational only: it sends no reminder, posts nothing, and is not a statement of account.",
    readOnlyNotice: "You have view-only access.",
    filtersTitle: "Filters",
    suppliersTitle: "Suppliers",
    reminderTitle: "Reminder",
    summaryTitle: "Summary",
    historyTitle: "Reminder History",
    search: "Search",
    supplier: "Supplier",
    currency: "Currency",
    collector: "Collector",
    salesRepresentative: "Sales Representative",
    category: "Supplier Category",
    positiveOnly: "Owing suppliers only",
    minimumBalance: "Minimum Balance",
    apply: "Apply",
    reset: "Clear",
    select: "Select",
    selectAll: "Select all",
    code: "Supplier Code",
    name: "Supplier Name",
    email: "Email",
    mobile: "Mobile",
    balance: "Balance",
    lastReminder: "Last Reminder",
    status: "Status",
    noEmail: "No email address",
    noRows: "No suppliers match",
    count: "Count",
    channel: "Delivery Channel",
    channelEmail: "Email",
    template: "Message Template",
    subject: "Subject",
    message: "Message",
    preview: "Preview",
    previewTitle: "Message Preview",
    previewFor: "Preview for",
    placeholders: "Available variables",
    loadTemplate: "Load approved template",
    selectedCount: "Selected Suppliers",
    selectedBalance: "Total Selected Balance",
    sendAction: "Send Reminder",
    sendConfirm: "A reminder will be sent to {count} suppliers. Continue?",
    sending: "Sending…",
    batchNumber: "Reminder No.",
    batchDate: "Date",
    recipients: "Recipients",
    sent: "Processed",
    skipped: "Skipped",
    failed: "Failed",
    totalBalance: "Total Balance",
    createdBy: "Sent by",
    createdAt: "Sent at",
    statusPrepared: "Prepared",
    statusCompleted: "Completed",
    statusCompletedWithErrors: "Completed with exceptions",
    statusFailed: "Failed",
    deliverySent: "Processed locally",
    deliverySkipped: "Skipped",
    deliveryFailed: "Failed",
    reason: "Reason",
    destination: "Destination",
    snapshot: "Balance at send",
    viewBatch: "View",
    noBatches: "No previous reminders",
    back: "Back",
    refresh: "Refresh",
    none: "—",
    actions: "Actions",
    sendSuccess: "Reminder run completed",
    loadError: "Could not load the data",
    sendError: "Could not send the reminder",
    previewError: "Could not build the preview",
  },
  prepaidExpenses: {
    title: "Prepaid Expenses",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "An asset paid for in advance becoming an expense over time: annual insurance, rent, a licence. The schedule is built on a line of a POSTED purchase invoice and says when that asset becomes a cost.",
    postingNotice: "Creating a schedule posts nothing at all. Only the periodic recognition posts: Dr expense, Cr prepaid — one voucher per period, dated at that period's end. Future periods are never recognized, and a mistake is corrected by reversal, never by deletion.",
    noPayableNotice: "The supplier is not charged twice: the purchase invoice raised the payable, and recognition never touches the supplier's balance.",
    readOnlyNotice: "You have view-only access.",
    noSuppliersInScope: "No supplier is within your daily-transaction access. It is granted under Payments → Master Data → User–Supplier Access.",
    listTitle: "Schedules",
    formTitle: "Schedule details",
    periodsTitle: "Periods",
    newRecord: "New schedule",
    search: "Search",
    searchHint: "Schedule, invoice or supplier",
    refresh: "Refresh",
    noRows: "No schedules.",
    stateFilter: "State",
    allStates: "All states",
    totalCount: "Count",
    totalOriginal: "Original total",
    totalRecognized: "Recognized total",
    number: "Schedule no.",
    date: "Date",
    supplier: "Supplier",
    sourceInvoice: "Source invoice",
    chooseInvoice: "Choose a posted purchase invoice",
    sourceLine: "Source line",
    chooseLine: "Choose a line",
    lineNet: "Line net",
    available: "Available to schedule",
    alreadyScheduled: "Already scheduled",
    returned: "Returned",
    amount: "Scheduled amount",
    prepaidAccount: "Prepaid account",
    expenseAccount: "Expense account",
    chooseAccount: "Choose an account",
    costCenter: "Cost center",
    none: "None",
    coverageStart: "Coverage start",
    coverageEnd: "Coverage end",
    basis: "Recognition basis",
    basisMonthly: "Monthly",
    periodCount: "Periods",
    currency: "Currency",
    exchangeRate: "Exchange rate",
    notes: "Notes",
    original: "Original",
    recognized: "Recognized",
    remaining: "Remaining",
    state: "State",
    draft: "Draft",
    active: "Active",
    partiallyRecognized: "Partially recognized",
    fullyRecognized: "Fully recognized",
    cancelled: "Cancelled",
    periodNumber: "Period",
    periodFrom: "From",
    periodTo: "To",
    recognitionDate: "Recognition date",
    scheduled: "Scheduled",
    periodRecognized: "Recognized",
    periodRemaining: "Remaining",
    voucher: "Voucher",
    periodState: "State",
    due: "Due",
    notDue: "Not due",
    reversedLabel: "Reversed",
    save: "Save",
    cancelEdit: "Cancel",
    edit: "Edit",
    view: "View",
    activate: "Activate",
    recognize: "Recognize due periods",
    recognizeOne: "Recognize",
    reverse: "Reverse",
    cancelSchedule: "Cancel schedule",
    cancelReason: "Cancellation reason",
    confirmCancel: "Confirm cancellation",
    reversalDate: "Reversal date",
    reversalReason: "Reversal reason",
    confirmReverse: "Confirm reversal",
    close: "Close",
    details: "Schedule details",
    createdBy: "Created by",
    createdAt: "Created",
    cancelledBy: "Cancelled by",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "The schedule was saved.",
    activatedOk: "The schedule was activated.",
    recognizedOk: "The due periods were recognized.",
    reversedOk: "The reversal was created.",
    cancelledOk: "The schedule was cancelled.",
    skippedLabel: "Periods left alone",
    loadError: "The schedules could not be loaded.",
    saveError: "The schedule could not be saved.",
    activateError: "The schedule could not be activated.",
    recognizeError: "The periods could not be recognized.",
    reverseError: "The reversal could not be created.",
    cancelError: "The schedule could not be cancelled.",
    exportFailed: "The file could not be produced.",
  },
  supplierMaster: {
    infoTitle: "Supplier Information Adjustment",
    infoBreadcrumb: "Financial Management / Payments / Daily Transactions",
    infoPurpose: "An audited change to a supplier's details: current value, proposed value, the reason, and who changed it when.",
    infoNoAccounting: "No accounting effect: no journal, no party balance. And nothing historical is rewritten — an invoice issued last year keeps the address it was issued to.",
    infoDelegation: "Every rule is the supplier card's own: a duplicate tax number, a control account that must accept postings, and the rest. This screen has no rules of its own. The credit limit cannot be changed here at all — it has its own screen and its own permission.",
    limitTitle: "Supplier Credit Limit Adjustment",
    limitBreadcrumb: "Financial Management / Payments / Daily Transactions",
    limitPurpose: "Moves the credit limit and keeps a permanent record of every move: previous, new, the difference, the reason, and who made it.",
    limitNoAccounting: "No accounting effect, no credit scoring and no purchasing block. It records a ceiling and who set it.",
    readOnlyNotice: "You have view-only access.",
    noSuppliersInScope: "No supplier is within your daily-transaction access. It is granted under Payments → Master Data → User–Supplier Access.",
    supplier: "Supplier",
    chooseSupplier: "Choose a supplier",
    currentValue: "Current value",
    proposedValue: "Proposed value",
    reason: "Reason",
    apply: "Apply change",
    refresh: "Refresh",
    noChanges: "No new value was entered.",
    historyTitle: "Change history",
    field: "Field",
    oldValue: "Before",
    newValue: "After",
    changedBy: "By",
    changedAt: "When",
    noHistory: "No changes recorded.",
    name: "Name",
    nameAr: "Arabic name",
    startDate: "Dealing start date",
    controlAccount: "Control account",
    currency: "Currency",
    taxTreatment: "Tax treatment",
    paymentTerm: "Payment term",
    phone: "Phone",
    mobile: "Mobile",
    fax: "Fax",
    email: "Email",
    website: "Website",
    contactPerson: "Contact person",
    taxNumber: "Tax number",
    commercialRegistration: "Commercial registration",
    nationalIdCompany: "Company national ID",
    referenceNumber: "Reference",
    country: "Country",
    city: "City",
    area: "Area",
    street: "Street",
    building: "Building",
    postalCode: "Postal code",
    poBox: "PO box",
    address: "Address",
    addressNotes: "Address notes",
    notes: "Notes",
    isActive: "Active",
    isBlacklisted: "Blacklisted",
    blacklistReason: "Blacklist reason",
    creditLimitReadOnly: "Credit limit (changed on its own screen)",
    currentLimit: "Current limit",
    newLimit: "New limit",
    changeAmount: "Difference",
    balance: "Balance",
    balanceExceeds: "Balance exceeds the limit",
    changeLimit: "Change limit",
    limitHistory: "Credit limit history",
    previousLimit: "Previous limit",
    keep: "Unchanged",
    clear: "Clear",
    unchanged: "Unchanged",
    savedFields: "field(s) changed",
    limitChanged: "The credit limit was changed.",
    loadError: "The supplier could not be loaded.",
    saveError: "The change could not be applied.",
    limitError: "The credit limit could not be changed.",
  },
  cheques: {
    title: "Received Cheques",
    breadcrumb: "Financial Management » Receivables » Daily Transactions » Received Cheques",
    purposeNotice: "Tracks cheques taken in on receipt vouchers, from arrival to collection or return.",
    readOnlyNotice: "You have view-only access.",
    noActionNotice: "No financial action is available on this cheque.",
    filtersTitle: "Filters",
    listTitle: "Cheques",
    detailTitle: "Cheque Details",
    chequeNumber: "Cheque No.",
    drawer: "Customer / Drawer",
    drawerBank: "Drawer Bank",
    bankBranch: "Bank Branch",
    chequeDate: "Cheque Date",
    dueDate: "Due Date",
    dueFrom: "Due From",
    dueTo: "Due To",
    amount: "Amount",
    currency: "Currency",
    cashbox: "Cashbox",
    status: "Status",
    sourceVoucher: "Source Receipt Voucher",
    clearedTo: "Cleared To",
    clearedDate: "Cleared Date",
    settlementVoucher: "Settlement Entry",
    notes: "Notes",
    allStatuses: "All statuses",
    statusUnderCollection: "Under Collection",
    statusDeposited: "Deposited",
    statusCleared: "Cleared",
    statusBounced: "Bounced",
    statusReturned: "Returned",
    depositAction: "Deposit for Collection",
    clearAction: "Clear",
    bounceAction: "Bounce",
    returnAction: "Return to Customer",
    depositTitle: "Deposit Cheque for Collection",
    clearTitle: "Clear Cheque",
    bounceTitle: "Bounce Cheque",
    returnTitle: "Return Cheque to Customer",
    depositConfirm: "The cheque will be recorded as lodged with a bank for collection. Operational only: no journal is written, because the value stays in the cheques-in-collection account.",
    clearConfirm: "The cheque will be collected into the selected bank account, posting Dr Bank / Cr Cheques in Collection.",
    bounceConfirm: "The bank returned this cheque unpaid. The customer receivable will be restored by this cheque's amount only. The receipt voucher itself is NOT reversed.",
    returnConfirm: "The cheque will be handed back to the customer without being collected. The customer receivable will be restored by this cheque's amount only. This is final and cannot be undone.",
    bankAccount: "Bank Account",
    chooseBankAccount: "Choose the receiving bank account",
    clearedOn: "Cleared Date",
    actionNotes: "Notes",
    confirm: "Confirm",
    cancel: "Cancel",
    deposited: "Cheque deposited for collection",
    cleared: "Cheque cleared",
    bounced: "Cheque bounced; customer receivable restored",
    returned: "Cheque returned to the customer; receivable restored",
    apply: "Apply",
    reset: "Clear",
    refresh: "Refresh",
    search: "Cheque number",
    noRows: "No cheques",
    count: "Count",
    choose: "All",
    none: "—",
    actions: "Actions",
    view: "View",
    close: "Close",
    loadError: "Could not load cheques",
    actionError: "Could not complete the action",
  },
  receiptBooks: {
    title: "Receipt Books",
    breadcrumb: "Financial Management › Receivables › Daily Transactions › Receipt Books",
    purposeNotice: "Control of physical receipt-number books: registering them, handing them to an employee, and taking them back.",
    noAccountingNotice: "This screen has NO accounting impact at all: no journal, no cash movement, no receivable. Money arrives with a Receipt Voucher, which is a different document.",
    readOnlyNotice: "You have view-only access.",
    closedNotice: "This book is closed and cannot be handed out.",
    noHoldersNotice: "This system has no master file for that kind of holder, so there are no names to offer. Only employees can hold a book today.",
    bookTitle: "Book details",
    handoverTitle: "Hand over / take back",
    historyTitle: "Movement history",
    listTitle: "Receipt Books",
    newBook: "New Book",
    bookNumber: "Book No.",
    fromNumber: "From Receipt No.",
    toNumber: "To Receipt No.",
    receiptCount: "Receipt Count",
    receivedDate: "Received Date",
    status: "Status",
    statusAvailable: "Available",
    statusAssigned: "Assigned",
    statusClosed: "Closed",
    allStatuses: "— All statuses —",
    currentHolder: "Current Holder",
    holderType: "Holder Type",
    holder: "Holder",
    holderEmployee: "Employee",
    holderCollector: "Collector",
    holderSalesRep: "Sales Representative",
    assignmentDate: "Assignment Date",
    branch: "Branch",
    notes: "Notes",
    lastMovement: "Last Movement",
    assignAction: "Assign Book",
    receiveAction: "Receive Book",
    closeAction: "Close Book",
    closeConfirm: "Close this book? It cannot be handed out afterwards.",
    save: "Save",
    moveAction: "Action",
    moveFrom: "From",
    moveTo: "To",
    moveDate: "Date",
    movePerformedBy: "Performed By",
    moveReceived: "New book received",
    moveAssigned: "Assigned",
    moveReturned: "Received back",
    moveClosed: "Closed",
    company: "Company",
    openBook: "Open",
    refresh: "Refresh",
    search: "Search",
    noRows: "No receipt books",
    count: "Count",
    choose: "— Select —",
    none: "None",
    actions: "Actions",
    saved: "Saved",
    assigned: "Book handed over",
    received: "Book received back",
    closed: "Book closed",
    loadError: "The receipt books could not be loaded",
    saveError: "The book could not be saved",
    actionError: "The action could not be completed",
  },
  salesPurchaseTax: {
    title: "Sales & Purchase Tax Types",
    breadcrumb: "Financial Management › Receivables › Settings › Sales & Purchase Tax Types",
    purposeNotice: "The approved sales and purchase tax catalogue, each type with the rate in force for it.",
    reuseNotice:
      "This screen reads and writes the SAME tax catalogue as the General Ledger tax settings — there is no second tax engine, and the permissions are the existing tax rights.",
    readOnlyNotice: "You have view-only access. Nothing here can be changed.",
    formTitle: "Tax type",
    gridTitle: "Sales & Purchase Tax Types",
    newRecord: "New Type",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    fromDate: "From Date",
    toDate: "To Date",
    rate: "Rate %",
    category: "Tax Category",
    side: "Tax Side",
    sideFromCategory: "— From tax category —",
    sideSales: "Sales",
    sidePurchase: "Purchase",
    account: "Account",
    debitAccount: "Debit Account",
    creditAccount: "Credit Account",
    active: "Active",
    defaultForSales: "Default for Sales",
    actions: "Actions",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    search: "Search",
    refresh: "Refresh",
    showInactive: "Show inactive",
    noRows: "No tax types",
    counts: "Total / Active",
    save: "Save",
    saveNew: "Save & New",
    cancel: "Cancel",
    choose: "— Select —",
    none: "None",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    close: "Close",
    saved: "Saved",
    activated: "Activated",
    deactivated: "Deactivated",
    defaultHint: "Only one type may be the sales default, company-wide.",
    loadError: "The tax types could not be loaded",
    saveError: "The tax type could not be saved",
    auditError: "The audit history could not be loaded",
  },
  agingPeriods: {
    title: "Receivables Aging Periods",
    breadcrumb: "Financial Management › Receivables › Settings › Receivables Aging Periods",
    purposeNotice: "The periods receivables age into. This screen is definition only.",
    emptyNotice:
      "No periods are defined and none were seeded. Periods are entered here and nowhere else.",
    notCalculatedNotice:
      "This build has no aging engine. The definitions are stored; nothing is calculated yet.",
    readOnlyNotice: "You have view-only access. Nothing here can be changed.",
    gridTitle: "Periods",
    formTitle: "Period details",
    newRow: "New Period",
    sequence: "Period Sequence",
    fromDay: "From Day",
    toDay: "To Day",
    actions: "Actions",
    edit: "Edit",
    remove: "Remove",
    removeConfirm: "Remove this period?",
    refresh: "Refresh",
    save: "Save",
    saveNew: "Save & New",
    cancel: "Cancel",
    noRows: "No periods are defined",
    count: "Periods",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    close: "Close",
    saved: "Saved",
    removed: "Removed",
    loadError: "The aging periods could not be loaded",
    saveError: "The period could not be saved",
    auditError: "The audit history could not be loaded",
  },
  paymentOrders: {
    title: "Payment Order", breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "An instruction to pay a supplier. An operational document with no accounting effect.",
    derivedNotice:
      "Paid and Remaining are computed from the payment vouchers that actually posted; neither is stored. Reversing a voucher restores the amount on the order by itself, because the total is read afresh each time.",
    readOnlyNotice: "You have read-only access.",
    noSuppliersInScope:
      "No supplier falls inside your daily-transaction access. Access is granted under Payments › Master Data › User–Supplier Access.",
    listTitle: "Orders", formTitle: "Order details", newRecord: "New order",
    search: "Search", searchHint: "Order number, reference or supplier", refresh: "Refresh",
    noRows: "No orders.", statusFilter: "Status", allStatuses: "All statuses",
    payableOnly: "Payable only",
    number: "Order No.", date: "Date", supplier: "Supplier", chooseSupplier: "Choose a supplier",
    requestedAmount: "Requested", paidAmount: "Paid", remainingAmount: "Remaining",
    currency: "Currency", exchangeRate: "Exchange rate", branch: "Branch",
    expectedDate: "Expected payment date", expectedMethod: "Preferred payment method",
    responsible: "Responsible", reference: "Reference", notes: "Notes", none: "None",
    status: "Status", open: "Open", cancelled: "Cancelled",
    progress: "Payment", unpaid: "Unpaid", partiallyPaid: "Partially paid", paid: "Paid",
    save: "Save", cancelEdit: "Cancel edit", edit: "Edit", view: "View",
    cancelOrder: "Cancel order", cancelReason: "Cancellation reason",
    confirmCancel: "Confirm cancellation", close: "Close", details: "Order details",
    createdAt: "Created at",
    vouchers: "Linked payment vouchers", voucherNumber: "Voucher No.", voucherDate: "Date",
    voucherStatus: "Status", voucherAmount: "Amount",
    effective: "Counts", notEffective: "Does not count", noVouchers: "No linked vouchers.",
    createVoucher: "Create payment voucher",
    saved: "Order saved.", cancelledOk: "Order cancelled.",
    loadError: "The orders could not be loaded.", saveError: "The order could not be saved.",
    cancelError: "The order could not be cancelled.", totalCount: "Orders",
  },
  paymentVouchers: {
    settlementTitle: "Invoice Settlement",
    settlementHint: "Choose the invoices this payment settles.",
    settlementInvoice: "Document No.",
    settlementSupplierInvoice: "Supplier Invoice No.",
    settlementDate: "Invoice Date",
    settlementDue: "Due Date",
    settlementOriginal: "Original Amount",
    settlementSettled: "Previously Settled",
    settlementOutstanding: "Outstanding",
    settlementAmount: "Amount to Settle",
    settlementAllocated: "Allocated",
    settlementUnapplied: "Unapplied",
    settlementNone: "This supplier has no open invoices.",
    settlementOverPayment: "The invoices settled exceed the payment amount.",
    settlementEffectiveOnPost: "Settlement takes effect when the voucher is posted.",
    settlementLoading: "Loading open invoices…",
    title: "Payment Voucher", breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "The accounting document for money going out.",
    postingNotice:
      "The entry is Dr supplier payable (or the chosen account on a direct payment), Cr cash or bank according to the payment method. Lifecycle: Draft → Audited → Posted, and whoever entered the voucher may not approve it. Posted vouchers are undone by an official reversal only.",
    chequeNotice:
      "Outgoing cheques are not supported in this build: the existing cheque register is for cheques received. Choosing a cheque payment method is refused outright and never posted as cash or a transfer.",
    readOnlyNotice: "You have read-only access.",
    noSuppliersInScope:
      "No supplier falls inside your daily-transaction access. Access is granted under Payments › Master Data › User–Supplier Access.",
    listTitle: "Vouchers", formTitle: "Voucher details", linesTitle: "Payment sources",
    newRecord: "New voucher", search: "Search", searchHint: "Voucher number, reference or supplier",
    refresh: "Refresh", noRows: "No vouchers.", statusFilter: "Status", allStatuses: "All statuses",
    number: "Voucher No.", date: "Date", mode: "Payment type",
    modeSupplier: "Pay a supplier", modeDirect: "Direct payment",
    supplier: "Supplier", chooseSupplier: "Choose a supplier",
    directAccount: "Account", chooseAccount: "Choose an account",
    paymentOrder: "Payment order", noOrder: "No order", currency: "Currency",
    exchangeRate: "Exchange rate", branch: "Branch", costCenter: "Cost center",
    reference: "Reference", notes: "Notes", none: "None", amount: "Amount",
    paymentMethod: "Payment method", chooseMethod: "Choose a method", lineAmount: "Amount",
    lineNotes: "Notes", addLine: "Add source", removeLine: "Remove", total: "Total",
    status: "Status", draft: "Draft", audited: "Audited", posted: "Posted", reversed: "Reversed",
    save: "Save", cancelEdit: "Cancel edit", edit: "Edit", view: "View",
    audit: "Audit", post: "Post", reverse: "Reverse", auditNotes: "Audit notes",
    reversalDate: "Reversal date", reversalReason: "Reason",
    confirmReverse: "Confirm reversal", close: "Close",
    details: "Voucher details", createdAt: "Created at", remainingOnOrder: "Remaining on order",
    saved: "Voucher saved.", auditedOk: "Voucher audited.", postedOk: "Voucher posted.",
    reversedOk: "Reversal posted.",
    loadError: "The vouchers could not be loaded.", saveError: "The voucher could not be saved.",
    auditError: "The voucher could not be audited.", postError: "The voucher could not be posted.",
    reverseError: "The reversal could not be posted.",
    totalCount: "Vouchers", totalPosted: "Total posted",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    exportFailed: "The file could not be produced.",
  },
  supplierDebitNotes: {
    title: "Supplier Debit Note",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "An adjustment charged to a supplier that reduces what is owed to them: an agreed rebate, a penalty, or a correction to an overcharge.",
    postingNotice: "The entry is Dr supplier payable, Cr each line's own offset account, and Cr input tax to give back what was claimed. The cycle is Draft → Audited → Posted. Nothing reaches the ledger before posting, and a mistake is corrected by reversal, never by deletion.",
    lineageNotice: "Citing a posted purchase invoice is for reference only: the invoice is not reopened, its entry is not altered, and nothing is taken off the return availability it carries. Several notes may cite the same invoice.",
    readOnlyNotice: "You have view-only access.",
    noSuppliersInScope: "No supplier is within your daily-transaction access. It is granted under Payments → Master Data → User–Supplier Access.",
    listTitle: "Notes",
    formTitle: "Note details",
    linesTitle: "Lines",
    newRecord: "New note",
    search: "Search",
    searchHint: "Note number or reference",
    refresh: "Refresh",
    noRows: "No notes.",
    statusFilter: "Status",
    allStatuses: "All statuses",
    totalCount: "Count",
    totalPosted: "Posted total",
    number: "Note no.",
    date: "Date",
    supplier: "Supplier",
    chooseSupplier: "Choose a supplier",
    sourceInvoice: "Source invoice (reference only)",
    noSourceInvoice: "No invoice",
    currency: "Currency",
    exchangeRate: "Exchange rate",
    branch: "Branch",
    costCenter: "Cost center",
    reference: "Reference",
    notes: "Notes",
    none: "None",
    taxInclusive: "Amounts include tax",
    description: "Description",
    offsetAccount: "Offset account",
    chooseAccount: "Choose an account",
    amount: "Amount",
    taxType: "Tax type",
    noTax: "No tax",
    taxPercent: "Rate",
    tax: "Tax",
    lineTotal: "Line total",
    lineNotes: "Line notes",
    addLine: "Add line",
    removeLine: "Remove",
    net: "Net",
    total: "Total",
    status: "Status",
    draft: "Draft",
    audited: "Audited",
    posted: "Posted",
    reversed: "Reversed",
    save: "Save",
    cancelEdit: "Cancel",
    edit: "Edit",
    view: "View",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    auditNotes: "Audit notes",
    reversalDate: "Reversal date",
    reversalReason: "Reversal reason",
    confirmReverse: "Confirm reversal",
    close: "Close",
    details: "Note details",
    createdAt: "Created",
    createdBy: "Created by",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "The note was saved.",
    auditedOk: "The note was audited.",
    postedOk: "The note was posted.",
    reversedOk: "The reversal was created.",
    loadError: "The notes could not be loaded.",
    saveError: "The note could not be saved.",
    auditError: "The note could not be audited.",
    postError: "The note could not be posted.",
    reverseError: "The reversal could not be created.",
    exportFailed: "The file could not be produced.",
  },
  supplierCreditNotes: {
    title: "Supplier Credit Note",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "An adjustment credited to a supplier that increases what is owed to them: an undercharge corrected, an agreed price rise, or freight and handling raised after the fact.",
    postingNotice: "The entry is Dr each line's own offset account and Dr input tax, Cr supplier payable — the exact inverse of the debit note. The cycle is Draft → Audited → Posted. Nothing reaches the ledger before posting, and a mistake is corrected by reversal, never by deletion.",
    lineageNotice: "Citing a posted purchase invoice is for reference only: the invoice is not reopened, its entry is not altered, and nothing is ADDED to the return availability it carries — otherwise the same money could be returned twice. Several notes may cite the same invoice.",
    readOnlyNotice: "You have view-only access.",
    noSuppliersInScope: "No supplier is within your daily-transaction access. It is granted under Payments → Master Data → User–Supplier Access.",
    listTitle: "Notes",
    formTitle: "Note details",
    linesTitle: "Lines",
    newRecord: "New note",
    search: "Search",
    searchHint: "Note number or reference",
    refresh: "Refresh",
    noRows: "No notes.",
    statusFilter: "Status",
    allStatuses: "All statuses",
    totalCount: "Count",
    totalPosted: "Posted total",
    number: "Note no.",
    date: "Date",
    supplier: "Supplier",
    chooseSupplier: "Choose a supplier",
    sourceInvoice: "Source invoice (reference only)",
    noSourceInvoice: "No invoice",
    currency: "Currency",
    exchangeRate: "Exchange rate",
    branch: "Branch",
    costCenter: "Cost center",
    reference: "Reference",
    notes: "Notes",
    none: "None",
    taxInclusive: "Amounts include tax",
    description: "Description",
    offsetAccount: "Offset account",
    chooseAccount: "Choose an account",
    amount: "Amount",
    taxType: "Tax type",
    noTax: "No tax",
    taxPercent: "Rate",
    tax: "Tax",
    lineTotal: "Line total",
    lineNotes: "Line notes",
    addLine: "Add line",
    removeLine: "Remove",
    net: "Net",
    total: "Total",
    status: "Status",
    draft: "Draft",
    audited: "Audited",
    posted: "Posted",
    reversed: "Reversed",
    save: "Save",
    cancelEdit: "Cancel",
    edit: "Edit",
    view: "View",
    audit: "Audit",
    post: "Post",
    reverse: "Reverse",
    auditNotes: "Audit notes",
    reversalDate: "Reversal date",
    reversalReason: "Reversal reason",
    confirmReverse: "Confirm reversal",
    close: "Close",
    details: "Note details",
    createdAt: "Created",
    createdBy: "Created by",
    printAction: "Print",
    pdfAction: "PDF",
    excelAction: "Excel",
    saved: "The note was saved.",
    auditedOk: "The note was audited.",
    postedOk: "The note was posted.",
    reversedOk: "The reversal was created.",
    loadError: "The notes could not be loaded.",
    saveError: "The note could not be saved.",
    auditError: "The note could not be audited.",
    postError: "The note could not be posted.",
    reverseError: "The reversal could not be created.",
    exportFailed: "The file could not be produced.",
  },
  supplierAccountSettlement: {
    title: "Supplier Account Settlement",
    breadcrumb: "Financial Management » Payments » Daily Transactions » Supplier Account Settlement",
    notice: "Matches a supplier's outstanding invoices against the payments and notes that pay them off. It creates no journal entry and moves nothing in the ledger.",
    partyLabel: "Supplier",
    pickPartyFirst: "Choose a supplier, then press Inquire.",
    polarityNotice: "On the payables side the direction is inverted: a purchase invoice is CREDITED to the supplier and is still the thing to settle, while a payment voucher is DEBITED and is the thing that settles. The engine reads the direction from the party type itself.",
    noSuppliersInScope: "No supplier is within your daily-transaction access. It is granted under Payments → Master Data → User–Supplier Access.",
    cancelTitle: "Cancel Purchase Invoice Settlement",
    cancelBreadcrumb: "Financial Management » Payments » Daily Transactions » Cancel Invoice Settlement",
    cancelNotice: "Cancelling a settlement reopens the amount on the invoice and on the payment alike. The line is not deleted: it stays on record with the reason and who withdrew it.",
  },
  paymentBooks: {
    title: "Payment Books Inquiry",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "The register of pre-printed payment-voucher books: the range, who holds each one, and when.",
    noAccountingNotice:
      "No accounting effect at any stage. A book is stationery under control; money leaves on a payment voucher, which is a different document. A voucher takes its number from the shared generator keyed by voucher type, never from a book.",
    historyNotice:
      "The movement history is never edited or deleted: correcting a mistaken hand-over means recording the return, not erasing the assignment. There is no delete — a book is closed.",
    readOnlyNotice: "You have read-only access.",
    listTitle: "Books", formTitle: "Book details", newRecord: "New book",
    search: "Search", searchHint: "Book number or notes", refresh: "Refresh",
    noRows: "No books.",
    statusFilter: "Status", allStatuses: "All statuses",
    counts: "Total / Available / Assigned", numbers: "Numbers",
    bookNumber: "Book No.", fromNumber: "From", toNumber: "To", count: "Count",
    branch: "Branch", none: "None", receivedDate: "Received",
    assignedDate: "Assigned", returnedDate: "Returned",
    holder: "Holder", chooseHolder: "Choose a holder", notes: "Notes",
    status: "Status", available: "Available", assigned: "Assigned", closed: "Closed",
    actions: "Actions", edit: "Edit", view: "View",
    assign: "Hand over", takeBack: "Take back", close: "Close",
    save: "Save", cancelEdit: "Cancel", movementDate: "Movement date",
    movementNotes: "Movement notes", confirm: "Confirm",
    history: "Movement history", historyAction: "Movement", historyFrom: "From",
    historyTo: "To", historyDate: "Date", historyBy: "By", historyNotes: "Notes",
    actionReceived: "Received into the company", actionAssigned: "Handed over",
    actionReturned: "Taken back from holder", actionClosed: "Closed",
    details: "Book details",
    noHolders: "No active personnel record exists to hand a book to. Add one under Payments › Master Data.",
    saved: "Book saved.", movedOk: "Movement recorded.",
    loadError: "The books could not be loaded.", saveError: "The book could not be saved.",
    moveError: "The movement could not be recorded.", totalCount: "Books",
  },
  servicePurchaseReturn: {
    title: "Service Purchase Return",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "A financial return against a posted service purchase invoice.",
    postingNotice:
      "The entry is the invoice's exact inversion: Dr supplier payable, Cr expense and Cr input tax. Lifecycle: Draft → Audited → Posted, undone by an official reversal.",
    boundNotice:
      "The return is bound to its invoice: supplier, currency, exchange rate, accounts and tax are read from the original invoice lines and are never chosen. You choose only which lines and how much of each. Reversing a return makes its quantity returnable again.",
    readOnlyNotice: "You have read-only access.",
    listTitle: "Returns", formTitle: "Return details", linesTitle: "Returnable lines",
    newRecord: "New return", search: "Search", searchHint: "Return number, reference or supplier",
    refresh: "Refresh", noRows: "No returns.",
    dateFrom: "Date from", dateTo: "Date to", statusFilter: "Status", allStatuses: "All statuses",
    number: "Return No.", date: "Date", sourceInvoice: "Purchase invoice",
    chooseInvoice: "Choose a posted invoice", supplier: "Supplier", currency: "Currency",
    reference: "Reference", notes: "Notes", costCenter: "Cost center", none: "None",
    branch: "Branch",
    lineNo: "#", expenseAccount: "Expense account", lineDescription: "Description",
    originalQuantity: "Invoiced", returnedQuantity: "Already returned",
    remainingQuantity: "Remaining", returnQuantity: "Return quantity",
    unitPrice: "Unit price", taxPercent: "Tax %",
    netAmount: "Net", taxAmount: "Tax", totalAmount: "Total",
    status: "Status", draft: "Draft", audited: "Audited", posted: "Posted", reversed: "Reversed",
    save: "Save", cancelEdit: "Cancel edit", edit: "Edit", view: "View",
    audit: "Audit", post: "Post", reverse: "Reverse", auditNotes: "Audit notes",
    reversalDate: "Reversal date", reversalReason: "Reason",
    confirmReverse: "Confirm reversal", close: "Close",
    details: "Return details", createdAt: "Created at",
    saved: "Return saved.", auditedOk: "Return audited.",
    postedOk: "Return posted.", reversedOk: "Reversal posted.",
    loadError: "The returns could not be loaded.", saveError: "The return could not be saved.",
    auditError: "The return could not be audited.", postError: "The return could not be posted.",
    reverseError: "The reversal could not be posted.", totalCount: "Returns",
    print: "Print", exportPdf: "PDF", exportExcel: "Excel",
    exportError: "The file could not be produced.",
    nothingReturnable: "Nothing on this invoice is still returnable.",
    invoiceNotPosted: "Only a posted invoice can be returned against.",
    pickInvoiceFirst: "Choose a posted purchase invoice to see its returnable lines.",
  },
  servicePurchaseInvoice: {
    title: "Service Purchase Invoice",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "An invoice for a service bought from a supplier. A full accounting document.",
    postingNotice:
      "Lifecycle: Draft → Audited → Posted. Nothing reaches the general ledger before posting, and whoever entered the document may not approve it. Posted invoices are undone by an official reversal — never deleted.",
    accountNotice:
      "The entry is Dr expense (per line) and Dr input tax, Cr supplier payable (the supplier's control account). Each line chooses its expense account from the chart of accounts — no revenue account and no fixed account is used.",
    readOnlyNotice: "You have read-only access.",
    scopeNotice: "Only invoices for suppliers you hold daily-transaction access to are shown.",
    noSuppliersInScope:
      "No supplier falls inside your daily-transaction access, so an invoice cannot be raised. Access is granted under Payments › Master Data › User–Supplier Access.",
    listTitle: "Invoices", formTitle: "Invoice details", linesTitle: "Lines",
    newRecord: "New invoice", search: "Search",
    searchHint: "Invoice number, supplier invoice number or supplier",
    refresh: "Refresh", noRows: "No invoices.",
    dateFrom: "Date from", dateTo: "Date to", statusFilter: "Status", allStatuses: "All statuses",
    number: "Invoice No.", date: "Date", supplier: "Supplier", chooseSupplier: "Choose a supplier",
    supplierInvoiceNumber: "Supplier invoice No.", sourceRequest: "Purchase request",
    currency: "Currency", exchangeRate: "Exchange rate", branch: "Branch",
    paymentMethod: "Payment method", costCenter: "Cost center", none: "None",
    notes: "Notes", pricing: "Pricing",
    taxInclusive: "Prices include tax", taxExclusive: "Prices exclude tax",
    expenseAccount: "Expense account", chooseAccount: "Choose an account",
    lineDescription: "Description", unit: "Unit", quantity: "Quantity", unitPrice: "Unit price",
    discountPercent: "Discount %", taxType: "Tax type",
    lineGross: "Gross", lineDiscount: "Discount", lineTax: "Tax", lineNet: "Net",
    addLine: "Add line", removeLine: "Remove",
    taxComputedOnSave: "Tax is computed on the server when the invoice is saved.",
    subtotal: "Subtotal", discountTotal: "Total discount",
    taxTotal: "Total tax", netTotal: "Net total",
    status: "Status", draft: "Draft", audited: "Audited", posted: "Posted",
    rejected: "Rejected", reversed: "Reversed",
    save: "Save", cancelEdit: "Cancel edit", edit: "Edit", view: "View",
    audit: "Audit", post: "Post", reverse: "Reverse", auditNotes: "Audit notes",
    reversalDate: "Reversal date", reversalReason: "Reason",
    confirmReverse: "Confirm reversal", close: "Close",
    details: "Invoice details", createdAt: "Created at", settled: "Settled",
    saved: "Invoice saved.", auditedOk: "Invoice audited.",
    postedOk: "Invoice posted.", reversedOk: "Reversal posted.",
    loadError: "The invoices could not be loaded.", saveError: "The invoice could not be saved.",
    auditError: "The invoice could not be audited.", postError: "The invoice could not be posted.",
    reverseError: "The reversal could not be posted.", totalCount: "Invoices",
    print: "Print", exportPdf: "PDF", exportExcel: "Excel",
    exportError: "The file could not be produced.",
  },
  servicePurchaseRequest: {
    title: "Service Purchase Request",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice:
      "A request to buy a service from a supplier. An operational, pre-accounting document: it records what is wanted and what it is estimated to cost, and commits nobody to anything.",
    noAccountingNotice:
      "No accounting effect at any stage: no journal, no payable, no posted tax. A request becomes money only when a Service Purchase Invoice is raised, and this build has no automatic request-to-invoice conversion.",
    readOnlyNotice: "You have read-only access.",
    scopeNotice: "Only requests for suppliers you hold daily-transaction access to are shown.",
    listTitle: "Requests",
    formTitle: "Request details",
    linesTitle: "Lines",
    newRecord: "New request",
    search: "Search",
    searchHint: "Request number, reference or supplier",
    refresh: "Refresh",
    noRows: "No requests.",
    dateFrom: "Date from",
    dateTo: "Date to",
    statusFilter: "Status",
    allStatuses: "All statuses",
    number: "Request No.",
    date: "Date",
    requiredDate: "Required by",
    supplier: "Supplier",
    chooseSupplier: "Choose a supplier",
    currency: "Currency",
    exchangeRate: "Exchange rate",
    branch: "Branch",
    paymentMethod: "Payment method",
    requestedBy: "Requested by",
    costCenter: "Cost center",
    none: "None",
    reference: "Reference",
    notes: "Notes",
    taxInclusive: "Prices include tax",
    taxExclusive: "Prices exclude tax",
    pricing: "Pricing",
    lineDescription: "Description",
    unit: "Unit",
    quantity: "Quantity",
    unitPrice: "Estimated price",
    discountPercent: "Discount %",
    discountType: "Discount type",
    discountValue: "Discount value",
    taxType: "Tax type",
    lineGross: "Gross",
    lineDiscount: "Discount",
    lineTax: "Tax",
    lineNet: "Net",
    addLine: "Add line",
    removeLine: "Remove",
    subtotal: "Subtotal",
    discountTotal: "Total discount",
    taxTotal: "Total tax",
    netTotal: "Net total",
    status: "Status",
    draft: "Draft",
    cancelled: "Cancelled",
    save: "Save",
    saveNew: "Save and new",
    cancelEdit: "Cancel edit",
    edit: "Edit",
    view: "View",
    cancelRequest: "Cancel request",
    cancelReason: "Cancellation reason",
    confirmCancel: "Confirm cancellation",
    close: "Close",
    details: "Request details",
    createdBy: "Created by",
    createdAt: "Created at",
    attachments: "Attachments",
    saved: "Request saved.",
    cancelledOk: "Request cancelled.",
    loadError: "The requests could not be loaded.",
    saveError: "The request could not be saved.",
    cancelError: "The request could not be cancelled.",
    linesRequired: "At least one line is required.",
    totalCount: "Requests",
    taxComputedOnSave: "Tax is computed on the server when the request is saved.",
    noSuppliersInScope:
      "No supplier falls inside your daily-transaction access, so a request cannot be raised. Access is granted under Payments › Master Data › User–Supplier Access.",
  },
  personnel: {
    collectorsTitle: "Collectors",
    driversTitle: "Drivers",
    coordinatorsTitle: "Coordinators",
    salesRepsTitle: "Sales Representatives",
    collectorsPurpose:
      "The approved collector list. The collector named on receipt vouchers and receipt orders is the same system user linked here — no second collector concept was introduced.",
    driversPurpose:
      "Operational driver master data only. No vehicles, no routes, no delivery schedule and no accounting treatment.",
    coordinatorsPurpose:
      "Operational coordinator master data only. No workflow responsibilities and no accounting behaviour beyond what is already defined.",
    salesRepsPurpose:
      "The approved sales representative list. The representative named on the customer card, on customer categories and on receipt orders is the same system user linked here.",
    breadcrumb: "Financial Management / Payments / Master Data",
    identityNotice:
      "Linking a system user is optional: someone with no login is recorded by name alone. Where a link exists it is the same user the existing documents already point at.",
    noAccountingNotice:
      "Operational data only: no account, no balance, no commission and no entries. There is no delete — records are deactivated and reactivated so historical documents stay explicable.",
    readOnlyNotice: "You have read-only access.",
    listTitle: "List",
    formTitle: "Details",
    newRecord: "New record",
    search: "Search",
    searchHint: "Code, name, mobile, phone or email",
    refresh: "Refresh",
    showInactive: "Show inactive",
    noRows: "No records.",
    branchFilter: "Branch",
    allBranches: "All branches",
    linkFilter: "User link",
    anyLink: "All",
    linkedOnly: "Linked to a user",
    unlinkedOnly: "Not linked",
    code: "Code",
    nameAr: "Arabic Name",
    nameEn: "English Name",
    user: "System User",
    noUser: "No user",
    userHint: "From the active system users.",
    mobile: "Mobile",
    phone: "Phone",
    email: "Email",
    branch: "Branch",
    noBranch: "No branch",
    notes: "Notes",
    active: "Active",
    actions: "Actions",
    edit: "Edit",
    view: "View",
    activate: "Activate",
    deactivate: "Deactivate",
    save: "Save",
    saveNew: "Save and new",
    cancel: "Cancel",
    details: "Record details",
    updatedAt: "Last updated",
    auditHistory: "Change history",
    auditNone: "No recorded changes.",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "By",
    auditChangedAt: "When",
    close: "Close",
    saved: "Saved.",
    activated: "Activated.",
    deactivated: "Deactivated.",
    counts: "Total / Active",
    matched: "Matched",
    loadError: "The list could not be loaded.",
    saveError: "The record could not be saved.",
    auditError: "The change history could not be loaded.",
  },
  fixedAssetsMasterData: {
    common: {
      code: "Code", nameAr: "Arabic Name", nameEn: "English Name",
      active: "Active", notes: "Notes", actions: "Actions",
      edit: "Edit", activate: "Activate", deactivate: "Deactivate", delete: "Delete",
      save: "Save", cancel: "Cancel", search: "Search", refresh: "Refresh",
      showInactive: "Show inactive", noRows: "No records.",
      readOnlyNotice: "You have view access only. Editing is not permitted.",
      saved: "Saved.", activated: "Activated.", deactivated: "Deactivated.",
      deleted: "Deleted.",
      loadError: "Could not load the records.", saveError: "Could not save.",
      parent: "Parent", noParent: "— No parent (root level) —", level: "Level",
      assets: "Assets", inactiveByParent: "Inactive via parent",
      deleteConfirm: "Delete this record permanently? Deletion is only possible for a record nothing has ever used.",
      none: "— None —", required: "Required",
    },
    groups: {
      title: "Fixed Asset Groups",
      breadcrumb: "General Accounting « Fixed Assets « Basic Data « Fixed Asset Groups",
      purposeNotice: "A hierarchical classification of fixed assets, and the place the accounts every asset beneath it posts to are decided.",
      scopeNotice: "Accounts are chosen from the company's own chart; nothing is hard-coded. A child group inherits from the nearest ancestor that names an account — resolved on read, never copied down.",
      treeTitle: "Group Tree", formTitle: "Group Details",
      newRoot: "Root Group", newChild: "Child Group",
      accountsSection: "Accounts", depreciationSection: "Depreciation Defaults",
      assetAccount: "Asset Account", accumulatedAccount: "Accumulated Depreciation",
      expenseAccount: "Depreciation Expense",
      disposalGainAccount: "Disposal Gain", disposalLossAccount: "Disposal Loss",
      inheritedFrom: "Inherited from", effective: "Effective account", notMapped: "Not mapped",
      chooseAccount: "— Select an account —",
      method: "Depreciation Method", straightLine: "Straight Line",
      decliningBalance: "Declining Balance",
      usefulLifeMonths: "Useful Life (months)", ratePercent: "Annual Rate %",
      salvagePercent: "Salvage %",
      defaultsNotice: "These are defaults copied onto an asset card when it is created. Changing them later alters no existing card and no posted depreciation.",
    },
    locations: {
      title: "Fixed Asset Locations",
      breadcrumb: "General Accounting « Fixed Assets « Basic Data « Fixed Asset Locations",
      purposeNotice: "A hierarchy of asset locations with no fixed depth: building, then floor, then department.",
      scopeNotice: "A location is not a cost centre. Where a thing physically sits and which part of the business bears its cost are different questions, and neither is inferred from the other.",
      treeTitle: "Location Tree", formTitle: "Location Details",
      newRoot: "Main Location", newChild: "Sub Location",
      branch: "Branch",
    },
    card: {
      title: "Fixed Asset Card",
      breadcrumb: "General Accounting « Fixed Assets « Basic Data « Fixed Asset Card",
      purposeNotice: "The authoritative record of one individual fixed asset. Every Fixed Assets operation points at this card and none of them re-describes the asset.",
      scopeNotice: "Master data only: this screen cannot sell, dispose of or depreciate. Sold, disposed and written-off are set by the operation that accounts for them.",
      listTitle: "Asset Register", newAsset: "New Asset", backToList: "Back to list",
      tabGeneral: "General", tabAcquisition: "Acquisition", tabDepreciation: "Depreciation",
      tabLocation: "Location & Custody", tabAccounting: "Accounting", tabAttachments: "Attachments",
      assetNumber: "Asset Number", group: "Group", location: "Location", branch: "Branch",
      status: "Status", description: "Description",
      statusDraft: "Draft", statusInService: "In Service", statusSuspended: "Suspended",
      statusFullyDepreciated: "Fully Depreciated", statusSold: "Sold",
      statusDisposed: "Disposed", statusWrittenOff: "Written Off",
      statusOperationOnly: "Set by operation",
      acquisitionSource: "Acquisition Source", sourcePurchase: "Purchase",
      sourceOpeningBalance: "Opening Balance",
      supplier: "Supplier", purchaseDate: "Purchase Date", inServiceDate: "In-Service Date",
      documentReference: "Purchase Document Reference",
      documentReferenceHint: "Free text: the Fixed Assets purchase invoice is a Daily Transaction that does not exist yet, and a key pointing at an empty table would be a promise this card cannot keep.",
      acquisitionCost: "Acquisition Cost", currency: "Currency", exchangeRate: "Exchange Rate",
      localCost: "Local Cost",
      serialNumber: "Serial Number", manufacturer: "Manufacturer", model: "Model",
      warrantyExpiry: "Warranty Expiry",
      method: "Depreciation Method", usefulLifeMonths: "Useful Life (months)",
      ratePercent: "Annual Rate %", salvageValue: "Salvage Value",
      depreciationStart: "Depreciation Start",
      openingAccumulated: "Opening Accumulated Depreciation",
      openingAccumulatedHint: "Applies only to an asset entered as an opening balance. An asset bought through this system takes its accumulated depreciation from the ledger.",
      depreciableAmount: "Depreciable Amount", accumulated: "Accumulated Depreciation",
      bookValue: "Book Value",
      derivedNotice: "Accumulated depreciation and book value are computed on the server and are never entered by hand.",
      noEngineNotice: "No depreciation has been posted yet, so book value is cost less the opening accumulated figure alone.",
      inheritFromGroup: "Take depreciation defaults from the group",
      inheritFromGroupHint: "On creation only: the values are copied once and then belong to the card.",
      custodian: "Custodian", department: "Department",
      accountingNotice: "These accounts come from the asset's group and are not edited from the card. Change the group to change them.",
      attachmentsHint: "Invoice, warranty, title deed, asset photograph. The image is optional and is never required to create a card.",
      upload: "Attach file", uploading: "Uploading…", download: "Download",
      fileName: "File", fileSize: "Size", uploadedAt: "Uploaded",
      noAttachments: "No attachments.",
      closedNotice: "This asset has left the company's books. Only descriptive fields can be changed.",
      filterGroup: "Group", filterLocation: "Location", filterSupplier: "Supplier",
      filterStatus: "Status",
      totalCost: "Total cost", totalBookValue: "Total book value", count: "Count",
    },
  },
  fixedAssetsTransactions: {
    common: {
      asset: "Asset", selectAsset: "Select an asset", date: "Date", notes: "Notes",
      description: "Description", amount: "Amount", currency: "Currency",
      exchangeRate: "Exchange rate", localAmount: "Local amount",
      status: "Status", statusDraft: "Draft", statusApproved: "Approved",
      statusPosted: "Posted", statusCancelled: "Cancelled", statusReversed: "Reversed",
      approve: "Approve", post: "Post", cancel: "Cancel", reverse: "Reverse",
      save: "Save", saveNew: "Save new", newDocument: "New document", edit: "Edit",
      close: "Close", refresh: "Refresh", search: "Search", none: "None",
      actions: "Actions", number: "Number", noRows: "Nothing to show",
      readOnlyNotice: "You have view access only. You cannot create, amend or post anything here.",
      loadError: "Could not load the data.", saveError: "Could not save the document.",
      saved: "Saved.", approvedMsg: "Approved.", postedMsg: "Posted to the ledger.",
      cancelledMsg: "Cancelled.", reversedMsg: "Reversed with an opposite voucher.",
      required: "Required",
      reasonPrompt: "Reason (optional)",
      confirmPost: "This writes a real journal voucher. Continue?",
      confirmReverse: "This posts an opposite voucher. The original document is not edited. Continue?",
      confirmCancel: "This cancels the document before it posts. Continue?",
      journalVoucher: "Journal voucher", notPosted: "Not posted",
      postedNotice: "This document is posted. It cannot be edited — correct it by reversing it.",
      condition: "Condition", conditionGood: "Good", conditionFair: "Fair",
      conditionDamaged: "Damaged", conditionOutOfService: "Out of service",
      settlement: "Settlement", settlementSupplier: "On the supplier's account",
      settlementOnAccount: "On account",
      settlementCashOrBank: "Cash or bank", settlementAccount: "Cash or bank account",
      supplier: "Supplier", customer: "Customer", costCenter: "Cost centre",
      branch: "Branch", location: "Location", total: "Total", count: "Count", all: "All",
      valuationTitle: "What the asset is carried at",
      valuationCost: "Cost", valuationAccumulated: "Accumulated depreciation",
      valuationNetBookValue: "Net book value", valuationSalvage: "Salvage value",
      valuationDepreciable: "Depreciable amount",
      valuationNotice: "These figures are computed on the server from posted documents only. They are never typed in.",
    },
    purchaseOrders: {
      title: "Asset Purchase Order",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Purchase Order",
      purposeNotice: "The register of asset purchase orders raised in the purchasing cycle. An order writes no journal entry; the invoice is what posts.",
      scopeNotice: "This screen reads orders, it does not raise them. Orders are created in the purchasing cycle and appear here to be turned into an asset invoice.",
      registerTitle: "Purchase orders", orderNumber: "Order number", orderDate: "Order date",
      expectedDate: "Expected date", netTotal: "Net total",
      availableOnly: "Not yet invoiced", invoiced: "Invoiced", available: "Available",
      totalOnOrder: "Total on order", availableCount: "Available to invoice",
      createInvoice: "Create invoice", orderStatus: "Order status",
    },
    purchaseInvoices: {
      title: "Asset Purchase Invoice",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Purchase Invoice",
      purposeNotice: "The acquisition document. Each line creates its own asset card with its own number, and the asset posts to the group's account against the supplier or the cash account.",
      scopeNotice: "One line per asset — there is no quantity. Ten machines mean ten lines and ten cards, because each has its own serial number, custodian and location.",
      registerTitle: "Purchase invoices", formTitle: "Invoice",
      invoiceNumber: "Invoice number", invoiceDate: "Invoice date",
      supplierInvoiceNumber: "Supplier's invoice number", purchaseOrder: "Purchase order",
      noPurchaseOrder: "No purchase order",
      linesTitle: "Assets acquired", addLine: "Add asset", removeLine: "Remove",
      assetName: "Asset name (English)", assetNameAr: "Asset name (Arabic)", group: "Group",
      serialNumber: "Serial number", manufacturer: "Manufacturer", model: "Model",
      unitCost: "Cost", lineTotal: "Line total", subtotal: "Subtotal",
      totalAcquired: "Total acquired", assetCount: "Assets",
      createdCards: "Asset cards created",
      linesRequired: "At least one line is required.",
      postNotice: "Posting writes the journal entry and brings the asset cards into service in the same act.",
    },
    custodyIssue: {
      title: "Custody Issue",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Custody Issue",
      purposeNotice: "Hand an asset to an employee as personal custody. An operational movement with no journal entry — nothing about the ownership has changed.",
      scopeNotice: "An asset already under open custody cannot be issued again. Return it first, or use Change Asset Custody to move it directly.",
      registerTitle: "Custody register", formTitle: "Issue custody",
      custodian: "Custodian", issuedOn: "Issued on", expectedReturnOn: "Expected return",
      conditionOnIssue: "Condition on issue", issue: "Issue custody",
      issued: "Custody issued.",
      openOnly: "Open only", openCount: "Open", overdueCount: "Overdue",
      overdue: "Overdue", returnedOn: "Returned on", conditionOnReturn: "Condition on return",
      open: "Open", closed: "Closed",
    },
    custodyReturn: {
      title: "Release from Custody / Return to Custody",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Release from Custody",
      purposeNotice: "Take the asset back from the employee and close the open custody, recording the condition it came back in.",
      scopeNotice: "A return needs an open custody on the asset. Returning it to a different location is recorded as its own location movement, with its own date.",
      formTitle: "Return custody", returnedOn: "Returned on",
      conditionOnReturn: "Condition on return", returnToLocation: "Return to location",
      keepLocation: "Leave the location as it is", returnAction: "Return custody",
      returned: "Custody returned.",
      openCustodyTitle: "Open custody", noOpenCustody: "This asset is not under custody.",
      heldBy: "Held by", heldSince: "Since",
    },
    custodyTransfer: {
      title: "Change Asset Custody",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Change Asset Custody",
      purposeNotice: "Move custody straight from one employee to another: the first custody closes and the second opens in a single movement.",
      scopeNotice: "A transfer needs an open custody, and cannot name the same custodian. Both movements stay in the history; neither is removed.",
      formTitle: "Transfer custody", fromCustodian: "From", toCustodian: "To",
      transferDate: "Transfer date", transfer: "Transfer custody",
      transferred: "Custody transferred.",
      historyTitle: "Custody history", movementType: "Movement",
      movementIssue: "Issue", movementReturn: "Return", movementTransfer: "Transfer",
      from: "From", to: "To", recordedAt: "Recorded at",
    },
    locationMovements: {
      title: "Change Asset Location",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Change Asset Location",
      purposeNotice: "Move an asset from one location to another, keeping the full history of every move. Operational — no journal entry.",
      scopeNotice: "A location change touches neither the group, the accounts, nor the custody. A move to the same location is refused.",
      registerTitle: "Location movements", formTitle: "Move an asset",
      fromLocation: "From location", toLocation: "To location", movementDate: "Movement date",
      reason: "Reason", move: "Move", moved: "Asset moved.",
      currentLocation: "Current location", noLocation: "No location", from: "From", to: "To",
    },
    insurance: {
      title: "Asset Insurance",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Insurance",
      purposeNotice: "Insurance policies over assets: the insurer, the policy number, the cover period, the insured value and the premium.",
      scopeNotice: "This screen records the policy; it does not post the premium. The premium is paid through Asset Expenses and then linked to the policy here.",
      registerTitle: "Insurance policies", formTitle: "Policy",
      insurer: "Insurer", policyNumber: "Policy number", startDate: "Cover from",
      endDate: "Cover to", insuredValue: "Insured value", premiumAmount: "Premium",
      coverageType: "Cover type", premiumExpense: "Premium expense",
      noPremiumExpense: "Not linked to an expense",
      policyStatus: "Policy status", statusActive: "Active", statusExpired: "Expired",
      statusCancelled: "Cancelled", expired: "Expired", daysToExpiry: "Days to expiry",
      activeCount: "Active", expiringSoon: "Expiring soon",
      totalInsuredValue: "Total insured value",
      premiumNotice: "Linking a posted expense makes the premium's accounting effect visible from the policy itself.",
    },
    maintenance: {
      title: "Asset Maintenance",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Maintenance",
      purposeNotice: "A maintenance history of its own for each asset: the kind of work, when, by whom, what it cost, and the meter reading and condition afterwards.",
      scopeNotice: "Maintenance is an operational record with its own date and does not post by itself. Its cost is paid through Asset Expenses and linked here.",
      registerTitle: "Maintenance history", formTitle: "Maintenance",
      maintenanceNumber: "Maintenance number", maintenanceType: "Type",
      typePreventive: "Preventive", typeCorrective: "Corrective",
      typeEmergency: "Emergency", typeImprovement: "Improvement",
      maintenanceDate: "Maintenance date",
      serviceProvider: "Service provider", cost: "Cost", costExpense: "Cost expense",
      noCostExpense: "Not linked to an expense", nextMaintenanceDate: "Next due",
      meterReading: "Meter reading", conditionAfter: "Condition after",
      due: "Due", dueCount: "Due", totalCost: "Total cost",
      costNotice: "Improvement work is often capitalised onto the asset's cost. That choice is made when the expense is raised, not here.",
    },
    inventoryCounts: {
      title: "Asset Inventory Count",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Inventory Count",
      purposeNotice: "A count is its own operation with its own number and date: a sheet is drawn of every asset expected at the location, then what was actually found is recorded against it.",
      scopeNotice: "A count records reality; it does not change it. It moves no asset, edits no card and writes no journal entry. Discrepancies are settled by separate movements.",
      registerTitle: "Counts", sheetTitle: "Count sheet",
      countNumber: "Count number", countDate: "Count date", responsible: "Responsible",
      openCount: "Open a count", opened: "Count opened and the sheet drawn.",
      complete: "Complete count", completed: "Count completed.",
      countStatus: "Status", statusDraft: "In progress", statusCompleted: "Completed",
      statusCancelled: "Cancelled",
      lineNumber: "#", expectedLocation: "Expected location", actualLocation: "Actual location",
      result: "Result", resultNotCounted: "Not counted", resultFound: "Found",
      resultFoundElsewhere: "Found elsewhere", resultMissing: "Missing",
      discrepancy: "Discrepancy", resolved: "Resolved", markResolved: "Resolved",
      recordLine: "Save line", recorded: "Count result recorded.",
      totalLines: "Lines", countedLines: "Counted", foundCount: "Found",
      foundElsewhereCount: "Found elsewhere", missingCount: "Missing",
      unresolved: "Unresolved discrepancies",
      backToRegister: "Back to the register", allLocations: "All locations",
      changesNothingNotice: "The expected location is a snapshot taken when the sheet was drawn, so the sheet stays a true witness even if the asset moves afterwards.",
      cancelCount: "Cancel count",
    },
    depreciation: {
      title: "Asset Depreciation",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Depreciation",
      purposeNotice: "A monthly depreciation run: the period's charge is computed for every eligible asset as a draft to review, then approved and posted as one journal entry.",
      scopeNotice: "The same period cannot post twice for the same group. The charge is derived from the current cost and is never typed in.",
      registerTitle: "Depreciation runs", runTitle: "Run detail",
      runNumber: "Run number", period: "Period", periodYear: "Year", periodMonth: "Month",
      runDate: "Run date", group: "Group", allGroups: "All groups",
      prepare: "Prepare run", prepared: "Run prepared as a draft.",
      totalDepreciation: "Total depreciation", assetCount: "Assets",
      postedCount: "Posted runs", totalPosted: "Total posted",
      lineNumber: "#", costBasis: "Cost basis", salvageValue: "Salvage",
      method: "Method", methodStraightLine: "Straight line",
      methodDecliningBalance: "Declining balance", usefulLifeMonths: "Life (months)",
      ratePercent: "Rate %", openingAccumulated: "Opening accumulated",
      charge: "Charge", closingAccumulated: "Closing accumulated",
      closingBookValue: "Closing book value", capped: "Capped",
      cappedNotice: "A charge marked capped was reduced so accumulated depreciation would not pass the depreciable amount.",
      backToRegister: "Back to the register",
      noLines: "No asset is eligible for depreciation in this period.",
      preparedNotice: "The draft can be reviewed and cancelled. Nothing is written to the ledger before it posts.",
    },
    annualDepreciation: {
      title: "Annual Depreciation", periodYear: "Year", fromMonth: "From month", toMonth: "To month",
      preview: "Preview", execute: "Create & Post Voucher",
      previewNotice: "Preview only: no voucher has been created and nothing has been posted yet.",
      voucherDate: "Voucher date", runCount: "Months", monthsCharged: "Months charged",
      category: "Category", voucherLines: "Proposed depreciation entry", account: "Account",
      debit: "Debit", credit: "Credit", total: "Total",
      confirmExecute: "One depreciation voucher of {total} dated {date} will be created and posted. Continue?",
      executed: "Voucher {voucher} was created and posted.",
    },
    sales: {
      title: "Asset Sale",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Sale",
      purposeNotice: "Sell the asset and take it off the books: cost and accumulated depreciation are closed out, and the difference against the price is recognised as gain or loss.",
      scopeNotice: "A sale is a separate movement from a disposal and from maintenance, with its own lifecycle and date. Once posted the asset leaves service and accepts no further movement.",
      registerTitle: "Sales", formTitle: "Sale",
      saleNumber: "Sale number", saleDate: "Sale date", saleAmount: "Sale amount",
      costAtSale: "Cost at sale", accumulatedAtSale: "Accumulated at sale",
      bookValueAtSale: "Book value at sale", gainOrLoss: "Gain / loss",
      gain: "Gain", loss: "Loss",
      totalProceeds: "Total proceeds", totalGainOrLoss: "Net gain / loss",
      postedCount: "Posted",
      snapshotNotice: "Cost and accumulated depreciation are captured at the moment of posting and kept on the document, so the gain stays explainable years later.",
    },
    disposals: {
      title: "Asset Disposal",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Disposal",
      purposeNotice: "Retire the asset with no proceeds: cost and accumulated depreciation are closed out and whatever book value remains is charged as a loss.",
      scopeNotice: "A disposal is a separate movement from a sale, with its own lifecycle and date. A fully depreciated asset is disposed of too — the entry closes cost and accumulation with no loss.",
      registerTitle: "Disposals", formTitle: "Disposal",
      disposalNumber: "Disposal number", disposalDate: "Disposal date", reason: "Reason",
      reasonDamaged: "Damaged", reasonObsolete: "Obsolete", reasonLost: "Lost",
      reasonStolen: "Stolen", reasonDonated: "Donated", reasonOther: "Other",
      costAtDisposal: "Cost at disposal",
      accumulatedAtDisposal: "Accumulated at disposal",
      bookValueAtDisposal: "Book value written off",
      totalWrittenOff: "Total written off", postedCount: "Posted",
      reasonMasterNotice: "Asset Disposal Reasons is not a master-data table in this build, so the reasons are a fixed list rather than one you can add to.",
    },
    expenses: {
      title: "Asset Expenses",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Expenses",
      purposeNotice: "Charge a cost to a particular asset: either against profit as an expense, or capitalised so it increases the asset's cost.",
      scopeNotice: "Capitalising raises the depreciation basis for future runs. It never re-computes a run that has already posted.",
      registerTitle: "Expenses", formTitle: "Expense",
      expenseNumber: "Expense number", expenseDate: "Expense date",
      expenseAccount: "Expense account", treatment: "Treatment",
      treatmentExpensed: "Charge to expense", treatmentCapitalised: "Capitalise onto the asset",
      totalExpensed: "Total expensed", totalCapitalised: "Total capitalised",
      postedCount: "Posted", allTreatments: "All treatments",
      treatmentNotice: "Choosing to capitalise makes this document part of the asset's cost, and it shows up in every later valuation.",
    },
    revenues: {
      title: "Asset Revenue",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Revenue",
      purposeNotice: "Revenue earned from using the asset — rental, operation and the like — with the asset staying in the company's ownership.",
      scopeNotice: "This is not a sale screen: revenue touches neither the asset's cost nor its accumulated depreciation, and never takes it out of service.",
      registerTitle: "Revenues", formTitle: "Revenue",
      revenueNumber: "Revenue number", revenueDate: "Revenue date",
      revenueAccount: "Revenue account", totalRevenue: "Total revenue",
      postedCount: "Posted",
      notASaleNotice: "To sell the asset itself use Asset Sale — that is the only screen that takes an asset off the books.",
    },
    revaluations: {
      title: "Asset Revaluation",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Revaluation",
      purposeNotice: "A movement of its own that restates what the asset is carried at: an increase posts to revaluation surplus, a decrease is charged as a loss.",
      scopeNotice: "A revaluation is not an edit to the asset card: it has a document, a number, a date, an approval and a posting, and is corrected by reversal rather than by editing.",
      registerTitle: "Revaluations", formTitle: "Revaluation",
      revaluationNumber: "Revaluation number", revaluationDate: "Revaluation date",
      previousCarryingAmount: "Carrying amount before",
      revaluedAmount: "Revalued amount", difference: "Difference",
      upward: "Upward", downward: "Downward", reason: "Reason",
      totalUpward: "Total upward", totalDownward: "Total downward", postedCount: "Posted",
      carryingNotice: "The carrying amount before is computed on the server from posted documents, and refreshes as soon as an asset is chosen.",
      carryingUnavailable: "Choose an asset to see what it is currently carried at.",
    },
    splits: {
      title: "Asset Split",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Split",
      purposeNotice: "Divide one asset into several: the parent's cost and accumulated depreciation are allocated across the children, and the parent is then closed.",
      scopeNotice: "After a split the company owns exactly what it owned before: the children's cost must sum to the parent's and their accumulated depreciation to the parent's — with no tolerance.",
      registerTitle: "Splits", formTitle: "Split",
      splitNumber: "Split number", splitDate: "Split date", parentAsset: "Parent asset",
      postedCount: "Completed",
      parentCost: "Parent cost", parentAccumulated: "Parent accumulated",
      parentBookValue: "Parent book value", reason: "Reason",
      childCount: "Children",
      linesTitle: "Resulting assets", addLine: "Add child asset", removeLine: "Remove",
      childAssetNumber: "Asset number", childName: "Name (English)",
      childNameAr: "Name (Arabic)", group: "Group",
      allocatedCost: "Allocated cost", allocatedAccumulated: "Allocated accumulated",
      allocatedBookValue: "Book value",
      allocatedTotals: "Allocated", balanced: "Allocation matches",
      notBalanced: "Allocation does not match",
      balanceNotice: "The split cannot be completed until the allocation matches the parent on cost and accumulated depreciation together.",
      complete: "Complete split", completed: "Split completed and the child assets created.",
      minimumLines: "A split needs at least two resulting assets.",
      backToRegister: "Back to the register",
    },
  },
  fixedAssetsProcurement: {
    custodyDeposit: {
      title: "Custody Deposit",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Custody Deposit",
      purposeNotice: "Place an asset into a keeper's custody for safekeeping, at a named store. Opens a custody exactly as an issue does.",
      scopeNotice: "A deposit is the counterpart of Custody Issue, not of Release from Custody: an issue sends an asset out to be used, a deposit puts one in to be kept, and both OPEN a custody. A release closes one. No journal entry at any point.",
      formTitle: "Deposit custody", registerTitle: "Open custodies",
      keeper: "Keeper", depositedOn: "Deposited on",
      depositLocation: "Deposit at", keepLocation: "Leave the location as it is",
      conditionOnDeposit: "Condition on deposit", expectedReturnOn: "Expected out",
      deposit: "Deposit", deposited: "Asset deposited into custody.",
      movementIssue: "Issue", movementReturn: "Return", movementTransfer: "Transfer",
      movementDeposit: "Deposit", movementType: "Movement",
      heldNotice: "An asset already under custody cannot be deposited. Release it first.",
      openCount: "Open custodies",
    },
    purchaseReturns: {
      title: "Asset Purchase Returns",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Purchase Returns",
      purposeNotice: "Send an acquired asset back to the supplier against a posted purchase invoice: the acquisition is reversed and the asset card leaves the books as Returned.",
      scopeNotice: "A return is not a deletion of the invoice or of the card: the company really did buy the asset and really did send it back, and the register keeps both facts. Supplier, currency, rate and cost all come from the source invoice and are never typed.",
      registerTitle: "Returns", formTitle: "Return",
      returnNumber: "Return number", returnDate: "Return date",
      sourceInvoice: "Source invoice", selectInvoice: "Choose a posted invoice",
      supplierInvoice: "Supplier",
      reason: "Reason", reasonDefective: "Defective",
      reasonNotAsSpecified: "Not as specified", reasonOverDelivered: "Over-delivered",
      reasonPurchaseCancelled: "Purchase cancelled", reasonOther: "Other",
      referenceNumber: "Supplier's credit note", returnAmount: "Return amount",
      originalCost: "Original cost",
      assetsTitle: "Assets returned", returnable: "Returnable", notReturnable: "Not returnable",
      blockedReason: "Why not", selectAll: "Select all",
      assetCount: "Assets", totalReturned: "Total returned", postedCount: "Posted",
      eligibilityNotice: "An asset that has been sold, disposed of, split, or depreciated cannot be returned. Where depreciation exists the run must be reversed first — nothing is corrected silently.",
      postNotice: "Posting writes the return entry and takes the asset cards off the books as Returned.",
      nothingReturnable: "Nothing on this invoice can still be returned.",
      returnedStatus: "Returned to supplier",
    },
    expenseReturns: {
      title: "Asset Expense Returns",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Expense Returns",
      purposeNotice: "Take back all or part of a posted asset expense. Where the money goes follows the original's treatment: an expensed cost is credited to the expense account, a capitalised one to the asset.",
      scopeNotice: "The treatment is copied from the source expense and never chosen again, and returns cannot exceed the original amount in total. The original document is never edited and never deleted.",
      registerTitle: "Expense returns", formTitle: "Return",
      returnNumber: "Return number", returnDate: "Return date",
      sourceExpense: "Source expense", selectExpense: "Choose a posted expense",
      sourceAmount: "Original amount", alreadyReturned: "Already returned",
      remainingReturnable: "Still returnable", returnAmount: "Return amount",
      treatment: "Original treatment", treatmentExpensed: "Charged to expense",
      treatmentCapitalised: "Capitalised onto the asset",
      reason: "Reason", totalReturned: "Total returned",
      totalCapitalisedReturned: "Capitalised returned", postedCount: "Posted",
      capitalisedNotice: "This expense was capitalised, so the return credits the asset account itself. The asset's cost falls the moment it posts, without a single field being edited.",
      treatmentLockedNotice: "The treatment comes from the original expense and cannot be changed here.",
      noneReturnable: "No posted expense has anything left to return.",
    },
    transfers: {
      title: "Asset / Item Transfer",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset / Item Transfer",
      purposeNotice: "A transfer document with two ends — dispatch and receipt. It moves an asset between locations and branches, and can also change its classification, keeping both the old and the new.",
      scopeNotice: "A transfer is a document with a status; Change Asset Location is one immediate step. The difference is that here the asset is IN TRANSIT between the two. The receipt is what actually moves it, and the only step that can post a reclassification.",
      registerTitle: "Transfers", formTitle: "Transfer",
      transferNumber: "Transfer number", transferDate: "Transfer date",
      fromSide: "From", toSide: "To",
      fromLocation: "Current location", toLocation: "New location",
      fromBranch: "Current branch", toBranch: "New branch",
      fromGroup: "Current group", toGroup: "New group",
      fromCustodian: "Current custodian", toCustodian: "New custodian",
      keepGroup: "Keep the group", keepLocation: "Keep the location",
      keepBranch: "Keep the branch", keepCustodian: "Keep the custodian",
      reason: "Reason",
      transferStatus: "Status", statusDraft: "Draft", statusApproved: "Approved",
      statusDispatched: "Dispatched", statusReceived: "Received", statusCancelled: "Cancelled",
      dispatch: "Dispatch", dispatched: "Asset dispatched.",
      receive: "Receive", received: "Asset received and the transfer applied.",
      dispatchedOn: "Dispatched on", receivedOn: "Received on",
      conditionOnDispatch: "Condition on dispatch", conditionOnReceipt: "Condition on receipt",
      inTransit: "In transit", inTransitCount: "In transit",
      receivedCount: "Received", reclassified: "Reclassified",
      reclassifiedCount: "Reclassifications",
      willReclassify: "The receipt will post a reclassification — the two groups use different accounts.",
      willNotReclassify: "The receipt will post nothing: the accounts are the same before and after.",
      costAtTransfer: "Cost at transfer",
      accumulatedAtTransfer: "Accumulated at transfer",
      reclassificationNotice: "A reclassification moves cost and accumulated depreciation together, so the net book value is untouched and no gain or loss is created.",
      receiptNotice: "The receipt applies the move: it writes a location movement into the same history Change Asset Location uses, and updates the branch, group and custodian.",
      dispatchNotice: "Once dispatched a transfer cannot be cancelled — the asset has actually left.",
    },
    worksheet: {
      title: "Asset Inventory Worksheet",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Asset Inventory Worksheet",
      purposeNotice: "The result sheet of an existing count: what was expected, what was actually found, and the differences — to read and to print.",
      scopeNotice: "Read-only. It opens no count, edits no line and moves no asset; the count itself is run from Asset Inventory Count.",
      selectCount: "Choose a count", sheetTitle: "Count sheet",
      countNumber: "Count number", countDate: "Count date", responsible: "Responsible",
      discrepanciesOnly: "Differences only", print: "Print",
      lineNumber: "#", serialNumber: "Serial number", group: "Group",
      custodian: "Custodian",
      expectedLocation: "Expected location", actualLocation: "Actual location",
      result: "Result", difference: "Difference",
      notCounted: "Not counted", counted: "Counted", discrepancies: "Differences",
      unresolved: "Unresolved", resolved: "Resolved",
      readOnlyNotice: "This sheet is for reading and printing. It changes nothing.",
      noCountSelected: "Choose a count to see its sheet.",
    },
    rfq: {
      title: "Request for Quotation",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Request for Quotation",
      purposeNotice: "Ask several suppliers what they would charge, before buying. A shared procurement document, marked when it is raised towards acquiring a fixed asset.",
      scopeNotice: "No accounting impact at any stage: a request is a question put to suppliers and commits the company to nothing. The commitment starts with the purchase order, and the debt with the supplier's invoice.",
      registerTitle: "Requests", formTitle: "Request",
      requestNumber: "Request number", requestDate: "Request date",
      requiredDate: "Required by", closingDate: "Answers close",
      subject: "Subject", terms: "Terms",
      purpose: "Purpose", purposeGeneral: "General procurement",
      purposeFixedAsset: "Fixed asset acquisition",
      assetGroup: "Asset group", intendedLocation: "Intended location",
      requestStatus: "Status", statusDraft: "Draft", statusIssued: "Issued",
      statusAwarded: "Awarded", statusCancelled: "Cancelled",
      linesTitle: "What is being asked for", addLine: "Add line", removeLine: "Remove",
      description: "Description", specification: "Specification", unit: "Unit",
      quantity: "Quantity",
      suppliersTitle: "Suppliers invited", addSupplier: "Invite supplier",
      invitedOn: "Invited on", responded: "Quoted", notResponded: "No answer",
      supplierCount: "Suppliers", quotationCount: "Quotations",
      issue: "Issue to suppliers", issued: "Request issued to the invited suppliers.",
      issueNotice: "Once issued the request cannot be edited: what suppliers were asked cannot change underneath them.",
      draftCount: "Drafts", issuedCount: "Issued", awardedCount: "Awarded",
      noAccountingNotice: "No journal entry.",
      awardedTo: "Awarded to",
    },
    quotations: {
      title: "Supplier Quotations", sectionTitle: "Quotations received",
      formTitle: "Record a supplier's quotation",
      supplier: "Supplier", supplierReference: "Supplier's reference",
      quotationDate: "Quotation date", validUntil: "Valid until", lapsed: "Lapsed",
      deliveryDays: "Delivery (days)", paymentTermDays: "Payment terms (days)",
      commercialNotes: "Commercial notes", technicalNotes: "Technical notes",
      unitPrice: "Unit price", discount: "Discount", taxPercent: "Tax %",
      lineTotal: "Line total", netTotal: "Net total",
      record: "Save quotation", recorded: "Quotation recorded.",
      withdraw: "Withdraw", withdrawn: "Quotation withdrawn.",
      quotationStatus: "Status", statusReceived: "Received", statusWithdrawn: "Withdrawn",
      notInvitedNotice: "A quotation cannot be recorded from a supplier who was not invited.",
      issueFirstNotice: "Quotations can only be recorded against an issued request.",
      noQuotations: "No quotation has been recorded against this request yet.",
      liveCount: "Live quotations", answersLine: "Answers",
    },
    comparison: {
      title: "Quotation Comparison",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Quotation Comparison",
      purposeNotice: "A matrix comparing the quotations actually recorded against one request: price, total, currency, delivery, payment terms and validity.",
      scopeNotice: "A read that stores nothing and invents nothing: every figure comes from a recorded supplier quotation. Currencies convert at the rate the quotation itself was recorded at, and an offer that cannot be converted is shown in its own currency and left out of the lowest-price markers.",
      matrixTitle: "Comparison", selectRequest: "Choose a request",
      noRequestSelected: "Choose a request to compare its quotations.",
      requestedLine: "Requested", lowest: "Lowest", lowestOverall: "Lowest overall",
      awarded: "Awarded", didNotQuote: "Did not quote", partialOffer: "Partial offer",
      invited: "Invited", responded: "Quoted",
      currencyWarning: "The quotations are in different currencies. The comparison uses the rate recorded on each one.",
      notComparable: "Not comparable", ownCurrency: "In its own currency",
      comparableTotal: "Comparable total", delivery: "Delivery",
      paymentTerms: "Payment", validity: "Validity", print: "Print",
      noQuotationsNotice: "There is no live quotation on this request to compare.",
      readOnlyNotice: "No journal entry, and nothing on this screen changes any data.",
    },
    award: {
      title: "Quotation Award",
      breadcrumb: "General Accounting « Fixed Assets « Daily Transactions « Quotation Award",
      purposeNotice: "Choose the winning quotation on a request, recording which quotation was chosen, by whom, on what date and why.",
      scopeNotice: "An award is a recorded decision, not a display flag. It writes no journal entry; the commitment starts with the purchase order that follows it.",
      selectRequest: "Choose a request",
      wholeAward: "Award the whole request", splitAward: "Split across lines",
      awardMode: "Award",
      awardDate: "Award date", awardReason: "Reason",
      award: "Award", awarded: "Quotation awarded.",
      chooseQuotation: "Winning quotation", chooseePerLine: "Winner for this line",
      awardedQuantity: "Awarded quantity",
      alreadyAwardedNotice: "This request has already been awarded and cannot be awarded again.",
      noAccountingNotice: "No journal entry.",
      awardedBy: "Awarded by", awardedOn: "on", unawarded: "Not awarded",
      confirmAward: "The award decision will be recorded permanently. Continue?",
      feedsOrderNotice: "An awarded request feeds the Asset Purchase Order, and then the Asset Purchase Invoice.",
    },
  },
    hrEmployeeReports: { category: "Employee Register Reports", documentsCategory: "Documents and Contracts Reports" },
    hrInquiries: {
      title: "Employee Affairs inquiries",
      description: "Every inquiry answers with a list, and every filter is optional — the employee included.",
      filters: "Filters", all: "All", none: "—",
      apply: "Show", reset: "Reset", refresh: "Refresh", search: "Search",
      loadFailed: "The results could not be loaded", noResults: "No results",
      employee: "Employee", employeeNumber: "Employee no.", fromDate: "From", toDate: "To",
      department: "Department", status: "Status", jobTitle: "Job title", hireDate: "Hire date",
      responsibleEntity: "Responsible entity", activeOnly: "In service",
      number: "Number", documentTitle: "Document", documentType: "Document type",
      documentNumber: "Document no.", issueDate: "Issued", expiryDate: "Expires",
      daysToExpiry: "Days left",
      expiryWindow: "Window", alreadyExpired: "Already expired", withinDays: "Within {n} days",
      noExpiry: "No expiry", expiredSince: "Expired {n} days ago",
      effectiveDate: "Effective", changeKind: "What changed",
      sourceDocument: "Source document", notes: "Notes",
      transactionKind: "Transaction",
      assetNumber: "Asset code", assetName: "Asset", handedOver: "Issued on",
      dueBack: "Due back", returned: "Returned",
      custodyState: "State", stillOut: "Still out",
      branch: "Branch", asOfDate: "As of", withinDaysFilter: "Within (days)", totals: "Totals",
      print: "Print PDF", exportExcel: "Export Excel",
      pageOf: "Page {page} of {pages}", nextPage: "Next", previousPage: "Previous",
      activeEmployeesOnly: "Active employees only",
      contractType: "Contract type", contractStatus: "Contract status",
      expiryFrom: "Expires from", expiryTo: "Expires to",
      contractStatuses: {
        1: "Not started", 2: "Active", 3: "Expired", 4: "Terminated", 5: "Renewed",
      },
      changeKinds: {
        1: "Branch", 2: "Department", 3: "Job title", 4: "Grade",
        5: "Technical classification", 6: "Direct manager", 7: "Supervisor", 8: "Employment status",
        9: "Cost centre", 10: "Contract", 11: "Secondary job title", 12: "Employee classification",
      },
      employmentStatuses: {
        1: "In service", 2: "On leave", 3: "Suspended", 4: "Terminated", 5: "Resigned",
      },
      screens: {
        jobTitleChanges: {
          title: "Job title changes",
          description: "Every job title change: from what, to what, and when it took effect.",
        },
        departureRequests: {
          title: "Departure requests",
          description: "Departure requests in every state — draft, submitted, approved, rejected.",
        },
        departureAmendments: {
          title: "Departure movements",
          description: "Amendments to approved departures: cancellation, extension, curtailment.",
        },
        iqamaMovements: {
          title: "Iqama movements",
          description: "Employee document movements filtered to the iqama type. There is no separate iqama register.",
        },
        healthCertificates: {
          title: "Health certificate movements",
          description: "The same employee document movement register, filtered to the health certificate type.",
        },
        disciplinaryActions: {
          title: "Disciplinary actions",
          description: "The violations and disciplinary actions recorded against employees.",
        },
        promotions: {
          title: "Promotions",
          description: "Promotions: grade and title before and after, and the effective date.",
        },
        transfers: {
          title: "Transfers",
          description: "Employee transfers between branches, departments and cost centres.",
        },
        overtimeRequests: {
          title: "Overtime requests",
          description: "Overtime requests and their state.",
        },
        companyDocuments: {
          title: "Expired company documents",
          description: "Registrations, licences and certificates — what has expired and what is due. Opens on the expired ones.",
        },
        employeeDirectory: {
          title: "Employee directory",
          description: "Number, name, department, title, entity, hire date and status. Nothing personal appears here.",
        },
        employeeActions: {
          title: "Employee actions",
          description: "The ledger of what changed for an employee, when, and by which document.",
        },
        employeeCustody: {
          title: "Employee custody",
          description: "The assets handed to employees and what is still out. Sourced from the asset custody ledger.",
        },
        dailyRegister: {
          title: "Daily register",
          description: "Every Employee Affairs document on one list. Shows only the kinds the signed-in user may view.",
        },
      },
    },
    hrSettings: {
      title: "Employee Affairs settings",
      description: "The vocabularies the employee card and its daily transactions pick from, and the leave rules. None of it is pre-filled.",
      cardGroup: "Employee card vocabularies",
      dailyGroup: "Daily transaction vocabularies",
      rulesGroup: "Rules",
      add: "Add", edit: "Edit", save: "Save", saving: "Saving…", cancel: "Cancel",
      search: "Search", code: "Code", nameAr: "Arabic name", nameEn: "English name",
      status: "Status", active: "Active", retired: "Retired",
      activate: "Reactivate", deactivate: "Retire",
      newEntry: "New entry", editEntry: "Edit entry",
      emptyList: "No entries yet",
      noPermission: "You may not edit this",
      saved: "Saved", activated: "Reactivated", deactivated: "Retired",
      loadFailed: "The list could not be loaded", saveFailed: "Could not save",
      groupIdentity: "Identity",
      groupNature: "Nature of the leave",
      groupDurations: "Durations",
      groupEntitlement: "Annual entitlement",
      groupEntitlementHint: "Leave blank when the leave carries no annual entitlement. Blank is not zero.",
      screens: {
        leaveTypes: {
          title: "Leave settings",
          description: "Every rule of every leave type: annual entitlement, minimum and maximum length, notice, whether a document is required, and carry-forward. This — not the code — is where a company's labour regime is stated.",
        },
        religions: {
          title: "Religions",
          description: "The religion as the employee card records it.",
        },
        departments: {
          title: "Departments",
          description: "The departments employees belong to. The leave reports and balances group on them.",
        },
        jobTitles: {
          title: "Job titles",
          description: "The primary and secondary job title on the employee card.",
        },
        employmentTypes: {
          title: "Employment types",
          description: "The nature of the employment, as the company names it.",
        },
        technicalClassifications: {
          title: "Technical classifications",
          description: "The employee's technical classification.",
        },
        grades: {
          title: "Grades",
          description: "The employee's grade.",
        },
        employeeClassifications: {
          title: "Employee classifications",
          description: "The employee classification the company recognises.",
        },
        contractTypes: {
          title: "Contract types",
          description: "The kind of contract an employee is engaged under.",
        },
        socialInsurancePolicies: {
          title: "Social insurance policies",
          description: "The insurance policy an employee is registered under. No deduction is computed from it — there is no payroll engine yet.",
        },
        healthInsurancePolicies: {
          title: "Health insurance policies",
          description: "The health insurance policy an employee is registered under.",
        },
        departureTypes: {
          title: "Departure types",
          description: "Personal, official, sick… the daily departure transaction picks from here.",
        },
        terminationReasons: {
          title: "Termination reasons",
          description: "Resignation, termination, end of term…",
        },
        violationTypes: {
          title: "Violation types",
          description: "The violations that may be recorded against an employee.",
        },
        disciplinaryActionTypes: {
          title: "Disciplinary action types",
          description: "Warning, deduction, suspension…",
        },
        employeeDocumentTypes: {
          title: "Employee document types",
          description: "Residency, passport, licence, health certificate…",
        },
        letterTypes: {
          title: "Letter types",
          description: "The kinds of administrative letter HR issues.",
        },
        injuryTypes: {
          title: "Injury types",
          description: "The work injuries recorded in the health file.",
        },
        medicalRecordTypes: {
          title: "Medical record types",
          description: "The kinds of medical record kept for an employee.",
        },
        companyDocumentTypes: {
          title: "Company document types",
          description: "The company documents whose expiry HR tracks.",
        },
        policyCategories: {
          title: "Policy categories",
          description: "How the internal policies published to employees are classified.",
        },
        clearanceDepartments: {
          title: "Clearance departments",
          description: "The department answerable for a clearance item — HR, finance, IT…",
        },
      },
    },
    fixedAssetsSettings: {
      search: "Search", add: "Add", edit: "Edit", save: "Save", saving: "Saving...",
      cancel: "Cancel", choose: "Choose...", none: "None", active: "Active", inactive: "Inactive",
      inUse: "Used by documents", activate: "Activate", deactivate: "Deactivate",
      activated: "Activated.", deactivated: "Withdrawn. It will not appear on new documents, and no existing document changed.",
      saved: "Saved.",
      emptyList: "Nothing here yet. Add the first entry when you need it - no defaults are created for you.",
      noPermission: "You cannot change Fixed Assets settings.",
      readOnlyNotice: "You have view access only. Saving needs the manage-settings right.",
      newEntry: "New entry", editEntry: "Edit entry",
      saveFailed: "That did not go through. Check the values and try again.",
      treatmentHint: "Decides whether an expense is charged to profit or capitalised onto the asset. The document keeps its own copy once saved.",
      classificationHint: "What this reason means to the accounting. Your wording appears on the document; the classification is what the ledger reads.",
      defaultAccountHint: "A default the document may override. Not a posting rule.",
      textHint: "The body of the term. It is copied into the request when chosen, so the request keeps the wording it went out with.",
      columns: {
        code: "Code", name: "Name (English)", nameAr: "Name (Arabic)",
        treatment: "Treatment", classification: "Classification", defaultAccount: "Default account",
        text: "Text", notes: "Notes", status: "Status",
      },
      treatment: { expensed: "Charged to expense", capitalised: "Capitalised" },
      disposalReason: {
        1: "Damaged", 2: "Obsolete", 3: "Lost", 4: "Stolen", 5: "Donated", 99: "Other",
      },
      frequency: { monthly: "Monthly" },
      groups: { depreciation: "Depreciation", documents: "Documents", numbering: "Numbering" },
      fields: {
        frequency: "Depreciation frequency",
        frequencyHint: "The engine charges monthly, and the verified rule is one charge per asset per month. No other frequency is supported.",
        defaultUsefulLife: "Default useful life (months)",
        defaultUsefulLifeHint: "Used when a new card's group, and nothing above it, sets a useful life.",
        defaultSalvage: "Default salvage %",
        defaultSalvageHint: "On the same rule: the group is the nearer answer, and this is the backstop.",
        requireDisposalReason: "Require a configured disposal reason",
        requireDisposalReasonHint: "When on, a disposal naming no configured reason is refused - checked on the server.",
        requireExpenseType: "Require a configured expense type",
        requireExpenseTypeHint: "When on, an asset expense naming no configured type is refused.",
        autoNumber: "Number assets automatically",
        autoNumberHint: "When on, a new card left without a number gets the next one in the sequence, and the counter advances inside the same save.",
        prefix: "Prefix", padding: "Digits",
        paddingHint: "Between 1 and 12.",
        nextValue: "Next number",
        nextValueHint: "Held as a counter, never derived from the highest existing number.",
        preview: "Next number preview",
      },
      screens: {
        assetModels: { title: "Asset Models", description: "The models an asset card chooses from." },
        brands: { title: "Brands", description: "The brands an asset card chooses from." },
        assetClasses: { title: "Asset Classes", description: "A descriptive classification the asset card carries." },
        assetCategories: { title: "Asset Categories", description: "A descriptive grouping the asset card carries." },
        assetProperties: { title: "Asset Properties", description: "The property vocabulary a company describes its assets with." },
        shippingMethods: { title: "Shipping Methods", description: "Read by the procurement documents." },
        expenseTypes: { title: "Expense Types", description: "Each carries a treatment - charged or capitalised - and a default account, both read by the Asset Expense document." },
        salesTypes: { title: "Sales Types", description: "Read by the Asset Sale document. Entirely separate from Asset Revenue." },
        disposalReasons: { title: "Asset Disposal Reasons", description: "Your own wording for a disposal reason; each carries the classification the ledger reads." },
        assetTypes: { title: "Asset Types", description: "A descriptive classification the asset card carries." },
        deliveryMethods: { title: "Delivery Methods", description: "Read by the procurement documents." },
        purchaseRoutes: { title: "Purchase Routes", description: "Read by the procurement documents." },
        quotationTerms: { title: "Quotation Terms", description: "The term's text is copied into the quotation request when chosen." },
        system: { title: "Fixed Assets System Settings", description: "How the Fixed Assets module behaves for this company. Every field here is read by something, and each says by what." },
        coding: { title: "Asset Coding Settings", description: "How new asset cards are numbered." },
      },
    },
  fixedAssetsInquiries: {
    readOnly: "Read-only: this screen creates, changes and deletes nothing.",
    noRows: "No results in the chosen scope.",
    noCostCentre: "No cost centre",
    voucherSeriesMismatchHint:
      "The voucher's series differs from the source document's type. Shown exactly as posted.",
    screens: {
      lifeCycle: {
        title: "Asset Life Cycle",
        description: "Everything that has happened to one asset since it arrived: custody, locations, transfers, insurance, maintenance, counts, depreciation, expenses, revenue, revaluation, sale, disposal and split.",
        assetRequired: "Choose an asset to see its life cycle.",
      },
      financialMovements: {
        title: "Asset Financial Movements",
        description: "What the ledger did to the assets, document by document, with the cost change, the depreciation, a running book value and the journal voucher.",
      },
      movements: {
        title: "Asset Movements",
        description: "The operational trail: custody, locations, transfers and counts. None of it posts to the ledger.",
      },
      expenses: {
        title: "Asset Expenses",
        description: "Spending on assets, net of returns, split between charged to profit and capitalised onto the asset by the persisted treatment.",
      },
      records: {
        title: "Asset Records",
        description: "The asset register as at a date: cost, capitalised additions, revaluation, accumulated depreciation, book value and status — all reconstructed from posted documents.",
      },
      transferStatus: {
        title: "Asset Item Transfer Status",
        description: "Where every transfer has got to — draft, approved, dispatched or received — and how many days an asset has been in transit.",
      },
      tree: {
        title: "Asset Tree",
        description: "The asset group hierarchy with each group's own figures beside its rolled-up ones, counting every asset exactly once.",
      },
      dailyTransactions: {
        title: "Daily Transactions",
        description: "The Fixed Assets day book: every document and movement the module recorded, newest first.",
      },
    },
    filters: {
      dateFrom: "Date from", dateTo: "Date to", asOfDate: "As of",
      asset: "Asset", chooseAsset: "Choose an asset", allAssets: "All assets",
      group: "Group", allGroups: "All groups", wholeTree: "Whole tree",
      location: "Location", allLocations: "All locations",
      supplier: "Supplier", allSuppliers: "All suppliers",
      branch: "Branch", allBranches: "All branches",
      custodian: "Custodian", allCustodians: "Anyone",
      costCentre: "Cost centre", allCostCentres: "All cost centres",
      status: "Status", allStatuses: "All statuses",
      transferStatus: "Transfer status",
      treatment: "Treatment", allTreatments: "All treatments",
      event: "Event", allEvents: "All events",
      search: "Search",
      includeUnposted: "Include unposted", postedOnly: "Posted only",
      periodFrom: "Period from", periodTo: "Period to",
      includeChildGroups: "Include child groups",
      includeChildLocations: "Include child locations",
      detailedView: "Detailed view",
      includeClosed: "Include closed assets", includeEmptyGroups: "Include empty groups",
      inTransitOnly: "In transit only",
    },
    columns: {
      date: "Date", event: "Event", document: "Document", asset: "Asset",
      description: "Description",
      from: "From", to: "To", status: "Status",
      debit: "Debit", credit: "Credit", costChange: "Cost change", depreciation: "Depreciation",
      bookValue: "Book value", voucher: "Voucher",
      assetNumber: "Asset no.", assetName: "Asset name", serialNumber: "Serial number",
      group: "Group", location: "Location", custodian: "Custodian", supplier: "Supplier",
      acquired: "Acquired", acquisitionCost: "Acquisition cost",
      additions: "Capitalised additions", revaluation: "Revaluation",
      cost: "Cost", accumulated: "Accum. depreciation",
      code: "Code", level: "Level", ownAssets: "Own assets", ownCost: "Own cost",
      totalAssets: "Total assets", totalCost: "Total cost",
      treatment: "Treatment", expenseAccount: "Expense account", costCentre: "Cost centre",
      original: "Original", returned: "Returned", net: "Net",
      fromGroup: "From group", toGroup: "To group",
      dispatched: "Dispatched", received: "Received", daysInTransit: "Days in transit",
    },
    figures: {
      events: "Events", transactions: "Transactions", assets: "Assets",
      groups: "Groups", expenses: "Expenses", transfers: "Transfers",
      acquired: "Acquired", closed: "Closed", daysHeld: "Days held",
      cost: "Cost", accumulated: "Accumulated", bookValue: "Book value",
      acquisitionCost: "Acquisition cost", additions: "Additions", revaluation: "Revaluation",
      totalDebit: "Total debit", totalCredit: "Total credit",
      costIncrease: "Cost increase", costDecrease: "Cost decrease",
      totalDepreciation: "Total depreciation", closingBookValue: "Closing book value",
      custodyEvents: "Custody", locationEvents: "Location",
      transferEvents: "Transfers", inventoryEvents: "Inventory",
      posted: "Posted", unposted: "Unposted", operational: "Operational",
      original: "Original", returned: "Returned",
      operating: "Operating", capitalised: "Capitalised", net: "Net",
      reclassified: "Reclassified", costMoved: "Cost moved",
    },
    kinds: {
      acquisition: "Acquisition", purchaseReturn: "Purchase return",
      custodyIssue: "Custody issue", custodyDeposit: "Custody deposit",
      custodyReturn: "Custody return", custodyTransfer: "Custody transfer",
      locationMovement: "Location change", transfer: "Transfer",
      insurance: "Insurance", maintenance: "Maintenance", inventoryCount: "Inventory count",
      depreciation: "Depreciation", expense: "Expense", expenseReturn: "Expense return",
      revenue: "Revenue", revaluation: "Revaluation", sale: "Sale", disposal: "Disposal",
      split: "Split",
    },
    assetStatus: {
      draft: "Draft", inService: "In service", suspended: "Suspended",
      fullyDepreciated: "Fully depreciated", sold: "Sold", disposed: "Disposed",
      writtenOff: "Written off", returned: "Returned to supplier",
    },
    transferStatus: {
      draft: "Draft", approved: "Approved", dispatched: "Dispatched",
      received: "Received", cancelled: "Cancelled",
    },
    treatment: { expensed: "Charged to expense", capitalised: "Capitalised onto the asset" },
  },
  cashBanksSettings: {
    scoped: {
      financialIntegrationTitle: "Financial Integration Settings — Cash and Banks",
      financialIntegrationBreadcrumb: "General Accounting « Cash and Banks « Settings « Financial Integration Settings",
      financialIntegrationNotice: "The link accounts Cash and Banks owns, and only those. Accounts are chosen from the company's own chart; nothing is hard-coded.",
      reportSignaturesTitle: "Report Signatures — Cash and Banks",
      reportSignaturesBreadcrumb: "General Accounting « Cash and Banks « Settings « Report Signatures",
      reportSignaturesNotice: "The signature blocks printed beneath Cash and Banks reports. The same shared record, opened on this module's scope.",
      bankOpeningTitle: "Bank Opening Balances",
      bankOpeningBreadcrumb: "General Accounting « Cash and Banks « Settings « Bank Opening Balances",
      bankOpeningNotice: "The opening balances of bank accounts, taken from the same approved opening-balance sheet the General Ledger uses — there is no second balances table.",
      cashboxOpeningTitle: "Cashbox Opening Balances",
      cashboxOpeningBreadcrumb: "General Accounting « Cash and Banks « Settings « Cashbox Opening Balances",
      cashboxOpeningNotice: "The opening balances of cashbox accounts, taken from the same approved opening-balance sheet the General Ledger uses — there is no second balances table.",
      sharedScreenNotice: "This is the authoritative screen its owning module maintains, not a copy of it: an edit here edits the same record.",
    },
    common: {
      code: "Code", nameAr: "Arabic Name", nameEn: "English Name",
      displayOrder: "Order", active: "Active", notes: "Notes", actions: "Actions",
      edit: "Edit", activate: "Activate", deactivate: "Deactivate",
      save: "Save", saveNew: "Save & New", cancel: "Cancel", newRecord: "New Record",
      search: "Search", refresh: "Refresh", showInactive: "Show inactive",
      noRows: "No records.", counts: "Total / Active", usage: "In use",
      readOnlyNotice: "You have view access only. Editing is not permitted.",
      saved: "Saved.", activated: "Activated.", deactivated: "Deactivated.",
      loadError: "Could not load the records.", saveError: "Could not save.",
      formTitle: "Details", listTitle: "Records",
    },
    cashboxGroups: {
      title: "Cashbox Groups",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Cashbox Groups",
      purposeNotice: "How the company groups its cashboxes for administration and reporting. A group holds no account, no balance, and changes no posting.",
      scopeNotice: "A group is not the tree: the tree says which cashbox sits under which, a group collects cashboxes that may be in different branches. There is no delete — a group in use is deactivated.",
      listTitle: "Groups", newRecord: "New Group",
      assignedCashboxes: "Cashboxes",
    },
    cashboxCashFloat: {
      title: "Cashbox Cash Float",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Cashbox Cash Float",
      purposeNotice: "What each cashbox is authorised to hold in cash, per currency. An administrative figure that posts nothing.",
      scopeNotice: "The authorised float is the company's decision; the balance is what the cash account actually says. The two are separate figures, and being over float never blocks a posting on its own.",
      listTitle: "Authorised Floats", formTitle: "Details", newRecord: "New Float",
      cashbox: "Cashbox", selectCashbox: "— Select a cashbox —",
      currency: "Currency", selectCurrency: "— Select a currency —",
      floatAmount: "Authorised Amount", effectiveFrom: "Effective From",
      custodianAr: "Custodian (Arabic)", custodianEn: "Custodian (English)",
      currentBalance: "Current Balance", variance: "Variance",
      noAccount: "No cash account mapped", overFloat: "Over float",
      totalAuthorised: "Total authorised", overFloatCount: "Over float",
    },
    letterOfCreditTypes: {
      title: "Letter of Credit Types",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Letter of Credit Types",
      purposeNotice: "The company's own classification of the credits it opens — sight, deferred payment, revolving — as its trade practice describes them.",
      scopeNotice: "A classification only, never a lifecycle: the credit's status remains the authoritative statement of where it stands. Credits opened before this list existed are not retro-classified.",
      listTitle: "Types", newRecord: "New Type",
      defaultMargin: "Default Margin %",
    },
    chequeReturnReasons: {
      title: "Cheque Return Reasons",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Cheque Return Reasons",
      purposeNotice: "The reasons a bank refuses a cheque, so returns become countable in reports instead of free text typed three different ways.",
      scopeNotice: "The free-text reason recorded on cheques already returned stays exactly as it was written. This list governs new returns.",
      listTitle: "Reasons", newRecord: "New Reason",
      drawerFault: "Drawer's fault", drawerFaultHint: "A classification for follow-up only; it changes no journal.",
    },
    userCashboxAccess: {
      title: "Link Users to Cashboxes",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Link Users to Cashboxes",
      purposeNotice: "Which cashboxes each user may work. The restriction is applied server-side across every screen and inquiry, never by hiding a control.",
      enforcementOffNotice: "Enforcement is currently off in Cash and Banks system settings, so these grants restrict nobody yet.",
      enforcementOnNotice: "Enforcement is on. A user with no grants at all stays unrestricted — so switching it on cannot lock a company out before anybody is mapped.",
      readOnlyNotice: "You have view access only. Editing is not permitted.",
      userSection: "User", user: "User", selectUser: "— Select a user —",
      userInactive: "Account is inactive", searchUsers: "Search users",
      grantsSection: "Cashboxes", cashboxCode: "Code", cashboxName: "Cashbox",
      allowed: "Allowed", grantedAt: "Granted", cashboxInactive: "Cashbox is closed or inactive",
      grantAll: "Grant all", revokeAll: "Revoke all",
      noUser: "Select a user to see their cashboxes.", noRows: "No cashboxes are defined.",
      grantedCount: "Cashboxes granted",
      saved: "Saved.", loadError: "Could not load the mapping.", saveError: "Could not save.",
    },
    system: {
      title: "Cash and Banks System Settings",
      breadcrumb: "General Accounting « Cash and Banks « Settings « Cash and Banks System Settings",
      purposeNotice: "The company-level rules this module works to. Every setting here is read by a real operational path.",
      prospectiveNotice: "All of these apply to what happens next: no posted voucher, recorded cheque or completed reconciliation is re-examined or rewritten.",
      readOnlyNotice: "You have view access only. Editing is not permitted.",
      cashboxSection: "Cashboxes", chequeSection: "Cheques", bankSection: "Banks",
      preventNegative: "Prevent a cashbox going below zero",
      preventNegativeHint: "Checked when a cash withdrawal is posted. Nothing already posted is re-examined.",
      enforceAccess: "Enforce user-cashbox links",
      enforceAccessHint: "When on, cashboxes are narrowed to the ones granted to the user across every screen and inquiry.",
      enforceAccessEmpty: "No user holds a cashbox grant yet, so turning this on would restrict nobody.",
      defaultCashbox: "Default cashbox", defaultBankAccount: "Default bank account",
      none: "— None —",
      requireLeaf: "Require outgoing cheques to draw a registered leaf",
      requireLeafHint: "Checked when an outgoing cheque is issued.",
      requireReason: "Require a cheque return reason from the list",
      requireReasonHint: "Checked on both the incoming and the outgoing return path.",
      requireReasonEmpty: "No return reason is defined yet. Turning this on would stop returns being recorded.",
      defaultTerm: "Default cheque term",
      termImmediate: "Immediate", termPostDated: "Post-dated",
      save: "Save", restoreDefaults: "Restore defaults",
      restoreConfirm: "Restore every setting to its shipped default?",
      saved: "Settings saved.", restored: "Defaults restored.",
      loadError: "Could not load the settings.", saveError: "Could not save the settings.",
    },
  },
  custodyCategories: {
    title: "Custody Categories",
    breadcrumb: "Financial Management › Receivables › Settings › Custody Categories",
    purposeNotice: "The approved custody vocabulary. This screen is configuration only.",
    scopeNotice:
      "A category carries no account, no limit and no posting rules: this build has no custody process to consume them yet.",
    readOnlyNotice: "You have view-only access. Nothing here can be changed.",
    listTitle: "Categories",
    formTitle: "Details",
    newRecord: "New Category",
    search: "Search",
    refresh: "Refresh",
    showInactive: "Show inactive",
    noRows: "No categories yet",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    active: "Active",
    actions: "Actions",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    save: "Save",
    saveNew: "Save & New",
    cancel: "Cancel",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    close: "Close",
    saved: "Saved",
    activated: "Activated",
    deactivated: "Deactivated",
    counts: "Total / Active",
    loadError: "Custody categories could not be loaded",
    saveError: "The category could not be saved",
    auditError: "The audit history could not be loaded",
  },
  countryRegions: {
    title: "Countries & Regions",
    breadcrumb: "Financial Management › Receivables › Settings › Countries & Regions",
    purposeNotice:
      "The tree of countries and the regions and cities beneath them. A root record is a country; every child beneath it is a region, area or city.",
    timeZoneNotice:
      "This system has no time-zone table. The list comes from the platform's own zone database — the same source the save validates against.",
    readOnlyNotice: "You have view-only access. Nothing here can be changed.",
    treeTitle: "Tree",
    formTitle: "Details",
    newRoot: "New Country",
    newChild: "New Child",
    search: "Search",
    refresh: "Refresh",
    showInactive: "Show inactive",
    noRows: "No records",
    parent: "Parent",
    noParent: "No parent (country)",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    nationalityAr: "Arabic Nationality",
    nationalityEn: "English Nationality",
    currency: "Currency",
    timeZone: "Time Zone",
    shortCode: "Short Code",
    internationalCode: "International Code",
    mobileDigits: "Mobile digits",
    documentDigits: "Document digits",
    isChild: "Child record",
    active: "Active",
    none: "None",
    flagSection: "Country Flag",
    flagUpload: "Upload image",
    flagReplace: "Replace",
    flagRemove: "Remove",
    flagNone: "No image",
    flagHint: "PNG, JPEG, GIF, WEBP or SVG, up to 1 MB.",
    flagRemoveConfirm: "Remove this country's flag?",
    save: "Save",
    saveNew: "Save & New",
    cancel: "Cancel",
    deactivate: "Deactivate",
    activate: "Activate",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    close: "Close",
    country: "Country",
    region: "Region",
    level: "Level",
    children: "Children",
    saved: "Saved",
    activated: "Activated",
    deactivated: "Deactivated",
    flagSaved: "Flag saved",
    flagRemoved: "Flag removed",
    loadError: "Countries and regions could not be loaded",
    saveError: "The record could not be saved",
    auditError: "The audit history could not be loaded",
    flagError: "The flag image could not be processed",
  },
  userSupplierAccess: {
    title: "User-Supplier Access",
    breadcrumb: "Financial Management › Payments › Master Data › User-Supplier Access",
    purposeNotice:
      "The administrator decides which suppliers each user may work with, at three independent levels.",
    denyByDefaultNotice:
      "A user with no mappings for a level sees no suppliers at that level. Absence means denied, not unrestricted.",
    readOnlyNotice: "You have view-only access. Mappings cannot be changed.",
    notIntegratedNotice:
      "Daily Transactions is the level enforced today. Inquiries and Reports are stored and ready, and switch on when their Payments screens are built.",
    userSection: "User",
    user: "User",
    selectUser: "Select a user",
    userInactive: "This user is deactivated. Existing mappings are read-only and no new ones are accepted.",
    dailySection: "Supplier access for daily transactions",
    inquirySection: "Supplier access for inquiries",
    reportSection: "Supplier access for reports",
    partnerCode: "Supplier Code",
    partnerName: "Supplier Name",
    allowed: "Allowed",
    actions: "Actions",
    add: "Add Supplier",
    remove: "Remove",
    search: "Search",
    refresh: "Refresh",
    noRows: "No supplier is mapped at this level",
    noUser: "Select a user to see their supplier mappings",
    partnerInactive: "Inactive supplier",
    pickerTitle: "Add Supplier",
    pickerSearch: "Search by code or name",
    pickerNone: "No supplier is available to add at this level",
    cancel: "Cancel",
    confirmRemove: "Remove this supplier mapping at this level?",
    granted: "Added",
    removed: "Removed",
    toggled: "Updated",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    auditFields: "Fields",
    close: "Close",
    loadError: "The mappings could not be loaded",
    saveError: "The change could not be saved",
    auditError: "The audit history could not be loaded",
  },
  userCustomerAccess: {
    title: "User-Customer Access",
    breadcrumb: "Financial Management › Receivables › Settings › User-Customer Access",
    purposeNotice:
      "The administrator decides which customers each user may work with, at three independent levels.",
    denyByDefaultNotice:
      "A user with no mappings for a level sees no customers at that level. Absence means denied, not unrestricted.",
    readOnlyNotice: "You have view-only access. Mappings cannot be changed.",
    notIntegratedNotice:
      "Daily Transactions is the level enforced today. Inquiries and Reports are stored and ready, and switch on when their Receivables screens are built.",
    userSection: "User",
    user: "User",
    selectUser: "Select a user",
    userInactive: "This user is deactivated. Existing mappings are read-only and no new ones are accepted.",
    dailySection: "Customer access for daily transactions",
    inquirySection: "Customer access for inquiries",
    reportSection: "Customer access for reports",
    partnerCode: "Customer Code",
    partnerName: "Customer Name",
    allowed: "Allowed",
    actions: "Actions",
    add: "Add Customer",
    remove: "Remove",
    search: "Search",
    refresh: "Refresh",
    noRows: "No customer is mapped at this level",
    noUser: "Select a user to see their customer mappings",
    partnerInactive: "Inactive customer",
    pickerTitle: "Add Customer",
    pickerSearch: "Search by code or name",
    pickerNone: "No customer is available to add at this level",
    cancel: "Cancel",
    confirmRemove: "Remove this customer mapping at this level?",
    granted: "Added",
    removed: "Removed",
    toggled: "Updated",
    auditHistory: "Audit History",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditChangedBy: "Changed by",
    auditChangedAt: "Changed at",
    auditFields: "Fields",
    close: "Close",
    loadError: "The mappings could not be loaded",
    saveError: "The change could not be saved",
    auditError: "The audit history could not be loaded",
  },
  smsSettings: {
    title: "SMS Settings",
    breadcrumb: "Financial Management › Receipts › Settings › SMS Settings",
    purposeNotice: "The customer SMS reminder policy: the monthly send time, the sending window and the message.",
    unavailableNotice:
      "This build has no SMS transport and no scheduler. The settings are stored; nothing is sent automatically yet.",
    readOnlyNotice: "You have view-only access. Settings cannot be edited.",
    shortMonthNotice:
      "The day is stored exactly as entered. What happens in months without the chosen day (29 · 30 · 31) is the sender's decision and has not been approved.",
    scheduleSection: "Monthly send time",
    day: "Day",
    hour: "Hour",
    minute: "Minute",
    windowSection: "Sending window",
    startMonth: "From month",
    endMonth: "To month",
    messageSection: "Message",
    messageText: "Text sent to the customer",
    messageHint: "Required once automatic sending is enabled.",
    automaticSending: "Automatic sending",
    active: "Active",
    save: "Save",
    cancel: "Cancel",
    restoreDefaults: "Restore Defaults",
    restoreConfirm: "Every setting will return to its default value. Continue?",
    auditHistory: "Audit History",
    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditChangedBy: "Changed By",
    auditChangedAt: "Changed At",
    close: "Close",
    lastChanged: "Last changed",
    saved: "Settings saved",
    restored: "Settings restored to defaults",
    loadError: "Could not load the settings.",
    saveError: "Could not save.",
    auditError: "Could not load the audit history.",
  },
  ePaymentPolicy: {
    title: "Electronic Payment Policy",
    breadcrumb: "Financial Management › Receipts › Settings › Electronic Payment Policy",
    purposeNotice:
      "Define the payment card types, then the commission policy each carries per branch as value ranges.",
    configurationNotice:
      "Configuration only: nothing posts and no balance moves. Later card payments read this.",
    readOnlyNotice: "You have view-only access. Nothing can be edited.",
    sections: "Payment card settings",
    cardTypesSection: "Payment Card Types",
    commissionSection: "Commission Policy",
    newCardType: "New card type",
    edit: "Edit",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    active: "Active",
    inactive: "Inactive",
    usage: "Policies",
    list: "Card types",
    search: "Search",
    searchPlaceholder: "Search by code or description",
    showInactive: "Show inactive",
    refresh: "Refresh",
    save: "Save",
    saveAndNew: "Save and New",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    actions: "Actions",
    empty: "No card types defined.",
    cardType: "Payment card type",
    branch: "Branch",
    chooseCardType: "— Select a card type —",
    chooseBranch: "— Select a branch —",
    ranges: "Commission ranges",
    fromAmount: "From amount",
    toAmount: "To amount",
    openEnded: "Open",
    commissionPercent: "Commission %",
    commissionAmount: "Commission amount",
    addRange: "Add range",
    removeRange: "Remove",
    policyList: "Defined policies",
    noPolicies: "No policies defined.",
    rangesCount: "ranges",
    savePolicy: "Save policy",
    editPolicy: "Edit policy",
    newPolicy: "New policy",
    saved: "Card type saved",
    policySaved: "Policy saved",
    activated: "Type activated",
    deactivated: "Type deactivated",
    loadError: "Could not load.",
    saveError: "Could not save.",
  },
  discountTypesScreen: {
    title: "Discount Types",
    breadcrumb: "Financial Management › Receipts › Settings › Discount Types",
    purposeNotice:
      "The approved kinds of discount. Each names the account its discount will later post to.",
    readOnlyNotice: "You have view-only access. Types cannot be added or edited.",
    newType: "New type",
    edit: "Edit",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    calculationType: "Discount Type",
    percentage: "Percentage",
    fixedAmount: "Fixed amount",
    account: "Account",
    accountHint: "An active, postable leaf account from the Chart of Accounts.",
    isDefault: "Default",
    active: "Active",
    inactive: "Inactive",
    list: "Defined types",
    search: "Search",
    searchPlaceholder: "Search by code or description",
    showInactive: "Show inactive",
    refresh: "Refresh",
    save: "Save",
    saveAndNew: "Save and New",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    actions: "Actions",
    empty: "No discount types defined.",
    auditHistory: "Audit History",
    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditAction: "Action",
    auditChangedBy: "Changed By",
    auditChangedAt: "Changed At",
    close: "Close",
    lastChanged: "Last changed",
    yes: "Yes",
    no: "No",
    saved: "Type saved",
    activated: "Type activated",
    deactivated: "Type deactivated",
    loadError: "Could not load the types.",
    saveError: "Could not save.",
    auditError: "Could not load the audit history.",
  },
  workflowActions: {
    title: "Workflow Actions",
    breadcrumb: "Financial Management › Receipts › Settings › Workflow Actions",
    purposeNotice:
      "The approved steps a company's working procedure runs through. A step marked mandatory is one the user must complete while raising the daily transactions it applies to.",
    scopeNotice:
      "Definitions only at this stage. There is no model yet that binds a step to a particular transaction, so nothing is enforced on any transaction today. The voucher lifecycle (Draft → Audited → Posted) is fixed in code and is not configured here.",
    readOnlyNotice: "You have view-only access. Actions cannot be added or edited.",
    newAction: "New action",
    edit: "Edit",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    sequence: "Order",
    mandatory: "Mandatory",
    active: "Active",
    inactive: "Inactive",
    list: "Defined actions",
    search: "Search",
    searchPlaceholder: "Search by code or description",
    showInactive: "Show inactive",
    refresh: "Refresh",
    save: "Save",
    saveAndNew: "Save and New",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    actions: "Actions",
    empty: "No actions defined.",
    auditHistory: "Audit History",
    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditAction: "Action",
    auditChangedBy: "Changed By",
    auditChangedAt: "Changed At",
    close: "Close",
    lastChanged: "Last changed",
    yes: "Yes",
    no: "No",
    saved: "Action saved",
    activated: "Action activated",
    deactivated: "Action deactivated",
    loadError: "Could not load the actions.",
    saveError: "Could not save.",
    auditError: "Could not load the audit history.",
  },
  receivablesLinks: {
    title: "Financial Link Settings",
    breadcrumb: "Financial Management › Receipts › Settings › Financial Link Settings",
    purposeNotice:
      "Nine predefined slots that later Receivables transactions will read. Nothing posts here and no balance changes.",
    readOnlyNotice: "You have view-only access. Links cannot be edited.",
    unlinkedNotice:
      "An unlinked slot stays on the grid. Clearing empties the account; it never removes the slot or its history.",
    accountsSection: "Accounts",
    account: "Account",
    linkLevel: "Link Level",
    detailPreview: "Detail Preview",
    actions: "Actions",
    companyLevel: "Company",
    companyLevelFixed: "These slots are always mapped at company level.",
    linked: "Linked",
    notLinked: "Not linked",
    linkedOf: "{linked} of {total} linked",
    edit: "Edit",
    clearLink: "Clear Link",
    clearConfirm: "The account will be removed from this slot; the slot and its history stay. Continue?",
    save: "Save",
    cancel: "Cancel",
    search: "Search",
    searchPlaceholder: "Search by slot name or account number",
    refresh: "Refresh",
    print: "Print",
    auditHistory: "Audit History",
    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditAction: "Action",
    auditChangedBy: "Changed By",
    auditChangedAt: "Changed At",
    close: "Close",
    chooseAccount: "Financial account",
    accountHint: "An active, postable leaf account from this company's Chart of Accounts.",
    requiredTypeHint: "This slot accepts accounts of type: ",
    detailsTitle: "Link details",
    detailLinkType: "Financial link type",
    detailLinkLevel: "Link level",
    detailCompany: "Company",
    detailAccountNumber: "Account number",
    detailAccountNameAr: "Arabic account name",
    detailAccountNameEn: "English account name",
    detailStatus: "Status",
    detailChangedBy: "Last changed by",
    detailChangedAt: "Last changed at",
    active: "Active",
    inactive: "Inactive",
    saved: "Link saved",
    cleared: "Link cleared",
    loadError: "Could not load the link slots.",
    saveError: "Could not save.",
    printError: "Could not print.",
    auditError: "Could not load the audit history.",
    typeExpense: "Expense",
    typeRevenue: "Revenue",
  },
  receivablesSettings: {
    title: "System Settings",
    breadcrumb: "Financial Management › Receipts › Settings › System Settings",
    purposeNotice:
      "Settings for this company alone. A rule that makes a field mandatory applies to saves from now on; cards recorded earlier stay as they are until they are next edited.",
    readOnlyNotice: "You have view-only access. Settings cannot be edited.",
    pendingModulesNotice:
      "Some settings describe modules that are not built yet (receipt vouchers, financing, promissory notes, aging). They are stored now so those modules read a decision already made; nothing reads them today.",
    description: "Description",
    value: "Value",
    sectionCustomerCard: "Customer Card",
    sectionReceipts: "Receipts & Collection",
    sectionAging: "Aging",
    sectionFinancing: "Financing",
    sectionPaymentTax: "Payment Methods & Tax",
    sectionCheques: "Cheques / Promissory Notes",
    save: "Save",
    cancel: "Cancel",
    restoreDefaults: "Restore Defaults",
    restoreConfirm: "Every setting will return to its default value. Continue?",
    auditHistory: "Audit History",
    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditSetting: "Setting",
    auditOldValue: "Old Value",
    auditNewValue: "New Value",
    auditChangedBy: "Changed By",
    auditChangedAt: "Changed At",
    close: "Close",
    yes: "Yes",
    no: "No",
    choose: "— Select —",
    none: "— None —",
    lastChanged: "Last changed",
    saved: "Settings saved",
    restored: "Settings restored to defaults",
    loadError: "Could not load the settings.",
    saveError: "Could not save.",
    auditError: "Could not load the audit history.",

    requireNationalOrTaxNumber: "Require National ID or Tax Number in Customer and Supplier Card",
    requireCustomerCategory: "Require Customer Category",
    requireAddressAndMobile: "Require Address and Mobile in Customer Card",
    requireCustomerTypeClassAndNature: "Require Customer Type, Customer Class and Business Nature",
    defaultCustomerCreditLimit: "Default Customer Credit Limit",
    defaultCustomerPaymentMethod: "Default Payment Method for Customer Card",
    defaultCustomerPriceTaxTreatment: "Default Customer Price Tax",
    hideCollectorFromVouchers: "Hide Collector from Vouchers",
    linkReferenceWithCollector: "Link Reference with Collector",
    electronicPaymentSalesRepresentative: "Sales Representative for Electronic Payment",
    enableReceiptCommissionEntry: "Enable Commission Journal Entry on Receipt Voucher",
    allowedSettlementDifference:
      "Allowed Difference Between Receipt Amount and Invoice Amount During Settlement",
    maximumDeliveriesToSamePerson: "Maximum Number of Deliveries to the Same Person",
    minimumCustomerResponsePercentage: "Minimum Customer Response Percentage",
    legalSettlementAlertDays: "Alert Days Before Legal Settlement Payment Due Date",
    paymentMethodEnforcementSource: "Enforce Customer Payment Method",
    createCustomerReceivablesForChequesInCollection:
      "Create Customer Receivables for Cheques in Collection",
    customerAgingGracePeriodDays: "Customer Grace Period in Customized Aging Inquiry",
    receivablesOpeningBalanceDate: "Opening Balance Date (Receivables Aging)",
    useOpeningBalanceDateAsAgingStart: "Use Opening Balance Date as Aging Start Date",
    agingInvoiceDateBasis: "Invoice Date Calculation Method in Aging",
    showNotYetDueAndAdvancePaymentsInAging:
      "Show Not-Yet-Due Balance and Advance Payments in Aging Inquiry",
    customerFinancingType: "Customer Financing Type",
    accountingFinancingType: "Accounting Financing Type",
    financingCostBasis: "Financing Cost Calculation Type",
    deferredInterestBasis: "Deferred / Scheduled Interest Calculation Method",
    decliningInterestMultiplier: "Declining Interest Multiplier for Financing Request",
    decliningInterestOnBalanceBeforePayment:
      "Calculate Declining Interest Based on Balance Before Payment",
    createIntermediateAccountInFinancingRequest:
      "Create Intermediate Account in Financing Request Voucher",
    promissoryNoteDelayPenalty: "Delay Penalty for Promissory Notes",
    hideCommissionFieldsInScheduling: "Hide Commission Fields in Promissory Note / Cheque Scheduling",

    financingFixed: "Fixed",
    financingDeclining: "Declining",
    costBasisCurrencies: "Currencies",
    interestMonthly: "Monthly",
    enforcementCustomerCard: "Customer Card",
    agingByInvoiceDate: "Invoice Date",
    agingByDueDate: "Due Date",
    paymentCash: "Cash",
    paymentCredit: "Credit",
    taxInclusive: "Inclusive",
    taxExclusive: "Exclusive",
    taxExempt: "Exempt",
  },
  partnerLookups: {
    title: "Customer and Supplier Classifications",
    breadcrumb: "Financial Management › Receipts › Settings › Classifications",
    lockedBreadcrumb: "Financial Management › Receipts › Settings › {list}",
    purposeNotice:
      "Fixed lists the customer and supplier card reads. A code and an Arabic description are unique WITHIN one list, so the same code may legitimately serve a different list.",
    readOnlyNotice: "You have view-only access. Classifications cannot be added or edited.",
    vocabulary: "List",
    allVocabularies: "— All lists —",
    newValue: "New value",
    edit: "Edit",
    code: "Code",
    descriptionAr: "Arabic Description",
    descriptionEn: "English Description",
    active: "Active",
    inactive: "Inactive",
    collector: "Collector",
    noCollector: "— None —",
    taxCode: "Tax Code",
    usage: "Usage",
    list: "Defined values",
    search: "Search",
    searchPlaceholder: "Search by code or description",
    showInactive: "Show inactive",
    refresh: "Refresh",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    actions: "Actions",
    empty: "No values defined.",
    inUseHint: "A value used by partner cards cannot be deleted; deactivate it instead.",
    saved: "Value saved",
    activated: "Value activated",
    deactivated: "Value deactivated",
    loadError: "Could not load the classifications.",
    saveError: "Could not save.",
    types: {
      category: "Category",
      importance: "Importance",
      partnerType: "Customer Type",
      class: "Customer Classes",
      title: "Title",
      nature: "Business Nature",
      source: "Source",
      nationality: "Nationality",
      parentClassification: "Parent Classification",
      reportTemplate: "Report Template",
      salePriceType: "Sale Price Type",
      commercialActivity: "Commercial Activities",
      documentType: "Document Types",
    },
  },
  partners: {
    delete: "Delete", deleteConfirm: "Delete «{name}» for good? Refused if any document names it.",
    deleted: "Card deleted.", deleteError: "Could not delete.",
    settlementActiveLabel: "Active",
    settlementCancelledOnCard: "Cancelled",
    settlementsTitle: "Settlements",
    settlementInvoice: "Invoice number",
    settlementReceipt: "Receipt number",
    settlementAmountCol: "Settled amount",
    settlementDateCol: "Settlement date",
    settlementStatusCol: "Status",
    settlementNoneOnCard: "This customer has no settlements.",
    settlementReadOnly: "Read-only. Settlement and cancellation are performed from the Customer Settlement screen.",
    creditSectionTitle: "Credit information",
    creditPickCustomer: "Choose a customer from the list below (Edit) to see and change their credit limit.",
    creditLimitLocked: "The credit limit is not changed from this form. Use the Change credit limit action — it needs its own permission and a reason, and it is recorded in the history.",
    creditNoEnforcement: "Note: no document currently blocks a sale when the limit is exceeded. In this build the limit is an approval figure only.",
    currentBalance: "Current balance",
    balanceExceedsLimit: "The customer's current balance exceeds the new credit limit.",
    changeCreditLimit: "Change credit limit",
    newCreditLimit: "New credit limit",
    changeReason: "Reason for the change",
    creditHistoryTitle: "Credit limit history",
    previousLimit: "Previous limit",
    newLimit: "New limit",
    changedBy: "Changed by",
    changedAt: "Date",
    balanceAtChange: "Balance at the change",
    noCreditHistory: "No credit limit changes recorded.",
    creditLimitChanged: "Credit limit changed. The accounting balance was not affected.",
    confirmCreditChange: "The customer's credit limit will change. This change does not affect the customer's current accounting balance.",
    creditZeroAccounting: "Changing the limit creates no journal entry and does not change the customer's balance.",
    customersTitle: "Customers",
    suppliersTitle: "Suppliers",
    customersBreadcrumb: "Financial Management › Receipts › Basic Data › Customers",
    suppliersBreadcrumb: "Financial Management › Payments › Basic Data › Suppliers",
    customerSingular: "Customer",
    supplierSingular: "Supplier",
    purposeNotice:
      "The card identifies the party and links it to its account in the Chart of Accounts. Balances and movements are read from the linked account; nothing is posted here.",
    readOnlyNotice: "You have view-only access. Cards cannot be added or edited.",

    sectionBasic: "Basic Information",
    sectionFinancial: "Financial Information",
    sectionContact: "Contact Information",
    sectionLink: "Financial Link Settings",
    sectionAddress: "Detailed Address Information",

    code: "Code",
    nameAr: "Arabic Name",
    nameEn: "English Name",
    startDate: "Start Date",
    category: "Category",
    importance: "Importance",
    partnerTypeLookup: "Customer Type",
    class: "Customer Class",
    supplierTypeLookup: "Supplier Type",
    supplierClass: "Supplier Class",
    title: "Title",
    nature: "Business Nature",
    source: "Source",
    nationality: "Nationality",
    parentClassification: "Parent Classification",
    reportTemplate: "Report Template",
    parentPartner: "Belongs to",
    noParent: "— None —",
    nationalIdIndividual: "National ID (Individual)",
    nationalIdCompany: "National ID (Company)",
    commercialRegistration: "Commercial Registration",
    referenceNumber: "Reference Number",
    notes: "Notes",

    controlAccount: "Control Account",
    controlAccountHint: "A postable leaf account from the Chart of Accounts — this party's movements post to it.",
    currency: "Currency",
    creditLimit: "Credit Limit",
    taxTreatment: "Tax Treatment",
    taxInclusive: "Tax inclusive",
    taxExclusive: "Tax exclusive",
    taxExempt: "Exempt",
    paymentMethod: "Payment Method",
    paymentCash: "Cash",
    paymentCredit: "Credit",
    defaultPaymentMethod: "Default Payment Method",
    defaultPaymentMethodHint: "Copied onto a transaction when this customer is selected; changing it there never changes this field.",
    salePriceType: "Sale Price Type",
    taxNumber: "Tax Number",
    turnoverRate: "Turnover Rate",
    turnoverRateHint: "Expected days to settle.",

    salesRepresentative: "Sales Representative",
    marketer: "Marketer",
    collector: "Collector",
    employee: "Responsible Employee",
    owner: "Owner",
    staffHint: "From the active system users.",

    phone: "Phone",
    mobile: "Mobile",
    fax: "Fax",
    email: "Email",
    website: "Website",
    contactPerson: "Contact Person",

    country: "Country",
    city: "City",
    area: "Area",
    street: "Street",
    building: "Building",
    postalCode: "Postal Code",
    poBox: "P.O. Box",
    address: "Address",
    addressNotes: "Address Notes",
    latitude: "Latitude",
    longitude: "Longitude",
    coordinatesHint: "Map coordinates, optional.",

    active: "Active",
    inactive: "Inactive",
    blacklisted: "Blacklisted",
    blacklistReason: "Blacklist Reason",
    blacklistHint: "A blacklisted party is withheld from new transactions; existing ones are untouched.",

    newRecord: "New card",
    edit: "Edit",
    save: "Save",
    saveAndNew: "Save and New",
    cancel: "Cancel",
    print: "Print",
    auditHistory: "Audit History",
    activate: "Activate",
    deactivate: "Deactivate",
    list: "Records",
    inquiry: "Inquiry", inquiryTitle: "Defined records",
    inquiryEmpty: "No card matches the search.",
    search: "Search",
    searchPlaceholder: "Search by code, name or phone",
    showInactive: "Show inactive",
    refresh: "Refresh",
    actions: "Actions",
    empty: "No records.",
    status: "Status",
    choose: "— Select —",
    chooseCountryFirst: "Select a country first",
    manageLookups: "Manage classifications",

    auditNone: "No recorded changes.",
    auditFields: "Fields",
    auditCreated: "Created",
    auditUpdated: "Updated",
    auditDeleted: "Deleted",
    close: "Close",

    saved: "Card saved",
    activated: "Card activated",
    deactivated: "Card deactivated",
    loadError: "Could not load the records.",
    saveError: "Could not save.",
    printError: "Could not print.",
    auditError: "Could not load the audit history.",

    identificationRequired: "A national ID or a tax number is required.",
    categoryRequired: "The customer category is required.",
    addressAndMobileRequired: "The address and the mobile number are required.",
    typeClassNatureRequired: "The customer type, class and business nature are required.",
  },
  common: {
    loading: "Loading…",
    notFound: "Page not found",
    notFoundHint: "The link may be wrong, or this screen does not belong to this module",
    retry: "Try again",
    error: "An unexpected error occurred",
    errorTitle: "Error",
    dismiss: "Dismiss",
    optional: "Optional",
    cancel: "Cancel",
  },
  paymentsReports: {
    supplierBlacklist: "Supplier Blacklist Report", blacklisted: "Blacklisted",
    notBlacklisted: "Not blacklisted", reason: "Reason",
    includeAllowed: "Show non-blacklisted too", noBlacklisted: "No blacklisted suppliers",
    flagNote: "A supplier appears because the blacklist flag is set on their card — never inferred from balance or activity.",
    expiringDocuments: "Customer / Supplier Documents Near Expiration",
    party: "Party", partyType: "Party type", customer: "Customer", supplier: "Supplier",
    documentType: "Document type", documentNumber: "Document number", issuedBy: "Issued by",
    issued: "Issued", expires: "Expires", daysLeft: "Days left", withinDays: "Within (days)",
    includeExpired: "Include expired", expired: "Expired", expiringSoon: "Expiring soon",
    noDocuments: "No documents near expiration",
    expiryNote: "Documents with no recorded expiry date never appear: a missing date is not an imminent one.",
    infoChanges: "Customer / Supplier Information Changes Report", whenChanged: "When",
    action: "Action", changedColumns: "Changed columns", user: "User",
    created: "Created", updated: "Updated", parties: "Parties",
    noChanges: "No changes in this range",
    auditNote: "Read from the audit trail itself — never reconstructed by comparing today's card against anything.",
    ageing: "Aging Report", currencyAgeing: "Currency Aging Report", current: "Current",
    over120: "Over 120", undated: "Undated", ageByDueDate: "Age by due date",
    byCurrency: "Split by currency", oldestDocument: "Oldest document",
    documentsCount: "Documents", grandTotal: "Grand total", noOutstanding: "Nothing outstanding",
    bucketNote: "Buckets are fixed at 30/60/90/120 and over, computed from what remains on each document after actual allocations.",
    trialBalance: "Short Trial Balance - Customers & Suppliers", opening: "Opening balance",
    periodDebit: "Period debit", periodCredit: "Period credit", closingDebit: "Closing debit",
    closingCredit: "Closing credit", customers: "Customers", suppliers: "Suppliers",
    noBalances: "No balances in this range",
    ledgerNote: "Balances come from posted ledger entries on each party's control account.",
    orders: "Receipt / Payment Orders Report", side: "Side", receiptOrder: "Receipt order",
    paymentOrder: "Payment order", orderNumber: "Order number", orderAmount: "Order amount",
    settled: "Settled", remaining: "Remaining", outstandingOnly: "Outstanding only",
    noOrders: "No orders in this range",
    ordersNote: "Two separate registers with separate numbering — shown together, never merged. Settled counts posted vouchers only.",
    guarantees: "Guarantees Report", facilityCode: "Facility code", bank: "Bank",
    ceiling: "Ceiling", rate: "Rate %", ceilingExceedable: "Ceiling exceedable",
    includeAllFacilities: "Show all facility types", noGuarantees: "No guarantees recorded",
    guaranteeNote: "A letter of guarantee is recorded as a bank facility here, so the report reads the facility register.",
    invoicesByType: "Service Purchase Invoice Report by Type", groupBy: "Group by",
    byItem: "Purchase item", byAccount: "Expense account", invoices: "Invoices",
    lines: "Lines", sharePercent: "Share %", noInvoices: "No invoices in this range",
    byTypeNote: "\"Type\" reads two ways: the item bought, or the account it posted to — you choose which.",
    capitalInvoices: "Service Invoices Waiting for Asset Integration",
    assetAccount: "Asset account", noCapitalInvoices: "No capital invoices in this range",
    capitalNote: "A line is listed because it posted to an ASSET account. This system has no fixed-asset register yet, so none can already be linked.",
    byAccountStatement: "Supplier Account Statement - Accounts", accountNumber: "Account number",
    accountName: "Account name", movements: "Movements", bySupplier: "Break down by supplier",
    accounts: "Accounts", noMovements: "No movements in this range",
    accountsNote: "Reads the same ledger the supplier statement reads, grouped by account — so its net equals that statement's net.",
  },
  paymentsSettings: {
    signaturesTitle: "Report Signatures",
    signaturesBreadcrumb: "Financial Management / Payments / Settings",
    signaturesNotice: "The signature blocks printed beneath reports. Presentation configuration only — nothing posts, and printing a signature proves nothing about the figures above it. There is no delete: a block already printed on issued reports is deactivated, not erased.",
    criteriaTitle: "Supplier Evaluation Criteria",
    criteriaBreadcrumb: "Financial Management / Payments / Settings",
    criteriaNotice: "What suppliers are judged on, and how much each criterion is worth. This screen defines the yardstick only and records no scores: evaluating a supplier is an operational act with its own workflow.",
    weightNotice: "The total weight is shown, not enforced — otherwise the first criterion could not be saved before the rest were defined.",
    code: "Code", title: "Role", titleAr: "Role (Arabic)",
    signatoryName: "Signatory name", signatoryNameAr: "Signatory name (Arabic)",
    module: "Module", allModules: "All modules", displayOrder: "Display order", notes: "Notes",
    name: "Name", nameAr: "Arabic name", weight: "Weight %", maximumScore: "Maximum score",
    description: "Description", descriptionAr: "Arabic description",
    totalWeight: "Total active weight",
    newRecord: "New record", editRecord: "Edit record", save: "Save", cancel: "Cancel",
    edit: "Edit", activate: "Activate", deactivate: "Deactivate", actions: "Actions",
    status: "Status", active: "Active", inactive: "Inactive",
    search: "Search", searchHint: "Code or name", refresh: "Refresh",
    noSignatures: "No signature blocks defined yet.", noCriteria: "No evaluation criteria defined yet.",
    saved: "Saved.", showInactive: "Show inactive",
  },
  purchaseOrders: {
    title: "Purchase Orders",
    breadcrumb: "Financial Management / Payments / Daily Transactions",
    purposeNotice: "A commitment to a supplier: what the company will take and at what price. Not a payment order, which is an instruction to pay.",
    noAccountingNotice: "Nothing here posts, at any stage. Money moves when the supplier's invoice posts, which is a different document. Invoiced and Paid are read at the moment they are shown — from the invoices naming the order and from the settlement engine — and never stored.",
    newRecord: "New purchase order", editRecord: "Edit purchase order",
    searchHint: "Order number or reference", noRows: "No purchase orders yet.",
    details: "Purchase order detail",
    orderNumber: "Order number", orderDate: "Order date", supplier: "Supplier",
    chooseSupplier: "Choose a supplier", branch: "Branch", currency: "Currency",
    expectedDate: "Expected date", paymentTerm: "Payment term (days)", costCenter: "Cost centre",
    supplierReference: "Supplier reference", taxInclusive: "Tax",
    taxInclusiveHint: "Prices include tax",
    lines: "Lines", addLine: "Add line", removeLine: "Remove", item: "Item",
    description: "Description", quantity: "Quantity", unitPrice: "Unit price",
    discount: "Discount", tax: "Tax", lineNet: "Line net", subtotal: "Subtotal",
    netTotal: "Total", invoiced: "Invoiced", paid: "Paid", remaining: "Remaining",
    beforeTax: "Before tax", taxComputedNote: "Tax is computed on the server at the rate in force on the order date.",
    status: "Status", draft: "Draft", approved: "Approved", closed: "Closed",
    cancelled: "Cancelled", actions: "Actions", view: "View", edit: "Edit",
    approve: "Approve", close: "Close", cancel: "Cancel", save: "Save", none: "None",
    saved: "Purchase order saved.", approvedOk: "Purchase order approved.",
    closedOk: "Purchase order closed.", cancelledOk: "Purchase order cancelled.",
  },
  purchaseItems: {
    title: "Purchase Items",
    breadcrumb: "Financial Management / Payments / Master Data",
    purposeNotice: "The catalogue of what the company buys. Separate from service sales types: the company buys things it never sells.",
    defaultsNotice: "Nothing here posts. The account, tax and cost centre are DEFAULTS a document copies once and then owns — changing one later never moves a posted figure. There is no delete: an item in use is deactivated, not erased.",
    newRecord: "New item", editRecord: "Edit item",
    searchHint: "Code or name", noRows: "No purchase items yet.",
    code: "Code", name: "Name", nameAr: "Arabic name", itemType: "Item type",
    service: "Service", goods: "Goods", unit: "Unit",
    defaultExpenseAccount: "Default expense account", defaultTaxType: "Default tax type",
    defaultCostCenter: "Default cost centre", usageCount: "Used by",
    status: "Status", active: "Active", actions: "Actions", edit: "Edit",
    activate: "Activate", deactivate: "Deactivate", save: "Save", notes: "Notes",
    none: "None", saved: "Purchase item saved.",
  },
  paymentsInquiries: {
    title: "Payments Inquiries",
    subtitle: "Nine read-only screens over the payables side. None of them posts an entry or edits a document.",
    readOnly: "Read-only screen — it creates and changes nothing.",
    scopeNote: "Only the suppliers you may inquire on are shown.",
    supplier: "Supplier",
    allSuppliers: "All suppliers",
    chooseSupplier: "Choose a supplier",
    supplierRequired: "Choose the supplier whose account is being inspected.",
    dateFrom: "From date",
    dateTo: "To date",
    branch: "Branch",
    currency: "Currency",
    costCenter: "Cost centre",
    account: "Account",
    documentType: "Document type",
    documentNumber: "Document number",
    documentDate: "Document date",
    reference: "Reference",
    description: "Description",
    amountFrom: "Amount from",
    amountTo: "Amount to",
    debit: "Debit",
    credit: "Credit",
    netMovement: "Net movement",
    balance: "Balance",
    status: "Status",
    postedOnly: "Posted only",
    includeReversed: "Include reversed",
    reversed: "Reversed",
    statusDraft: "Draft",
    statusAudited: "Audited",
    statusPosted: "Posted",
    statusRejected: "Rejected",
    statusCancelled: "Cancelled",
    statusReversed: "Reversed",
    paymentMethod: "Payment method",
    user: "User",
    all: "All",
    yes: "Yes",
    no: "No",
    none: "—",
    totalDebit: "Total debit",
    totalCredit: "Total credit",
    rowCount: "Rows",
    side: "Side",
    sideDebit: "Debit only",
    sideCredit: "Credit only",
    open: "Open",
  },
  supplierPayables: {
    title: "Total Supplier Payables",
    description: "One row per supplier: movement, balance, what is payable and what was paid ahead.",
    code: "Code",
    name: "Supplier name",
    controlAccount: "Control account",
    accountingBalance: "Accounting balance",
    payable: "Payable",
    advance: "Advance",
    balanceType: "Balance type",
    payableOnly: "Payable only",
    advanceOnly: "Advances only",
    zeroOnly: "Zero balances only",
    minimumBalance: "Minimum balance",
    maximumBalance: "Maximum balance",
    openDocuments: "Open documents",
    withOpenOnly: "With open documents",
    withoutOpenOnly: "Without open documents",
    unsettledInvoices: "Unsettled invoices",
    unappliedPayments: "Unapplied payments",
    lastMovement: "Last movement",
    creditLimit: "Credit limit",
    availableCredit: "Available credit",
    supplierCount: "Suppliers",
    totalPayable: "Total payable",
    totalAdvance: "Total advances",
    activeOnly: "Active only",
    inactiveOnly: "Inactive only",
    noSuppliers: "No suppliers match these filters",
    ledgerNote: "Balances come from posted ledger entries on each supplier's control account.",
  },
  supplierAccountMovements: {
    title: "Supplier Account Movements",
    description: "One supplier's statement, with an opening balance and a running balance.",
    openingBalance: "Opening balance",
    closingBalance: "Closing balance",
    runningBalance: "Running balance",
    pickSupplier: "Choose a supplier to see their statement.",
    noMovements: "No movements in this range",
    balanceNote: "The opening balance is the net movement before the From date; the running balance accumulates on top of it.",
    supplierInvoiceNumber: "Supplier invoice no.",
    supplierInvoiceDate: "Supplier invoice date",
    dueDate: "Due date",
    natureDebit: "Dr",
    natureCredit: "Cr",
  },
  supplierMovements: {
    title: "Supplier Movements",
    description: "The same movements across every supplier you may inquire on.",
    noMovements: "No movements in this range",
    everySupplier: "No running balance — one would be meaningless across several suppliers.",
  },
  paymentsDailyTransactions: {
    title: "Payments Daily Transactions",
    description: "Every payables document raised in a window, accounting and non-accounting alike.",
    documentFamily: "Document family",
    nature: "Nature",
    accounting: "Accounting",
    nonAccounting: "Non-accounting",
    accountingCount: "Accounting documents",
    nonAccountingCount: "Non-accounting documents",
    totalAccountingAmount: "Total accounting amount",
    createdAt: "Created",
    noDocuments: "No documents in this range",
    natureNote: "A non-accounting document — a payment order, for instance — moves no ledger, and is excluded from the accounting total.",
    families: {
      ServicePurchaseRequest: "Service Purchase Request",
      ServicePurchaseInvoice: "Service Purchase Invoice",
      ServicePurchaseReturn: "Service Purchase Return",
      PaymentOrder: "Payment Order",
      PaymentVoucher: "Payment Voucher",
      SupplierDebitNote: "Supplier Debit Note",
      SupplierCreditNote: "Supplier Credit Note",
      SupplierTransactionTransfer: "Supplier Transaction Transfer",
      PrepaidExpenseSchedule: "Prepaid Expense Schedule",
      PrepaidExpenseRecognition: "Prepaid Expense Recognition",
      SupplierSettlement: "Supplier Settlement",
      SettlementCancellation: "Settlement Cancellation",
      SupplierBalanceReminder: "Supplier Balance Reminder",
      SupplierInformationAdjustment: "Supplier Information Adjustment",
      SupplierCreditLimitAdjustment: "Supplier Credit Limit Adjustment",
      PaymentBook: "Payment Book",
    },
  },
  paymentVoucherMovements: {
    title: "Payment Voucher Movements",
    description: "Payment vouchers with the real split across payment methods.",
    voucherNumber: "Voucher number",
    voucherDate: "Voucher date",
    paidTo: "Paid to",
    directAccount: "Direct account",
    paymentOrder: "Payment order",
    components: "Payment components",
    componentCount: "Components",
    cashbox: "Cashbox",
    bankAccount: "Bank account",
    localAmount: "Local amount",
    includeDirect: "Include direct payments",
    postedCount: "Posted",
    reversedCount: "Reversed",
    totalAmount: "Total",
    noVouchers: "No payment vouchers in this range",
    splitNote: "The split is shown exactly as recorded on the voucher; no single method is invented for a split voucher.",
    viewComponents: "Show components",
    hideComponents: "Hide components",
  },
  servicePurchaseDocuments: {
    title: "Service Purchase Document Movements",
    description: "Request, then invoice, then return — as one chain.",
    request: "Request",
    invoice: "Invoice",
    documentReturn: "Return",
    supplierReference: "Supplier reference",
    sourceDocument: "Source document",
    netAmount: "Net",
    taxAmount: "Tax",
    totalAmount: "Total",
    requestCount: "Requests",
    invoiceCount: "Invoices",
    returnCount: "Returns",
    totalNet: "Total net",
    totalTax: "Total tax",
    includeLines: "Include lines",
    lineDescription: "Line description",
    quantity: "Quantity",
    unitPrice: "Unit price",
    taxPercent: "Tax %",
    expenseAccount: "Expense account",
    noDocuments: "No documents in this range",
    historicalNote: "Amounts and rates are shown as the document recorded them, not as the master data reads today.",
    viewLines: "Show lines",
    hideLines: "Hide lines",
  },
  supplierCostCenterMovements: {
    title: "Supplier Movements by Cost Center",
    description: "Supplier postings split by the cost centre each posting actually carries.",
    includeChildren: "Include child cost centres",
    costCenterOnly: "Only movements with a cost centre",
    uncosted: "No cost centre",
    uncostedCount: "Uncosted movements",
    parentCostCenter: "Parent centre",
    bySupplier: "By supplier",
    byCostCenter: "By cost centre",
    byAccount: "By account",
    subtotals: "Subtotals",
    noMovements: "No movements in this range",
    authoritativeNote: "The cost centre is read from the posting itself. It is never inferred from the supplier card, the branch or any other document.",
  },
  paymentBooksInquiry: {
    title: "Payment Books",
    description: "Book custody: ranges, holders, and every hand-over on record.",
    bookNumber: "Book number",
    fromNumber: "From number",
    toNumber: "To number",
    voucherCount: "Numbers in book",
    holder: "Current holder",
    holderType: "Holder type",
    receivedDate: "Received",
    assignedDate: "Assigned",
    returnedDate: "Returned",
    assignedFrom: "Assigned from",
    assignedTo: "Assigned to",
    lastMovement: "Last movement",
    movements: "Custody movements",
    viewMovements: "Show custody movements",
    hideMovements: "Hide custody movements",
    totalBooks: "Books",
    totalVouchers: "Registered numbers",
    noBooks: "No payment books match these filters",
    custodyNote: "This screen involves no supplier, so supplier scope does not apply — company, branch and the inquiry permission still do.",
  },
  paymentBookRangeGaps: {
    title: "Missing Payment Book Sequences",
    description: "Gaps between REGISTERED payment book ranges — numbers belonging to no registered book.",
    previousBook: "Previous book",
    previousEnd: "Ends at",
    nextBook: "Next book",
    nextStart: "Starts at",
    gapFrom: "Gap from",
    gapTo: "Gap to",
    gapSize: "Numbers in gap",
    gapCount: "Gaps",
    unregisteredNumbers: "Unregistered numbers",
    booksExamined: "Books examined",
    rangeFrom: "Range from",
    rangeTo: "Range to",
    noGaps: "The registered book ranges are contiguous",
    detectionNote: "Payment books do not consume payment voucher numbers in this system, so this screen reports number ranges registered to no book. It does not identify missing, lost or unused payment vouchers.",
  },
  expenseInquiries: {
    movements: "Expense Movements",
    account: "Expense Account Statement",
    movementsByCostCenter: "Expense Movements by Cost Center",
    accountByCostCenter: "Expense Account by Cost Center",
    glNote: "This is the General Ledger's own screen with the account picker narrowed to expense accounts. There is no separate expense engine in this system.",
  },
  unsettledPaymentOrders: {
    title: "Payment Orders Not Settled by Payment Vouchers",
    description: "Payment orders issued and not yet fully covered by posted payment vouchers.",
    orderNumber: "Order number", orderDate: "Order date",
    orderAmount: "Order amount", paidAmount: "Paid", remainingAmount: "Remaining",
    settlementState: "Settlement state", expectedPaymentDate: "Expected payment date",
    voucherCount: "Vouchers", includeFullyPaid: "Include fully paid",
    includeCancelled: "Include cancelled", minimumRemaining: "Minimum remaining",
    totalOrdered: "Total ordered", unpaidCount: "Unpaid",
    partiallyPaidCount: "Partially paid", unpaid: "Unpaid",
    partiallyPaid: "Partially paid", paid: "Paid",
    noOrders: "No unsettled payment orders",
    derivedNote: "Paid comes from vouchers that actually posted; reversing one removes it from the total automatically.",
  },
  supplierInformationInquiry: {
    title: "Supplier Information",
    description: "The supplier card, read only, within your inquiry scope.",
    taxNumber: "Tax number", commercialRegistration: "Commercial registration",
    nationalId: "National ID", phone: "Phone", mobile: "Mobile", email: "Email",
    address: "Address", city: "City", country: "Country", creditLimit: "Credit limit",
    activeCount: "Active", inactiveCount: "Inactive", active: "Active", inactive: "Inactive",
    noSuppliers: "No suppliers match these filters",
    readOnlyNote: "A view only. Editing belongs to Master Data, and adjusting to the Supplier Information Adjustment document.",
  },
  supplierCurrencyStatement: {
    title: "Supplier Account - Currency / Local",
    description: "A supplier statement in both the transaction currency and the local one.",
    transactionCurrency: "Transaction currency", exchangeRate: "Exchange rate",
    foreignDebit: "Debit (FX)", foreignCredit: "Credit (FX)", foreignBalance: "Balance (FX)",
    localDebit: "Debit (local)", localCredit: "Credit (local)", localBalance: "Balance (local)",
    openingForeign: "Opening (FX)", openingLocal: "Opening (local)",
    closingForeign: "Closing (FX)", closingLocal: "Closing (local)",
    ledgerCurrencies: "Ledger currencies", movements: "Movements",
    pickSupplier: "Choose a supplier to see their statement by currency.",
    noMovements: "No movements in this range",
    rateNote: "The exchange rate is read from the posting as it was made, never recomputed from today's rates.",
  },
  supplierBalancesByCostCenter: {
    title: "Total Supplier Balances by Cost Center",
    description: "Supplier control-account balances split by the cost centre each posting carries.",
    unassigned: "Unassigned", unassignedAmount: "Unassigned amount",
    includeUnassigned: "Show unassigned", includeZeroBalances: "Show zero balances",
    upToDate: "As of", costCenterCount: "Cost centres",
    noBalances: "No balances in this range",
    reconcileNote: "The total reconciles to the control account exactly, because uncosted postings appear in the Unassigned row rather than being dropped.",
  },
  purchaseOrdersAwaitingPayment: {
    title: "Purchase Orders Awaiting Payment",
    description: "Approved purchase orders whose commitment is not yet fully paid.",
    orderNumber: "Purchase order", committed: "Committed", invoiced: "Invoiced",
    uninvoiced: "Uninvoiced", expectedDate: "Expected date", paymentTerm: "Payment term (days)",
    includeSettled: "Include settled", includeNonOutstanding: "Include non-approved",
    totalCommitted: "Total committed", totalInvoiced: "Total invoiced",
    totalUninvoiced: "Total uninvoiced",
    noOrders: "No purchase orders awaiting payment",
    genuineNote: "Genuine purchase orders — not payment orders. Invoiced comes from invoices naming the order; paid from the settlement engine.",
  },
  combinedBooks: {
    title: "Receipt and Payment Books",
    description: "Both book registers side by side, read only.",
    bookType: "Book type", receiptBook: "Receipt book", paymentBook: "Payment book",
    allBooks: "All books", holder: "Holder", movementCount: "Movements",
    receiptBookCount: "Receipt books", paymentBookCount: "Payment books",
    registeredNumbers: "Registered numbers",
    noBooks: "No books match these filters",
    separateNote: "The two registers keep separate numbering and separate custody history — shown together, never merged, and each row opens its own register.",
  },
  supplierItemTransactions: {
    title: "Supplier Item Transactions",
    description: "Purchase lines by purchase item, at the values the line recorded.",
    item: "Item", allItems: "All items", itemType: "Item type",
    service: "Service", goods: "Goods", quantity: "Quantity", unit: "Unit",
    unitPrice: "Unit price", discount: "Discount", net: "Net",
    uncatalogued: "Uncatalogued", includeUncatalogued: "Include lines with no item",
    documentType: "Document type", purchaseOrder: "Purchase order", purchaseRequest: "Purchase request",
    purchaseInvoice: "Purchase invoice", purchaseReturn: "Purchase return",
    totalQuantity: "Total quantity", itemCount: "Items",
    noTransactions: "No item transactions in this range",
    historicalNote: "Items come from the purchase catalogue, not from service sales types. Values are as the line recorded them, and returns carry a negative quantity.",
  },
  outstandingSupplierInvoices: {
    title: "Outstanding Supplier Invoices",
    description: "What is still owed to suppliers, invoice by invoice.",
    invoiceNumber: "Invoice number", invoiceDate: "Invoice date", dueDate: "Due date",
    originalAmount: "Original amount", appliedAmount: "Applied", outstandingAmount: "Outstanding",
    daysOutstanding: "Days outstanding", daysOverdue: "Days overdue",
    overdueOnly: "Overdue only", outstandingOnly: "Outstanding only",
    minimumOutstanding: "Minimum outstanding", asOfDate: "As of",
    overdueCount: "Overdue invoices", overdueAmount: "Overdue amount",
    totalOriginal: "Total original", totalApplied: "Total applied",
    totalOutstanding: "Total outstanding",
    noInvoices: "No outstanding supplier invoices",
    allocationNote: "Outstanding comes from actual allocations in the reconciliation engine, not from an invoice's status.",
  },
  paidUnpaidSupplierInvoices: {
    title: "Paid & Unpaid Supplier Invoices",
    description: "Every supplier invoice classified by what has actually been applied to it.",
    paymentState: "Payment state", paid: "Paid", partiallyPaid: "Partially paid",
    unpaid: "Unpaid", invoiceAmount: "Invoice amount", remainingAmount: "Remaining",
    totalInvoiced: "Total invoiced", totalPaid: "Total paid",
    totalRemaining: "Total remaining",
    noInvoices: "No invoices in this range",
    engineNote: "The classification is the settlement engine's: Paid means nothing remains, not that the invoice posted.",
  },
  unappliedPaymentVouchers: {
    title: "Payment Vouchers Not Applied to Invoices",
    description: "Money paid to a supplier that no invoice has claimed.",
    voucherAmount: "Voucher amount", appliedAmount: "Applied", unappliedAmount: "Unapplied",
    cashboxOrBank: "Cashbox / Bank", minimumUnapplied: "Minimum unapplied",
    includeFullyApplied: "Include fully applied",
    totalVoucherAmount: "Total vouchers", totalUnapplied: "Total unapplied",
    noVouchers: "No unapplied payment vouchers",
    noGuessNote: "Linked by actual allocation only — never matched by number, reference or equal amounts.",
  },

  employees: {
    title: "Employee Card",
    breadcrumb: "Human Resources « Employee Affairs « Basic Data « Employee Card",
    purposeNotice:
      "Who the company employs: code, name, job title, department, branch and contact details.",
    identityNotice:
      "The linked system user is the SAME user the rest of the system names, not a second identity. The field is optional: most employees have no login.",
    noPayrollNotice:
      "Master data only — no salary, no contract value, no leave balance, no attendance and no accounting entry. There is no delete: withdrawing an employee deactivates them and keeps the record readable.",
    vocabulariesEmpty:
      "The job title, department and contract type lists are empty. This card READS those vocabularies and never creates them — they arrive later from HR settings.",
    readOnlyNotice: "You have view access only; saving and editing are disabled.",
    listTitle: "Employees",
    formTitle: "Employee details",
    newRecord: "New record",
    search: "Search",
    searchHint: "Code, name, national id, mobile or email",
    refresh: "Refresh",
    showInactive: "Show deactivated",
    noRows: "No employees",
    branchFilter: "Branch",
    allBranches: "All branches",
    allDepartments: "All departments",
    linkFilter: "User link",
    anyLink: "Any",
    linkedOnly: "Linked only",
    unlinkedOnly: "Unlinked only",
    code: "Employee code",
    nameAr: "Arabic name",
    nameEn: "English name",
    user: "System user",
    noUser: "No user",
    userHint: "The same user id the rest of the system already carries.",
    jobTitle: "Job title",
    noJobTitle: "No job title",
    department: "Department",
    noDepartment: "No department",
    contractType: "Contract type",
    noContractType: "No contract type",
    branch: "Branch",
    noBranch: "No branch",
    hireDate: "Hire date",
    nationalId: "National id",
    mobile: "Mobile",
    phone: "Phone",
    email: "Email",
    notes: "Notes",
    active: "Active",
    actions: "Actions",
    edit: "Edit",
    view: "View",
    activate: "Activate",
    deactivate: "Deactivate",
    save: "Save",
    saveNew: "Save & new",
    cancel: "Cancel",
    details: "Employee details",
    updatedAt: "Last updated",
    auditHistory: "Change history",
    auditNone: "No recorded changes",
    auditAction: "Action",
    auditFields: "Fields",
    auditChangedBy: "By",
    auditChangedAt: "When",
    close: "Close",
    saved: "Saved",
    activated: "Activated",
    deactivated: "Deactivated",
    counts: "Total / active",
    matched: "Matched",
    loadError: "Could not load employees",
    saveError: "Could not save",
    auditError: "Could not load the change history",
  },
  settingsExport: {
    print: "Print",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    working: "Working…",
    failed: "Export failed",
  },
  hr: {
    employeeCard: "Employee Card", responsibleEntities: "Responsible Entities",
    employeeRegister: "Employee register",
    inquiry: "Find", newRecord: "New", save: "Save", saveAndNew: "Save + New", cancel: "Cancel",
    close: "Close", edit: "Edit", delete: "Delete", refresh: "Refresh", search: "Search",
    searchAndFilter: "Search & filter", all: "All", none: "—", actions: "Actions", status: "Status",
    active: "Active", inactive: "Inactive", activeRecord: "Active record", unsaved: "Unsaved",
    saved: "Saved", deleted: "Deleted", loadFailed: "Could not load the data",
    saveFailed: "Could not save", deleteFailed: "Could not delete",
    confirmDeleteTitle: "Confirm deletion", deleteEntityConfirm: "The entity \"{name}\" will be deleted.",
    code: "Code", nameAr: "Arabic name", nameEn: "English name", notes: "Notes", employees: "Employees",
    newEntity: "New entity", editEntity: "Edit entity", noEntities: "No matching entities",
    noEmployees: "No matching employees",
    selectEmployee: "Pick an employee from the register, or start a new card",
    identification: "Identification", serial: "Serial", employeeNumber: "Employee no.",
    firstNameAr: "First name", secondNameAr: "Second name", thirdNameAr: "Third name",
    familyNameAr: "Family name",
    firstNameEn: "First name (EN)", secondNameEn: "Second name (EN)",
    thirdNameEn: "Third name (EN)", familyNameEn: "Family name (EN)",
    photo: "Employee photo", uploadPhoto: "Upload photo", removePhoto: "Remove",
    photoSaved: "Photo saved", photoFailed: "Could not upload the photo",
    personalInfo: "Personal information", nationality: "Nationality", religion: "Religion",
    gender: "Gender", male: "Male", female: "Female",
    maritalStatus: "Marital status", single: "Single", married: "Married",
    divorced: "Divorced", widowed: "Widowed",
    nationalId: "National id", birthDate: "Date of birth", birthDateHijri: "Date of birth (Hijri)",
    birthPlace: "Place of birth", motherName: "Mother's name", specialNeeds: "Has special needs",
    address: "Address", country: "Country", governorate: "Governorate", city: "City", area: "Area",
    addressLine: "Address", phone: "Phone", altPhone: "Alternate phone", email: "Email",
    employmentInfo: "Employment information", hireDate: "Hire date", department: "Department",
    jobTitle: "Job title", employmentType: "Employment type", responsibleEntity: "Responsible entity",
    branch: "Branch", costCenter: "Cost centre", employmentStatus: "Employment status",
    statusActive: "Active", statusOnLeave: "On leave", statusSuspended: "Suspended",
    statusTerminated: "Terminated", statusResigned: "Resigned",
    jobDetails: "Job details",
    contractSection: "Contract",
    insuranceSection: "Social insurance & cover",
    secondaryJobTitle: "Secondary job title",
    technicalClassification: "Technical classification",
    grade: "Grade",
    employeeClassification: "Employee classification",
    directManager: "Direct manager",
    supervisor: "Supervisor",
    dailyWorkingHours: "Daily working hours",
    probationPeriodDays: "Probation (days)",
    employmentStatusDate: "Status date",
    isHazardousOccupation: "Hazardous occupation",
    allowsOvertime: "Allows overtime",
    spouseIsEmployed: "Spouse is employed",
    isFamilyProvider: "Family provider",
    payFrequency: "Pay frequency",
    payMonthly: "Monthly",
    payDaily: "Daily",
    payHourly: "Hourly",
    contractType: "Contract type",
    contractStart: "Contract start",
    contractEnd: "Contract end",
    isFixedTermContract: "Fixed term",
    renewalMethod: "Renewal method",
    renewalDaily: "By days",
    renewalMonthly: "By months",
    renewalCalendar: "By calendar",
    leaveCategory: "Leave category",
    isGosiRegistered: "Registered with GOSI",
    gosiRegistrationDate: "GOSI registration date",
    gosiNumber: "GOSI number",
    gosiPolicy: "GOSI policy",
    hasHealthInsurance: "Has health insurance",
    healthInsurancePolicy: "Health insurance policy",
  },
  hrLeave: {
    leave: "Leave",
    leaveTypes: "Leave Types",
    leaveRequests: "Leave Requests",
    leaveAmendments: "Leave Amendments",
    leaveBalanceAdjustments: "Leave Balance Adjustments",
    leaveMovements: "Leave Movements",
    leaveBalances: "Employee Leave Balances",
    new: "New",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    close: "Close",
    refresh: "Refresh",
    search: "Search",
    actions: "Actions",
    status: "Status",
    all: "All",
    none: "—",
    saved: "Saved",
    deleted: "Deleted",
    loadFailed: "Could not load the data",
    saveFailed: "Could not save",
    deleteFailed: "Could not delete",
    noResults: "No results",
    required: "Required",
    confirmDeleteTitle: "Confirm deletion",
    confirmDelete: "Delete this record?",
    yes: "Yes",
    no: "No",
    days: "days",
    day: "day",
    typeCode: "Code",
    typeNameAr: "Arabic name",
    typeNameEn: "English name",
    isActive: "Active",
    isPaid: "Paid",
    requiresBalance: "Draws on a balance",
    allowsNegativeBalance: "May go negative",
    requiresAttachment: "Attachment required",
    minimumDays: "Minimum days",
    maximumDays: "Maximum days",
    noticeDays: "Notice days",
    annualEntitlementDays: "Annual entitlement (days)",
    carryForwardAllowed: "Carry forward allowed",
    maximumCarryForwardDays: "Maximum carried forward (days)",
    notes: "Notes",
    newLeaveType: "New leave type",
    editLeaveType: "Edit leave type",
    noLeaveTypes: "No leave type has been defined yet",
    typeInUse: "In use",
    deactivateInstead: "A type in use cannot be deleted — deactivate it instead.",
    policy: "Policy",
    paid: "Paid",
    unpaid: "Unpaid",
    balanceDriven: "Balance",
    noBalance: "No balance",
    number: "Number",
    employee: "Employee",
    leaveType: "Leave type",
    requestDate: "Request date",
    fromDate: "From",
    toDate: "To",
    returnDate: "Return date",
    requestedDays: "Requested days",
    approvedDays: "Approved days",
    replacement: "Replacement",
    branch: "Branch",
    department: "Department",
    reason: "Reason",
    newRequest: "New leave request",
    noRequests: "No requests",
    submit: "Submit",
    approve: "Approve",
    reject: "Reject",
    cancelRequest: "Cancel request",
    rejectionReason: "Rejection reason",
    submitted: "Submitted",
    approved: "Approved",
    rejected: "Rejected",
    cancelledStatus: "Cancelled",
    draft: "Draft",
    available: "Available",
    upcoming: "Upcoming",
    attachments: "Attachments",
    addAttachment: "Add attachment",
    noAttachments: "No attachments",
    amendments: "Amendments",
    noAmendments: "No amendments",
    amend: "Amend leave",
    amendAction: "Amendment",
    actionCancel: "Cancel",
    actionExtend: "Extend",
    actionInterrupt: "Interrupt",
    actionChangeCategory: "Change category",
    effectiveDate: "Effective date",
    newToDate: "New end date",
    newLeaveType2: "New leave type",
    daysDifference: "Days difference",
    oldDays: "Days before",
    newDays: "Days after",
    submittedAt: "Submitted at",
    approvedAt: "Approved at",
    rejectedAt: "Rejected at",
    cancelledAt: "Cancelled at",
    by: "by",
    adjustment: "Balance adjustment",
    newAdjustment: "New balance adjustment",
    direction: "Direction",
    increase: "Grant",
    decrease: "Deduct",
    isOpeningBalance: "Opening balance",
    adjustmentDays: "Days",
    adjustmentReason: "Reason",
    noAdjustments: "No balance adjustments",
    movementType: "Movement",
    movementOpeningBalance: "Opening balance",
    movementEntitlement: "Entitlement",
    movementUsage: "Usage",
    movementAdjustment: "Adjustment",
    movementReversal: "Reversal",
    balanceBefore: "Balance before",
    balanceAfter: "Balance after",
    sourceDocument: "Document",
    entitled: "Entitled",
    used: "Used",
    adjusted: "Adjusted",
    noMovements: "No movements",
    noBalances: "No balances",
    activeEmployeesOnly: "Active employees only",
    asOf: "As of",
    filters: "Filters",
    apply: "Show",
    reset: "Clear",
    rows: "rows",
    print: "Print",
    exportExcel: "Export to Excel",
    allowSelfApproval: "Submitter may approve",
    employmentStatus: "Employment status",
    groupBy: "Group by",
    groupByLeaveType: "Leave type",
    groupByEmployee: "Employee",
    groupByDepartment: "Department",
    groupByBranch: "Branch",
    groupByMonth: "Month",
    statusOnLeave: "On leave",
    statusSuspended: "Suspended",
    statusTerminated: "Terminated",
    statusResigned: "Resigned",
    statusActiveEmployee: "Active",
  },
  hotelFrontDesk: {
    title: "Front Desk",
    roomBoardTitle: "Room Board",
    masterDataTitle: "Master Data",
    settingsTitle: "Hotel Settings",
    roomTypesTitle: "Room Types",
    roomsTitle: "Rooms",

    statusVacant: "Vacant",
    statusOccupiedFirstNight: "Occupied — first night",
    statusOccupiedContinuing: "Occupied — ongoing stay",
    statusOccupiedCheckoutToday: "Occupied — checking out today",
    statusVacantDirtyToday: "Checked out today — not cleaned",
    statusVacantDirtyStale: "Checked out earlier — still not cleaned",
    statusReserved: "Reserved (not arrived)",
    statusOutOfService: "Out of Service",
    needsCleaningBadge: "Needs Cleaning",

    book: "Book",
    checkIn: "Check In",
    checkOut: "Check Out",
    markCleaned: "Mark Cleaned",
    flagNeedsCleaning: "Needs Cleaning",
    setOutOfService: "Out of Service (Maintenance)",
    returnToService: "Return to Service",
    outOfServiceReasonPrompt: "Reason for out of service",
    outOfServiceReasonHint: "Optional — e.g. electrical repair, leak",
    outOfServiceBadge: "Maintenance",

    guestName: "Guest Name",
    guestPhone: "Guest Phone",
    findOrAddGuest: "Find or add a guest",
    existingGuestFound: "A returning guest with this number was found",
    newGuestWillBeCreated: "A new guest card will be created",

    checkInDate: "Check-in Date",
    checkOutDate: "Check-out Date",
    actualCheckOutDate: "Actual Check-out Date",
    nights: "Nights",
    nightlyRate: "Nightly Rate",
    discountAmount: "Discount Amount",
    paymentMethod: "Payment Method",
    cash: "Cash",
    visa: "Visa",
    passportNumber: "Passport Number",
    totalPax: "Total Pax",
    notes: "Notes (nationality, etc.)",

    code: "Code",
    name: "Name",
    nameAr: "Arabic Name",
    pricePerNight: "Price per Night",
    serviceSalesType: "Service Sales Type",
    roomNumber: "Room Number",
    floor: "Floor",
    roomType: "Room Type",
    outOfService: "Out of Service",
    outOfServiceReason: "Reason",
    active: "Active",

    defaultCostCenter: "Default Cost Centre",
    defaultGuestControlAccount: "Default Guest Control Account",
    discountType: "Discount Type (Fixed Amount)",
    none: "None",
    cleaningDueTime: "Cleaning Due Time",

    save: "Save",
    cancel: "Cancel",
    close: "Close",
    confirm: "Confirm",
    newRoomType: "New Room Type",
    newRoom: "New Room",

    invoiceCreatedNotice: "A draft invoice was created. Awaiting the accountant's audit and posting.",
    loadFailed: "Failed to load data",
    saveFailed: "Failed to save",
    empty: "No data",
  },
  hrDaily: {
    inquiry: "Inquiry",
    new: "New",
    save: "Save",
    saveAndNew: "Save + New",
    print: "Print",
    cancel: "Cancel",
    submit: "Submit",
    approve: "Approve",
    reject: "Reject",
    execute: "Execute",
    send: "Send",
    sent: "Sent",
    cancelDocument: "Cancel document",
    close: "Close",
    delete: "Delete",
    refresh: "Refresh",
    apply: "Show",
    all: "All",
    none: "—",
    actions: "Actions",
    status: "Status",
    draft: "Draft",
    submitted: "Submitted",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
    executed: "Executed",
    number: "Number",
    employee: "Employee",
    employeeNumber: "Employee number",
    documentDate: "Document date",
    fromDate: "From",
    toDate: "To",
    branch: "Branch",
    department: "Department",
    attachments: "Attachments",
    addAttachment: "Add attachment",
    noAttachments: "No attachments",
    audit: "Audit trail",
    submittedAt: "Submitted",
    approvedAt: "Approved",
    rejectedAt: "Rejected",
    cancelledAt: "Cancelled",
    executedAt: "Executed",
    rejectionReason: "Rejection reason",
    noResults: "No records match these criteria",
    loadFailed: "Could not load the data",
    saveFailed: "Could not save",
    saved: "Saved",
    openDocument: "Open",
    remove: "Remove",
    dailyTransactions: "Daily Transactions",
    sectionLeave: "Leave",
    sectionDepartures: "Departures",
    sectionJobMovement: "Job Movement",
    sectionContracts: "Contracts & Service",
    sectionDocuments: "Documents",
    sectionTimeTravel: "Overtime & Travel",
    sectionConductHealth: "Conduct & Health",
    sectionCustody: "Custody",
    sectionAdministration: "Administration & Communications",
    custodyNote: "Employee custody is managed in Fixed Assets. This screen shows the custody records linked to employees; it does not create a parallel register.",
    iqamaMovements: "Iqama Movements",
    healthCertificates: "Health Certificate Movements",
    employeeCustody: "Employee Custody Movements",
  },
  glGuide: {
    subtitleTree: "Every module, section by section and screen by screen — read from the module itself, not a separate list.",
    module: "Module",
    coverage: "{written} of {total} screens documented in this module.",
    notWritten: "Not written yet — the screen exists, its explanation does not.",
    notBuilt: "Part of the approved structure, not built yet.",
    title: "Accounts Guide",
    subtitle: "{count} screens — what each one does, how it is used, and where it lands in the accounts.",
    search: "Search the guide",
    noResults: "No matching entries",
    openScreen: "Open screen →",
    purpose: "Purpose",
    usage: "How it is used",
    accounting: "Accounting effect",
    caution: "Watch out",
  },
  systemSettings: {
    search: "Search",
    add: "Add",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    remove: "Remove",
    retry: "Retry",
    saved: "Saved",
    deleted: "Deleted",
    actions: "Actions",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
    noResults: "No results",
    showing: "Showing {from}-{to} of {total}",
    previous: "Previous",
    next: "Next",
    code: "Code",
    name: "Name",
    nameAr: "Arabic name",
    description: "Description",
    phone: "Phone",
    email: "Email",
    address: "Address",
    city: "City",
    country: "Country",
    loadError: "Could not load the data",
    saveError: "Could not save",
    deleteError: "Could not delete",
    confirmDeleteTitle: "Confirm deletion",

    users: "Users",
    newUser: "New user",
    editUser: "Edit user",
    userName: "User name",
    fullName: "Full name",
    fullNameAr: "Arabic full name",
    password: "Password",
    newPassword: "New password",
    showPassword: "Show",
    hidePassword: "Hide",
    confirmPassword: "Confirm password",
    passwordMismatch: "The passwords do not match",
    language: "Interface language",
    english: "English",
    arabic: "Arabic",
    userRoles: "Roles",
    companyAccess: "Company access",
    allCompanies: "Every company in the tenant",
    defaultCompany: "Default company",
    lastLogin: "Last sign-in",
    neverSignedIn: "Never signed in",
    mustChangePassword: "Must change password at next sign-in",
    systemAdministrator: "System administrator",
    resetPassword: "Set password",
    passwordWasReset: "Password set",
    assignRoles: "Assign roles",
    rolesAssigned: "Roles updated",
    deleteUserConfirm: "The user \"{name}\" will be deleted.",
    statusPending: "Pending",
    statusActive: "Active",
    statusSuspended: "Suspended",
    statusLocked: "Locked",
    statusDisabled: "Disabled",
    noUsers: "No matching users",

    roles: "Roles",
    newRole: "New role",
    editRole: "Edit role",
    systemRole: "System role",
    userCount: "Users",
    permissionCount: "Permissions",
    editPermissions: "Edit permissions",
    permissionsSaved: "Permissions updated",
    deleteRoleConfirm: "The role \"{name}\" will be deleted.",
    selectAll: "Select all",
    clearAll: "Clear all",
    notGrantable: "You do not hold this permission, so you cannot grant it",
    granted: "Granted: {count}",
    noRoles: "No matching roles",

    permissions: "Permissions",
    permissionKey: "Key",
    module: "Module",
    grantedToRoles: "Granted to roles",
    grantedToNoRole: "Granted to no role",
    catalogueCounts: "{permissions} permissions across {modules} modules",

    auditTrail: "System Audit Trail",
    occurredAt: "When",
    user: "User",
    action: "Action",
    entity: "Entity",
    record: "Record",
    summary: "Summary",
    changedFields: "Changed fields",
    ipAddress: "IP address",
    correlationId: "Request id",
    viewDetails: "Details",
    valuesBefore: "Values before",
    valuesAfter: "Values after",
    dateFrom: "From",
    dateTo: "To",
    anyAction: "All actions",
    anyEntity: "All entities",
    noAuditEntries: "No matching entries",
    actionCreated: "Created",
    actionUpdated: "Updated",
    actionDeleted: "Deleted",
    actionLogin: "Sign-in",
    actionLoginFailed: "Failed sign-in",
    actionLogout: "Sign-out",
    actionPermissionDenied: "Permission denied",
    actionJournalAudited: "Journal audited",
    actionJournalPosted: "Journal posted",
    actionJournalRejected: "Journal rejected",
    actionJournalReversed: "Journal reversed",
    actionJournalReopened: "Posted journal reopened",
    actionExported: "Exported",

    accessMatrix: "User Access Matrix",
    effectivePermissions: "Effective permissions",
    viaRoles: "Via roles",
    holdsPermission: "Holds permission",
    anyPermission: "Any permission",
    noAccess: "No role assigned",

    companies: "Companies",
    newCompany: "New company",
    editCompany: "Edit company",
    legalName: "Legal name",
    taxNumber: "Tax number",
    registrationNumber: "Commercial registration",
    website: "Website",
    localCurrency: "Reporting currency",
    fiscalYearStart: "Fiscal year starts",
    branchCount: "Branches",
    deleteCompanyConfirm: "The company \"{name}\" will be deleted.",
    noCompanies: "No matching companies",
    branches: "Branches",
    newBranch: "New branch",
    editBranch: "Edit branch",
    company: "Company",
    anyCompany: "All companies",
    headOffice: "Head office",
    deleteBranchConfirm: "The branch \"{name}\" will be deleted.",
    noBranches: "No matching branches",
    query: "Find",
    newRecord: "New",
    saveAndNew: "Save + New",
    userInformation: "User information",
    userRegister: "User register",
    loginName: "Login",
    arabicDescription: "Arabic name",
    englishDescription: "English name",
    defaultLanguage: "Default language",
    createdAt: "Created",
    passwordChangedAt: "Password last changed",
    lockedOutUntil: "Locked until",
    failedAttempts: "Failed sign-ins",
    notSaved: "Unsaved",
    unsavedRecord: "New record",
    selectUser: "Pick a user from the register, or start a new record",
    changePassword: "Change password",
    passwordOnCreateOnly: "The password is set at creation, then changed from the Change password action",
    defaultCompanyBranch: "Default company and branch",
    defaultBranch: "Default branch",
    tabPermissions: "Permissions",
    tabCompanies: "Companies & branches",
    tabRestricted: "Restricted access",
    assigned: "Assigned",
    permissionsFromRoles: "Effective permissions are derived from the assigned roles",
    noPermissions: "No permissions",
    grantedByRole: "via",
    restrictedSuppliers: "Permitted suppliers",
    restrictedCustomers: "Permitted customers",
    restrictedCashboxes: "Permitted cash boxes",
    openManager: "Open screen",
    accessRows: "{count} rows",
    unrestricted: "Unrestricted",
    notSupported: "Not supported in this system",
    fiscalYearAccess: "Fiscal-year access",
    blockedAccounts: "Blocked accounts",
    reminderNotifications: "Reminder notifications",
    interactiveScreens: "Interactive screens",
    specialPermissions: "Special permissions",
    twoFactor: "Two-factor authentication",
    cloudUser: "Cloud user",
    userCode: "Code",
    identificationCode: "Identification code",
    expiryDate: "Expiry date",
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
  },
};

export const dictionaries: Record<Locale, Dictionary> = { ar, en };
